import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Position,
  MarkerType,
  Handle,
  useReactFlow,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import API_BASE_URL from '../api/config';

import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Chip,
  Fab,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Alert,
  Drawer,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Message as MessageIcon,
  CloudQueue as ApiIcon,
  CloudUpload as UploadIcon,
  Settings as ProcessingIcon,
  Psychology as AiIcon,
  Input as InputIcon,
  AccountTree as BranchIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  OpenInFull as ExpandIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as PreviewIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  PlayCircleOutline as TestIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Pause as PauseIcon,
  SkipNext as NextIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
  DataObject as DataIcon,
  SettingsOutlined as SettingsIcon,
  Info as InfoIcon,
  Description as ReportIcon,
  SmartToy as AIAssistantIcon,
  Loop as LoopIcon
} from '@mui/icons-material';
import AIFlowBuilderModal from './AIFlowBuilderModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator as DragIcon } from '@mui/icons-material';
import api from '../api/axios';

// Sortable Column Component for drag-and-drop functionality
const SortableColumnCard = ({ 
  column, 
  index, 
  onColumnChange, 
  onColumnDelete, 
  children 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      sx={{ 
        mb: 2, 
        p: 2, 
        backgroundColor: '#f0fff0', 
        border: '1px solid #d4edda',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: '#666',
            '&:hover': {
              color: '#333'
            }
          }}
        >
          <DragIcon />
        </Box>
        <Box flexGrow={1}>
          {children}
        </Box>
        <IconButton
          size="small"
          onClick={() => onColumnDelete(index)}
          sx={{ color: '#d32f2f' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
};

// Sortable Comparison Field Component for drag-and-drop functionality
const SortableComparisonFieldCard = ({ 
  field, 
  index, 
  onFieldChange, 
  onFieldDelete, 
  children 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      sx={{ 
        mb: 2, 
        p: 2, 
        backgroundColor: '#f0fff0', 
        border: '1px solid #d4edda',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: '#666',
            '&:hover': {
              color: '#333'
            }
          }}
        >
          <DragIcon />
        </Box>
        <Box flexGrow={1}>
          {children}
        </Box>
        <IconButton
          size="small"
          onClick={() => onFieldDelete(index)}
          sx={{ color: '#d32f2f' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
};

// Sortable Variable Operation Component for drag-and-drop functionality
const SortableVariableOperationCard = ({ 
  operation, 
  index, 
  onOperationDelete, 
  children 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: operation.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      sx={{ 
        mb: 2, 
        p: 2, 
        backgroundColor: '#f0f8f0', 
        border: '1px solid #d4edda',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: '#666',
            '&:hover': {
              color: '#333'
            },
            pt: 1
          }}
        >
          <DragIcon />
        </Box>
        <Box flexGrow={1}>
          {children}
        </Box>
        <IconButton
          size="small"
          onClick={() => onOperationDelete(index)}
          sx={{ color: '#d32f2f' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
};

// Sortable Transform Array Field Mapping Component
const SortableTransformFieldMappingCard = ({ 
  mapping, 
  index, 
  onMappingDelete, 
  children 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mapping.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      sx={{ 
        mb: 2, 
        p: 2, 
        backgroundColor: '#f8f9fa', 
        border: '1px solid #e0e0e0',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: '#666',
            '&:hover': {
              color: '#333'
            },
            pt: 1
          }}
        >
          <DragIcon />
        </Box>
        <Box flexGrow={1}>
          {children}
        </Box>
        <IconButton
          size="small"
          onClick={() => onMappingDelete(index)}
          sx={{ color: '#d32f2f' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
};

// Sortable Form Field Component for drag-and-drop functionality in Simple Form
const SortableFormFieldCard = ({ 
  field, 
  index, 
  onFieldChange, 
  onFieldDelete, 
  children 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      sx={{ 
        mb: 2, 
        p: 2, 
        backgroundColor: '#f0f8ff', 
        border: '1px solid #e1e7ff',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: '#666',
            paddingTop: '4px',
            '&:hover': {
              color: '#333'
            }
          }}
        >
          <DragIcon />
        </Box>
        <Box flexGrow={1}>
          {children}
        </Box>
        <IconButton
          size="small"
          onClick={() => onFieldDelete(index)}
          sx={{ color: '#d32f2f' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
};

// Sortable Header Field Component for drag-and-drop functionality in Table with Header Fields
const SortableHeaderFieldCard = ({ 
  field, 
  index, 
  onFieldDelete, 
  children 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      sx={{ 
        mb: 2, 
        p: 2, 
        backgroundColor: '#f8f9ff', 
        border: '1px solid #e3e7ff',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: '#666',
            paddingTop: '4px',
            '&:hover': {
              color: '#333'
            }
          }}
        >
          <DragIcon />
        </Box>
        <Box flexGrow={1}>
          {children}
        </Box>
        <IconButton
          size="small"
          onClick={() => onFieldDelete(index)}
          sx={{ color: '#d32f2f' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
};

// Sortable Search Field Component for drag-and-drop functionality in Record Search
const SortableSearchFieldCard = ({ 
  field, 
  index, 
  onFieldDelete, 
  children 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      sx={{ 
        mb: 2, 
        p: 2, 
        backgroundColor: '#fafafa', 
        border: '1px solid #e0e0e0',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: '#666',
            paddingTop: '4px',
            '&:hover': {
              color: '#333'
            }
          }}
        >
          <DragIcon />
        </Box>
        <Box flexGrow={1}>
          {children}
        </Box>
        <IconButton
          size="small"
          onClick={() => onFieldDelete(index)}
          sx={{ color: '#d32f2f' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
};

// Sortable Result Column Component for drag-and-drop functionality in Record Search
const SortableResultColumnCard = ({ 
  column, 
  index, 
  isSelectable,
  onColumnDelete, 
  children 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      sx={{ 
        mb: 2, 
        p: 2, 
        backgroundColor: isSelectable ? '#e3f2fd' : '#fafafa', 
        border: isSelectable ? '1px solid #1976d2' : '1px solid #e0e0e0',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: '#666',
            paddingTop: '4px',
            '&:hover': {
              color: '#333'
            }
          }}
        >
          <DragIcon />
        </Box>
        <Box flexGrow={1}>
          {children}
        </Box>
        <IconButton
          size="small"
          onClick={() => onColumnDelete(index)}
          sx={{ color: '#d32f2f' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
};

// Enhanced Node Components for Data Extraction Platform
const SmartStartNode = ({ data, id, selected }) => (
  <Box sx={{ position: 'relative' }}>
    <Card sx={{ 
      minWidth: 160, 
      backgroundColor: '#10b981', 
      color: 'white',
      boxShadow: selected ? 8 : 3,
      border: selected ? '3px solid #fbbf24' : '2px solid #059669',
      cursor: 'pointer',
      '&:hover': {
        boxShadow: 6,
        transform: 'translateY(-2px)'
      }
    }}
    onClick={() => {
      if (data.onSelect) {
        data.onSelect(id, data.step);
      }
    }}>
      <CardContent sx={{ p: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
          <StartIcon />
          <UploadIcon fontSize="small" />
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          SMART START
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          File Upload & API Input
        </Typography>
      </CardContent>
    </Card>
    <Handle
      type="source"
      position={Position.Bottom}
      style={{ background: '#059669', border: '2px solid white' }}
    />
  </Box>
);

const EndNode = ({ data, id, selected }) => {
  const config = data.step?.config || {};
  const interfaceType = config.interfaceType || 'summary_card';
  const hasDisplayFields = config.displayFields?.length > 0;
  const hasTableColumns = config.tableColumns?.length > 0 || config.tabs?.some(t => t.tableColumns?.length > 0);
  const hasCustomTemplate = config.customTemplate?.trim().length > 0;
  const exportEnabled = config.enableExport !== false;
  
  // Determine if node is properly configured
  const isConfigured = hasDisplayFields || hasTableColumns || hasCustomTemplate;
  
  // Get interface type display name
  const interfaceTypeNames = {
    summary_card: 'Summary Card',
    data_table: 'Data Table',
    detailed_report: 'Detailed Report',
    dashboard: 'Dashboard',
    custom_template: 'Custom Template'
  };
  
  const interfaceDisplayName = interfaceTypeNames[interfaceType] || 'Summary Card';
  
  // Count configured fields
  const tabCount = config.enableMultiTab && config.tabs?.length > 1 ? config.tabs.length : 0;
  const fieldCount = hasDisplayFields ? config.displayFields.length : 
                     hasTableColumns ? (config.tableColumns?.length || config.tabs?.reduce((sum, t) => sum + (t.tableColumns?.length || 0), 0) || 0) : 0;
  
  return (
    <Box sx={{ position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: '#dc2626', border: '2px solid white' }}
      />
      <Card sx={{ 
        minWidth: 160, 
        backgroundColor: '#ef4444', 
        color: 'white',
        boxShadow: selected ? 8 : 3,
        border: selected ? '3px solid #fbbf24' : '2px solid #dc2626',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)'
        }
      }}
      onClick={() => {
        if (data.onSelect) {
          data.onSelect(id, data.step);
        }
      }}>
        <CardContent sx={{ p: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
            <EndIcon />
            {isConfigured && (
              <Tooltip title={`Configured: ${interfaceDisplayName}${fieldCount > 0 ? ` with ${fieldCount} fields` : ''}${exportEnabled ? ', Export enabled' : ''}`}>
                <PreviewIcon fontSize="small" sx={{ opacity: 0.8 }} />
              </Tooltip>
            )}
            {exportEnabled && (
              <Tooltip title="Export enabled">
                <DownloadIcon fontSize="small" sx={{ opacity: 0.8 }} />
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            END
          </Typography>
          {isConfigured && (
            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
              {interfaceDisplayName}
            </Typography>
          )}
          {isConfigured && fieldCount > 0 && (
            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
              {fieldCount} field{fieldCount !== 1 ? 's' : ''}{tabCount > 0 ? ` · ${tabCount} tabs` : ''}
            </Typography>
          )}
          {!isConfigured && (
            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
              Click to configure output
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const StepNode = ({ data, id, selected }) => {
  const getNodeStyle = (type) => {
    const styles = {
      data_processing: { 
        backgroundColor: '#3b82f6', 
        borderColor: '#2563eb', 
        icon: <ProcessingIcon />,
        title: 'DOCUMENT OCR & VISION',
        subtitle: 'Text Extraction'
      },
      ai_extraction: { 
        backgroundColor: '#8b5cf6', 
        borderColor: '#7c3aed', 
        icon: <AiIcon />,
        title: 'AI FIELD MAPPING',
        subtitle: 'Smart Data Extraction'
      },
      enhanced_api_call: { 
        backgroundColor: '#f59e0b', 
        borderColor: '#d97706', 
        icon: <ApiIcon />,
        title: 'API CALL',
        subtitle: 'Enhanced Integration'
      },
      advanced_collect_input: { 
        backgroundColor: '#06b6d4', 
        borderColor: '#0891b2', 
        icon: <InputIcon />,
        title: 'COLLECT INPUT',
        subtitle: 'Data Preview & Edit'
      },
      branch_logic: { 
        backgroundColor: '#f97316', 
        borderColor: '#ea580c', 
        icon: <BranchIcon />,
        title: 'BRANCH LOGIC',
        subtitle: 'Quality & Routing'
      },
      variable_operations: { 
        backgroundColor: '#10b981', 
        borderColor: '#059669', 
        icon: <EditIcon />,
        title: 'VARIABLE OPS',
        subtitle: 'Data Manipulation'
      },
      webhook: { 
        backgroundColor: '#dc2626', 
        borderColor: '#b91c1c', 
        icon: <ApiIcon />,
        title: 'WEBHOOK',
        subtitle: 'Async API Callback'
      },
      wait: { 
        backgroundColor: '#9333ea', 
        borderColor: '#7c3aed', 
        icon: <PauseIcon />,
        title: 'WAIT',
        subtitle: 'Delay Execution'
      },
      external_data_source: { 
        backgroundColor: '#7c3aed', 
        borderColor: '#6d28d9', 
        icon: <DataIcon />,
        title: 'EXTERNAL DATA SOURCE',
        subtitle: 'Google Sheets, Docs & More'
      },
      report_template: { 
        backgroundColor: '#f59e0b', 
        borderColor: '#d97706', 
        icon: <ReportIcon />,
        title: 'REPORT TEMPLATE',
        subtitle: 'Generate Formatted Reports'
      },
      for_each_loop: { 
        backgroundColor: '#9c27b0', 
        borderColor: '#7b1fa2', 
        icon: <LoopIcon />,
        title: 'FOR EACH LOOP',
        subtitle: 'Iterate Over Arrays'
      }
    };
    return styles[type] || { 
      backgroundColor: '#6b7280', 
      borderColor: '#4b5563', 
      icon: <ProcessingIcon />,
      title: 'UNKNOWN',
      subtitle: 'Node Type'
    };
  };

  const style = getNodeStyle(data.type);
  const isBranchLogic = data.type === 'branch_logic';
  const isDisabled = data.step?.enabled === false;
  const isInLoopBoundary = data.isInLoopBoundary || false;

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Loop boundary visual indicator */}
      {isInLoopBoundary && (
        <Box sx={{
          position: 'absolute',
          top: -8,
          left: -8,
          right: -8,
          bottom: -8,
          border: '3px dashed #9c27b0',
          borderRadius: 2,
          backgroundColor: 'rgba(156, 39, 176, 0.1)',
          pointerEvents: 'none',
          zIndex: -1
        }} />
      )}
      
      {/* Input handle - always on top for all nodes */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: style.borderColor, border: '2px solid white' }}
      />
      
      {/* Branch Logic specific left output handle */}
      {isBranchLogic && (
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={{ 
            background: style.borderColor, 
            border: '2px solid white',
            top: '50%'
          }}
        />
      )}
      
      {/* Branch Logic specific right output handle */}
      {isBranchLogic && (
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{ 
            background: style.borderColor, 
            border: '2px solid white',
            top: '50%'
          }}
        />
      )}
      
      <Card sx={{ 
        minWidth: 200,
        maxWidth: 280,
        backgroundColor: isDisabled ? '#9ca3af' : style.backgroundColor, 
        color: 'white',
        boxShadow: selected ? 8 : (isInLoopBoundary ? 6 : 3),
        border: selected 
          ? `3px solid #fbbf24` 
          : isInLoopBoundary
            ? `2px solid #9c27b0`
            : `2px solid ${isDisabled ? '#6b7280' : style.borderColor}`,
        cursor: 'pointer',
        opacity: isDisabled ? 0.6 : 1,
        position: 'relative',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)'
        }
      }}
      onClick={() => {
        if (data.onSelect) {
          data.onSelect(id, data.step);
        }
      }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {isDisabled && (
            <Box sx={{ 
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '50%',
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="caption" sx={{ fontSize: '12px', fontWeight: 'bold' }}>
                ⏭
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            {style.icon}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {style.title}
                {isDisabled && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.8 }}>
                    (DISABLED)
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {style.subtitle}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
            {data.step?.name || 'Unnamed Step'}
          </Typography>
          {data.step?.description && (
            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
              {data.step.description.length > 60 
                ? `${data.step.description.substring(0, 60)}...` 
                : data.step.description}
            </Typography>
          )}
          
          {/* Branch Logic specific labels */}
          {isBranchLogic && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '10px' }}>
                {data.step?.config?.failureLabel || 'FAIL'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '10px' }}>
                {data.step?.config?.successLabel || 'PASS'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Action buttons for selected nodes */}
      {selected && (
        <Box sx={{ 
          position: 'absolute', 
          top: -10, 
          right: -10, 
          display: 'flex',
          gap: 0.5
        }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (data.onCopy) {
                data.onCopy(id, data.step);
              }
            }}
            sx={{ 
              bgcolor: 'white', 
              color: 'primary.main',
              boxShadow: 2,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <CopyIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (data.onDelete) {
                data.onDelete(id);
              }
            }}
            sx={{ 
              bgcolor: 'white', 
              color: 'error.main',
              boxShadow: 2,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      
      {/* Standard output handle for non-branch logic nodes */}
      {!isBranchLogic && (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: style.borderColor, border: '2px solid white' }}
        />
      )}
    </Box>
  );
};

// Enhanced Node Configuration Panel
const NodeConfigurationPanel = ({ 
  step, 
  onStepChange, 
  availableApiEndpoints = [], 
  availableVariables = [],
  availableLibraries = [],
  librarySources = {},
  flowData = { steps: [] },
  onClose,
  projectId,
  companyId,
  openWizardFieldDialog,
  isMobile = false,
  configPanelOpen = true,
  setConfigPanelOpen = () => {}
}) => {
  // Drag and drop sensors - must be at the top level, before any conditional logic
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Modal state for expanded view
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Excel template libraries for Smart Document template selection
  const [excelTemplateLibraries, setExcelTemplateLibraries] = useState([]);
  const [excelTemplateLibsLoaded, setExcelTemplateLibsLoaded] = useState(false);

  useEffect(() => {
    if (!companyId || !projectId || excelTemplateLibsLoaded) return;
    if (step?.type !== 'data_processing') return;
    api.get(`/companies/${companyId}/projects/${projectId}/excel-template-libraries`)
      .then(res => setExcelTemplateLibraries(res.data.libraries || []))
      .catch(() => {})
      .finally(() => setExcelTemplateLibsLoaded(true));
  }, [companyId, projectId, excelTemplateLibsLoaded, step?.type]);

  // Active tab index for multi-tab Data Table config
  const [activeDataTableTab, setActiveDataTableTab] = useState(0);

  // Local state buffer for performance optimization
  const [localStep, setLocalStep] = useState(step);
  const localStepRef = useRef(localStep); // Ref to always access the latest localStep value
  const isDirtyRef = useRef(false); // Track if localStep has uncommitted changes
  const lastStepIdRef = useRef(step?.id);
  const autoCommitTimerRef = useRef(null);
  
  // Keep ref in sync with localStep state
  useEffect(() => {
    localStepRef.current = localStep;
  }, [localStep]);

  // Sync local state when step prop changes
  // This happens when: 1) user clicks on a different step, or 2) parent updates after save
  useEffect(() => {
    if (step) {
      // Always sync if step ID changed (different step selected)
      if (step.id !== lastStepIdRef.current) {
        console.log('🔄 Syncing to new step:', step.name);
        setLocalStep(step);
        isDirtyRef.current = false;
        lastStepIdRef.current = step.id;
        return;
      }
      
      // For same step, only sync if we don't have uncommitted changes
      if (!isDirtyRef.current) {
        // No uncommitted changes, safe to sync
        console.log('✅ Syncing clean step from parent:', step.name);
        setLocalStep(step);
      } else {
        console.log('🚫 Skipping sync - uncommitted changes for:', localStep?.name);
      }
    }
  }, [step, step?.id]);

  // Commit buffered changes to parent
  const commitChanges = useCallback(() => {
    // Clear any pending auto-commit
    if (autoCommitTimerRef.current) {
      clearTimeout(autoCommitTimerRef.current);
      autoCommitTimerRef.current = null;
    }
    
    // Use ref to get the latest localStep value, avoiding stale closure issues
    const currentStep = localStepRef.current;
    if (currentStep && onStepChange) {
      console.log('💾 Committing changes to parent:', currentStep.name);
      onStepChange(currentStep);
      // Mark as clean after committing
      isDirtyRef.current = false;
    }
  }, [onStepChange]);
  
  // Schedule auto-commit after user stops typing
  const scheduleAutoCommit = useCallback(() => {
    // Clear any existing timer
    if (autoCommitTimerRef.current) {
      clearTimeout(autoCommitTimerRef.current);
    }
    
    // Schedule commit after 800ms of inactivity
    autoCommitTimerRef.current = setTimeout(() => {
      console.log('⏰ Auto-committing changes after typing stopped');
      commitChanges();
    }, 800);
  }, [commitChanges]);

  if (!step || !localStep) {
    // On mobile, don't show anything if no step is selected
    if (isMobile) {
      return null;
    }
    
    return (
      <Paper sx={{ width: 400, height: '100%', p: 3, borderLeft: '1px solid #e0e0e0' }}>
        <Typography variant="h6" color="textSecondary" textAlign="center" mt={4}>
          Select a node to configure
        </Typography>
      </Paper>
    );
  }

  const handleFieldChange = (field, value) => {
    setLocalStep((prevStep) => ({ ...prevStep, [field]: value }));
    isDirtyRef.current = true; // Mark as dirty
    scheduleAutoCommit(); // Auto-commit after user stops typing
  };

  // Immediate commit for toggle switches and other instant changes
  const handleFieldChangeAndCommit = (field, value) => {
    setLocalStep((prevStep) => {
      const updatedStep = { ...prevStep, [field]: value };
      console.log(`🔄 Immediately committing ${field} change:`, value);
      // Immediately commit the change with the updated value
      if (onStepChange) {
        onStepChange(updatedStep);
      }
      isDirtyRef.current = false; // Mark as clean since we just committed
      return updatedStep;
    });
  };

  const handleConfigChange = (configPath, value) => {
    setLocalStep((prevStep) => {
      const updatedStep = { ...prevStep };
      
      // Handle nested config paths (e.g., 'field.subfield')
      if (configPath.includes('.')) {
        const parts = configPath.split('.');
        let current = updatedStep.config = updatedStep.config || {};
        
        for (let i = 0; i < parts.length - 1; i++) {
          current[parts[i]] = current[parts[i]] || {};
          current = current[parts[i]];
        }
        
        current[parts[parts.length - 1]] = value;
      } else {
        if (!updatedStep.config) updatedStep.config = {};
        updatedStep.config[configPath] = value;
      }
      
      return updatedStep;
    });
    isDirtyRef.current = true; // Mark as dirty
    scheduleAutoCommit(); // Auto-commit after user stops typing
  };

  // Function to clean up legacy fields based on step type
  const cleanupLegacyFields = (stepType, config) => {
    if (stepType === 'data_processing') {
      // Define the current valid fields for Document OCR & Vision steps
      const validDataProcessingFields = [
        'ocrEngine',
        'textractModel',
        'enableFallback', 
        'confidenceThreshold',
        'processingMode',
        'enableAiVision',
        'visionMethod',
        'visionModel',
        'visionEnhancedModel',
        'visionQuickModel',
        'visionResponsesModel',
        'visionGeminiModel',
        'visionQwenModel',
        'visionDetail',
        'visionTimeout',
        'visionPrompt',
        'outputVariable',
        'includeConfidenceScores',
        'confidenceFieldName',
        // NEW: Image rotation detection fields
        'rotationDetectionMethod',
        'manualRotation',
        // NEW: File source selection
        'fileSource',
        // Excel/CSV template fields
        'excelCsvOutputFormat',
        'useExcelTemplates',
        'excelTemplateLibraryId'
      ];
      
      // Create a new config object with only valid fields
      const cleanedConfig = {};
      validDataProcessingFields.forEach(field => {
        if (config[field] !== undefined) {
          cleanedConfig[field] = config[field];
        }
      });
      
      console.log('🧹 [FLOW DEBUG] Cleaned up Document OCR & Vision config:', {
        originalFields: Object.keys(config),
        cleanedFields: Object.keys(cleanedConfig),
        removedFields: Object.keys(config).filter(key => !validDataProcessingFields.includes(key))
      });
      
      return cleanedConfig;
    }
    
    // For other step types, return config as-is
    return config;
  };

  // Handle drag end for table columns
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const isMultiTab = localStep.config?.enableMultiTab && localStep.config?.tabs?.length > 0;
      const currentColumns = isMultiTab
        ? (localStep.config?.tabs?.[activeDataTableTab]?.tableColumns || [])
        : (localStep.config?.tableColumns || []);
      const oldIndex = currentColumns.findIndex(column => column.id === active.id);
      const newIndex = currentColumns.findIndex(column => column.id === over.id);
      
      const newColumns = arrayMove(currentColumns, oldIndex, newIndex);
      if (isMultiTab) {
        const tabs = [...(localStep.config?.tabs || [])];
        tabs[activeDataTableTab] = { ...tabs[activeDataTableTab], tableColumns: newColumns };
        handleConfigChange('tabs', tabs);
      } else {
        handleConfigChange('tableColumns', newColumns);
      }
    }
  };

  // Handle drag end for variable operations
  const handleOperationsDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const currentOperations = localStep.config?.operations || [];
      const oldIndex = currentOperations.findIndex(op => op.id === active.id);
      const newIndex = currentOperations.findIndex(op => op.id === over.id);
      
      const newOperations = arrayMove(currentOperations, oldIndex, newIndex);
      handleConfigChange('operations', newOperations);
    }
  };

  // Handle drag end for transform array field mappings
  const handleTransformFieldMappingsDragEnd = (operationIndex) => (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const updatedOperations = [...localStep.config.operations];
      const operation = updatedOperations[operationIndex];
      const currentMappings = operation.transformArrayConfig?.fieldMappings || [];
      const oldIndex = currentMappings.findIndex(mapping => mapping.id === active.id);
      const newIndex = currentMappings.findIndex(mapping => mapping.id === over.id);
      
      const newMappings = arrayMove(currentMappings, oldIndex, newIndex);
      updatedOperations[operationIndex] = {
        ...operation,
        transformArrayConfig: {
          ...operation.transformArrayConfig,
          fieldMappings: newMappings
        }
      };
      handleConfigChange('operations', updatedOperations);
    }
  };

  // Handle drag end for comparison fields
  const handleComparisonFieldsDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const currentFields = localStep.config?.comparisonFields || [];
      const oldIndex = currentFields.findIndex(field => field.id === active.id);
      const newIndex = currentFields.findIndex(field => field.id === over.id);
      
      const newFields = arrayMove(currentFields, oldIndex, newIndex);
      handleConfigChange('comparisonFields', newFields);
    }
  };

  // Handle drag end for form fields (Simple Form)
  const handleFormFieldsDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const currentFields = localStep.config?.formFields || [];
      const oldIndex = currentFields.findIndex(field => field.id === active.id);
      const newIndex = currentFields.findIndex(field => field.id === over.id);
      
      const newFields = arrayMove(currentFields, oldIndex, newIndex);
      handleConfigChange('formFields', newFields);
    }
  };

  // Handle drag end for header fields (Table with Header Fields)
  const handleHeaderFieldsDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const currentFields = localStep.config?.headerFields || [];
      const oldIndex = currentFields.findIndex(field => field.id === active.id);
      const newIndex = currentFields.findIndex(field => field.id === over.id);
      
      const newFields = arrayMove(currentFields, oldIndex, newIndex);
      handleConfigChange('headerFields', newFields);
    }
  };

  // Handle drag end for record search search fields
  const handleRecordSearchFieldsDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const currentConfig = localStep.config?.recordSearchConfig || {};
      const currentFields = currentConfig.searchFields || [];
      const oldIndex = currentFields.findIndex(field => field.id === active.id);
      const newIndex = currentFields.findIndex(field => field.id === over.id);
      
      const newFields = arrayMove(currentFields, oldIndex, newIndex);
      handleConfigChange('recordSearchConfig', {
        ...currentConfig,
        searchFields: newFields
      });
    }
  };

  // Handle drag end for record search result columns
  const handleRecordSearchColumnsDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const currentConfig = localStep.config?.recordSearchConfig || {};
      const currentColumns = currentConfig.resultColumns || [];
      const oldIndex = currentColumns.findIndex(col => col.id === active.id);
      const newIndex = currentColumns.findIndex(col => col.id === over.id);
      
      const newColumns = arrayMove(currentColumns, oldIndex, newIndex);
      handleConfigChange('recordSearchConfig', {
        ...currentConfig,
        resultColumns: newColumns
      });
    }
  };

  const renderSmartStartConfig = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Configure how data enters your workflow through file uploads.
      </Typography>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📁 File Upload Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Accepted File Types</InputLabel>
              <Select
                multiple
                value={localStep.config?.fileTypes || []}
                onChange={(e) => handleConfigChange('fileTypes', e.target.value)}
                onBlur={commitChanges}
              >
                <MenuItem value="pdf">PDF Documents</MenuItem>
                <MenuItem value="image">Images (PNG, JPG, TIFF)</MenuItem>
                <MenuItem value="excel">Excel Spreadsheets</MenuItem>
                <MenuItem value="csv">CSV Files</MenuItem>
                <MenuItem value="word">Word Documents</MenuItem>
              </Select>
              <FormHelperText>
                {localStep.config?.fileTypes?.length > 0 
                  ? `✅ File upload will be required on client side`
                  : `⚠️ No file types selected - workflow will start without file upload`
                }
              </FormHelperText>
            </FormControl>
            
            {localStep.config?.fileTypes?.length > 0 && (
              <>
                <TextField
                  label="Max File Size (MB)"
                  type="number"
                  value={localStep.config?.maxFileSize || 10}
                  onChange={(e) => handleConfigChange('maxFileSize', parseInt(e.target.value))}
                  onBlur={commitChanges}
                  fullWidth
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={localStep.config?.autoDetectFileType || true}
                      onChange={(e) => handleConfigChange('autoDetectFileType', e.target.checked)}
                      onBlur={commitChanges}
                    />
                  }
                  label="Auto-detect file type"
                />
              </>
            )}
            
            {/* Excel Multi-Sheet Configuration */}
            {localStep.config?.fileTypes?.includes('excel') && (
              <Box sx={{ p: 2, backgroundColor: '#e8f4f8', borderRadius: 1, border: '1px solid #b3d9e6', mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#0277bd', mb: 2 }}>
                  📊 Excel Multi-Sheet Configuration
                </Typography>
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Excel Parse Mode</InputLabel>
                  <Select
                    value={localStep.config?.excelParseMode || 'auto-merge'}
                    onChange={(e) => handleConfigChange('excelParseMode', e.target.value)}
                    onBlur={commitChanges}
                  >
                    <MenuItem value="auto-merge">Auto-Merge All Sheets</MenuItem>
                    <MenuItem value="individual-sheets">Keep Individual Sheets</MenuItem>
                    <MenuItem value="selected-sheets">Select Specific Sheets</MenuItem>
                  </Select>
                  <FormHelperText>
                    {localStep.config?.excelParseMode === 'auto-merge' && 'Automatically combine data from all sheets into a single array'}
                    {localStep.config?.excelParseMode === 'individual-sheets' && 'Keep each sheet as a separate data structure with metadata'}
                    {localStep.config?.excelParseMode === 'selected-sheets' && 'Specify which sheets to process (comma-separated list)'}
                  </FormHelperText>
                </FormControl>
                
                {localStep.config?.excelParseMode === 'auto-merge' && (
                  <FormControl fullWidth>
                    <InputLabel>Merge Strategy</InputLabel>
                    <Select
                      value={localStep.config?.mergeStrategy || 'append'}
                      onChange={(e) => handleConfigChange('mergeStrategy', e.target.value)}
                      onBlur={commitChanges}
                    >
                      <MenuItem value="append">Append All Sheets</MenuItem>
                      <MenuItem value="first-only">First Sheet Only</MenuItem>
                    </Select>
                    <FormHelperText>
                      How to combine multiple sheets when merging
                    </FormHelperText>
                  </FormControl>
                )}
                
                {localStep.config?.excelParseMode === 'selected-sheets' && (
                  <Box>
                    {/* Toggle between Simple and Advanced mode */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={localStep.config?.advancedSheetConfig || false}
                          onChange={(e) => {
                            handleConfigChange('advancedSheetConfig', e.target.checked);
                            // Initialize sheetConfigs if enabling advanced mode
                            if (e.target.checked && (!localStep.config?.sheetConfigs || localStep.config.sheetConfigs.length === 0)) {
                              const existingSheets = localStep.config?.selectedSheets || [];
                              const initialConfigs = existingSheets.length > 0 
                                ? existingSheets.map(name => ({
                                    id: `sheet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                    sheetName: name,
                                    startRow: 1,
                                    endRow: '',
                                    columns: ''
                                  }))
                                : [{
                                    id: `sheet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                    sheetName: '',
                                    startRow: 1,
                                    endRow: '',
                                    columns: ''
                                  }];
                              handleConfigChange('sheetConfigs', initialConfigs);
                            }
                            commitChanges();
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Advanced Sheet Configuration
                        </Typography>
                      }
                      sx={{ mb: 1 }}
                    />
                    
                    {!localStep.config?.advancedSheetConfig ? (
                      /* Simple mode - just sheet names */
                      <TextField
                        label="Sheet Names"
                        value={localStep.config?.selectedSheetsRaw ?? localStep.config?.selectedSheets?.join(', ') ?? ''}
                        onChange={(e) => {
                          handleConfigChange('selectedSheetsRaw', e.target.value);
                        }}
                        onBlur={(e) => {
                          const sheets = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                          handleConfigChange('selectedSheets', sheets);
                          handleConfigChange('selectedSheetsRaw', undefined);
                          commitChanges();
                        }}
                        fullWidth
                        placeholder="Sheet1, Sheet2, My Data Sheet"
                        helperText="Enter sheet names separated by commas"
                      />
                    ) : (
                      /* Advanced mode - per-sheet configuration */
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          Configure extraction settings for each sheet. Leave row/column fields empty to extract all.
                        </Typography>
                        
                        {(localStep.config?.sheetConfigs || []).map((sheetConfig, index) => (
                          <Paper 
                            key={sheetConfig.id} 
                            variant="outlined" 
                            sx={{ 
                              p: 2, 
                              mb: 1.5, 
                              backgroundColor: '#fafafa',
                              border: '1px solid #e0e0e0',
                              borderRadius: 1
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                Sheet {index + 1}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  const updatedConfigs = localStep.config.sheetConfigs.filter((_, i) => i !== index);
                                  handleConfigChange('sheetConfigs', updatedConfigs);
                                  // Also update selectedSheets array
                                  handleConfigChange('selectedSheets', updatedConfigs.map(c => c.sheetName).filter(n => n));
                                  commitChanges();
                                }}
                                sx={{ color: '#d32f2f' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            
                            <TextField
                              label="Sheet Name"
                              value={sheetConfig.sheetName || ''}
                              onChange={(e) => {
                                const updatedConfigs = [...localStep.config.sheetConfigs];
                                updatedConfigs[index] = { ...updatedConfigs[index], sheetName: e.target.value };
                                handleConfigChange('sheetConfigs', updatedConfigs);
                              }}
                              onBlur={() => {
                                // Update selectedSheets array for compatibility
                                const sheetNames = localStep.config.sheetConfigs.map(c => c.sheetName).filter(n => n);
                                handleConfigChange('selectedSheets', sheetNames);
                                commitChanges();
                              }}
                              fullWidth
                              size="small"
                              placeholder="e.g., Sales Data, Sheet1"
                              sx={{ mb: 1.5 }}
                            />
                            
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                              <TextField
                                label="Start Row"
                                type="number"
                                value={sheetConfig.startRow || 1}
                                onChange={(e) => {
                                  const updatedConfigs = [...localStep.config.sheetConfigs];
                                  updatedConfigs[index] = { ...updatedConfigs[index], startRow: parseInt(e.target.value) || 1 };
                                  handleConfigChange('sheetConfigs', updatedConfigs);
                                }}
                                onBlur={commitChanges}
                                size="small"
                                inputProps={{ min: 1 }}
                                sx={{ flex: 1 }}
                                helperText="First row to extract"
                              />
                              <TextField
                                label="End Row"
                                type="number"
                                value={sheetConfig.endRow || ''}
                                onChange={(e) => {
                                  const updatedConfigs = [...localStep.config.sheetConfigs];
                                  updatedConfigs[index] = { ...updatedConfigs[index], endRow: e.target.value ? parseInt(e.target.value) : '' };
                                  handleConfigChange('sheetConfigs', updatedConfigs);
                                }}
                                onBlur={commitChanges}
                                size="small"
                                inputProps={{ min: 1 }}
                                sx={{ flex: 1 }}
                                placeholder="All"
                                helperText="Last row (empty = all)"
                              />
                            </Box>
                            
                            <TextField
                              label="Columns"
                              value={sheetConfig.columns || ''}
                              onChange={(e) => {
                                const updatedConfigs = [...localStep.config.sheetConfigs];
                                updatedConfigs[index] = { ...updatedConfigs[index], columns: e.target.value };
                                handleConfigChange('sheetConfigs', updatedConfigs);
                              }}
                              onBlur={commitChanges}
                              fullWidth
                              size="small"
                              placeholder="e.g., A:F or A,C,E,G or empty for all"
                              helperText="Column range (A:Z), specific columns (A,C,E), or empty for all columns"
                            />
                          </Paper>
                        ))}
                        
                        <Button
                          startIcon={<AddIcon />}
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            const currentConfigs = localStep.config?.sheetConfigs || [];
                            const newConfig = {
                              id: `sheet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                              sheetName: '',
                              startRow: 1,
                              endRow: '',
                              columns: ''
                            };
                            handleConfigChange('sheetConfigs', [...currentConfigs, newConfig]);
                            commitChanges();
                          }}
                          sx={{ mt: 0.5 }}
                        >
                          Add Sheet
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
            
            {/* Visual Feedback Box */}
            {localStep.config?.fileTypes?.length === 0 ? (
              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc02' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57c00', mb: 1 }}>
                  🚀 No File Upload Required
                </Typography>
                <Typography variant="body2" color="#ef6c00" sx={{ fontSize: '0.875rem' }}>
                  When no file types are selected, the client will show a "Start Workflow Execution" button without file upload section. 
                  Use this for workflows that start with data from previous steps, webhooks, or manual input.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                  📤 File Upload Enabled
                </Typography>
                <Typography variant="body2" color="#2e7d32" sx={{ fontSize: '0.875rem' }}>
                  Users will be required to upload files before starting the workflow. 
                  Supported formats: {localStep.config?.fileTypes?.join(', ')}
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Batch Execution Mode Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📦 Batch Execution Mode</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={localStep.config?.enableBatchUpload || false}
                  onChange={(e) => {
                    handleConfigChange('enableBatchUpload', e.target.checked);
                    commitChanges();
                  }}
                />
              }
              label="Enable Batch Upload Execution"
            />
            <FormHelperText sx={{ mt: -1, ml: 0 }}>
              Allow clients to upload files for multiple executions at once. Each execution group will run as a separate workflow execution.
            </FormHelperText>
            
            {localStep.config?.enableBatchUpload && (
              <Box sx={{ mt: 1 }}>
                <TextField
                  label="Max Concurrent Executions Per Batch"
                  type="number"
                  inputProps={{ min: 1, max: 3 }}
                  value={localStep.config?.maxConcurrentBatchExecutions || 1}
                  onChange={(e) => {
                    const value = Math.min(3, Math.max(1, parseInt(e.target.value) || 1));
                    handleConfigChange('maxConcurrentBatchExecutions', value);
                  }}
                  onBlur={commitChanges}
                  fullWidth
                  helperText="How many executions from a batch can run simultaneously (1-3). Remaining executions will be queued."
                />
                
                <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9', mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1 }}>
                    ℹ️ How Batch Execution Works
                  </Typography>
                  <Typography variant="body2" color="#1976d2" sx={{ fontSize: '0.875rem' }}>
                    • Clients can create multiple execution groups, each with its own files<br/>
                    • Each group becomes a separate execution with its own execution ID<br/>
                    • Executions are queued and run based on the concurrent limit above<br/>
                    • Notifications are sent when executions need input or encounter errors<br/>
                    • Batch names follow the format: "DD MMMM, YYYY - XXX" (e.g., "12 January, 2026 - 001")
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderDataProcessingConfig = () => {
    // Get available file sources from previous steps
    const getAvailableFileSources = () => {
      const sources = [
        { value: '', label: '📁 All Files (Default)', description: 'Process files from all sources' }
      ];
      
      // Check if Smart Start has file uploads enabled
      if (flowData?.steps) {
        const smartStartStep = flowData.steps.find(s => s.type === 'smart_start' || s.type === 'start');
        if (smartStartStep?.config?.enableFileUpload !== false) {
          sources.push({ 
            value: '__smart_start__', 
            label: '🚀 Smart Start Files', 
            description: 'Files uploaded in Smart Start step only' 
          });
        }
        
        // Find ALL file upload collect input steps in the flow
        // Note: We show all file upload steps regardless of array position because
        // the array order reflects creation order, not flow execution order
        console.log('🔍 [File Source Debug] Current step:', step.name, 'ID:', step.id);
        console.log('🔍 [File Source Debug] All steps:', flowData.steps.map(s => ({ name: s.name, type: s.type, id: s.id })));
        
        flowData.steps.forEach((s) => {
          // Skip the current step itself
          if (s.id === step.id) return;
          
          // Check interfaceType in multiple possible locations (config structure can vary)
          const interfaceType = s.config?.interfaceType || 
                               s.config?.collectInputConfig?.interfaceType || 
                               s.collectInputConfig?.interfaceType;
          
          // Check outputVariable in multiple possible locations
          const outputVar = s.config?.outputVariable || 
                           s.config?.collectInputConfig?.outputVariable || 
                           s.collectInputConfig?.outputVariable || 
                           'uploadedFiles';
          
          console.log('🔍 [File Source Debug] Checking step:', s.name, 'Type:', s.type, 'InterfaceType:', interfaceType);
          
          if ((s.type === 'advanced_collect_input' || s.type === 'collect_input') && 
              interfaceType === 'file_upload') {
            console.log('✅ [File Source Debug] Found file upload step:', s.name, 'Variable:', outputVar);
            sources.push({
              value: outputVar,
              label: `📄 ${s.name || 'File Upload'} (${outputVar})`,
              description: `Files from "${s.name || 'Collect Input'}" step`
            });
          }
        });
      }
      
      console.log('🔍 [File Source Debug] Available sources:', sources);
      return sources;
    };
    
    const fileSources = getAvailableFileSources();
    
    return (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Configure OCR engines and data parsing options for optimal extraction.
      </Typography>
      
      {/* File Source Selection - NEW */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📁 File Source Selection</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Choose which files to process. By default, all files from Smart Start and Collect Input file uploads are processed together.
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>File Source</InputLabel>
              <Select
                value={localStep.config?.fileSource || ''}
                onChange={(e) => handleConfigChange('fileSource', e.target.value)}
                onBlur={commitChanges}
              >
                {fileSources.map((source) => (
                  <MenuItem key={source.value} value={source.value}>
                    {source.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {localStep.config?.fileSource === '__smart_start__' 
                  ? 'Only files uploaded in the Smart Start step will be processed'
                  : localStep.config?.fileSource 
                    ? `Only files from the "${localStep.config.fileSource}" variable will be processed`
                    : 'All available files from Smart Start and Collect Input steps will be processed'
                }
              </FormHelperText>
            </FormControl>
            
            {fileSources.length <= 1 && (
              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                <Typography variant="body2" sx={{ color: '#e65100' }}>
                  💡 <strong>Tip:</strong> Add a Smart Start step with file uploads or a Collect Input step with "File Upload" interface type before this step to enable file source selection.
                </Typography>
              </Box>
            )}
            
            {localStep.config?.fileSource && (
              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                <Typography variant="body2" sx={{ color: '#1565c0' }}>
                  ✅ <strong>Selected Source:</strong> {fileSources.find(s => s.value === localStep.config?.fileSource)?.description || 'Custom variable'}
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">OCR Engine Selection</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Primary OCR Engine</InputLabel>
              <Select
                value={localStep.config?.ocrEngine || 'tesseract'}
                onChange={(e) => handleConfigChange('ocrEngine', e.target.value)}
                onBlur={commitChanges}
              >
                <MenuItem value="tesseract">Tesseract (Free)</MenuItem>
                <MenuItem value="guten_ocr">Guten OCR (Free)</MenuItem>
                <MenuItem value="google_ai_vision">Google AI Vision (API Key)</MenuItem>
                <MenuItem value="aws_textract">AWS Textract (API Key)</MenuItem>
              </Select>
            </FormControl>
            
            {localStep.config?.ocrEngine === 'aws_textract' && (
              <FormControl fullWidth>
                <InputLabel>AWS Textract Model</InputLabel>
                <Select
                  value={localStep.config?.textractModel || 'detect_text'}
                  onChange={(e) => handleConfigChange('textractModel', e.target.value)}
                  onBlur={commitChanges}
                >
                  <MenuItem value="detect_text">Basic Text Detection</MenuItem>
                  <MenuItem value="analyze_document_full">Document Analysis (Full)</MenuItem>
                  <MenuItem value="analyze_document_forms">Document Analysis (Forms)</MenuItem>
                  <MenuItem value="analyze_document_tables">Document Analysis (Tables)</MenuItem>
                  <MenuItem value="analyze_expense">Expense Analysis (Invoices/Receipts)</MenuItem>
                  <MenuItem value="analyze_id">Identity Document Analysis</MenuItem>
                </Select>
                <FormHelperText>
                  Choose the best model for your document type. Expense Analysis is excellent for invoices and receipts.
                </FormHelperText>
              </FormControl>
            )}

            {localStep.config?.ocrEngine === 'google_ai_vision' && (
              <FormControl fullWidth>
                <InputLabel>Google AI Vision Model</InputLabel>
                <Select
                  value={localStep.config?.visionGeminiModel || 'gemini-1.5-flash'}
                  onChange={(e) => handleConfigChange('visionGeminiModel', e.target.value)}
                  onBlur={commitChanges}
                >
                  <MenuItem value="gemini-1.5-flash">Gemini 1.5 Flash</MenuItem>
                  <MenuItem value="gemini-1.5-pro">Gemini 1.5 Pro</MenuItem>
                  <MenuItem value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite</MenuItem>
                  <MenuItem value="gemini-2.0-flash">Gemini 2.0 Flash</MenuItem>
                  <MenuItem value="gemini-2.0">Gemini 2.0</MenuItem>
                  <MenuItem value="gemini-2.0-pro">Gemini 2.0 Pro</MenuItem>
                  <MenuItem value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite</MenuItem>
                  <MenuItem value="gemini-2.5-flash">Gemini 2.5 Flash</MenuItem>
                  <MenuItem value="gemini-2.5">Gemini 2.5</MenuItem>
                  <MenuItem value="gemini-2.5-pro">Gemini 2.5 Pro</MenuItem>
                </Select>
                <FormHelperText>
                  Select the Gemini model for Google AI Vision OCR processing
                </FormHelperText>
              </FormControl>
            )}
            
            <FormControlLabel
              control={
                <Switch
                  checked={localStep.config?.enableFallback ?? true}
                  onChange={(e) => handleConfigChange('enableFallback', e.target.checked)}
                  onBlur={commitChanges}
                />
              }
              label="Enable fallback OCR engine"
            />
            
            <Typography variant="caption" color="textSecondary">
              OCR Confidence Threshold
            </Typography>
            <Slider
              value={localStep.config?.confidenceThreshold || 0.7}
              onChange={(e, value) => handleConfigChange('confidenceThreshold', value)}
              onBlur={commitChanges}
              min={0.1}
              max={1.0}
              step={0.1}
              marks
              valueLabelDisplay="auto"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🤖 AI Vision Processing</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Processing Mode Selection */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Processing Mode
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Mode</InputLabel>
              <Select
                value={localStep.config?.processingMode || 'text_extraction'}
                onChange={(e) => handleConfigChange('processingMode', e.target.value)}
                onBlur={commitChanges}
                >
                  <MenuItem value="text_extraction">📝 Text Extraction (OCR + AI Vision)</MenuItem>
                  <MenuItem value="image_analysis">🔍 Image Analysis (AI Vision Only)</MenuItem>
                </Select>
                <FormHelperText>
                  {localStep.config?.processingMode === 'image_analysis' 
                    ? 'Image Analysis mode uses only AI Vision for tasks like counting items, describing scenes, object detection. No OCR fallback.'
                    : 'Text extraction for documents with AI Vision enhancement and OCR fallback for reliability.'}
                </FormHelperText>
              </FormControl>
            </Box>

            {/* Alert for Image Analysis Mode */}
            {localStep.config?.processingMode === 'image_analysis' && (
              <Alert severity="info" sx={{ mb: 1 }}>
                <strong>Image Analysis Mode:</strong> This mode is optimized for analyzing images (counting objects, describing scenes, identifying items). 
                AI Vision must be enabled and there is no OCR fallback. Perfect for non-text tasks.
              </Alert>
            )}
            
            <FormControlLabel
              control={
                <Switch
                  checked={localStep.config?.enableAiVision || false}
                  onChange={(e) => handleConfigChange('enableAiVision', e.target.checked)}
                  onBlur={commitChanges}
                />
              }
              label="Enable AI Vision Analysis"
            />
            
            {localStep.config?.enableAiVision && (
              <>
                {/* Vision Method Selection */}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Vision Processing Method
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Processing Method</InputLabel>
                    <Select
                      value={localStep.config?.visionMethod || 'responses'}
                      onChange={(e) => handleConfigChange('visionMethod', e.target.value)}
                      onBlur={commitChanges}
                    >
                      <MenuItem value="responses">
                        Responses API (Recommended) - Latest & Best
                      </MenuItem>
                      <MenuItem value="gemini">
                        Google Gemini 1.5/2.5 Flash - Fast & Accurate
                      </MenuItem>
                      <MenuItem value="enhanced">
                        Enhanced Vision (Legacy) - High Accuracy
                      </MenuItem>
                      <MenuItem value="quick">
                        Quick Vision (Legacy) - Speed Optimized
                      </MenuItem>
                                              <MenuItem value="auto">
                          Auto-Select (Try Responses → Gemini → Enhanced → Quick → OCR)
                        </MenuItem>
                        <MenuItem value="qwen">QWEN Vision (Alibaba Cloud - Fast & Accurate)</MenuItem>
                    </Select>
                    <FormHelperText>
                      {localStep.config?.visionMethod === 'responses' && 'Latest OpenAI vision technology with enhanced capabilities (50MB images, 500 image batches, better accuracy)'}
                      {localStep.config?.visionMethod === 'gemini' && 'Google Vertex AI Gemini 1.5/2.5 Flash - excellent for invoices & purchase orders with structured JSON extraction'}
                      {localStep.config?.visionMethod === 'enhanced' && 'Previous generation high accuracy vision processing (5MB limit)'}
                                              {localStep.config?.visionMethod === 'quick' && 'Previous generation speed-optimized processing'}
                        {(localStep.config?.visionMethod === 'auto' || (!localStep.config?.visionMethod && localStep.config?.visionMethod !== 'responses')) && 'Automatically tries methods starting with latest Responses API, then Gemini, then fallbacks'}
                        {localStep.config?.visionMethod === 'qwen' && 'Alibaba Cloud QWEN VL Plus - high performance vision processing for OCR and document analysis'}
                    </FormHelperText>
                  </FormControl>
                </Box>

                {/* Model Selection for Enhanced Vision (Legacy) */}
                {(localStep.config?.visionMethod === 'enhanced' || localStep.config?.visionMethod === 'auto') && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Enhanced Vision Model (Legacy)
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Legacy Model</InputLabel>
                      <Select
                        value={localStep.config?.visionEnhancedModel || 'gpt-4o'}
                        onChange={(e) => handleConfigChange('visionEnhancedModel', e.target.value)}
                        onBlur={commitChanges}
                      >
                        <MenuItem value="gpt-4o">GPT-4o (Maximum Accuracy)</MenuItem>
                        <MenuItem value="gpt-4o-mini">GPT-4o Mini (Balanced)</MenuItem>
                      </Select>
                      <FormHelperText>
                        Previous generation vision models with 5MB image limit (fallback when Responses API fails)
                      </FormHelperText>
                    </FormControl>
                  </Box>
                )}

                {/* Model Selection for Quick Vision (Legacy) */}
                {(localStep.config?.visionMethod === 'quick' || localStep.config?.visionMethod === 'auto') && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Quick Vision Model (Legacy)
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Legacy Speed Model</InputLabel>
                      <Select
                        value={localStep.config?.visionQuickModel || 'gpt-4o-mini'}
                        onChange={(e) => handleConfigChange('visionQuickModel', e.target.value)}
                        onBlur={commitChanges}
                        disabled={localStep.config?.visionMethod === 'enhanced' || localStep.config?.visionMethod === 'responses'}
                      >
                        <MenuItem value="gpt-4o-mini">GPT-4o Mini (Recommended)</MenuItem>
                        <MenuItem value="gpt-4o">GPT-4o (Higher Quality, Slower)</MenuItem>
                      </Select>
                      <FormHelperText>
                        {(localStep.config?.visionMethod === 'enhanced' || localStep.config?.visionMethod === 'responses') ? 'Not used in this mode' : 'Previous generation speed-optimized models'}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                )}

                {/* Responses API Model Selection */}
                {(localStep.config?.visionMethod === 'responses' || localStep.config?.visionMethod === 'auto' || !localStep.config?.visionMethod) && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Responses API Model (Recommended)
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Latest Model</InputLabel>
                      <Select
                        value={localStep.config?.visionResponsesModel || 'gpt-4.1-mini'}
                        onChange={(e) => handleConfigChange('visionResponsesModel', e.target.value)}
                        onBlur={commitChanges}
                      >
                        <MenuItem value="gpt-5.1">GPT-5.1 (Maximum Accuracy)</MenuItem>
                        <MenuItem value="gpt-4.1-mini">GPT-4.1 Mini (Balanced) - Recommended</MenuItem>
                        <MenuItem value="gpt-4.1">GPT-4.1 (High Accuracy)</MenuItem>
                        <MenuItem value="gpt-4.1-nano">GPT-4.1 Nano (Ultra Fast)</MenuItem>
                      </Select>
                      <FormHelperText>
                        Latest generation models with enhanced vision capabilities (up to 50MB images, 500 images per request)
                      </FormHelperText>
                    </FormControl>
                  </Box>
                )}

                {/* Gemini Model Selection */}
                {(step.config?.visionMethod === 'gemini' || step.config?.visionMethod === 'auto') && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Google Gemini Model
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Gemini Model</InputLabel>
                      <Select
                        value={step.config?.visionGeminiModel || 'gemini-1.5-flash'}
                        onChange={(e) => handleConfigChange('visionGeminiModel', e.target.value)}
                      >
                        <MenuItem value="gemini-1.5-flash">Gemini 1.5 Flash (Balanced) - Recommended</MenuItem>
                        <MenuItem value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite (Ultra Fast)</MenuItem>
                        <MenuItem value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite (Very Fast)</MenuItem>
                        <MenuItem value="gemini-1.5-pro">Gemini 1.5 Pro (Quality)</MenuItem>
                        <MenuItem value="gemini-2.0-flash">Gemini 2.0 Flash (Balanced)</MenuItem>
                        <MenuItem value="gemini-2.5-flash">Gemini 2.5 Flash (Balanced)</MenuItem>
                      </Select>
                      <FormHelperText>
                        Google Vertex AI models optimized for document analysis and structured data extraction
                      </FormHelperText>
                    </FormControl>
                  </Box>
                )}

                {/* QWEN Model Selection */}
                {step.config?.visionMethod === 'qwen' && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      QWEN Vision Model
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>QWEN Model</InputLabel>
                      <Select
                        value={step.config?.visionQwenModel || 'qwen-vl-plus'}
                        onChange={(e) => handleConfigChange('visionQwenModel', e.target.value)}
                      >
                        <MenuItem value="qwen-vl-plus">QWEN VL Plus (Balanced) - Recommended</MenuItem>
                        <MenuItem value="qwen-vl-max">QWEN VL Max (Premium Quality)</MenuItem>
                        <MenuItem value="qwen-vl-chat">QWEN VL Chat (Interactive)</MenuItem>
                      </Select>
                      <FormHelperText>
                        Alibaba Cloud QWEN models optimized for vision processing and OCR tasks
                      </FormHelperText>
                    </FormControl>
                  </Box>
                )}

                {/* Vision Processing Configuration */}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Vision Processing Settings
                  </Typography>
                  
                  <Box display="flex" flexDirection="column" gap={2}>
                    <FormControl fullWidth>
                      <InputLabel>Image Detail Level</InputLabel>
                      <Select
                        value={step.config?.visionDetail || 'high'}
                        onChange={(e) => handleConfigChange('visionDetail', e.target.value)}
                      >
                        <MenuItem value="high">High Detail (Better accuracy, slower)</MenuItem>
                        <MenuItem value="low">Low Detail (Faster processing)</MenuItem>
                        <MenuItem value="auto">Auto (High for Enhanced, Low for Quick)</MenuItem>
                      </Select>
                      <FormHelperText>
                        Higher detail provides better accuracy but costs more and takes longer
                      </FormHelperText>
                    </FormControl>

                    <TextField
                      label="Processing Timeout (seconds)"
                      type="number"
                      value={step.config?.visionTimeout || 90}
                      onChange={(e) => handleConfigChange('visionTimeout', parseInt(e.target.value))}
                      inputProps={{ min: 30, max: 300 }}
                      helperText="Maximum time to wait for vision processing (30-300 seconds)"
                    />
                  </Box>
                </Box>

                {/* Vision Analysis Prompt */}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Vision Analysis Prompt
                  </Typography>
                  <TextField
                    value={step.config?.visionPrompt || (
                      step.config?.processingMode === 'image_analysis' 
                        ? 'Analyze this image in detail. Identify and count all visible objects, describe the scene, and provide structured information about what you see. Include quantities, descriptions, and any relevant details.'
                        : 'Extract all text and data from this image, maintaining the original structure and formatting.'
                    )}
                    onChange={(e) => handleConfigChange('visionPrompt', e.target.value)}
                    multiline
                    minRows={8}
                    maxRows={16}
                    fullWidth
                    placeholder={
                      step.config?.processingMode === 'image_analysis'
                        ? "Describe what you want the AI to analyze in the images (e.g., count objects, identify items, describe scene)"
                        : "Describe what you want the AI to extract from the images"
                    }
                    helperText={
                      step.config?.processingMode === 'image_analysis'
                        ? "Custom prompt for AI image analysis. Be specific about what to identify, count, or describe."
                        : "Custom prompt for AI vision analysis. Be specific about what data to extract."
                    }
                  />
                </Box>

                {/* Processing Priority Info */}
                <Box sx={{ p: 2, backgroundColor: step.config?.processingMode === 'image_analysis' ? '#e3f2fd' : '#e8f5e8', borderRadius: 1, border: step.config?.processingMode === 'image_analysis' ? '1px solid #90caf9' : '1px solid #c8e6c9' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: step.config?.processingMode === 'image_analysis' ? '#1976d2' : '#2e7d32', mb: 1 }}>
                    🎯 Processing Priority:
                  </Typography>
                  <Typography variant="body2" color={step.config?.processingMode === 'image_analysis' ? '#1976d2' : '#2e7d32'} sx={{ fontSize: '0.875rem' }}>
                    {step.config?.processingMode === 'image_analysis' ? (
                      <>
                        {step.config?.visionMethod === 'responses' && '1. Responses API (Latest) → No OCR Fallback'}
                        {step.config?.visionMethod === 'gemini' && '1. Google Gemini (Vertex AI) → No OCR Fallback'}
                        {step.config?.visionMethod === 'enhanced' && '1. Enhanced Vision (Legacy) → No OCR Fallback'}
                        {step.config?.visionMethod === 'quick' && '1. Quick Vision (Legacy) → No OCR Fallback'}
                        {(step.config?.visionMethod === 'auto' || !step.config?.visionMethod) && '1. Responses API → 2. Google Gemini → 3. Enhanced Vision → 4. Quick Vision → No OCR Fallback'}
                        {step.config?.visionMethod === 'qwen' && '1. QWEN Vision (Alibaba Cloud) → No OCR Fallback'}
                      </>
                    ) : (
                      <>
                        {step.config?.visionMethod === 'responses' && '1. Responses API (Latest) → 2. OCR Fallback'}
                        {step.config?.visionMethod === 'gemini' && '1. Google Gemini (Vertex AI) → 2. OCR Fallback'}
                        {step.config?.visionMethod === 'enhanced' && '1. Enhanced Vision (Legacy) → 2. OCR Fallback'}
                        {step.config?.visionMethod === 'quick' && '1. Quick Vision (Legacy) → 2. OCR Fallback'}
                        {(step.config?.visionMethod === 'auto' || !step.config?.visionMethod) && '1. Responses API (Latest) → 2. Google Gemini → 3. Enhanced Vision → 4. Quick Vision → 5. OCR Fallback'}
                        {step.config?.visionMethod === 'qwen' && '1. QWEN Vision (Alibaba Cloud) → 2. OCR Fallback'}
                      </>
                    )}
                  </Typography>
                  <Typography variant="caption" color={step.config?.processingMode === 'image_analysis' ? '#0d47a1' : '#1b5e20'} sx={{ display: 'block', mt: 1 }}>
                    {step.config?.processingMode === 'image_analysis' 
                      ? 'Image Analysis mode uses only AI Vision with no OCR fallback. Perfect for counting objects, describing scenes, and identifying items in images.'
                      : 'System automatically falls back to the next method if the current one fails or times out. Gemini excels at structured document extraction for invoices and purchase orders, while Responses API provides best overall accuracy with enhanced capabilities.'}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Output Variable Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📤 Output Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Configure how the extracted raw text/data will be stored for use in subsequent steps.
            </Typography>
            
            <TextField
              label="Output Variable Name"
              value={step.config?.outputVariable || 'extractedText'}
              onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
              fullWidth
              placeholder="extractedText"
              helperText="Name of the variable to store the extracted raw text/data. Use in AI Field Mapping steps as {{variableName}}"
            />
            
            {/* NEW: Excel/CSV Output Format Option */}
            <FormControl fullWidth>
              <InputLabel>Excel/CSV Output Format</InputLabel>
              <Select
                value={step.config?.excelCsvOutputFormat || 'structured'}
                onChange={(e) => handleConfigChange('excelCsvOutputFormat', e.target.value)}
              >
                <MenuItem value="structured">Structured Data (Array of Objects)</MenuItem>
                <MenuItem value="smart-document">Smart Document (Sections)</MenuItem>
                <MenuItem value="text">Plain Text</MenuItem>
              </Select>
              <FormHelperText>
                {step.config?.excelCsvOutputFormat === 'smart-document'
                  ? 'Automatically detects document sections (headers, metadata, tables, totals, footer). Best for trade documents with merged cells.'
                  : step.config?.excelCsvOutputFormat === 'text'
                    ? 'Excel/CSV files will be converted to plain text (use for AI Field Mapping)'
                    : 'Excel/CSV files will be parsed into arrays of objects (recommended for data processing)'}
              </FormHelperText>
            </FormControl>

            {step.config?.excelCsvOutputFormat === 'smart-document' && (
              <Box sx={{ p: 2, backgroundColor: '#f3e5f5', borderRadius: 1, border: '1px solid #ce93d8' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!step.config?.useExcelTemplates}
                      onChange={(e) => handleConfigChange('useExcelTemplates', e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2" fontWeight={600}>Use Excel Templates</Typography>}
                />
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: step.config?.useExcelTemplates ? 1.5 : 0 }}>
                  Match uploaded files against pre-defined templates for guided extraction with variable assignments and column mappings.
                </Typography>
                {step.config?.useExcelTemplates && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Template Library</InputLabel>
                    <Select
                      value={step.config?.excelTemplateLibraryId || ''}
                      label="Template Library"
                      onChange={(e) => handleConfigChange('excelTemplateLibraryId', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None selected</em>
                      </MenuItem>
                      {excelTemplateLibraries.map(lib => (
                        <MenuItem key={lib.libraryId} value={lib.libraryId}>
                          {lib.name} ({lib.templateCount || 0} templates)
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select which template library to match uploaded files against. Manage libraries in the Excel Templates tab.
                    </FormHelperText>
                  </FormControl>
                )}
              </Box>
            )}
            
            <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1 }}>
                📊 Excel/CSV Handling:
              </Typography>
              <Typography variant="body2" color="#1976d2" sx={{ mb: 1, fontSize: '0.875rem' }}>
                • <strong>Structured Data</strong>: Recommended for clean spreadsheets. Returns arrays you can directly use with Variable Operations (Normalize, Split, Transform).
              </Typography>
              <Typography variant="body2" color="#1976d2" sx={{ mb: 1, fontSize: '0.875rem' }}>
                • <strong>Smart Document</strong>: Best for messy Excel files with merged cells (invoices, sales contracts, POs). Separates header info, metadata, data tables, totals, and footer into clean sections.
              </Typography>
              <Typography variant="body2" color="#1976d2" sx={{ fontSize: '0.875rem' }}>
                • <strong>Plain Text</strong>: Use when you want to process Excel/CSV with AI Field Mapping instead of using the structured format.
              </Typography>
            </Box>
            
            <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #c8e6c9' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                💡 How to use this variable:
              </Typography>
              <Typography variant="body2" color="#2e7d32" sx={{ mb: 1 }}>
                In your AI Field Mapping step, you can reference this data using:
              </Typography>
              <Box component="code" sx={{ 
                backgroundColor: '#f1f8e9', 
                p: 1, 
                borderRadius: 0.5, 
                display: 'block', 
                fontSize: '0.875rem',
                color: '#1b5e20'
              }}>
                {`Extract product information from: {{${step.config?.outputVariable || 'extractedText'}}}`}
              </Box>
            </Box>
            
            {/* Include Confidence Scores */}
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.includeConfidenceScores !== undefined ? step.config.includeConfidenceScores : true}
                  onChange={(e) => handleConfigChange('includeConfidenceScores', e.target.checked)}
                />
              }
              label="Include confidence scores in output"
            />
            {step.config?.includeConfidenceScores !== false && (
              <>
                <TextField
                  label="Confidence Field Name"
                  value={step.config?.confidenceFieldName || 'confidence'}
                  onChange={(e) => handleConfigChange('confidenceFieldName', e.target.value)}
                  fullWidth
                  placeholder="confidence"
                  helperText="Field name for confidence scores (e.g., 'confidence', 'confidence_score')"
                  size="small"
                />
                <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc02' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57c00', mb: 1 }}>
                    🚧 Backend Implementation Required
                  </Typography>
                  <Typography variant="body2" color="#ef6c00" sx={{ fontSize: '0.875rem' }}>
                    Confidence scores will be simulated in the frontend until the backend AI Vision processing is updated to include actual confidence values in the output.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* NEW: Image Rotation Detection Options */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🔄 Image Rotation Detection</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary" mb={1}>
              Choose how to handle rotated images before OCR processing for optimal text recognition.
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Rotation Detection Method</InputLabel>
              <Select
                value={step.config?.rotationDetectionMethod || 'disabled'}
                onChange={(e) => handleConfigChange('rotationDetectionMethod', e.target.value)}
                label="Rotation Detection Method"
              >
                <MenuItem value="disabled">
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>🚫</span>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Disabled</Typography>
                      <Typography variant="caption" color="textSecondary">No rotation detection (fastest)</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                
                <MenuItem value="tesseract">
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>⚡</span>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Tesseract (Recommended)</Typography>
                      <Typography variant="caption" color="textSecondary">Fast & free rotation detection</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                
                <MenuItem value="google_ai">
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>🤖</span>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Google AI Vision</Typography>
                      <Typography variant="caption" color="textSecondary">Most accurate but slower & costly</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                
                <MenuItem value="manual">
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>✋</span>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Manual</Typography>
                      <Typography variant="caption" color="textSecondary">Specify rotation angle manually</Typography>
                    </Box>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            
            {/* Manual rotation angle selector */}
            {step.config?.rotationDetectionMethod === 'manual' && (
              <FormControl fullWidth>
                <InputLabel>Manual Rotation Angle</InputLabel>
                <Select
                  value={step.config?.manualRotation || 0}
                  onChange={(e) => handleConfigChange('manualRotation', e.target.value)}
                  label="Manual Rotation Angle"
                >
                  <MenuItem value={0}>0° (No rotation)</MenuItem>
                  <MenuItem value={90}>90° (Rotate clockwise)</MenuItem>
                  <MenuItem value={180}>180° (Upside down)</MenuItem>
                  <MenuItem value={270}>270° (Rotate counter-clockwise)</MenuItem>
                </Select>
              </FormControl>
            )}
            
            {/* Method descriptions */}
            {step.config?.rotationDetectionMethod === 'tesseract' && (
              <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                  ⚡ Tesseract Detection (Recommended)
                </Typography>
                <Typography variant="body2" color="#2e7d32" sx={{ fontSize: '0.875rem' }}>
                  Fast and free rotation detection using Tesseract OCR. Tests all 4 orientations and picks the best one based on text recognition quality. Perfect balance of speed and accuracy.
                </Typography>
              </Box>
            )}
            
            {step.config?.rotationDetectionMethod === 'google_ai' && (
              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ef6c00', mb: 1 }}>
                  🤖 Google AI Vision Detection
                </Typography>
                <Typography variant="body2" color="#ef6c00" sx={{ fontSize: '0.875rem' }}>
                  Most accurate rotation detection using Google AI Vision. Tests all orientations but uses API calls (slower and costs money). Use when highest accuracy is needed.
                </Typography>
              </Box>
            )}
            
            {step.config?.rotationDetectionMethod === 'manual' && (
              <Box sx={{ p: 2, backgroundColor: '#f3e5f5', borderRadius: 1, border: '1px solid #9c27b0' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2', mb: 1 }}>
                  ✋ Manual Rotation
                </Typography>
                <Typography variant="body2" color="#7b1fa2" sx={{ fontSize: '0.875rem' }}>
                  Specify the exact rotation angle when you know how your images are oriented. No automatic detection, just applies the specified rotation.
                </Typography>
              </Box>
            )}
            
            {step.config?.rotationDetectionMethod === 'disabled' && (
              <Box sx={{ p: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #9e9e9e' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#616161', mb: 1 }}>
                  🚫 Rotation Detection Disabled
                </Typography>
                <Typography variant="body2" color="#616161" sx={{ fontSize: '0.875rem' }}>
                  Images will be processed in their original orientation. Use when you know your images are already correctly oriented.
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
  };

  const renderAiExtractionConfig = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Configure LLM-powered intelligent data extraction with advanced prompt engineering and context understanding.
      </Typography>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🤖 LLM Engine Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>LLM Model</InputLabel>
              <Select
                value={step.config?.llmModel || 'gpt-4o'}
                onChange={(e) => handleConfigChange('llmModel', e.target.value)}
              >
                {/* OpenAI Models */}
                <MenuItem value="gpt-4o">GPT-4o (Most Capable)</MenuItem>
                <MenuItem value="gpt-4o-mini">GPT-4o Mini (Balanced)</MenuItem>
                <MenuItem value="gpt-5">GPT-5 (Latest Advanced)</MenuItem>
                <MenuItem value="gpt-5-mini">GPT-5 Mini (Fast)</MenuItem>
                <MenuItem value="gpt-5-nano">GPT-5 Nano (Very Fast)</MenuItem>
                <MenuItem value="gpt-4.1">GPT-4.1 (Advanced)</MenuItem>
                <MenuItem value="gpt-4.1-mini">GPT-4.1 Mini (Fast)</MenuItem>
                <MenuItem value="gpt-4.1-nano">GPT-4.1 Nano (Ultra-fast)</MenuItem>
                <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo (Ultra-fast)</MenuItem>
                
                {/* Google AI Models */}
                <MenuItem value="gemini-1.5-flash">Gemini 1.5 Flash (Google AI)</MenuItem>
                <MenuItem value="gemini-1.5-pro">Gemini 1.5 Pro (Google AI - Premium)</MenuItem>
                <MenuItem value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite (Google AI - Fast)</MenuItem>
                <MenuItem value="gemini-2.0-flash">Gemini 2.0 Flash (Google AI)</MenuItem>
                <MenuItem value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite (Google AI - Latest Fast)</MenuItem> 
                <MenuItem value="gemini-2.5-flash">Gemini 2.5 Flash (Google AI - Latest)</MenuItem>
                {/* AWS Bedrock Models - User's Available Models */}
                {/* Nova models temporarily disabled - require inference profiles
                <MenuItem value="bedrock-nova-pro">Nova Pro (AWS Bedrock - Multimodal)</MenuItem>
                <MenuItem value="bedrock-nova-lite">Nova Lite (AWS Bedrock - Fast Multimodal)</MenuItem>
                <MenuItem value="bedrock-nova-micro">Nova Micro (AWS Bedrock - Ultra Fast)</MenuItem>
                */}
                <MenuItem value="bedrock-claude-3-haiku">Claude 3 Haiku (AWS Bedrock - Fast)</MenuItem>
                <MenuItem value="bedrock-claude-3-sonnet">Claude 3 Sonnet (AWS Bedrock - Balanced)</MenuItem>
                <MenuItem value="bedrock-claude-3-5-sonnet">Claude 3.5 Sonnet (AWS Bedrock - Balanced)</MenuItem>
                <MenuItem value="bedrock-claude-3-5-sonnet-v2">Claude 3.5 Sonnet v2 (AWS Bedrock - Latest Enhanced)</MenuItem>
                <MenuItem value="bedrock-claude-3-7-sonnet">Claude 3.7 Sonnet (AWS Bedrock - Advanced)</MenuItem>
                <MenuItem value="bedrock-claude-sonnet-4">Claude Sonnet 4 (AWS Bedrock - Premium)</MenuItem>
                <MenuItem value="bedrock-embed-english">Embed English (AWS Bedrock - Text Embeddings)</MenuItem>
                <MenuItem value="bedrock-embed-multilingual">Embed Multilingual (AWS Bedrock - Multi-language Embeddings)</MenuItem>
                  
                  <MenuItem value="qwen-plus">QWEN Plus (Alibaba Cloud)</MenuItem>
                  <MenuItem value="qwen-max">QWEN Max (Alibaba Cloud - Premium)</MenuItem>
                  <MenuItem value="qwen-turbo">QWEN Turbo (Alibaba Cloud - Fast)</MenuItem>
                
                {/* Direct Anthropic API Models */}
                <MenuItem value="anthropic-claude-sonnet-4">Claude Sonnet 4.5 (Anthropic Direct - Balanced)</MenuItem>
                <MenuItem value="anthropic-claude-opus-4">Claude Opus 4.5 (Anthropic Direct - Most Capable)</MenuItem>
              </Select>
              <FormHelperText>
                Choose model based on complexity: GPT-4o for complex documents, mini/nano for fast extraction, Gemini for Google AI integration, AWS Bedrock for enterprise models, Anthropic for Claude Sonnet/Opus 4.5.
                <br />
                <strong>Note:</strong> AWS Bedrock models require AWS credentials, Anthropic models require Anthropic API key - configure in Project Settings.
              </FormHelperText>
            </FormControl>
            
            <TextField
              label="System Prompt"
              value={step.config?.systemPrompt || 'You are an expert data extraction AI. Extract information accurately and provide confidence scores.'}
              onChange={(e) => handleConfigChange('systemPrompt', e.target.value)}
              multiline
              minRows={8}
              maxRows={20}
              fullWidth
              helperText="Define the AI's role and behavior"
              sx={{
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start'
                },
                '& .MuiInputBase-input': {
                  resize: 'vertical'
                }
              }}
            />

            <TextField
              label="Input Prompt Template"
              value={step.config?.inputPrompt || 'Extract all relevant data from the following text: {{extractedText}}'}
              onChange={(e) => handleConfigChange('inputPrompt', e.target.value)}
              multiline
              minRows={8}
              maxRows={20}
              fullWidth
              placeholder="Extract product information from: {{extractedText}}"
              helperText="Use {{variableName}} syntax to reference data from previous steps. The AI will extract structured data from this input."
              sx={{
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start'
                },
                '& .MuiInputBase-input': {
                  resize: 'vertical'
                }
              }}
            />
            
            <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #bbdefb' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1 }}>
                💡 Template Variable Usage:
              </Typography>
              <Typography variant="body2" color="#1565c0" sx={{ mb: 1 }}>
                Reference data from previous steps using double curly braces:
              </Typography>
              <Box component="pre" sx={{ 
                backgroundColor: '#f3f8ff', 
                p: 1, 
                borderRadius: 0.5, 
                fontSize: '0.875rem',
                color: '#0d47a1',
                margin: 0,
                whiteSpace: 'pre-wrap'
              }}>
{`• {{extractedText}} - from Document OCR & Vision step
• {{rawData}} - custom output variable name
• {{previousStepResult}} - any variable from flow


Example:
"Extract product details from: {{extractedText}}
Focus on finding products with their prices and SKUs."`}
              </Box>
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                label="Temperature"
                type="number"
                inputProps={{ min: 0, max: 2, step: 0.1 }}
                value={step.config?.temperature || 0.1}
                onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                fullWidth
                helperText="Lower = more consistent"
              />
              <TextField
                label="Max Tokens"
                type="number"
                value={step.config?.maxTokens || 4000}
                onChange={(e) => handleConfigChange('maxTokens', parseInt(e.target.value))}
                fullWidth
                helperText="Response length limit"
              />
            </Box>

            

            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.enableStreamingResponse || false}
                  onChange={(e) => handleConfigChange('enableStreamingResponse', e.target.checked)}
                />
              }
              label="Enable streaming response"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🎯 Smart Extraction Fields</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Define what data the LLM should extract. The AI will intelligently locate and extract these fields with confidence scoring.
            </Typography>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  LLM Extraction Targets
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const currentFields = step.config?.extractionFields || [];
                    const newField = {
                      id: `llm-field-${Date.now()}`,
                      fieldName: '',
                      variableName: '',
                      dataType: 'string',
                      extractionHint: '',
                      required: false,
                      includeConfidence: true,
                      countsAsBillableObject: true,
                      confidenceVariable: '',
                      validationRules: []
                    };
                    handleConfigChange('extractionFields', [...currentFields, newField]);
                  }}
                >
                  Add Extraction Target
                </Button>
              </Box>
              
              {(step.config?.extractionFields || []).map((field, index) => (
                <Card key={field.id} sx={{ mb: 2, p: 2, backgroundColor: '#f0f4ff', border: '1px solid #e1e7ff' }}>
                  <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
                    <Box flexGrow={1}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="What to Extract"
                            value={field.fieldName}
                            onChange={(e) => {
                              const updatedFields = [...step.config.extractionFields];
                              updatedFields[index] = { ...field, fieldName: e.target.value };
                              handleConfigChange('extractionFields', updatedFields);
                            }}
                            fullWidth
                            placeholder="e.g., Invoice Number, Company Name, Total Amount"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="Store as Variable"
                            value={field.variableName}
                            onChange={(e) => {
                              const updatedFields = [...step.config.extractionFields];
                              updatedFields[index] = { ...field, variableName: e.target.value };
                              handleConfigChange('extractionFields', updatedFields);
                            }}
                            fullWidth
                            placeholder="e.g., invoiceNumber, companyName, totalAmount"
                          />
                        </Grid>
                        
                        <Grid item xs={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Expected Data Type</InputLabel>
                            <Select
                              value={field.dataType}
                              onChange={(e) => {
                                const updatedFields = [...step.config.extractionFields];
                                updatedFields[index] = { ...field, dataType: e.target.value };
                                handleConfigChange('extractionFields', updatedFields);
                              }}
                              label="Expected Data Type"
                            >
                              <MenuItem value="string">Text</MenuItem>
                              <MenuItem value="number">Number</MenuItem>
                              <MenuItem value="currency">Currency ($123.45)</MenuItem>
                              <MenuItem value="date">Date</MenuItem>
                              <MenuItem value="boolean">True/False</MenuItem>
                              <MenuItem value="email">Email Address</MenuItem>
                              <MenuItem value="phone">Phone Number</MenuItem>
                              <MenuItem value="url">URL/Website</MenuItem>
                              <MenuItem value="address">Full Address</MenuItem>
                              <MenuItem value="array">List of Items</MenuItem>
                              <MenuItem value="json_object">Structured Object</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Box display="flex" gap={1}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={field.required || false}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.extractionFields];
                                    updatedFields[index] = { ...field, required: e.target.checked };
                                    handleConfigChange('extractionFields', updatedFields);
                                  }}
                                />
                              }
                              label="Required"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={field.includeConfidence !== false}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.extractionFields];
                                    updatedFields[index] = { ...field, includeConfidence: e.target.checked };
                                    handleConfigChange('extractionFields', updatedFields);
                                  }}
                                />
                              }
                              label="Confidence"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={field.countsAsBillableObject !== false}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.extractionFields];
                                    updatedFields[index] = { ...field, countsAsBillableObject: e.target.checked };
                                    handleConfigChange('extractionFields', updatedFields);
                                  }}
                                />
                              }
                              label="Billable"
                            />
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="LLM Extraction Hint"
                            value={field.extractionHint}
                            onChange={(e) => {
                              const updatedFields = [...step.config.extractionFields];
                              updatedFields[index] = { ...field, extractionHint: e.target.value };
                              handleConfigChange('extractionFields', updatedFields);
                            }}
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={6}
                            placeholder="Help the LLM understand exactly what to look for and where:

Examples:
• 'Find the total amount at the bottom of the invoice, usually after tax and before any payment terms'
• 'Look for a 10-digit number starting with INV- or similar, typically in the header area'
• 'Extract the company name from the top of the document, avoiding recipient/billing addresses'
• 'Find line items in a table format with descriptions, quantities, and prices'

Be specific about location, format, and context to improve accuracy."
                            helperText="Provide detailed, specific instructions to improve LLM accuracy. Use multiple lines for complex extraction logic."
                            sx={{
                              '& .MuiInputBase-root': {
                                alignItems: 'flex-start'
                              },
                              '& .MuiInputBase-input': {
                                resize: 'vertical',
                                minHeight: '120px'
                              }
                            }}
                          />
                        </Grid>

                        {field.includeConfidence !== false && (
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Confidence Variable"
                              value={field.confidenceVariable || `${field.variableName}_confidence`}
                              onChange={(e) => {
                                const updatedFields = [...step.config.extractionFields];
                                updatedFields[index] = { ...field, confidenceVariable: e.target.value };
                                handleConfigChange('extractionFields', updatedFields);
                              }}
                              fullWidth
                              placeholder={`${field.variableName || 'field'}_confidence`}
                              helperText="LLM confidence score (0.0 to 1.0)"
                            />
                          </Grid>
                        )}

                        {/* Validation Rules */}
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                            ✅ Validation Rules (Optional)
                          </Typography>
                          <TextField
                            size="small"
                            label="Validation Pattern/Rules"
                            value={field.validationPattern || ''}
                            onChange={(e) => {
                              const updatedFields = [...step.config.extractionFields];
                              updatedFields[index] = { ...field, validationPattern: e.target.value };
                              handleConfigChange('extractionFields', updatedFields);
                            }}
                            fullWidth
                            placeholder="For email: '^[\\w\\.-]+@[\\w\\.-]+\\.[a-zA-Z]{2,}$' or for currency: '^\\$?[0-9,]+\\.?[0-9]*$'"
                            helperText="Regex pattern or validation rule to verify extracted data"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updatedFields = step.config.extractionFields.filter((_, i) => i !== index);
                        handleConfigChange('extractionFields', updatedFields);
                      }}
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              ))}
              
              {(!step.config?.extractionFields || step.config.extractionFields.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2">
                    No extraction targets defined. Add fields for the LLM to intelligently extract.
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {/* LLM-Focused Quick Templates */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                🤖 LLM-Powered Templates
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const llmInvoiceFields = [
                      { id: 'llm-inv-1', fieldName: 'Invoice Number', variableName: 'invoiceNumber', dataType: 'string', extractionHint: 'Look for a unique identifier, often labeled as Invoice #, Invoice Number, or similar', required: true, includeConfidence: true, confidenceVariable: 'invoiceNumber_confidence' },
                      { id: 'llm-inv-2', fieldName: 'Vendor/Company Name', variableName: 'vendorName', dataType: 'string', extractionHint: 'Find the business name issuing the invoice, usually at the top of the document', required: true, includeConfidence: true, confidenceVariable: 'vendorName_confidence' },
                      { id: 'llm-inv-3', fieldName: 'Invoice Date', variableName: 'invoiceDate', dataType: 'date', extractionHint: 'Locate the date when the invoice was created or issued', required: true, includeConfidence: true, confidenceVariable: 'invoiceDate_confidence' },
                      { id: 'llm-inv-4', fieldName: 'Due Date', variableName: 'dueDate', dataType: 'date', extractionHint: 'Find when payment is due, may be labeled as Due Date, Payment Due, etc.', required: true, includeConfidence: true, confidenceVariable: 'dueDate_confidence' },
                      { id: 'llm-inv-5', fieldName: 'Total Amount', variableName: 'totalAmount', dataType: 'currency', extractionHint: 'Find the final amount to be paid, usually the largest number at the bottom', required: true, includeConfidence: true, confidenceVariable: 'totalAmount_confidence', validationPattern: '^\\$?[0-9,]+\\.?[0-9]*$' },
                      { id: 'llm-inv-6', fieldName: 'Line Items', variableName: 'lineItems', dataType: 'array', extractionHint: 'Extract all individual items or services listed with their descriptions and amounts', required: false, includeConfidence: true, confidenceVariable: 'lineItems_confidence' }
                    ];
                    handleConfigChange('extractionFields', llmInvoiceFields);
                  }}
                >
                  🧾 Smart Invoice
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const llmContractFields = [
                      { id: 'llm-cont-1', fieldName: 'Contract Title', variableName: 'contractTitle', dataType: 'string', extractionHint: 'Find the main title or subject of the contract', required: true, includeConfidence: true, confidenceVariable: 'contractTitle_confidence' },
                      { id: 'llm-cont-2', fieldName: 'Parties Involved', variableName: 'parties', dataType: 'array', extractionHint: 'Identify all parties, companies, or individuals involved in the contract', required: true, includeConfidence: true, confidenceVariable: 'parties_confidence' },
                      { id: 'llm-cont-3', fieldName: 'Contract Value', variableName: 'contractValue', dataType: 'currency', extractionHint: 'Find the total monetary value or price specified in the contract', required: false, includeConfidence: true, confidenceVariable: 'contractValue_confidence' },
                      { id: 'llm-cont-4', fieldName: 'Start Date', variableName: 'startDate', dataType: 'date', extractionHint: 'Locate when the contract begins or becomes effective', required: true, includeConfidence: true, confidenceVariable: 'startDate_confidence' },
                      { id: 'llm-cont-5', fieldName: 'End Date', variableName: 'endDate', dataType: 'date', extractionHint: 'Find when the contract expires or terminates', required: false, includeConfidence: true, confidenceVariable: 'endDate_confidence' },
                      { id: 'llm-cont-6', fieldName: 'Key Terms', variableName: 'keyTerms', dataType: 'array', extractionHint: 'Extract important terms, conditions, or clauses mentioned in the contract', required: false, includeConfidence: true, confidenceVariable: 'keyTerms_confidence' }
                    ];
                    handleConfigChange('extractionFields', llmContractFields);
                  }}
                >
                  📄 Smart Contract
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const llmDocumentFields = [
                      { id: 'llm-doc-1', fieldName: 'Document Type', variableName: 'documentType', dataType: 'string', extractionHint: 'Identify what type of document this is (letter, report, form, etc.)', required: true, includeConfidence: true, confidenceVariable: 'documentType_confidence' },
                      { id: 'llm-doc-2', fieldName: 'Author/Sender', variableName: 'author', dataType: 'string', extractionHint: 'Find who created or sent this document', required: false, includeConfidence: true, confidenceVariable: 'author_confidence' },
                      { id: 'llm-doc-3', fieldName: 'Recipients', variableName: 'recipients', dataType: 'array', extractionHint: 'Identify who the document is addressed to or intended for', required: false, includeConfidence: true, confidenceVariable: 'recipients_confidence' },
                      { id: 'llm-doc-4', fieldName: 'Document Date', variableName: 'documentDate', dataType: 'date', extractionHint: 'Find when the document was created or dated', required: false, includeConfidence: true, confidenceVariable: 'documentDate_confidence' },
                      { id: 'llm-doc-5', fieldName: 'Main Subject/Topic', variableName: 'subject', dataType: 'string', extractionHint: 'Determine the primary subject or main topic of the document', required: true, includeConfidence: true, confidenceVariable: 'subject_confidence' },
                      { id: 'llm-doc-6', fieldName: 'Key Information', variableName: 'keyInfo', dataType: 'array', extractionHint: 'Extract the most important facts, numbers, or information mentioned', required: false, includeConfidence: true, confidenceVariable: 'keyInfo_confidence' }
                    ];
                    handleConfigChange('extractionFields', llmDocumentFields);
                  }}
                >
                  📋 Smart Document
                </Button>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Hybrid Extraction Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🚀 Hybrid Extraction (Speed + Accuracy)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Combine regex pattern matching with LLM intelligence for optimal speed and accuracy. 
              Regex extracts common fields instantly, with LLM fallback for complex cases.
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Extraction Method</InputLabel>
              <Select
                value={step.config?.extractionMethod || 'llm_only'}
                onChange={(e) => handleConfigChange('extractionMethod', e.target.value)}
              >
                <MenuItem value="llm_only">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>🤖 LLM Only</Typography>
                    <Typography variant="caption" color="textSecondary">Current behavior - uses AI for all extraction</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="hybrid_regex_first">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>🚀 Hybrid (Regex First)</Typography>
                    <Typography variant="caption" color="textSecondary">Try regex patterns first, fallback to LLM</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="hybrid_llm_first">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>🔍 Hybrid (LLM First)</Typography>
                    <Typography variant="caption" color="textSecondary">Use LLM first, validate with regex patterns</Typography>
                  </Box>
                </MenuItem>
              </Select>
              <FormHelperText>
                {step.config?.extractionMethod === 'llm_only' && 'Pure AI Field Mapping - slower but handles any document format'}
                {step.config?.extractionMethod === 'hybrid_regex_first' && 'Fast regex first, AI fallback - best for consistent document formats'}
                {step.config?.extractionMethod === 'hybrid_llm_first' && 'AI first with regex validation - best accuracy with speed boost'}
              </FormHelperText>
            </FormControl>

            {/* Always show hybrid configuration help */}
            <Box sx={{ p: 2, backgroundColor: '#f3f8ff', borderRadius: 1, border: '1px solid #d1e7ff', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1 }}>
                💡 How to Enable Hybrid Extraction:
              </Typography>
              <Typography variant="body2" color="#1565c0">
                1. Select "Hybrid (Regex First)" or "Hybrid (LLM First)" above<br/>
                2. Configure regex patterns below for instant field extraction<br/>
                3. Keep LLM fallback enabled for accuracy on complex documents
              </Typography>
            </Box>

            {step.config?.extractionMethod !== 'llm_only' && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  🔧 Hybrid Configuration
                </Typography>
                
                <Box display="flex" gap={2} mb={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={step.config?.enableRegexExtraction !== false}
                        onChange={(e) => handleConfigChange('enableRegexExtraction', e.target.checked)}
                      />
                    }
                    label="Enable Regex Patterns"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={step.config?.fallbackToLLM !== false}
                        onChange={(e) => handleConfigChange('fallbackToLLM', e.target.checked)}
                      />
                    }
                    label="LLM Fallback"
                  />
                </Box>

                <TextField
                  label="Confidence Threshold"
                  type="number"
                  inputProps={{ min: 0.1, max: 1.0, step: 0.1 }}
                  value={step.config?.confidenceThreshold || 0.7}
                  onChange={(e) => handleConfigChange('confidenceThreshold', parseFloat(e.target.value))}
                  fullWidth
                  helperText="Minimum confidence to use regex results (0.1-1.0)"
                  sx={{ mb: 2 }}
                />

                {/* Regex Pattern Configuration */}
                {step.config?.enableRegexExtraction !== false && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                      🎯 Custom Regex Patterns
                    </Typography>
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2" color="textSecondary">
                        Define regex patterns for fast field extraction. Each pattern targets specific fields.
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const currentPatterns = step.config?.regexPatterns || [];
                          const newPattern = {
                            id: `regex-pattern-${Date.now()}`,
                            fieldName: '',
                            patterns: [''],
                            confidence: 0.8,
                            enabled: true
                          };
                          handleConfigChange('regexPatterns', [...currentPatterns, newPattern]);
                        }}
                      >
                        Add Pattern
                      </Button>
                    </Box>

                    {(step.config?.regexPatterns || []).map((pattern, index) => (
                      <Card key={pattern.id} sx={{ mb: 2, p: 2, backgroundColor: '#fff3e0', border: '1px solid #ffcc02' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                          <Box flexGrow={1}>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Target Field"
                                  value={pattern.fieldName}
                                  onChange={(e) => {
                                    const updatedPatterns = [...step.config.regexPatterns];
                                    updatedPatterns[index] = { ...pattern, fieldName: e.target.value };
                                    handleConfigChange('regexPatterns', updatedPatterns);
                                  }}
                                  fullWidth
                                  placeholder="e.g., purchaseOrder, invoiceNumber"
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <TextField
                                  size="small"
                                  label="Confidence"
                                  type="number"
                                  inputProps={{ min: 0.1, max: 1.0, step: 0.1 }}
                                  value={pattern.confidence || 0.8}
                                  onChange={(e) => {
                                    const updatedPatterns = [...step.config.regexPatterns];
                                    updatedPatterns[index] = { ...pattern, confidence: parseFloat(e.target.value) };
                                    handleConfigChange('regexPatterns', updatedPatterns);
                                  }}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={pattern.enabled !== false}
                                      onChange={(e) => {
                                        const updatedPatterns = [...step.config.regexPatterns];
                                        updatedPatterns[index] = { ...pattern, enabled: e.target.checked };
                                        handleConfigChange('regexPatterns', updatedPatterns);
                                      }}
                                    />
                                  }
                                  label="Enabled"
                                />
                              </Grid>
                              
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                  Regex Patterns (try multiple for better coverage):
                                </Typography>
                                {(pattern.patterns || ['']).map((patternStr, patternIndex) => (
                                  <Box key={patternIndex} display="flex" gap={1} mb={1}>
                                    <TextField
                                      size="small"
                                      label={`Pattern ${patternIndex + 1}`}
                                      value={patternStr}
                                      onChange={(e) => {
                                        const updatedPatterns = [...step.config.regexPatterns];
                                        const updatedPatternStrs = [...pattern.patterns];
                                        updatedPatternStrs[patternIndex] = e.target.value;
                                        updatedPatterns[index] = { ...pattern, patterns: updatedPatternStrs };
                                        handleConfigChange('regexPatterns', updatedPatterns);
                                      }}
                                      fullWidth
                                      placeholder="e.g., /po[:\s]*([A-Z0-9-]+)/gi"
                                      helperText={patternIndex === 0 ? "Use capturing groups () to extract the value" : ""}
                                    />
                                    {pattern.patterns.length > 1 && (
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const updatedPatterns = [...step.config.regexPatterns];
                                          const updatedPatternStrs = pattern.patterns.filter((_, i) => i !== patternIndex);
                                          updatedPatterns[index] = { ...pattern, patterns: updatedPatternStrs };
                                          handleConfigChange('regexPatterns', updatedPatterns);
                                        }}
                                        sx={{ color: '#d32f2f' }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Box>
                                ))}
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const updatedPatterns = [...step.config.regexPatterns];
                                    const updatedPatternStrs = [...pattern.patterns, ''];
                                    updatedPatterns[index] = { ...pattern, patterns: updatedPatternStrs };
                                    handleConfigChange('regexPatterns', updatedPatterns);
                                  }}
                                  sx={{ mt: 1 }}
                                >
                                  Add Alternative Pattern
                                </Button>
                              </Grid>
                            </Grid>
                          </Box>
                          
                          <IconButton
                            size="small"
                            onClick={() => {
                              const updatedPatterns = step.config.regexPatterns.filter((_, i) => i !== index);
                              handleConfigChange('regexPatterns', updatedPatterns);
                            }}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Card>
                    ))}

                    {/* Pattern Templates */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        📋 Quick Pattern Templates:
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const commonPatterns = [
                              {
                                id: `regex-po-${Date.now()}`,
                                fieldName: 'purchaseOrder',
                                patterns: [
                                  '(?:po|purchase\\s+order)[:\\s#]*([A-Z0-9-]+)',
                                  'order[:\\s#]*([A-Z0-9-]+)',
                                  'ref[:\\s#]*([A-Z0-9-]+)'
                                ],
                                confidence: 0.8,
                                enabled: true
                              },
                              {
                                id: `regex-batch-${Date.now()}`,
                                fieldName: 'batch',
                                patterns: [
                                  'batch[:\\s]*\\n?\\s*([^\\n\\s]+)',
                                  'lot[:\\s]*\\n?\\s*([^\\n\\s]+)'
                                ],
                                confidence: 0.8,
                                enabled: true
                              },
                              {
                                id: `regex-expiry-${Date.now()}`,
                                fieldName: 'expiryDate',
                                patterns: [
                                  '(?:ed|exp|expiry)[:\\s]*\\n?\\s*(\\d{1,2}[.\\-\\/]\\d{1,2}[.\\-\\/]\\d{2,4})'
                                ],
                                confidence: 0.8,
                                enabled: true
                              }
                            ];
                            handleConfigChange('regexPatterns', commonPatterns);
                          }}
                        >
                          📋 Proof of Delivery
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const invoicePatterns = [
                              {
                                id: `regex-inv-${Date.now()}`,
                                fieldName: 'invoiceNumber',
                                patterns: [
                                  'invoice[:\\s#]*([A-Z0-9-]+)',
                                  'inv[:\\s#]*([A-Z0-9-]+)'
                                ],
                                confidence: 0.8,
                                enabled: true
                              }
                            ];
                            handleConfigChange('regexPatterns', invoicePatterns);
                          }}
                        >
                          🧾 Invoice
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Performance Indicators */}
                <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                    📊 Expected Performance Benefits:
                  </Typography>
                  <Box sx={{ color: '#2e7d32', fontSize: '0.875rem' }}>
                    <Box>• ⚡ 70-80% faster processing for standard documents</Box>
                    <Box>• 💰 60-70% cost reduction when regex succeeds</Box>
                    <Box>• 🎯 LLM fallback ensures accuracy for complex cases</Box>
                    <Box>• 🔄 Automatic pattern learning from your document types</Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">⚙️ Advanced LLM Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.enableChainOfThought || true}
                  onChange={(e) => handleConfigChange('enableChainOfThought', e.target.checked)}
                />
              }
              label="Enable Chain-of-Thought reasoning"
              helperText="LLM explains its extraction process for better accuracy"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.enableSelfCorrection || true}
                  onChange={(e) => handleConfigChange('enableSelfCorrection', e.target.checked)}
                />
              }
              label="Enable self-correction and validation"
              helperText="LLM reviews and corrects its own extractions"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.enableMultiPassExtraction || false}
                  onChange={(e) => handleConfigChange('enableMultiPassExtraction', e.target.checked)}
                />
              }
              label="Enable multi-pass extraction"
              helperText="Run extraction multiple times and compare results (higher accuracy, slower)"
            />
            
            <TextField
              label="Confidence Threshold"
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              value={step.config?.confidenceThreshold || 0.7}
              onChange={(e) => handleConfigChange('confidenceThreshold', parseFloat(e.target.value))}
              fullWidth
              helperText="Minimum confidence required for extracted data (0.0 to 1.0)"
            />

            <TextField
              label="Output Variable Prefix"
              value={step.config?.outputPrefix || 'extracted_'}
              onChange={(e) => handleConfigChange('outputPrefix', e.target.value)}
              fullWidth
              placeholder="extracted_"
              helperText="Prefix for all extracted variables (e.g., 'extracted_invoiceNumber')"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.saveRawResponse || false}
                  onChange={(e) => handleConfigChange('saveRawResponse', e.target.checked)}
                />
              }
              label="Save raw LLM response"
              helperText="Store the complete LLM response for debugging"
            />
            
            {/* Include Confidence Scores */}
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.includeConfidenceScores !== undefined ? step.config.includeConfidenceScores : true}
                  onChange={(e) => handleConfigChange('includeConfidenceScores', e.target.checked)}
                />
              }
              label="Include confidence scores in extracted data"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.enableFieldConfidence !== undefined ? step.config.enableFieldConfidence : false}
                  onChange={(e) => handleConfigChange('enableFieldConfidence', e.target.checked)}
                />
              }
              label="Enable field-level confidence indicators"
            />
            {step.config?.includeConfidenceScores !== false && (
              <>
                <TextField
                  label="Confidence Field Name"
                  value={step.config?.confidenceFieldName || 'confidence'}
                  onChange={(e) => handleConfigChange('confidenceFieldName', e.target.value)}
                  fullWidth
                  placeholder="confidence"
                  helperText="Field name for confidence scores in extracted objects (e.g., 'confidence', 'confidence_score')"
                  size="small"
                />
                <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc02' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57c00', mb: 1 }}>
                    🚧 Backend Implementation Required
                  </Typography>
                  <Typography variant="body2" color="#ef6c00" sx={{ fontSize: '0.875rem' }}>
                    Confidence scores will be simulated in the frontend until the backend AI Field Mapping processing is updated to include actual confidence values in the extracted data objects.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderEnhancedApiCallConfig = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Configure API calls with Postman-like interface and advanced response handling.
      </Typography>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🌐 Request Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>

                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Method</InputLabel>
                  <Select
                    value={step.config?.method || 'GET'}
                    onChange={(e) => handleConfigChange('method', e.target.value)}
                    label="Method"
                  >
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                    <MenuItem value="PATCH">PATCH</MenuItem>
                    <MenuItem value="DELETE">DELETE</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="API URL"
                  value={step.config?.url || ''}
                  onChange={(e) => handleConfigChange('url', e.target.value)}
                  fullWidth
                  placeholder="https://api.example.com/endpoint/:id"
                  helperText="Use :paramName for path parameters"
                />

          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Authentication Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🔐 Authentication</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Authentication Type</InputLabel>
              <Select
                value={step.config?.authType || 'none'}
                onChange={(e) => handleConfigChange('authType', e.target.value)}
              >
                <MenuItem value="none">No Authentication</MenuItem>
                <MenuItem value="bearer_token">Bearer Token</MenuItem>
                <MenuItem value="basic_auth">Basic Auth</MenuItem>
                <MenuItem value="jwt_token">JWT Token</MenuItem>
                <MenuItem value="api_key">API Key</MenuItem>
                <MenuItem value="digest_auth">Digest Auth</MenuItem>
                <MenuItem value="oauth1">OAuth 1.0</MenuItem>
                <MenuItem value="oauth2">OAuth 2.0</MenuItem>
              </Select>
            </FormControl>

            {/* Bearer Token */}
            {step.config?.authType === 'bearer_token' && (
              <TextField
                label="Bearer Token"
                value={step.config?.bearerToken || ''}
                onChange={(e) => handleConfigChange('bearerToken', e.target.value)}
                fullWidth
                placeholder="Enter token or use {{tokenVariable}}"
                helperText="Token will be added to Authorization header"
              />
            )}

            {/* Basic Auth */}
            {step.config?.authType === 'basic_auth' && (
              <Box display="flex" gap={2}>
                <TextField
                  label="Username"
                  value={step.config?.basicUsername || ''}
                  onChange={(e) => handleConfigChange('basicUsername', e.target.value)}
                  fullWidth
                  placeholder="Username or {{usernameVar}}"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={step.config?.basicPassword || ''}
                  onChange={(e) => handleConfigChange('basicPassword', e.target.value)}
                  fullWidth
                  placeholder="Password or {{passwordVar}}"
                />
              </Box>
            )}

            {/* JWT Token */}
            {step.config?.authType === 'jwt_token' && (
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="JWT Token"
                  value={step.config?.jwtToken || ''}
                  onChange={(e) => handleConfigChange('jwtToken', e.target.value)}
                  fullWidth
                  placeholder="Enter JWT token or use {{jwtVariable}}"
                />
                <FormControl fullWidth>
                  <InputLabel>Add to</InputLabel>
                  <Select
                    value={step.config?.jwtLocation || 'header'}
                    onChange={(e) => handleConfigChange('jwtLocation', e.target.value)}
                  >
                    <MenuItem value="header">Authorization Header</MenuItem>
                    <MenuItem value="query">Query Parameter</MenuItem>
                    <MenuItem value="cookie">Cookie</MenuItem>
                  </Select>
                </FormControl>
                {step.config?.jwtLocation === 'query' && (
                  <TextField
                    label="Query Parameter Name"
                    value={step.config?.jwtQueryParam || 'token'}
                    onChange={(e) => handleConfigChange('jwtQueryParam', e.target.value)}
                    fullWidth
                  />
                )}
                {step.config?.jwtLocation === 'cookie' && (
                  <TextField
                    label="Cookie Name"
                    value={step.config?.jwtCookieName || 'jwt'}
                    onChange={(e) => handleConfigChange('jwtCookieName', e.target.value)}
                    fullWidth
                  />
                )}
              </Box>
            )}

            {/* API Key */}
            {step.config?.authType === 'api_key' && (
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="API Key"
                  value={step.config?.apiKey || ''}
                  onChange={(e) => handleConfigChange('apiKey', e.target.value)}
                  fullWidth
                  placeholder="Enter API key or use {{keyVariable}}"
                />
                <FormControl fullWidth>
                  <InputLabel>Add to</InputLabel>
                  <Select
                    value={step.config?.apiKeyLocation || 'header'}
                    onChange={(e) => handleConfigChange('apiKeyLocation', e.target.value)}
                  >
                    <MenuItem value="header">Header</MenuItem>
                    <MenuItem value="query">Query Parameter</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={step.config?.apiKeyLocation === 'header' ? 'Header Name' : 'Parameter Name'}
                  value={step.config?.apiKeyName || (step.config?.apiKeyLocation === 'header' ? 'X-API-Key' : 'api_key')}
                  onChange={(e) => handleConfigChange('apiKeyName', e.target.value)}
                  fullWidth
                  placeholder={step.config?.apiKeyLocation === 'header' ? 'X-API-Key' : 'api_key'}
                />
              </Box>
            )}

            {/* Digest Auth */}
            {step.config?.authType === 'digest_auth' && (
              <Box display="flex" gap={2}>
                <TextField
                  label="Username"
                  value={step.config?.digestUsername || ''}
                  onChange={(e) => handleConfigChange('digestUsername', e.target.value)}
                  fullWidth
                  placeholder="Username or {{usernameVar}}"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={step.config?.digestPassword || ''}
                  onChange={(e) => handleConfigChange('digestPassword', e.target.value)}
                  fullWidth
                  placeholder="Password or {{passwordVar}}"
                />
              </Box>
            )}

            {/* OAuth 1.0 */}
            {step.config?.authType === 'oauth1' && (
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Consumer Key"
                  value={step.config?.oauth1ConsumerKey || ''}
                  onChange={(e) => handleConfigChange('oauth1ConsumerKey', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Consumer Secret"
                  type="password"
                  value={step.config?.oauth1ConsumerSecret || ''}
                  onChange={(e) => handleConfigChange('oauth1ConsumerSecret', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Access Token"
                  value={step.config?.oauth1AccessToken || ''}
                  onChange={(e) => handleConfigChange('oauth1AccessToken', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Token Secret"
                  type="password"
                  value={step.config?.oauth1TokenSecret || ''}
                  onChange={(e) => handleConfigChange('oauth1TokenSecret', e.target.value)}
                  fullWidth
                />
              </Box>
            )}

            {/* OAuth 2.0 */}
            {step.config?.authType === 'oauth2' && (
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Access Token"
                  value={step.config?.oauth2AccessToken || ''}
                  onChange={(e) => handleConfigChange('oauth2AccessToken', e.target.value)}
                  fullWidth
                  placeholder="Enter access token or {{tokenVariable}}"
                />
                <FormControl fullWidth>
                  <InputLabel>Token Type</InputLabel>
                  <Select
                    value={step.config?.oauth2TokenType || 'Bearer'}
                    onChange={(e) => handleConfigChange('oauth2TokenType', e.target.value)}
                  >
                    <MenuItem value="Bearer">Bearer</MenuItem>
                    <MenuItem value="MAC">MAC</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Header Prefix (Optional)"
                  value={step.config?.oauth2HeaderPrefix || ''}
                  onChange={(e) => handleConfigChange('oauth2HeaderPrefix', e.target.value)}
                  fullWidth
                  placeholder="Leave empty for default"
                />
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Path Parameters */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🛣️ Path Parameters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Define path parameters for URLs like "/users/:userId/posts/:postId"
            </Typography>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Path Parameters
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const currentParams = step.config?.pathParams || [];
                    const newParam = {
                      id: `path-param-${Date.now()}`,
                      key: '',
                      value: '',
                      description: ''
                    };
                    handleConfigChange('pathParams', [...currentParams, newParam]);
                  }}
                >
                  Add Parameter
                </Button>
              </Box>
              
              {(step.config?.pathParams || []).map((param, index) => (
                <Card key={param.id} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Header with parameter name and delete button */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                        Path Parameter #{index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const updatedParams = step.config.pathParams.filter((_, i) => i !== index);
                          handleConfigChange('pathParams', updatedParams);
                        }}
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    
                    {/* Main fields in a row */}
                    <Box display="flex" gap={2}>
                      <TextField
                        size="small"
                        label="Parameter Name"
                        value={param.key}
                        onChange={(e) => {
                          const updatedParams = [...step.config.pathParams];
                          updatedParams[index] = { ...param, key: e.target.value };
                          handleConfigChange('pathParams', updatedParams);
                        }}
                        sx={{ flex: 1 }}
                        placeholder="userId"
                        helperText="URL path parameter name"
                      />
                      <TextField
                        size="small"
                        label="Value"
                        value={param.value}
                        onChange={(e) => {
                          const updatedParams = [...step.config.pathParams];
                          updatedParams[index] = { ...param, value: e.target.value };
                          handleConfigChange('pathParams', updatedParams);
                        }}
                        sx={{ flex: 1.5 }}
                        placeholder="123 or {{userIdVariable}}"
                        helperText="Static value or variable"
                      />
                    </Box>
                    
                    {/* Description field on its own line */}
                    <TextField
                      size="small"
                      label="Description (Optional)"
                      value={param.description}
                      onChange={(e) => {
                        const updatedParams = [...step.config.pathParams];
                        updatedParams[index] = { ...param, description: e.target.value };
                        handleConfigChange('pathParams', updatedParams);
                      }}
                      fullWidth
                      placeholder="Brief description of this parameter"
                      helperText="Help text for documentation"
                    />
                    
                    {/* Preview of how this will look in the URL */}
                    {param.key && (
                      <Box sx={{ 
                        p: 1.5, 
                        backgroundColor: '#e8f4fd', 
                        borderRadius: 1, 
                        border: '1px solid #b3d9ff' 
                      }}>
                        <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                          URL Preview:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1976d2' }}>
                          .../:
                          <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>{param.key}</span>
                          /...
                          {param.value && (
                            <span style={{ color: '#388e3c', marginLeft: 8 }}>
                              → {param.value}
                            </span>
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Card>
              ))}
              
              {(!step.config?.pathParams || step.config.pathParams.length === 0) && (
                <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
                  No path parameters defined. Click "Add Parameter" to start.
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Query Parameters */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">❓ Query Parameters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Add query parameters to your request (e.g., ?page=1&limit=10)
            </Typography>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Query Parameters
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const currentParams = step.config?.queryParams || [];
                    const newParam = {
                      id: `query-param-${Date.now()}`,
                      key: '',
                      value: '',
                      description: '',
                      enabled: true
                    };
                    handleConfigChange('queryParams', [...currentParams, newParam]);
                  }}
                >
                  Add Parameter
                </Button>
              </Box>
              
              {(step.config?.queryParams || []).map((param, index) => (
                <Card key={param.id} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Header with parameter name, enabled toggle, and delete button */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                          Query Parameter #{index + 1}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={param.enabled !== false}
                              onChange={(e) => {
                                const updatedParams = [...step.config.queryParams];
                                updatedParams[index] = { ...param, enabled: e.target.checked };
                                handleConfigChange('queryParams', updatedParams);
                              }}
                            />
                          }
                          label={param.enabled !== false ? "Enabled" : "Disabled"}
                          sx={{ margin: 0, ml: 1 }}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const updatedParams = step.config.queryParams.filter((_, i) => i !== index);
                          handleConfigChange('queryParams', updatedParams);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    
                    {/* Main fields in a row */}
                    <Box display="flex" gap={2}>
                      <TextField
                        size="small"
                        label="Parameter Name"
                        value={param.key}
                        onChange={(e) => {
                          const updatedParams = [...step.config.queryParams];
                          updatedParams[index] = { ...param, key: e.target.value };
                          handleConfigChange('queryParams', updatedParams);
                        }}
                        sx={{ flex: 1 }}
                        placeholder="page"
                        helperText="Query parameter name"
                        disabled={param.enabled === false}
                      />
                      <TextField
                        size="small"
                        label="Value"
                        value={param.value}
                        onChange={(e) => {
                          const updatedParams = [...step.config.queryParams];
                          updatedParams[index] = { ...param, value: e.target.value };
                          handleConfigChange('queryParams', updatedParams);
                        }}
                        sx={{ flex: 1.5 }}
                        placeholder="1 or {{pageVariable}}"
                        helperText="Static value or variable"
                        disabled={param.enabled === false}
                      />
                    </Box>
                    
                    {/* Description field on its own line */}
                    <TextField
                      size="small"
                      label="Description (Optional)"
                      value={param.description}
                      onChange={(e) => {
                        const updatedParams = [...step.config.queryParams];
                        updatedParams[index] = { ...param, description: e.target.value };
                        handleConfigChange('queryParams', updatedParams);
                      }}
                      fullWidth
                      placeholder="Brief description of this parameter"
                      helperText="Help text for documentation"
                      disabled={param.enabled === false}
                    />
                    
                    {/* Preview of how this will look in the URL */}
                    {param.key && param.enabled !== false && (
                      <Box sx={{ 
                        p: 1.5, 
                        backgroundColor: '#e8f4fd', 
                        borderRadius: 1, 
                        border: '1px solid #b3d9ff' 
                      }}>
                        <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                          Query String Preview:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1976d2' }}>
                          ?<span style={{ fontWeight: 'bold', color: '#d32f2f' }}>{param.key}</span>=
                          <span style={{ color: '#388e3c' }}>
                            {param.value || 'value'}
                          </span>
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Disabled state indicator */}
                    {param.enabled === false && (
                      <Box sx={{ 
                        p: 1.5, 
                        backgroundColor: '#fff3e0', 
                        borderRadius: 1, 
                        border: '1px solid #ffcc02' 
                      }}>
                        <Typography variant="caption" color="textSecondary">
                          ⚠️ This parameter is disabled and will not be included in the request
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Card>
              ))}
              
              {(!step.config?.queryParams || step.config.queryParams.length === 0) && (
                <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
                  No query parameters defined. Click "Add Parameter" to start.
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Headers */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📋 Headers</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Add custom headers to your request
            </Typography>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Custom Headers
                </Typography>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const currentHeaders = step.config?.customHeaders || [];
                      const commonHeaders = [
                        { key: 'Content-Type', value: 'application/json' },
                        { key: 'Accept', value: 'application/json' },
                        { key: 'User-Agent', value: 'NeoConverter/1.0' }
                      ];
                      const newHeaders = [...currentHeaders, ...commonHeaders.filter(h => 
                        !currentHeaders.some(ch => ch.key.toLowerCase() === h.key.toLowerCase())
                      )];
                      handleConfigChange('customHeaders', newHeaders.map(h => ({
                        ...h,
                        id: h.id || `header-${Date.now()}-${Math.random()}`,
                        enabled: true,
                        description: h.description || ''
                      })));
                    }}
                  >
                    Add Common
                  </Button>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentHeaders = step.config?.customHeaders || [];
                      const newHeader = {
                        id: `header-${Date.now()}`,
                        key: '',
                        value: '',
                        description: '',
                        enabled: true
                      };
                      handleConfigChange('customHeaders', [...currentHeaders, newHeader]);
                    }}
                  >
                    Add Header
                  </Button>
                </Box>
              </Box>
              
              {(step.config?.customHeaders || []).map((header, index) => (
                <Card key={header.id} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {/* Header with header name, enabled toggle, and delete button */}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                          Header #{index + 1}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={header.enabled !== false}
                              onChange={(e) => {
                                const updatedHeaders = [...step.config.customHeaders];
                                updatedHeaders[index] = { ...header, enabled: e.target.checked };
                                handleConfigChange('customHeaders', updatedHeaders);
                              }}
                            />
                          }
                          label={header.enabled !== false ? "Enabled" : "Disabled"}
                          sx={{ margin: 0, ml: 1 }}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const updatedHeaders = step.config.customHeaders.filter((_, i) => i !== index);
                          handleConfigChange('customHeaders', updatedHeaders);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    
                    {/* Main fields in a row */}
                    <Box display="flex" gap={2}>
                      <TextField
                        size="small"
                        label="Header Name"
                        value={header.key}
                        onChange={(e) => {
                          const updatedHeaders = [...step.config.customHeaders];
                          updatedHeaders[index] = { ...header, key: e.target.value };
                          handleConfigChange('customHeaders', updatedHeaders);
                        }}
                        sx={{ flex: 1 }}
                        placeholder="Content-Type"
                        helperText="HTTP header name"
                        disabled={header.enabled === false}
                      />
                      <TextField
                        size="small"
                        label="Value"
                        value={header.value}
                        onChange={(e) => {
                          const updatedHeaders = [...step.config.customHeaders];
                          updatedHeaders[index] = { ...header, value: e.target.value };
                          handleConfigChange('customHeaders', updatedHeaders);
                        }}
                        sx={{ flex: 2 }}
                        placeholder="application/json or {{contentTypeVariable}}"
                        helperText="Header value or variable"
                        disabled={header.enabled === false}
                      />
                    </Box>
                    
                    {/* Description field on its own line */}
                    <TextField
                      size="small"
                      label="Description (Optional)"
                      value={header.description}
                      onChange={(e) => {
                        const updatedHeaders = [...step.config.customHeaders];
                        updatedHeaders[index] = { ...header, description: e.target.value };
                        handleConfigChange('customHeaders', updatedHeaders);
                      }}
                      fullWidth
                      placeholder="Brief description of this header"
                      helperText="Help text for documentation"
                      disabled={header.enabled === false}
                    />
                    
                    {/* Preview of how this will look in the request */}
                    {header.key && header.enabled !== false && (
                      <Box sx={{ 
                        p: 1.5, 
                        backgroundColor: '#e8f4fd', 
                        borderRadius: 1, 
                        border: '1px solid #b3d9ff' 
                      }}>
                        <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                          Header Preview:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1976d2' }}>
                          <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>{header.key}</span>: 
                          <span style={{ color: '#388e3c', marginLeft: 4 }}>
                            {header.value || 'value'}
                          </span>
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Disabled state indicator */}
                    {header.enabled === false && (
                      <Box sx={{ 
                        p: 1.5, 
                        backgroundColor: '#fff3e0', 
                        borderRadius: 1, 
                        border: '1px solid #ffcc02' 
                      }}>
                        <Typography variant="caption" color="textSecondary">
                          ⚠️ This header is disabled and will not be included in the request
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Card>
              ))}
              
              {(!step.config?.customHeaders || step.config.customHeaders.length === 0) && (
                <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
                  No custom headers defined. Click "Add Header" to start.
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Request Body (for POST, PUT, PATCH) */}
      {['POST', 'PUT', 'PATCH'].includes(step.config?.method) && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">📄 Request Body</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Body Type</InputLabel>
                <Select
                  value={step.config?.bodyType || 'raw'}
                  onChange={(e) => handleConfigChange('bodyType', e.target.value)}
                >
                  <MenuItem value="raw">Raw</MenuItem>
                  <MenuItem value="form_data">Form Data</MenuItem>
                  <MenuItem value="url_encoded">URL Encoded Form</MenuItem>
                  <MenuItem value="binary">Binary</MenuItem>
                  <MenuItem value="graphql">GraphQL</MenuItem>
                </Select>
                <FormHelperText>
                  {(step.config?.bodyType || 'raw') === 'raw' && 'Send raw data (JSON, XML, text, etc.)'}
                  {step.config?.bodyType === 'form_data' && 'Send form data with files support (multipart/form-data)'}
                  {step.config?.bodyType === 'url_encoded' && 'Send form data as URL encoded (application/x-www-form-urlencoded)'}
                  {step.config?.bodyType === 'binary' && 'Send binary data (files, images, etc.)'}
                  {step.config?.bodyType === 'graphql' && 'Send GraphQL queries and mutations'}
                </FormHelperText>
              </FormControl>

              {/* Raw Body */}
              {(step.config?.bodyType || 'raw') === 'raw' && (
                <Box display="flex" flexDirection="column" gap={2}>
                  <FormControl fullWidth>
                    <InputLabel>Content Type</InputLabel>
                    <Select
                      value={step.config?.rawBodyContentType || 'application/json'}
                      onChange={(e) => handleConfigChange('rawBodyContentType', e.target.value)}
                    >
                      <MenuItem value="application/json">JSON</MenuItem>
                      <MenuItem value="application/xml">XML</MenuItem>
                      <MenuItem value="text/plain">Text</MenuItem>
                      <MenuItem value="text/html">HTML</MenuItem>
                      <MenuItem value="application/javascript">JavaScript</MenuItem>
                      <MenuItem value="text/css">CSS</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Raw Body Content"
                    value={step.config?.rawBody || ''}
                    onChange={(e) => handleConfigChange('rawBody', e.target.value)}
                    multiline
                    rows={8}
                    fullWidth
                    placeholder={(step.config?.rawBodyContentType || 'application/json') === 'application/json' ? 
                      '{\n  "name": "{{productName}}",\n  "quantity": {{quantity}},\n  "price": {{price}}\n}' :
                      step.config?.rawBodyContentType === 'application/xml' ? 
                      '<product>\n  <name>{{productName}}</name>\n  <quantity>{{quantity}}</quantity>\n</product>' :
                      'Enter your raw body content here...'
                    }
                    helperText="Use {{variable}} syntax for dynamic values"
                  />
                </Box>
              )}

              {/* Form Data */}
              {step.config?.bodyType === 'form_data' && (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography variant="body2" color="textSecondary">
                    Form data fields (multipart/form-data)
                  </Typography>
                  
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Form Fields
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const currentFields = step.config?.formFields || [];
                          const newField = {
                            id: `form-field-${Date.now()}`,
                            key: '',
                            value: '',
                            type: 'text',
                            description: '',
                            enabled: true
                          };
                          handleConfigChange('formFields', [...currentFields, newField]);
                        }}
                      >
                        Add Field
                      </Button>
                    </Box>
                    
                    {(step.config?.formFields || []).map((field, index) => (
                      <Card key={field.id} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                        <Box display="flex" flexDirection="column" gap={2}>
                          {/* Header with field info, enabled toggle, and delete button */}
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                Form Field #{index + 1}
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={field.enabled !== false}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.formFields];
                                      updatedFields[index] = { ...field, enabled: e.target.checked };
                                      handleConfigChange('formFields', updatedFields);
                                    }}
                                  />
                                }
                                label={field.enabled !== false ? "Enabled" : "Disabled"}
                                sx={{ margin: 0, ml: 1 }}
                              />
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updatedFields = step.config.formFields.filter((_, i) => i !== index);
                                handleConfigChange('formFields', updatedFields);
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                          
                          {/* Main fields in a row */}
                          <Box display="flex" gap={2}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Field Type</InputLabel>
                              <Select
                                value={field.type || 'text'}
                                onChange={(e) => {
                                  const updatedFields = [...step.config.formFields];
                                  updatedFields[index] = { ...field, type: e.target.value };
                                  handleConfigChange('formFields', updatedFields);
                                }}
                                label="Field Type"
                                disabled={field.enabled === false}
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="file">File</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              label="Field Name"
                              value={field.key}
                              onChange={(e) => {
                                const updatedFields = [...step.config.formFields];
                                updatedFields[index] = { ...field, key: e.target.value };
                                handleConfigChange('formFields', updatedFields);
                              }}
                              sx={{ flex: 1 }}
                              placeholder="fieldName"
                              helperText="Form field name"
                              disabled={field.enabled === false}
                            />
                            <TextField
                              size="small"
                              label={field.type === 'file' ? 'File Path/Variable' : 'Value'}
                              value={field.value}
                              onChange={(e) => {
                                const updatedFields = [...step.config.formFields];
                                updatedFields[index] = { ...field, value: e.target.value };
                                handleConfigChange('formFields', updatedFields);
                              }}
                              sx={{ flex: 1.5 }}
                              placeholder={field.type === 'file' ? '{{fileVariable}} or /path/to/file' : 'Field value or {{variable}}'}
                              helperText={field.type === 'file' ? 'File source' : 'Field value'}
                              disabled={field.enabled === false}
                            />
                          </Box>
                          
                          {/* Preview of how this will be sent */}
                          {field.key && field.enabled !== false && (
                            <Box sx={{ 
                              p: 1.5, 
                              backgroundColor: '#e8f4fd', 
                              borderRadius: 1, 
                              border: '1px solid #b3d9ff' 
                            }}>
                              <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                Form Data Preview:
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1976d2' }}>
                                <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>{field.key}</span>: 
                                <span style={{ color: '#388e3c', marginLeft: 4 }}>
                                  {field.type === 'file' ? `[${field.type}] ${field.value || 'file'}` : (field.value || 'value')}
                                </span>
                                <span style={{ color: '#666', fontSize: '0.8em', marginLeft: 8 }}>
                                  ({field.type})
                                </span>
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Disabled state indicator */}
                          {field.enabled === false && (
                            <Box sx={{ 
                              p: 1.5, 
                              backgroundColor: '#fff3e0', 
                              borderRadius: 1, 
                              border: '1px solid #ffcc02' 
                            }}>
                              <Typography variant="caption" color="textSecondary">
                                ⚠️ This field is disabled and will not be included in the form data
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Card>
                    ))}
                    
                    {(!step.config?.formFields || step.config.formFields.length === 0) && (
                      <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
                        No form fields defined. Click "Add Field" to start.
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* URL Encoded Form */}
              {step.config?.bodyType === 'url_encoded' && (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography variant="body2" color="textSecondary">
                    URL encoded form fields (application/x-www-form-urlencoded)
                  </Typography>
                  
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        URL Encoded Fields
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const currentFields = step.config?.urlEncodedFields || [];
                          const newField = {
                            id: `url-encoded-${Date.now()}`,
                            key: '',
                            value: '',
                            description: '',
                            enabled: true
                          };
                          handleConfigChange('urlEncodedFields', [...currentFields, newField]);
                        }}
                      >
                        Add Field
                      </Button>
                    </Box>
                    
                    {(step.config?.urlEncodedFields || []).map((field, index) => (
                      <Card key={field.id} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                        <Box display="flex" flexDirection="column" gap={2}>
                          {/* Header with field info, enabled toggle, and delete button */}
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                URL Encoded Field #{index + 1}
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={field.enabled !== false}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.urlEncodedFields];
                                      updatedFields[index] = { ...field, enabled: e.target.checked };
                                      handleConfigChange('urlEncodedFields', updatedFields);
                                    }}
                                  />
                                }
                                label={field.enabled !== false ? "Enabled" : "Disabled"}
                                sx={{ margin: 0, ml: 1 }}
                              />
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updatedFields = step.config.urlEncodedFields.filter((_, i) => i !== index);
                                handleConfigChange('urlEncodedFields', updatedFields);
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                          
                          {/* Main fields in a row */}
                          <Box display="flex" gap={2}>
                            <TextField
                              size="small"
                              label="Field Name"
                              value={field.key}
                              onChange={(e) => {
                                const updatedFields = [...step.config.urlEncodedFields];
                                updatedFields[index] = { ...field, key: e.target.value };
                                handleConfigChange('urlEncodedFields', updatedFields);
                              }}
                              sx={{ flex: 1 }}
                              placeholder="username"
                              helperText="Form field name"
                              disabled={field.enabled === false}
                            />
                            <TextField
                              size="small"
                              label="Value"
                              value={field.value}
                              onChange={(e) => {
                                const updatedFields = [...step.config.urlEncodedFields];
                                updatedFields[index] = { ...field, value: e.target.value };
                                handleConfigChange('urlEncodedFields', updatedFields);
                              }}
                              sx={{ flex: 1.5 }}
                              placeholder="john_doe or {{usernameVariable}}"
                              helperText="Field value or variable"
                              disabled={field.enabled === false}
                            />
                          </Box>
                          
                          {/* Description field on its own line */}
                          <TextField
                            size="small"
                            label="Description (Optional)"
                            value={field.description}
                            onChange={(e) => {
                              const updatedFields = [...step.config.urlEncodedFields];
                              updatedFields[index] = { ...field, description: e.target.value };
                              handleConfigChange('urlEncodedFields', updatedFields);
                            }}
                            fullWidth
                            placeholder="Brief description of this field"
                            helperText="Help text for documentation"
                            disabled={field.enabled === false}
                          />
                          
                          {/* Preview of how this will be sent */}
                          {field.key && field.enabled !== false && (
                            <Box sx={{ 
                              p: 1.5, 
                              backgroundColor: '#e8f4fd', 
                              borderRadius: 1, 
                              border: '1px solid #b3d9ff' 
                            }}>
                              <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                URL Encoded Preview:
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1976d2' }}>
                                <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>{field.key}</span>=
                                <span style={{ color: '#388e3c' }}>
                                  {encodeURIComponent(field.value || 'value')}
                                </span>
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Disabled state indicator */}
                          {field.enabled === false && (
                            <Box sx={{ 
                              p: 1.5, 
                              backgroundColor: '#fff3e0', 
                              borderRadius: 1, 
                              border: '1px solid #ffcc02' 
                            }}>
                              <Typography variant="caption" color="textSecondary">
                                ⚠️ This field is disabled and will not be included in the URL encoded data
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Card>
                    ))}
                    
                    {(!step.config?.urlEncodedFields || step.config.urlEncodedFields.length === 0) && (
                      <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
                        No URL encoded fields defined. Click "Add Field" to start.
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Binary Body */}
              {step.config?.bodyType === 'binary' && (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography variant="body2" color="textSecondary">
                    Binary data (files, images, etc.)
                  </Typography>
                  <TextField
                    label="Binary Data Source"
                    value={step.config?.binarySource || ''}
                    onChange={(e) => handleConfigChange('binarySource', e.target.value)}
                    fullWidth
                    placeholder="{{fileVariable}} or /path/to/file"
                    helperText="Variable containing binary data or file path"
                  />
                  <TextField
                    label="Content Type"
                    value={step.config?.binaryContentType || ''}
                    onChange={(e) => handleConfigChange('binaryContentType', e.target.value)}
                    fullWidth
                    placeholder="image/jpeg, application/pdf, etc."
                    helperText="MIME type of the binary data"
                  />
                </Box>
              )}

              {/* GraphQL Body */}
              {step.config?.bodyType === 'graphql' && (
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="GraphQL Query"
                    value={step.config?.graphqlQuery || ''}
                    onChange={(e) => handleConfigChange('graphqlQuery', e.target.value)}
                    multiline
                    rows={6}
                    fullWidth
                    placeholder={`query GetUser($id: ID!) {
  user(id: $id) {
    name
    email
    posts {
      title
      content
    }
  }
}`}
                    helperText="Enter your GraphQL query or mutation"
                  />
                  <TextField
                    label="Variables (JSON)"
                    value={step.config?.graphqlVariables || ''}
                    onChange={(e) => handleConfigChange('graphqlVariables', e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                    placeholder='{"id": "{{userId}}"}'
                    helperText="GraphQL variables in JSON format"
                  />
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📥 Response Mapping</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Map API response data to variables. Support for nested objects, arrays, and complex data structures.
            </Typography>
            
            <TextField
              label="Raw Response Variable"
              value={step.config?.responseVariable || 'apiResponse'}
              onChange={(e) => handleConfigChange('responseVariable', e.target.value)}
              fullWidth
              helperText="Variable to store the complete API response"
            />
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Response Field Mappings
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const currentMappings = step.config?.responseMappings || [];
                    const newMapping = {
                      id: `mapping-${Date.now()}`,
                      variableName: '',
                      jsonPath: '',
                      dataType: 'string',
                      description: '',
                      isArray: false,
                      arrayItemPath: ''
                    };
                    handleConfigChange('responseMappings', [...currentMappings, newMapping]);
                  }}
                >
                  Add Field Mapping
                </Button>
              </Box>
              
              {(step.config?.responseMappings || []).map((mapping, index) => (
                <Card key={mapping.id} sx={{ mb: 2, p: 2, backgroundColor: '#f0f8ff', border: '1px solid #e1e7ff' }}>
                  <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
                    <Box flexGrow={1}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="Variable Name"
                            value={mapping.variableName}
                            onChange={(e) => {
                              const updatedMappings = [...step.config.responseMappings];
                              updatedMappings[index] = { ...mapping, variableName: e.target.value };
                              handleConfigChange('responseMappings', updatedMappings);
                            }}
                            fullWidth
                            placeholder="e.g., productName, products, orderItems"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="JSON Path"
                            value={mapping.jsonPath}
                            onChange={(e) => {
                              const updatedMappings = [...step.config.responseMappings];
                              updatedMappings[index] = { ...mapping, jsonPath: e.target.value };
                              handleConfigChange('responseMappings', updatedMappings);
                            }}
                            fullWidth
                            placeholder="e.g., data.product.name, data.products, orderItems"
                            helperText="Use dot notation for nested objects"
                          />
                        </Grid>
                        
                        <Grid item xs={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Data Type</InputLabel>
                            <Select
                              value={mapping.dataType}
                              onChange={(e) => {
                                const updatedMappings = [...step.config.responseMappings];
                                updatedMappings[index] = { ...mapping, dataType: e.target.value };
                                handleConfigChange('responseMappings', updatedMappings);
                              }}
                              label="Data Type"
                            >
                              <MenuItem value="string">String</MenuItem>
                              <MenuItem value="number">Number</MenuItem>
                              <MenuItem value="boolean">Boolean</MenuItem>
                              <MenuItem value="object">Object</MenuItem>
                              <MenuItem value="array">Array</MenuItem>
                              <MenuItem value="date">Date</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={mapping.isArray || false}
                                onChange={(e) => {
                                  const updatedMappings = [...step.config.responseMappings];
                                  updatedMappings[index] = { ...mapping, isArray: e.target.checked };
                                  handleConfigChange('responseMappings', updatedMappings);
                                }}
                              />
                            }
                            label="Is Array"
                          />
                        </Grid>
                        
                        {mapping.isArray && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                                📋 Array Configuration
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                size="small"
                                label="Array Item Properties (comma-separated)"
                                value={mapping.arrayItemPath || ''}
                                onChange={(e) => {
                                  const updatedMappings = [...step.config.responseMappings];
                                  updatedMappings[index] = { ...mapping, arrayItemPath: e.target.value };
                                  handleConfigChange('responseMappings', updatedMappings);
                                }}
                                fullWidth
                                placeholder="name, sku, price, quantity"
                                helperText="Properties to extract from each array item for easy access"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1 }}>
                                  💡 Array Usage Examples:
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: '#1565c0', fontFamily: 'monospace' }}>
                                  • Access full array: {mapping.variableName || 'arrayVariable'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: '#1565c0', fontFamily: 'monospace' }}>
                                  • Access array items: {mapping.variableName || 'arrayVariable'}[*].{mapping.arrayItemPath?.split(',')[0]?.trim() || 'property'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: '#1565c0', fontFamily: 'monospace' }}>
                                  • Access specific item: {mapping.variableName || 'arrayVariable'}[0].{mapping.arrayItemPath?.split(',')[0]?.trim() || 'property'}
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                        
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="Description"
                            value={mapping.description}
                            onChange={(e) => {
                              const updatedMappings = [...step.config.responseMappings];
                              updatedMappings[index] = { ...mapping, description: e.target.value };
                              handleConfigChange('responseMappings', updatedMappings);
                            }}
                            fullWidth
                            placeholder="What this variable contains (e.g., 'List of products in the order')"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updatedMappings = step.config.responseMappings.filter((_, i) => i !== index);
                        handleConfigChange('responseMappings', updatedMappings);
                      }}
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              ))}
              
              {(!step.config?.responseMappings || step.config.responseMappings.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2">
                    No response mappings defined. Add mappings to extract specific data from API responses.
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Quick Templates for Response Mapping */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                🚀 Quick Response Templates
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const purchaseOrderMappings = [
                      { id: 'po-map-1', variableName: 'orderNumber', jsonPath: 'data.orderNumber', dataType: 'string', description: 'Purchase order number', isArray: false },
                      { id: 'po-map-2', variableName: 'vendor', jsonPath: 'data.vendor.name', dataType: 'string', description: 'Vendor company name', isArray: false },
                      { id: 'po-map-3', variableName: 'orderItems', jsonPath: 'data.items', dataType: 'array', description: 'List of items in the purchase order', isArray: true, arrayItemPath: 'productName, sku, expectedQuantity, unitPrice' },
                      { id: 'po-map-4', variableName: 'totalAmount', jsonPath: 'data.totalAmount', dataType: 'number', description: 'Total order amount', isArray: false }
                    ];
                    handleConfigChange('responseMappings', purchaseOrderMappings);
                  }}
                >
                  📋 Purchase Order API
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const productCatalogMappings = [
                      { id: 'cat-map-1', variableName: 'products', jsonPath: 'products', dataType: 'array', description: 'Product catalog listing', isArray: true, arrayItemPath: 'name, sku, price, description, category' },
                      { id: 'cat-map-2', variableName: 'totalCount', jsonPath: 'totalCount', dataType: 'number', description: 'Total number of products', isArray: false },
                      { id: 'cat-map-3', variableName: 'categories', jsonPath: 'categories', dataType: 'array', description: 'Available product categories', isArray: true, arrayItemPath: 'name, id' }
                    ];
                    handleConfigChange('responseMappings', productCatalogMappings);
                  }}
                >
                  📦 Product Catalog
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const userDataMappings = [
                      { id: 'user-map-1', variableName: 'userId', jsonPath: 'user.id', dataType: 'string', description: 'Unique user identifier', isArray: false },
                      { id: 'user-map-2', variableName: 'userName', jsonPath: 'user.name', dataType: 'string', description: 'User full name', isArray: false },
                      { id: 'user-map-3', variableName: 'userPermissions', jsonPath: 'user.permissions', dataType: 'array', description: 'User access permissions', isArray: true, arrayItemPath: 'resource, action, allowed' }
                    ];
                    handleConfigChange('responseMappings', userDataMappings);
                  }}
                >
                  👤 User Data
                </Button>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">❌ Error Response Mapping</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Configure how to handle and extract data from error responses.
            </Typography>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Error Response Mappings
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const currentErrorMappings = step.config?.errorResponseMappings || [];
                    const newMapping = {
                      id: `error-mapping-${Date.now()}`,
                      variableName: '',
                      jsonPath: '',
                      statusCodes: '400,404,500',
                      description: ''
                    };
                    handleConfigChange('errorResponseMappings', [...currentErrorMappings, newMapping]);
                  }}
                >
                  Add Error Mapping
                </Button>
              </Box>
              
              {(step.config?.errorResponseMappings || []).map((mapping, index) => (
                <Card key={mapping.id} sx={{ mb: 2, p: 2, backgroundColor: '#fff5f5', border: '1px solid #fed7d7' }}>
                  <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
                    <Box flexGrow={1}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="Error Variable Name"
                            value={mapping.variableName}
                            onChange={(e) => {
                              const updatedMappings = [...step.config.errorResponseMappings];
                              updatedMappings[index] = { ...mapping, variableName: e.target.value };
                              handleConfigChange('errorResponseMappings', updatedMappings);
                            }}
                            fullWidth
                            placeholder="e.g., errorMessage, validationErrors"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="JSON Path in Error Response"
                            value={mapping.jsonPath}
                            onChange={(e) => {
                              const updatedMappings = [...step.config.errorResponseMappings];
                              updatedMappings[index] = { ...mapping, jsonPath: e.target.value };
                              handleConfigChange('errorResponseMappings', updatedMappings);
                            }}
                            fullWidth
                            placeholder="e.g., error.message, errors"
                          />
                        </Grid>
                        
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="Status Codes (comma-separated)"
                            value={mapping.statusCodes}
                            onChange={(e) => {
                              const updatedMappings = [...step.config.errorResponseMappings];
                              updatedMappings[index] = { ...mapping, statusCodes: e.target.value };
                              handleConfigChange('errorResponseMappings', updatedMappings);
                            }}
                            fullWidth
                            placeholder="400,404,500"
                            helperText="Apply this mapping for these HTTP status codes"
                          />
                        </Grid>
                        
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="Description"
                            value={mapping.description}
                            onChange={(e) => {
                              const updatedMappings = [...step.config.errorResponseMappings];
                              updatedMappings[index] = { ...mapping, description: e.target.value };
                              handleConfigChange('errorResponseMappings', updatedMappings);
                            }}
                            fullWidth
                            placeholder="What this error variable contains"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updatedMappings = step.config.errorResponseMappings.filter((_, i) => i !== index);
                        handleConfigChange('errorResponseMappings', updatedMappings);
                      }}
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              ))}
              
              {(!step.config?.errorResponseMappings || step.config.errorResponseMappings.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2">
                    No error response mappings defined. Add mappings to extract error details.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">⚙️ Request Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Timeout (seconds)"
              type="number"
              value={step.config?.timeout || 30}
              onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
              fullWidth
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.enableRetry || true}
                  onChange={(e) => handleConfigChange('enableRetry', e.target.checked)}
                />
              }
              label="Enable automatic retry"
            />

            <TextField
              label="Retry Attempts"
              type="number"
              value={step.config?.retryAttempts || 3}
              onChange={(e) => handleConfigChange('retryAttempts', parseInt(e.target.value))}
              fullWidth
              disabled={!step.config?.enableRetry}
              helperText="Number of retry attempts on failure"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.saveRawResponse || false}
                  onChange={(e) => handleConfigChange('saveRawResponse', e.target.checked)}
                />
              }
              label="Save raw response for debugging"
            />

            {/* Show mapped variables summary */}
            {step.config?.responseMappings && step.config.responseMappings.length > 0 && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                  📋 Mapped Variables Summary
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', mb: 1 }}>
                  These variables will be available for use in subsequent steps:
                </Typography>
                {step.config.responseMappings.map((mapping, index) => (
                  <Typography key={index} variant="caption" sx={{ display: 'block', color: '#2e7d32', fontFamily: 'monospace', mb: 0.5 }}>
                    • {mapping.variableName}: {mapping.description || mapping.jsonPath}
                    {mapping.isArray && ' (Array - use [*] for all items)'}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderAdvancedCollectInputConfig = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Configure advanced input collection with custom forms, data comparison tables, and validation capabilities.
      </Typography>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🎯 Interface Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Interface Type</InputLabel>
              <Select
                value={step.config?.interfaceType || 'form'}
                onChange={(e) => handleConfigChange('interfaceType', e.target.value)}
              >
                <MenuItem value="form">Simple Form</MenuItem>
                <MenuItem value="message">Message with Buttons</MenuItem>
                <MenuItem value="data_comparison">Data Comparison Table</MenuItem>
                <MenuItem value="table_editor">Data Table Editor</MenuItem>
                <MenuItem value="table_with_header">Table with Header Fields</MenuItem>
                <MenuItem value="table_builder">Table Builder (Build from Array)</MenuItem>
                <MenuItem value="modal">Modal Dialog</MenuItem>
                <MenuItem value="wizard">Multi-step Wizard</MenuItem>
                <MenuItem value="barcode_scanner">📱 Barcode/QR Scanner</MenuItem>
                <MenuItem value="file_upload">📁 File Upload</MenuItem>
                <MenuItem value="record_search">🔍 Record Search</MenuItem>
              </Select>
              <FormHelperText>
                {(!step.config?.interfaceType || step.config?.interfaceType === 'form') && "Perfect for collecting single fields like Purchase Order number"}
                {step.config?.interfaceType === 'message' && "Display a custom message with optional action buttons. If no action buttons are configured, a submit button with customizable text will be shown instead."}
                {step.config?.interfaceType === 'data_comparison' && "Ideal for comparing extracted data with server data side-by-side"}
                {step.config?.interfaceType === 'table_editor' && "Best for editing tabular data with multiple rows"}
                {step.config?.interfaceType === 'table_with_header' && "Perfect for showing context fields (like PO number) above a data table"}
                {step.config?.interfaceType === 'table_builder' && "Start with an empty table, add rows by selecting items from a source array, and auto-populate columns from the selected item"}
                {step.config?.interfaceType === 'modal' && "Show input form in a popup dialog"}
                {step.config?.interfaceType === 'wizard' && "Multi-step guided input process"}
                {step.config?.interfaceType === 'barcode_scanner' && "Scan barcodes or QR codes using the device camera - works offline!"}
                {step.config?.interfaceType === 'file_upload' && "Dedicated file upload interface with drag & drop, OCR processing, and S3 storage options"}
                {step.config?.interfaceType === 'record_search' && "Search for records using optional form fields, display results in a table, and select a row to map values to variables"}
              </FormHelperText>
            </FormControl>
            
            <TextField
              label="Title"
              value={step.config?.title || ''}
              onChange={(e) => handleConfigChange('title', e.target.value)}
              fullWidth
              placeholder="e.g., Enter Purchase Order, Review & Confirm Data"
            />
            
            {/* Message field for Message with Buttons interface, Instructions for others */}
            {step.config?.interfaceType === 'message' ? (
              <>
                <TextField
                  label="Message"
                  value={step.config?.message || ''}
                  onChange={(e) => handleConfigChange('message', e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="e.g., Please select one of the options below to continue"
                  helperText="The main message to display to users"
                />
                <TextField
                  label="Instructions"
                  value={step.config?.instructions || ''}
                  onChange={(e) => handleConfigChange('instructions', e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  placeholder="e.g., Technical instructions or additional context (optional)"
                  helperText="Optional additional instructions or context for users"
                />
              </>
            ) : (
              <TextField
                label="Instructions"
                value={step.config?.instructions || ''}
                onChange={(e) => handleConfigChange('instructions', e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="e.g., Please enter the Purchase Order number for this delivery"
              />
            )}

            {/* Submit Button Text - show for all interfaces including message */}
            <TextField
              label="Submit Button Text"
              value={step.config?.submitButtonText || 'Continue'}
              onChange={(e) => handleConfigChange('submitButtonText', e.target.value)}
              fullWidth
              placeholder="e.g., Continue, Submit, Confirm"
              helperText={
                step.config?.interfaceType === 'message' 
                  ? "Text for the submit button when no action buttons are configured"
                  : "Text for the main submit button"
              }
            />

            {/* Simple Form Default Output Variable */}
            {(!step.config?.interfaceType || step.config?.interfaceType === 'form') && (
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Default Output Variable"
                  value={step.config?.defaultOutputVariable || 'userInput'}
                  onChange={(e) => handleConfigChange('defaultOutputVariable', e.target.value)}
                  fullWidth
                  placeholder="e.g., userInput, purchaseOrder, comments"
                  helperText="Variable name for simple text input (when no custom form fields are configured)"
                />
                
                {/* Character Limit Configuration for Simple Form Default Input */}
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#fafafa' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Default Input Field Settings
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                    These settings apply to the fallback text input when no custom form fields are configured
                  </Typography>
                  
                  <Box display="flex" gap={2} alignItems="center">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={step.config?.enableCharacterLimit || false}
                          onChange={(e) => {
                            handleConfigChange('enableCharacterLimit', e.target.checked);
                            if (!e.target.checked) {
                              handleConfigChange('maxCharacters', undefined);
                            }
                          }}
                        />
                      }
                      label="Enable Character Limit"
                    />
                    
                    {step.config?.enableCharacterLimit && (
                      <TextField
                        size="small"
                        label="Max Characters"
                        type="number"
                        value={step.config?.maxCharacters || ''}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || undefined;
                          handleConfigChange('maxCharacters', value);
                        }}
                        inputProps={{
                          min: 1,
                          max: 10000
                        }}
                        sx={{ width: 150 }}
                        placeholder="e.g., 500"
                        helperText="1-10000 characters"
                      />
                    )}
                  </Box>
                  
                  {step.config?.enableCharacterLimit && step.config?.maxCharacters && (
                    <Box mt={1}>
                      <Typography variant="caption" color="textSecondary">
                        Users will see a character counter showing "{step.config.maxCharacters} characters remaining" as they type
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>


      {/* Simple Form Configuration */}
      {(!step.config?.interfaceType || step.config?.interfaceType === 'form') && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">📝 Form Fields Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Define custom input fields for your form. Perfect for collecting missing data like Purchase Order numbers.
              </Typography>
              
              <Box>
                {/* Cascading Fields Toggle */}
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={step.config?.enableCascadingFields || false}
                        onChange={(e) => handleConfigChange('enableCascadingFields', e.target.checked)}
                      />
                    }
                    label="Enable Cascading Fields (Progressive Disclosure)"
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                    When enabled, fields appear one at a time. Each field is only shown after the previous field is filled. 
                    For autocomplete fields that depend on previous fields, data loading is automatically delayed until dependencies are filled.
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Form Fields
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentFields = step.config?.formFields || [];
                      const newField = {
                        id: `field-${Date.now()}`,
                        name: '',
                        label: '',
                        type: 'text',
                        required: true,
                        placeholder: '',
                        helperText: '',
                        outputVariable: ''
                      };
                      handleConfigChange('formFields', [...currentFields, newField]);
                    }}
                  >
                    Add Field
                  </Button>
                </Box>
                
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleFormFieldsDragEnd}
                >
                  <SortableContext 
                    items={step.config?.formFields || []}
                    strategy={verticalListSortingStrategy}
                  >
                    {(step.config?.formFields || []).map((field, index) => (
                      <SortableFormFieldCard
                        key={field.id}
                        field={field}
                        index={index}
                        onFieldChange={(updatedField) => {
                          const updatedFields = [...step.config.formFields];
                          updatedFields[index] = updatedField;
                          handleConfigChange('formFields', updatedFields);
                        }}
                        onFieldDelete={(idx) => {
                          const updatedFields = step.config.formFields.filter((_, i) => i !== idx);
                          handleConfigChange('formFields', updatedFields);
                        }}
                      >
                        <Box flexGrow={1}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Field Name"
                              value={field.name}
                              onChange={(e) => {
                                const updatedFields = [...step.config.formFields];
                                updatedFields[index] = { ...field, name: e.target.value };
                                handleConfigChange('formFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., purchaseOrder"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Field Label"
                              value={field.label}
                              onChange={(e) => {
                                const updatedFields = [...step.config.formFields];
                                updatedFields[index] = { ...field, label: e.target.value };
                                handleConfigChange('formFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., Purchase Order Number"
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Field Type</InputLabel>
                              <Select
                                value={field.type}
                                onChange={(e) => {
                                  const updatedFields = [...step.config.formFields];
                                  updatedFields[index] = { ...field, type: e.target.value };
                                  handleConfigChange('formFields', updatedFields);
                                }}
                                label="Field Type"
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="password">Password</MenuItem>
                                <MenuItem value="textarea">Text Area</MenuItem>
                                <MenuItem value="select">Dropdown</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="time">Time</MenuItem>
                                <MenuItem value="checkbox">Checkbox</MenuItem>
                                <MenuItem value="autocomplete">Autocomplete (SQL/API Search)</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Box display="flex" gap={1} alignItems="center">
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={field.required || false}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.formFields];
                                      updatedFields[index] = { ...field, required: e.target.checked };
                                      handleConfigChange('formFields', updatedFields);
                                    }}
                                  />
                                }
                                label="Required"
                              />
                            </Box>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Placeholder"
                              value={field.placeholder}
                              onChange={(e) => {
                                const updatedFields = [...step.config.formFields];
                                updatedFields[index] = { ...field, placeholder: e.target.value };
                                handleConfigChange('formFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., Enter PO number..."
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Output Variable"
                              value={field.outputVariable}
                              onChange={(e) => {
                                const updatedFields = [...step.config.formFields];
                                updatedFields[index] = { ...field, outputVariable: e.target.value };
                                handleConfigChange('formFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., purchaseOrder"
                              helperText="Variable name to store the value"
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              size="small"
                              label="Helper Text"
                              value={field.helperText}
                              onChange={(e) => {
                                const updatedFields = [...step.config.formFields];
                                updatedFields[index] = { ...field, helperText: e.target.value };
                                handleConfigChange('formFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="Additional help text for the user"
                            />
                          </Grid>
                          
                          {/* Default Value for all field types except file */}
                          {field.type !== 'file' && (
                            <Grid item xs={12}>
                              <TextField
                                size="small"
                                label="Default Value (Optional)"
                                value={field.defaultValue || ''}
                                onChange={(e) => {
                                  const updatedFields = [...step.config.formFields];
                                  updatedFields[index] = { ...field, defaultValue: e.target.value };
                                  handleConfigChange('formFields', updatedFields);
                                }}
                                fullWidth
                                placeholder={
                                  field.type === 'autocomplete' 
                                    ? "e.g., {{vendorName}} or static value" 
                                    : "e.g., {{extractedValue}} or static text"
                                }
                                helperText={
                                  field.type === 'autocomplete'
                                    ? "Pre-fill field with a value. Use variables like {{vendorName}} or a static value. For autocomplete, this will pre-populate the field and trigger a search."
                                    : "Pre-fill field with a value. Supports variables like {{extractedValue}} or static text."
                                }
                              />
                            </Grid>
                          )}
                          
                          {/* Options for select fields */}
                          {field.type === 'select' && (
                            <>
                              <Grid item xs={12}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Options Mode</InputLabel>
                                  <Select
                                    value={field.optionsMode || 'simple'}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.formFields];
                                      updatedFields[index] = { ...field, optionsMode: e.target.value };
                                      handleConfigChange('formFields', updatedFields);
                                    }}
                                    label="Options Mode"
                                  >
                                    <MenuItem value="simple">Simple (Comma-separated)</MenuItem>
                                    <MenuItem value="advanced">Advanced (Name-Value Pairs)</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              {(!field.optionsMode || field.optionsMode === 'simple') && (
                                <Grid item xs={12}>
                                  <TextField
                                    size="small"
                                    label="Options (comma-separated)"
                                    value={field.options || ''}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.formFields];
                                      updatedFields[index] = { ...field, options: e.target.value };
                                      handleConfigChange('formFields', updatedFields);
                                    }}
                                    fullWidth
                                    placeholder="Option 1, Option 2, Option 3"
                                    helperText="Comma-separated list of dropdown options"
                                  />
                                </Grid>
                              )}
                              
                              {field.optionsMode === 'advanced' && (
                                <>
                                  <Grid item xs={12}>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Data Source</InputLabel>
                                      <Select
                                        value={field.optionsDataSource || 'manual'}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.formFields];
                                          updatedFields[index] = { ...field, optionsDataSource: e.target.value };
                                          handleConfigChange('formFields', updatedFields);
                                        }}
                                        label="Data Source"
                                      >
                                        <MenuItem value="manual">Manual Entry</MenuItem>
                                        <MenuItem value="variable">From Variable</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  
                                  {(!field.optionsDataSource || field.optionsDataSource === 'manual') && (
                                    <Grid item xs={12}>
                                      <Box>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                          <Typography variant="body2" fontWeight="medium">
                                            Dropdown Options (Name-Value Pairs)
                                          </Typography>
                                          <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                              const updatedFields = [...step.config.formFields];
                                              const advancedOptions = field.advancedOptions || [];
                                              updatedFields[index] = { 
                                                ...field, 
                                                advancedOptions: [...advancedOptions, { name: '', value: '' }] 
                                              };
                                              handleConfigChange('formFields', updatedFields);
                                            }}
                                          >
                                            Add Option
                                          </Button>
                                        </Box>
                                        
                                        {(!field.advancedOptions || field.advancedOptions.length === 0) && (
                                          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 2 }}>
                                            No options added yet. Click "Add Option" to create name-value pairs.
                                          </Typography>
                                        )}
                                        
                                        {(field.advancedOptions || []).map((option, optIndex) => (
                                          <Box key={optIndex} display="flex" gap={1} alignItems="center" mb={1}>
                                            <TextField
                                              size="small"
                                              label="Display Name"
                                              value={option.name || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.formFields];
                                                const advancedOptions = [...(field.advancedOptions || [])];
                                                advancedOptions[optIndex] = { ...advancedOptions[optIndex], name: e.target.value };
                                                updatedFields[index] = { ...field, advancedOptions };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              placeholder="e.g., Apple"
                                              sx={{ flex: 1 }}
                                            />
                                            <TextField
                                              size="small"
                                              label="Value"
                                              value={option.value || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.formFields];
                                                const advancedOptions = [...(field.advancedOptions || [])];
                                                advancedOptions[optIndex] = { ...advancedOptions[optIndex], value: e.target.value };
                                                updatedFields[index] = { ...field, advancedOptions };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              placeholder="e.g., trx52"
                                              sx={{ flex: 1 }}
                                            />
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => {
                                                const updatedFields = [...step.config.formFields];
                                                const advancedOptions = [...(field.advancedOptions || [])];
                                                advancedOptions.splice(optIndex, 1);
                                                updatedFields[index] = { ...field, advancedOptions };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Grid>
                                  )}
                                  
                                  {field.optionsDataSource === 'variable' && (
                                    <>
                                      <Grid item xs={12}>
                                        <TextField
                                          size="small"
                                          label="Variable Name"
                                          value={field.optionsVariable || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.formFields];
                                            updatedFields[index] = { ...field, optionsVariable: e.target.value };
                                            handleConfigChange('formFields', updatedFields);
                                          }}
                                          fullWidth
                                          placeholder="e.g., products"
                                          helperText="Variable containing array of objects"
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          size="small"
                                          label="Display Name Field"
                                          value={field.optionsNameField || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.formFields];
                                            updatedFields[index] = { ...field, optionsNameField: e.target.value };
                                            handleConfigChange('formFields', updatedFields);
                                          }}
                                          fullWidth
                                          placeholder="e.g., [*].name"
                                          helperText="Field path for display name"
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          size="small"
                                          label="Value Field"
                                          value={field.optionsValueField || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.formFields];
                                            updatedFields[index] = { ...field, optionsValueField: e.target.value };
                                            handleConfigChange('formFields', updatedFields);
                                          }}
                                          fullWidth
                                          placeholder="e.g., [*].value"
                                          helperText="Field path for stored value"
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                          <Typography variant="caption" color="info.dark">
                                            <strong>Example:</strong> If you have a variable "products" with value:<br/>
                                            [{`{name:"Apple", value:"trx52"}, {name:"Orange", value:"trx55"}`}]<br/>
                                            Set Variable Name = "products", Display Name Field = "[*].name", Value Field = "[*].value"
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          )}
                          
                          {/* Character Limit Configuration - for text, textarea, email, password fields */}
                          {(field.type === 'text' || field.type === 'textarea' || field.type === 'email' || field.type === 'password') && (
                            <Grid item xs={12}>
                              <Box display="flex" gap={2} alignItems="center">
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={field.enableCharacterLimit || false}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.formFields];
                                        updatedFields[index] = { 
                                          ...field, 
                                          enableCharacterLimit: e.target.checked,
                                          ...(e.target.checked ? {} : { maxCharacters: undefined })
                                        };
                                        handleConfigChange('formFields', updatedFields);
                                      }}
                                    />
                                  }
                                  label="Enable Character Limit"
                                />
                                
                                {field.enableCharacterLimit && (
                                  <TextField
                                    size="small"
                                    label="Max Characters"
                                    type="number"
                                    value={field.maxCharacters || ''}
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value) || undefined;
                                      const updatedFields = [...step.config.formFields];
                                      updatedFields[index] = { ...field, maxCharacters: value };
                                      handleConfigChange('formFields', updatedFields);
                                    }}
                                    inputProps={{
                                      min: 1,
                                      max: 10000
                                    }}
                                    sx={{ width: 150 }}
                                    placeholder="e.g., 100"
                                    helperText="1-10000 characters"
                                  />
                                )}
                              </Box>
                              
                              {field.enableCharacterLimit && field.maxCharacters && (
                                <Box mt={1}>
                                  <Typography variant="caption" color="textSecondary">
                                    Users will see a character counter showing "{field.maxCharacters} characters remaining" as they type
                                  </Typography>
                                </Box>
                              )}
                            </Grid>
                          )}

                          {/* Date Format Configuration - only for date fields */}
                          {field.type === 'date' && (
                            <Grid item xs={12}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Date Format</InputLabel>
                                <Select
                                  value={field.dateFormat || 'MM/dd/yyyy'}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.formFields];
                                    updatedFields[index] = { ...field, dateFormat: e.target.value };
                                    handleConfigChange('formFields', updatedFields);
                                  }}
                                  label="Date Format"
                                >
                                  <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (12/25/2023)</MenuItem>
                                  <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (25/12/2023)</MenuItem>
                                  <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (2023-12-25)</MenuItem>
                                  <MenuItem value="dd.MM.yyyy">dd.MM.yyyy (25.12.2023)</MenuItem>
                                  <MenuItem value="MM-dd-yyyy">MM-dd-yyyy (12-25-2023)</MenuItem>
                                  <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (25-12-2023)</MenuItem>
                                  <MenuItem value="MMM dd, yyyy">MMM dd, yyyy (Dec 25, 2023)</MenuItem>
                                  <MenuItem value="MMMM dd, yyyy">MMMM dd, yyyy (December 25, 2023)</MenuItem>
                                  <MenuItem value="dd MMM yyyy">dd MMM yyyy (25 Dec 2023)</MenuItem>
                                  <MenuItem value="dd MMMM yyyy">dd MMMM yyyy (25 December 2023)</MenuItem>
                                  <MenuItem value="custom">Custom...</MenuItem>
                                </Select>
                              </FormControl>
                              {field.dateFormat === 'custom' && (
                                <TextField
                                  size="small"
                                  label="Custom Date Format"
                                  value={field.customDateFormat || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.formFields];
                                    updatedFields[index] = { 
                                      ...field, 
                                      customDateFormat: e.target.value,
                                      dateFormat: 'custom'
                                    };
                                    handleConfigChange('formFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., DD/MM/YYYY, dd.mm.yyyy, MM-dd-yy"
                                  helperText="Enter custom date format (supports DD, MM, YYYY, dd, mm, yyyy)"
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Grid>
                          )}

                          {/* Time Format Configuration - only for time fields */}
                          {field.type === 'time' && (
                            <Grid item xs={12}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Time Format</InputLabel>
                                <Select
                                  value={field.timeFormat || 'HH:mm:ss'}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.formFields];
                                    updatedFields[index] = { ...field, timeFormat: e.target.value };
                                    handleConfigChange('formFields', updatedFields);
                                  }}
                                  label="Time Format"
                                >
                                  <MenuItem value="HH:mm:ss">24-hour with seconds (14:30:44)</MenuItem>
                                  <MenuItem value="HH:mm">24-hour without seconds (14:30)</MenuItem>
                                  <MenuItem value="hh:mm:ss A">12-hour with seconds (02:30:44 PM)</MenuItem>
                                  <MenuItem value="hh:mm A">12-hour without seconds (02:30 PM)</MenuItem>
                                  <MenuItem value="h:mm A">12-hour no leading zero (2:30 PM)</MenuItem>
                                  <MenuItem value="h:mm:ss A">12-hour no leading zero with seconds (2:30:44 PM)</MenuItem>
                                  <MenuItem value="custom">Custom...</MenuItem>
                                </Select>
                                <FormHelperText>
                                  Choose how to display time values. Input format is HHMMSS (e.g., 035044 = 03:50:44)
                                </FormHelperText>
                              </FormControl>
                              {field.timeFormat === 'custom' && (
                                <TextField
                                  size="small"
                                  label="Custom Time Format"
                                  value={field.customTimeFormat || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.formFields];
                                    updatedFields[index] = { 
                                      ...field, 
                                      customTimeFormat: e.target.value,
                                      timeFormat: 'custom'
                                    };
                                    handleConfigChange('formFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., HH:mm, h:mm A, HH.mm.ss"
                                  helperText="Custom time format (HH=24hr, hh=12hr, mm=minutes, ss=seconds, A=AM/PM)"
                                  sx={{ mt: 1 }}
                                />
                              )}
                              
                              {/* Timezone Configuration */}
                              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                                <InputLabel>Timezone</InputLabel>
                                <Select
                                  value={field.timezone || 'UTC'}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.formFields];
                                    updatedFields[index] = { ...field, timezone: e.target.value };
                                    handleConfigChange('formFields', updatedFields);
                                  }}
                                  label="Timezone"
                                >
                                  <MenuItem value="UTC">UTC (Server Time)</MenuItem>
                                  <MenuItem value="UTC+1">UTC+1 (Central European Time)</MenuItem>
                                  <MenuItem value="UTC+2">UTC+2 (Eastern European Time)</MenuItem>
                                  <MenuItem value="UTC+3">UTC+3 (Moscow Time)</MenuItem>
                                  <MenuItem value="UTC+4">UTC+4 (Gulf Standard Time)</MenuItem>
                                  <MenuItem value="UTC+5">UTC+5 (Pakistan Standard Time)</MenuItem>
                                  <MenuItem value="UTC+6">UTC+6 (Bangladesh Standard Time)</MenuItem>
                                  <MenuItem value="UTC+7">UTC+7 (Indochina Time)</MenuItem>
                                  <MenuItem value="UTC+8">UTC+8 (China Standard Time)</MenuItem>
                                  <MenuItem value="UTC+9">UTC+9 (Japan Standard Time)</MenuItem>
                                  <MenuItem value="UTC+10">UTC+10 (Australian Eastern Time)</MenuItem>
                                  <MenuItem value="UTC+11">UTC+11 (Solomon Islands Time)</MenuItem>
                                  <MenuItem value="UTC+12">UTC+12 (New Zealand Time)</MenuItem>
                                  <MenuItem value="UTC-1">UTC-1 (Cape Verde Time)</MenuItem>
                                  <MenuItem value="UTC-2">UTC-2 (South Georgia Time)</MenuItem>
                                  <MenuItem value="UTC-3">UTC-3 (Argentina Time)</MenuItem>
                                  <MenuItem value="UTC-4">UTC-4 (Atlantic Standard Time)</MenuItem>
                                  <MenuItem value="UTC-5">UTC-5 (Eastern Standard Time)</MenuItem>
                                  <MenuItem value="UTC-6">UTC-6 (Central Standard Time)</MenuItem>
                                  <MenuItem value="UTC-7">UTC-7 (Mountain Standard Time)</MenuItem>
                                  <MenuItem value="UTC-8">UTC-8 (Pacific Standard Time)</MenuItem>
                                  <MenuItem value="UTC-9">UTC-9 (Alaska Standard Time)</MenuItem>
                                  <MenuItem value="UTC-10">UTC-10 (Hawaii Standard Time)</MenuItem>
                                  <MenuItem value="UTC-11">UTC-11 (Samoa Standard Time)</MenuItem>
                                  <MenuItem value="UTC-12">UTC-12 (Baker Island Time)</MenuItem>
                                </Select>
                                <FormHelperText>
                                  Select the timezone for time display. This will convert server time to your local timezone.
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                          )}
                          
                          {/* Autocomplete Configuration */}
                          {field.type === 'autocomplete' && (
                            <Grid item xs={12}>
                              <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                  Autocomplete fields allow searching and selecting data from SQL databases or APIs. 
                                  Configure the data source below.
                                </Typography>
                              </Alert>
                              
                              <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography variant="subtitle2">🔗 Data Source</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box display="flex" flexDirection="column" gap={2}>
                                    <FormControl fullWidth>
                                      <InputLabel>Data Source</InputLabel>
                                      <Select
                                        value={field.autocompleteConfig?.dataSource || 'sql'}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.formFields];
                                          updatedFields[index] = {
                                            ...field,
                                            autocompleteConfig: {
                                              ...field.autocompleteConfig,
                                              dataSource: e.target.value
                                            }
                                          };
                                          handleConfigChange('formFields', updatedFields);
                                        }}
                                        label="Data Source"
                                      >
                                        <MenuItem value="sql">SQL Database</MenuItem>
                                        <MenuItem value="api">API Endpoint</MenuItem>
                                        <MenuItem value="xero">Xero</MenuItem>
                                      </Select>
                                      <FormHelperText>Choose where to fetch autocomplete data from</FormHelperText>
                                    </FormControl>
                                    
                                    <FormControl fullWidth>
                                      <InputLabel>Search Mode</InputLabel>
                                      <Select
                                        value={field.autocompleteConfig?.searchMode || 'realtime'}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.formFields];
                                          updatedFields[index] = {
                                            ...field,
                                            autocompleteConfig: {
                                              ...field.autocompleteConfig,
                                              searchMode: e.target.value
                                            }
                                          };
                                          handleConfigChange('formFields', updatedFields);
                                        }}
                                        label="Search Mode"
                                      >
                                        <MenuItem value="realtime">Real-time (as you type)</MenuItem>
                                        <MenuItem value="manual">Manual (click to search)</MenuItem>
                                        <MenuItem value="hybrid">Hybrid (both)</MenuItem>
                                        <MenuItem value="prepopulated">Prepopulated (load all, filter locally)</MenuItem>
                                      </Select>
                                      <FormHelperText>
                                        {field.autocompleteConfig?.searchMode === 'prepopulated' 
                                          ? 'Loads all data on mount, filters client-side (best for <500 items)'
                                          : 'When to trigger search'}
                                      </FormHelperText>
                                    </FormControl>
                                    
                                    {/* Depends On Field Selector */}
                                    <FormControl fullWidth>
                                      <InputLabel>Depends On (Optional)</InputLabel>
                                      <Select
                                        value={field.autocompleteConfig?.dependsOn || ''}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.formFields];
                                          updatedFields[index] = {
                                            ...field,
                                            autocompleteConfig: {
                                              ...field.autocompleteConfig,
                                              dependsOn: e.target.value || undefined
                                            }
                                          };
                                          handleConfigChange('formFields', updatedFields);
                                        }}
                                        label="Depends On (Optional)"
                                      >
                                        <MenuItem value="">None</MenuItem>
                                        {step.config.formFields
                                          .filter((f, i) => i < index) // Only show fields before this one
                                          .map((prevField) => (
                                            <MenuItem key={prevField.id} value={prevField.id}>
                                              {prevField.label || prevField.name} ({prevField.outputVariable || prevField.name})
                                            </MenuItem>
                                          ))
                                        }
                                      </Select>
                                      <FormHelperText>
                                        Select a previous field if this autocomplete depends on its value. The parent field's value will be available as a variable in your conditions.
                                      </FormHelperText>
                                    </FormControl>
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                              
                              {/* SQL Database Configuration */}
                              {(!field.autocompleteConfig?.dataSource || field.autocompleteConfig?.dataSource === 'sql') && (
                                <>
                                  <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Typography variant="subtitle2">🗄️ SQL Query Configuration</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Box display="flex" flexDirection="column" gap={2}>
                                        <FormControl fullWidth>
                                          <InputLabel>Query Type</InputLabel>
                                          <Select
                                            value={field.autocompleteConfig?.sql?.queryType || 'visual'}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.formFields];
                                              updatedFields[index] = {
                                                ...field,
                                                autocompleteConfig: {
                                                  ...field.autocompleteConfig,
                                                  sql: {
                                                    ...field.autocompleteConfig?.sql,
                                                    queryType: e.target.value
                                                  }
                                                }
                                              };
                                              handleConfigChange('formFields', updatedFields);
                                            }}
                                            label="Query Type"
                                          >
                                            <MenuItem value="visual">Visual Query Builder</MenuItem>
                                            <MenuItem value="raw">Raw SQL Query</MenuItem>
                                          </Select>
                                        </FormControl>
                                        
                                        {(!field.autocompleteConfig?.sql?.queryType || field.autocompleteConfig?.sql?.queryType === 'visual') ? (
                                          <>
                                            <TextField
                                              label="Table Name"
                                              value={field.autocompleteConfig?.sql?.table || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.formFields];
                                                updatedFields[index] = {
                                                  ...field,
                                                  autocompleteConfig: {
                                                    ...field.autocompleteConfig,
                                                    sql: {
                                                      ...field.autocompleteConfig?.sql,
                                                      table: e.target.value
                                                    }
                                                  }
                                                };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              fullWidth
                                              required
                                              placeholder="users, products, orders, etc."
                                              helperText="Name of the database table"
                                            />
                                            
                                            <TextField
                                              label="Select Columns"
                                              value={field.autocompleteConfig?.sql?.columns || '*'}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.formFields];
                                                updatedFields[index] = {
                                                  ...field,
                                                  autocompleteConfig: {
                                                    ...field.autocompleteConfig,
                                                    sql: {
                                                      ...field.autocompleteConfig?.sql,
                                                      columns: e.target.value
                                                    }
                                                  }
                                                };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              fullWidth
                                              placeholder="*, id, name, email"
                                              helperText="Comma-separated column names or * for all"
                                            />
                                            
                                            {field.autocompleteConfig?.searchMode !== 'prepopulated' && (
                                              <TextField
                                                label="Search Column"
                                                value={field.autocompleteConfig?.sql?.searchColumn || ''}
                                                onChange={(e) => {
                                                  const updatedFields = [...step.config.formFields];
                                                  updatedFields[index] = {
                                                    ...field,
                                                    autocompleteConfig: {
                                                      ...field.autocompleteConfig,
                                                      sql: {
                                                        ...field.autocompleteConfig?.sql,
                                                        searchColumn: e.target.value
                                                      }
                                                    }
                                                  };
                                                  handleConfigChange('formFields', updatedFields);
                                                }}
                                                fullWidth
                                                required
                                                placeholder="name"
                                                helperText="Column to search in (e.g., customer name)"
                                              />
                                            )}
                                            
                                            {field.autocompleteConfig?.searchMode === 'prepopulated' && (
                                              <Alert severity="info" sx={{ mb: 2 }}>
                                                <strong>Prepopulated Mode:</strong> Search Column not needed. Data is loaded once using conditions below, then filtered client-side as user types.
                                              </Alert>
                                            )}
                                            
                                            <TextField
                                              label="Order By Column (Optional)"
                                              value={field.autocompleteConfig?.sql?.orderBy || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.formFields];
                                                updatedFields[index] = {
                                                  ...field,
                                                  autocompleteConfig: {
                                                    ...field.autocompleteConfig,
                                                    sql: {
                                                      ...field.autocompleteConfig?.sql,
                                                      orderBy: e.target.value
                                                    }
                                                  }
                                                };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              fullWidth
                                              placeholder="name, created_at, id"
                                              helperText="Column to sort results by"
                                            />
                                            
                                            <TextField
                                              label="Limit"
                                              type="number"
                                              value={field.autocompleteConfig?.sql?.limit || 50}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.formFields];
                                                updatedFields[index] = {
                                                  ...field,
                                                  autocompleteConfig: {
                                                    ...field.autocompleteConfig,
                                                    sql: {
                                                      ...field.autocompleteConfig?.sql,
                                                      limit: parseInt(e.target.value) || 50
                                                    }
                                                  }
                                                };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              fullWidth
                                              inputProps={{ min: 1, max: 200 }}
                                              helperText="Maximum number of results"
                                            />
                                            
                                            <Divider sx={{ my: 2 }}>
                                              <Chip label="WHERE Conditions (Filter Results)" size="small" />
                                            </Divider>
                                            
                                            {(!field.autocompleteConfig?.sql?.conditions || field.autocompleteConfig.sql.conditions.length === 0) && (
                                              <Alert severity="info" sx={{ mb: 2 }}>
                                                No conditions added. {field.autocompleteConfig?.searchMode === 'prepopulated' ? 'All rows will be loaded.' : 'Results will be filtered only by search term.'} Click "Add Condition" to filter results by variables or static values.
                                              </Alert>
                                            )}
                                            
                                            {field.autocompleteConfig?.sql?.conditions?.map((condition, conditionIndex) => (
                                              <Paper key={conditionIndex} sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                                  <Typography variant="body2" fontWeight="bold">
                                                    Condition {conditionIndex + 1}
                                                  </Typography>
                                                  {conditionIndex > 0 && (
                                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                                      <Select
                                                        value={condition.logicalOperator || 'AND'}
                                                        onChange={(e) => {
                                                          const updatedFields = [...step.config.formFields];
                                                          const newConditions = [...(field.autocompleteConfig?.sql?.conditions || [])];
                                                          newConditions[conditionIndex] = { ...newConditions[conditionIndex], logicalOperator: e.target.value };
                                                          updatedFields[index] = {
                                                            ...field,
                                                            autocompleteConfig: {
                                                              ...field.autocompleteConfig,
                                                              sql: {
                                                                ...field.autocompleteConfig?.sql,
                                                                conditions: newConditions
                                                              }
                                                            }
                                                          };
                                                          handleConfigChange('formFields', updatedFields);
                                                        }}
                                                      >
                                                        <MenuItem value="AND">AND</MenuItem>
                                                        <MenuItem value="OR">OR</MenuItem>
                                                      </Select>
                                                    </FormControl>
                                                  )}
                                                  <Box flexGrow={1} />
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                      const updatedFields = [...step.config.formFields];
                                                      const newConditions = (field.autocompleteConfig?.sql?.conditions || []).filter((_, i) => i !== conditionIndex);
                                                      updatedFields[index] = {
                                                        ...field,
                                                        autocompleteConfig: {
                                                          ...field.autocompleteConfig,
                                                          sql: {
                                                            ...field.autocompleteConfig?.sql,
                                                            conditions: newConditions
                                                          }
                                                        }
                                                      };
                                                      handleConfigChange('formFields', updatedFields);
                                                    }}
                                                  >
                                                    <DeleteIcon />
                                                  </IconButton>
                                                </Box>
                                                
                                                <Box display="flex" gap={1} flexWrap="wrap">
                                                  <TextField
                                                    label="Column"
                                                    value={condition.column || ''}
                                                    onChange={(e) => {
                                                      const updatedFields = [...step.config.formFields];
                                                      const newConditions = [...(field.autocompleteConfig?.sql?.conditions || [])];
                                                      newConditions[conditionIndex] = { ...newConditions[conditionIndex], column: e.target.value };
                                                      updatedFields[index] = {
                                                        ...field,
                                                        autocompleteConfig: {
                                                          ...field.autocompleteConfig,
                                                          sql: {
                                                            ...field.autocompleteConfig?.sql,
                                                            conditions: newConditions
                                                          }
                                                        }
                                                      };
                                                      handleConfigChange('formFields', updatedFields);
                                                    }}
                                                    placeholder="column_name"
                                                    size="small"
                                                    sx={{ flexGrow: 1, minWidth: '150px' }}
                                                  />
                                                  
                                                  <FormControl size="small" sx={{ minWidth: '150px' }}>
                                                    <InputLabel>Operator</InputLabel>
                                                    <Select
                                                      value={condition.operator || 'equals'}
                                                      onChange={(e) => {
                                                        const updatedFields = [...step.config.formFields];
                                                        const newConditions = [...(field.autocompleteConfig?.sql?.conditions || [])];
                                                        newConditions[conditionIndex] = { ...newConditions[conditionIndex], operator: e.target.value };
                                                        updatedFields[index] = {
                                                          ...field,
                                                          autocompleteConfig: {
                                                            ...field.autocompleteConfig,
                                                            sql: {
                                                              ...field.autocompleteConfig?.sql,
                                                              conditions: newConditions
                                                            }
                                                          }
                                                        };
                                                        handleConfigChange('formFields', updatedFields);
                                                      }}
                                                      label="Operator"
                                                    >
                                                      <MenuItem value="equals">=  Equals</MenuItem>
                                                      <MenuItem value="not_equals">≠  Not Equals</MenuItem>
                                                      <MenuItem value="greater_than">&gt;  Greater Than</MenuItem>
                                                      <MenuItem value="less_than">&lt;  Less Than</MenuItem>
                                                      <MenuItem value="greater_or_equal">≥  Greater or Equal</MenuItem>
                                                      <MenuItem value="less_or_equal">≤  Less or Equal</MenuItem>
                                                      <MenuItem value="contains">Contains</MenuItem>
                                                      <MenuItem value="not_contains">Not Contains</MenuItem>
                                                      <MenuItem value="starts_with">Starts With</MenuItem>
                                                      <MenuItem value="ends_with">Ends With</MenuItem>
                                                      <MenuItem value="is_null">Is NULL</MenuItem>
                                                      <MenuItem value="is_not_null">Is Not NULL</MenuItem>
                                                      <MenuItem value="in">IN (multiple values)</MenuItem>
                                                      <MenuItem value="not_in">NOT IN (multiple values)</MenuItem>
                                                    </Select>
                                                  </FormControl>
                                                  
                                                  {condition.operator !== 'is_null' && condition.operator !== 'is_not_null' && (
                                                    <TextField
                                                      label="Value"
                                                      value={condition.value || ''}
                                                      onChange={(e) => {
                                                        const updatedFields = [...step.config.formFields];
                                                        const newConditions = [...(field.autocompleteConfig?.sql?.conditions || [])];
                                                        newConditions[conditionIndex] = { ...newConditions[conditionIndex], value: e.target.value };
                                                        updatedFields[index] = {
                                                          ...field,
                                                          autocompleteConfig: {
                                                            ...field.autocompleteConfig,
                                                            sql: {
                                                              ...field.autocompleteConfig?.sql,
                                                              conditions: newConditions
                                                            }
                                                          }
                                                        };
                                                        handleConfigChange('formFields', updatedFields);
                                                      }}
                                                      placeholder={
                                                        condition.operator === 'in' || condition.operator === 'not_in'
                                                          ? "value1, value2, value3 or {{variableName}}"
                                                          : "value or {{variable}}"
                                                      }
                                                      size="small"
                                                      sx={{ flexGrow: 1, minWidth: '150px' }}
                                                      multiline={condition.operator === 'in' || condition.operator === 'not_in'}
                                                      rows={condition.operator === 'in' || condition.operator === 'not_in' ? 2 : 1}
                                                      helperText={
                                                        condition.operator === 'in' || condition.operator === 'not_in'
                                                          ? "Comma-separated values or {{variableName}}"
                                                          : "Use {{variableName}} for flow variables"
                                                      }
                                                    />
                                                  )}
                                                </Box>
                                              </Paper>
                                            ))}
                                            
                                            <Button
                                              variant="outlined"
                                              startIcon={<AddIcon />}
                                              onClick={() => {
                                                const updatedFields = [...step.config.formFields];
                                                const newConditions = [...(field.autocompleteConfig?.sql?.conditions || [])];
                                                newConditions.push({
                                                  column: '',
                                                  operator: 'equals',
                                                  value: '',
                                                  logicalOperator: newConditions.length > 0 ? 'AND' : undefined
                                                });
                                                updatedFields[index] = {
                                                  ...field,
                                                  autocompleteConfig: {
                                                    ...field.autocompleteConfig,
                                                    sql: {
                                                      ...field.autocompleteConfig?.sql,
                                                      conditions: newConditions
                                                    }
                                                  }
                                                };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              fullWidth
                                            >
                                              Add Condition
                                            </Button>
                                          </>
                                        ) : (
                                          <TextField
                                            label="Raw SQL Query"
                                            value={field.autocompleteConfig?.sql?.rawQuery || ''}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.formFields];
                                              updatedFields[index] = {
                                                ...field,
                                                autocompleteConfig: {
                                                  ...field.autocompleteConfig,
                                                  sql: {
                                                    ...field.autocompleteConfig?.sql,
                                                    rawQuery: e.target.value
                                                  }
                                                }
                                              };
                                              handleConfigChange('formFields', updatedFields);
                                            }}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            placeholder="SELECT id, name FROM customers WHERE name LIKE CONCAT('%', ?, '%') LIMIT 50"
                                            helperText="Use ? as placeholder for search term. The user's input will replace ?"
                                          />
                                        )}
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                </>
                              )}
                              
                              {/* API Endpoint Configuration */}
                              {field.autocompleteConfig?.dataSource === 'api' && (
                                <>
                                  <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Typography variant="subtitle2">🌐 API Request</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Box display="flex" flexDirection="column" gap={2}>
                                        <FormControl fullWidth>
                                          <InputLabel>HTTP Method</InputLabel>
                                          <Select
                                            value={field.autocompleteConfig?.api?.method || 'GET'}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.formFields];
                                              updatedFields[index] = {
                                                ...field,
                                                autocompleteConfig: {
                                                  ...field.autocompleteConfig,
                                                  api: {
                                                    ...field.autocompleteConfig?.api,
                                                    method: e.target.value
                                                  }
                                                }
                                              };
                                              handleConfigChange('formFields', updatedFields);
                                            }}
                                            label="HTTP Method"
                                          >
                                            <MenuItem value="GET">GET</MenuItem>
                                            <MenuItem value="POST">POST</MenuItem>
                                            <MenuItem value="PUT">PUT</MenuItem>
                                            <MenuItem value="PATCH">PATCH</MenuItem>
                                            <MenuItem value="DELETE">DELETE</MenuItem>
                                          </Select>
                                        </FormControl>
                                        
                                        <TextField
                                          label="API URL"
                                          value={field.autocompleteConfig?.api?.url || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.formFields];
                                            updatedFields[index] = {
                                              ...field,
                                              autocompleteConfig: {
                                                ...field.autocompleteConfig,
                                                api: {
                                                  ...field.autocompleteConfig?.api,
                                                  url: e.target.value
                                                }
                                              }
                                            };
                                            handleConfigChange('formFields', updatedFields);
                                          }}
                                          fullWidth
                                          required
                                          placeholder="https://api.example.com/search"
                                          helperText="Use {{searchTerm}} for search input and {{variables}} for other data"
                                        />
                                        
                                        <TextField
                                          label="Search Parameter Name"
                                          value={field.autocompleteConfig?.api?.searchParameter || 'q'}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.formFields];
                                            updatedFields[index] = {
                                              ...field,
                                              autocompleteConfig: {
                                                ...field.autocompleteConfig,
                                                api: {
                                                  ...field.autocompleteConfig?.api,
                                                  searchParameter: e.target.value
                                                }
                                              }
                                            };
                                            handleConfigChange('formFields', updatedFields);
                                          }}
                                          fullWidth
                                          placeholder="q, search, query, term"
                                          helperText="Query parameter name for search term (GET) or body field name (POST)"
                                        />
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                  
                                  <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Typography variant="subtitle2">🔐 Authentication</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Box display="flex" flexDirection="column" gap={2}>
                                        <FormControl fullWidth>
                                          <InputLabel>Authentication Type</InputLabel>
                                          <Select
                                            value={field.autocompleteConfig?.api?.authType || 'none'}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.formFields];
                                              updatedFields[index] = {
                                                ...field,
                                                autocompleteConfig: {
                                                  ...field.autocompleteConfig,
                                                  api: {
                                                    ...field.autocompleteConfig?.api,
                                                    authType: e.target.value
                                                  }
                                                }
                                              };
                                              handleConfigChange('formFields', updatedFields);
                                            }}
                                            label="Authentication Type"
                                          >
                                            <MenuItem value="none">No Authentication</MenuItem>
                                            <MenuItem value="bearer">Bearer Token</MenuItem>
                                            <MenuItem value="apiKey">API Key</MenuItem>
                                            <MenuItem value="basic">Basic Auth</MenuItem>
                                          </Select>
                                        </FormControl>
                                        
                                        {field.autocompleteConfig?.api?.authType === 'bearer' && (
                                          <TextField
                                            label="Bearer Token"
                                            value={field.autocompleteConfig?.api?.bearerToken || ''}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.formFields];
                                              updatedFields[index] = {
                                                ...field,
                                                autocompleteConfig: {
                                                  ...field.autocompleteConfig,
                                                  api: {
                                                    ...field.autocompleteConfig?.api,
                                                    bearerToken: e.target.value
                                                  }
                                                }
                                              };
                                              handleConfigChange('formFields', updatedFields);
                                            }}
                                            fullWidth
                                            placeholder="token or {{tokenVariable}}"
                                            helperText="Token for Authorization header"
                                          />
                                        )}
                                        
                                        {field.autocompleteConfig?.api?.authType === 'apiKey' && (
                                          <>
                                            <TextField
                                              label="API Key"
                                              value={field.autocompleteConfig?.api?.apiKey || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.formFields];
                                                updatedFields[index] = {
                                                  ...field,
                                                  autocompleteConfig: {
                                                    ...field.autocompleteConfig,
                                                    api: {
                                                      ...field.autocompleteConfig?.api,
                                                      apiKey: e.target.value
                                                    }
                                                  }
                                                };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              fullWidth
                                              placeholder="key or {{keyVariable}}"
                                            />
                                            <TextField
                                              label="API Key Header Name"
                                              value={field.autocompleteConfig?.api?.apiKeyHeader || 'X-API-Key'}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.formFields];
                                                updatedFields[index] = {
                                                  ...field,
                                                  autocompleteConfig: {
                                                    ...field.autocompleteConfig,
                                                    api: {
                                                      ...field.autocompleteConfig?.api,
                                                      apiKeyHeader: e.target.value
                                                    }
                                                  }
                                                };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              fullWidth
                                              placeholder="X-API-Key"
                                            />
                                          </>
                                        )}
                                        
                                        {field.autocompleteConfig?.api?.authType === 'basic' && (
                                          <Box display="flex" gap={2}>
                                            <TextField
                                              label="Username"
                                              value={field.autocompleteConfig?.api?.username || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.formFields];
                                                updatedFields[index] = {
                                                  ...field,
                                                  autocompleteConfig: {
                                                    ...field.autocompleteConfig,
                                                    api: {
                                                      ...field.autocompleteConfig?.api,
                                                      username: e.target.value
                                                    }
                                                  }
                                                };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              fullWidth
                                            />
                                            <TextField
                                              label="Password"
                                              type="password"
                                              value={field.autocompleteConfig?.api?.password || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.formFields];
                                                updatedFields[index] = {
                                                  ...field,
                                                  autocompleteConfig: {
                                                    ...field.autocompleteConfig,
                                                    api: {
                                                      ...field.autocompleteConfig?.api,
                                                      password: e.target.value
                                                    }
                                                  }
                                                };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              fullWidth
                                            />
                                          </Box>
                                        )}
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                  
                                  <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Typography variant="subtitle2">❓ Query Parameters</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Box display="flex" flexDirection="column" gap={2}>
                                        <Typography variant="body2" color="textSecondary">
                                          Additional query parameters to include in the URL (e.g., ?limit=10&sort=asc)
                                        </Typography>
                                        <TextField
                                          label="Query Parameters (JSON)"
                                          value={field.autocompleteConfig?.api?.queryParams || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.formFields];
                                            updatedFields[index] = {
                                              ...field,
                                              autocompleteConfig: {
                                                ...field.autocompleteConfig,
                                                api: {
                                                  ...field.autocompleteConfig?.api,
                                                  queryParams: e.target.value
                                                }
                                              }
                                            };
                                            handleConfigChange('formFields', updatedFields);
                                          }}
                                          fullWidth
                                          multiline
                                          rows={3}
                                          placeholder='{"limit": "10", "sort": "asc"}'
                                          helperText="JSON object of key-value pairs. Supports {{variables}}"
                                        />
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                  
                                  <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Typography variant="subtitle2">📋 Headers</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Box display="flex" flexDirection="column" gap={2}>
                                        <Typography variant="body2" color="textSecondary">
                                          Custom headers to include in the request
                                        </Typography>
                                        <TextField
                                          label="Custom Headers (JSON)"
                                          value={field.autocompleteConfig?.api?.headers || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.formFields];
                                            updatedFields[index] = {
                                              ...field,
                                              autocompleteConfig: {
                                                ...field.autocompleteConfig,
                                                api: {
                                                  ...field.autocompleteConfig?.api,
                                                  headers: e.target.value
                                                }
                                              }
                                            };
                                            handleConfigChange('formFields', updatedFields);
                                          }}
                                          fullWidth
                                          multiline
                                          rows={3}
                                          placeholder='{"Content-Type": "application/json", "Accept": "application/json"}'
                                          helperText="JSON object of header key-value pairs. Supports {{variables}}"
                                        />
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                  
                                  {['POST', 'PUT', 'PATCH'].includes(field.autocompleteConfig?.api?.method) && (
                                    <Accordion>
                                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle2">📦 Request Body</Typography>
                                      </AccordionSummary>
                                      <AccordionDetails>
                                        <Box display="flex" flexDirection="column" gap={2}>
                                          <TextField
                                            label="Request Body Template (JSON)"
                                            value={field.autocompleteConfig?.api?.bodyTemplate || ''}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.formFields];
                                              updatedFields[index] = {
                                                ...field,
                                                autocompleteConfig: {
                                                  ...field.autocompleteConfig,
                                                  api: {
                                                    ...field.autocompleteConfig?.api,
                                                    bodyTemplate: e.target.value
                                                  }
                                                }
                                              };
                                              handleConfigChange('formFields', updatedFields);
                                            }}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            placeholder='{"query": "{{searchTerm}}", "filters": {"status": "active"}}'
                                            helperText="Use {{searchTerm}} for user input and {{variables}} for other data"
                                          />
                                        </Box>
                                      </AccordionDetails>
                                    </Accordion>
                                  )}
                                </>
                              )}
                              
                              {/* Xero Configuration */}
                              {field.autocompleteConfig?.dataSource === 'xero' && (
                                <>
                                  <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Typography variant="subtitle2">💼 Xero Query Configuration</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                      <Box display="flex" flexDirection="column" gap={2}>
                                        <Alert severity="info">
                                          <Typography variant="body2">
                                            Search Xero data using your authenticated Xero connection. Supports user-level or project-level authentication.
                                          </Typography>
                                        </Alert>
                                        
                                        <FormControl fullWidth>
                                          <InputLabel>Xero Operation</InputLabel>
                                          <Select
                                            value={field.autocompleteConfig?.xero?.operation || 'getContacts'}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.formFields];
                                              updatedFields[index] = {
                                                ...field,
                                                autocompleteConfig: {
                                                  ...field.autocompleteConfig,
                                                  xero: {
                                                    ...field.autocompleteConfig?.xero,
                                                    operation: e.target.value
                                                  }
                                                }
                                              };
                                              handleConfigChange('formFields', updatedFields);
                                            }}
                                            label="Xero Operation"
                                          >
                                            <MenuItem value="getContacts">Get Contacts</MenuItem>
                                            <MenuItem value="getAccounts">Get Accounts (Chart of Accounts)</MenuItem>
                                            <MenuItem value="getInvoices">Get Invoices</MenuItem>
                                            <MenuItem value="getPayments">Get Payments</MenuItem>
                                            <MenuItem value="getBankTransactions">Get Bank Transactions</MenuItem>
                                          </Select>
                                          <FormHelperText>Choose which Xero data to search</FormHelperText>
                                        </FormControl>
                                        
                                        <TextField
                                          label="Search Parameter"
                                          value={field.autocompleteConfig?.xero?.searchParameter || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.formFields];
                                            updatedFields[index] = {
                                              ...field,
                                              autocompleteConfig: {
                                                ...field.autocompleteConfig,
                                                xero: {
                                                  ...field.autocompleteConfig?.xero,
                                                  searchParameter: e.target.value
                                                }
                                              }
                                            };
                                            handleConfigChange('formFields', updatedFields);
                                          }}
                                          fullWidth
                                          placeholder="Name, Code, InvoiceNumber, etc."
                                          helperText={
                                            field.autocompleteConfig?.xero?.operation === 'getContacts' ? "e.g., 'Name' to search contact names" :
                                            field.autocompleteConfig?.xero?.operation === 'getAccounts' ? "e.g., 'Name' or 'Code' to search account names/codes" :
                                            field.autocompleteConfig?.xero?.operation === 'getInvoices' ? "e.g., 'InvoiceNumber' or 'ContactName'" :
                                            "Xero field name to search (uses Contains matching)"
                                          }
                                        />
                                        
                                        <TextField
                                          label="Order By (Optional)"
                                          value={field.autocompleteConfig?.xero?.orderBy || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.formFields];
                                            updatedFields[index] = {
                                              ...field,
                                              autocompleteConfig: {
                                                ...field.autocompleteConfig,
                                                xero: {
                                                  ...field.autocompleteConfig?.xero,
                                                  orderBy: e.target.value
                                                }
                                              }
                                            };
                                            handleConfigChange('formFields', updatedFields);
                                          }}
                                          fullWidth
                                          placeholder="Name ASC, UpdatedDateUTC DESC"
                                          helperText="Xero ordering (e.g., 'Name ASC' or 'UpdatedDateUTC DESC')"
                                        />
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                </>
                              )}
                              
                              {/* Response Mapping (Common for SQL, API, and Xero) */}
                              <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography variant="subtitle2">🎯 Response Mapping</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box display="flex" flexDirection="column" gap={2}>
                                    <Typography variant="body2" color="textSecondary">
                                      Map response data to dropdown display and stored value
                                      {field.autocompleteConfig?.dataSource === 'xero' && (
                                        <> (e.g., for Contacts use 'name' and 'contactID', for Accounts use 'name' and 'accountID')</>
                                      )}
                                    </Typography>
                                    
                                    {field.autocompleteConfig?.dataSource === 'api' && (
                                      <TextField
                                        label="Response Data Path"
                                        value={field.autocompleteConfig?.responseDataPath || ''}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.formFields];
                                          updatedFields[index] = {
                                            ...field,
                                            autocompleteConfig: {
                                              ...field.autocompleteConfig,
                                              responseDataPath: e.target.value
                                            }
                                          };
                                          handleConfigChange('formFields', updatedFields);
                                        }}
                                        fullWidth
                                        placeholder="data.results or results"
                                        helperText="Path to array in response (e.g., 'data.items' or leave empty if response is array)"
                                      />
                                    )}
                                    
                                    <TextField
                                      label="Display Field"
                                      value={field.autocompleteConfig?.displayField || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.formFields];
                                        updatedFields[index] = {
                                          ...field,
                                          autocompleteConfig: {
                                            ...field.autocompleteConfig,
                                            displayField: e.target.value
                                          }
                                        };
                                        handleConfigChange('formFields', updatedFields);
                                      }}
                                      fullWidth
                                      required
                                      placeholder="name"
                                      helperText="Field to show in dropdown (e.g., '{{name}}' or '{{firstname}} {{lastname}}')"
                                    />
                                    
                                    <TextField
                                      label="Value Field"
                                      value={field.autocompleteConfig?.valueField || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.formFields];
                                        updatedFields[index] = {
                                          ...field,
                                          autocompleteConfig: {
                                            ...field.autocompleteConfig,
                                            valueField: e.target.value
                                          }
                                        };
                                        handleConfigChange('formFields', updatedFields);
                                      }}
                                      fullWidth
                                      required
                                      placeholder="id"
                                      helperText="Field to store as value (e.g., '{{id}}' or '{{customerId}}')"
                                    />
                                    
                                    <Divider sx={{ my: 2 }} />
                                    
                                    {/* Display Field Variables */}
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        📋 Display Field Variables (Optional)
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                        Extract additional variables from the selected row. These can be used in dependent fields or SQL conditions.
                                      </Typography>
                                      
                                      {(() => {
                                        const displayFieldVars = field.autocompleteConfig?.displayFieldVariables || {};
                                        const varEntries = Object.entries(displayFieldVars);
                                        
                                        return (
                                          <>
                                            {varEntries.map(([varName, template], varIndex) => (
                                              <Box key={varIndex} display="flex" gap={1} mb={1} alignItems="flex-start">
                                                <TextField
                                                  label="Variable Name"
                                                  value={varName}
                                                  onChange={(e) => {
                                                    const updatedFields = [...step.config.formFields];
                                                    const newVars = { ...displayFieldVars };
                                                    const oldName = varName;
                                                    const newName = e.target.value;
                                                    
                                                    // Rename the key
                                                    if (newName && newName !== oldName) {
                                                      delete newVars[oldName];
                                                      newVars[newName] = template;
                                                    }
                                                    
                                                    updatedFields[index] = {
                                                      ...field,
                                                      autocompleteConfig: {
                                                        ...field.autocompleteConfig,
                                                        displayFieldVariables: newVars
                                                      }
                                                    };
                                                    handleConfigChange('formFields', updatedFields);
                                                  }}
                                                  size="small"
                                                  placeholder="entityId"
                                                  sx={{ flex: 1 }}
                                                />
                                                <TextField
                                                  label="Template"
                                                  value={template}
                                                  onChange={(e) => {
                                                    const updatedFields = [...step.config.formFields];
                                                    const newVars = { ...displayFieldVars };
                                                    newVars[varName] = e.target.value;
                                                    
                                                    updatedFields[index] = {
                                                      ...field,
                                                      autocompleteConfig: {
                                                        ...field.autocompleteConfig,
                                                        displayFieldVariables: newVars
                                                      }
                                                    };
                                                    handleConfigChange('formFields', updatedFields);
                                                  }}
                                                  size="small"
                                                  placeholder="{{EntityID}}"
                                                  sx={{ flex: 1 }}
                                                />
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  onClick={() => {
                                                    const updatedFields = [...step.config.formFields];
                                                    const newVars = { ...displayFieldVars };
                                                    delete newVars[varName];
                                                    
                                                    updatedFields[index] = {
                                                      ...field,
                                                      autocompleteConfig: {
                                                        ...field.autocompleteConfig,
                                                        displayFieldVariables: newVars
                                                      }
                                                    };
                                                    handleConfigChange('formFields', updatedFields);
                                                  }}
                                                  sx={{ mt: 0.5 }}
                                                >
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </Box>
                                            ))}
                                            
                                            <Button
                                              startIcon={<AddIcon />}
                                              onClick={() => {
                                                const updatedFields = [...step.config.formFields];
                                                const newVars = { ...displayFieldVars };
                                                
                                                // Generate unique variable name
                                                let counter = 1;
                                                let newVarName = 'variable1';
                                                while (newVars[newVarName]) {
                                                  counter++;
                                                  newVarName = `variable${counter}`;
                                                }
                                                
                                                newVars[newVarName] = '';
                                                
                                                updatedFields[index] = {
                                                  ...field,
                                                  autocompleteConfig: {
                                                    ...field.autocompleteConfig,
                                                    displayFieldVariables: newVars
                                                  }
                                                };
                                                handleConfigChange('formFields', updatedFields);
                                              }}
                                              size="small"
                                              variant="outlined"
                                            >
                                              Add Variable
                                            </Button>
                                            
                                            {varEntries.length > 0 && (
                                              <Alert severity="info" sx={{ mt: 2 }}>
                                                <Typography variant="caption">
                                                  <strong>Example:</strong> If you set <code>entityId: {'{'}{'{'} EntityID {'}'}{'}'}</code>, 
                                                  when a user selects an option, the <code>entityId</code> variable will be extracted 
                                                  and available for use in dependent fields' SQL conditions.
                                                </Typography>
                                              </Alert>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </Box>
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                              
                              {/* Performance & Behavior */}
                              <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography variant="subtitle2">⚙️ Performance & Behavior</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box display="flex" flexDirection="column" gap={2}>
                                    <TextField
                                      label="Min Search Length"
                                      type="number"
                                      value={field.autocompleteConfig?.minSearchLength || 2}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.formFields];
                                        updatedFields[index] = {
                                          ...field,
                                          autocompleteConfig: {
                                            ...field.autocompleteConfig,
                                            minSearchLength: parseInt(e.target.value) || 2
                                          }
                                        };
                                        handleConfigChange('formFields', updatedFields);
                                      }}
                                      fullWidth
                                      inputProps={{ min: 0, max: 10 }}
                                      helperText="Minimum characters before search is triggered"
                                    />
                                    
                                    <TextField
                                      label="Debounce Delay (ms)"
                                      type="number"
                                      value={field.autocompleteConfig?.debounceMs || 300}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.formFields];
                                        updatedFields[index] = {
                                          ...field,
                                          autocompleteConfig: {
                                            ...field.autocompleteConfig,
                                            debounceMs: parseInt(e.target.value) || 300
                                          }
                                        };
                                        handleConfigChange('formFields', updatedFields);
                                      }}
                                      fullWidth
                                      inputProps={{ min: 0, max: 2000 }}
                                      helperText="Delay after typing before search (prevents excessive requests)"
                                    />
                                    
                                    <TextField
                                      label="Cache Duration (seconds)"
                                      type="number"
                                      value={field.autocompleteConfig?.cacheDuration || 0}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.formFields];
                                        updatedFields[index] = {
                                          ...field,
                                          autocompleteConfig: {
                                            ...field.autocompleteConfig,
                                            cacheDuration: parseInt(e.target.value) || 0
                                          }
                                        };
                                        handleConfigChange('formFields', updatedFields);
                                      }}
                                      fullWidth
                                      inputProps={{ min: 0, max: 3600 }}
                                      helperText="Cache search results for this duration (0 = no cache)"
                                    />
                                    
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={field.autocompleteConfig?.removeDuplicates || false}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.formFields];
                                            updatedFields[index] = {
                                              ...field,
                                              autocompleteConfig: {
                                                ...field.autocompleteConfig,
                                                removeDuplicates: e.target.checked
                                              }
                                            };
                                            handleConfigChange('formFields', updatedFields);
                                          }}
                                        />
                                      }
                                      label="Remove Duplicate Values"
                                    />
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: -1, display: 'block' }}>
                                      When enabled, duplicate display values will be filtered out from the dropdown. Useful when SQL queries return the same display value multiple times.
                                    </Typography>
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                      </SortableFormFieldCard>
                    ))}
                  </SortableContext>
                </DndContext>
                
                {(!step.config?.formFields || step.config.formFields.length === 0) && (
                  <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="body2">
                      No form fields defined. Click "Add Field" to create input fields.
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Quick Templates for Forms */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  🚀 Quick Form Templates
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const purchaseOrderField = [
                        { id: 'po-field-1', name: 'purchaseOrder', label: 'Purchase Order Number', type: 'text', required: true, placeholder: 'Enter PO number...', helperText: 'The Purchase Order number for this delivery', outputVariable: 'purchaseOrder' }
                      ];
                      handleConfigChange('formFields', purchaseOrderField);
                    }}
                  >
                    📋 Purchase Order Input
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const contactFields = [
                        { id: 'contact-1', name: 'contactName', label: 'Contact Name', type: 'text', required: true, placeholder: 'Enter name...', outputVariable: 'contactName' },
                        { id: 'contact-2', name: 'contactEmail', label: 'Email Address', type: 'email', required: true, placeholder: 'Enter email...', outputVariable: 'contactEmail' },
                        { id: 'contact-3', name: 'contactPhone', label: 'Phone Number', type: 'text', required: false, placeholder: 'Enter phone...', outputVariable: 'contactPhone' }
                      ];
                      handleConfigChange('formFields', contactFields);
                    }}
                  >
                    👤 Contact Info
                  </Button>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Barcode Scanner Configuration */}
      {step.config?.interfaceType === 'barcode_scanner' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">📱 Barcode/QR Scanner Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Configure barcode and QR code scanning. The scanner works entirely offline using the device camera.
              </Typography>
              
              <FormControl fullWidth>
                <InputLabel>Scan Type</InputLabel>
                <Select
                  value={step.config?.scanType || 'both'}
                  onChange={(e) => handleConfigChange('scanType', e.target.value)}
                >
                  <MenuItem value="barcode">🔵 Barcode Only</MenuItem>
                  <MenuItem value="qr">📱 QR Code Only</MenuItem>
                  <MenuItem value="both">🔵📱 Both Barcode & QR</MenuItem>
                </Select>
                <FormHelperText>
                  {step.config?.scanType === 'barcode' && "Scan traditional barcodes (UPC, EAN, Code 128, etc.)"}
                  {step.config?.scanType === 'qr' && "Scan QR codes only"}
                  {(step.config?.scanType === 'both' || !step.config?.scanType) && "Automatically detect and scan both barcodes and QR codes"}
                </FormHelperText>
              </FormControl>

              <TextField
                label="Output Variable"
                value={step.config?.outputVariable || 'scannedCode'}
                onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
                fullWidth
                placeholder="e.g., scannedCode, productBarcode, qrData"
                helperText="Variable name where the scanned result will be stored"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={step.config?.allowMultiple || false}
                    onChange={(e) => handleConfigChange('allowMultiple', e.target.checked)}
                  />
                }
                label="Allow Multiple Scans"
              />
              
              <Typography variant="caption" color="textSecondary">
                {step.config?.allowMultiple 
                  ? "Multiple scans will be stored as an array in the output variable"
                  : "Only one scan will be captured and stored"
                }
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={step.config?.continuousMode || false}
                    onChange={(e) => handleConfigChange('continuousMode', e.target.checked)}
                  />
                }
                label="Continuous Scanning Mode"
              />
              
              <Typography variant="caption" color="textSecondary">
                {step.config?.continuousMode 
                  ? "Scanner stays active until user manually stops or timeout is reached"
                  : "Scanner stops after first successful scan"
                }
              </Typography>

              <TextField
                label="Validation Pattern (Optional)"
                value={step.config?.validationPattern || ''}
                onChange={(e) => handleConfigChange('validationPattern', e.target.value)}
                fullWidth
                placeholder="e.g., ^[0-9]{12}$ for 12-digit codes"
                helperText="Regular expression to validate scanned codes (leave empty to accept all)"
              />

              <TextField
                label="Validation Error Message"
                value={step.config?.validationMessage || 'Invalid scan result'}
                onChange={(e) => handleConfigChange('validationMessage', e.target.value)}
                fullWidth
                placeholder="Custom error message for invalid scans"
              />

              <TextField
                label="Timeout (seconds)"
                type="number"
                value={step.config?.timeout || 0}
                onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value) || 0)}
                fullWidth
                inputProps={{ min: 0, max: 300 }}
                helperText="0 = no timeout, scanner stays active until scan or user cancels"
              />
              
              {/* Preview Box */}
              <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                  📱 Scanner Preview
                </Typography>
                <Typography variant="body2" color="#2e7d32" sx={{ fontSize: '0.875rem' }}>
                  Scan Type: {step.config?.scanType === 'barcode' ? 'Barcode Only' : step.config?.scanType === 'qr' ? 'QR Code Only' : 'Barcode & QR Code'}<br/>
                  Multiple Scans: {step.config?.allowMultiple ? 'Enabled' : 'Disabled'}<br/>
                  Continuous Mode: {step.config?.continuousMode ? 'Enabled' : 'Disabled'}<br/>
                  Output Variable: {step.config?.outputVariable || 'scannedCode'}<br/>
                  {step.config?.validationPattern && `Validation: ${step.config.validationPattern}`}<br/>
                  {step.config?.timeout > 0 && `Timeout: ${step.config.timeout} seconds`}
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* File Upload Configuration */}
      {step.config?.interfaceType === 'file_upload' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">📁 File Upload Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Configure file upload settings for collecting documents, images, and other files from users.
              </Typography>
              
              <TextField
                label="Output Variable"
                value={step.config?.outputVariable || 'uploadedFiles'}
                onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
                fullWidth
                placeholder="e.g., uploadedFiles, documents, receipts"
                helperText="Variable name where uploaded file information will be stored"
              />
              
              <Box display="flex" gap={2}>
                <TextField
                  label="Max Files"
                  type="number"
                  value={step.config?.maxFiles || 5}
                  onChange={(e) => handleConfigChange('maxFiles', parseInt(e.target.value) || 5)}
                  inputProps={{ min: 1, max: 20 }}
                  sx={{ flex: 1 }}
                />
                
                <TextField
                  label="Max File Size (MB)"
                  type="number"
                  value={step.config?.maxFileSize || 10}
                  onChange={(e) => handleConfigChange('maxFileSize', parseInt(e.target.value) || 10)}
                  inputProps={{ min: 1, max: 100 }}
                  sx={{ flex: 1 }}
                />
              </Box>
              
              <FormControl fullWidth>
                <InputLabel>Accepted File Types</InputLabel>
                <Select
                  multiple
                  value={step.config?.acceptedFileTypes || ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']}
                  onChange={(e) => handleConfigChange('acceptedFileTypes', e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                >
                  <MenuItem value="pdf">PDF Documents</MenuItem>
                  <MenuItem value="png">PNG Images</MenuItem>
                  <MenuItem value="jpg">JPG Images</MenuItem>
                  <MenuItem value="jpeg">JPEG Images</MenuItem>
                  <MenuItem value="xlsx">Excel Files</MenuItem>
                  <MenuItem value="xls">Excel Files (Legacy .xls)</MenuItem>
                  <MenuItem value="docx">Word Documents</MenuItem>
                  <MenuItem value="txt">Text Files</MenuItem>
                  <MenuItem value="csv">CSV Files</MenuItem>
                </Select>
                <FormHelperText>
                  Select the file types that users can upload
                </FormHelperText>
              </FormControl>
              
              {/* Excel Multi-Sheet Configuration - shown when xlsx or xls is in accepted types */}
              {(step.config?.acceptedFileTypes?.includes('xlsx') || step.config?.acceptedFileTypes?.includes('xls')) && (
                <Box sx={{ p: 2, backgroundColor: '#e8f4f8', borderRadius: 1, border: '1px solid #b3d9e6' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0277bd', mb: 2 }}>
                    📊 Excel Multi-Sheet Configuration
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Excel Parse Mode</InputLabel>
                    <Select
                      value={step.config?.excelParseMode || 'auto-merge'}
                      onChange={(e) => handleConfigChange('excelParseMode', e.target.value)}
                    >
                      <MenuItem value="auto-merge">Auto-Merge All Sheets</MenuItem>
                      <MenuItem value="individual-sheets">Keep Individual Sheets</MenuItem>
                      <MenuItem value="selected-sheets">Select Specific Sheets</MenuItem>
                    </Select>
                    <FormHelperText>
                      {step.config?.excelParseMode === 'auto-merge' && 'Automatically combine data from all sheets into a single array'}
                      {step.config?.excelParseMode === 'individual-sheets' && 'Keep each sheet as a separate data structure with metadata'}
                      {step.config?.excelParseMode === 'selected-sheets' && 'Specify which sheets to process (comma-separated list)'}
                      {!step.config?.excelParseMode && 'Automatically combine data from all sheets into a single array'}
                    </FormHelperText>
                  </FormControl>
                  
                  {(step.config?.excelParseMode === 'auto-merge' || !step.config?.excelParseMode) && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Merge Strategy</InputLabel>
                      <Select
                        value={step.config?.mergeStrategy || 'append'}
                        onChange={(e) => handleConfigChange('mergeStrategy', e.target.value)}
                      >
                        <MenuItem value="append">Append All Sheets</MenuItem>
                        <MenuItem value="first-only">First Sheet Only</MenuItem>
                      </Select>
                      <FormHelperText>
                        How to combine multiple sheets when merging
                      </FormHelperText>
                    </FormControl>
                  )}
                  
                  {step.config?.excelParseMode === 'selected-sheets' && (
                    <Box>
                      {/* Toggle between Simple and Advanced mode */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={step.config?.advancedSheetConfig || false}
                            onChange={(e) => {
                              handleConfigChange('advancedSheetConfig', e.target.checked);
                              // Initialize sheetConfigs if enabling advanced mode
                              if (e.target.checked && (!step.config?.sheetConfigs || step.config.sheetConfigs.length === 0)) {
                                const existingSheets = step.config?.selectedSheets || [];
                                const initialConfigs = existingSheets.length > 0 
                                  ? existingSheets.map(name => ({
                                      id: `sheet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                      sheetName: name,
                                      startRow: 1,
                                      endRow: '',
                                      columns: ''
                                    }))
                                  : [{
                                      id: `sheet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                      sheetName: '',
                                      startRow: 1,
                                      endRow: '',
                                      columns: ''
                                    }];
                                handleConfigChange('sheetConfigs', initialConfigs);
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Advanced Sheet Configuration
                          </Typography>
                        }
                        sx={{ mb: 1 }}
                      />
                      
                      {!step.config?.advancedSheetConfig ? (
                        /* Simple mode - just sheet names */
                        <TextField
                          label="Sheet Names"
                          value={step.config?.selectedSheetsRaw ?? step.config?.selectedSheets?.join(', ') ?? ''}
                          onChange={(e) => {
                            handleConfigChange('selectedSheetsRaw', e.target.value);
                          }}
                          onBlur={(e) => {
                            const sheets = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                            handleConfigChange('selectedSheets', sheets);
                            handleConfigChange('selectedSheetsRaw', undefined);
                          }}
                          fullWidth
                          placeholder="Sheet1, Sheet2, My Data Sheet"
                          helperText="Enter sheet names separated by commas"
                        />
                      ) : (
                        /* Advanced mode - per-sheet configuration */
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            Configure extraction settings for each sheet. Leave row/column fields empty to extract all.
                          </Typography>
                          
                          {(step.config?.sheetConfigs || []).map((sheetConfig, index) => (
                            <Paper 
                              key={sheetConfig.id} 
                              variant="outlined" 
                              sx={{ 
                                p: 2, 
                                mb: 1.5, 
                                backgroundColor: '#fafafa',
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                  Sheet {index + 1}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  onClick={() => {
                                    const updatedConfigs = step.config.sheetConfigs.filter((_, i) => i !== index);
                                    handleConfigChange('sheetConfigs', updatedConfigs);
                                    // Also update selectedSheets array
                                    handleConfigChange('selectedSheets', updatedConfigs.map(c => c.sheetName).filter(n => n));
                                  }}
                                  sx={{ color: '#d32f2f' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              
                              <TextField
                                label="Sheet Name"
                                value={sheetConfig.sheetName || ''}
                                onChange={(e) => {
                                  const updatedConfigs = [...step.config.sheetConfigs];
                                  updatedConfigs[index] = { ...updatedConfigs[index], sheetName: e.target.value };
                                  handleConfigChange('sheetConfigs', updatedConfigs);
                                }}
                                onBlur={() => {
                                  // Update selectedSheets array for compatibility
                                  const sheetNames = step.config.sheetConfigs.map(c => c.sheetName).filter(n => n);
                                  handleConfigChange('selectedSheets', sheetNames);
                                }}
                                fullWidth
                                size="small"
                                placeholder="e.g., Sales Data, Sheet1"
                                sx={{ mb: 1.5 }}
                              />
                              
                              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                                <TextField
                                  label="Start Row"
                                  type="number"
                                  value={sheetConfig.startRow || 1}
                                  onChange={(e) => {
                                    const updatedConfigs = [...step.config.sheetConfigs];
                                    updatedConfigs[index] = { ...updatedConfigs[index], startRow: parseInt(e.target.value) || 1 };
                                    handleConfigChange('sheetConfigs', updatedConfigs);
                                  }}
                                  size="small"
                                  inputProps={{ min: 1 }}
                                  sx={{ flex: 1 }}
                                  helperText="First row to extract"
                                />
                                <TextField
                                  label="End Row"
                                  type="number"
                                  value={sheetConfig.endRow || ''}
                                  onChange={(e) => {
                                    const updatedConfigs = [...step.config.sheetConfigs];
                                    updatedConfigs[index] = { ...updatedConfigs[index], endRow: e.target.value ? parseInt(e.target.value) : '' };
                                    handleConfigChange('sheetConfigs', updatedConfigs);
                                  }}
                                  size="small"
                                  inputProps={{ min: 1 }}
                                  sx={{ flex: 1 }}
                                  placeholder="All"
                                  helperText="Last row (empty = all)"
                                />
                              </Box>
                              
                              <TextField
                                label="Columns"
                                value={sheetConfig.columns || ''}
                                onChange={(e) => {
                                  const updatedConfigs = [...step.config.sheetConfigs];
                                  updatedConfigs[index] = { ...updatedConfigs[index], columns: e.target.value };
                                  handleConfigChange('sheetConfigs', updatedConfigs);
                                }}
                                fullWidth
                                size="small"
                                placeholder="e.g., A:F or A,C,E,G or empty for all"
                                helperText="Column range (A:Z), specific columns (A,C,E), or empty for all columns"
                              />
                            </Paper>
                          ))}
                          
                          <Button
                            startIcon={<AddIcon />}
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const currentConfigs = step.config?.sheetConfigs || [];
                              const newConfig = {
                                id: `sheet-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                sheetName: '',
                                startRow: 1,
                                endRow: '',
                                columns: ''
                              };
                              handleConfigChange('sheetConfigs', [...currentConfigs, newConfig]);
                            }}
                            sx={{ mt: 0.5 }}
                          >
                            Add Sheet
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              
              <FormControlLabel
                control={
                  <Switch
                    checked={step.config?.fileUploadRequired !== false}
                    onChange={(e) => handleConfigChange('fileUploadRequired', e.target.checked)}
                  />
                }
                label="File Upload Required"
              />
              
              <TextField
                label="Upload Instructions"
                value={step.config?.fileUploadInstructions || ''}
                onChange={(e) => handleConfigChange('fileUploadInstructions', e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="e.g., Upload receipt images or PDF documents"
                helperText="Instructions shown to users about what files to upload"
              />
              
              {/* S3 Storage Info - Always enabled for auto-scaling */}
              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1 }}>
                  ☁️ Cloud Storage (S3)
                </Typography>
                <Typography variant="body2" color="#1976d2" sx={{ fontSize: '0.875rem', mb: 1 }}>
                  Files are automatically uploaded to S3 temporary storage with encryption. This ensures files are accessible across auto-scaling servers and provides secure, reliable storage during flow execution.
                </Typography>
                <Typography variant="caption" color="#1565c0">
                  Files are automatically cleaned up after flow completion based on retention settings.
                </Typography>
              </Box>
              
              <Box sx={{ p: 2, backgroundColor: '#e8f4f8', borderRadius: 1, border: '1px solid #1976d2' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                  💡 OCR Processing Tip
                </Typography>
                <Typography variant="body2" color="#1976d2" sx={{ fontSize: '0.875rem' }}>
                  To extract text from uploaded images or PDFs, add a "Document OCR & Vision" step after this file upload step. The OCR step will automatically process files from the variable: <strong>{step.config?.outputVariable || 'uploadedFiles'}</strong>
                </Typography>
              </Box>
              
              {/* Preview Box */}
              <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                  📁 Upload Preview
                </Typography>
                <Typography variant="body2" color="#2e7d32" sx={{ fontSize: '0.875rem' }}>
                  Max Files: {step.config?.maxFiles || 5}<br/>
                  Max Size: {step.config?.maxFileSize || 10}MB per file<br/>
                  Types: {(step.config?.acceptedFileTypes || ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']).join(', ')}<br/>
                  Required: {step.config?.fileUploadRequired !== false ? 'Yes' : 'No'}<br/>
                  Variable: {step.config?.outputVariable || 'uploadedFiles'}<br/>
                  Storage: S3 (Encrypted, Auto-scaling Ready)<br/>
                  {(step.config?.acceptedFileTypes?.includes('xlsx') || step.config?.acceptedFileTypes?.includes('xls')) && (
                    <>Excel Mode: {step.config?.excelParseMode === 'individual-sheets' ? 'Individual Sheets' : step.config?.excelParseMode === 'selected-sheets' ? 'Selected Sheets' : 'Auto-Merge'}</>
                  )}
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Record Search Configuration */}
      {step.config?.interfaceType === 'record_search' && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">🔍 Record Search Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Configure a searchable form where users can enter optional search criteria, 
                trigger a search via API or SQL, view results in a table, and select a record 
                to map values to flow variables.
              </Typography>
              
              <Divider sx={{ my: 1 }} />
              
              {/* Search Fields Configuration */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  📝 Search Fields (Optional Inputs)
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Define the form fields users can fill to filter search results. Drag to reorder.
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentConfig = step.config?.recordSearchConfig || {};
                      const currentFields = currentConfig.searchFields || [];
                      const newField = {
                        id: `search-${Date.now()}`,
                        name: '',
                        label: '',
                        type: 'text',
                        required: false,
                        placeholder: '',
                        helperText: '',
                        width: 'half'
                      };
                      handleConfigChange('recordSearchConfig', {
                        ...currentConfig,
                        searchFields: [...currentFields, newField]
                      });
                    }}
                  >
                    Add Search Field
                  </Button>
                </Box>
                
                {(step.config?.recordSearchConfig?.searchFields || []).length === 0 && (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 2 }}>
                    No search fields added yet. Click "Add Search Field" to create input fields for the search form.
                  </Typography>
                )}
                
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleRecordSearchFieldsDragEnd}
                >
                  <SortableContext 
                    items={step.config?.recordSearchConfig?.searchFields || []}
                    strategy={verticalListSortingStrategy}
                  >
                    {(step.config?.recordSearchConfig?.searchFields || []).map((field, index) => (
                      <SortableSearchFieldCard
                        key={field.id}
                        field={field}
                        index={index}
                        onFieldDelete={(idx) => {
                          const currentConfig = step.config?.recordSearchConfig || {};
                          const updatedFields = currentConfig.searchFields.filter((_, i) => i !== idx);
                          handleConfigChange('recordSearchConfig', {
                            ...currentConfig,
                            searchFields: updatedFields
                          });
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>Search Field {index + 1}</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Field Name"
                              value={field.name || ''}
                              onChange={(e) => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                const updatedFields = [...currentConfig.searchFields];
                                updatedFields[index] = { ...field, name: e.target.value };
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  searchFields: updatedFields
                                });
                              }}
                              fullWidth
                              placeholder="e.g., lastName"
                              helperText="API/SQL parameter name"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Label"
                              value={field.label || ''}
                              onChange={(e) => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                const updatedFields = [...currentConfig.searchFields];
                                updatedFields[index] = { ...field, label: e.target.value };
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  searchFields: updatedFields
                                });
                              }}
                              fullWidth
                              placeholder="e.g., Last Name"
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={field.type || 'text'}
                                onChange={(e) => {
                                  const currentConfig = step.config?.recordSearchConfig || {};
                                  const updatedFields = [...currentConfig.searchFields];
                                  updatedFields[index] = { ...field, type: e.target.value };
                                  handleConfigChange('recordSearchConfig', {
                                    ...currentConfig,
                                    searchFields: updatedFields
                                  });
                                }}
                                label="Type"
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="select">Dropdown</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              size="small"
                              label="Placeholder"
                              value={field.placeholder || ''}
                              onChange={(e) => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                const updatedFields = [...currentConfig.searchFields];
                                updatedFields[index] = { ...field, placeholder: e.target.value };
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  searchFields: updatedFields
                                });
                              }}
                              fullWidth
                              placeholder="e.g., Enter last name..."
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={field.required || false}
                                  onChange={(e) => {
                                    const currentConfig = step.config?.recordSearchConfig || {};
                                    const updatedFields = [...currentConfig.searchFields];
                                    updatedFields[index] = { ...field, required: e.target.checked };
                                    handleConfigChange('recordSearchConfig', {
                                      ...currentConfig,
                                      searchFields: updatedFields
                                    });
                                  }}
                                />
                              }
                              label="Required"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Width</InputLabel>
                              <Select
                                value={field.width || 'half'}
                                onChange={(e) => {
                                  const currentConfig = step.config?.recordSearchConfig || {};
                                  const updatedFields = [...currentConfig.searchFields];
                                  updatedFields[index] = { ...field, width: e.target.value };
                                  handleConfigChange('recordSearchConfig', {
                                    ...currentConfig,
                                    searchFields: updatedFields
                                  });
                                }}
                                label="Width"
                              >
                                <MenuItem value="full">Full Width</MenuItem>
                                <MenuItem value="half">Half Width</MenuItem>
                                <MenuItem value="third">One Third</MenuItem>
                                <MenuItem value="quarter">One Quarter</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {field.type === 'select' && (
                            <Grid item xs={12}>
                              <TextField
                                size="small"
                                label="Options (comma-separated)"
                                value={field.options || ''}
                                onChange={(e) => {
                                  const currentConfig = step.config?.recordSearchConfig || {};
                                  const updatedFields = [...currentConfig.searchFields];
                                  updatedFields[index] = { ...field, options: e.target.value };
                                  handleConfigChange('recordSearchConfig', {
                                    ...currentConfig,
                                    searchFields: updatedFields
                                  });
                                }}
                                fullWidth
                                placeholder="Option 1, Option 2, Option 3"
                              />
                            </Grid>
                          )}
                        </Grid>
                      </SortableSearchFieldCard>
                    ))}
                  </SortableContext>
                </DndContext>
              </Box>
              
              <Divider sx={{ my: 1 }} />
              
              {/* Search Button Configuration */}
              <TextField
                size="small"
                label="Search Button Text"
                value={step.config?.recordSearchConfig?.searchButtonText || 'Search'}
                onChange={(e) => {
                  const currentConfig = step.config?.recordSearchConfig || {};
                  handleConfigChange('recordSearchConfig', {
                    ...currentConfig,
                    searchButtonText: e.target.value
                  });
                }}
                fullWidth
                placeholder="e.g., Search Patients"
              />
              
              <Divider sx={{ my: 1 }} />
              
              {/* Data Source Configuration */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  🔗 Data Source
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Data Source Type</InputLabel>
                  <Select
                    value={step.config?.recordSearchConfig?.dataSource || 'api'}
                    onChange={(e) => {
                      const currentConfig = step.config?.recordSearchConfig || {};
                      handleConfigChange('recordSearchConfig', {
                        ...currentConfig,
                        dataSource: e.target.value
                      });
                    }}
                    label="Data Source Type"
                  >
                    <MenuItem value="api">API Endpoint</MenuItem>
                    <MenuItem value="sql">SQL Database</MenuItem>
                  </Select>
                </FormControl>
                
                {/* API Configuration */}
                {(step.config?.recordSearchConfig?.dataSource || 'api') === 'api' && (
                  <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>API Configuration</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          label="API URL"
                          value={step.config?.recordSearchConfig?.api?.url || ''}
                          onChange={(e) => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              api: { ...currentConfig.api, url: e.target.value }
                            });
                          }}
                          fullWidth
                          placeholder="https://api.example.com/patients/search"
                          helperText="Supports {{variable}} placeholders"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Method</InputLabel>
                          <Select
                            value={step.config?.recordSearchConfig?.api?.method || 'POST'}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, method: e.target.value }
                              });
                            }}
                            label="Method"
                          >
                            <MenuItem value="GET">GET</MenuItem>
                            <MenuItem value="POST">POST</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          size="small"
                          label="Response Data Path"
                          value={step.config?.recordSearchConfig?.api?.responseDataPath || ''}
                          onChange={(e) => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              api: { ...currentConfig.api, responseDataPath: e.target.value }
                            });
                          }}
                          fullWidth
                          placeholder="e.g., data.results"
                          helperText="Path to array in response"
                        />
                      </Grid>
                    </Grid>
                    
                    {/* Request Body Template - only for POST */}
                    {(step.config?.recordSearchConfig?.api?.method || 'POST') === 'POST' && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          size="small"
                          label="Request Body Template (for POST)"
                          value={step.config?.recordSearchConfig?.api?.bodyTemplate || ''}
                          onChange={(e) => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              api: { ...currentConfig.api, bodyTemplate: e.target.value }
                            });
                          }}
                          fullWidth
                          multiline
                          rows={4}
                          placeholder='{"firstName":"{{firstName}}","lastName":"{{lastName}}"}'
                          helperText="JSON template with {{fieldName}} placeholders. Leave empty to send search field values directly."
                        />
                      </Box>
                    )}
                    
                    {/* Authentication Section */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                        🔐 Authentication
                      </Typography>
                      
                      <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Authentication Type</InputLabel>
                        <Select
                          value={step.config?.recordSearchConfig?.api?.authType || 'none'}
                          onChange={(e) => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              api: { ...currentConfig.api, authType: e.target.value }
                            });
                          }}
                          label="Authentication Type"
                        >
                          <MenuItem value="none">No Authentication</MenuItem>
                          <MenuItem value="bearer_token">Bearer Token</MenuItem>
                          <MenuItem value="basic_auth">Basic Auth</MenuItem>
                          <MenuItem value="jwt_token">JWT Token</MenuItem>
                          <MenuItem value="api_key">API Key</MenuItem>
                          <MenuItem value="digest_auth">Digest Auth</MenuItem>
                          <MenuItem value="oauth1">OAuth 1.0</MenuItem>
                          <MenuItem value="oauth2">OAuth 2.0</MenuItem>
                        </Select>
                      </FormControl>
                      
                      {/* Bearer Token */}
                      {step.config?.recordSearchConfig?.api?.authType === 'bearer_token' && (
                        <TextField
                          size="small"
                          label="Bearer Token"
                          value={step.config?.recordSearchConfig?.api?.bearerToken || ''}
                          onChange={(e) => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              api: { ...currentConfig.api, bearerToken: e.target.value }
                            });
                          }}
                          fullWidth
                          placeholder="Enter token or use {{tokenVariable}}"
                          helperText="Token will be added to Authorization header"
                        />
                      )}
                      
                      {/* Basic Auth */}
                      {step.config?.recordSearchConfig?.api?.authType === 'basic_auth' && (
                        <Box display="flex" gap={2}>
                          <TextField
                            size="small"
                            label="Username"
                            value={step.config?.recordSearchConfig?.api?.basicUsername || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, basicUsername: e.target.value }
                              });
                            }}
                            fullWidth
                            placeholder="Username or {{usernameVar}}"
                          />
                          <TextField
                            size="small"
                            label="Password"
                            type="password"
                            value={step.config?.recordSearchConfig?.api?.basicPassword || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, basicPassword: e.target.value }
                              });
                            }}
                            fullWidth
                            placeholder="Password or {{passwordVar}}"
                          />
                        </Box>
                      )}
                      
                      {/* JWT Token */}
                      {step.config?.recordSearchConfig?.api?.authType === 'jwt_token' && (
                        <Box display="flex" flexDirection="column" gap={2}>
                          <TextField
                            size="small"
                            label="JWT Token"
                            value={step.config?.recordSearchConfig?.api?.jwtToken || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, jwtToken: e.target.value }
                              });
                            }}
                            fullWidth
                            placeholder="Enter JWT token or use {{jwtVariable}}"
                          />
                          <FormControl size="small" fullWidth>
                            <InputLabel>Add to</InputLabel>
                            <Select
                              value={step.config?.recordSearchConfig?.api?.jwtLocation || 'header'}
                              onChange={(e) => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  api: { ...currentConfig.api, jwtLocation: e.target.value }
                                });
                              }}
                              label="Add to"
                            >
                              <MenuItem value="header">Authorization Header</MenuItem>
                              <MenuItem value="query">Query Parameter</MenuItem>
                              <MenuItem value="cookie">Cookie</MenuItem>
                            </Select>
                          </FormControl>
                          {step.config?.recordSearchConfig?.api?.jwtLocation === 'query' && (
                            <TextField
                              size="small"
                              label="Query Parameter Name"
                              value={step.config?.recordSearchConfig?.api?.jwtQueryParam || 'token'}
                              onChange={(e) => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  api: { ...currentConfig.api, jwtQueryParam: e.target.value }
                                });
                              }}
                              fullWidth
                            />
                          )}
                          {step.config?.recordSearchConfig?.api?.jwtLocation === 'cookie' && (
                            <TextField
                              size="small"
                              label="Cookie Name"
                              value={step.config?.recordSearchConfig?.api?.jwtCookieName || 'jwt'}
                              onChange={(e) => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  api: { ...currentConfig.api, jwtCookieName: e.target.value }
                                });
                              }}
                              fullWidth
                            />
                          )}
                        </Box>
                      )}
                      
                      {/* API Key */}
                      {step.config?.recordSearchConfig?.api?.authType === 'api_key' && (
                        <Box display="flex" flexDirection="column" gap={2}>
                          <TextField
                            size="small"
                            label="API Key"
                            value={step.config?.recordSearchConfig?.api?.apiKey || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, apiKey: e.target.value }
                              });
                            }}
                            fullWidth
                            placeholder="Enter API key or use {{keyVariable}}"
                          />
                          <FormControl size="small" fullWidth>
                            <InputLabel>Add to</InputLabel>
                            <Select
                              value={step.config?.recordSearchConfig?.api?.apiKeyLocation || 'header'}
                              onChange={(e) => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  api: { ...currentConfig.api, apiKeyLocation: e.target.value }
                                });
                              }}
                              label="Add to"
                            >
                              <MenuItem value="header">Header</MenuItem>
                              <MenuItem value="query">Query Parameter</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label={step.config?.recordSearchConfig?.api?.apiKeyLocation === 'header' ? 'Header Name' : 'Parameter Name'}
                            value={step.config?.recordSearchConfig?.api?.apiKeyName || (step.config?.recordSearchConfig?.api?.apiKeyLocation === 'header' ? 'X-API-Key' : 'api_key')}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, apiKeyName: e.target.value }
                              });
                            }}
                            fullWidth
                            placeholder={step.config?.recordSearchConfig?.api?.apiKeyLocation === 'header' ? 'X-API-Key' : 'api_key'}
                          />
                        </Box>
                      )}
                      
                      {/* Digest Auth */}
                      {step.config?.recordSearchConfig?.api?.authType === 'digest_auth' && (
                        <Box display="flex" gap={2}>
                          <TextField
                            size="small"
                            label="Username"
                            value={step.config?.recordSearchConfig?.api?.digestUsername || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, digestUsername: e.target.value }
                              });
                            }}
                            fullWidth
                            placeholder="Username or {{usernameVar}}"
                          />
                          <TextField
                            size="small"
                            label="Password"
                            type="password"
                            value={step.config?.recordSearchConfig?.api?.digestPassword || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, digestPassword: e.target.value }
                              });
                            }}
                            fullWidth
                            placeholder="Password or {{passwordVar}}"
                          />
                        </Box>
                      )}
                      
                      {/* OAuth 1.0 */}
                      {step.config?.recordSearchConfig?.api?.authType === 'oauth1' && (
                        <Box display="flex" flexDirection="column" gap={2}>
                          <TextField
                            size="small"
                            label="Consumer Key"
                            value={step.config?.recordSearchConfig?.api?.oauth1ConsumerKey || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, oauth1ConsumerKey: e.target.value }
                              });
                            }}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label="Consumer Secret"
                            type="password"
                            value={step.config?.recordSearchConfig?.api?.oauth1ConsumerSecret || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, oauth1ConsumerSecret: e.target.value }
                              });
                            }}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label="Access Token"
                            value={step.config?.recordSearchConfig?.api?.oauth1AccessToken || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, oauth1AccessToken: e.target.value }
                              });
                            }}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label="Token Secret"
                            type="password"
                            value={step.config?.recordSearchConfig?.api?.oauth1TokenSecret || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, oauth1TokenSecret: e.target.value }
                              });
                            }}
                            fullWidth
                          />
                        </Box>
                      )}
                      
                      {/* OAuth 2.0 */}
                      {step.config?.recordSearchConfig?.api?.authType === 'oauth2' && (
                        <Box display="flex" flexDirection="column" gap={2}>
                          <TextField
                            size="small"
                            label="Access Token"
                            value={step.config?.recordSearchConfig?.api?.oauth2AccessToken || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, oauth2AccessToken: e.target.value }
                              });
                            }}
                            fullWidth
                            placeholder="Enter access token or {{tokenVariable}}"
                          />
                          <FormControl size="small" fullWidth>
                            <InputLabel>Token Type</InputLabel>
                            <Select
                              value={step.config?.recordSearchConfig?.api?.oauth2TokenType || 'Bearer'}
                              onChange={(e) => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  api: { ...currentConfig.api, oauth2TokenType: e.target.value }
                                });
                              }}
                              label="Token Type"
                            >
                              <MenuItem value="Bearer">Bearer</MenuItem>
                              <MenuItem value="MAC">MAC</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Header Prefix (Optional)"
                            value={step.config?.recordSearchConfig?.api?.oauth2HeaderPrefix || ''}
                            onChange={(e) => {
                              const currentConfig = step.config?.recordSearchConfig || {};
                              handleConfigChange('recordSearchConfig', {
                                ...currentConfig,
                                api: { ...currentConfig.api, oauth2HeaderPrefix: e.target.value }
                              });
                            }}
                            fullWidth
                            placeholder="Leave empty for default"
                          />
                        </Box>
                      )}
                    </Box>
                    
                    {/* Path Parameters Section */}
                    <Box sx={{ mt: 3 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          🛣️ Path Parameters
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            const currentParams = currentConfig.api?.pathParams || [];
                            const newParam = {
                              id: `path-${Date.now()}`,
                              key: '',
                              value: '',
                              description: ''
                            };
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              api: { 
                                ...currentConfig.api, 
                                pathParams: [...currentParams, newParam] 
                              }
                            });
                          }}
                        >
                          Add Path Param
                        </Button>
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Define path parameters for URLs like "/patients/:patientId/records/:recordId"
                      </Typography>
                      
                      {(step.config?.recordSearchConfig?.api?.pathParams || []).length === 0 && (
                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 1 }}>
                          No path parameters. Click "Add Path Param" if your URL contains :param placeholders.
                        </Typography>
                      )}
                      
                      {(step.config?.recordSearchConfig?.api?.pathParams || []).map((param, index) => (
                        <Card key={param.id} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                            <Box flexGrow={1}>
                              <Grid container spacing={2}>
                                <Grid item xs={5}>
                                  <TextField
                                    size="small"
                                    label="Parameter Name"
                                    value={param.key || ''}
                                    onChange={(e) => {
                                      const currentConfig = step.config?.recordSearchConfig || {};
                                      const updatedParams = [...(currentConfig.api?.pathParams || [])];
                                      updatedParams[index] = { ...param, key: e.target.value };
                                      handleConfigChange('recordSearchConfig', {
                                        ...currentConfig,
                                        api: { ...currentConfig.api, pathParams: updatedParams }
                                      });
                                    }}
                                    fullWidth
                                    placeholder="patientId"
                                    helperText="Name in URL path"
                                  />
                                </Grid>
                                <Grid item xs={7}>
                                  <TextField
                                    size="small"
                                    label="Value"
                                    value={param.value || ''}
                                    onChange={(e) => {
                                      const currentConfig = step.config?.recordSearchConfig || {};
                                      const updatedParams = [...(currentConfig.api?.pathParams || [])];
                                      updatedParams[index] = { ...param, value: e.target.value };
                                      handleConfigChange('recordSearchConfig', {
                                        ...currentConfig,
                                        api: { ...currentConfig.api, pathParams: updatedParams }
                                      });
                                    }}
                                    fullWidth
                                    placeholder="{{patientId}} or static value"
                                    helperText="Variable or static value"
                                  />
                                </Grid>
                              </Grid>
                              {param.key && (
                                <Box sx={{ mt: 1, p: 1, backgroundColor: '#e8f4fd', borderRadius: 1, border: '1px solid #b3d9ff' }}>
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#1976d2' }}>
                                    .../<span style={{ fontWeight: 'bold', color: '#d32f2f' }}>:{param.key}</span>/...
                                    {param.value && <span style={{ color: '#388e3c', marginLeft: 8 }}>→ {param.value}</span>}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                const updatedParams = (currentConfig.api?.pathParams || []).filter((_, i) => i !== index);
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  api: { ...currentConfig.api, pathParams: updatedParams }
                                });
                              }}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                    
                    {/* Query Parameters Section */}
                    <Box sx={{ mt: 3 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          ❓ Query Parameters
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            const currentParams = currentConfig.api?.queryParams || [];
                            const newParam = {
                              id: `query-${Date.now()}`,
                              key: '',
                              value: '',
                              description: '',
                              enabled: true
                            };
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              api: { 
                                ...currentConfig.api, 
                                queryParams: [...currentParams, newParam] 
                              }
                            });
                          }}
                        >
                          Add Query Param
                        </Button>
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Add query parameters to your request (e.g., ?page=1&limit=10). These are added in addition to search field values.
                      </Typography>
                      
                      {(step.config?.recordSearchConfig?.api?.queryParams || []).length === 0 && (
                        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 1 }}>
                          No query parameters. Click "Add Query Param" to add URL query parameters.
                        </Typography>
                      )}
                      
                      {(step.config?.recordSearchConfig?.api?.queryParams || []).map((param, index) => (
                        <Card key={param.id} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                            <Box flexGrow={1}>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={param.enabled !== false}
                                      onChange={(e) => {
                                        const currentConfig = step.config?.recordSearchConfig || {};
                                        const updatedParams = [...(currentConfig.api?.queryParams || [])];
                                        updatedParams[index] = { ...param, enabled: e.target.checked };
                                        handleConfigChange('recordSearchConfig', {
                                          ...currentConfig,
                                          api: { ...currentConfig.api, queryParams: updatedParams }
                                        });
                                      }}
                                    />
                                  }
                                  label={param.enabled !== false ? "Enabled" : "Disabled"}
                                  sx={{ margin: 0 }}
                                />
                              </Box>
                              <Grid container spacing={2}>
                                <Grid item xs={5}>
                                  <TextField
                                    size="small"
                                    label="Parameter Name"
                                    value={param.key || ''}
                                    onChange={(e) => {
                                      const currentConfig = step.config?.recordSearchConfig || {};
                                      const updatedParams = [...(currentConfig.api?.queryParams || [])];
                                      updatedParams[index] = { ...param, key: e.target.value };
                                      handleConfigChange('recordSearchConfig', {
                                        ...currentConfig,
                                        api: { ...currentConfig.api, queryParams: updatedParams }
                                      });
                                    }}
                                    fullWidth
                                    placeholder="limit"
                                    helperText="Query param name"
                                    disabled={param.enabled === false}
                                  />
                                </Grid>
                                <Grid item xs={7}>
                                  <TextField
                                    size="small"
                                    label="Value"
                                    value={param.value || ''}
                                    onChange={(e) => {
                                      const currentConfig = step.config?.recordSearchConfig || {};
                                      const updatedParams = [...(currentConfig.api?.queryParams || [])];
                                      updatedParams[index] = { ...param, value: e.target.value };
                                      handleConfigChange('recordSearchConfig', {
                                        ...currentConfig,
                                        api: { ...currentConfig.api, queryParams: updatedParams }
                                      });
                                    }}
                                    fullWidth
                                    placeholder="50 or {{limitVariable}}"
                                    helperText="Variable or static value"
                                    disabled={param.enabled === false}
                                  />
                                </Grid>
                              </Grid>
                              {param.key && param.enabled !== false && (
                                <Box sx={{ mt: 1, p: 1, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#e65100' }}>
                                    ?...<span style={{ fontWeight: 'bold' }}>{param.key}</span>=
                                    <span style={{ color: '#388e3c' }}>{param.value || '...'}</span>
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                const updatedParams = (currentConfig.api?.queryParams || []).filter((_, i) => i !== index);
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  api: { ...currentConfig.api, queryParams: updatedParams }
                                });
                              }}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                    
                    {/* Response Mapping Section */}
                    <Box sx={{ mt: 3 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          📋 Response Mapping
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            const currentMappings = currentConfig.api?.responseMappings || [];
                            const newMapping = {
                              id: `rmap-${Date.now()}`,
                              variableName: '',
                              jsonPath: '',
                              dataType: 'string',
                              isArray: false,
                              arrayItemProperties: ''
                            };
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              api: { 
                                ...currentConfig.api, 
                                responseMappings: [...currentMappings, newMapping] 
                              }
                            });
                          }}
                        >
                          Add Mapping
                        </Button>
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Map API response fields to use in Result Table Columns. The Column Key should match the Variable Name.
                      </Typography>
                      
                      {(step.config?.recordSearchConfig?.api?.responseMappings || []).length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1, mb: 2 }}>
                          <Typography variant="body2">
                            No response mappings defined. Add mappings to extract specific data from API responses.
                          </Typography>
                        </Box>
                      )}
                      
                      {(step.config?.recordSearchConfig?.api?.responseMappings || []).map((mapping, index) => (
                        <Card key={mapping.id} sx={{ mb: 2, p: 2, backgroundColor: '#f0f8ff', border: '1px solid #e1e7ff' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                            <Box flexGrow={1}>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <TextField
                                    size="small"
                                    label="Variable Name"
                                    value={mapping.variableName || ''}
                                    onChange={(e) => {
                                      const currentConfig = step.config?.recordSearchConfig || {};
                                      const updatedMappings = [...(currentConfig.api?.responseMappings || [])];
                                      updatedMappings[index] = { ...mapping, variableName: e.target.value };
                                      handleConfigChange('recordSearchConfig', {
                                        ...currentConfig,
                                        api: { ...currentConfig.api, responseMappings: updatedMappings }
                                      });
                                    }}
                                    fullWidth
                                    placeholder="e.g., patientId, firstName"
                                    helperText="Use this as Column Key in table"
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField
                                    size="small"
                                    label="JSON Path"
                                    value={mapping.jsonPath || ''}
                                    onChange={(e) => {
                                      const currentConfig = step.config?.recordSearchConfig || {};
                                      const updatedMappings = [...(currentConfig.api?.responseMappings || [])];
                                      updatedMappings[index] = { ...mapping, jsonPath: e.target.value };
                                      handleConfigChange('recordSearchConfig', {
                                        ...currentConfig,
                                        api: { ...currentConfig.api, responseMappings: updatedMappings }
                                      });
                                    }}
                                    fullWidth
                                    placeholder="e.g., patient_id, first_name"
                                    helperText="Path in API response (dot notation for nested)"
                                  />
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Data Type</InputLabel>
                                    <Select
                                      value={mapping.dataType || 'string'}
                                      onChange={(e) => {
                                        const currentConfig = step.config?.recordSearchConfig || {};
                                        const updatedMappings = [...(currentConfig.api?.responseMappings || [])];
                                        updatedMappings[index] = { ...mapping, dataType: e.target.value };
                                        handleConfigChange('recordSearchConfig', {
                                          ...currentConfig,
                                          api: { ...currentConfig.api, responseMappings: updatedMappings }
                                        });
                                      }}
                                      label="Data Type"
                                    >
                                      <MenuItem value="string">String</MenuItem>
                                      <MenuItem value="number">Number</MenuItem>
                                      <MenuItem value="boolean">Boolean</MenuItem>
                                      <MenuItem value="date">Date</MenuItem>
                                      <MenuItem value="object">Object</MenuItem>
                                      <MenuItem value="array">Array</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={mapping.isArray || false}
                                        onChange={(e) => {
                                          const currentConfig = step.config?.recordSearchConfig || {};
                                          const updatedMappings = [...(currentConfig.api?.responseMappings || [])];
                                          updatedMappings[index] = { ...mapping, isArray: e.target.checked };
                                          handleConfigChange('recordSearchConfig', {
                                            ...currentConfig,
                                            api: { ...currentConfig.api, responseMappings: updatedMappings }
                                          });
                                        }}
                                      />
                                    }
                                    label="Is Array"
                                  />
                                </Grid>
                                
                                {mapping.isArray && (
                                  <Grid item xs={12}>
                                    <TextField
                                      size="small"
                                      label="Array Item Properties (comma-separated)"
                                      value={mapping.arrayItemProperties || ''}
                                      onChange={(e) => {
                                        const currentConfig = step.config?.recordSearchConfig || {};
                                        const updatedMappings = [...(currentConfig.api?.responseMappings || [])];
                                        updatedMappings[index] = { ...mapping, arrayItemProperties: e.target.value };
                                        handleConfigChange('recordSearchConfig', {
                                          ...currentConfig,
                                          api: { ...currentConfig.api, responseMappings: updatedMappings }
                                        });
                                      }}
                                      fullWidth
                                      placeholder="e.g., id, name, value, date"
                                      helperText="Properties available in each array item"
                                    />
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                            
                            <IconButton
                              size="small"
                              onClick={() => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                const updatedMappings = (currentConfig.api?.responseMappings || []).filter((_, i) => i !== index);
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  api: { ...currentConfig.api, responseMappings: updatedMappings }
                                });
                              }}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Card>
                      ))}
                      
                      {/* Helper box showing available mappings for columns */}
                      {(step.config?.recordSearchConfig?.api?.responseMappings || []).length > 0 && (
                        <Box sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 1, mt: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                            💡 Available Column Keys from Response Mapping:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {(step.config?.recordSearchConfig?.api?.responseMappings || []).map(m => (
                              m.variableName && (
                                <Chip 
                                  key={m.id} 
                                  label={m.variableName} 
                                  size="small" 
                                  color="success" 
                                  variant="outlined"
                                />
                              )
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                
                {/* SQL Configuration */}
                {step.config?.recordSearchConfig?.dataSource === 'sql' && (
                  <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>SQL Configuration</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          label="Table Name"
                          value={step.config?.recordSearchConfig?.sql?.table || ''}
                          onChange={(e) => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              sql: { ...currentConfig.sql, table: e.target.value }
                            });
                          }}
                          fullWidth
                          placeholder="e.g., patients"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          label="Raw SQL Query (Optional)"
                          value={step.config?.recordSearchConfig?.sql?.rawQuery || ''}
                          onChange={(e) => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              sql: { ...currentConfig.sql, rawQuery: e.target.value }
                            });
                          }}
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="SELECT * FROM patients WHERE lastName LIKE '%{{lastName}}%'"
                          helperText="Raw SQL query with {{fieldName}} placeholders. If provided, table name is ignored."
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
              
              <Divider sx={{ my: 1 }} />
              
              {/* Result Table Columns */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  📊 Result Table Columns
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Define the columns to display in the search results table. Drag to reorder.
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentConfig = step.config?.recordSearchConfig || {};
                      const currentColumns = currentConfig.resultColumns || [];
                      const newColumn = {
                        id: `col-${Date.now()}`,
                        key: '',
                        label: '',
                        dataPath: '',
                        isSelectable: false,
                        type: 'text'
                      };
                      handleConfigChange('recordSearchConfig', {
                        ...currentConfig,
                        resultColumns: [...currentColumns, newColumn]
                      });
                    }}
                  >
                    Add Column
                  </Button>
                </Box>
                
                {(step.config?.recordSearchConfig?.resultColumns || []).length === 0 && (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 2 }}>
                    No columns added yet. Click "Add Column" to define table columns.
                  </Typography>
                )}
                
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleRecordSearchColumnsDragEnd}
                >
                  <SortableContext 
                    items={step.config?.recordSearchConfig?.resultColumns || []}
                    strategy={verticalListSortingStrategy}
                  >
                    {(step.config?.recordSearchConfig?.resultColumns || []).map((column, index) => (
                      <SortableResultColumnCard
                        key={column.id}
                        column={column}
                        index={index}
                        isSelectable={column.isSelectable}
                        onColumnDelete={(idx) => {
                          const currentConfig = step.config?.recordSearchConfig || {};
                          const updatedColumns = currentConfig.resultColumns.filter((_, i) => i !== idx);
                          handleConfigChange('recordSearchConfig', {
                            ...currentConfig,
                            resultColumns: updatedColumns
                          });
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                          Column {index + 1} {column.isSelectable && <Chip size="small" label="Selectable" color="primary" sx={{ ml: 1 }} />}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <TextField
                              size="small"
                              label="Column Key"
                              value={column.key || ''}
                              onChange={(e) => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                const updatedColumns = [...currentConfig.resultColumns];
                                updatedColumns[index] = { ...column, key: e.target.value, dataPath: e.target.value };
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  resultColumns: updatedColumns
                                });
                              }}
                              fullWidth
                              placeholder="e.g., patientId"
                              helperText="Field key in response or from Response Mapping"
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              size="small"
                              label="Label"
                              value={column.label || ''}
                              onChange={(e) => {
                                const currentConfig = step.config?.recordSearchConfig || {};
                                const updatedColumns = [...currentConfig.resultColumns];
                                updatedColumns[index] = { ...column, label: e.target.value };
                                handleConfigChange('recordSearchConfig', {
                                  ...currentConfig,
                                  resultColumns: updatedColumns
                                });
                              }}
                              fullWidth
                              placeholder="e.g., Patient ID"
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={column.type || 'text'}
                                onChange={(e) => {
                                  const currentConfig = step.config?.recordSearchConfig || {};
                                  const updatedColumns = [...currentConfig.resultColumns];
                                  updatedColumns[index] = { ...column, type: e.target.value };
                                  handleConfigChange('recordSearchConfig', {
                                    ...currentConfig,
                                    resultColumns: updatedColumns
                                  });
                                }}
                                label="Type"
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="currency">Currency</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={column.isSelectable || false}
                                  onChange={(e) => {
                                    const currentConfig = step.config?.recordSearchConfig || {};
                                    const updatedColumns = [...currentConfig.resultColumns];
                                    updatedColumns[index] = { ...column, isSelectable: e.target.checked };
                                    handleConfigChange('recordSearchConfig', {
                                      ...currentConfig,
                                      resultColumns: updatedColumns
                                    });
                                  }}
                                />
                              }
                              label="Clickable for Selection (users click this column to select the row)"
                            />
                          </Grid>
                        </Grid>
                      </SortableResultColumnCard>
                    ))}
                  </SortableContext>
                </DndContext>
              </Box>
              
              <Divider sx={{ my: 1 }} />
              
              {/* Variable Mappings */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  🔗 Variable Mappings
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Map column values from the selected row to flow variables
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentConfig = step.config?.recordSearchConfig || {};
                      const currentMappings = currentConfig.variableMappings || [];
                      const newMapping = {
                        columnKey: '',
                        variableName: ''
                      };
                      handleConfigChange('recordSearchConfig', {
                        ...currentConfig,
                        variableMappings: [...currentMappings, newMapping]
                      });
                    }}
                  >
                    Add Mapping
                  </Button>
                </Box>
                
                {(step.config?.recordSearchConfig?.variableMappings || []).length === 0 && (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 2 }}>
                    No mappings added yet. Click "Add Mapping" to map column values to variables.
                  </Typography>
                )}
                
                {(step.config?.recordSearchConfig?.variableMappings || []).map((mapping, index) => (
                  <Box key={index} display="flex" gap={2} alignItems="center" mb={1}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Column</InputLabel>
                      <Select
                        value={mapping.columnKey || ''}
                        onChange={(e) => {
                          const currentConfig = step.config?.recordSearchConfig || {};
                          const updatedMappings = [...currentConfig.variableMappings];
                          updatedMappings[index] = { ...mapping, columnKey: e.target.value };
                          handleConfigChange('recordSearchConfig', {
                            ...currentConfig,
                            variableMappings: updatedMappings
                          });
                        }}
                        label="Column"
                      >
                        {(step.config?.recordSearchConfig?.resultColumns || []).map(col => (
                          <MenuItem key={col.key} value={col.key}>{col.label || col.key}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="body2">→</Typography>
                    <TextField
                      size="small"
                      label="Variable Name"
                      value={mapping.variableName || ''}
                      onChange={(e) => {
                        const currentConfig = step.config?.recordSearchConfig || {};
                        const updatedMappings = [...currentConfig.variableMappings];
                        updatedMappings[index] = { ...mapping, variableName: e.target.value };
                        handleConfigChange('recordSearchConfig', {
                          ...currentConfig,
                          variableMappings: updatedMappings
                        });
                      }}
                      placeholder="e.g., patientId"
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const currentConfig = step.config?.recordSearchConfig || {};
                        const updatedMappings = currentConfig.variableMappings.filter((_, i) => i !== index);
                        handleConfigChange('recordSearchConfig', {
                          ...currentConfig,
                          variableMappings: updatedMappings
                        });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              
              <Divider sx={{ my: 1 }} />
              
              {/* Selected Display Fields */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  📋 Selected Record Display
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Choose which column values to display in the header after a row is selected
                </Typography>
                <Box display="flex" justifyContent="flex-end" mb={2}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentConfig = step.config?.recordSearchConfig || {};
                      const currentFields = currentConfig.selectedDisplayFields || [];
                      const newField = {
                        label: '',
                        columnKey: ''
                      };
                      handleConfigChange('recordSearchConfig', {
                        ...currentConfig,
                        selectedDisplayFields: [...currentFields, newField]
                      });
                    }}
                  >
                    Add Display Field
                  </Button>
                </Box>
                
                {(step.config?.recordSearchConfig?.selectedDisplayFields || []).map((field, index) => (
                  <Box key={index} display="flex" gap={2} alignItems="center" mb={1}>
                    <TextField
                      size="small"
                      label="Display Label"
                      value={field.label || ''}
                      onChange={(e) => {
                        const currentConfig = step.config?.recordSearchConfig || {};
                        const updatedFields = [...currentConfig.selectedDisplayFields];
                        updatedFields[index] = { ...field, label: e.target.value };
                        handleConfigChange('recordSearchConfig', {
                          ...currentConfig,
                          selectedDisplayFields: updatedFields
                        });
                      }}
                      placeholder="e.g., Patient ID"
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Column</InputLabel>
                      <Select
                        value={field.columnKey || ''}
                        onChange={(e) => {
                          const currentConfig = step.config?.recordSearchConfig || {};
                          const updatedFields = [...currentConfig.selectedDisplayFields];
                          updatedFields[index] = { ...field, columnKey: e.target.value };
                          handleConfigChange('recordSearchConfig', {
                            ...currentConfig,
                            selectedDisplayFields: updatedFields
                          });
                        }}
                        label="Column"
                      >
                        {(step.config?.recordSearchConfig?.resultColumns || []).map(col => (
                          <MenuItem key={col.key} value={col.key}>{col.label || col.key}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const currentConfig = step.config?.recordSearchConfig || {};
                        const updatedFields = currentConfig.selectedDisplayFields.filter((_, i) => i !== index);
                        handleConfigChange('recordSearchConfig', {
                          ...currentConfig,
                          selectedDisplayFields: updatedFields
                        });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              
              <Divider sx={{ my: 1 }} />
              
              {/* Additional Settings */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  ⚙️ Additional Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      label="Max Results"
                      type="number"
                      value={step.config?.recordSearchConfig?.maxResults || 50}
                      onChange={(e) => {
                        const currentConfig = step.config?.recordSearchConfig || {};
                        handleConfigChange('recordSearchConfig', {
                          ...currentConfig,
                          maxResults: parseInt(e.target.value) || 50
                        });
                      }}
                      fullWidth
                      inputProps={{ min: 1, max: 500 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      label="No Results Message"
                      value={step.config?.recordSearchConfig?.noResultsMessage || 'No records found matching your search criteria.'}
                      onChange={(e) => {
                        const currentConfig = step.config?.recordSearchConfig || {};
                        handleConfigChange('recordSearchConfig', {
                          ...currentConfig,
                          noResultsMessage: e.target.value
                        });
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={step.config?.recordSearchConfig?.requireSelection !== false}
                          onChange={(e) => {
                            const currentConfig = step.config?.recordSearchConfig || {};
                            handleConfigChange('recordSearchConfig', {
                              ...currentConfig,
                              requireSelection: e.target.checked
                            });
                          }}
                        />
                      }
                      label="Require selection before continuing"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Multi-Step Wizard Configuration */}
      {step.config?.interfaceType === 'wizard' && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ my: 2 }} />
          
          {/* Wizard Global Settings */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">⚙️ Wizard Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    label="Submit Button Text"
                    value={step.config?.wizardConfig?.submitButtonText || 'Complete'}
                    onChange={(e) => {
                      const wizardConfig = step.config?.wizardConfig || {};
                      handleConfigChange('wizardConfig', { ...wizardConfig, submitButtonText: e.target.value });
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Progress Position</InputLabel>
                    <Select
                      value={step.config?.wizardConfig?.progressPosition || 'top'}
                      onChange={(e) => {
                        const wizardConfig = step.config?.wizardConfig || {};
                        handleConfigChange('wizardConfig', { ...wizardConfig, progressPosition: e.target.value });
                      }}
                      label="Progress Position"
                    >
                      <MenuItem value="top">Top</MenuItem>
                      <MenuItem value="left">Left Sidebar</MenuItem>
                      <MenuItem value="bottom">Bottom</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={step.config?.wizardConfig?.showStepNumbers !== false}
                        onChange={(e) => {
                          const wizardConfig = step.config?.wizardConfig || {};
                          handleConfigChange('wizardConfig', { ...wizardConfig, showStepNumbers: e.target.checked });
                        }}
                      />
                    }
                    label="Show Step Numbers"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={step.config?.wizardConfig?.allowStepNavigation || false}
                        onChange={(e) => {
                          const wizardConfig = step.config?.wizardConfig || {};
                          handleConfigChange('wizardConfig', { ...wizardConfig, allowStepNavigation: e.target.checked });
                        }}
                      />
                    }
                    label="Allow Clicking Stepper to Navigate"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          
          {/* Wizard Steps Configuration */}
          <Accordion defaultExpanded sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">📋 Wizard Steps</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2">
                    Define the steps in your wizard. Each step can have a different interface type.
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentSteps = step.config?.wizardSteps || [];
                      const newStep = {
                        id: `wizard-step-${Date.now()}`,
                        stepTitle: `Step ${currentSteps.length + 1}`,
                        stepDescription: '',
                        order: currentSteps.length,
                        stepInterfaceType: 'form',
                        stepConfig: {
                          title: '',
                          instructions: '',
                          formFields: []
                        },
                        allowBack: true,
                        allowSkip: false,
                        required: true
                      };
                      handleConfigChange('wizardSteps', [...currentSteps, newStep]);
                    }}
                  >
                    Add Step
                  </Button>
                </Box>
                
                {(!step.config?.wizardSteps || step.config.wizardSteps.length === 0) && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No wizard steps defined. Click "Add Step" to create your first step.
                  </Alert>
                )}
                
                {/* Render each wizard step */}
                {(step.config?.wizardSteps || []).map((wizardStep, stepIndex) => (
                  <Card key={wizardStep.id} sx={{ mb: 2, border: '2px solid #1976d2' }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flexGrow={1}>
                          <Grid container spacing={2}>
                            {/* Step Basic Info */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ color: '#1976d2', mb: 1 }}>
                                Step {stepIndex + 1} Configuration
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={6}>
                              <TextField
                                size="small"
                                label="Step Title"
                                value={wizardStep.stepTitle}
                                onChange={(e) => {
                                  const updated = [...step.config.wizardSteps];
                                  updated[stepIndex] = { ...wizardStep, stepTitle: e.target.value };
                                  handleConfigChange('wizardSteps', updated);
                                }}
                                fullWidth
                                required
                              />
                            </Grid>
                            
                            <Grid item xs={6}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Interface Type</InputLabel>
                                <Select
                                  value={wizardStep.stepInterfaceType}
                                  onChange={(e) => {
                                    const updated = [...step.config.wizardSteps];
                                    updated[stepIndex] = { ...wizardStep, stepInterfaceType: e.target.value };
                                    handleConfigChange('wizardSteps', updated);
                                  }}
                                  label="Interface Type"
                                >
                                  <MenuItem value="form">Form</MenuItem>
                                  <MenuItem value="table_editor">Table Editor</MenuItem>
                                  <MenuItem value="table_with_header">Table with Header</MenuItem>
                                  <MenuItem value="barcode_scanner">Barcode Scanner</MenuItem>
                                  <MenuItem value="file_upload">File Upload</MenuItem>
                                  <MenuItem value="message">Message/Review</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <TextField
                                size="small"
                                label="Step Description"
                                value={wizardStep.stepDescription || ''}
                                onChange={(e) => {
                                  const updated = [...step.config.wizardSteps];
                                  updated[stepIndex] = { ...wizardStep, stepDescription: e.target.value };
                                  handleConfigChange('wizardSteps', updated);
                                }}
                                fullWidth
                                multiline
                                rows={2}
                                placeholder="Optional description shown to user"
                              />
                            </Grid>
                            
                            {/* Step Options */}
                            <Grid item xs={4}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={wizardStep.allowBack !== false}
                                    onChange={(e) => {
                                      const updated = [...step.config.wizardSteps];
                                      updated[stepIndex] = { ...wizardStep, allowBack: e.target.checked };
                                      handleConfigChange('wizardSteps', updated);
                                    }}
                                  />
                                }
                                label="Allow Back"
                              />
                            </Grid>
                            
                            <Grid item xs={4}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={wizardStep.allowSkip || false}
                                    onChange={(e) => {
                                      const updated = [...step.config.wizardSteps];
                                      updated[stepIndex] = { ...wizardStep, allowSkip: e.target.checked };
                                      handleConfigChange('wizardSteps', updated);
                                    }}
                                  />
                                }
                                label="Allow Skip"
                              />
                            </Grid>
                            
                            <Grid item xs={4}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={wizardStep.required !== false}
                                    onChange={(e) => {
                                      const updated = [...step.config.wizardSteps];
                                      updated[stepIndex] = { ...wizardStep, required: e.target.checked };
                                      handleConfigChange('wizardSteps', updated);
                                    }}
                                  />
                                }
                                label="Required"
                              />
                            </Grid>
                            
                            {/* Step-specific configuration based on interface type */}
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                Step Content Configuration
                              </Typography>
                              
                              {/* Render configuration UI based on stepInterfaceType */}
                              {wizardStep.stepInterfaceType === 'form' && (
                                <Box>
                                  <TextField
                                    size="small"
                                    label="Form Title"
                                    value={wizardStep.stepConfig?.title || ''}
                                    onChange={(e) => {
                                      const updated = [...step.config.wizardSteps];
                                      updated[stepIndex] = {
                                        ...wizardStep,
                                        stepConfig: { ...wizardStep.stepConfig, title: e.target.value }
                                      };
                                      handleConfigChange('wizardSteps', updated);
                                    }}
                                    fullWidth
                                    sx={{ mt: 1 }}
                                  />
                                  
                                  <TextField
                                    size="small"
                                    label="Instructions"
                                    value={wizardStep.stepConfig?.instructions || ''}
                                    onChange={(e) => {
                                      const updated = [...step.config.wizardSteps];
                                      updated[stepIndex] = {
                                        ...wizardStep,
                                        stepConfig: { ...wizardStep.stepConfig, instructions: e.target.value }
                                      };
                                      handleConfigChange('wizardSteps', updated);
                                    }}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    sx={{ mt: 1 }}
                                    placeholder="Instructions for this step..."
                                  />
                                  
                                  {/* Cascading Fields Toggle for this Wizard Step */}
                                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={wizardStep.stepConfig?.enableCascadingFields || false}
                                          onChange={(e) => {
                                            const updated = [...step.config.wizardSteps];
                                            updated[stepIndex] = {
                                              ...wizardStep,
                                              stepConfig: { ...wizardStep.stepConfig, enableCascadingFields: e.target.checked }
                                            };
                                            handleConfigChange('wizardSteps', updated);
                                          }}
                                        />
                                      }
                                      label="Enable Cascading Fields (Progressive Disclosure)"
                                    />
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                      When enabled, fields in this step appear one at a time. Each field is only shown after the previous field is filled.
                                    </Typography>
                                  </Box>
                                  
                                  {/* Add form fields button and count */}
                                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                                    <Button
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() => openWizardFieldDialog(null, stepIndex, null)}
                                    >
                                      Add Form Field
                                    </Button>
                                    
                                    <Typography variant="caption" color="textSecondary">
                                      {(wizardStep.stepConfig?.formFields || []).length} field(s) configured
                                    </Typography>
                                  </Box>
                                  
                                  {/* Show field names if any exist - clickable chips */}
                                  {(wizardStep.stepConfig?.formFields || []).length > 0 && (
                                    <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                      {wizardStep.stepConfig.formFields.map((field, fIdx) => (
                                        <Chip 
                                          key={field.id} 
                                          label={field.label || field.name} 
                                          size="small" 
                                          sx={{ m: 0.5, cursor: 'pointer' }}
                                          onClick={() => openWizardFieldDialog(field, stepIndex, fIdx)}
                                          onDelete={() => {
                                            const updated = [...step.config.wizardSteps];
                                            const currentFields = wizardStep.stepConfig.formFields.filter((_, i) => i !== fIdx);
                                            updated[stepIndex] = {
                                              ...wizardStep,
                                              stepConfig: { ...wizardStep.stepConfig, formFields: currentFields }
                                            };
                                            handleConfigChange('wizardSteps', updated);
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                              
                                {wizardStep.stepInterfaceType === 'table_editor' && (
                                  <Box>
                                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                    <InputLabel>Table Data Source Variable</InputLabel>
                                    <Select
                                      value={wizardStep.stepConfig?.dataSource || ''}
                                      onChange={(e) => {
                                        const updated = [...step.config.wizardSteps];
                                        updated[stepIndex] = {
                                          ...wizardStep,
                                          stepConfig: { ...wizardStep.stepConfig, dataSource: e.target.value }
                                        };
                                        handleConfigChange('wizardSteps', updated);
                                      }}
                                      label="Table Data Source Variable"
                                    >
                                      {availableVariables.map(variable => (
                                        <MenuItem key={variable} value={variable}>
                                          {variable}
                                          {(variable.toLowerCase().includes('items') || 
                                            variable.toLowerCase().includes('products') || 
                                            variable.toLowerCase().includes('data') ||
                                            variable.toLowerCase().includes('list') ||
                                            variable.toLowerCase().includes('array')) && 
                                            <Chip size="small" label="Array" sx={{ ml: 1, height: 16 }} />
                                          }
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    <FormHelperText>
                                      Select any variable that contains array/table data. Variables with "Array" label are likely candidates.
                                    </FormHelperText>
                                  </FormControl>
                                  
                                  {/* Table Columns Configuration */}
                                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Table Columns ({(wizardStep.stepConfig?.tableColumns || []).length})
                                    </Typography>
                                    <Button
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() => {
                                        const updated = [...step.config.wizardSteps];
                                        const currentColumns = wizardStep.stepConfig?.tableColumns || [];
                                        updated[stepIndex] = {
                                          ...wizardStep,
                                          stepConfig: {
                                            ...wizardStep.stepConfig,
                                            tableColumns: [...currentColumns, {
                                              id: `col-${Date.now()}`,
                                              name: `column${currentColumns.length + 1}`,
                                              label: 'New Column',
                                              type: 'text',
                                              editable: true
                                            }]
                                          }
                                        };
                                        handleConfigChange('wizardSteps', updated);
                                      }}
                                    >
                                      Add Column
                                    </Button>
                                  </Box>
                                  
                                  {(wizardStep.stepConfig?.tableColumns || []).map((column, colIdx) => (
                                    <Card key={column.id} sx={{ mb: 1.5, p: 1.5, backgroundColor: '#fafafa', border: '1px solid #ddd' }}>
                                      <Box display="flex" justifyContent="space-between">
                                        <Grid container spacing={1.5} sx={{ flexGrow: 1 }}>
                                          <Grid item xs={4}>
                                            <TextField
                                              size="small"
                                              label="Column Name"
                                              value={column.name}
                                              onChange={(e) => {
                                                const updated = [...step.config.wizardSteps];
                                                const currentColumns = [...wizardStep.stepConfig.tableColumns];
                                                currentColumns[colIdx] = { ...column, name: e.target.value };
                                                updated[stepIndex] = {
                                                  ...wizardStep,
                                                  stepConfig: { ...wizardStep.stepConfig, tableColumns: currentColumns }
                                                };
                                                handleConfigChange('wizardSteps', updated);
                                              }}
                                              fullWidth
                                            />
                                          </Grid>
                                          <Grid item xs={4}>
                                            <TextField
                                              size="small"
                                              label="Column Label"
                                              value={column.label}
                                              onChange={(e) => {
                                                const updated = [...step.config.wizardSteps];
                                                const currentColumns = [...wizardStep.stepConfig.tableColumns];
                                                currentColumns[colIdx] = { ...column, label: e.target.value };
                                                updated[stepIndex] = {
                                                  ...wizardStep,
                                                  stepConfig: { ...wizardStep.stepConfig, tableColumns: currentColumns }
                                                };
                                                handleConfigChange('wizardSteps', updated);
                                              }}
                                              fullWidth
                                            />
                                          </Grid>
                                          <Grid item xs={4}>
                                            <FormControl size="small" fullWidth>
                                              <InputLabel>Type</InputLabel>
                                              <Select
                                                value={column.type || 'text'}
                                                onChange={(e) => {
                                                  const updated = [...step.config.wizardSteps];
                                                  const currentColumns = [...wizardStep.stepConfig.tableColumns];
                                                  currentColumns[colIdx] = { ...column, type: e.target.value };
                                                  updated[stepIndex] = {
                                                    ...wizardStep,
                                                    stepConfig: { ...wizardStep.stepConfig, tableColumns: currentColumns }
                                                  };
                                                  handleConfigChange('wizardSteps', updated);
                                                }}
                                                label="Type"
                                              >
                                                <MenuItem value="text">Text</MenuItem>
                                                <MenuItem value="number">Number</MenuItem>
                                                <MenuItem value="select">Dropdown</MenuItem>
                                                <MenuItem value="checkbox">Checkbox</MenuItem>
                                              </Select>
                                            </FormControl>
                                          </Grid>
                                        </Grid>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const updated = [...step.config.wizardSteps];
                                            const currentColumns = wizardStep.stepConfig.tableColumns.filter((_, i) => i !== colIdx);
                                            updated[stepIndex] = {
                                              ...wizardStep,
                                              stepConfig: { ...wizardStep.stepConfig, tableColumns: currentColumns }
                                            };
                                            handleConfigChange('wizardSteps', updated);
                                          }}
                                          sx={{ color: '#d32f2f', ml: 1 }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Card>
                                  ))}
                                  
                                  {(!wizardStep.stepConfig?.tableColumns || wizardStep.stepConfig.tableColumns.length === 0) && (
                                    <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
                                      No columns defined. Click "Add Column" above.
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              
                                {wizardStep.stepInterfaceType === 'table_with_header' && (
                                  <Box>
                                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                    <InputLabel>Table Data Source Variable</InputLabel>
                                    <Select
                                      value={wizardStep.stepConfig?.dataSource || ''}
                                      onChange={(e) => {
                                        const updated = [...step.config.wizardSteps];
                                        updated[stepIndex] = {
                                          ...wizardStep,
                                          stepConfig: { ...wizardStep.stepConfig, dataSource: e.target.value }
                                        };
                                        handleConfigChange('wizardSteps', updated);
                                      }}
                                      label="Table Data Source Variable"
                                    >
                                      {availableVariables.map(variable => (
                                        <MenuItem key={variable} value={variable}>
                                          {variable}
                                          {(variable.toLowerCase().includes('items') || 
                                            variable.toLowerCase().includes('products') || 
                                            variable.toLowerCase().includes('data') ||
                                            variable.toLowerCase().includes('list') ||
                                            variable.toLowerCase().includes('array')) && 
                                            <Chip size="small" label="Array" sx={{ ml: 1, height: 16 }} />
                                          }
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    <FormHelperText>
                                      Select any variable that contains array/table data for header fields + table rows.
                                    </FormHelperText>
                                  </FormControl>
                                  
                                  <Divider sx={{ my: 2 }} />
                                  
                                  {/* Header Fields Section */}
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Header Fields ({(wizardStep.stepConfig?.headerFields || []).length})
                                    </Typography>
                                    <Button
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() => {
                                        const updated = [...step.config.wizardSteps];
                                        const currentHeaders = wizardStep.stepConfig?.headerFields || [];
                                        updated[stepIndex] = {
                                          ...wizardStep,
                                          stepConfig: {
                                            ...wizardStep.stepConfig,
                                            headerFields: [...currentHeaders, {
                                              id: `header-${Date.now()}`,
                                              name: `header${currentHeaders.length + 1}`,
                                              label: 'New Header Field',
                                              type: 'text'
                                            }]
                                          }
                                        };
                                        handleConfigChange('wizardSteps', updated);
                                      }}
                                    >
                                      Add Header Field
                                    </Button>
                                  </Box>
                                  
                                  {(wizardStep.stepConfig?.headerFields || []).map((header, hIdx) => (
                                    <Card key={header.id} sx={{ mb: 1.5, p: 1.5, backgroundColor: '#fff8e1', border: '1px solid #ffecb3' }}>
                                      <Box display="flex" justifyContent="space-between">
                                        <Grid container spacing={1.5} sx={{ flexGrow: 1 }}>
                                          <Grid item xs={6}>
                                            <TextField
                                              size="small"
                                              label="Field Name"
                                              value={header.name}
                                              onChange={(e) => {
                                                const updated = [...step.config.wizardSteps];
                                                const currentHeaders = [...wizardStep.stepConfig.headerFields];
                                                currentHeaders[hIdx] = { ...header, name: e.target.value };
                                                updated[stepIndex] = {
                                                  ...wizardStep,
                                                  stepConfig: { ...wizardStep.stepConfig, headerFields: currentHeaders }
                                                };
                                                handleConfigChange('wizardSteps', updated);
                                              }}
                                              fullWidth
                                            />
                                          </Grid>
                                          <Grid item xs={6}>
                                            <TextField
                                              size="small"
                                              label="Field Label"
                                              value={header.label}
                                              onChange={(e) => {
                                                const updated = [...step.config.wizardSteps];
                                                const currentHeaders = [...wizardStep.stepConfig.headerFields];
                                                currentHeaders[hIdx] = { ...header, label: e.target.value };
                                                updated[stepIndex] = {
                                                  ...wizardStep,
                                                  stepConfig: { ...wizardStep.stepConfig, headerFields: currentHeaders }
                                                };
                                                handleConfigChange('wizardSteps', updated);
                                              }}
                                              fullWidth
                                            />
                                          </Grid>
                                        </Grid>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const updated = [...step.config.wizardSteps];
                                            const currentHeaders = wizardStep.stepConfig.headerFields.filter((_, i) => i !== hIdx);
                                            updated[stepIndex] = {
                                              ...wizardStep,
                                              stepConfig: { ...wizardStep.stepConfig, headerFields: currentHeaders }
                                            };
                                            handleConfigChange('wizardSteps', updated);
                                          }}
                                          sx={{ color: '#d32f2f', ml: 1 }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Card>
                                  ))}
                                  
                                  <Divider sx={{ my: 2 }} />
                                  
                                  {/* Table Columns Section */}
                                  <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Table Columns ({(wizardStep.stepConfig?.tableColumns || []).length})
                                    </Typography>
                                    <Button
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() => {
                                        const updated = [...step.config.wizardSteps];
                                        const currentColumns = wizardStep.stepConfig?.tableColumns || [];
                                        updated[stepIndex] = {
                                          ...wizardStep,
                                          stepConfig: {
                                            ...wizardStep.stepConfig,
                                            tableColumns: [...currentColumns, {
                                              id: `col-${Date.now()}`,
                                              name: `column${currentColumns.length + 1}`,
                                              label: 'New Column',
                                              type: 'text'
                                            }]
                                          }
                                        };
                                        handleConfigChange('wizardSteps', updated);
                                      }}
                                    >
                                      Add Column
                                    </Button>
                                  </Box>
                                  
                                  {(wizardStep.stepConfig?.tableColumns || []).map((column, colIdx) => (
                                    <Card key={column.id} sx={{ mb: 1.5, p: 1.5, backgroundColor: '#fafafa', border: '1px solid #ddd' }}>
                                      <Box display="flex" justifyContent="space-between">
                                        <Grid container spacing={1.5} sx={{ flexGrow: 1 }}>
                                          <Grid item xs={4}>
                                            <TextField
                                              size="small"
                                              label="Column Name"
                                              value={column.name}
                                              onChange={(e) => {
                                                const updated = [...step.config.wizardSteps];
                                                const currentColumns = [...wizardStep.stepConfig.tableColumns];
                                                currentColumns[colIdx] = { ...column, name: e.target.value };
                                                updated[stepIndex] = {
                                                  ...wizardStep,
                                                  stepConfig: { ...wizardStep.stepConfig, tableColumns: currentColumns }
                                                };
                                                handleConfigChange('wizardSteps', updated);
                                              }}
                                              fullWidth
                                            />
                                          </Grid>
                                          <Grid item xs={4}>
                                            <TextField
                                              size="small"
                                              label="Column Label"
                                              value={column.label}
                                              onChange={(e) => {
                                                const updated = [...step.config.wizardSteps];
                                                const currentColumns = [...wizardStep.stepConfig.tableColumns];
                                                currentColumns[colIdx] = { ...column, label: e.target.value };
                                                updated[stepIndex] = {
                                                  ...wizardStep,
                                                  stepConfig: { ...wizardStep.stepConfig, tableColumns: currentColumns }
                                                };
                                                handleConfigChange('wizardSteps', updated);
                                              }}
                                              fullWidth
                                            />
                                          </Grid>
                                          <Grid item xs={4}>
                                            <FormControl size="small" fullWidth>
                                              <InputLabel>Type</InputLabel>
                                              <Select
                                                value={column.type || 'text'}
                                                onChange={(e) => {
                                                  const updated = [...step.config.wizardSteps];
                                                  const currentColumns = [...wizardStep.stepConfig.tableColumns];
                                                  currentColumns[colIdx] = { ...column, type: e.target.value };
                                                  updated[stepIndex] = {
                                                    ...wizardStep,
                                                    stepConfig: { ...wizardStep.stepConfig, tableColumns: currentColumns }
                                                  };
                                                  handleConfigChange('wizardSteps', updated);
                                                }}
                                                label="Type"
                                              >
                                                <MenuItem value="text">Text</MenuItem>
                                                <MenuItem value="number">Number</MenuItem>
                                                <MenuItem value="select">Dropdown</MenuItem>
                                                <MenuItem value="checkbox">Checkbox</MenuItem>
                                              </Select>
                                            </FormControl>
                                          </Grid>
                                        </Grid>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const updated = [...step.config.wizardSteps];
                                            const currentColumns = wizardStep.stepConfig.tableColumns.filter((_, i) => i !== colIdx);
                                            updated[stepIndex] = {
                                              ...wizardStep,
                                              stepConfig: { ...wizardStep.stepConfig, tableColumns: currentColumns }
                                            };
                                            handleConfigChange('wizardSteps', updated);
                                          }}
                                          sx={{ color: '#d32f2f', ml: 1 }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Card>
                                  ))}
                                </Box>
                              )}
                              
                              {wizardStep.stepInterfaceType === 'message' && (
                                <Box>
                                  <TextField
                                    size="small"
                                    label="Message Text"
                                    value={wizardStep.stepConfig?.messageText || ''}
                                    onChange={(e) => {
                                      const updated = [...step.config.wizardSteps];
                                      updated[stepIndex] = {
                                        ...wizardStep,
                                        stepConfig: { ...wizardStep.stepConfig, messageText: e.target.value }
                                      };
                                      handleConfigChange('wizardSteps', updated);
                                    }}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    sx={{ mt: 1 }}
                                    placeholder="Review your information before submitting..."
                                    helperText="Message or summary to show in this step"
                                  />
                                </Box>
                              )}
                              
                              {wizardStep.stepInterfaceType === 'barcode_scanner' && (
                                <Box>
                                  <TextField
                                    size="small"
                                    label="Output Variable"
                                    value={wizardStep.stepConfig?.outputVariable || ''}
                                    onChange={(e) => {
                                      const updated = [...step.config.wizardSteps];
                                      updated[stepIndex] = {
                                        ...wizardStep,
                                        stepConfig: { ...wizardStep.stepConfig, outputVariable: e.target.value }
                                      };
                                      handleConfigChange('wizardSteps', updated);
                                    }}
                                    fullWidth
                                    sx={{ mt: 1 }}
                                    placeholder="e.g., scannedCode"
                                    helperText="Variable to store scanned value"
                                  />
                                  
                                  <TextField
                                    size="small"
                                    label="Scan Instructions"
                                    value={wizardStep.stepConfig?.scanInstructions || ''}
                                    onChange={(e) => {
                                      const updated = [...step.config.wizardSteps];
                                      updated[stepIndex] = {
                                        ...wizardStep,
                                        stepConfig: { ...wizardStep.stepConfig, scanInstructions: e.target.value }
                                      };
                                      handleConfigChange('wizardSteps', updated);
                                    }}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    sx={{ mt: 2 }}
                                    placeholder="Point your camera at the barcode..."
                                    helperText="Instructions shown to user"
                                  />
                                </Box>
                              )}
                              
                              {wizardStep.stepInterfaceType === 'file_upload' && (
                                <Box>
                                  <TextField
                                    size="small"
                                    label="Output Variable"
                                    value={wizardStep.stepConfig?.outputVariable || ''}
                                    onChange={(e) => {
                                      const updated = [...step.config.wizardSteps];
                                      updated[stepIndex] = {
                                        ...wizardStep,
                                        stepConfig: { ...wizardStep.stepConfig, outputVariable: e.target.value }
                                      };
                                      handleConfigChange('wizardSteps', updated);
                                    }}
                                    fullWidth
                                    sx={{ mt: 1 }}
                                    placeholder="e.g., uploadedFile"
                                    helperText="Variable to store uploaded file"
                                  />
                                  
                                  <FormControl size="small" fullWidth sx={{ mt: 2 }}>
                                    <InputLabel>File Type</InputLabel>
                                    <Select
                                      value={wizardStep.stepConfig?.fileType || 'all'}
                                      onChange={(e) => {
                                        const updated = [...step.config.wizardSteps];
                                        updated[stepIndex] = {
                                          ...wizardStep,
                                          stepConfig: { ...wizardStep.stepConfig, fileType: e.target.value }
                                        };
                                        handleConfigChange('wizardSteps', updated);
                                      }}
                                      label="File Type"
                                    >
                                      <MenuItem value="all">All Files</MenuItem>
                                      <MenuItem value="image">Images Only</MenuItem>
                                      <MenuItem value="pdf">PDF Only</MenuItem>
                                      <MenuItem value="document">Documents (PDF/Word/Excel)</MenuItem>
                                    </Select>
                                  </FormControl>
                                  
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={wizardStep.stepConfig?.enableS3Upload || false}
                                        onChange={(e) => {
                                          const updated = [...step.config.wizardSteps];
                                          updated[stepIndex] = {
                                            ...wizardStep,
                                            stepConfig: { ...wizardStep.stepConfig, enableS3Upload: e.target.checked }
                                          };
                                          handleConfigChange('wizardSteps', updated);
                                        }}
                                      />
                                    }
                                    label="Upload to S3 Storage"
                                    sx={{ mt: 2 }}
                                  />
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                        
                        {/* Delete step button */}
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updated = step.config.wizardSteps.filter((_, i) => i !== stepIndex);
                            handleConfigChange('wizardSteps', updated);
                          }}
                          sx={{ color: '#d32f2f', ml: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Data Table Editor Configuration */}
      {step.config?.interfaceType === 'table_editor' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">📊 Data Table Editor Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Configure a data table for viewing and editing tabular data. Perfect for reviewing extracted line items, products, or any structured data.
              </Typography>
              
              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100', mb: 1 }}>
                  🔗 Available Variables from Previous Steps
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#e65100', mb: 1 }}>
                  Variables you can reference for table data (examples based on common workflow):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="extractedItems" variant="outlined" />
                  <Chip size="small" label="lineItems" variant="outlined" />
                  <Chip size="small" label="products" variant="outlined" />
                  <Chip size="small" label="orderItems" variant="outlined" />
                  <Chip size="small" label="invoiceItems" variant="outlined" />
                  <Chip size="small" label="apiResponse.data" variant="outlined" />
                </Box>
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>Data Source Variable</InputLabel>
                <Select
                  value={step.config?.dataSource || ''}
                  onChange={(e) => handleConfigChange('dataSource', e.target.value)}
                >
                  {availableVariables.map(variable => (
                    <MenuItem key={variable} value={variable}>
                      {variable}
                      {(variable.toLowerCase().includes('items') || 
                        variable.toLowerCase().includes('products') || 
                        variable.toLowerCase().includes('data') ||
                        variable.toLowerCase().includes('list') ||
                        variable.toLowerCase().includes('array')) && 
                        <Chip size="small" label="Array" sx={{ ml: 1, height: 16 }} />
                      }
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Select any variable that contains array/table data. Variables with "Array" label are likely candidates.
                </FormHelperText>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={step.config?.allowAddRows !== undefined ? step.config.allowAddRows : true}
                    onChange={(e) => handleConfigChange('allowAddRows', e.target.checked)}
                  />
                }
                label="Allow adding new rows"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={step.config?.allowDeleteRows !== undefined ? step.config.allowDeleteRows : true}
                    onChange={(e) => handleConfigChange('allowDeleteRows', e.target.checked)}
                  />
                }
                label="Allow deleting rows"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={step.config?.allowReorderRows !== undefined ? step.config.allowReorderRows : false}
                    onChange={(e) => handleConfigChange('allowReorderRows', e.target.checked)}
                  />
                }
                label="Allow reordering rows (drag & drop)"
              />
              

              
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Table Columns Configuration
                  </Typography>
                </Box>
                
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={step.config?.tableColumns || []}
                    strategy={verticalListSortingStrategy}
                  >
                    {(step.config?.tableColumns || []).map((column, index) => (
                      <SortableColumnCard
                        key={column.id}
                        column={column}
                        index={index}
                        onColumnChange={(index, updatedColumn) => {
                          const updatedColumns = [...step.config.tableColumns];
                          updatedColumns[index] = updatedColumn;
                          handleConfigChange('tableColumns', updatedColumns);
                        }}
                        onColumnDelete={(index) => {
                          const updatedColumns = step.config.tableColumns.filter((_, i) => i !== index);
                          handleConfigChange('tableColumns', updatedColumns);
                        }}
                      >
                        <Box flexGrow={1}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Column Key"
                              value={column.key}
                              onChange={(e) => {
                                const updatedColumns = [...step.config.tableColumns];
                                updatedColumns[index] = { ...column, key: e.target.value };
                                handleConfigChange('tableColumns', updatedColumns);
                              }}
                              fullWidth
                              placeholder="e.g., productName, sku, quantity"
                              helperText="Unique identifier for this column"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Column Label"
                              value={column.label}
                              onChange={(e) => {
                                const updatedColumns = [...step.config.tableColumns];
                                updatedColumns[index] = { ...column, label: e.target.value };
                                handleConfigChange('tableColumns', updatedColumns);
                              }}
                              fullWidth
                              placeholder="e.g., Product Name, SKU, Quantity"
                              helperText="Display name for the column header"
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              size="small"
                              label="Data Path"
                              value={column.dataPath}
                              onChange={(e) => {
                                const updatedColumns = [...step.config.tableColumns];
                                updatedColumns[index] = { ...column, dataPath: e.target.value };
                                handleConfigChange('tableColumns', updatedColumns);
                              }}
                              fullWidth
                              placeholder="e.g., productName, item.name, details.sku"
                              helperText="Path to the data in each row object (supports dot notation for nested properties)"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                                💡 Data Path Examples:
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', fontFamily: 'monospace', mb: 0.5 }}>
                                • Simple property: productName
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', fontFamily: 'monospace', mb: 0.5 }}>
                                • Nested object: item.details.name
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', fontFamily: 'monospace', mb: 0.5 }}>
                                • Array index: specifications[0].value
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', fontFamily: 'monospace' }}>
                                • Calculated: price * quantity (for computed columns)
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Column Type</InputLabel>
                              <Select
                                value={column.type || 'text'}
                                onChange={(e) => {
                                  const updatedColumns = [...step.config.tableColumns];
                                  // If changing to computed, automatically set editable to false
                                  const newColumn = { ...column, type: e.target.value };
                                  if (e.target.value === 'computed') {
                                    newColumn.editable = false;
                                  }
                                  updatedColumns[index] = newColumn;
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                label="Column Type"
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="currency">Currency</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="time">Time</MenuItem>
                                <MenuItem value="boolean">Checkbox</MenuItem>
                                <MenuItem value="select">Dropdown</MenuItem>
                                <MenuItem value="image">Image</MenuItem>
                                <MenuItem value="link">Link</MenuItem>
                                <MenuItem value="computed">Computed</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={4}>
                            <TextField
                              size="small"
                              label="Width"
                              value={column.width || 'auto'}
                              onChange={(e) => {
                                const updatedColumns = [...step.config.tableColumns];
                                updatedColumns[index] = { ...column, width: e.target.value };
                                handleConfigChange('tableColumns', updatedColumns);
                              }}
                              fullWidth
                              placeholder="auto, 100px, 20%"
                              helperText="Column width"
                            />
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Alignment</InputLabel>
                              <Select
                                value={column.alignment || 'left'}
                                onChange={(e) => {
                                  const updatedColumns = [...step.config.tableColumns];
                                  updatedColumns[index] = { ...column, alignment: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                label="Alignment"
                              >
                                <MenuItem value="left">Left</MenuItem>
                                <MenuItem value="center">Center</MenuItem>
                                <MenuItem value="right">Right</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={column.editable !== false}
                                  disabled={column.type === 'computed'}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, editable: e.target.checked };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                />
                              }
                              label={column.type === 'computed' ? 'Editable (Auto-disabled for computed)' : 'Editable'}
                            />
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={column.sortable !== false}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, sortable: e.target.checked };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                />
                              }
                              label="Sortable"
                            />
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={column.required || false}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, required: e.target.checked };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                />
                              }
                              label="Required"
                            />
                          </Grid>
                          
                          {/* Conditional Validation for Number columns */}
                          {column.type === 'number' && (
                            <>
                              <Grid item xs={4}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={column.conditionalValidation || false}
                                      onChange={(e) => {
                                        const updatedColumns = [...step.config.tableColumns];
                                        updatedColumns[index] = { 
                                          ...column, 
                                          conditionalValidation: e.target.checked,
                                          // Reset conditional fields if disabling
                                          ...(e.target.checked ? {} : {
                                            validationOperator: undefined,
                                            validationValue: undefined
                                          })
                                        };
                                        handleConfigChange('tableColumns', updatedColumns);
                                      }}
                                    />
                                  }
                                  label="Conditional Validation"
                                />
                              </Grid>
                              
                              {column.conditionalValidation && (
                                <>
                                  <Grid item xs={4}>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Operator</InputLabel>
                                      <Select
                                        value={column.validationOperator || 'gt'}
                                        onChange={(e) => {
                                          const updatedColumns = [...step.config.tableColumns];
                                          updatedColumns[index] = { ...column, validationOperator: e.target.value };
                                          handleConfigChange('tableColumns', updatedColumns);
                                        }}
                                        label="Operator"
                                      >
                                        <MenuItem value="gt">Greater than (&gt;)</MenuItem>
                                        <MenuItem value="gte">Greater than or equal (≥)</MenuItem>
                                        <MenuItem value="lt">Less than (&lt;)</MenuItem>
                                        <MenuItem value="lte">Less than or equal (≤)</MenuItem>
                                        <MenuItem value="eq">Equal to (=)</MenuItem>
                                        <MenuItem value="ne">Not equal to (≠)</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  
                                  <Grid item xs={4}>
                                    <TextField
                                      size="small"
                                      label="Value"
                                      type="number"
                                      value={column.validationValue !== undefined ? column.validationValue : ''}
                                      onChange={(e) => {
                                        const updatedColumns = [...step.config.tableColumns];
                                        const numValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                        updatedColumns[index] = { ...column, validationValue: isNaN(numValue) ? 0 : numValue };
                                        handleConfigChange('tableColumns', updatedColumns);
                                      }}
                                      fullWidth
                                      placeholder="Enter comparison value"
                                    />
                                  </Grid>
                                </>
                              )}
                            </>
                          )}
                          
                          <Grid item xs={4}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={column.textWrap || false}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, textWrap: e.target.checked };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                />
                              }
                              label="Text Wrap"
                            />
                          </Grid>
                          
                          {/* Options for select columns */}
                          {column.type === 'select' && (
                            <>
                              <Grid item xs={12}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Options Mode</InputLabel>
                                  <Select
                                    value={column.optionsMode || 'simple'}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, optionsMode: e.target.value };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    label="Options Mode"
                                  >
                                    <MenuItem value="simple">Simple (Comma-separated)</MenuItem>
                                    <MenuItem value="advanced">Advanced (Name-Value Pairs)</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              {(!column.optionsMode || column.optionsMode === 'simple') && (
                                <Grid item xs={12}>
                                  <TextField
                                    size="small"
                                    label="Dropdown Options (comma-separated)"
                                    value={column.options || ''}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, options: e.target.value };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    fullWidth
                                    placeholder="Option 1, Option 2, Option 3"
                                    helperText="Comma-separated list of dropdown options"
                                  />
                                </Grid>
                              )}
                              
                              {column.optionsMode === 'advanced' && (
                                <>
                                  <Grid item xs={12}>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Data Source</InputLabel>
                                      <Select
                                        value={column.optionsDataSource || 'manual'}
                                        onChange={(e) => {
                                          const updatedColumns = [...step.config.tableColumns];
                                          updatedColumns[index] = { ...column, optionsDataSource: e.target.value };
                                          handleConfigChange('tableColumns', updatedColumns);
                                        }}
                                        label="Data Source"
                                      >
                                        <MenuItem value="manual">Manual Entry</MenuItem>
                                        <MenuItem value="variable">From Variable</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  
                                  {(!column.optionsDataSource || column.optionsDataSource === 'manual') && (
                                    <Grid item xs={12}>
                                      <Box>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                          <Typography variant="body2" fontWeight="medium">
                                            Dropdown Options (Name-Value Pairs)
                                          </Typography>
                                          <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                              const updatedColumns = [...step.config.tableColumns];
                                              const advancedOptions = column.advancedOptions || [];
                                              updatedColumns[index] = { 
                                                ...column, 
                                                advancedOptions: [...advancedOptions, { name: '', value: '' }] 
                                              };
                                              handleConfigChange('tableColumns', updatedColumns);
                                            }}
                                          >
                                            Add Option
                                          </Button>
                                        </Box>
                                        
                                        {(!column.advancedOptions || column.advancedOptions.length === 0) && (
                                          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 2 }}>
                                            No options added yet. Click "Add Option" to create name-value pairs.
                                          </Typography>
                                        )}
                                        
                                        {(column.advancedOptions || []).map((option, optIndex) => (
                                          <Box key={optIndex} display="flex" gap={1} alignItems="center" mb={1}>
                                            <TextField
                                              size="small"
                                              label="Display Name"
                                              value={option.name || ''}
                                              onChange={(e) => {
                                                const updatedColumns = [...step.config.tableColumns];
                                                const advancedOptions = [...(column.advancedOptions || [])];
                                                advancedOptions[optIndex] = { ...advancedOptions[optIndex], name: e.target.value };
                                                updatedColumns[index] = { ...column, advancedOptions };
                                                handleConfigChange('tableColumns', updatedColumns);
                                              }}
                                              placeholder="e.g., Apple"
                                              sx={{ flex: 1 }}
                                            />
                                            <TextField
                                              size="small"
                                              label="Value"
                                              value={option.value || ''}
                                              onChange={(e) => {
                                                const updatedColumns = [...step.config.tableColumns];
                                                const advancedOptions = [...(column.advancedOptions || [])];
                                                advancedOptions[optIndex] = { ...advancedOptions[optIndex], value: e.target.value };
                                                updatedColumns[index] = { ...column, advancedOptions };
                                                handleConfigChange('tableColumns', updatedColumns);
                                              }}
                                              placeholder="e.g., trx52"
                                              sx={{ flex: 1 }}
                                            />
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => {
                                                const updatedColumns = [...step.config.tableColumns];
                                                const advancedOptions = [...(column.advancedOptions || [])];
                                                advancedOptions.splice(optIndex, 1);
                                                updatedColumns[index] = { ...column, advancedOptions };
                                                handleConfigChange('tableColumns', updatedColumns);
                                              }}
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Grid>
                                  )}
                                  
                                  {column.optionsDataSource === 'variable' && (
                                    <>
                                      <Grid item xs={12}>
                                        <TextField
                                          size="small"
                                          label="Variable Name"
                                          value={column.optionsVariable || ''}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { ...column, optionsVariable: e.target.value };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                          fullWidth
                                          placeholder="e.g., products"
                                          helperText="Variable containing array of objects"
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          size="small"
                                          label="Display Name Field"
                                          value={column.optionsNameField || ''}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { ...column, optionsNameField: e.target.value };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                          fullWidth
                                          placeholder="e.g., [*].name"
                                          helperText="Field path for display name"
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          size="small"
                                          label="Value Field"
                                          value={column.optionsValueField || ''}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { ...column, optionsValueField: e.target.value };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                          fullWidth
                                          placeholder="e.g., [*].value"
                                          helperText="Field path for stored value"
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                          <Typography variant="caption" color="info.dark">
                                            <strong>Example:</strong> If you have a variable "products" with value:<br/>
                                            [{`{name:"Apple", value:"trx52"}, {name:"Orange", value:"trx55"}`}]<br/>
                                            Set Variable Name = "products", Display Name Field = "[*].name", Value Field = "[*].value"
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          )}
                          
                          {/* Computed column formula */}
                          {column.type === 'computed' && (
                            <Grid item xs={12}>
                              <TextField
                                size="small"
                                label="Computation Formula"
                                value={column.formula || ''}
                                onChange={(e) => {
                                  const updatedColumns = [...step.config.tableColumns];
                                  updatedColumns[index] = { ...column, formula: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                fullWidth
                                placeholder="unitPrice * quantity"
                                helperText="Formula for computed values (e.g., 'unitPrice * quantity', '(price - discount) * quantity')"
                              />
                            </Grid>
                          )}
                          
                          {/* Number, Currency, and Computed Formatting Options */}
                          {(column.type === 'number' || column.type === 'currency' || column.type === 'computed') && (
                            <>
                              <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Thousands Separator</InputLabel>
                                  <Select
                                    value={column.thousandsSeparator || ','}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, thousandsSeparator: e.target.value };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    label="Thousands Separator"
                                  >
                                    <MenuItem value=",">Comma (,) - e.g., 1,000</MenuItem>
                                    <MenuItem value=".">Period (.) - e.g., 1.000</MenuItem>
                                    <MenuItem value=" ">Space ( ) - e.g., 1 000</MenuItem>
                                    <MenuItem value="">None - e.g., 1000</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Decimal Separator</InputLabel>
                                  <Select
                                    value={column.decimalSeparator || '.'}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, decimalSeparator: e.target.value };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    label="Decimal Separator"
                                  >
                                    <MenuItem value=".">Period (.) - e.g., 10.50</MenuItem>
                                    <MenuItem value=",">Comma (,) - e.g., 10,50</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={column.type === 'currency' ? 6 : 12}>
                                <TextField
                                  size="small"
                                  label="Decimal Places"
                                  type="number"
                                  value={column.decimalPlaces !== undefined ? column.decimalPlaces : (column.type === 'currency' ? 2 : 0)}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, decimalPlaces: parseInt(e.target.value) || 0 };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  fullWidth
                                  inputProps={{ min: 0, max: 10 }}
                                  helperText="Number of decimal places to display (0-10)"
                                />
                              </Grid>
                              
                              {/* Currency Symbol Configuration - only for currency columns */}
                              {column.type === 'currency' && (
                                <Grid item xs={6}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Currency Symbol</InputLabel>
                                    <Select
                                      value={column.currencySymbol || '$'}
                                      onChange={(e) => {
                                        const updatedColumns = [...step.config.tableColumns];
                                        updatedColumns[index] = { ...column, currencySymbol: e.target.value };
                                        handleConfigChange('tableColumns', updatedColumns);
                                      }}
                                      label="Currency Symbol"
                                    >
                                      <MenuItem value="$">Dollar ($)</MenuItem>
                                      <MenuItem value="€">Euro (€)</MenuItem>
                                      <MenuItem value="£">Pound (£)</MenuItem>
                                      <MenuItem value="¥">Yen (¥)</MenuItem>
                                      <MenuItem value="₹">Rupee (₹)</MenuItem>
                                      <MenuItem value="₽">Ruble (₽)</MenuItem>
                                      <MenuItem value="₦">Naira (₦)</MenuItem>
                                      <MenuItem value="R">Rand (R)</MenuItem>
                                      <MenuItem value="₨">Pakistani Rupee (₨)</MenuItem>
                                      <MenuItem value="Rp.">Indonesian Rupiah (Rp.)</MenuItem>
                                      <MenuItem value="custom">Custom...</MenuItem>
                                    </Select>
                                  </FormControl>
                                  {column.currencySymbol === 'custom' && (
                                    <TextField
                                      size="small"
                                      label="Custom Currency Symbol"
                                      value={column.customCurrencySymbol || ''}
                                      onChange={(e) => {
                                        const updatedColumns = [...step.config.tableColumns];
                                        updatedColumns[index] = { 
                                          ...column, 
                                          customCurrencySymbol: e.target.value,
                                          currencySymbol: 'custom'
                                        };
                                        handleConfigChange('tableColumns', updatedColumns);
                                      }}
                                      fullWidth
                                      placeholder="Enter custom symbol (e.g., ¤, kr, etc.)"
                                      helperText="Enter your custom currency symbol"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                </Grid>
                              )}
                            </>
                          )}
                          
                          {/* Date Format Configuration - only for date columns */}
                          {column.type === 'date' && (
                            <Grid item xs={12}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Date Format</InputLabel>
                                <Select
                                  value={column.dateFormat || 'MM/dd/yyyy'}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, dateFormat: e.target.value };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  label="Date Format"
                                >
                                  <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (12/25/2023)</MenuItem>
                                  <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (25/12/2023)</MenuItem>
                                  <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (2023-12-25)</MenuItem>
                                  <MenuItem value="dd.MM.yyyy">dd.MM.yyyy (25.12.2023)</MenuItem>
                                  <MenuItem value="MM-dd-yyyy">MM-dd-yyyy (12-25-2023)</MenuItem>
                                  <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (25-12-2023)</MenuItem>
                                  <MenuItem value="MMM dd, yyyy">MMM dd, yyyy (Dec 25, 2023)</MenuItem>
                                  <MenuItem value="MMMM dd, yyyy">MMMM dd, yyyy (December 25, 2023)</MenuItem>
                                  <MenuItem value="dd MMM yyyy">dd MMM yyyy (25 Dec 2023)</MenuItem>
                                  <MenuItem value="dd MMMM yyyy">dd MMMM yyyy (25 December 2023)</MenuItem>
                                  <MenuItem value="custom">Custom...</MenuItem>
                                </Select>
                              </FormControl>
                              {column.dateFormat === 'custom' && (
                                <TextField
                                  size="small"
                                  label="Custom Date Format"
                                  value={column.customDateFormat || ''}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { 
                                      ...column, 
                                      customDateFormat: e.target.value,
                                      dateFormat: 'custom'
                                    };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  fullWidth
                                  placeholder="e.g., DD/MM/YYYY, dd.mm.yyyy, MM-dd-yy"
                                  helperText="Enter custom date format (supports DD, MM, YYYY, dd, mm, yyyy)"
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Grid>
                          )}

                          {/* Time Format Configuration - only for time columns */}
                          {column.type === 'time' && (
                            <Grid item xs={12}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Time Format</InputLabel>
                                <Select
                                  value={column.timeFormat || 'HH:mm:ss'}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, timeFormat: e.target.value };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  label="Time Format"
                                >
                                  <MenuItem value="HH:mm:ss">24-hour with seconds (14:30:44)</MenuItem>
                                  <MenuItem value="HH:mm">24-hour without seconds (14:30)</MenuItem>
                                  <MenuItem value="hh:mm:ss A">12-hour with seconds (02:30:44 PM)</MenuItem>
                                  <MenuItem value="hh:mm A">12-hour without seconds (02:30 PM)</MenuItem>
                                  <MenuItem value="h:mm A">12-hour no leading zero (2:30 PM)</MenuItem>
                                  <MenuItem value="h:mm:ss A">12-hour no leading zero with seconds (2:30:44 PM)</MenuItem>
                                  <MenuItem value="custom">Custom...</MenuItem>
                                </Select>
                                <FormHelperText>
                                  Choose how to display time values. Input format is HHMMSS (e.g., 035044 = 03:50:44)
                                </FormHelperText>
                              </FormControl>
                              {column.timeFormat === 'custom' && (
                                <TextField
                                  size="small"
                                  label="Custom Time Format"
                                  value={column.customTimeFormat || ''}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { 
                                      ...column, 
                                      customTimeFormat: e.target.value,
                                      timeFormat: 'custom'
                                    };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  fullWidth
                                  placeholder="e.g., HH:mm, h:mm A, HH.mm.ss"
                                  helperText="Custom time format (HH=24hr, hh=12hr, mm=minutes, ss=seconds, A=AM/PM)"
                                  sx={{ mt: 1 }}
                                />
                              )}
                              
                              {/* Timezone Configuration */}
                              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                                <InputLabel>Timezone</InputLabel>
                                <Select
                                  value={column.timezone || 'UTC'}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, timezone: e.target.value };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  label="Timezone"
                                >
                                  <MenuItem value="UTC">UTC (Server Time)</MenuItem>
                                  <MenuItem value="UTC+1">UTC+1 (Central European Time)</MenuItem>
                                  <MenuItem value="UTC+2">UTC+2 (Eastern European Time)</MenuItem>
                                  <MenuItem value="UTC+3">UTC+3 (Moscow Time)</MenuItem>
                                  <MenuItem value="UTC+4">UTC+4 (Gulf Standard Time)</MenuItem>
                                  <MenuItem value="UTC+5">UTC+5 (Pakistan Standard Time)</MenuItem>
                                  <MenuItem value="UTC+6">UTC+6 (Bangladesh Standard Time)</MenuItem>
                                  <MenuItem value="UTC+7">UTC+7 (Indochina Time)</MenuItem>
                                  <MenuItem value="UTC+8">UTC+8 (China Standard Time)</MenuItem>
                                  <MenuItem value="UTC+9">UTC+9 (Japan Standard Time)</MenuItem>
                                  <MenuItem value="UTC+10">UTC+10 (Australian Eastern Time)</MenuItem>
                                  <MenuItem value="UTC+11">UTC+11 (Solomon Islands Time)</MenuItem>
                                  <MenuItem value="UTC+12">UTC+12 (New Zealand Time)</MenuItem>
                                  <MenuItem value="UTC-1">UTC-1 (Cape Verde Time)</MenuItem>
                                  <MenuItem value="UTC-2">UTC-2 (South Georgia Time)</MenuItem>
                                  <MenuItem value="UTC-3">UTC-3 (Argentina Time)</MenuItem>
                                  <MenuItem value="UTC-4">UTC-4 (Atlantic Standard Time)</MenuItem>
                                  <MenuItem value="UTC-5">UTC-5 (Eastern Standard Time)</MenuItem>
                                  <MenuItem value="UTC-6">UTC-6 (Central Standard Time)</MenuItem>
                                  <MenuItem value="UTC-7">UTC-7 (Mountain Standard Time)</MenuItem>
                                  <MenuItem value="UTC-8">UTC-8 (Pacific Standard Time)</MenuItem>
                                  <MenuItem value="UTC-9">UTC-9 (Alaska Standard Time)</MenuItem>
                                  <MenuItem value="UTC-10">UTC-10 (Hawaii Standard Time)</MenuItem>
                                  <MenuItem value="UTC-11">UTC-11 (Samoa Standard Time)</MenuItem>
                                  <MenuItem value="UTC-12">UTC-12 (Baker Island Time)</MenuItem>
                                </Select>
                                <FormHelperText>
                                  Select the timezone for time display. This will convert server time to your local timezone.
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                      </SortableColumnCard>
                    ))}
                  </SortableContext>
                </DndContext>
                
                {(!step.config?.tableColumns || step.config.tableColumns.length === 0) && (
                  <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="body2">
                      No table columns defined. Add columns to configure your data table.
                    </Typography>
                  </Box>
                )}
                
                {/* Add Column Button - moved to bottom for better UX */}
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentColumns = step.config?.tableColumns || [];
                      const newColumn = {
                        id: `col-${Date.now()}`,
                        key: '',
                        label: '',
                        dataPath: '',
                        type: 'text',
                        editable: true,
                        required: false,
                        width: 'auto',
                        alignment: 'left'
                      };
                      handleConfigChange('tableColumns', [...currentColumns, newColumn]);
                    }}
                  >
                    Add Column
                  </Button>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Table with Header Fields Configuration */}
      {step.config?.interfaceType === 'table_with_header' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">📋 Table with Header Fields Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Perfect for showing context information (like Purchase Order number) above a data table. 
                Users can edit header fields and review/modify table data in a single interface.
              </Typography>
              
              <Divider sx={{ my: 1 }} />
              
              {/* Header Fields Configuration */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  📝 Header Fields (shown above table)
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Context fields displayed before the table (e.g., Purchase Order, Customer Name, Date)
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentFields = step.config?.headerFields || [];
                      const newField = {
                        id: `header-${Date.now()}`,
                        name: '',
                        label: '',
                        type: 'text',
                        required: true,
                        readOnly: false,
                        placeholder: '',
                        helperText: '',
                        outputVariable: '',
                        defaultValue: '',
                        width: 'full'
                      };
                      handleConfigChange('headerFields', [...currentFields, newField]);
                    }}
                  >
                    Add Header Field
                  </Button>
                </Box>
                
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleHeaderFieldsDragEnd}
                >
                  <SortableContext 
                    items={step.config?.headerFields || []}
                    strategy={verticalListSortingStrategy}
                  >
                    {(step.config?.headerFields || []).map((field, index) => (
                      <SortableHeaderFieldCard
                        key={field.id}
                        field={field}
                        index={index}
                        onFieldDelete={(index) => {
                          const updatedFields = step.config.headerFields.filter((_, i) => i !== index);
                          handleConfigChange('headerFields', updatedFields);
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Field Name"
                              value={field.name}
                              onChange={(e) => {
                                const updatedFields = [...step.config.headerFields];
                                updatedFields[index] = { ...field, name: e.target.value };
                                handleConfigChange('headerFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., purchaseOrder"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Field Label"
                              value={field.label}
                              onChange={(e) => {
                                const updatedFields = [...step.config.headerFields];
                                updatedFields[index] = { ...field, label: e.target.value };
                                handleConfigChange('headerFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., Purchase Order Number"
                            />
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Field Type</InputLabel>
                              <Select
                                value={field.type}
                                onChange={(e) => {
                                  const updatedFields = [...step.config.headerFields];
                                  updatedFields[index] = { ...field, type: e.target.value };
                                  handleConfigChange('headerFields', updatedFields);
                                }}
                                label="Field Type"
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="time">Time</MenuItem>
                                <MenuItem value="textarea">Text Area</MenuItem>
                                <MenuItem value="select">Dropdown</MenuItem>
                                <MenuItem value="calculated">🧮 Calculated</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Width</InputLabel>
                              <Select
                                value={field.width || 'full'}
                                onChange={(e) => {
                                  const updatedFields = [...step.config.headerFields];
                                  updatedFields[index] = { ...field, width: e.target.value };
                                  handleConfigChange('headerFields', updatedFields);
                                }}
                                label="Width"
                              >
                                <MenuItem value="full">Full Width</MenuItem>
                                <MenuItem value="half">Half Width</MenuItem>
                                <MenuItem value="third">One Third</MenuItem>
                                <MenuItem value="quarter">One Quarter</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={4}>
                            <Box display="flex" flexDirection="column" gap={1}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={field.required || false}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.headerFields];
                                      updatedFields[index] = { ...field, required: e.target.checked };
                                      handleConfigChange('headerFields', updatedFields);
                                    }}
                                  />
                                }
                                label="Required"
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={field.readOnly || false}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.headerFields];
                                      updatedFields[index] = { ...field, readOnly: e.target.checked };
                                      handleConfigChange('headerFields', updatedFields);
                                    }}
                                  />
                                }
                                label="Read Only"
                              />
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={field.clearButton || false}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.headerFields];
                                      updatedFields[index] = { ...field, clearButton: e.target.checked };
                                      handleConfigChange('headerFields', updatedFields);
                                    }}
                                  />
                                }
                                label="Clear Button"
                              />
                            </Box>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Placeholder"
                              value={field.placeholder}
                              onChange={(e) => {
                                const updatedFields = [...step.config.headerFields];
                                updatedFields[index] = { ...field, placeholder: e.target.value };
                                handleConfigChange('headerFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., Enter PO number..."
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Default Value"
                              value={field.defaultValue}
                              onChange={(e) => {
                                const updatedFields = [...step.config.headerFields];
                                updatedFields[index] = { ...field, defaultValue: e.target.value };
                                handleConfigChange('headerFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., {{extractedPurchaseOrder}}"
                              helperText="Can use variables like {{extractedPurchaseOrder}}"
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Output Variable"
                              value={field.outputVariable}
                              onChange={(e) => {
                                const updatedFields = [...step.config.headerFields];
                                updatedFields[index] = { ...field, outputVariable: e.target.value };
                                handleConfigChange('headerFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., purchaseOrder"
                              helperText="Variable name to store the value"
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Helper Text"
                              value={field.helperText}
                              onChange={(e) => {
                                const updatedFields = [...step.config.headerFields];
                                updatedFields[index] = { ...field, helperText: e.target.value };
                                handleConfigChange('headerFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="Additional help text for the user"
                            />
                          </Grid>
                          
                          {/* Options for select fields */}
                          {field.type === 'select' && (
                            <>
                              <Grid item xs={12}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Options Mode</InputLabel>
                                  <Select
                                    value={field.optionsMode || 'simple'}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.headerFields];
                                      updatedFields[index] = { ...field, optionsMode: e.target.value };
                                      handleConfigChange('headerFields', updatedFields);
                                    }}
                                    label="Options Mode"
                                  >
                                    <MenuItem value="simple">Simple (Comma-separated)</MenuItem>
                                    <MenuItem value="advanced">Advanced (Name-Value Pairs)</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              {(!field.optionsMode || field.optionsMode === 'simple') && (
                                <Grid item xs={12}>
                                  <TextField
                                    size="small"
                                    label="Options (comma-separated)"
                                    value={field.options || ''}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.headerFields];
                                      updatedFields[index] = { ...field, options: e.target.value };
                                      handleConfigChange('headerFields', updatedFields);
                                    }}
                                    fullWidth
                                    placeholder="Option 1, Option 2, Option 3"
                                    helperText="Comma-separated list of dropdown options"
                                  />
                                </Grid>
                              )}
                              
                              {field.optionsMode === 'advanced' && (
                                <>
                                  <Grid item xs={12}>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Data Source</InputLabel>
                                      <Select
                                        value={field.optionsDataSource || 'manual'}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { ...field, optionsDataSource: e.target.value };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                        label="Data Source"
                                      >
                                        <MenuItem value="manual">Manual Entry</MenuItem>
                                        <MenuItem value="variable">From Variable</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  
                                  {(!field.optionsDataSource || field.optionsDataSource === 'manual') && (
                                    <Grid item xs={12}>
                                      <Box>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                          <Typography variant="body2" fontWeight="medium">
                                            Dropdown Options (Name-Value Pairs)
                                          </Typography>
                                          <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                              const updatedFields = [...step.config.headerFields];
                                              const advancedOptions = field.advancedOptions || [];
                                              updatedFields[index] = { 
                                                ...field, 
                                                advancedOptions: [...advancedOptions, { name: '', value: '' }] 
                                              };
                                              handleConfigChange('headerFields', updatedFields);
                                            }}
                                          >
                                            Add Option
                                          </Button>
                                        </Box>
                                        
                                        {(!field.advancedOptions || field.advancedOptions.length === 0) && (
                                          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 2 }}>
                                            No options added yet. Click "Add Option" to create name-value pairs.
                                          </Typography>
                                        )}
                                        
                                        {(field.advancedOptions || []).map((option, optIndex) => (
                                          <Box key={optIndex} display="flex" gap={1} alignItems="center" mb={1}>
                                            <TextField
                                              size="small"
                                              label="Display Name"
                                              value={option.name || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.headerFields];
                                                const advancedOptions = [...(field.advancedOptions || [])];
                                                advancedOptions[optIndex] = { ...advancedOptions[optIndex], name: e.target.value };
                                                updatedFields[index] = { ...field, advancedOptions };
                                                handleConfigChange('headerFields', updatedFields);
                                              }}
                                              placeholder="e.g., Apple"
                                              sx={{ flex: 1 }}
                                            />
                                            <TextField
                                              size="small"
                                              label="Value"
                                              value={option.value || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.headerFields];
                                                const advancedOptions = [...(field.advancedOptions || [])];
                                                advancedOptions[optIndex] = { ...advancedOptions[optIndex], value: e.target.value };
                                                updatedFields[index] = { ...field, advancedOptions };
                                                handleConfigChange('headerFields', updatedFields);
                                              }}
                                              placeholder="e.g., trx52"
                                              sx={{ flex: 1 }}
                                            />
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => {
                                                const updatedFields = [...step.config.headerFields];
                                                const advancedOptions = [...(field.advancedOptions || [])];
                                                advancedOptions.splice(optIndex, 1);
                                                updatedFields[index] = { ...field, advancedOptions };
                                                handleConfigChange('headerFields', updatedFields);
                                              }}
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Grid>
                                  )}
                                  
                                  {field.optionsDataSource === 'variable' && (
                                    <>
                                      <Grid item xs={12}>
                                        <TextField
                                          size="small"
                                          label="Variable Name"
                                          value={field.optionsVariable || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.headerFields];
                                            updatedFields[index] = { ...field, optionsVariable: e.target.value };
                                            handleConfigChange('headerFields', updatedFields);
                                          }}
                                          fullWidth
                                          placeholder="e.g., products"
                                          helperText="Variable containing array of objects"
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          size="small"
                                          label="Display Name Field"
                                          value={field.optionsNameField || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.headerFields];
                                            updatedFields[index] = { ...field, optionsNameField: e.target.value };
                                            handleConfigChange('headerFields', updatedFields);
                                          }}
                                          fullWidth
                                          placeholder="e.g., [*].name"
                                          helperText="Field path for display name"
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          size="small"
                                          label="Value Field"
                                          value={field.optionsValueField || ''}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.headerFields];
                                            updatedFields[index] = { ...field, optionsValueField: e.target.value };
                                            handleConfigChange('headerFields', updatedFields);
                                          }}
                                          fullWidth
                                          placeholder="e.g., [*].value"
                                          helperText="Field path for stored value"
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                          <Typography variant="caption" color="info.dark">
                                            <strong>Example:</strong> If you have a variable "products" with value:<br/>
                                            [{`{name:"Apple", value:"trx52"}, {name:"Orange", value:"trx55"}`}]<br/>
                                            Set Variable Name = "products", Display Name Field = "[*].name", Value Field = "[*].value"
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          )}
                          
                          {/* Calculated Field Configuration */}
                          {field.type === 'calculated' && (
                            <>
                              <Grid item xs={12}>
                                <Box sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: 1, border: '1px solid #90caf9' }}>
                                  <Typography variant="body2" fontWeight="600" color="primary" gutterBottom>
                                    🧮 Calculated Field Settings
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    Automatically calculates values from a table column and updates in real-time when data changes
                                  </Typography>
                                </Box>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Column Key to Calculate"
                                  value={field.calculationColumn || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, calculationColumn: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., quantity"
                                  helperText="The column key from the table to calculate"
                                />
                              </Grid>
                              
                              <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Calculation Operation</InputLabel>
                                  <Select
                                    value={field.calculationOperation || 'sum'}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.headerFields];
                                      updatedFields[index] = { ...field, calculationOperation: e.target.value };
                                      handleConfigChange('headerFields', updatedFields);
                                    }}
                                    label="Calculation Operation"
                                  >
                                    <MenuItem value="sum">Sum - Add all values</MenuItem>
                                    <MenuItem value="average">Average - Calculate mean</MenuItem>
                                    <MenuItem value="min">Minimum - Find lowest value</MenuItem>
                                    <MenuItem value="max">Maximum - Find highest value</MenuItem>
                                    <MenuItem value="count">Count - Count non-null values</MenuItem>
                                    <MenuItem value="multiply">Multiply - Multiply all values</MenuItem>
                                  </Select>
                                  <FormHelperText>Operation to perform on column values</FormHelperText>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Decimal Places"
                                  value={field.decimalPlaces !== undefined ? field.decimalPlaces : 2}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, decimalPlaces: parseInt(e.target.value) || 0 };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  inputProps={{ min: 0, max: 10 }}
                                  helperText="Number of decimal places to display"
                                />
                              </Grid>
                              
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Prefix Text"
                                  value={field.calculationPrefix || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, calculationPrefix: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., $, Total: "
                                  helperText="Optional text to show before the value"
                                />
                              </Grid>
                              
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Thousands Separator"
                                  value={field.thousandsSeparator !== undefined ? field.thousandsSeparator : ','}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, thousandsSeparator: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder=","
                                  helperText="Separator for thousands (e.g., 1,000)"
                                  inputProps={{ maxLength: 1 }}
                                />
                              </Grid>
                              
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Decimal Separator"
                                  value={field.decimalSeparator !== undefined ? field.decimalSeparator : '.'}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, decimalSeparator: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="."
                                  helperText="Separator for decimals (e.g., 10.50)"
                                  inputProps={{ maxLength: 1 }}
                                />
                              </Grid>
                              
                              <Grid item xs={12}>
                                <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                                  <Typography variant="caption" color="success.dark">
                                    <strong>💡 Example:</strong> If your table has a "quantity" column with values [2000, 7500, 5000], 
                                    and you select "Sum" operation with prefix "$" and 2 decimal places, 
                                    this field will automatically display <strong>"$14,500.00"</strong> and update whenever the table data changes.
                                  </Typography>
                                </Box>
                              </Grid>
                            </>
                          )}
                          
                          {/* Character Limit Configuration - for text, textarea, email fields */}
                          {(field.type === 'text' || field.type === 'textarea' || field.type === 'email') && (
                            <Grid item xs={12}>
                              <Box display="flex" gap={2} alignItems="center">
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={field.enableCharacterLimit || false}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { 
                                          ...field, 
                                          enableCharacterLimit: e.target.checked,
                                          ...(e.target.checked ? {} : { maxCharacters: undefined })
                                        };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                    />
                                  }
                                  label="Enable Character Limit"
                                />
                                
                                {field.enableCharacterLimit && (
                                  <TextField
                                    size="small"
                                    label="Max Characters"
                                    type="number"
                                    value={field.maxCharacters || ''}
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value) || undefined;
                                      const updatedFields = [...step.config.headerFields];
                                      updatedFields[index] = { ...field, maxCharacters: value };
                                      handleConfigChange('headerFields', updatedFields);
                                    }}
                                    inputProps={{
                                      min: 1,
                                      max: 10000
                                    }}
                                    sx={{ width: 150 }}
                                    placeholder="e.g., 100"
                                    helperText="1-10000 characters"
                                  />
                                )}
                              </Box>
                              
                              {field.enableCharacterLimit && field.maxCharacters && (
                                <Box mt={1}>
                                  <Typography variant="caption" color="textSecondary">
                                    Users will see a character counter showing "{field.maxCharacters - (field.defaultValue?.length || 0)} characters remaining" as they type
                                  </Typography>
                                </Box>
                              )}
                            </Grid>
                          )}

                          {/* Text Transformation Rules - only for text type fields */}
                          {field.type === 'text' && (
                            <Grid item xs={12}>
                              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                  ✨ Text Transformation Rules
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                  Automatically transform text as users type or submit
                                </Typography>
                                
                                <Box display="flex" flexDirection="column" gap={1.5}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={field.textTransform === 'uppercase'}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { 
                                            ...field, 
                                            textTransform: e.target.checked ? 'uppercase' : undefined 
                                          };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                      />
                                    }
                                    label="Convert to UPPERCASE"
                                  />
                                  
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={field.textTransform === 'lowercase'}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { 
                                            ...field, 
                                            textTransform: e.target.checked ? 'lowercase' : undefined 
                                          };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                      />
                                    }
                                    label="Convert to lowercase"
                                  />
                                  
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={field.trimWhitespace || false}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { 
                                            ...field, 
                                            trimWhitespace: e.target.checked 
                                          };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                      />
                                    }
                                    label="Trim whitespace (remove leading/trailing spaces)"
                                  />
                                </Box>
                                
                                {(field.textTransform || field.trimWhitespace) && (
                                  <Box mt={1.5} sx={{ p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                                    <Typography variant="caption" color="info.dark">
                                      <strong>Preview:</strong> "{field.placeholder || 'Sample Text'}" → "
                                      {(() => {
                                        let text = field.placeholder || 'Sample Text';
                                        if (field.textTransform === 'uppercase') text = text.toUpperCase();
                                        if (field.textTransform === 'lowercase') text = text.toLowerCase();
                                        if (field.trimWhitespace) text = text.trim();
                                        return text;
                                      })()}"
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          )}

                          {/* Date Format Configuration - only for date fields */}
                          {field.type === 'date' && (
                            <Grid item xs={12}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Date Format</InputLabel>
                                <Select
                                  value={field.dateFormat || 'MM/dd/yyyy'}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, dateFormat: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  label="Date Format"
                                >
                                  <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (12/25/2023)</MenuItem>
                                  <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (25/12/2023)</MenuItem>
                                  <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (2023-12-25)</MenuItem>
                                  <MenuItem value="dd.MM.yyyy">dd.MM.yyyy (25.12.2023)</MenuItem>
                                  <MenuItem value="MM-dd-yyyy">MM-dd-yyyy (12-25-2023)</MenuItem>
                                  <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (25-12-2023)</MenuItem>
                                  <MenuItem value="MMM dd, yyyy">MMM dd, yyyy (Dec 25, 2023)</MenuItem>
                                  <MenuItem value="MMMM dd, yyyy">MMMM dd, yyyy (December 25, 2023)</MenuItem>
                                  <MenuItem value="dd MMM yyyy">dd MMM yyyy (25 Dec 2023)</MenuItem>
                                  <MenuItem value="dd MMMM yyyy">dd MMMM yyyy (25 December 2023)</MenuItem>
                                  <MenuItem value="custom">Custom...</MenuItem>
                                </Select>
                              </FormControl>
                              {field.dateFormat === 'custom' && (
                                <TextField
                                  size="small"
                                  label="Custom Date Format"
                                  value={field.customDateFormat || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { 
                                      ...field, 
                                      customDateFormat: e.target.value,
                                      dateFormat: 'custom'
                                    };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., DD/MM/YYYY, dd.mm.yyyy, MM-dd-yy"
                                  helperText="Enter custom date format (supports DD, MM, YYYY, dd, mm, yyyy)"
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Grid>
                          )}

                          {/* Time Format Configuration - only for time fields */}
                          {field.type === 'time' && (
                            <Grid item xs={12}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Time Format</InputLabel>
                                <Select
                                  value={field.timeFormat || 'HH:mm:ss'}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, timeFormat: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  label="Time Format"
                                >
                                  <MenuItem value="HH:mm:ss">24-hour with seconds (14:30:44)</MenuItem>
                                  <MenuItem value="HH:mm">24-hour without seconds (14:30)</MenuItem>
                                  <MenuItem value="hh:mm:ss A">12-hour with seconds (02:30:44 PM)</MenuItem>
                                  <MenuItem value="hh:mm A">12-hour without seconds (02:30 PM)</MenuItem>
                                  <MenuItem value="h:mm A">12-hour no leading zero (2:30 PM)</MenuItem>
                                  <MenuItem value="h:mm:ss A">12-hour no leading zero with seconds (2:30:44 PM)</MenuItem>
                                  <MenuItem value="custom">Custom...</MenuItem>
                                </Select>
                                <FormHelperText>
                                  Choose how to display time values. Input format is HHMMSS (e.g., 035044 = 03:50:44)
                                </FormHelperText>
                              </FormControl>
                              {field.timeFormat === 'custom' && (
                                <TextField
                                  size="small"
                                  label="Custom Time Format"
                                  value={field.customTimeFormat || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { 
                                      ...field, 
                                      customTimeFormat: e.target.value,
                                      timeFormat: 'custom'
                                    };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., HH:mm, h:mm A, HH.mm.ss"
                                  helperText="Custom time format (HH=24hr, hh=12hr, mm=minutes, ss=seconds, A=AM/PM)"
                                  sx={{ mt: 1 }}
                                />
                              )}
                              
                              {/* Timezone Configuration */}
                              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                                <InputLabel>Timezone</InputLabel>
                                <Select
                                  value={field.timezone || 'UTC'}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, timezone: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  label="Timezone"
                                >
                                  <MenuItem value="UTC">UTC (Server Time)</MenuItem>
                                  <MenuItem value="UTC+1">UTC+1 (Central European Time)</MenuItem>
                                  <MenuItem value="UTC+2">UTC+2 (Eastern European Time)</MenuItem>
                                  <MenuItem value="UTC+3">UTC+3 (Moscow Time)</MenuItem>
                                  <MenuItem value="UTC+4">UTC+4 (Gulf Standard Time)</MenuItem>
                                  <MenuItem value="UTC+5">UTC+5 (Pakistan Standard Time)</MenuItem>
                                  <MenuItem value="UTC+6">UTC+6 (Bangladesh Standard Time)</MenuItem>
                                  <MenuItem value="UTC+7">UTC+7 (Indochina Time)</MenuItem>
                                  <MenuItem value="UTC+8">UTC+8 (China Standard Time)</MenuItem>
                                  <MenuItem value="UTC+9">UTC+9 (Japan Standard Time)</MenuItem>
                                  <MenuItem value="UTC+10">UTC+10 (Australian Eastern Time)</MenuItem>
                                  <MenuItem value="UTC+11">UTC+11 (Solomon Islands Time)</MenuItem>
                                  <MenuItem value="UTC+12">UTC+12 (New Zealand Time)</MenuItem>
                                  <MenuItem value="UTC-1">UTC-1 (Cape Verde Time)</MenuItem>
                                  <MenuItem value="UTC-2">UTC-2 (South Georgia Time)</MenuItem>
                                  <MenuItem value="UTC-3">UTC-3 (Argentina Time)</MenuItem>
                                  <MenuItem value="UTC-4">UTC-4 (Atlantic Standard Time)</MenuItem>
                                  <MenuItem value="UTC-5">UTC-5 (Eastern Standard Time)</MenuItem>
                                  <MenuItem value="UTC-6">UTC-6 (Central Standard Time)</MenuItem>
                                  <MenuItem value="UTC-7">UTC-7 (Mountain Standard Time)</MenuItem>
                                  <MenuItem value="UTC-8">UTC-8 (Pacific Standard Time)</MenuItem>
                                  <MenuItem value="UTC-9">UTC-9 (Alaska Standard Time)</MenuItem>
                                  <MenuItem value="UTC-10">UTC-10 (Hawaii Standard Time)</MenuItem>
                                  <MenuItem value="UTC-11">UTC-11 (Samoa Standard Time)</MenuItem>
                                  <MenuItem value="UTC-12">UTC-12 (Baker Island Time)</MenuItem>
                                </Select>
                                <FormHelperText>
                                  Select the timezone for time display. This will convert server time to your local timezone.
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                          )}
                        </Grid>
                      </SortableHeaderFieldCard>
                    ))}
                  </SortableContext>
                </DndContext>
                
                {(!step.config?.headerFields || step.config.headerFields.length === 0) && (
                  <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="body2">
                      No header fields defined. Click "Add Header Field" to add context fields.
                    </Typography>
                  </Box>
                )}
                
                {/* Quick Templates for Header Fields */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    🚀 Quick Header Templates
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const purchaseOrderHeader = [
                          { id: 'po-header-1', name: 'purchaseOrder', label: 'Purchase Order Number', type: 'text', required: true, readOnly: false, placeholder: 'Enter PO number...', helperText: 'The Purchase Order number for this delivery', outputVariable: 'purchaseOrder', defaultValue: '{{extractedPurchaseOrder}}', width: 'half' }
                        ];
                        handleConfigChange('headerFields', purchaseOrderHeader);
                      }}
                    >
                      📋 Purchase Order
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const deliveryHeader = [
                          { id: 'po-header-1', name: 'purchaseOrder', label: 'Purchase Order Number', type: 'text', required: true, readOnly: false, placeholder: 'Enter PO number...', outputVariable: 'purchaseOrder', defaultValue: '{{extractedPurchaseOrder}}', width: 'half' },
                          { id: 'delivery-header-1', name: 'deliveryDate', label: 'Delivery Date', type: 'date', required: true, readOnly: false, outputVariable: 'deliveryDate', defaultValue: '{{extractedDeliveryDate}}', width: 'half' }
                        ];
                        handleConfigChange('headerFields', deliveryHeader);
                      }}
                    >
                      📦 Delivery Info
                    </Button>
                  </Box>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Data Table Configuration */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  📊 Data Table Configuration
                </Typography>
                
                <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100', mb: 1 }}>
                    🔗 Available Variables from Previous Steps
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#e65100', mb: 1 }}>
                    Variables you can reference for table data (examples based on common workflow):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip size="small" label="extractedItems" variant="outlined" />
                    <Chip size="small" label="lineItems" variant="outlined" />
                    <Chip size="small" label="products" variant="outlined" />
                    <Chip size="small" label="tableData" variant="outlined" />
                    <Chip size="small" label="extractedTable" variant="outlined" />
                  </Box>
                </Box>

                <FormControl fullWidth size="small">
                  <InputLabel>Data Source Variable</InputLabel>
                  <Select
                    value={step.config?.dataSource || ''}
                    onChange={(e) => handleConfigChange('dataSource', e.target.value)}
                  >
                    {availableVariables.map(variable => (
                      <MenuItem key={variable} value={variable}>
                        {variable}
                        {(variable.toLowerCase().includes('items') || 
                          variable.toLowerCase().includes('products') || 
                          variable.toLowerCase().includes('data') ||
                          variable.toLowerCase().includes('list') ||
                          variable.toLowerCase().includes('array')) && 
                          <Chip size="small" label="Array" sx={{ ml: 1, height: 16 }} />
                        }
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select any variable that contains array/table data. Variables with "Array" label are likely candidates.
                  </FormHelperText>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={step.config?.allowAddRows !== undefined ? step.config.allowAddRows : true}
                      onChange={(e) => handleConfigChange('allowAddRows', e.target.checked)}
                    />
                  }
                  label="Allow adding new rows"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={step.config?.allowDeleteRows !== undefined ? step.config.allowDeleteRows : true}
                      onChange={(e) => handleConfigChange('allowDeleteRows', e.target.checked)}
                    />
                  }
                  label="Allow deleting rows"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={step.config?.allowReorderRows !== undefined ? step.config.allowReorderRows : false}
                      onChange={(e) => handleConfigChange('allowReorderRows', e.target.checked)}
                    />
                  }
                  label="Allow reordering rows (drag & drop)"
                />
                
                                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Table Columns Configuration
                      </Typography>
                    </Box>
                  
                  <DndContext 
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext 
                      items={step.config?.tableColumns || []}
                      strategy={verticalListSortingStrategy}
                    >
                      {(step.config?.tableColumns || []).map((column, index) => (
                        <SortableColumnCard
                          key={column.id}
                          column={column}
                          index={index}
                          onColumnChange={(index, updatedColumn) => {
                            const updatedColumns = [...step.config.tableColumns];
                            updatedColumns[index] = updatedColumn;
                            handleConfigChange('tableColumns', updatedColumns);
                          }}
                          onColumnDelete={(index) => {
                            const updatedColumns = step.config.tableColumns.filter((_, i) => i !== index);
                            handleConfigChange('tableColumns', updatedColumns);
                          }}
                        >
                          <Box flexGrow={1}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                size="small"
                                label="Column Key"
                                value={column.key}
                                onChange={(e) => {
                                  const updatedColumns = [...step.config.tableColumns];
                                  updatedColumns[index] = { ...column, key: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                fullWidth
                                placeholder="e.g., productName, sku, quantity"
                                helperText="Unique identifier for this column"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                size="small"
                                label="Column Label"
                                value={column.label}
                                onChange={(e) => {
                                  const updatedColumns = [...step.config.tableColumns];
                                  updatedColumns[index] = { ...column, label: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                fullWidth
                                placeholder="e.g., Product Name, SKU, Quantity"
                                helperText="Display name for the column header"
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <TextField
                                size="small"
                                label="Data Path"
                                value={column.dataPath}
                                onChange={(e) => {
                                  const updatedColumns = [...step.config.tableColumns];
                                  updatedColumns[index] = { ...column, dataPath: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                fullWidth
                                placeholder="e.g., productName, item.name, details.sku"
                                helperText="Path to the data in each row object (supports dot notation for nested properties)"
                              />
                            </Grid>
                            
                            <Grid item xs={4}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Column Type</InputLabel>
                                <Select
                                  value={column.type || 'text'}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    // If changing to computed, automatically set editable to false
                                    const newColumn = { ...column, type: e.target.value };
                                    if (e.target.value === 'computed') {
                                      newColumn.editable = false;
                                    }
                                    updatedColumns[index] = newColumn;
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  label="Column Type"
                                >
                                  <MenuItem value="text">Text</MenuItem>
                                  <MenuItem value="number">Number</MenuItem>
                                  <MenuItem value="currency">Currency</MenuItem>
                                  <MenuItem value="date">Date</MenuItem>
                                  <MenuItem value="time">Time</MenuItem>
                                  <MenuItem value="boolean">Checkbox</MenuItem>
                                  <MenuItem value="select">Dropdown</MenuItem>
                                  <MenuItem value="computed">Computed</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={4}>
                              <TextField
                                size="small"
                                label="Width"
                                value={column.width || 'auto'}
                                onChange={(e) => {
                                  const updatedColumns = [...step.config.tableColumns];
                                  updatedColumns[index] = { ...column, width: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                fullWidth
                                placeholder="auto, 100px, 20%"
                                helperText="Column width"
                              />
                            </Grid>
                            
                            <Grid item xs={4}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Alignment</InputLabel>
                                <Select
                                  value={column.alignment || 'left'}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, alignment: e.target.value };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  label="Alignment"
                                >
                                  <MenuItem value="left">Left</MenuItem>
                                  <MenuItem value="center">Center</MenuItem>
                                  <MenuItem value="right">Right</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={4}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={column.editable !== false}
                                    disabled={column.type === 'computed'}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, editable: e.target.checked };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                  />
                                }
                                label={column.type === 'computed' ? 'Editable (Auto-disabled for computed)' : 'Editable'}
                              />
                            </Grid>
                            
                            <Grid item xs={4}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={column.required || false}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, required: e.target.checked };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                  />
                                }
                                label="Required"
                              />
                            </Grid>
                            
                            {/* Conditional Validation for Number columns */}
                            {column.type === 'number' && (
                              <>
                                <Grid item xs={4}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={column.conditionalValidation || false}
                                        onChange={(e) => {
                                          const updatedColumns = [...step.config.tableColumns];
                                          updatedColumns[index] = { 
                                            ...column, 
                                            conditionalValidation: e.target.checked,
                                            // Reset conditional fields if disabling
                                            ...(e.target.checked ? {} : {
                                              validationOperator: undefined,
                                              validationValue: undefined
                                            })
                                          };
                                          handleConfigChange('tableColumns', updatedColumns);
                                        }}
                                      />
                                    }
                                    label="Conditional Validation"
                                  />
                                </Grid>
                                
                                {column.conditionalValidation && (
                                  <>
                                    <Grid item xs={4}>
                                      <FormControl size="small" fullWidth>
                                        <InputLabel>Operator</InputLabel>
                                        <Select
                                          value={column.validationOperator || 'gt'}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { ...column, validationOperator: e.target.value };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                          label="Operator"
                                        >
                                          <MenuItem value="gt">Greater than (&gt;)</MenuItem>
                                          <MenuItem value="gte">Greater than or equal (≥)</MenuItem>
                                          <MenuItem value="lt">Less than (&lt;)</MenuItem>
                                          <MenuItem value="lte">Less than or equal (≤)</MenuItem>
                                          <MenuItem value="eq">Equal to (=)</MenuItem>
                                          <MenuItem value="ne">Not equal to (≠)</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    
                                    <Grid item xs={4}>
                                      <TextField
                                        size="small"
                                        label="Value"
                                        type="number"
                                        value={column.validationValue !== undefined ? column.validationValue : ''}
                                        onChange={(e) => {
                                          const updatedColumns = [...step.config.tableColumns];
                                          const numValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                          updatedColumns[index] = { ...column, validationValue: isNaN(numValue) ? 0 : numValue };
                                          handleConfigChange('tableColumns', updatedColumns);
                                        }}
                                        fullWidth
                                        placeholder="Enter comparison value"
                                      />
                                    </Grid>
                                  </>
                                )}
                              </>
                            )}
                            
                            <Grid item xs={4}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={column.sortable !== false}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, sortable: e.target.checked };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                  />
                                }
                                label="Sortable"
                              />
                            </Grid>
                            
                            <Grid item xs={4}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={column.textWrap || false}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, textWrap: e.target.checked };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                  />
                                }
                                label="Text Wrap"
                              />
                            </Grid>
                            
                            {/* Options for select columns */}
                            {column.type === 'select' && (
                              <>
                                <Grid item xs={12}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Options Mode</InputLabel>
                                    <Select
                                      value={column.optionsMode || 'simple'}
                                      onChange={(e) => {
                                        const updatedColumns = [...step.config.tableColumns];
                                        updatedColumns[index] = { ...column, optionsMode: e.target.value };
                                        handleConfigChange('tableColumns', updatedColumns);
                                      }}
                                      label="Options Mode"
                                    >
                                      <MenuItem value="simple">Simple (Comma-separated)</MenuItem>
                                      <MenuItem value="advanced">Advanced (Name-Value Pairs)</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                
                                {(!column.optionsMode || column.optionsMode === 'simple') && (
                                  <Grid item xs={12}>
                                    <TextField
                                      size="small"
                                      label="Dropdown Options (comma-separated)"
                                      value={column.options || ''}
                                      onChange={(e) => {
                                        const updatedColumns = [...step.config.tableColumns];
                                        updatedColumns[index] = { ...column, options: e.target.value };
                                        handleConfigChange('tableColumns', updatedColumns);
                                      }}
                                      fullWidth
                                      placeholder="Option 1, Option 2, Option 3"
                                      helperText="Comma-separated list of dropdown options"
                                    />
                                  </Grid>
                                )}
                                
                                {column.optionsMode === 'advanced' && (
                                  <>
                                    <Grid item xs={12}>
                                      <FormControl size="small" fullWidth>
                                        <InputLabel>Data Source</InputLabel>
                                        <Select
                                          value={column.optionsDataSource || 'manual'}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { ...column, optionsDataSource: e.target.value };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                          label="Data Source"
                                        >
                                          <MenuItem value="manual">Manual Entry</MenuItem>
                                          <MenuItem value="variable">From Variable</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    
                                    {(!column.optionsDataSource || column.optionsDataSource === 'manual') && (
                                      <Grid item xs={12}>
                                        <Box>
                                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="body2" fontWeight="medium">
                                              Dropdown Options (Name-Value Pairs)
                                            </Typography>
                                            <Button
                                              size="small"
                                              startIcon={<AddIcon />}
                                              onClick={() => {
                                                const updatedColumns = [...step.config.tableColumns];
                                                const advancedOptions = column.advancedOptions || [];
                                                updatedColumns[index] = { 
                                                  ...column, 
                                                  advancedOptions: [...advancedOptions, { name: '', value: '' }] 
                                                };
                                                handleConfigChange('tableColumns', updatedColumns);
                                              }}
                                            >
                                              Add Option
                                            </Button>
                                          </Box>
                                          
                                          {(!column.advancedOptions || column.advancedOptions.length === 0) && (
                                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 2 }}>
                                              No options added yet. Click "Add Option" to create name-value pairs.
                                            </Typography>
                                          )}
                                          
                                          {(column.advancedOptions || []).map((option, optIndex) => (
                                            <Box key={optIndex} display="flex" gap={1} alignItems="center" mb={1}>
                                              <TextField
                                                size="small"
                                                label="Display Name"
                                                value={option.name || ''}
                                                onChange={(e) => {
                                                  const updatedColumns = [...step.config.tableColumns];
                                                  const advancedOptions = [...(column.advancedOptions || [])];
                                                  advancedOptions[optIndex] = { ...advancedOptions[optIndex], name: e.target.value };
                                                  updatedColumns[index] = { ...column, advancedOptions };
                                                  handleConfigChange('tableColumns', updatedColumns);
                                                }}
                                                placeholder="e.g., Apple"
                                                sx={{ flex: 1 }}
                                              />
                                              <TextField
                                                size="small"
                                                label="Value"
                                                value={option.value || ''}
                                                onChange={(e) => {
                                                  const updatedColumns = [...step.config.tableColumns];
                                                  const advancedOptions = [...(column.advancedOptions || [])];
                                                  advancedOptions[optIndex] = { ...advancedOptions[optIndex], value: e.target.value };
                                                  updatedColumns[index] = { ...column, advancedOptions };
                                                  handleConfigChange('tableColumns', updatedColumns);
                                                }}
                                                placeholder="e.g., trx52"
                                                sx={{ flex: 1 }}
                                              />
                                              <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => {
                                                  const updatedColumns = [...step.config.tableColumns];
                                                  const advancedOptions = [...(column.advancedOptions || [])];
                                                  advancedOptions.splice(optIndex, 1);
                                                  updatedColumns[index] = { ...column, advancedOptions };
                                                  handleConfigChange('tableColumns', updatedColumns);
                                                }}
                                              >
                                                <DeleteIcon />
                                              </IconButton>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Grid>
                                    )}
                                    
                                    {column.optionsDataSource === 'variable' && (
                                      <>
                                        <Grid item xs={12}>
                                          <TextField
                                            size="small"
                                            label="Variable Name"
                                            value={column.optionsVariable || ''}
                                            onChange={(e) => {
                                              const updatedColumns = [...step.config.tableColumns];
                                              updatedColumns[index] = { ...column, optionsVariable: e.target.value };
                                              handleConfigChange('tableColumns', updatedColumns);
                                            }}
                                            fullWidth
                                            placeholder="e.g., products"
                                            helperText="Variable containing array of objects"
                                          />
                                        </Grid>
                                        <Grid item xs={6}>
                                          <TextField
                                            size="small"
                                            label="Display Name Field"
                                            value={column.optionsNameField || ''}
                                            onChange={(e) => {
                                              const updatedColumns = [...step.config.tableColumns];
                                              updatedColumns[index] = { ...column, optionsNameField: e.target.value };
                                              handleConfigChange('tableColumns', updatedColumns);
                                            }}
                                            fullWidth
                                            placeholder="e.g., [*].name"
                                            helperText="Field path for display name"
                                          />
                                        </Grid>
                                        <Grid item xs={6}>
                                          <TextField
                                            size="small"
                                            label="Value Field"
                                            value={column.optionsValueField || ''}
                                            onChange={(e) => {
                                              const updatedColumns = [...step.config.tableColumns];
                                              updatedColumns[index] = { ...column, optionsValueField: e.target.value };
                                              handleConfigChange('tableColumns', updatedColumns);
                                            }}
                                            fullWidth
                                            placeholder="e.g., [*].value"
                                            helperText="Field path for stored value"
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                            <Typography variant="caption" color="info.dark">
                                              <strong>Example:</strong> If you have a variable "products" with value:<br/>
                                              [{`{name:"Apple", value:"trx52"}, {name:"Orange", value:"trx55"}`}]<br/>
                                              Set Variable Name = "products", Display Name Field = "[*].name", Value Field = "[*].value"
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      </>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                            
                            {/* Computed column formula */}
                            {column.type === 'computed' && (
                              <Grid item xs={12}>
                                <TextField
                                  size="small"
                                  label="Computation Formula"
                                  value={column.formula || ''}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, formula: e.target.value };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  fullWidth
                                  placeholder="unitPrice * quantity"
                                  helperText="Formula for computed values (e.g., 'unitPrice * quantity', '(price - discount) * quantity')"
                                />
                              </Grid>
                            )}
                            
                            {/* Number, Currency, and Computed Formatting Options */}
                            {(column.type === 'number' || column.type === 'currency' || column.type === 'computed') && (
                              <>
                                <Grid item xs={6}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Thousands Separator</InputLabel>
                                    <Select
                                      value={column.thousandsSeparator || ','}
                                      onChange={(e) => {
                                        const updatedColumns = [...step.config.tableColumns];
                                        updatedColumns[index] = { ...column, thousandsSeparator: e.target.value };
                                        handleConfigChange('tableColumns', updatedColumns);
                                      }}
                                      label="Thousands Separator"
                                    >
                                      <MenuItem value=",">Comma (,) - e.g., 1,000</MenuItem>
                                      <MenuItem value=".">Period (.) - e.g., 1.000</MenuItem>
                                      <MenuItem value=" ">Space ( ) - e.g., 1 000</MenuItem>
                                      <MenuItem value="">None - e.g., 1000</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Decimal Separator</InputLabel>
                                    <Select
                                      value={column.decimalSeparator || '.'}
                                      onChange={(e) => {
                                        const updatedColumns = [...step.config.tableColumns];
                                        updatedColumns[index] = { ...column, decimalSeparator: e.target.value };
                                        handleConfigChange('tableColumns', updatedColumns);
                                      }}
                                      label="Decimal Separator"
                                    >
                                      <MenuItem value=".">Period (.) - e.g., 10.50</MenuItem>
                                      <MenuItem value=",">Comma (,) - e.g., 10,50</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                
                                <Grid item xs={column.type === 'currency' ? 6 : 12}>
                                  <TextField
                                    size="small"
                                    label="Decimal Places"
                                    type="number"
                                    value={column.decimalPlaces !== undefined ? column.decimalPlaces : (column.type === 'currency' ? 2 : 0)}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, decimalPlaces: parseInt(e.target.value) || 0 };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    fullWidth
                                    inputProps={{ min: 0, max: 10 }}
                                    helperText="Number of decimal places to display (0-10)"
                                  />
                                </Grid>
                                
                                {/* Currency Symbol Configuration - only for currency columns */}
                                {column.type === 'currency' && (
                                  <Grid item xs={6}>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Currency Symbol</InputLabel>
                                      <Select
                                        value={column.currencySymbol || '$'}
                                        onChange={(e) => {
                                          const updatedColumns = [...step.config.tableColumns];
                                          updatedColumns[index] = { ...column, currencySymbol: e.target.value };
                                          handleConfigChange('tableColumns', updatedColumns);
                                        }}
                                        label="Currency Symbol"
                                      >
                                        <MenuItem value="$">Dollar ($)</MenuItem>
                                        <MenuItem value="€">Euro (€)</MenuItem>
                                        <MenuItem value="£">Pound (£)</MenuItem>
                                        <MenuItem value="¥">Yen (¥)</MenuItem>
                                        <MenuItem value="₹">Rupee (₹)</MenuItem>
                                        <MenuItem value="₽">Ruble (₽)</MenuItem>
                                        <MenuItem value="₦">Naira (₦)</MenuItem>
                                        <MenuItem value="R">Rand (R)</MenuItem>
                                        <MenuItem value="₨">Pakistani Rupee (₨)</MenuItem>
                                        <MenuItem value="Rp.">Indonesian Rupiah (Rp.)</MenuItem>
                                        <MenuItem value="custom">Custom...</MenuItem>
                                      </Select>
                                    </FormControl>
                                    {column.currencySymbol === 'custom' && (
                                      <TextField
                                        size="small"
                                        label="Custom Currency Symbol"
                                        value={column.customCurrencySymbol || ''}
                                        onChange={(e) => {
                                          const updatedColumns = [...step.config.tableColumns];
                                          updatedColumns[index] = { 
                                            ...column, 
                                            customCurrencySymbol: e.target.value,
                                            currencySymbol: 'custom'
                                          };
                                          handleConfigChange('tableColumns', updatedColumns);
                                        }}
                                        fullWidth
                                        placeholder="Enter custom symbol (e.g., ¤, kr, etc.)"
                                        helperText="Enter your custom currency symbol"
                                        sx={{ mt: 1 }}
                                      />
                                    )}
                                  </Grid>
                                )}
                              </>
                            )}
                            
                            {/* Date Format Configuration - only for date columns */}
                            {column.type === 'date' && (
                              <Grid item xs={12}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Date Format</InputLabel>
                                  <Select
                                    value={column.dateFormat || 'MM/dd/yyyy'}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, dateFormat: e.target.value };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    label="Date Format"
                                  >
                                    <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (12/25/2023)</MenuItem>
                                    <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (25/12/2023)</MenuItem>
                                    <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (2023-12-25)</MenuItem>
                                    <MenuItem value="dd.MM.yyyy">dd.MM.yyyy (25.12.2023)</MenuItem>
                                    <MenuItem value="MM-dd-yyyy">MM-dd-yyyy (12-25-2023)</MenuItem>
                                    <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (25-12-2023)</MenuItem>
                                    <MenuItem value="MMM dd, yyyy">MMM dd, yyyy (Dec 25, 2023)</MenuItem>
                                    <MenuItem value="MMMM dd, yyyy">MMMM dd, yyyy (December 25, 2023)</MenuItem>
                                    <MenuItem value="dd MMM yyyy">dd MMM yyyy (25 Dec 2023)</MenuItem>
                                    <MenuItem value="dd MMMM yyyy">dd MMMM yyyy (25 December 2023)</MenuItem>
                                    <MenuItem value="custom">Custom...</MenuItem>
                                  </Select>
                                </FormControl>
                                {column.dateFormat === 'custom' && (
                                  <TextField
                                    size="small"
                                    label="Custom Date Format"
                                    value={column.customDateFormat || ''}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { 
                                        ...column, 
                                        customDateFormat: e.target.value,
                                        dateFormat: 'custom'
                                      };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    fullWidth
                                    placeholder="e.g., DD/MM/YYYY, dd.mm.yyyy, MM-dd-yy"
                                    helperText="Enter custom date format (supports DD, MM, YYYY, dd, mm, yyyy)"
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </Grid>
                            )}

                            {/* Time Format Configuration - only for time columns */}
                            {column.type === 'time' && (
                              <Grid item xs={12}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Time Format</InputLabel>
                                  <Select
                                    value={column.timeFormat || 'HH:mm:ss'}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, timeFormat: e.target.value };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    label="Time Format"
                                  >
                                    <MenuItem value="HH:mm:ss">24-hour with seconds (14:30:44)</MenuItem>
                                    <MenuItem value="HH:mm">24-hour without seconds (14:30)</MenuItem>
                                    <MenuItem value="hh:mm:ss A">12-hour with seconds (02:30:44 PM)</MenuItem>
                                    <MenuItem value="hh:mm A">12-hour without seconds (02:30 PM)</MenuItem>
                                    <MenuItem value="h:mm A">12-hour no leading zero (2:30 PM)</MenuItem>
                                    <MenuItem value="h:mm:ss A">12-hour no leading zero with seconds (2:30:44 PM)</MenuItem>
                                    <MenuItem value="custom">Custom...</MenuItem>
                                  </Select>
                                  <FormHelperText>
                                    Choose how to display time values. Input format is HHMMSS (e.g., 035044 = 03:50:44)
                                  </FormHelperText>
                                </FormControl>
                                {column.timeFormat === 'custom' && (
                                  <TextField
                                    size="small"
                                    label="Custom Time Format"
                                    value={column.customTimeFormat || ''}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { 
                                        ...column, 
                                        customTimeFormat: e.target.value,
                                        timeFormat: 'custom'
                                      };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    fullWidth
                                    placeholder="e.g., HH:mm, h:mm A, HH.mm.ss"
                                    helperText="Custom time format (HH=24hr, hh=12hr, mm=minutes, ss=seconds, A=AM/PM)"
                                    sx={{ mt: 1 }}
                                  />
                                )}
                                
                                {/* Timezone Configuration */}
                                <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                                  <InputLabel>Timezone</InputLabel>
                                  <Select
                                    value={column.timezone || 'UTC'}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, timezone: e.target.value };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    label="Timezone"
                                  >
                                    <MenuItem value="UTC">UTC (Server Time)</MenuItem>
                                    <MenuItem value="UTC+1">UTC+1 (Central European Time)</MenuItem>
                                    <MenuItem value="UTC+2">UTC+2 (Eastern European Time)</MenuItem>
                                    <MenuItem value="UTC+3">UTC+3 (Moscow Time)</MenuItem>
                                    <MenuItem value="UTC+4">UTC+4 (Gulf Standard Time)</MenuItem>
                                    <MenuItem value="UTC+5">UTC+5 (Pakistan Standard Time)</MenuItem>
                                    <MenuItem value="UTC+6">UTC+6 (Bangladesh Standard Time)</MenuItem>
                                    <MenuItem value="UTC+7">UTC+7 (Indochina Time)</MenuItem>
                                    <MenuItem value="UTC+8">UTC+8 (China Standard Time)</MenuItem>
                                    <MenuItem value="UTC+9">UTC+9 (Japan Standard Time)</MenuItem>
                                    <MenuItem value="UTC+10">UTC+10 (Australian Eastern Time)</MenuItem>
                                    <MenuItem value="UTC+11">UTC+11 (Solomon Islands Time)</MenuItem>
                                    <MenuItem value="UTC+12">UTC+12 (New Zealand Time)</MenuItem>
                                    <MenuItem value="UTC-1">UTC-1 (Cape Verde Time)</MenuItem>
                                    <MenuItem value="UTC-2">UTC-2 (South Georgia Time)</MenuItem>
                                    <MenuItem value="UTC-3">UTC-3 (Argentina Time)</MenuItem>
                                    <MenuItem value="UTC-4">UTC-4 (Atlantic Standard Time)</MenuItem>
                                    <MenuItem value="UTC-5">UTC-5 (Eastern Standard Time)</MenuItem>
                                    <MenuItem value="UTC-6">UTC-6 (Central Standard Time)</MenuItem>
                                    <MenuItem value="UTC-7">UTC-7 (Mountain Standard Time)</MenuItem>
                                    <MenuItem value="UTC-8">UTC-8 (Pacific Standard Time)</MenuItem>
                                    <MenuItem value="UTC-9">UTC-9 (Alaska Standard Time)</MenuItem>
                                    <MenuItem value="UTC-10">UTC-10 (Hawaii Standard Time)</MenuItem>
                                    <MenuItem value="UTC-11">UTC-11 (Samoa Standard Time)</MenuItem>
                                    <MenuItem value="UTC-12">UTC-12 (Baker Island Time)</MenuItem>
                                  </Select>
                                  <FormHelperText>
                                    Select the timezone for time display. This will convert server time to your local timezone.
                                  </FormHelperText>
                                </FormControl>
                              </Grid>
                            )}

                            {/* Text Transformation Rules - only for text type columns */}
                            {column.type === 'text' && (
                              <Grid item xs={12}>
                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                    ✨ Text Transformation Rules
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                    Automatically transform text as users type or submit
                                  </Typography>
                                  
                                  <Box display="flex" flexDirection="column" gap={1.5}>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          size="small"
                                          checked={column.textTransform === 'uppercase'}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { 
                                              ...column, 
                                              textTransform: e.target.checked ? 'uppercase' : undefined 
                                            };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                        />
                                      }
                                      label="Convert to UPPERCASE"
                                    />
                                    
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          size="small"
                                          checked={column.textTransform === 'lowercase'}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { 
                                              ...column, 
                                              textTransform: e.target.checked ? 'lowercase' : undefined 
                                            };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                        />
                                      }
                                      label="Convert to lowercase"
                                    />
                                    
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          size="small"
                                          checked={column.trimWhitespace || false}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { 
                                              ...column, 
                                              trimWhitespace: e.target.checked 
                                            };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                        />
                                      }
                                      label="Trim whitespace (remove leading/trailing spaces)"
                                    />
                                  </Box>
                                  
                                  {(column.textTransform || column.trimWhitespace) && (
                                    <Box mt={1.5} sx={{ p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                                      <Typography variant="caption" color="info.dark">
                                        <strong>Preview:</strong> "  Sample Text  " → "
                                        {(() => {
                                          let text = '  Sample Text  ';
                                          if (column.textTransform === 'uppercase') text = text.toUpperCase();
                                          if (column.textTransform === 'lowercase') text = text.toLowerCase();
                                          if (column.trimWhitespace) text = text.trim();
                                          return text;
                                        })()}"
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                        </SortableColumnCard>
                      ))}
                    </SortableContext>
                  </DndContext>
                  
                  {(!step.config?.tableColumns || step.config.tableColumns.length === 0) && (
                    <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="body2">
                        No table columns defined. Add columns to configure your data table.
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Add Column Button - moved to bottom for better UX */}
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const currentColumns = step.config?.tableColumns || [];
                        const newColumn = {
                          id: `col-${Date.now()}`,
                          key: '',
                          label: '',
                          dataPath: '',
                          type: 'text',
                          editable: true,
                          required: false,
                          width: 'auto',
                          alignment: 'left'
                        };
                        handleConfigChange('tableColumns', [...currentColumns, newColumn]);
                      }}
                    >
                      Add Column
                    </Button>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                {/* Quick Templates for Table Columns */}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                    🚀 Quick Column Templates
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const productColumns = [
                          { id: 'col-1', key: 'productName', label: 'Product Name', dataPath: 'productName', type: 'text', editable: true, required: true, width: '200px', alignment: 'left' },
                          { id: 'col-2', key: 'sku', label: 'SKU', dataPath: 'sku', type: 'text', editable: true, required: true, width: '120px', alignment: 'left' },
                          { id: 'col-3', key: 'quantity', label: 'Quantity', dataPath: 'quantity', type: 'number', editable: true, required: true, width: '100px', alignment: 'right' },
                          { id: 'col-4', key: 'unitPrice', label: 'Unit Price', dataPath: 'unitPrice', type: 'currency', editable: true, required: true, width: '120px', alignment: 'right' },
                          { id: 'col-5', key: 'totalPrice', label: 'Total', dataPath: 'totalPrice', type: 'computed', formula: 'quantity * unitPrice', editable: false, required: false, width: '120px', alignment: 'right' }
                        ];
                        handleConfigChange('tableColumns', productColumns);
                      }}
                    >
                      📦 Product/Line Items
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const invoiceColumns = [
                          { id: 'col-1', key: 'description', label: 'Description', dataPath: 'description', type: 'text', editable: true, required: true, width: '250px', alignment: 'left', textWrap: true },
                          { id: 'col-2', key: 'quantity', label: 'Qty', dataPath: 'quantity', type: 'number', editable: true, required: true, width: '80px', alignment: 'right' },
                          { id: 'col-3', key: 'rate', label: 'Rate', dataPath: 'rate', type: 'currency', editable: true, required: true, width: '100px', alignment: 'right' },
                          { id: 'col-4', key: 'amount', label: 'Amount', dataPath: 'amount', type: 'computed', formula: 'quantity * rate', editable: false, required: false, width: '120px', alignment: 'right' }
                        ];
                        handleConfigChange('tableColumns', invoiceColumns);
                      }}
                    >
                      🧾 Invoice Line Items
                    </Button>
                  </Box>
                </Box>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              {/* Re-match Data Configuration */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  🔄 Re-match Data Configuration
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Allow users to manually re-match items from the primary data source with items from another array variable.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={step.config?.enableReMatch || false}
                      onChange={(e) => handleConfigChange('enableReMatch', e.target.checked)}
                    />
                  }
                  label="Enable re-match data functionality"
                />
                
                {step.config?.enableReMatch && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9ff', borderRadius: 1, border: '1px solid #e3e7ff' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Secondary Array Variable (to match against)</InputLabel>
                          <Select
                            value={step.config?.reMatchConfig?.secondaryArray || ''}
                            onChange={(e) => handleConfigChange('reMatchConfig.secondaryArray', e.target.value)}
                          >
                            {availableVariables.map(variable => (
                              <MenuItem key={variable} value={variable}>
                                {variable}
                                {(variable.toLowerCase().includes('items') || 
                                  variable.toLowerCase().includes('products') || 
                                  variable.toLowerCase().includes('data') ||
                                  variable.toLowerCase().includes('list') ||
                                  variable.toLowerCase().includes('array')) && 
                                  <Chip size="small" label="Array" sx={{ ml: 1, height: 16 }} />
                                }
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            Select the array variable to match against (e.g., responseItems, orderItems, apiData)
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <TextField
                          size="small"
                          label="Primary Array Field (for matching)"
                          value={step.config?.reMatchConfig?.primaryField || ''}
                          onChange={(e) => handleConfigChange('reMatchConfig.primaryField', e.target.value)}
                          fullWidth
                          placeholder="e.g., productName, materialDesc"
                          helperText="Field in primary array (data source) to use for matching"
                        />
                      </Grid>
                      
                      <Grid item xs={6}>
                        <TextField
                          size="small"
                          label="Secondary Array Field (for matching)"
                          value={step.config?.reMatchConfig?.secondaryField || ''}
                          onChange={(e) => handleConfigChange('reMatchConfig.secondaryField', e.target.value)}
                          fullWidth
                          placeholder="e.g., MATERIAL_DESC, productName"
                          helperText="Field in secondary array to match against"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          label="Re-match Button Label"
                          value={step.config?.reMatchConfig?.buttonLabel || 'Find Match'}
                          onChange={(e) => handleConfigChange('reMatchConfig.buttonLabel', e.target.value)}
                          fullWidth
                          placeholder="e.g., Find Match, Re-match Item, Match with PO"
                          helperText="Text displayed on the re-match button"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          🔄 Field Mapping Configuration
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Configure which fields from the secondary array should be copied to the primary array when a match is selected.
                        </Typography>
                        
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Field Mappings
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                const currentMappings = step.config?.reMatchConfig?.fieldMappings || [];
                                const newMapping = {
                                  id: `mapping-${Date.now()}`,
                                  sourceField: '',
                                  targetField: '',
                                  description: '',
                                  showInPreview: true // Show in preview by default
                                };
                                handleConfigChange('reMatchConfig.fieldMappings', [...currentMappings, newMapping]);
                              }}
                            >
                              Add Field Mapping
                            </Button>
                          </Box>
                          
                          {(step.config?.reMatchConfig?.fieldMappings || []).map((mapping, index) => (
                            <Card key={mapping.id} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9ff', border: '1px solid #e3e7ff' }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <TextField
                                    size="small"
                                    label="Source Field (Secondary Array)"
                                    value={mapping.sourceField}
                                    onChange={(e) => {
                                      const updatedMappings = [...step.config.reMatchConfig.fieldMappings];
                                      updatedMappings[index] = { ...mapping, sourceField: e.target.value };
                                      handleConfigChange('reMatchConfig.fieldMappings', updatedMappings);
                                    }}
                                    fullWidth
                                    placeholder="e.g., MATERIAL_DESC, QUANTITY_PO"
                                    helperText="Field from secondary array to copy"
                                  />
                                </Grid>
                                
                                <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                  <Typography variant="h6" color="primary">→</Typography>
                                </Grid>
                                
                                <Grid item xs={4}>
                                  <TextField
                                    size="small"
                                    label="Target Field (Primary Array)"
                                    value={mapping.targetField}
                                    onChange={(e) => {
                                      const updatedMappings = [...step.config.reMatchConfig.fieldMappings];
                                      updatedMappings[index] = { ...mapping, targetField: e.target.value };
                                      handleConfigChange('reMatchConfig.fieldMappings', updatedMappings);
                                    }}
                                    fullWidth
                                    placeholder="e.g., productName, quantityPO"
                                    helperText="Field in primary array to update"
                                  />
                                </Grid>
                                
                                <Grid item xs={2}>
                                  <TextField
                                    size="small"
                                    label="Description"
                                    value={mapping.description}
                                    onChange={(e) => {
                                      const updatedMappings = [...step.config.reMatchConfig.fieldMappings];
                                      updatedMappings[index] = { ...mapping, description: e.target.value };
                                      handleConfigChange('reMatchConfig.fieldMappings', updatedMappings);
                                    }}
                                    fullWidth
                                    placeholder="e.g., Product Name"
                                    helperText="Optional description"
                                  />
                                </Grid>
                                
                                <Grid item xs={1}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const updatedMappings = step.config.reMatchConfig.fieldMappings.filter((_, i) => i !== index);
                                      handleConfigChange('reMatchConfig.fieldMappings', updatedMappings);
                                    }}
                                    sx={{ color: '#d32f2f' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Grid>
                                
                                {/* Show in Preview Toggle */}
                                <Grid item xs={12}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={mapping.showInPreview !== false} // Default to true if not set
                                        onChange={(e) => {
                                          const updatedMappings = [...step.config.reMatchConfig.fieldMappings];
                                          updatedMappings[index] = { ...mapping, showInPreview: e.target.checked };
                                          handleConfigChange('reMatchConfig.fieldMappings', updatedMappings);
                                        }}
                                      />
                                    }
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2">
                                          👁️ Show in Preview
                                        </Typography>
                                        <Tooltip title="When enabled, this field will be displayed in the re-match modal to help differentiate between items. Useful when multiple items have the same name but different values.">
                                          <InfoIcon fontSize="small" sx={{ color: 'action.active', fontSize: 16 }} />
                                        </Tooltip>
                                      </Box>
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </Card>
                          ))}
                          
                          {(!step.config?.reMatchConfig?.fieldMappings || step.config.reMatchConfig.fieldMappings.length === 0) && (
                            <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                              <Typography variant="body2">
                                No field mappings defined. Add mappings to specify which fields should be copied when a match is selected.
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Quick Templates for Field Mappings */}
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                              🚀 Quick Mapping Templates:
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const template = [
                                    { id: `mapping-${Date.now()}-1`, sourceField: 'MATERIAL_DESC', targetField: 'productName', description: 'Product Name', showInPreview: true },
                                    { id: `mapping-${Date.now()}-2`, sourceField: 'QUANTITY_PO', targetField: 'quantityPO', description: 'PO Quantity', showInPreview: true },
                                    { id: `mapping-${Date.now()}-3`, sourceField: 'MATERIAL_ID', targetField: 'materialId', description: 'Material ID', showInPreview: true },
                                    { id: `mapping-${Date.now()}-4`, sourceField: 'ITEM_PO', targetField: 'itemPO', description: 'PO Item Number', showInPreview: false }
                                  ];
                                  handleConfigChange('reMatchConfig.fieldMappings', template);
                                }}
                              >
                                📦 SAP PO Mapping
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const template = [
                                    { id: `mapping-${Date.now()}-1`, sourceField: 'productName', targetField: 'productName', description: 'Product Name', showInPreview: true },
                                    { id: `mapping-${Date.now()}-2`, sourceField: 'quantity', targetField: 'quantity', description: 'Quantity', showInPreview: true },
                                    { id: `mapping-${Date.now()}-3`, sourceField: 'price', targetField: 'price', description: 'Price', showInPreview: true }
                                  ];
                                  handleConfigChange('reMatchConfig.fieldMappings', template);
                                }}
                              >
                                🛒 Generic Product Mapping
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          🎯 When to Show Re-match Button
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Configure when the re-match button should appear for each row
                        </Typography>
                        
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Re-match Conditions
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                const currentConditions = step.config?.reMatchConfig?.showConditions || [];
                                const newCondition = {
                                  id: `rematch-condition-${Date.now()}`,
                                  field: '',
                                  operator: 'equals',
                                  value: '',
                                  logicalOperator: 'OR'
                                };
                                handleConfigChange('reMatchConfig.showConditions', [...currentConditions, newCondition]);
                              }}
                            >
                              Add Condition
                            </Button>
                          </Box>
                          
                          {(step.config?.reMatchConfig?.showConditions || []).map((condition, index) => (
                            <Card key={condition.id} sx={{ mb: 2, p: 2, backgroundColor: '#fff3e0', border: '1px solid #ffcc02' }}>
                              <Box display="flex" alignItems="center" gap={2}>
                                <TextField
                                  size="small"
                                  label="Field"
                                  value={condition.field}
                                  onChange={(e) => {
                                    const updatedConditions = [...step.config.reMatchConfig.showConditions];
                                    updatedConditions[index] = { ...condition, field: e.target.value };
                                    handleConfigChange('reMatchConfig.showConditions', updatedConditions);
                                  }}
                                  placeholder="e.g., exists_in_PO, materialId"
                                  sx={{ flex: 1 }}
                                />
                                
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                  <InputLabel>Operator</InputLabel>
                                  <Select
                                    value={condition.operator}
                                    onChange={(e) => {
                                      const updatedConditions = [...step.config.reMatchConfig.showConditions];
                                      updatedConditions[index] = { ...condition, operator: e.target.value };
                                      handleConfigChange('reMatchConfig.showConditions', updatedConditions);
                                    }}
                                    label="Operator"
                                  >
                                    <MenuItem value="equals">Equals</MenuItem>
                                    <MenuItem value="not_equals">Not Equals</MenuItem>
                                    <MenuItem value="is_empty">Is Empty</MenuItem>
                                    <MenuItem value="is_not_empty">Is Not Empty</MenuItem>
                                    <MenuItem value="is_null">Is Null</MenuItem>
                                    <MenuItem value="is_not_null">Is Not Null</MenuItem>
                                    <MenuItem value="is_true">Is True</MenuItem>
                                    <MenuItem value="is_false">Is False</MenuItem>
                                  </Select>
                                </FormControl>
                                
                                <TextField
                                  size="small"
                                  label="Value"
                                  value={condition.value}
                                  onChange={(e) => {
                                    const updatedConditions = [...step.config.reMatchConfig.showConditions];
                                    updatedConditions[index] = { ...condition, value: e.target.value };
                                    handleConfigChange('reMatchConfig.showConditions', updatedConditions);
                                  }}
                                  placeholder="true, false, null, etc."
                                  sx={{ flex: 1 }}
                                />
                                
                                {index < (step.config?.reMatchConfig?.showConditions || []).length - 1 && (
                                  <FormControl size="small" sx={{ minWidth: 80 }}>
                                    <InputLabel>Logic</InputLabel>
                                    <Select
                                      value={condition.logicalOperator}
                                      onChange={(e) => {
                                        const updatedConditions = [...step.config.reMatchConfig.showConditions];
                                        updatedConditions[index] = { ...condition, logicalOperator: e.target.value };
                                        handleConfigChange('reMatchConfig.showConditions', updatedConditions);
                                      }}
                                      label="Logic"
                                    >
                                      <MenuItem value="AND">AND</MenuItem>
                                      <MenuItem value="OR">OR</MenuItem>
                                    </Select>
                                  </FormControl>
                                )}
                                
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const updatedConditions = step.config.reMatchConfig.showConditions.filter((_, i) => i !== index);
                                    handleConfigChange('reMatchConfig.showConditions', updatedConditions);
                                  }}
                                  sx={{ color: '#d32f2f' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Card>
                          ))}
                          
                          {(!step.config?.reMatchConfig?.showConditions || step.config.reMatchConfig.showConditions.length === 0) && (
                            <Box sx={{ textAlign: 'center', py: 3, color: 'success.main', backgroundColor: 'rgba(76, 175, 80, 0.08)', border: '2px dashed #4caf50', borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                ✅ Re-match button will appear on ALL rows
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem', mt: 1 }}>
                                No conditions defined means the re-match button will always be visible. Add conditions above to show the button only for specific rows.
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Quick Templates */}
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                              🚀 Quick Condition Templates:
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const template = [
                                    { id: `condition-${Date.now()}`, field: 'exists_in_PO', operator: 'is_false', value: '', logicalOperator: 'OR' }
                                  ];
                                  handleConfigChange('reMatchConfig.showConditions', template);
                                }}
                              >
                                🔍 Missing from PO
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const template = [
                                    { id: `condition-${Date.now()}-1`, field: 'materialId', operator: 'is_empty', value: '', logicalOperator: 'OR' },
                                    { id: `condition-${Date.now()}-2`, field: 'itemPO', operator: 'is_empty', value: '', logicalOperator: 'OR' }
                                  ];
                                  handleConfigChange('reMatchConfig.showConditions', template);
                                }}
                              >
                                🏷️ Missing Key Fields
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    {/* Preview of Re-match Configuration */}
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                        🔄 Re-match Configuration Preview:
                      </Typography>
                      <Typography variant="body2" color="#2e7d32" sx={{ fontSize: '0.875rem' }}>
                        • Primary Array: {step.config?.dataSource || 'Not set'} (table data source)<br/>
                        • Secondary Array: {step.config?.reMatchConfig?.secondaryArray || 'Not set'}<br/>
                        • Matching: {step.config?.reMatchConfig?.primaryField || 'Not set'} ↔ {step.config?.reMatchConfig?.secondaryField || 'Not set'}<br/>
                        • Button Label: "{step.config?.reMatchConfig?.buttonLabel || 'Find Match'}"<br/>
                        • Conditions: {(step.config?.reMatchConfig?.showConditions || []).length} configured<br/>
                        • Field Mappings: {(step.config?.reMatchConfig?.fieldMappings || []).length} configured
                        {(step.config?.reMatchConfig?.fieldMappings || []).length > 0 && (
                          <>
                            <br/>
                            • Mappings: {(step.config?.reMatchConfig?.fieldMappings || []).map(m => `${m.sourceField} → ${m.targetField}`).join(', ')}
                            <br/>
                            • Preview Fields: {(() => {
                              const previewFields = (step.config?.reMatchConfig?.fieldMappings || []).filter(m => m.showInPreview !== false);
                              return previewFields.length > 0 
                                ? previewFields.map(m => m.description || m.targetField).join(', ')
                                : 'All fields';
                            })()}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Library Re-Match Configuration */}
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={step.config?.enableLibraryReMatch || false}
                        onChange={(e) => {
                          handleConfigChange('enableLibraryReMatch', e.target.checked);
                          if (e.target.checked && !step.config?.libraryReMatchConfig) {
                            handleConfigChange('libraryReMatchConfig', {
                              targetField: '',
                              sourceField: '',
                              libraryId: '',
                              originalPrefix: 'original_',
                              buttonLabel: 'Correct Match',
                              aliasSourceMappings: []
                            });
                          }
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>Enable Library Re-Match</Typography>
                        <Tooltip title="Allow users to correct library-matched fields using the matching library search">
                          <InfoIcon fontSize="small" color="action" />
                        </Tooltip>
                      </Box>
                    }
                  />

                  {step.config?.enableLibraryReMatch && (
                    <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid #2196f3' }}>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Configure library-based field correction. When users correct a value, the original is backed up and the field is updated with the correction.
                      </Typography>

                      {/* Target Field */}
                      <TextField
                        fullWidth
                        size="small"
                        label="Target Field to Correct"
                        value={step.config?.libraryReMatchConfig?.targetField || ''}
                        onChange={(e) => handleConfigChange('libraryReMatchConfig.targetField', e.target.value)}
                        placeholder="e.g., official_description, productName"
                        helperText="The field in the table that stores the matched result (target field from library lookup)"
                        sx={{ mb: 2 }}
                      />

                      {/* Source Field for Learning */}
                      <TextField
                        fullWidth
                        size="small"
                        label="Source Field for Learning"
                        value={step.config?.libraryReMatchConfig?.sourceField || ''}
                        onChange={(e) => handleConfigChange('libraryReMatchConfig.sourceField', e.target.value)}
                        placeholder="e.g., description, productCode"
                        helperText="The original field used for library lookup (must match your learning fields configuration)"
                        sx={{ mb: 2 }}
                      />

                      {/* Source Field for Alias (auto-populate source in confirmation dialog) */}
                      <TextField
                        fullWidth
                        size="small"
                        label="Source Field for Alias"
                        value={step.config?.libraryReMatchConfig?.sourceFieldForAlias || ''}
                        onChange={(e) => handleConfigChange('libraryReMatchConfig.sourceFieldForAlias', e.target.value)}
                        placeholder="e.g., customerName, companyName"
                        helperText="Field name whose value will be used as the alias source in the confirmation dialog. Checks row data first, then execution variables (e.g., from an API endpoint). If set, the source is auto-populated so users don't have to type it."
                        sx={{ mb: 2 }}
                      />

                      {/* Library Selection */}
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Matching Library</InputLabel>
                        <Select
                          value={step.config?.libraryReMatchConfig?.libraryId || ''}
                          onChange={(e) => handleConfigChange('libraryReMatchConfig.libraryId', e.target.value)}
                          label="Matching Library"
                        >
                          {(availableLibraries || []).map(lib => (
                            <MenuItem key={lib.libraryId} value={lib.libraryId}>
                              {lib.name} ({lib.entryCount || lib.entries?.length || 0} entries)
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {!availableLibraries || availableLibraries.length === 0
                            ? 'Go to Project Settings → Matching Libraries to create libraries'
                            : 'Library to search when users correct matches'
                          }
                        </FormHelperText>
                      </FormControl>

                      {/* Original Value Prefix */}
                      <TextField
                        fullWidth
                        size="small"
                        label="Original Value Prefix"
                        value={step.config?.libraryReMatchConfig?.originalPrefix || 'original_'}
                        onChange={(e) => handleConfigChange('libraryReMatchConfig.originalPrefix', e.target.value)}
                        placeholder="e.g., original_, backup_, old_"
                        helperText="Prefix for backing up the original value (e.g., original_productName)"
                        sx={{ mb: 2 }}
                      />

                      {/* Button Label */}
                      <TextField
                        fullWidth
                        size="small"
                        label="Correction Button Label"
                        value={step.config?.libraryReMatchConfig?.buttonLabel || 'Correct Match'}
                        onChange={(e) => handleConfigChange('libraryReMatchConfig.buttonLabel', e.target.value)}
                        placeholder="e.g., Correct Match, Fix Product"
                        helperText="Label for the correction button shown in the table"
                        sx={{ mb: 2 }}
                      />

                      {/* Source Prioritization */}
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Prioritized Sources (Display Order)</InputLabel>
                        <Select
                          multiple
                          value={step.config?.libraryReMatchConfig?.prioritizedSources || []}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Limit to maximum 5 sources
                            const limitedValue = Array.isArray(value) ? value.slice(0, 5) : [];
                            handleConfigChange('libraryReMatchConfig.prioritizedSources', limitedValue);
                          }}
                          label="Prioritized Sources (Display Order)"
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Box>
                          )}
                        >
                          {(() => {
                            const libraryId = step.config?.libraryReMatchConfig?.libraryId;
                            const sources = libraryId ? (librarySources[libraryId] || []) : [];
                            const selectedSources = step.config?.libraryReMatchConfig?.prioritizedSources || [];
                            
                            return sources.length > 0 ? (
                              sources.map(sourceObj => {
                                const sourceName = sourceObj.source || sourceObj;
                                return (
                                  <MenuItem 
                                    key={sourceName} 
                                    value={sourceName}
                                    disabled={
                                      selectedSources.length >= 5 &&
                                      !selectedSources.includes(sourceName)
                                    }
                                  >
                                    {sourceName} {sourceObj.count ? `(${sourceObj.count})` : ''}
                                  </MenuItem>
                                );
                              })
                            ) : (
                              <MenuItem disabled>
                                {libraryId ? 'Loading sources...' : 'Select a library first'}
                              </MenuItem>
                            );
                          })()}
                        </Select>
                        <FormHelperText>
                          Select up to 5 sources to prioritize in the "Also known as" display. Sources will be shown in the order selected.
                        </FormHelperText>
                      </FormControl>

                      {/* Alias Source Mappings */}
                      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            🔗 Return Alias Values by Source
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const currentConfig = step.config?.libraryReMatchConfig || {};
                              const aliasSourceMappings = currentConfig.aliasSourceMappings || [];
                              aliasSourceMappings.push({
                                sourceToMatch: '',
                                targetField: ''
                              });
                              handleConfigChange('libraryReMatchConfig.aliasSourceMappings', aliasSourceMappings);
                            }}
                          >
                            Add Source Mapping
                          </Button>
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                          When a user selects a library entry during re-match, automatically populate additional fields with alias values from specific sources.
                          Example: Return "itemId" alias value into "itemId" field.
                        </Typography>
                        
                        {(step.config?.libraryReMatchConfig?.aliasSourceMappings || []).map((aliasMapping, aliasIdx) => (
                          <Box key={aliasIdx} sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, mb: 1, border: '1px solid #e0e0e0' }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={5}>
                                <TextField
                                  label="Source Name"
                                  value={aliasMapping.sourceToMatch || ''}
                                  onChange={(e) => {
                                    const currentConfig = step.config?.libraryReMatchConfig || {};
                                    const aliasSourceMappings = [...(currentConfig.aliasSourceMappings || [])];
                                    aliasSourceMappings[aliasIdx] = { ...aliasSourceMappings[aliasIdx], sourceToMatch: e.target.value };
                                    handleConfigChange('libraryReMatchConfig.aliasSourceMappings', aliasSourceMappings);
                                  }}
                                  fullWidth
                                  size="small"
                                  placeholder="itemId, companyCode"
                                  helperText="Source name to get alias from"
                                />
                              </Grid>
                              <Grid item xs={5}>
                                <TextField
                                  label="Target Field"
                                  value={aliasMapping.targetField || ''}
                                  onChange={(e) => {
                                    const currentConfig = step.config?.libraryReMatchConfig || {};
                                    const aliasSourceMappings = [...(currentConfig.aliasSourceMappings || [])];
                                    aliasSourceMappings[aliasIdx] = { ...aliasSourceMappings[aliasIdx], targetField: e.target.value };
                                    handleConfigChange('libraryReMatchConfig.aliasSourceMappings', aliasSourceMappings);
                                  }}
                                  fullWidth
                                  size="small"
                                  placeholder="itemId"
                                  helperText="Field to store alias value"
                                />
                              </Grid>
                              <Grid item xs={2}>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  fullWidth
                                  onClick={() => {
                                    const currentConfig = step.config?.libraryReMatchConfig || {};
                                    const aliasSourceMappings = [...(currentConfig.aliasSourceMappings || [])];
                                    aliasSourceMappings.splice(aliasIdx, 1);
                                    handleConfigChange('libraryReMatchConfig.aliasSourceMappings', aliasSourceMappings);
                                  }}
                                  sx={{ mt: 0.5 }}
                                >
                                  Remove
                                </Button>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                        
                        {(!step.config?.libraryReMatchConfig?.aliasSourceMappings || step.config?.libraryReMatchConfig?.aliasSourceMappings.length === 0) && (
                          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 1 }}>
                            <Typography variant="caption">
                              Add source mappings to return alias values from specific sources.
                              This allows you to populate multiple fields with different alias values when users correct a match.
                            </Typography>
                          </Alert>
                        )}
                      </Box>

                      {/* Preview of Library Re-match Configuration */}
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                          📚 Library Re-match Configuration Preview:
                        </Typography>
                        <Typography variant="body2" color="#1976d2" sx={{ fontSize: '0.875rem' }}>
                          • Target Field: {step.config?.libraryReMatchConfig?.targetField || 'Not set'}<br/>
                          • Source Field (for learning): {step.config?.libraryReMatchConfig?.sourceField || 'Not set'}<br/>
                          • Library: {(() => {
                            const lib = availableLibraries?.find(l => l.libraryId === step.config?.libraryReMatchConfig?.libraryId);
                            return lib ? lib.name : 'Not selected';
                          })()}<br/>
                          • Original Backup: {step.config?.libraryReMatchConfig?.originalPrefix || 'original_'}{step.config?.libraryReMatchConfig?.targetField || '[field]'}<br/>
                          • Corrected Field: {step.config?.libraryReMatchConfig?.targetField || '[field]'} (updated in place)<br/>
                          • Button Label: "{step.config?.libraryReMatchConfig?.buttonLabel || 'Correct Match'}"<br/>
                          • Prioritized Sources: {(() => {
                            const sources = step.config?.libraryReMatchConfig?.prioritizedSources || [];
                            return sources.length > 0 ? sources.join(', ') : 'None (show all)';
                          })()}<br/>
                          • Alias Source Mappings: {(() => {
                            const mappings = step.config?.libraryReMatchConfig?.aliasSourceMappings || [];
                            if (mappings.length === 0) return 'None';
                            return mappings.map(m => `${m.sourceToMatch} → ${m.targetField}`).join(', ');
                          })()}
                        </Typography>
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="caption">
                            <strong>How it works:</strong> When a user corrects a value, the original value is backed up to "{step.config?.libraryReMatchConfig?.originalPrefix || 'original_'}{step.config?.libraryReMatchConfig?.targetField || 'field'}" and the main field "{step.config?.libraryReMatchConfig?.targetField || 'field'}" is updated with the correction. The correction is learned under the source field "{step.config?.libraryReMatchConfig?.sourceField || 'field'}" name.
                            {step.config?.libraryReMatchConfig?.prioritizedSources?.length > 0 && (
                              <><br/><br/><strong>Display Priority:</strong> Aliases from prioritized sources will be shown first in the order configured, up to 5 total aliases.</>
                            )}
                            {step.config?.libraryReMatchConfig?.aliasSourceMappings?.length > 0 && (
                              <><br/><br/><strong>Alias Source Mappings:</strong> When a user selects a library entry, additional fields will be populated with alias values from the specified sources. For example, if you map "itemId" → "itemId" field, the alias from the "itemId" source will be populated into the "itemId" field in your table.</>
                            )}
                          </Typography>
                        </Alert>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Table Builder Configuration */}
      {step.config?.interfaceType === 'table_builder' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">🔨 Table Builder Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Build a table from scratch by selecting items from a source array. 
                Users add rows and pick items from a dropdown; other columns auto-populate from the selected item.
              </Typography>
              
              <Divider sx={{ my: 1 }} />

              {/* Source Array Variable */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  📦 Source Array (items to select from)
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Source Array Variable</InputLabel>
                  <Select
                    value={step.config?.sourceArrayVariable || ''}
                    onChange={(e) => handleConfigChange('sourceArrayVariable', e.target.value)}
                  >
                    {availableVariables.map(variable => (
                      <MenuItem key={variable} value={variable}>
                        {variable}
                        {(variable.toLowerCase().includes('items') || 
                          variable.toLowerCase().includes('products') || 
                          variable.toLowerCase().includes('data') ||
                          variable.toLowerCase().includes('list') ||
                          variable.toLowerCase().includes('array')) && 
                          <Chip size="small" label="Array" sx={{ ml: 1, height: 16 }} />
                        }
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    The array variable containing items users can select from (e.g. availableProducts, inventoryItems)
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* Output Variable */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  📤 Output Variable
                </Typography>
                <TextField
                  size="small"
                  label="Output Variable Name"
                  value={step.config?.outputVariable || ''}
                  onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
                  fullWidth
                  placeholder="e.g. invoiceItems, selectedProducts"
                  helperText="Variable name where the built table data will be saved"
                />
              </Box>

              {/* Options */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  ⚙️ Options
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      label="Minimum Rows"
                      type="number"
                      value={step.config?.minRows ?? ''}
                      onChange={(e) => handleConfigChange('minRows', e.target.value ? parseInt(e.target.value) : undefined)}
                      fullWidth
                      placeholder="e.g. 1"
                      helperText="Minimum rows required before submit"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size="small"
                      label="Maximum Rows"
                      type="number"
                      value={step.config?.maxRows ?? ''}
                      onChange={(e) => handleConfigChange('maxRows', e.target.value ? parseInt(e.target.value) : undefined)}
                      fullWidth
                      placeholder="e.g. 50"
                      helperText="Maximum rows allowed"
                    />
                  </Grid>
                </Grid>
                <FormControlLabel
                  control={
                    <Switch
                      checked={step.config?.allowDuplicateSelections !== undefined ? step.config.allowDuplicateSelections : true}
                      onChange={(e) => handleConfigChange('allowDuplicateSelections', e.target.checked)}
                    />
                  }
                  label="Allow selecting the same item in multiple rows"
                  sx={{ mt: 1 }}
                />
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Optional Header Fields - mirrors table_with_header header config */}
              <Accordion variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📝 Header Fields (optional, shown above table)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2" color="textSecondary">
                        Context fields displayed before the table (e.g., Customer Name, Order Date)
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const currentFields = step.config?.headerFields || [];
                          const newField = {
                            id: `header-${Date.now()}`,
                            name: '',
                            label: '',
                            type: 'text',
                            required: true,
                            readOnly: false,
                            placeholder: '',
                            helperText: '',
                            outputVariable: '',
                            defaultValue: '',
                            width: 'full'
                          };
                          handleConfigChange('headerFields', [...currentFields, newField]);
                        }}
                      >
                        Add Header Field
                      </Button>
                    </Box>
                    
                    <DndContext 
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleHeaderFieldsDragEnd}
                    >
                      <SortableContext 
                        items={step.config?.headerFields || []}
                        strategy={verticalListSortingStrategy}
                      >
                        {(step.config?.headerFields || []).map((field, index) => (
                          <SortableHeaderFieldCard
                            key={field.id}
                            field={field}
                            index={index}
                            onFieldDelete={(index) => {
                              const updatedFields = step.config.headerFields.filter((_, i) => i !== index);
                              handleConfigChange('headerFields', updatedFields);
                            }}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Field Name"
                                  value={field.name}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, name: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., purchaseOrder"
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Field Label"
                                  value={field.label}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, label: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., Purchase Order Number"
                                />
                              </Grid>
                              
                              <Grid item xs={4}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Field Type</InputLabel>
                                  <Select
                                    value={field.type}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.headerFields];
                                      updatedFields[index] = { ...field, type: e.target.value };
                                      handleConfigChange('headerFields', updatedFields);
                                    }}
                                    label="Field Type"
                                  >
                                    <MenuItem value="text">Text</MenuItem>
                                    <MenuItem value="number">Number</MenuItem>
                                    <MenuItem value="email">Email</MenuItem>
                                    <MenuItem value="date">Date</MenuItem>
                                    <MenuItem value="time">Time</MenuItem>
                                    <MenuItem value="textarea">Text Area</MenuItem>
                                    <MenuItem value="select">Dropdown</MenuItem>
                                    <MenuItem value="calculated">🧮 Calculated</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={4}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Width</InputLabel>
                                  <Select
                                    value={field.width || 'full'}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.headerFields];
                                      updatedFields[index] = { ...field, width: e.target.value };
                                      handleConfigChange('headerFields', updatedFields);
                                    }}
                                    label="Width"
                                  >
                                    <MenuItem value="full">Full Width</MenuItem>
                                    <MenuItem value="half">Half Width</MenuItem>
                                    <MenuItem value="third">One Third</MenuItem>
                                    <MenuItem value="quarter">One Quarter</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={4}>
                                <Box display="flex" flexDirection="column" gap={1}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={field.required || false}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { ...field, required: e.target.checked };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                      />
                                    }
                                    label="Required"
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={field.readOnly || false}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { ...field, readOnly: e.target.checked };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                      />
                                    }
                                    label="Read Only"
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={field.clearButton || false}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { ...field, clearButton: e.target.checked };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                      />
                                    }
                                    label="Clear Button"
                                  />
                                </Box>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Placeholder"
                                  value={field.placeholder}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, placeholder: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., Enter PO number..."
                                />
                              </Grid>
                              
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Default Value"
                                  value={field.defaultValue}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, defaultValue: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., {{extractedPurchaseOrder}}"
                                  helperText="Can use variables like {{extractedPurchaseOrder}}"
                                />
                              </Grid>
                              
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Output Variable"
                                  value={field.outputVariable}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, outputVariable: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., purchaseOrder"
                                  helperText="Variable name to store the value"
                                />
                              </Grid>
                              
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  label="Helper Text"
                                  value={field.helperText}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.headerFields];
                                    updatedFields[index] = { ...field, helperText: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="Additional help text for the user"
                                />
                              </Grid>
                              
                              {/* Options for select fields */}
                              {field.type === 'select' && (
                                <>
                                  <Grid item xs={12}>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Options Mode</InputLabel>
                                      <Select
                                        value={field.optionsMode || 'simple'}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { ...field, optionsMode: e.target.value };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                        label="Options Mode"
                                      >
                                        <MenuItem value="simple">Simple (Comma-separated)</MenuItem>
                                        <MenuItem value="advanced">Advanced (Name-Value Pairs)</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  
                                  {(!field.optionsMode || field.optionsMode === 'simple') && (
                                    <Grid item xs={12}>
                                      <TextField
                                        size="small"
                                        label="Options (comma-separated)"
                                        value={field.options || ''}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { ...field, options: e.target.value };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                        fullWidth
                                        placeholder="Option 1, Option 2, Option 3"
                                        helperText="Comma-separated list of dropdown options"
                                      />
                                    </Grid>
                                  )}
                                  
                                  {field.optionsMode === 'advanced' && (
                                    <>
                                      <Grid item xs={12}>
                                        <FormControl size="small" fullWidth>
                                          <InputLabel>Data Source</InputLabel>
                                          <Select
                                            value={field.optionsDataSource || 'manual'}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.headerFields];
                                              updatedFields[index] = { ...field, optionsDataSource: e.target.value };
                                              handleConfigChange('headerFields', updatedFields);
                                            }}
                                            label="Data Source"
                                          >
                                            <MenuItem value="manual">Manual Entry</MenuItem>
                                            <MenuItem value="variable">From Variable</MenuItem>
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                      
                                      {(!field.optionsDataSource || field.optionsDataSource === 'manual') && (
                                        <Grid item xs={12}>
                                          <Box>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                              <Typography variant="body2" fontWeight="medium">
                                                Dropdown Options (Name-Value Pairs)
                                              </Typography>
                                              <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => {
                                                  const updatedFields = [...step.config.headerFields];
                                                  const advancedOptions = field.advancedOptions || [];
                                                  updatedFields[index] = { 
                                                    ...field, 
                                                    advancedOptions: [...advancedOptions, { name: '', value: '' }] 
                                                  };
                                                  handleConfigChange('headerFields', updatedFields);
                                                }}
                                              >
                                                Add Option
                                              </Button>
                                            </Box>
                                            
                                            {(!field.advancedOptions || field.advancedOptions.length === 0) && (
                                              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', py: 2 }}>
                                                No options added yet. Click "Add Option" to create name-value pairs.
                                              </Typography>
                                            )}
                                            
                                            {(field.advancedOptions || []).map((option, optIndex) => (
                                              <Box key={optIndex} display="flex" gap={1} alignItems="center" mb={1}>
                                                <TextField
                                                  size="small"
                                                  label="Display Name"
                                                  value={option.name || ''}
                                                  onChange={(e) => {
                                                    const updatedFields = [...step.config.headerFields];
                                                    const advancedOptions = [...(field.advancedOptions || [])];
                                                    advancedOptions[optIndex] = { ...advancedOptions[optIndex], name: e.target.value };
                                                    updatedFields[index] = { ...field, advancedOptions };
                                                    handleConfigChange('headerFields', updatedFields);
                                                  }}
                                                  placeholder="e.g., Apple"
                                                  sx={{ flex: 1 }}
                                                />
                                                <TextField
                                                  size="small"
                                                  label="Value"
                                                  value={option.value || ''}
                                                  onChange={(e) => {
                                                    const updatedFields = [...step.config.headerFields];
                                                    const advancedOptions = [...(field.advancedOptions || [])];
                                                    advancedOptions[optIndex] = { ...advancedOptions[optIndex], value: e.target.value };
                                                    updatedFields[index] = { ...field, advancedOptions };
                                                    handleConfigChange('headerFields', updatedFields);
                                                  }}
                                                  placeholder="e.g., trx52"
                                                  sx={{ flex: 1 }}
                                                />
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  onClick={() => {
                                                    const updatedFields = [...step.config.headerFields];
                                                    const advancedOptions = [...(field.advancedOptions || [])];
                                                    advancedOptions.splice(optIndex, 1);
                                                    updatedFields[index] = { ...field, advancedOptions };
                                                    handleConfigChange('headerFields', updatedFields);
                                                  }}
                                                >
                                                  <DeleteIcon />
                                                </IconButton>
                                              </Box>
                                            ))}
                                          </Box>
                                        </Grid>
                                      )}
                                      
                                      {field.optionsDataSource === 'variable' && (
                                        <>
                                          <Grid item xs={12}>
                                            <TextField
                                              size="small"
                                              label="Variable Name"
                                              value={field.optionsVariable || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.headerFields];
                                                updatedFields[index] = { ...field, optionsVariable: e.target.value };
                                                handleConfigChange('headerFields', updatedFields);
                                              }}
                                              fullWidth
                                              placeholder="e.g., products"
                                              helperText="Variable containing array of objects"
                                            />
                                          </Grid>
                                          <Grid item xs={6}>
                                            <TextField
                                              size="small"
                                              label="Display Name Field"
                                              value={field.optionsNameField || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.headerFields];
                                                updatedFields[index] = { ...field, optionsNameField: e.target.value };
                                                handleConfigChange('headerFields', updatedFields);
                                              }}
                                              fullWidth
                                              placeholder="e.g., [*].name"
                                              helperText="Field path for display name"
                                            />
                                          </Grid>
                                          <Grid item xs={6}>
                                            <TextField
                                              size="small"
                                              label="Value Field"
                                              value={field.optionsValueField || ''}
                                              onChange={(e) => {
                                                const updatedFields = [...step.config.headerFields];
                                                updatedFields[index] = { ...field, optionsValueField: e.target.value };
                                                handleConfigChange('headerFields', updatedFields);
                                              }}
                                              fullWidth
                                              placeholder="e.g., [*].value"
                                              helperText="Field path for stored value"
                                            />
                                          </Grid>
                                          <Grid item xs={12}>
                                            <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                              <Typography variant="caption" color="info.dark">
                                                <strong>Example:</strong> If you have a variable "products" with value:<br/>
                                                [{`{name:"Apple", value:"trx52"}, {name:"Orange", value:"trx55"}`}]<br/>
                                                Set Variable Name = "products", Display Name Field = "[*].name", Value Field = "[*].value"
                                              </Typography>
                                            </Box>
                                          </Grid>
                                        </>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                              
                              {/* Calculated Field Configuration */}
                              {field.type === 'calculated' && (
                                <>
                                  <Grid item xs={12}>
                                    <Box sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: 1, border: '1px solid #90caf9' }}>
                                      <Typography variant="body2" fontWeight="600" color="primary" gutterBottom>
                                        🧮 Calculated Field Settings
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        Automatically calculates values from a table column and updates in real-time when data changes
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  
                                  <Grid item xs={6}>
                                    <TextField
                                      size="small"
                                      label="Column Key to Calculate"
                                      value={field.calculationColumn || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { ...field, calculationColumn: e.target.value };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="e.g., quantity"
                                      helperText="The column key from the table to calculate"
                                    />
                                  </Grid>
                                  
                                  <Grid item xs={6}>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Calculation Operation</InputLabel>
                                      <Select
                                        value={field.calculationOperation || 'sum'}
                                        onChange={(e) => {
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { ...field, calculationOperation: e.target.value };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                        label="Calculation Operation"
                                      >
                                        <MenuItem value="sum">Sum - Add all values</MenuItem>
                                        <MenuItem value="average">Average - Calculate mean</MenuItem>
                                        <MenuItem value="min">Minimum - Find lowest value</MenuItem>
                                        <MenuItem value="max">Maximum - Find highest value</MenuItem>
                                        <MenuItem value="count">Count - Count non-null values</MenuItem>
                                        <MenuItem value="multiply">Multiply - Multiply all values</MenuItem>
                                      </Select>
                                      <FormHelperText>Operation to perform on column values</FormHelperText>
                                    </FormControl>
                                  </Grid>
                                  
                                  <Grid item xs={6}>
                                    <TextField
                                      size="small"
                                      type="number"
                                      label="Decimal Places"
                                      value={field.decimalPlaces !== undefined ? field.decimalPlaces : 2}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { ...field, decimalPlaces: parseInt(e.target.value) || 0 };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                      fullWidth
                                      inputProps={{ min: 0, max: 10 }}
                                      helperText="Number of decimal places to display"
                                    />
                                  </Grid>
                                  
                                  <Grid item xs={6}>
                                    <TextField
                                      size="small"
                                      label="Prefix Text"
                                      value={field.calculationPrefix || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { ...field, calculationPrefix: e.target.value };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="e.g., $, Total: "
                                      helperText="Optional text to show before the value"
                                    />
                                  </Grid>
                                  
                                  <Grid item xs={6}>
                                    <TextField
                                      size="small"
                                      label="Thousands Separator"
                                      value={field.thousandsSeparator !== undefined ? field.thousandsSeparator : ','}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { ...field, thousandsSeparator: e.target.value };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder=","
                                      helperText="Separator for thousands (e.g., 1,000)"
                                      inputProps={{ maxLength: 1 }}
                                    />
                                  </Grid>
                                  
                                  <Grid item xs={6}>
                                    <TextField
                                      size="small"
                                      label="Decimal Separator"
                                      value={field.decimalSeparator !== undefined ? field.decimalSeparator : '.'}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { ...field, decimalSeparator: e.target.value };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="."
                                      helperText="Separator for decimals (e.g., 10.50)"
                                      inputProps={{ maxLength: 1 }}
                                    />
                                  </Grid>
                                  
                                  <Grid item xs={12}>
                                    <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                                      <Typography variant="caption" color="success.dark">
                                        <strong>Example:</strong> If your table has a "quantity" column with values [2000, 7500, 5000], 
                                        and you select "Sum" operation with prefix "$" and 2 decimal places, 
                                        this field will automatically display <strong>"$14,500.00"</strong> and update whenever the table data changes.
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </>
                              )}
                              
                              {/* Character Limit Configuration - for text, textarea, email fields */}
                              {(field.type === 'text' || field.type === 'textarea' || field.type === 'email') && (
                                <Grid item xs={12}>
                                  <Box display="flex" gap={2} alignItems="center">
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          size="small"
                                          checked={field.enableCharacterLimit || false}
                                          onChange={(e) => {
                                            const updatedFields = [...step.config.headerFields];
                                            updatedFields[index] = { 
                                              ...field, 
                                              enableCharacterLimit: e.target.checked,
                                              ...(e.target.checked ? {} : { maxCharacters: undefined })
                                            };
                                            handleConfigChange('headerFields', updatedFields);
                                          }}
                                        />
                                      }
                                      label="Enable Character Limit"
                                    />
                                    
                                    {field.enableCharacterLimit && (
                                      <TextField
                                        size="small"
                                        label="Max Characters"
                                        type="number"
                                        value={field.maxCharacters || ''}
                                        onChange={(e) => {
                                          const value = parseInt(e.target.value) || undefined;
                                          const updatedFields = [...step.config.headerFields];
                                          updatedFields[index] = { ...field, maxCharacters: value };
                                          handleConfigChange('headerFields', updatedFields);
                                        }}
                                        inputProps={{
                                          min: 1,
                                          max: 10000
                                        }}
                                        sx={{ width: 150 }}
                                        placeholder="e.g., 100"
                                        helperText="1-10000 characters"
                                      />
                                    )}
                                  </Box>
                                </Grid>
                              )}

                              {/* Text Transformation Rules - only for text type fields */}
                              {field.type === 'text' && (
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                      Text Transformation Rules
                                    </Typography>
                                    <Box display="flex" flexDirection="column" gap={1.5}>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            size="small"
                                            checked={field.textTransform === 'uppercase'}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.headerFields];
                                              updatedFields[index] = { 
                                                ...field, 
                                                textTransform: e.target.checked ? 'uppercase' : undefined 
                                              };
                                              handleConfigChange('headerFields', updatedFields);
                                            }}
                                          />
                                        }
                                        label="Convert to UPPERCASE"
                                      />
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            size="small"
                                            checked={field.textTransform === 'lowercase'}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.headerFields];
                                              updatedFields[index] = { 
                                                ...field, 
                                                textTransform: e.target.checked ? 'lowercase' : undefined 
                                              };
                                              handleConfigChange('headerFields', updatedFields);
                                            }}
                                          />
                                        }
                                        label="Convert to lowercase"
                                      />
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            size="small"
                                            checked={field.trimWhitespace || false}
                                            onChange={(e) => {
                                              const updatedFields = [...step.config.headerFields];
                                              updatedFields[index] = { 
                                                ...field, 
                                                trimWhitespace: e.target.checked 
                                              };
                                              handleConfigChange('headerFields', updatedFields);
                                            }}
                                          />
                                        }
                                        label="Trim whitespace (remove leading/trailing spaces)"
                                      />
                                    </Box>
                                  </Box>
                                </Grid>
                              )}

                              {/* Date Format Configuration - only for date fields */}
                              {field.type === 'date' && (
                                <Grid item xs={12}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Date Format</InputLabel>
                                    <Select
                                      value={field.dateFormat || 'MM/dd/yyyy'}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { ...field, dateFormat: e.target.value };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                      label="Date Format"
                                    >
                                      <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (12/25/2023)</MenuItem>
                                      <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (25/12/2023)</MenuItem>
                                      <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (2023-12-25)</MenuItem>
                                      <MenuItem value="dd.MM.yyyy">dd.MM.yyyy (25.12.2023)</MenuItem>
                                      <MenuItem value="MM-dd-yyyy">MM-dd-yyyy (12-25-2023)</MenuItem>
                                      <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (25-12-2023)</MenuItem>
                                      <MenuItem value="MMM dd, yyyy">MMM dd, yyyy (Dec 25, 2023)</MenuItem>
                                      <MenuItem value="MMMM dd, yyyy">MMMM dd, yyyy (December 25, 2023)</MenuItem>
                                      <MenuItem value="dd MMM yyyy">dd MMM yyyy (25 Dec 2023)</MenuItem>
                                      <MenuItem value="dd MMMM yyyy">dd MMMM yyyy (25 December 2023)</MenuItem>
                                      <MenuItem value="custom">Custom...</MenuItem>
                                    </Select>
                                  </FormControl>
                                  {field.dateFormat === 'custom' && (
                                    <TextField
                                      size="small"
                                      label="Custom Date Format"
                                      value={field.customDateFormat || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { 
                                          ...field, 
                                          customDateFormat: e.target.value,
                                          dateFormat: 'custom'
                                        };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="e.g., DD/MM/YYYY, dd.mm.yyyy, MM-dd-yy"
                                      helperText="Enter custom date format (supports DD, MM, YYYY, dd, mm, yyyy)"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                </Grid>
                              )}

                              {/* Time Format Configuration - only for time fields */}
                              {field.type === 'time' && (
                                <Grid item xs={12}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Time Format</InputLabel>
                                    <Select
                                      value={field.timeFormat || 'HH:mm:ss'}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { ...field, timeFormat: e.target.value };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                      label="Time Format"
                                    >
                                      <MenuItem value="HH:mm:ss">24-hour with seconds (14:30:44)</MenuItem>
                                      <MenuItem value="HH:mm">24-hour without seconds (14:30)</MenuItem>
                                      <MenuItem value="hh:mm:ss A">12-hour with seconds (02:30:44 PM)</MenuItem>
                                      <MenuItem value="hh:mm A">12-hour without seconds (02:30 PM)</MenuItem>
                                      <MenuItem value="h:mm A">12-hour no leading zero (2:30 PM)</MenuItem>
                                      <MenuItem value="h:mm:ss A">12-hour no leading zero with seconds (2:30:44 PM)</MenuItem>
                                      <MenuItem value="custom">Custom...</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                      Choose how to display time values
                                    </FormHelperText>
                                  </FormControl>
                                  {field.timeFormat === 'custom' && (
                                    <TextField
                                      size="small"
                                      label="Custom Time Format"
                                      value={field.customTimeFormat || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { 
                                          ...field, 
                                          customTimeFormat: e.target.value,
                                          timeFormat: 'custom'
                                        };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="e.g., HH:mm, h:mm A, HH.mm.ss"
                                      helperText="Custom time format (HH=24hr, hh=12hr, mm=minutes, ss=seconds, A=AM/PM)"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                  
                                  <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                                    <InputLabel>Timezone</InputLabel>
                                    <Select
                                      value={field.timezone || 'UTC'}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.headerFields];
                                        updatedFields[index] = { ...field, timezone: e.target.value };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                      label="Timezone"
                                    >
                                      <MenuItem value="UTC">UTC (Server Time)</MenuItem>
                                      <MenuItem value="UTC+1">UTC+1 (Central European Time)</MenuItem>
                                      <MenuItem value="UTC+2">UTC+2 (Eastern European Time)</MenuItem>
                                      <MenuItem value="UTC+3">UTC+3 (Moscow Time)</MenuItem>
                                      <MenuItem value="UTC+5">UTC+5 (Pakistan Standard Time)</MenuItem>
                                      <MenuItem value="UTC+8">UTC+8 (China Standard Time)</MenuItem>
                                      <MenuItem value="UTC+9">UTC+9 (Japan Standard Time)</MenuItem>
                                      <MenuItem value="UTC+10">UTC+10 (Australian Eastern Time)</MenuItem>
                                      <MenuItem value="UTC-5">UTC-5 (Eastern Standard Time)</MenuItem>
                                      <MenuItem value="UTC-6">UTC-6 (Central Standard Time)</MenuItem>
                                      <MenuItem value="UTC-7">UTC-7 (Mountain Standard Time)</MenuItem>
                                      <MenuItem value="UTC-8">UTC-8 (Pacific Standard Time)</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                      Select the timezone for time display
                                    </FormHelperText>
                                  </FormControl>
                                </Grid>
                              )}
                            </Grid>
                          </SortableHeaderFieldCard>
                        ))}
                      </SortableContext>
                    </DndContext>
                    
                    {(!step.config?.headerFields || step.config.headerFields.length === 0) && (
                      <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="body2">
                          No header fields defined. Click "Add Header Field" to add context fields.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ my: 1 }} />

              {/* Table Columns Configuration */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  📊 Table Columns
                </Typography>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Define columns for the table. Mark one column as the "Lookup Column" to trigger auto-population.
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentColumns = step.config?.tableColumns || [];
                      const newColumn = {
                        id: `col-${Date.now()}`,
                        key: '',
                        label: '',
                        type: 'text',
                        isLookupColumn: false,
                        editable: true
                      };
                      handleConfigChange('tableColumns', [...currentColumns, newColumn]);
                    }}
                  >
                    Add Column
                  </Button>
                </Box>

                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={step.config?.tableColumns || []}
                    strategy={verticalListSortingStrategy}
                  >
                    {(step.config?.tableColumns || []).map((column, index) => (
                      <SortableColumnCard
                        key={column.id}
                        column={column}
                        index={index}
                        onColumnChange={(index, updatedColumn) => {
                          const updatedColumns = [...step.config.tableColumns];
                          updatedColumns[index] = updatedColumn;
                          handleConfigChange('tableColumns', updatedColumns);
                        }}
                        onColumnDelete={(index) => {
                          const updatedColumns = step.config.tableColumns.filter((_, i) => i !== index);
                          handleConfigChange('tableColumns', updatedColumns);
                        }}
                      >
                        <Box flexGrow={1} sx={column.isLookupColumn ? { 
                          borderLeft: '3px solid #1976d2',
                          pl: 1.5,
                          ml: -1
                        } : undefined}>
                          {column.isLookupColumn && (
                            <Chip size="small" label="LOOKUP" color="primary" sx={{ mb: 1 }} />
                          )}
                          <Grid container spacing={2}>
                            <Grid item xs={3}>
                              <TextField
                                size="small"
                                label="Column Key"
                                value={column.key || ''}
                                onChange={(e) => {
                                  const updatedColumns = [...step.config.tableColumns];
                                  updatedColumns[index] = { ...column, key: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                fullWidth
                                placeholder="e.g. productId"
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                size="small"
                                label="Column Label"
                                value={column.label || ''}
                                onChange={(e) => {
                                  const updatedColumns = [...step.config.tableColumns];
                                  updatedColumns[index] = { ...column, label: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                fullWidth
                                placeholder="e.g. Product"
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                  value={column.type || 'text'}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, type: e.target.value };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  label="Type"
                                >
                                  <MenuItem value="text">Text</MenuItem>
                                  <MenuItem value="number">Number</MenuItem>
                                  <MenuItem value="currency">Currency</MenuItem>
                                  <MenuItem value="date">Date</MenuItem>
                                  <MenuItem value="dropdown">Dropdown</MenuItem>
                                  <MenuItem value="computed">Computed</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                size="small"
                                label="Width"
                                value={column.width || 'auto'}
                                onChange={(e) => {
                                  const updatedColumns = [...step.config.tableColumns];
                                  updatedColumns[index] = { ...column, width: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                fullWidth
                                placeholder="auto, 100px, 20%"
                                helperText="Column width"
                              />
                            </Grid>

                            {/* Lookup Column Toggle */}
                            <Grid item xs={4}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={column.isLookupColumn || false}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      if (e.target.checked) {
                                        updatedColumns.forEach((col, i) => {
                                          updatedColumns[i] = { ...col, isLookupColumn: i === index };
                                        });
                                        updatedColumns[index] = { 
                                          ...updatedColumns[index], 
                                          type: 'dropdown',
                                          isLookupColumn: true,
                                          optionsDataSource: 'variable',
                                          optionsVariable: step.config?.sourceArrayVariable || ''
                                        };
                                      } else {
                                        updatedColumns[index] = { ...column, isLookupColumn: false };
                                      }
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    size="small"
                                  />
                                }
                                label="Lookup Column"
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={column.editable !== false}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, editable: e.target.checked };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    size="small"
                                    disabled={column.type === 'computed'}
                                  />
                                }
                                label="Editable"
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={column.required || false}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, required: e.target.checked };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    size="small"
                                  />
                                }
                                label="Required"
                              />
                            </Grid>

                            {/* Conditional Validation for Number columns */}
                            {column.type === 'number' && (
                              <>
                                <Grid item xs={4}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        size="small"
                                        checked={column.conditionalValidation || false}
                                        onChange={(e) => {
                                          const updatedColumns = [...step.config.tableColumns];
                                          updatedColumns[index] = { 
                                            ...column, 
                                            conditionalValidation: e.target.checked,
                                            ...(e.target.checked ? {
                                              validationConditions: column.validationConditions?.length 
                                                ? column.validationConditions 
                                                : [{ id: `vc-${Date.now()}`, operator: 'gt', valueType: 'static', value: 0 }]
                                            } : {
                                              validationConditions: undefined,
                                              validationOperator: undefined,
                                              validationValue: undefined,
                                              validationValueType: undefined,
                                              validationVariable: undefined
                                            })
                                          };
                                          handleConfigChange('tableColumns', updatedColumns);
                                        }}
                                      />
                                    }
                                    label="Conditional Validation"
                                  />
                                </Grid>
                                
                                {column.conditionalValidation && (
                                  <Grid item xs={12}>
                                    <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          Validation Conditions ({(column.validationConditions || []).length})
                                        </Typography>
                                        <Button
                                          size="small"
                                          startIcon={<AddIcon />}
                                          onClick={() => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            const conditions = [...(column.validationConditions || []), { id: `vc-${Date.now()}`, operator: 'gt', valueType: 'static', value: 0 }];
                                            updatedColumns[index] = { ...column, validationConditions: conditions };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                        >
                                          Add Condition
                                        </Button>
                                      </Box>

                                      {(column.validationConditions || []).map((condition, cIdx) => (
                                        <Box key={condition.id || cIdx} sx={{ mb: 1.5, p: 1.5, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="caption" color="textSecondary">
                                              Condition {cIdx + 1}
                                            </Typography>
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => {
                                                const updatedColumns = [...step.config.tableColumns];
                                                const conditions = (column.validationConditions || []).filter((_, i) => i !== cIdx);
                                                updatedColumns[index] = { ...column, validationConditions: conditions };
                                                handleConfigChange('tableColumns', updatedColumns);
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Box>
                                          <Grid container spacing={1}>
                                            <Grid item xs={3}>
                                              <FormControl size="small" fullWidth>
                                                <InputLabel>Operator</InputLabel>
                                                <Select
                                                  value={condition.operator || 'gt'}
                                                  onChange={(e) => {
                                                    const updatedColumns = [...step.config.tableColumns];
                                                    const conditions = [...(column.validationConditions || [])];
                                                    conditions[cIdx] = { ...condition, operator: e.target.value };
                                                    updatedColumns[index] = { ...column, validationConditions: conditions };
                                                    handleConfigChange('tableColumns', updatedColumns);
                                                  }}
                                                  label="Operator"
                                                >
                                                  <MenuItem value="gt">Greater than (&gt;)</MenuItem>
                                                  <MenuItem value="gte">Greater or equal (≥)</MenuItem>
                                                  <MenuItem value="lt">Less than (&lt;)</MenuItem>
                                                  <MenuItem value="lte">Less or equal (≤)</MenuItem>
                                                  <MenuItem value="eq">Equal to (=)</MenuItem>
                                                  <MenuItem value="ne">Not equal (≠)</MenuItem>
                                                </Select>
                                              </FormControl>
                                            </Grid>
                                            <Grid item xs={3}>
                                              <FormControl size="small" fullWidth>
                                                <InputLabel>Compare To</InputLabel>
                                                <Select
                                                  value={condition.valueType || 'static'}
                                                  onChange={(e) => {
                                                    const updatedColumns = [...step.config.tableColumns];
                                                    const conditions = [...(column.validationConditions || [])];
                                                    conditions[cIdx] = { 
                                                      ...condition, 
                                                      valueType: e.target.value,
                                                      value: e.target.value === 'static' ? 0 : undefined,
                                                      variable: e.target.value === 'variable' ? '' : undefined,
                                                      columnKey: e.target.value === 'column' ? '' : undefined
                                                    };
                                                    updatedColumns[index] = { ...column, validationConditions: conditions };
                                                    handleConfigChange('tableColumns', updatedColumns);
                                                  }}
                                                  label="Compare To"
                                                >
                                                  <MenuItem value="static">Static Value</MenuItem>
                                                  <MenuItem value="variable">Variable</MenuItem>
                                                  <MenuItem value="column">Column (Same Row)</MenuItem>
                                                </Select>
                                              </FormControl>
                                            </Grid>
                                            <Grid item xs={6}>
                                              {(condition.valueType || 'static') === 'static' && (
                                                <TextField
                                                  size="small"
                                                  label="Value"
                                                  type="number"
                                                  value={condition.value !== undefined ? condition.value : ''}
                                                  onChange={(e) => {
                                                    const updatedColumns = [...step.config.tableColumns];
                                                    const conditions = [...(column.validationConditions || [])];
                                                    const numValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                    conditions[cIdx] = { ...condition, value: isNaN(numValue) ? 0 : numValue };
                                                    updatedColumns[index] = { ...column, validationConditions: conditions };
                                                    handleConfigChange('tableColumns', updatedColumns);
                                                  }}
                                                  fullWidth
                                                  placeholder="Enter comparison value"
                                                />
                                              )}
                                              {condition.valueType === 'variable' && (
                                                <FormControl size="small" fullWidth>
                                                  <InputLabel>Variable</InputLabel>
                                                  <Select
                                                    value={condition.variable || ''}
                                                    onChange={(e) => {
                                                      const updatedColumns = [...step.config.tableColumns];
                                                      const conditions = [...(column.validationConditions || [])];
                                                      conditions[cIdx] = { ...condition, variable: e.target.value };
                                                      updatedColumns[index] = { ...column, validationConditions: conditions };
                                                      handleConfigChange('tableColumns', updatedColumns);
                                                    }}
                                                    label="Variable"
                                                  >
                                                    {(availableVariables || []).map(v => (
                                                      <MenuItem key={v} value={v}>{v}</MenuItem>
                                                    ))}
                                                  </Select>
                                                </FormControl>
                                              )}
                                              {condition.valueType === 'column' && (
                                                <FormControl size="small" fullWidth>
                                                  <InputLabel>Column (Same Row)</InputLabel>
                                                  <Select
                                                    value={condition.columnKey || ''}
                                                    onChange={(e) => {
                                                      const updatedColumns = [...step.config.tableColumns];
                                                      const conditions = [...(column.validationConditions || [])];
                                                      conditions[cIdx] = { ...condition, columnKey: e.target.value };
                                                      updatedColumns[index] = { ...column, validationConditions: conditions };
                                                      handleConfigChange('tableColumns', updatedColumns);
                                                    }}
                                                    label="Column (Same Row)"
                                                  >
                                                    {(step.config?.tableColumns || [])
                                                      .filter((col) => col.key !== column.key && (col.type === 'number' || col.type === 'currency' || col.type === 'computed'))
                                                      .map(col => (
                                                        <MenuItem key={col.key} value={col.key}>{col.label || col.key}</MenuItem>
                                                      ))
                                                    }
                                                  </Select>
                                                </FormControl>
                                              )}
                                            </Grid>
                                          </Grid>
                                        </Box>
                                      ))}

                                      {(!column.validationConditions || column.validationConditions.length === 0) && (
                                        <Typography variant="caption" color="textSecondary">
                                          No conditions configured. Add conditions to validate this column's values.
                                        </Typography>
                                      )}
                                    </Box>
                                  </Grid>
                                )}
                              </>
                            )}

                            {/* Lookup Column - Dropdown Config */}
                            {column.isLookupColumn && (
                              <>
                                <Grid item xs={12}>
                                  <Alert severity="info" sx={{ mb: 1 }}>
                                    This column triggers auto-population. When a user selects an item, the cascade fields below will auto-fill other columns.
                                  </Alert>
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField
                                    size="small"
                                    label="Display Name Field"
                                    value={column.optionsNameField || ''}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, optionsNameField: e.target.value };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    fullWidth
                                    placeholder="[*].name"
                                    helperText="Field path for dropdown display text"
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField
                                    size="small"
                                    label="Value Field"
                                    value={column.optionsValueField || ''}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, optionsValueField: e.target.value };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    fullWidth
                                    placeholder="[*].id"
                                    helperText="Field path for the stored value"
                                  />
                                </Grid>

                                {/* Cascade Fields */}
                                <Grid item xs={12}>
                                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Cascade Fields (auto-populate other columns)
                                      </Typography>
                                      <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                          const updatedColumns = [...step.config.tableColumns];
                                          const cascadeFields = [...(column.cascadeFields || []), { sourceField: '', targetColumn: '' }];
                                          updatedColumns[index] = { ...column, cascadeFields };
                                          handleConfigChange('tableColumns', updatedColumns);
                                        }}
                                      >
                                        Add Mapping
                                      </Button>
                                    </Box>

                                    {(column.cascadeFields || []).map((cascade, cIdx) => (
                                      <Box key={cIdx} display="flex" gap={1} alignItems="center" mb={1}>
                                        <TextField
                                          size="small"
                                          label="Source Field (from array item)"
                                          value={cascade.sourceField || ''}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            const cascadeFields = [...(column.cascadeFields || [])];
                                            cascadeFields[cIdx] = { ...cascade, sourceField: e.target.value };
                                            updatedColumns[index] = { ...column, cascadeFields };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                          placeholder="e.g. price"
                                          sx={{ flex: 1 }}
                                        />
                                        <FormControl size="small" sx={{ flex: 1 }}>
                                          <InputLabel>Target Column</InputLabel>
                                          <Select
                                            value={cascade.targetColumn || ''}
                                            onChange={(e) => {
                                              const updatedColumns = [...step.config.tableColumns];
                                              const cascadeFields = [...(column.cascadeFields || [])];
                                              cascadeFields[cIdx] = { ...cascade, targetColumn: e.target.value };
                                              updatedColumns[index] = { ...column, cascadeFields };
                                              handleConfigChange('tableColumns', updatedColumns);
                                            }}
                                            label="Target Column"
                                          >
                                            {(step.config?.tableColumns || [])
                                              .filter((col, i) => i !== index)
                                              .map(col => (
                                                <MenuItem key={col.key} value={col.key}>{col.label || col.key}</MenuItem>
                                              ))
                                            }
                                          </Select>
                                        </FormControl>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            const cascadeFields = (column.cascadeFields || []).filter((_, i) => i !== cIdx);
                                            updatedColumns[index] = { ...column, cascadeFields };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    ))}

                                    {(!column.cascadeFields || column.cascadeFields.length === 0) && (
                                      <Typography variant="caption" color="textSecondary">
                                        No cascade mappings yet. Add mappings to auto-populate other columns when an item is selected.
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              </>
                            )}

                            {/* Computed Column Formula */}
                            {column.type === 'computed' && (
                              <Grid item xs={12}>
                                <TextField
                                  size="small"
                                  label="Formula"
                                  value={column.formula || ''}
                                  onChange={(e) => {
                                    const updatedColumns = [...step.config.tableColumns];
                                    updatedColumns[index] = { ...column, formula: e.target.value };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  fullWidth
                                  placeholder="e.g. unitPrice * quantity"
                                  helperText="Use other column keys in the formula"
                                />
                              </Grid>
                            )}

                            {/* Number, Currency, and Computed Formatting Options */}
                            {(column.type === 'number' || column.type === 'currency' || column.type === 'computed') && (
                              <>
                                <Grid item xs={6}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Thousands Separator</InputLabel>
                                    <Select
                                      value={column.thousandsSeparator || ','}
                                      onChange={(e) => {
                                        const updatedColumns = [...step.config.tableColumns];
                                        updatedColumns[index] = { ...column, thousandsSeparator: e.target.value };
                                        handleConfigChange('tableColumns', updatedColumns);
                                      }}
                                      label="Thousands Separator"
                                    >
                                      <MenuItem value=",">Comma (,) - e.g., 1,000</MenuItem>
                                      <MenuItem value=".">Period (.) - e.g., 1.000</MenuItem>
                                      <MenuItem value=" ">Space ( ) - e.g., 1 000</MenuItem>
                                      <MenuItem value="">None - e.g., 1000</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Decimal Separator</InputLabel>
                                    <Select
                                      value={column.decimalSeparator || '.'}
                                      onChange={(e) => {
                                        const updatedColumns = [...step.config.tableColumns];
                                        updatedColumns[index] = { ...column, decimalSeparator: e.target.value };
                                        handleConfigChange('tableColumns', updatedColumns);
                                      }}
                                      label="Decimal Separator"
                                    >
                                      <MenuItem value=".">Period (.) - e.g., 10.50</MenuItem>
                                      <MenuItem value=",">Comma (,) - e.g., 10,50</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                
                                <Grid item xs={column.type === 'currency' ? 6 : 12}>
                                  <TextField
                                    size="small"
                                    label="Decimal Places"
                                    type="number"
                                    value={column.decimalPlaces !== undefined ? column.decimalPlaces : (column.type === 'currency' ? 2 : 0)}
                                    onChange={(e) => {
                                      const updatedColumns = [...step.config.tableColumns];
                                      updatedColumns[index] = { ...column, decimalPlaces: parseInt(e.target.value) || 0 };
                                      handleConfigChange('tableColumns', updatedColumns);
                                    }}
                                    fullWidth
                                    inputProps={{ min: 0, max: 10 }}
                                    helperText="Number of decimal places to display (0-10)"
                                  />
                                </Grid>
                                
                                {column.type === 'currency' && (
                                  <Grid item xs={6}>
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Currency Symbol</InputLabel>
                                      <Select
                                        value={column.currencySymbol || '$'}
                                        onChange={(e) => {
                                          const updatedColumns = [...step.config.tableColumns];
                                          updatedColumns[index] = { ...column, currencySymbol: e.target.value };
                                          handleConfigChange('tableColumns', updatedColumns);
                                        }}
                                        label="Currency Symbol"
                                      >
                                        <MenuItem value="$">Dollar ($)</MenuItem>
                                        <MenuItem value="€">Euro (€)</MenuItem>
                                        <MenuItem value="£">Pound (£)</MenuItem>
                                        <MenuItem value="¥">Yen (¥)</MenuItem>
                                        <MenuItem value="R">Rand (R)</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                )}
                              </>
                            )}

                            {/* Text Transformation Rules */}
                            {column.type === 'text' && (
                              <Grid item xs={12}>
                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                    ✨ Text Transformation Rules
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                    Automatically transform text as users type or submit
                                  </Typography>
                                  
                                  <Box display="flex" flexDirection="column" gap={1.5}>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          size="small"
                                          checked={column.textTransform === 'uppercase'}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { 
                                              ...column, 
                                              textTransform: e.target.checked ? 'uppercase' : undefined 
                                            };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                        />
                                      }
                                      label="Convert to UPPERCASE"
                                    />
                                    
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          size="small"
                                          checked={column.textTransform === 'lowercase'}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { 
                                              ...column, 
                                              textTransform: e.target.checked ? 'lowercase' : undefined 
                                            };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                        />
                                      }
                                      label="Convert to lowercase"
                                    />
                                    
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          size="small"
                                          checked={column.trimWhitespace || false}
                                          onChange={(e) => {
                                            const updatedColumns = [...step.config.tableColumns];
                                            updatedColumns[index] = { 
                                              ...column, 
                                              trimWhitespace: e.target.checked 
                                            };
                                            handleConfigChange('tableColumns', updatedColumns);
                                          }}
                                        />
                                      }
                                      label="Trim whitespace (remove leading/trailing spaces)"
                                    />
                                  </Box>
                                  
                                  {(column.textTransform || column.trimWhitespace) && (
                                    <Box mt={1.5} sx={{ p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                                      <Typography variant="caption" color="info.dark">
                                        <strong>Preview:</strong> "  Sample Text  " → "
                                        {(() => {
                                          let text = '  Sample Text  ';
                                          if (column.textTransform === 'uppercase') text = text.toUpperCase();
                                          if (column.textTransform === 'lowercase') text = text.toLowerCase();
                                          if (column.trimWhitespace) text = text.trim();
                                          return text;
                                        })()}"
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </SortableColumnCard>
                    ))}
                  </SortableContext>
                </DndContext>

                {(!step.config?.tableColumns || step.config.tableColumns.length === 0) && (
                  <Alert severity="info">
                    No columns configured yet. Add columns to define your table structure.
                  </Alert>
                )}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Data Comparison Configuration */}
      {step.config?.interfaceType === 'data_comparison' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">🔍 Data Comparison Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Configure side-by-side data comparison for reconciling extracted data with server data. Perfect for goods received verification.
              </Typography>
              
              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100', mb: 1 }}>
                  🔗 Available Variables from Previous Steps
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#e65100', mb: 1 }}>
                  Variables you can reference in data paths (examples based on common workflow):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip size="small" label="extractedItems" variant="outlined" />
                  <Chip size="small" label="orderItems" variant="outlined" />
                  <Chip size="small" label="productName" variant="outlined" />
                  <Chip size="small" label="sku" variant="outlined" />
                  <Chip size="small" label="purchaseOrder" variant="outlined" />
                  <Chip size="small" label="serverData" variant="outlined" />
                  <Chip size="small" label="apiResponse" variant="outlined" />
                </Box>
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>Left Data Source (Extracted)</InputLabel>
                <Select
                  value={step.config?.leftDataSource || ''}
                  onChange={(e) => handleConfigChange('leftDataSource', e.target.value)}
                >
                  {availableVariables.map(variable => (
                    <MenuItem key={variable} value={variable}>
                      {variable}
                      {(variable.toLowerCase().includes('items') || 
                        variable.toLowerCase().includes('products') || 
                        variable.toLowerCase().includes('data') ||
                        variable.toLowerCase().includes('list') ||
                        variable.toLowerCase().includes('array') ||
                        variable.toLowerCase().includes('extracted')) && 
                        <Chip size="small" label="Data" sx={{ ml: 1, height: 16 }} />
                      }
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Root variable containing extracted data from file (e.g., 'extractedData', 'extractedItems')
                </FormHelperText>
              </FormControl>
              
              <FormControl fullWidth size="small">
                <InputLabel>Right Data Source (Server/Expected)</InputLabel>
                <Select
                  value={step.config?.rightDataSource || ''}
                  onChange={(e) => handleConfigChange('rightDataSource', e.target.value)}
                >
                  {availableVariables.map(variable => (
                    <MenuItem key={variable} value={variable}>
                      {variable}
                      {(variable.toLowerCase().includes('items') || 
                        variable.toLowerCase().includes('products') || 
                        variable.toLowerCase().includes('data') ||
                        variable.toLowerCase().includes('list') ||
                        variable.toLowerCase().includes('array') ||
                        variable.toLowerCase().includes('server') ||
                        variable.toLowerCase().includes('api')) && 
                        <Chip size="small" label="Data" sx={{ ml: 1, height: 16 }} />
                      }
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Root variable containing server/expected data (e.g., 'orderItems', 'apiResponse')
                </FormHelperText>
              </FormControl>
              
              <TextField
                label="Left Column Title"
                value={step.config?.leftColumnTitle || 'From File'}
                onChange={(e) => handleConfigChange('leftColumnTitle', e.target.value)}
                fullWidth
              />
              
              <TextField
                label="Right Column Title"
                value={step.config?.rightColumnTitle || 'From Server'}
                onChange={(e) => handleConfigChange('rightColumnTitle', e.target.value)}
                fullWidth
              />
              
                              <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Comparison Fields Configuration
                    </Typography>
                  </Box>
                
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleComparisonFieldsDragEnd}
                >
                  <SortableContext 
                    items={step.config?.comparisonFields || []}
                    strategy={verticalListSortingStrategy}
                  >
                    {(step.config?.comparisonFields || []).map((field, index) => (
                      <SortableComparisonFieldCard
                        key={field.id}
                        field={field}
                        index={index}
                        onFieldChange={(index, updatedField) => {
                          const updatedFields = [...step.config.comparisonFields];
                          updatedFields[index] = updatedField;
                          handleConfigChange('comparisonFields', updatedFields);
                        }}
                        onFieldDelete={(index) => {
                          const updatedFields = step.config.comparisonFields.filter((_, i) => i !== index);
                          handleConfigChange('comparisonFields', updatedFields);
                        }}
                      >
                        <Box flexGrow={1}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Field Name"
                              value={field.fieldName}
                              onChange={(e) => {
                                const updatedFields = [...step.config.comparisonFields];
                                updatedFields[index] = { ...field, fieldName: e.target.value };
                                handleConfigChange('comparisonFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., productName"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Display Name"
                              value={field.displayName}
                              onChange={(e) => {
                                const updatedFields = [...step.config.comparisonFields];
                                updatedFields[index] = { ...field, displayName: e.target.value };
                                handleConfigChange('comparisonFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., Product Name"
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              size="small"
                              label="Left Data Path (Extracted Data)"
                              value={field.leftPath}
                              onChange={(e) => {
                                const updatedFields = [...step.config.comparisonFields];
                                updatedFields[index] = { ...field, leftPath: e.target.value };
                                handleConfigChange('comparisonFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., extractedItems[*].productName, items[0].name, productName"
                              helperText="Variable path for extracted data. Use [*] for all array items, [0] for specific index"
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              size="small"
                              label="Right Data Path (Server/Expected Data)"
                              value={field.rightPath}
                              onChange={(e) => {
                                const updatedFields = [...step.config.comparisonFields];
                                updatedFields[index] = { ...field, rightPath: e.target.value };
                                handleConfigChange('comparisonFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., orderItems[*].productName, products[0].name, expectedProductName"
                              helperText="Variable path for server/expected data. Use [*] for all array items"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                                💡 Variable Path Examples:
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', fontFamily: 'monospace', mb: 0.5 }}>
                                • Simple variable: productName
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', fontFamily: 'monospace', mb: 0.5 }}>
                                • Object property: product.name
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', fontFamily: 'monospace', mb: 0.5 }}>
                                • Array (all items): products[*].name
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', fontFamily: 'monospace', mb: 0.5 }}>
                                • Array (specific item): products[0].name
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', fontFamily: 'monospace' }}>
                                • Nested arrays: orders[*].items[*].name
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={3}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Data Type</InputLabel>
                              <Select
                                value={field.dataType || 'text'}
                                onChange={(e) => {
                                  const updatedFields = [...step.config.comparisonFields];
                                  updatedFields[index] = { ...field, dataType: e.target.value };
                                  handleConfigChange('comparisonFields', updatedFields);
                                }}
                                label="Data Type"
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="currency">Currency</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="time">Time</MenuItem>
                                <MenuItem value="boolean">Boolean</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={3}>
                            <TextField
                              size="small"
                              label="Column Width"
                              value={field.columnWidth || 'auto'}
                              onChange={(e) => {
                                const updatedFields = [...step.config.comparisonFields];
                                updatedFields[index] = { ...field, columnWidth: e.target.value };
                                handleConfigChange('comparisonFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="auto, 150px, 20%"
                              helperText="Width for this column"
                            />
                          </Grid>
                          
                          <Grid item xs={3}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={field.editable !== false}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.comparisonFields];
                                    updatedFields[index] = { ...field, editable: e.target.checked };
                                    handleConfigChange('comparisonFields', updatedFields);
                                  }}
                                />
                              }
                              label="Left Editable"
                            />
                          </Grid>
                          
                          <Grid item xs={3}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Matching Logic</InputLabel>
                              <Select
                                value={field.matchingLogic || 'exact'}
                                onChange={(e) => {
                                  const updatedFields = [...step.config.comparisonFields];
                                  updatedFields[index] = { ...field, matchingLogic: e.target.value };
                                  handleConfigChange('comparisonFields', updatedFields);
                                }}
                                label="Matching Logic"
                              >
                                <MenuItem value="exact">Exact Match</MenuItem>
                                <MenuItem value="contains">Contains</MenuItem>
                                <MenuItem value="sku_match">SKU Match</MenuItem>
                                <MenuItem value="fuzzy">Fuzzy Match</MenuItem>
                                <MenuItem value="numeric_tolerance">Numeric Tolerance</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={3}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={field.textWrap || false}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.comparisonFields];
                                    updatedFields[index] = { ...field, textWrap: e.target.checked };
                                    handleConfigChange('comparisonFields', updatedFields);
                                  }}
                                />
                              }
                              label="Text Wrap"
                            />
                          </Grid>
                          
                          {/* Tolerance setting for numeric comparisons */}
                          {field.matchingLogic === 'numeric_tolerance' && (
                            <Grid item xs={12}>
                              <TextField
                                size="small"
                                label="Tolerance Value"
                                value={field.tolerance || '0.01'}
                                onChange={(e) => {
                                  const updatedFields = [...step.config.comparisonFields];
                                  updatedFields[index] = { ...field, tolerance: e.target.value };
                                  handleConfigChange('comparisonFields', updatedFields);
                                }}
                                fullWidth
                                placeholder="0.01"
                                helperText="Acceptable difference for numeric comparisons (e.g., 0.01 for currency)"
                              />
                            </Grid>
                          )}
                          
                          {/* Number and Currency Formatting Options */}
                          {(field.dataType === 'number' || field.dataType === 'currency') && (
                            <>
                              <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Thousands Separator</InputLabel>
                                  <Select
                                    value={field.thousandsSeparator || ','}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.comparisonFields];
                                      updatedFields[index] = { ...field, thousandsSeparator: e.target.value };
                                      handleConfigChange('comparisonFields', updatedFields);
                                    }}
                                    label="Thousands Separator"
                                  >
                                    <MenuItem value=",">Comma (,) - e.g., 1,000</MenuItem>
                                    <MenuItem value=".">Period (.) - e.g., 1.000</MenuItem>
                                    <MenuItem value=" ">Space ( ) - e.g., 1 000</MenuItem>
                                    <MenuItem value="">None - e.g., 1000</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Decimal Separator</InputLabel>
                                  <Select
                                    value={field.decimalSeparator || '.'}
                                    onChange={(e) => {
                                      const updatedFields = [...step.config.comparisonFields];
                                      updatedFields[index] = { ...field, decimalSeparator: e.target.value };
                                      handleConfigChange('comparisonFields', updatedFields);
                                    }}
                                    label="Decimal Separator"
                                  >
                                    <MenuItem value=".">Period (.) - e.g., 10.50</MenuItem>
                                    <MenuItem value=",">Comma (,) - e.g., 10,50</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={field.dataType === 'currency' ? 6 : 12}>
                                <TextField
                                  size="small"
                                  label="Decimal Places"
                                  type="number"
                                  value={field.decimalPlaces !== undefined ? field.decimalPlaces : (field.dataType === 'currency' ? 2 : 0)}
                                  onChange={(e) => {
                                    const updatedFields = [...step.config.comparisonFields];
                                    updatedFields[index] = { ...field, decimalPlaces: parseInt(e.target.value) || 0 };
                                    handleConfigChange('comparisonFields', updatedFields);
                                  }}
                                  fullWidth
                                  inputProps={{ min: 0, max: 10 }}
                                  helperText="Number of decimal places to display (0-10)"
                                />
                              </Grid>
                              
                              {/* Currency Symbol Configuration - only for currency fields */}
                              {field.dataType === 'currency' && (
                                <Grid item xs={6}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Currency Symbol</InputLabel>
                                    <Select
                                      value={field.currencySymbol || '$'}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.comparisonFields];
                                        updatedFields[index] = { ...field, currencySymbol: e.target.value };
                                        handleConfigChange('comparisonFields', updatedFields);
                                      }}
                                      label="Currency Symbol"
                                    >
                                      <MenuItem value="$">Dollar ($)</MenuItem>
                                      <MenuItem value="€">Euro (€)</MenuItem>
                                      <MenuItem value="£">Pound (£)</MenuItem>
                                      <MenuItem value="¥">Yen (¥)</MenuItem>
                                      <MenuItem value="₹">Rupee (₹)</MenuItem>
                                      <MenuItem value="₽">Ruble (₽)</MenuItem>
                                      <MenuItem value="₦">Naira (₦)</MenuItem>
                                      <MenuItem value="R">Rand (R)</MenuItem>
                                      <MenuItem value="₨">Pakistani Rupee (₨)</MenuItem>
                                      <MenuItem value="Rp.">Indonesian Rupiah (Rp.)</MenuItem>
                                      <MenuItem value="custom">Custom...</MenuItem>
                                    </Select>
                                  </FormControl>
                                  {field.currencySymbol === 'custom' && (
                                    <TextField
                                      size="small"
                                      label="Custom Currency Symbol"
                                      value={field.customCurrencySymbol || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.comparisonFields];
                                        updatedFields[index] = { 
                                          ...field, 
                                          customCurrencySymbol: e.target.value,
                                          currencySymbol: 'custom'
                                        };
                                        handleConfigChange('comparisonFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="Enter custom symbol (e.g., ¤, kr, etc.)"
                                      helperText="Enter your custom currency symbol"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                </Grid>
                              )}
                              
                              {/* Date Format Configuration - only for date fields */}
                              {field.dataType === 'date' && (
                                <Grid item xs={12}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Date Format</InputLabel>
                                    <Select
                                      value={field.dateFormat || 'MM/dd/yyyy'}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.comparisonFields];
                                        updatedFields[index] = { ...field, dateFormat: e.target.value };
                                        handleConfigChange('comparisonFields', updatedFields);
                                      }}
                                      label="Date Format"
                                    >
                                      <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (12/25/2023)</MenuItem>
                                      <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (25/12/2023)</MenuItem>
                                      <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (2023-12-25)</MenuItem>
                                      <MenuItem value="dd.MM.yyyy">dd.MM.yyyy (25.12.2023)</MenuItem>
                                      <MenuItem value="MM-dd-yyyy">MM-dd-yyyy (12-25-2023)</MenuItem>
                                      <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (25-12-2023)</MenuItem>
                                      <MenuItem value="MMM dd, yyyy">MMM dd, yyyy (Dec 25, 2023)</MenuItem>
                                      <MenuItem value="MMMM dd, yyyy">MMMM dd, yyyy (December 25, 2023)</MenuItem>
                                      <MenuItem value="dd MMM yyyy">dd MMM yyyy (25 Dec 2023)</MenuItem>
                                      <MenuItem value="dd MMMM yyyy">dd MMMM yyyy (25 December 2023)</MenuItem>
                                      <MenuItem value="custom">Custom...</MenuItem>
                                    </Select>
                                  </FormControl>
                                  {field.dateFormat === 'custom' && (
                                    <TextField
                                      size="small"
                                      label="Custom Date Format"
                                      value={field.customDateFormat || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.comparisonFields];
                                        updatedFields[index] = { 
                                          ...field, 
                                          customDateFormat: e.target.value,
                                          dateFormat: 'custom'
                                        };
                                        handleConfigChange('comparisonFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="e.g., DD/MM/YYYY, dd.mm.yyyy, MM-dd-yy"
                                      helperText="Enter custom date format (supports DD, MM, YYYY, dd, mm, yyyy)"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                </Grid>
                              )}

                              {/* Time Format Configuration - only for time fields */}
                              {field.dataType === 'time' && (
                                <Grid item xs={12}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Time Format</InputLabel>
                                    <Select
                                      value={field.timeFormat || 'HH:mm:ss'}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.comparisonFields];
                                        updatedFields[index] = { ...field, timeFormat: e.target.value };
                                        handleConfigChange('comparisonFields', updatedFields);
                                      }}
                                      label="Time Format"
                                    >
                                      <MenuItem value="HH:mm:ss">24-hour with seconds (14:30:44)</MenuItem>
                                      <MenuItem value="HH:mm">24-hour without seconds (14:30)</MenuItem>
                                      <MenuItem value="hh:mm:ss A">12-hour with seconds (02:30:44 PM)</MenuItem>
                                      <MenuItem value="hh:mm A">12-hour without seconds (02:30 PM)</MenuItem>
                                      <MenuItem value="h:mm A">12-hour no leading zero (2:30 PM)</MenuItem>
                                      <MenuItem value="h:mm:ss A">12-hour no leading zero with seconds (2:30:44 PM)</MenuItem>
                                      <MenuItem value="custom">Custom...</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                      Choose how to display time values. Input format is HHMMSS (e.g., 035044 = 03:50:44)
                                    </FormHelperText>
                                  </FormControl>
                                  {field.timeFormat === 'custom' && (
                                    <TextField
                                      size="small"
                                      label="Custom Time Format"
                                      value={field.customTimeFormat || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...step.config.comparisonFields];
                                        updatedFields[index] = { 
                                          ...field, 
                                          customTimeFormat: e.target.value,
                                          timeFormat: 'custom'
                                        };
                                        handleConfigChange('comparisonFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="e.g., HH:mm, h:mm A, HH.mm.ss"
                                      helperText="Custom time format (HH=24hr, hh=12hr, mm=minutes, ss=seconds, A=AM/PM)"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                </Grid>
                              )}
                            </>
                          )}
                        </Grid>
                      </Box>
                      </SortableComparisonFieldCard>
                    ))}
                  </SortableContext>
                </DndContext>
                
                {(!step.config?.comparisonFields || step.config.comparisonFields.length === 0) && (
                  <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="body2">
                      No comparison fields defined. Add fields to compare extracted vs server data.
                    </Typography>
                  </Box>
                )}
                
                {/* Add Comparison Field Button - moved to bottom for better UX */}
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentFields = step.config?.comparisonFields || [];
                      const newField = {
                        id: `comp-field-${Date.now()}`,
                        fieldName: '',
                        displayName: '',
                        leftPath: '',
                        rightPath: '',
                        editable: true,
                        matchingLogic: 'exact',
                        columnWidth: 'auto',
                        dataType: 'text'
                      };
                      handleConfigChange('comparisonFields', [...currentFields, newField]);
                    }}
                  >
                    Add Comparison Field
                  </Button>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Quick Templates for Data Comparison */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  🚀 Quick Comparison Templates
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const goodsReceivedFields = [
                        { id: 'goods-1', fieldName: 'productName', displayName: 'Product Name', leftPath: 'extractedItems[*].productName', rightPath: 'orderItems[*].productName', editable: true, matchingLogic: 'fuzzy', textWrap: true },
                        { id: 'goods-2', fieldName: 'sku', displayName: 'SKU', leftPath: 'extractedItems[*].sku', rightPath: 'orderItems[*].sku', editable: true, matchingLogic: 'exact' },
                        { id: 'goods-3', fieldName: 'quantity', displayName: 'Quantity', leftPath: 'extractedItems[*].quantity', rightPath: 'orderItems[*].expectedQuantity', editable: true, matchingLogic: 'exact' },
                        { id: 'goods-4', fieldName: 'price', displayName: 'Unit Price', leftPath: 'extractedItems[*].price', rightPath: 'orderItems[*].unitPrice', editable: true, matchingLogic: 'exact' }
                      ];
                      handleConfigChange('comparisonFields', goodsReceivedFields);
                    }}
                  >
                    📦 Goods Received
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const invoiceComparisonFields = [
                        { id: 'inv-comp-1', fieldName: 'invoiceNumber', displayName: 'Invoice #', leftPath: 'invoiceNumber', rightPath: 'expectedInvoiceNumber', editable: true, matchingLogic: 'exact' },
                        { id: 'inv-comp-2', fieldName: 'vendorName', displayName: 'Vendor', leftPath: 'vendorName', rightPath: 'expectedVendor', editable: false, matchingLogic: 'fuzzy' },
                        { id: 'inv-comp-3', fieldName: 'totalAmount', displayName: 'Total Amount', leftPath: 'totalAmount', rightPath: 'expectedTotal', editable: true, matchingLogic: 'exact' }
                      ];
                      handleConfigChange('comparisonFields', invoiceComparisonFields);
                    }}
                  >
                    🧾 Invoice Comparison
                  </Button>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* General Data Preview Settings */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">⚙️ Display & Behavior Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.showDataPreview !== undefined ? step.config.showDataPreview : true}
                  onChange={(e) => handleConfigChange('showDataPreview', e.target.checked)}
                />
              }
              label="Show data preview section"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.enableConfidenceColors !== undefined ? step.config.enableConfidenceColors : true}
                  onChange={(e) => handleConfigChange('enableConfidenceColors', e.target.checked)}
                />
              }
              label="Color-code by confidence level"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.enableFieldConfidence !== undefined ? step.config.enableFieldConfidence : false}
                  onChange={(e) => handleConfigChange('enableFieldConfidence', e.target.checked)}
                />
              }
              label="Show field-level confidence indicators"
            />
            
            {/* Enhanced Field Confidence Rules Configuration */}
            {step.config?.enableFieldConfidence !== false && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc02' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  🎨 Field-Level Confidence Rules
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                  Set confidence thresholds and colors for individual fields. Each field can have its own confidence rules.
                </Typography>
                
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Field Confidence Rules
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const currentRules = step.config?.fieldConfidenceRules || [];
                        const newRule = {
                          fieldKey: '',
                          thresholds: [{
                            value: 80,
                            color: '#fff3cd',
                            label: 'Medium Confidence'
                          }]
                        };
                        handleConfigChange('fieldConfidenceRules', [...currentRules, newRule]);
                      }}
                    >
                      Add Field Rule
                    </Button>
                  </Box>
                  
                  {(step.config?.fieldConfidenceRules || []).map((rule, ruleIndex) => (
                    <Card key={ruleIndex} sx={{ mb: 2, p: 2, backgroundColor: '#fff9c4', border: '1px solid #f9f9f9' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                        <Box flexGrow={1}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={4}>
                              <TextField
                                size="small"
                                label="Field Key"
                                value={rule.fieldKey}
                                onChange={(e) => {
                                  const updatedRules = [...step.config.fieldConfidenceRules];
                                  updatedRules[ruleIndex] = { ...rule, fieldKey: e.target.value };
                                  handleConfigChange('fieldConfidenceRules', updatedRules);
                                }}
                                fullWidth
                                placeholder="e.g., productName, quantity"
                              />
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant="caption" color="textSecondary">
                                Thresholds (sorted by value, highest first)
                              </Typography>
                            </Grid>
                          </Grid>
                          
                          {/* Thresholds for this field */}
                          {rule.thresholds.map((threshold, thresholdIndex) => (
                            <Box key={thresholdIndex} sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={3}>
                                  <TextField
                                    size="small"
                                    label="Threshold %"
                                    type="number"
                                    inputProps={{ min: 0, max: 100 }}
                                    value={threshold.value}
                                    onChange={(e) => {
                                      const updatedRules = [...step.config.fieldConfidenceRules];
                                      const updatedThresholds = [...rule.thresholds];
                                      updatedThresholds[thresholdIndex] = { ...threshold, value: parseInt(e.target.value) };
                                      updatedRules[ruleIndex] = { ...rule, thresholds: updatedThresholds };
                                      handleConfigChange('fieldConfidenceRules', updatedRules);
                                    }}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={4}>
                                  <TextField
                                    size="small"
                                    label="Label"
                                    value={threshold.label}
                                    onChange={(e) => {
                                      const updatedRules = [...step.config.fieldConfidenceRules];
                                      const updatedThresholds = [...rule.thresholds];
                                      updatedThresholds[thresholdIndex] = { ...threshold, label: e.target.value };
                                      updatedRules[ruleIndex] = { ...rule, thresholds: updatedThresholds };
                                      handleConfigChange('fieldConfidenceRules', updatedRules);
                                    }}
                                    fullWidth
                                    placeholder="e.g., Low Confidence"
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <input
                                      type="color"
                                      value={threshold.color}
                                      onChange={(e) => {
                                        const updatedRules = [...step.config.fieldConfidenceRules];
                                        const updatedThresholds = [...rule.thresholds];
                                        updatedThresholds[thresholdIndex] = { ...threshold, color: e.target.value };
                                        updatedRules[ruleIndex] = { ...rule, thresholds: updatedThresholds };
                                        handleConfigChange('fieldConfidenceRules', updatedRules);
                                      }}
                                      style={{
                                        width: '40px',
                                        height: '32px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                      }}
                                    />
                                    <Typography variant="caption">
                                      Color
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={2}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const updatedRules = [...step.config.fieldConfidenceRules];
                                      const updatedThresholds = rule.thresholds.filter((_, i) => i !== thresholdIndex);
                                      updatedRules[ruleIndex] = { ...rule, thresholds: updatedThresholds };
                                      handleConfigChange('fieldConfidenceRules', updatedRules);
                                    }}
                                    sx={{ color: '#d32f2f' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                          
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const updatedRules = [...step.config.fieldConfidenceRules];
                              const updatedThresholds = [...rule.thresholds];
                              updatedThresholds.push({
                                value: 60,
                                color: '#f8d7da',
                                label: 'Low Confidence'
                              });
                              updatedRules[ruleIndex] = { ...rule, thresholds: updatedThresholds };
                              handleConfigChange('fieldConfidenceRules', updatedRules);
                            }}
                            sx={{ mt: 1 }}
                          >
                            Add Threshold
                          </Button>
                        </Box>
                        
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updatedRules = step.config.fieldConfidenceRules.filter((_, i) => i !== ruleIndex);
                            handleConfigChange('fieldConfidenceRules', updatedRules);
                          }}
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            {/* Row Color Rules Configuration */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                🎨 Row Color Rules
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                Set row background colors based on variable conditions. Field colors will overlay row colors.
              </Typography>
              
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Row Color Rules
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentRules = step.config?.rowColorRules || [];
                      const newRule = {
                        condition: {
                          variable: '',
                          operator: 'equals',
                          value: false,
                          logicalOperator: 'AND',
                          secondVariable: '',
                          secondOperator: 'equals',
                          secondValue: false
                        },
                        color: '#d4edda',
                        label: 'New Rule'
                      };
                      handleConfigChange('rowColorRules', [...currentRules, newRule]);
                    }}
                  >
                    Add Row Rule
                  </Button>
                </Box>
                
                {(step.config?.rowColorRules || []).map((rule, ruleIndex) => (
                  <Card key={ruleIndex} sx={{ mb: 2, p: 2, backgroundColor: '#f1f8e9', border: '1px solid #c8e6c9' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                      <Box flexGrow={1}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={3}>
                            <TextField
                              size="small"
                              label="Variable"
                              value={rule.condition.variable}
                              onChange={(e) => {
                                const updatedRules = [...step.config.rowColorRules];
                                updatedRules[ruleIndex] = {
                                  ...rule,
                                  condition: { ...rule.condition, variable: e.target.value }
                                };
                                handleConfigChange('rowColorRules', updatedRules);
                              }}
                              fullWidth
                              placeholder="e.g., exists_in_GR"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Operator</InputLabel>
                              <Select
                                value={rule.condition.operator}
                                onChange={(e) => {
                                  const updatedRules = [...step.config.rowColorRules];
                                  updatedRules[ruleIndex] = {
                                    ...rule,
                                    condition: { ...rule.condition, operator: e.target.value }
                                  };
                                  handleConfigChange('rowColorRules', updatedRules);
                                }}
                                label="Operator"
                              >
                                <MenuItem value="equals">Equals</MenuItem>
                                <MenuItem value="not_equals">Not Equals</MenuItem>
                                <MenuItem value="contains">Contains</MenuItem>
                                <MenuItem value="not_contains">Not Contains</MenuItem>
                                <MenuItem value="exists">Exists</MenuItem>
                                <MenuItem value="not_exists">Not Exists</MenuItem>
                                <MenuItem value="greater_than">Greater Than</MenuItem>
                                <MenuItem value="less_than">Less Than</MenuItem>
                                <MenuItem value="is_true">Is True</MenuItem>
                                <MenuItem value="is_false">Is False</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              size="small"
                              label="Value"
                              value={rule.condition.value}
                              onChange={(e) => {
                                const updatedRules = [...step.config.rowColorRules];
                                updatedRules[ruleIndex] = {
                                  ...rule,
                                  condition: { ...rule.condition, value: e.target.value }
                                };
                                handleConfigChange('rowColorRules', updatedRules);
                              }}
                              fullWidth
                              placeholder="false"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              size="small"
                              label="Label"
                              value={rule.label}
                              onChange={(e) => {
                                const updatedRules = [...step.config.rowColorRules];
                                updatedRules[ruleIndex] = { ...rule, label: e.target.value };
                                handleConfigChange('rowColorRules', updatedRules);
                              }}
                              fullWidth
                              placeholder="e.g., Not in GR"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <input
                                type="color"
                                value={rule.color}
                                onChange={(e) => {
                                  const updatedRules = [...step.config.rowColorRules];
                                  updatedRules[ruleIndex] = { ...rule, color: e.target.value };
                                  handleConfigChange('rowColorRules', updatedRules);
                                }}
                                style={{
                                  width: '40px',
                                  height: '32px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              />
                              <Typography variant="caption">
                                Color
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={1}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updatedRules = step.config.rowColorRules.filter((_, i) => i !== ruleIndex);
                                handleConfigChange('rowColorRules', updatedRules);
                              }}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                        
                        {/* Second condition for AND/OR logic */}
                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                            Second Condition (Optional)
                          </Typography>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={2}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Logic</InputLabel>
                                <Select
                                  value={rule.condition.logicalOperator}
                                  onChange={(e) => {
                                    const updatedRules = [...step.config.rowColorRules];
                                    updatedRules[ruleIndex] = {
                                      ...rule,
                                      condition: { ...rule.condition, logicalOperator: e.target.value }
                                    };
                                    handleConfigChange('rowColorRules', updatedRules);
                                  }}
                                  label="Logic"
                                >
                                  <MenuItem value="AND">AND</MenuItem>
                                  <MenuItem value="OR">OR</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                size="small"
                                label="Second Variable"
                                value={rule.condition.secondVariable}
                                onChange={(e) => {
                                  const updatedRules = [...step.config.rowColorRules];
                                  updatedRules[ruleIndex] = {
                                    ...rule,
                                    condition: { ...rule.condition, secondVariable: e.target.value }
                                  };
                                  handleConfigChange('rowColorRules', updatedRules);
                                }}
                                fullWidth
                                placeholder="e.g., exists_in_purchase_order"
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Operator</InputLabel>
                                <Select
                                  value={rule.condition.secondOperator}
                                  onChange={(e) => {
                                    const updatedRules = [...step.config.rowColorRules];
                                    updatedRules[ruleIndex] = {
                                      ...rule,
                                      condition: { ...rule.condition, secondOperator: e.target.value }
                                    };
                                    handleConfigChange('rowColorRules', updatedRules);
                                  }}
                                  label="Operator"
                                >
                                  <MenuItem value="equals">Equals</MenuItem>
                                  <MenuItem value="not_equals">Not Equals</MenuItem>
                                  <MenuItem value="contains">Contains</MenuItem>
                                  <MenuItem value="not_contains">Not Contains</MenuItem>
                                  <MenuItem value="exists">Exists</MenuItem>
                                  <MenuItem value="not_exists">Not Exists</MenuItem>
                                  <MenuItem value="greater_than">Greater Than</MenuItem>
                                  <MenuItem value="less_than">Less Than</MenuItem>
                                  <MenuItem value="is_true">Is True</MenuItem>
                                  <MenuItem value="is_false">Is False</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                              <TextField
                                size="small"
                                label="Second Value"
                                value={rule.condition.secondValue}
                                onChange={(e) => {
                                  const updatedRules = [...step.config.rowColorRules];
                                  updatedRules[ruleIndex] = {
                                    ...rule,
                                    condition: { ...rule.condition, secondValue: e.target.value }
                                  };
                                  handleConfigChange('rowColorRules', updatedRules);
                                }}
                                fullWidth
                                placeholder="false"
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Box>

            {/* Cell Color Rules Configuration */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                🎨 Cell Font Color Rules
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                Set individual cell font colors based on variable conditions. For example, if value A is greater than value B, make the cell text red. Use {`{{variableName}}`} format for variables in values.
              </Typography>
              
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Cell Font Color Rules
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentRules = step.config?.cellColorRules || [];
                      const newRule = {
                        fieldKey: '',
                        condition: {
                          variable: '',
                          operator: 'equals',
                          value: '',
                          logicalOperator: 'AND',
                          secondVariable: '',
                          secondOperator: 'equals',
                          secondValue: ''
                        },
                        color: '#d32f2f',
                        label: 'New Font Color Rule'
                      };
                      handleConfigChange('cellColorRules', [...currentRules, newRule]);
                    }}
                  >
                    Add Font Color Rule
                  </Button>
                </Box>
                
                {(step.config?.cellColorRules || []).map((rule, ruleIndex) => (
                  <Card key={ruleIndex} sx={{ mb: 2, p: 2, backgroundColor: '#fff8e1', border: '1px solid #ffb74d' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                      <Box flexGrow={1}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={2}>
                            <TextField
                              size="small"
                              label="Field Key"
                              value={rule.fieldKey}
                              onChange={(e) => {
                                const updatedRules = [...step.config.cellColorRules];
                                updatedRules[ruleIndex] = { ...rule, fieldKey: e.target.value };
                                handleConfigChange('cellColorRules', updatedRules);
                              }}
                              fullWidth
                              placeholder="e.g., unitPrice"
                              helperText="Column field to apply rule to"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              size="small"
                              label="Variable"
                              value={rule.condition.variable}
                              onChange={(e) => {
                                const updatedRules = [...step.config.cellColorRules];
                                updatedRules[ruleIndex] = {
                                  ...rule,
                                  condition: { ...rule.condition, variable: e.target.value }
                                };
                                handleConfigChange('cellColorRules', updatedRules);
                              }}
                              fullWidth
                              placeholder="e.g., unitPrice"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Operator</InputLabel>
                              <Select
                                value={rule.condition.operator}
                                label="Operator"
                                onChange={(e) => {
                                  const updatedRules = [...step.config.cellColorRules];
                                  updatedRules[ruleIndex] = {
                                    ...rule,
                                    condition: { ...rule.condition, operator: e.target.value }
                                  };
                                  handleConfigChange('cellColorRules', updatedRules);
                                }}
                              >
                                <MenuItem value="equals">Equals</MenuItem>
                                <MenuItem value="not_equals">Not Equals</MenuItem>
                                <MenuItem value="greater_than">Greater Than</MenuItem>
                                <MenuItem value="less_than">Less Than</MenuItem>
                                <MenuItem value="contains">Contains</MenuItem>
                                <MenuItem value="not_contains">Not Contains</MenuItem>
                                <MenuItem value="exists">Exists</MenuItem>
                                <MenuItem value="not_exists">Not Exists</MenuItem>
                                <MenuItem value="is_true">Is True</MenuItem>
                                <MenuItem value="is_false">Is False</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              size="small"
                              label="Value"
                              value={rule.condition.value}
                              onChange={(e) => {
                                const updatedRules = [...step.config.cellColorRules];
                                updatedRules[ruleIndex] = {
                                  ...rule,
                                  condition: { ...rule.condition, value: e.target.value }
                                };
                                handleConfigChange('cellColorRules', updatedRules);
                              }}
                              fullWidth
                              placeholder="100 or {{quantityPO}}"
                              helperText="Use {{varName}} for variables"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              size="small"
                              label="Label"
                              value={rule.label}
                              onChange={(e) => {
                                const updatedRules = [...step.config.cellColorRules];
                                updatedRules[ruleIndex] = { ...rule, label: e.target.value };
                                handleConfigChange('cellColorRules', updatedRules);
                              }}
                              fullWidth
                              placeholder="Rule description"
                            />
                          </Grid>
                          <Grid item xs={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <input
                                type="color"
                                value={rule.color}
                                onChange={(e) => {
                                  const updatedRules = [...step.config.cellColorRules];
                                  updatedRules[ruleIndex] = { ...rule, color: e.target.value };
                                  handleConfigChange('cellColorRules', updatedRules);
                                }}
                                style={{
                                  width: '40px',
                                  height: '32px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              />
                              <Typography variant="caption">
                                Font Color
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={1}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updatedRules = step.config.cellColorRules.filter((_, i) => i !== ruleIndex);
                                handleConfigChange('cellColorRules', updatedRules);
                              }}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                        
                        {/* Second condition for AND/OR logic */}
                        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                            Optional: Add a second condition with AND/OR logic
                          </Typography>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={2}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Logic</InputLabel>
                                <Select
                                  value={rule.condition.logicalOperator || 'AND'}
                                  label="Logic"
                                  onChange={(e) => {
                                    const updatedRules = [...step.config.cellColorRules];
                                    updatedRules[ruleIndex] = {
                                      ...rule,
                                      condition: { ...rule.condition, logicalOperator: e.target.value }
                                    };
                                    handleConfigChange('cellColorRules', updatedRules);
                                  }}
                                >
                                  <MenuItem value="AND">AND</MenuItem>
                                  <MenuItem value="OR">OR</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                              <TextField
                                size="small"
                                label="Second Variable"
                                value={rule.condition.secondVariable || ''}
                                onChange={(e) => {
                                  const updatedRules = [...step.config.cellColorRules];
                                  updatedRules[ruleIndex] = {
                                    ...rule,
                                    condition: { ...rule.condition, secondVariable: e.target.value }
                                  };
                                  handleConfigChange('cellColorRules', updatedRules);
                                }}
                                fullWidth
                                placeholder="e.g., quantity"
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Operator</InputLabel>
                                <Select
                                  value={rule.condition.secondOperator || 'equals'}
                                  label="Operator"
                                  onChange={(e) => {
                                    const updatedRules = [...step.config.cellColorRules];
                                    updatedRules[ruleIndex] = {
                                      ...rule,
                                      condition: { ...rule.condition, secondOperator: e.target.value }
                                    };
                                    handleConfigChange('cellColorRules', updatedRules);
                                  }}
                                >
                                  <MenuItem value="equals">Equals</MenuItem>
                                  <MenuItem value="not_equals">Not Equals</MenuItem>
                                  <MenuItem value="greater_than">Greater Than</MenuItem>
                                  <MenuItem value="less_than">Less Than</MenuItem>
                                  <MenuItem value="contains">Contains</MenuItem>
                                  <MenuItem value="not_contains">Not Contains</MenuItem>
                                  <MenuItem value="exists">Exists</MenuItem>
                                  <MenuItem value="not_exists">Not Exists</MenuItem>
                                  <MenuItem value="is_true">Is True</MenuItem>
                                  <MenuItem value="is_false">Is False</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                              <TextField
                                size="small"
                                label="Second Value"
                                value={rule.condition.secondValue || ''}
                                onChange={(e) => {
                                  const updatedRules = [...step.config.cellColorRules];
                                  updatedRules[ruleIndex] = {
                                    ...rule,
                                    condition: { ...rule.condition, secondValue: e.target.value }
                                  };
                                  handleConfigChange('cellColorRules', updatedRules);
                                }}
                                fullWidth
                                placeholder="50 or {{minOrder}}"
                                helperText="Use {{varName}} for variables"
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Box>

            {/* Legacy Confidence Level Configuration */}
            {step.config?.enableConfidenceColors !== false && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0f8ff', borderRadius: 1, border: '1px solid #e1e7ff' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  🎨 Legacy Row-Level Confidence Color Coding
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                  Set confidence thresholds and colors. Rows will be colored based on their confidence level.
                </Typography>
                
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Confidence Thresholds
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const currentThresholds = step.config?.confidenceThresholds || [];
                        const newThreshold = {
                          id: `threshold-${Date.now()}`,
                          value: 80,
                          color: '#ffeb3b',
                          label: 'Medium Confidence'
                        };
                        handleConfigChange('confidenceThresholds', [...currentThresholds, newThreshold]);
                      }}
                    >
                      Add Threshold
                    </Button>
                  </Box>
                  
                  {(step.config?.confidenceThresholds || []).map((threshold, index) => (
                    <Card key={threshold.id} sx={{ mb: 2, p: 2, backgroundColor: '#fff9c4', border: '1px solid #f9f9f9' }}>
                      <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
                        <Box flexGrow={1}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={3}>
                              <TextField
                                size="small"
                                label="Threshold %"
                                type="number"
                                inputProps={{ min: 0, max: 100 }}
                                value={threshold.value}
                                onChange={(e) => {
                                  const updatedThresholds = [...step.config.confidenceThresholds];
                                  updatedThresholds[index] = { ...threshold, value: parseInt(e.target.value) };
                                  handleConfigChange('confidenceThresholds', updatedThresholds);
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                size="small"
                                label="Label"
                                value={threshold.label}
                                onChange={(e) => {
                                  const updatedThresholds = [...step.config.confidenceThresholds];
                                  updatedThresholds[index] = { ...threshold, label: e.target.value };
                                  handleConfigChange('confidenceThresholds', updatedThresholds);
                                }}
                                fullWidth
                                placeholder="e.g., Low Confidence"
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                  type="color"
                                  value={threshold.color}
                                  onChange={(e) => {
                                    const updatedThresholds = [...step.config.confidenceThresholds];
                                    updatedThresholds[index] = { ...threshold, color: e.target.value };
                                    handleConfigChange('confidenceThresholds', updatedThresholds);
                                  }}
                                  style={{
                                    width: '40px',
                                    height: '32px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                />
                                <Typography variant="caption">
                                  Color
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'center', display: 'block' }}>
                                &lt; {threshold.value}%
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updatedThresholds = step.config.confidenceThresholds.filter((_, i) => i !== index);
                            handleConfigChange('confidenceThresholds', updatedThresholds);
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Card>
                  ))}
                  
                  {(!step.config?.confidenceThresholds || step.config.confidenceThresholds.length === 0) && (
                    <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        No confidence thresholds configured. Click "Add Threshold" to create color coding rules.
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Quick Templates */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Quick Templates:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const defaultThresholds = [
                            { id: 'threshold-90', value: 90, color: '#ffeb3b', label: 'Medium Confidence' },
                            { id: 'threshold-70', value: 70, color: '#f44336', label: 'Low Confidence' }
                          ];
                          handleConfigChange('confidenceThresholds', defaultThresholds);
                        }}
                      >
                        🎯 Standard (70%, 90%)
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const strictThresholds = [
                            { id: 'threshold-95', value: 95, color: '#ff9800', label: 'Good' },
                            { id: 'threshold-85', value: 85, color: '#ffeb3b', label: 'Fair' },
                            { id: 'threshold-70', value: 70, color: '#f44336', label: 'Poor' }
                          ];
                          handleConfigChange('confidenceThresholds', strictThresholds);
                        }}
                      >
                        🔥 Strict (70%, 85%, 95%)
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.allowInlineEditing !== undefined ? step.config.allowInlineEditing : true}
                  onChange={(e) => handleConfigChange('allowInlineEditing', e.target.checked)}
                />
              }
              label="Allow inline editing"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.highlightDifferences !== undefined ? step.config.highlightDifferences : true}
                  onChange={(e) => handleConfigChange('highlightDifferences', e.target.checked)}
                />
              }
              label="Highlight differences in comparison view"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.requireConfirmation !== undefined ? step.config.requireConfirmation : false}
                  onChange={(e) => handleConfigChange('requireConfirmation', e.target.checked)}
                />
              }
              label="Require user confirmation before proceeding"
            />
            
            <TextField
              label="Output Variable Name"
              value={step.config?.outputVariable || 'collectedData'}
              onChange={(e) => handleConfigChange('outputVariable', e.target.value)}
              fullWidth
              helperText="Variable name to store the collected/modified data"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Custom Action Buttons Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🔘 Custom Action Buttons</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Add custom buttons that allow users to navigate to specific steps or set variables. Useful for "Skip", "Previous", "Refresh Data", etc. You can use variable mappings to set values like "tryAgain: true" and let branch logic determine the next step.
            </Typography>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Action Buttons
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const currentButtons = step.config?.actionButtons || [];
                    const newButton = {
                      id: `button-${Date.now()}`,
                      label: 'Skip',
                      targetStepId: '',
                      variant: 'outlined',
                      color: 'primary',
                      enabled: true,
                      showInHeader: false
                    };
                    handleConfigChange('actionButtons', [...currentButtons, newButton]);
                  }}
                >
                  Add Button
                </Button>
              </Box>
              
                            {(step.config?.actionButtons || []).map((button, index) => (
                <Card key={button.id} sx={{ mb: 2, p: 2, backgroundColor: '#fff3e0', border: '1px solid #ffcc02' }}>
                  <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
                    <Box flexGrow={1}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="Button Label"
                            value={button.label}
                            onChange={(e) => {
                              const updatedButtons = [...step.config.actionButtons];
                              updatedButtons[index] = { ...button, label: e.target.value };
                              handleConfigChange('actionButtons', updatedButtons);
                            }}
                            fullWidth
                            placeholder="e.g., Skip, Previous, Refresh"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Target Step</InputLabel>
                            <Select
                              value={button.targetStepId}
                              onChange={(e) => {
                                const updatedButtons = [...step.config.actionButtons];
                                updatedButtons[index] = { ...button, targetStepId: e.target.value };
                                handleConfigChange('actionButtons', updatedButtons);
                              }}
                              label="Target Step"
                            >
                              <MenuItem value="">
                                <em>Select a step...</em>
                              </MenuItem>
                              {flowData.steps
                                ?.filter(s => s.id !== step.id) // Don't allow self-reference
                                ?.map(targetStep => (
                                  <MenuItem key={targetStep.id} value={targetStep.id}>
                                    {targetStep.name} ({targetStep.type})
                                  </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                              Optional: Step to navigate to when this button is clicked. Leave empty to just set variables and continue to next step.
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={4}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Button Style</InputLabel>
                            <Select
                              value={button.variant || 'outlined'}
                              onChange={(e) => {
                                const updatedButtons = [...step.config.actionButtons];
                                updatedButtons[index] = { ...button, variant: e.target.value };
                                handleConfigChange('actionButtons', updatedButtons);
                              }}
                              label="Button Style"
                            >
                              <MenuItem value="outlined">Outlined</MenuItem>
                              <MenuItem value="contained">Filled</MenuItem>
                              <MenuItem value="text">Text</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={4}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Button Color</InputLabel>
                            <Select
                              value={button.color || 'primary'}
                              onChange={(e) => {
                                const updatedButtons = [...step.config.actionButtons];
                                updatedButtons[index] = { ...button, color: e.target.value };
                                handleConfigChange('actionButtons', updatedButtons);
                              }}
                              label="Button Color"
                            >
                              <MenuItem value="primary">Primary</MenuItem>
                              <MenuItem value="secondary">Secondary</MenuItem>
                              <MenuItem value="success">Success</MenuItem>
                              <MenuItem value="warning">Warning</MenuItem>
                              <MenuItem value="error">Error</MenuItem>
                              <MenuItem value="info">Info</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={button.enabled !== false}
                                onChange={(e) => {
                                  const updatedButtons = [...step.config.actionButtons];
                                  updatedButtons[index] = { ...button, enabled: e.target.checked };
                                  handleConfigChange('actionButtons', updatedButtons);
                                }}
                              />
                            }
                            label="Enabled"
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={button.showInHeader || false}
                                onChange={(e) => {
                                  const updatedButtons = [...step.config.actionButtons];
                                  updatedButtons[index] = { ...button, showInHeader: e.target.checked };
                                  handleConfigChange('actionButtons', updatedButtons);
                                }}
                              />
                            }
                            label="Show in Header Fields section"
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            When enabled, this button will also appear in the Header Fields section for quick access
                          </Typography>
                        </Grid>
                        
                        {/* Variable Mapping Section */}
                        <Grid item xs={12}>
                          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                              🎯 Variable Mapping (Optional)
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                              Set variables when this button is clicked. Useful for tracking user choices like "userChoice: accepted" or "skipReason: not_applicable"
                            </Typography>
                            
                            <Box>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                  Variables to Set
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    const updatedButtons = [...step.config.actionButtons];
                                    const currentMappings = updatedButtons[index].variableMappings || [];
                                    updatedButtons[index] = { 
                                      ...button, 
                                      variableMappings: [...currentMappings, { variable: '', value: '', valueType: 'static' }]
                                    };
                                    handleConfigChange('actionButtons', updatedButtons);
                                  }}
                                >
                                  + Add Variable
                                </Button>
                              </Box>
                              
                              {(button.variableMappings || []).map((mapping, mappingIndex) => (
                                <Box key={mappingIndex} sx={{ mb: 1, p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                  <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={4}>
                                      <TextField
                                        size="small"
                                        label="Variable Name"
                                        value={mapping.variable}
                                        onChange={(e) => {
                                          const updatedButtons = [...step.config.actionButtons];
                                          const updatedMappings = [...updatedButtons[index].variableMappings];
                                          updatedMappings[mappingIndex] = { ...mapping, variable: e.target.value };
                                          updatedButtons[index] = { ...button, variableMappings: updatedMappings };
                                          handleConfigChange('actionButtons', updatedButtons);
                                        }}
                                        fullWidth
                                        placeholder="e.g., userChoice"
                                      />
                                    </Grid>
                                    <Grid item xs={3}>
                                      <FormControl size="small" fullWidth>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                          value={mapping.valueType || 'static'}
                                          onChange={(e) => {
                                            const updatedButtons = [...step.config.actionButtons];
                                            const updatedMappings = [...updatedButtons[index].variableMappings];
                                            updatedMappings[mappingIndex] = { ...mapping, valueType: e.target.value };
                                            updatedButtons[index] = { ...button, variableMappings: updatedMappings };
                                            handleConfigChange('actionButtons', updatedButtons);
                                          }}
                                          label="Type"
                                        >
                                          <MenuItem value="static">Static Value</MenuItem>
                                          <MenuItem value="template">Template Variable</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={4}>
                                      <TextField
                                        size="small"
                                        label="Value"
                                        value={mapping.value}
                                        onChange={(e) => {
                                          const updatedButtons = [...step.config.actionButtons];
                                          const updatedMappings = [...updatedButtons[index].variableMappings];
                                          updatedMappings[mappingIndex] = { ...mapping, value: e.target.value };
                                          updatedButtons[index] = { ...button, variableMappings: updatedMappings };
                                          handleConfigChange('actionButtons', updatedButtons);
                                        }}
                                        fullWidth
                                        placeholder={mapping.valueType === 'template' ? "e.g., {{userName}}" : "e.g., accepted"}
                                      />
                                    </Grid>
                                    <Grid item xs={1}>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const updatedButtons = [...step.config.actionButtons];
                                          const updatedMappings = updatedButtons[index].variableMappings.filter((_, i) => i !== mappingIndex);
                                          updatedButtons[index] = { ...button, variableMappings: updatedMappings };
                                          handleConfigChange('actionButtons', updatedButtons);
                                        }}
                                        sx={{ color: 'error.main' }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}
                              
                                                    {(!button.variableMappings || button.variableMappings.length === 0) && (
                        <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                          No variables configured. This button will only navigate to the target step (if specified).
                        </Typography>
                      )}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updatedButtons = step.config.actionButtons.filter((_, i) => i !== index);
                        handleConfigChange('actionButtons', updatedButtons);
                      }}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))}
              
              {(!step.config?.actionButtons || step.config.actionButtons.length === 0) && (
                <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    No action buttons configured. Click "Add Button" to create custom navigation buttons.
                  </Typography>
                </Box>
              )}
              
              {/* Button Templates */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Quick Templates:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const currentButtons = step.config?.actionButtons || [];
                      const skipButton = {
                        id: `button-${Date.now()}`,
                        label: 'Skip',
                        targetStepId: '',
                        variant: 'outlined',
                        color: 'secondary',
                        enabled: true,
                        showInHeader: false,
                        variableMappings: [
                          { variable: 'userChoice', value: 'skipped', valueType: 'static' },
                          { variable: 'skipTimestamp', value: '{{timestamp}}', valueType: 'template' }
                        ]
                      };
                      handleConfigChange('actionButtons', [...currentButtons, skipButton]);
                    }}
                  >
                    ⏭️ Skip Button
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const currentButtons = step.config?.actionButtons || [];
                      const previousButton = {
                        id: `button-${Date.now()}`,
                        label: 'Previous',
                        targetStepId: '',
                        variant: 'outlined',
                        color: 'primary',
                        enabled: true,
                        showInHeader: false
                      };
                      handleConfigChange('actionButtons', [...currentButtons, previousButton]);
                    }}
                  >
                    ⬅️ Previous Button
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const currentButtons = step.config?.actionButtons || [];
                      const refreshButton = {
                        id: `button-${Date.now()}`,
                        label: 'Refresh Data',
                        targetStepId: '',
                        variant: 'contained',
                        color: 'info',
                        enabled: true,
                        showInHeader: true,
                        variableMappings: [
                          { variable: 'refreshRequested', value: 'true', valueType: 'static' }
                        ]
                      };
                      handleConfigChange('actionButtons', [...currentButtons, refreshButton]);
                    }}
                  >
                    🔄 Refresh Data Button
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const currentButtons = step.config?.actionButtons || [];
                      const acceptButton = {
                        id: `button-${Date.now()}`,
                        label: 'Accept',
                        targetStepId: '',
                        variant: 'contained',
                        color: 'success',
                        enabled: true,
                        showInHeader: false,
                        variableMappings: [
                          { variable: 'userChoice', value: 'accepted', valueType: 'static' },
                          { variable: 'acceptedBy', value: '{{userFullName}}', valueType: 'template' }
                        ]
                      };
                      handleConfigChange('actionButtons', [...currentButtons, acceptButton]);
                    }}
                  >
                    ✅ Accept Button
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const currentButtons = step.config?.actionButtons || [];
                      const rejectButton = {
                        id: `button-${Date.now()}`,
                        label: 'Reject',
                        targetStepId: '',
                        variant: 'contained',
                        color: 'error',
                        enabled: true,
                        showInHeader: false,
                        variableMappings: [
                          { variable: 'userChoice', value: 'rejected', valueType: 'static' },
                          { variable: 'rejectedBy', value: '{{userFullName}}', valueType: 'template' }
                        ]
                      };
                      handleConfigChange('actionButtons', [...currentButtons, rejectButton]);
                    }}
                  >
                    ❌ Reject Button
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderBranchLogicConfig = () => {
    // Initialize conditions array if it doesn't exist
    const conditions = step.config?.conditions || [
      {
        id: `condition-${Date.now()}`,
        variable: '',
        operator: 'exists',
        value: '',
        logicalOperator: 'AND' // For connecting to next condition
      }
    ];

    const addCondition = () => {
      const newConditions = [...conditions, {
        id: `condition-${Date.now()}`,
        variable: '',
        operator: 'exists',
        value: '',
        logicalOperator: 'AND'
      }];
      handleConfigChange('conditions', newConditions);
    };

    const removeCondition = (conditionId) => {
      const updatedConditions = conditions.filter(c => c.id !== conditionId);
      handleConfigChange('conditions', updatedConditions);
    };

    const updateCondition = (conditionId, field, value) => {
      const updatedConditions = conditions.map(c => 
        c.id === conditionId ? { ...c, [field]: value } : c
      );
      handleConfigChange('conditions', updatedConditions);
    };

    return (
      <Box>
        <Typography variant="body2" color="textSecondary" mb={2}>
          Configure multi-condition branching logic. Create complex rules using AND/OR operators.
        </Typography>
        
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Branch Conditions</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Branch Type Selection */}
              <FormControl fullWidth>
                <InputLabel>Branch Type</InputLabel>
                <Select
                  value={step.config?.branchType || 'custom'}
                  onChange={(e) => handleConfigChange('branchType', e.target.value)}
                >
                  <MenuItem value="custom">Custom Conditions</MenuItem>
                  <MenuItem value="confidence">Data Quality/Confidence</MenuItem>
                  <MenuItem value="fileType">File Type Routing</MenuItem>
                  <MenuItem value="errorHandling">Error Handling</MenuItem>
                </Select>
              </FormControl>

              {/* Conditions List */}
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Conditions</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addCondition}
                    variant="outlined"
                  >
                    Add Condition
                  </Button>
                </Box>

                {conditions.map((condition, index) => (
                  <Paper key={condition.id} sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Condition Header */}
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight="bold">
                          Condition {index + 1}
                        </Typography>
                        {conditions.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => removeCondition(condition.id)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Variable Input */}
                      <TextField
                        label="Variable Name"
                        value={condition.variable || ''}
                        onChange={(e) => updateCondition(condition.id, 'variable', e.target.value)}
                        fullWidth
                        placeholder="e.g., purchaseOrder, extractedData.confidence"
                        helperText="Variable to evaluate (supports dot notation for nested properties)"
                      />

                      {/* Operator Selection */}
                      <FormControl fullWidth>
                        <InputLabel>Operator</InputLabel>
                        <Select
                          value={condition.operator || 'exists'}
                          onChange={(e) => updateCondition(condition.id, 'operator', e.target.value)}
                        >
                          <MenuItem value="exists">Exists</MenuItem>
                          <MenuItem value="not_exists">Does Not Exist</MenuItem>
                          <MenuItem value="equals">Equals</MenuItem>
                          <MenuItem value="not_equals">Not Equals</MenuItem>
                          <MenuItem value="equals_null">Equals Null</MenuItem>
                          <MenuItem value="not_equals_null">Not Equals Null</MenuItem>
                          <MenuItem value="equals_true">Equals True</MenuItem>
                          <MenuItem value="equals_false">Equals False</MenuItem>
                          <MenuItem value="greater_than">Greater Than</MenuItem>
                          <MenuItem value="less_than">Less Than</MenuItem>
                          <MenuItem value="greater_than_or_equal">Greater Than or Equal</MenuItem>
                          <MenuItem value="less_than_or_equal">Less Than or Equal</MenuItem>
                          <MenuItem value="contains">Contains</MenuItem>
                          <MenuItem value="not_contains">Does Not Contain</MenuItem>
                          <MenuItem value="starts_with">Starts With</MenuItem>
                          <MenuItem value="ends_with">Ends With</MenuItem>
                          <MenuItem value="is_empty">Is Empty</MenuItem>
                          <MenuItem value="is_not_empty">Is Not Empty</MenuItem>
                          <MenuItem value="array_length_greater_than">Array Length Greater Than</MenuItem>
                          <MenuItem value="array_length_equals">Array Length Equals</MenuItem>
                        </Select>
                      </FormControl>

                      {/* Value Input - Show only for operators that need a value */}
                      {!['exists', 'not_exists', 'equals_null', 'not_equals_null', 'equals_true', 'equals_false', 'is_empty', 'is_not_empty'].includes(condition.operator) && (
                        <TextField
                          label="Comparison Value"
                          value={condition.value || ''}
                          onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
                          fullWidth
                          placeholder="Value to compare against"
                          helperText={
                            ['greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'array_length_greater_than', 'array_length_equals'].includes(condition.operator)
                              ? "Enter a number"
                              : "Enter text or value to compare"
                          }
                        />
                      )}

                      {/* Logical Operator - Show for all but the last condition */}
                      {index < conditions.length - 1 && (
                        <FormControl fullWidth>
                          <InputLabel>Logical Operator</InputLabel>
                          <Select
                            value={condition.logicalOperator || 'AND'}
                            onChange={(e) => updateCondition(condition.id, 'logicalOperator', e.target.value)}
                          >
                            <MenuItem value="AND">AND</MenuItem>
                            <MenuItem value="OR">OR</MenuItem>
                          </Select>
                          <FormHelperText>
                            How this condition connects to the next condition
                          </FormHelperText>
                        </FormControl>
                      )}
                    </Box>
                  </Paper>
                ))}

                {/* Condition Logic Preview */}
                {conditions.length > 0 && (
                  <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Logic Preview:
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {conditions.map((condition, index) => {
                        const conditionText = `${condition.variable || '[variable]'} ${condition.operator || 'exists'} ${
                          !['exists', 'not_exists', 'equals_null', 'not_equals_null', 'equals_true', 'equals_false', 'is_empty', 'is_not_empty'].includes(condition.operator) 
                            ? (condition.value || '[value]') 
                            : ''
                        }`.trim();
                        
                        return (
                          <span key={condition.id}>
                            {conditionText}
                            {index < conditions.length - 1 && (
                              <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                                {' '}{condition.logicalOperator || 'AND'}{' '}
                              </span>
                            )}
                          </span>
                        );
                      }).reduce((prev, curr, index) => index === 0 ? [curr] : [...prev, curr], [])}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Branch Routing</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Configure what happens when conditions pass or fail.
              </Typography>
              
              <TextField
                label="Success Path Label"
                value={step.config?.successLabel || 'PASS'}
                onChange={(e) => handleConfigChange('successLabel', e.target.value)}
                fullWidth
                helperText="Label for the right output (when conditions are met)"
              />
              
              <TextField
                label="Failure Path Label"
                value={step.config?.failureLabel || 'FAIL'}
                onChange={(e) => handleConfigChange('failureLabel', e.target.value)}
                fullWidth
                helperText="Label for the left output (when conditions are not met)"
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Quick Templates</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2" color="textSecondary" mb={1}>
                Apply common condition templates:
              </Typography>
              
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const template = [
                    {
                      id: `condition-${Date.now()}`,
                      variable: 'purchaseOrder',
                      operator: 'exists',
                      value: '',
                      logicalOperator: 'AND'
                    },
                    {
                      id: `condition-${Date.now() + 1}`,
                      variable: 'purchaseOrder',
                      operator: 'not_equals_null',
                      value: '',
                      logicalOperator: 'AND'
                    }
                  ];
                  handleConfigChange('conditions', template);
                }}
                sx={{ mb: 1 }}
              >
                Variable Exists & Not Null
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const template = [
                    {
                      id: `condition-${Date.now()}`,
                      variable: 'extractedData.confidence',
                      operator: 'greater_than',
                      value: '0.8',
                      logicalOperator: 'OR'
                    },
                    {
                      id: `condition-${Date.now() + 1}`,
                      variable: 'manualReview',
                      operator: 'equals_true',
                      value: '',
                      logicalOperator: 'AND'
                    }
                  ];
                  handleConfigChange('conditions', template);
                }}
                sx={{ mb: 1 }}
              >
                High Confidence OR Manual Review
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const template = [
                    {
                      id: `condition-${Date.now()}`,
                      variable: 'fileType',
                      operator: 'equals',
                      value: 'pdf',
                      logicalOperator: 'AND'
                    },
                    {
                      id: `condition-${Date.now() + 1}`,
                      variable: 'fileSize',
                      operator: 'less_than',
                      value: '5000000',
                      logicalOperator: 'AND'
                    }
                  ];
                  handleConfigChange('conditions', template);
                }}
              >
                PDF File Under 5MB
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const renderVariableOperationsConfig = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Configure variable manipulation operations to transform, create, and modify data throughout your workflow.
      </Typography>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🔧 Variable Operations</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Add multiple operations that will be executed in sequence. Each operation can create, modify, or transform variables.
            </Typography>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Variable Operations List
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const currentOperations = step.config?.operations || [];
                    const newOperation = {
                      id: `operation-${Date.now()}`,
                      type: 'assign_value',
                      targetVariable: '',
                      sourceVariable: '',
                      value: '',
                      enabled: true,
                      excludeFromPersistence: false
                    };
                    handleConfigChange('operations', [...currentOperations, newOperation]);
                  }}
                >
                  Add Operation
                </Button>
              </Box>
              
              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleOperationsDragEnd}
              >
                <SortableContext 
                  items={step.config?.operations || []}
                  strategy={verticalListSortingStrategy}
                >
                  {(step.config?.operations || []).map((operation, index) => (
                    <SortableVariableOperationCard
                      key={operation.id}
                      operation={operation}
                      index={index}
                      onOperationDelete={(idx) => {
                        const updatedOperations = step.config.operations.filter((_, i) => i !== idx);
                        handleConfigChange('operations', updatedOperations);
                      }}
                    >
                      <Box flexGrow={1}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="body2" fontWeight="bold">
                              Operation {index + 1}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={operation.enabled !== false}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, enabled: e.target.checked };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                />
                              }
                              label="Enabled"
                            />
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Operation Type</InputLabel>
                            <Select
                              value={operation.type || 'assign_value'}
                              onChange={(e) => {
                                const updatedOperations = [...step.config.operations];
                                let newOperation = { ...operation, type: e.target.value };
                                
                                // Initialize default configs for specific operation types
                                if (e.target.value === 'enrich_array') {
                                  newOperation.enrichmentConfig = {
                                    groupBy: 'productName',
                                    enrichments: [{
                                      sourceField: 'quantity',
                                      targetField: 'cumulativeQuantity',
                                      operation: 'sum'
                                    }]
                                  };
                                } else if (e.target.value === 'aggregate_array') {
                                  newOperation.aggregationConfig = {
                                    groupBy: 'productName',
                                    aggregations: [{
                                      sourceField: 'quantity',
                                      targetField: 'totalQuantity',
                                      operation: 'sum'
                                    }]
                                  };
                                } else if (e.target.value === 'calculate_fields') {
                                  newOperation.fieldCalculations = [{
                                    id: `calc-${Date.now()}`,
                                    fieldName: '',
                                    formula: '',
                                    overwriteExisting: false
                                  }];
                                } else if (e.target.value === 'convert_field_types') {
                                  newOperation.fieldConversions = [{
                                    id: `conv-${Date.now()}`,
                                    fieldName: '',
                                    targetType: 'number',
                                    defaultForNull: '',
                                    overwriteExisting: false
                                  }];
                                } else if (e.target.value === 'string_operation') {
                                  // Initialize with default string operation
                                  newOperation.stringOp = 'uppercase';
                                  newOperation.sourceVariable = '';
                                } else if (e.target.value === 'string_operation_array') {
                                  // Initialize with default string operation for arrays
                                  newOperation.stringOp = 'uppercase';
                                  newOperation.sourceVariable = '';
                                  newOperation.stringFields = [];
                                } else if (e.target.value === 'normalize_field') {
                                  // Initialize with default normalization config
                                  newOperation.normalizationConfig = {
                                    method: 'fuzzy',
                                    threshold: 0.8,
                                    targetField: '',
                                    outputField: '',
                                    manualMappings: []
                                  };
                                } else if (e.target.value === 'split_array_by_field') {
                                  // Initialize with default split config
                                  newOperation.splitByField = '';
                                } else if (e.target.value === 'parse_date_column') {
                                  // Initialize with default date parsing config
                                  newOperation.dateField = '';
                                  newOperation.dateFormat = '';
                                }
                                
                                updatedOperations[index] = newOperation;
                                handleConfigChange('operations', updatedOperations);
                              }}
                            >
                              <MenuItem value="assign_value">Assign Value</MenuItem>
                              <MenuItem value="assign_variable">Copy Variable</MenuItem>
                              <MenuItem value="set_null">Set to Null</MenuItem>
                              <MenuItem value="clear_variable">Clear Variable</MenuItem>
                              <MenuItem value="create_object">Create Object</MenuItem>
                              <MenuItem value="create_array">Create Empty Array</MenuItem>
                              <MenuItem value="append_to_array">Append to Array</MenuItem>
                              <MenuItem value="concat_arrays">🔗 Concat Arrays</MenuItem>
                              <MenuItem value="remove_from_array">Remove from Array</MenuItem>
                              <MenuItem value="add_to_object">Add Object Property</MenuItem>
                              <MenuItem value="remove_from_object">Remove Object Property</MenuItem>
                              <MenuItem value="merge_objects">Merge Objects</MenuItem>
                              <MenuItem value="convert_type">Convert Data Type</MenuItem>
                              <MenuItem value="string_operation">String Operation</MenuItem>
                              <MenuItem value="string_operation_array">📝 String Operations on Arrays</MenuItem>
                              <MenuItem value="math_operation">Math Operation</MenuItem>
                              <MenuItem value="date_create">Create Date</MenuItem>
                              <MenuItem value="date_format">Format Date</MenuItem>
                              <MenuItem value="date_calculate">Calculate Date</MenuItem>
                              <MenuItem value="date_combine">Combine Date + Time</MenuItem>
                              <MenuItem value="format_dates">📅 Format Dates in Arrays</MenuItem>
                              <MenuItem value="format_numbers">🔢 Format Numbers in Arrays</MenuItem>
                              <MenuItem value="generate_random">Generate Random</MenuItem>
                              <MenuItem value="conditional_assign">Conditional Assignment</MenuItem>
                              <MenuItem value="ai_transform">🤖 AI-Powered Transform</MenuItem>
                              <MenuItem value="combine_arrays">🔗 Combine Arrays</MenuItem>
                              <MenuItem value="aggregate_array">📊 Aggregate Array</MenuItem>
                              <MenuItem value="calculate_array">🧮 Calculate Array Values</MenuItem>
                              <MenuItem value="enrich_array">🔍 Enrich Array</MenuItem>
                              <MenuItem value="lookup_library">📚 Library Lookup</MenuItem>
                              <MenuItem value="transform_array">💫 Transform Array</MenuItem>
                              <MenuItem value="filter_array">🔍 Filter Array</MenuItem>
                              <MenuItem value="calculate_fields">🧮 Calculate Fields</MenuItem>
                              <MenuItem value="convert_field_types">🔄 Convert Field Types</MenuItem>
                              <MenuItem value="object_to_string_array">📋 Object to String Array</MenuItem>
                              <MenuItem value="base64_encode">🔐 Base64 Encode</MenuItem>
                              <MenuItem value="hash">🔑 Hash</MenuItem>
                              <MenuItem value="normalize_field">🏢 Normalize Field</MenuItem>
                              <MenuItem value="split_array_by_field">✂️ Split Array by Field</MenuItem>
                              <MenuItem value="parse_date_column">📅 Parse Date Column</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        {/* Target Variable - Always needed */}
                        <Grid item xs={operation.type === 'create_object' ? 12 : 6} sx={{ width: operation.type === 'create_object' ? '100%' : 'auto' }}>
                          <TextField
                            label="Target Variable"
                            value={operation.targetVariable || ''}
                            onChange={(e) => {
                              const updatedOperations = [...step.config.operations];
                              updatedOperations[index] = { ...operation, targetVariable: e.target.value };
                              handleConfigChange('operations', updatedOperations);
                            }}
                            fullWidth
                            placeholder="e.g., myVariable, user.name, result[0]"
                            helperText="Variable to store the result (supports dot notation)"
                          />
                        </Grid>
                        
                        {/* Dynamic fields based on operation type */}
                        {operation.type === 'assign_value' && (
                          <Grid item xs={6}>
                            <TextField
                              label="Value"
                              value={operation.value || ''}
                              onChange={(e) => {
                                const updatedOperations = [...step.config.operations];
                                updatedOperations[index] = { ...operation, value: e.target.value };
                                handleConfigChange('operations', updatedOperations);
                              }}
                              fullWidth
                              placeholder="Static value or {{variable}} reference"
                              helperText="Use {{variable}} for dynamic values"
                            />
                          </Grid>
                        )}
                        
                        {operation.type === 'assign_variable' && (
                          <Grid item xs={6}>
                            <TextField
                              label="Source Variable"
                              value={operation.sourceVariable || ''}
                              onChange={(e) => {
                                const updatedOperations = [...step.config.operations];
                                updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                handleConfigChange('operations', updatedOperations);
                              }}
                              fullWidth
                              placeholder="e.g., otherVariable, data.field"
                              helperText="Variable to copy from"
                            />
                          </Grid>
                        )}
                        
                        {operation.type === 'create_object' && (
                          <Grid item xs={12} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ width: '100%', mt: 1 }}>
                              <TextField
                                label="Object Properties (JSON)"
                                value={operation.objectData || '{}'}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, objectData: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                multiline
                                minRows={8}
                                maxRows={16}
                                placeholder='{"name": "{{userName}}", "age": 25, "active": true}'
                                helperText="JSON object structure with {{variable}} support"
                                InputProps={{
                                  style: {
                                    maxHeight: '400px',
                                    overflowY: 'auto'
                                  }
                                }}
                                sx={{
                                  width: '100%',
                                  '& .MuiInputBase-root': {
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    alignItems: 'flex-start'
                                  },
                                  '& textarea': {
                                    maxHeight: '360px',
                                    overflowY: 'auto !important'
                                  }
                                }}
                              />
                            </Box>
                          </Grid>
                        )}
                        
                        {(operation.type === 'append_to_array' || operation.type === 'remove_from_array') && (
                          <>
                            <Grid item xs={6}>
                              <TextField
                                label="Array Variable"
                                value={operation.arrayVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, arrayVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="arrayName"
                                helperText="Existing array variable"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label={operation.type === 'append_to_array' ? 'Value to Add' : 'Value/Index to Remove'}
                                value={operation.value || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, value: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder={operation.type === 'append_to_array' ? '{{newItem}}' : '0 or {{itemValue}}'}
                                helperText={operation.type === 'append_to_array' ? 'Value or variable to append' : 'Index number or value to remove'}
                              />
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'concat_arrays' && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                🔗 Concat Items Configuration
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Concatenate multiple arrays and/or individual items into a single flat array. Arrays are spread (flattened), objects/values are added as single elements.
                              </Typography>
                              
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  const updatedOperations = [...step.config.operations];
                                  const currentItems = operation.concatItems || [];
                                  updatedOperations[index] = {
                                    ...operation,
                                    concatItems: [
                                      ...currentItems,
                                      {
                                        id: `item-${Date.now()}`,
                                        type: 'variable',
                                        value: ''
                                      }
                                    ]
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                sx={{ mb: 2 }}
                              >
                                Add Item to Concat
                              </Button>
                              
                              {(operation.concatItems || []).map((item, itemIndex) => (
                                <Box key={item.id} sx={{ p: 2, mb: 2, backgroundColor: '#f0f8ff', borderRadius: 1, border: '1px solid #b3d9ff' }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="body2" fontWeight="bold">
                                      Item {itemIndex + 1}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const updatedOperations = [...step.config.operations];
                                        const updatedItems = [...operation.concatItems];
                                        updatedItems.splice(itemIndex, 1);
                                        updatedOperations[index] = {
                                          ...operation,
                                          concatItems: updatedItems
                                        };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  
                                  <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Item Type</InputLabel>
                                        <Select
                                          value={item.type || 'variable'}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            const updatedItems = [...operation.concatItems];
                                            updatedItems[itemIndex] = { ...item, type: e.target.value, value: '' };
                                            updatedOperations[index] = {
                                              ...operation,
                                              concatItems: updatedItems
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          label="Item Type"
                                        >
                                          <MenuItem value="variable">Variable Reference</MenuItem>
                                          <MenuItem value="object">Inline Object (JSON)</MenuItem>
                                          <MenuItem value="array">Inline Array (JSON)</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    
                                    <Grid item xs={8}>
                                      <TextField
                                        label={
                                          item.type === 'variable' ? 'Variable Name' :
                                          item.type === 'object' ? 'JSON Object' :
                                          'JSON Array'
                                        }
                                        value={item.value || ''}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          const updatedItems = [...operation.concatItems];
                                          updatedItems[itemIndex] = { ...item, value: e.target.value };
                                          updatedOperations[index] = {
                                            ...operation,
                                            concatItems: updatedItems
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                        fullWidth
                                        size="small"
                                        multiline={item.type !== 'variable'}
                                        rows={item.type !== 'variable' ? 3 : 1}
                                        placeholder={
                                          item.type === 'variable' ? 'items' :
                                          item.type === 'object' ? '{"trDate": "{{transactionDate}}", "trTime": "{{transactionTime}}"}' :
                                          '[{"item": 1}, {"item": 2}]'
                                        }
                                        helperText={
                                          item.type === 'variable' ? 'Variable name (array or object)' :
                                          item.type === 'object' ? 'Single object to add (supports {{variables}})' :
                                          'Array to spread/flatten (supports {{variables}})'
                                        }
                                      />
                                    </Grid>
                                  </Grid>
                                  
                                  <Box sx={{ mt: 1, p: 1, backgroundColor: '#e8f4f8', borderRadius: 1 }}>
                                    <Typography variant="caption" color="textSecondary">
                                      {item.type === 'variable' && (
                                        <>📌 If variable is an array, its items will be spread. If it's an object/value, it will be added as single element.</>
                                      )}
                                      {item.type === 'object' && (
                                        <>📌 This object will be added as a single element to the result array.</>
                                      )}
                                      {item.type === 'array' && (
                                        <>📌 This array's items will be spread/flattened into the result array.</>
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                              
                              <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                                <Typography variant="body2" fontWeight="bold" gutterBottom>
                                  💡 Example for your SALES ORDER structure:
                                </Typography>
                                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                  Item 1: Type = "Inline Object", Value =<br/>
                                  {`{"trDate": "{{transactionDate}}", "trTime": "{{transactionTime}}", "trType": "{{transactionType}}", "custBillTo": "{{custBillTo}}"}`}<br/><br/>
                                  Item 2: Type = "Variable Reference", Value = items<br/><br/>
                                  Result: [headerObject, ...allItemsFromArray]
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                        
                        {(operation.type === 'add_to_object' || operation.type === 'remove_from_object') && (
                          <>
                            <Grid item xs={4}>
                              <TextField
                                label="Object Variable"
                                value={operation.objectVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, objectVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="objectName"
                                helperText="Target object"
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                label="Property Key"
                                value={operation.propertyKey || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, propertyKey: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="propertyName"
                                helperText="Object property name"
                              />
                            </Grid>
                            {operation.type === 'add_to_object' && (
                              <Grid item xs={4}>
                                <TextField
                                  label="Property Value"
                                  value={operation.propertyValue || ''}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, propertyValue: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                  fullWidth
                                  placeholder="{{value}}"
                                  helperText="Value to set"
                                />
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {operation.type === 'merge_objects' && (
                          <Grid item xs={6}>
                            <TextField
                              label="Source Objects (comma-separated)"
                              value={operation.sourceObjects || ''}
                              onChange={(e) => {
                                const updatedOperations = [...step.config.operations];
                                updatedOperations[index] = { ...operation, sourceObjects: e.target.value };
                                handleConfigChange('operations', updatedOperations);
                              }}
                              fullWidth
                              placeholder="obj1, obj2, obj3"
                              helperText="Objects to merge (right overwrites left)"
                            />
                          </Grid>
                        )}
                        
                        {operation.type === 'convert_type' && (
                          <>
                            <Grid item xs={3}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <FormControl fullWidth>
                                <InputLabel>Convert To</InputLabel>
                                <Select
                                  value={operation.convertTo || 'string'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, convertTo: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="string">String</MenuItem>
                                  <MenuItem value="number">Number</MenuItem>
                                  <MenuItem value="boolean">Boolean</MenuItem>
                                  <MenuItem value="array">Array</MenuItem>
                                  <MenuItem value="json">JSON Object</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'string_operation' && (
                          <>
                            <Grid item xs={3}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <FormControl fullWidth>
                                <InputLabel>String Operation</InputLabel>
                                <Select
                                  value={operation.stringOp || 'uppercase'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, stringOp: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="uppercase">Uppercase</MenuItem>
                                  <MenuItem value="lowercase">Lowercase</MenuItem>
                                  <MenuItem value="trim">Trim Whitespace</MenuItem>
                                  <MenuItem value="substring">Extract Substring</MenuItem>
                                  <MenuItem value="replace">Find & Replace</MenuItem>
                                  <MenuItem value="split">Split into Array</MenuItem>
                                  <MenuItem value="concat">Concatenate</MenuItem>
                                  <MenuItem value="split_after">✂️ Extract After Delimiter</MenuItem>
                                  <MenuItem value="split_before">✂️ Extract Before Delimiter</MenuItem>
                                  <MenuItem value="format_time">⏰ Format Time</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {operation.stringOp === 'format_time' && (
                              <Grid item xs={6}>
                                <FormControl fullWidth>
                                  <InputLabel>Time Format</InputLabel>
                                  <Select
                                    value={operation.timeFormat || 'HH:MM:SS'}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, timeFormat: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                  >
                                    <MenuItem value="HH:MM:SS">HH:MM:SS (e.g., 16:48:11)</MenuItem>
                                    <MenuItem value="HH:MM">HH:MM (e.g., 16:48)</MenuItem>
                                    <MenuItem value="HH.MM.SS">HH.MM.SS (e.g., 16.48.11)</MenuItem>
                                    <MenuItem value="HH.MM">HH.MM (e.g., 16.48)</MenuItem>
                                    <MenuItem value="HH-MM-SS">HH-MM-SS (e.g., 16-48-11)</MenuItem>
                                    <MenuItem value="HH-MM">HH-MM (e.g., 16-48)</MenuItem>
                                    <MenuItem value="HHMM">HHMM (e.g., 1648)</MenuItem>
                                  </Select>
                                  <FormHelperText>Convert HHMMSS format to readable time</FormHelperText>
                                </FormControl>
                              </Grid>
                            )}
                            {operation.stringOp === 'format_time' && (
                              <>
                                <Grid item xs={6}>
                                  <TextField
                                    label="Convert to Timezone (optional)"
                                    value={operation.convertToTimezone || ''}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, convertToTimezone: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                    fullWidth
                                    placeholder="+14:00 or -05:00"
                                    helperText="Enter timezone offset to convert time (e.g., +14:00 for UTC+14)"
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={operation.includeTimezone || false}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = { ...operation, includeTimezone: e.target.checked };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                      />
                                    }
                                    label="Include Timezone"
                                  />
                                </Grid>
                                {operation.includeTimezone && (
                                  <>
                                    <Grid item xs={3}>
                                      <FormControl fullWidth>
                                        <InputLabel>Timezone Source</InputLabel>
                                        <Select
                                          value={operation.timezoneType || 'offset'}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            updatedOperations[index] = { ...operation, timezoneType: e.target.value };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                        >
                                          <MenuItem value="offset">Server Offset (e.g., +07:00)</MenuItem>
                                          <MenuItem value="abbr">Server Abbreviation (e.g., WIB)</MenuItem>
                                          <MenuItem value="name">Server Full Name</MenuItem>
                                          <MenuItem value="custom">Custom Timezone</MenuItem>
                                        </Select>
                                        <FormHelperText>Timezone to append</FormHelperText>
                                      </FormControl>
                                    </Grid>
                                    {operation.timezoneType === 'custom' && (
                                      <Grid item xs={3}>
                                        <TextField
                                          label="Custom Timezone"
                                          value={operation.customTimezone || ''}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            updatedOperations[index] = { ...operation, customTimezone: e.target.value };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          fullWidth
                                          placeholder="+05:30 or EST"
                                          helperText="Enter timezone offset or abbreviation"
                                        />
                                      </Grid>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                            {(operation.stringOp === 'split_after' || operation.stringOp === 'split_before') && (
                              <>
                                <Grid item xs={3}>
                                  <TextField
                                    label="Delimiter"
                                    value={operation.delimiter || ''}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, delimiter: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                    fullWidth
                                    placeholder="- or @ or ."
                                    helperText="Character or string to split on"
                                  />
                                </Grid>
                                <Grid item xs={2}>
                                  <FormControl fullWidth>
                                    <InputLabel>Occurrence</InputLabel>
                                    <Select
                                      value={operation.occurrence || 'first'}
                                      onChange={(e) => {
                                        const updatedOperations = [...step.config.operations];
                                        updatedOperations[index] = { ...operation, occurrence: e.target.value };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                    >
                                      <MenuItem value="first">First</MenuItem>
                                      <MenuItem value="last">Last</MenuItem>
                                      <MenuItem value="1">1st</MenuItem>
                                      <MenuItem value="2">2nd</MenuItem>
                                      <MenuItem value="3">3rd</MenuItem>
                                      <MenuItem value="4">4th</MenuItem>
                                    </Select>
                                    <FormHelperText>Which occurrence to use</FormHelperText>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={2}>
                                  <FormControl fullWidth>
                                    <InputLabel>If Not Found</InputLabel>
                                    <Select
                                      value={operation.fallback || 'original'}
                                      onChange={(e) => {
                                        const updatedOperations = [...step.config.operations];
                                        updatedOperations[index] = { ...operation, fallback: e.target.value };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                    >
                                      <MenuItem value="original">Return Original</MenuItem>
                                      <MenuItem value="empty">Return Empty</MenuItem>
                                    </Select>
                                    <FormHelperText>Fallback behavior</FormHelperText>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={2}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={operation.includeDelimiter || false}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = { ...operation, includeDelimiter: e.target.checked };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                      />
                                    }
                                    label="Include Delimiter"
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <Alert severity="info" sx={{ py: 1 }}>
                                    <Typography variant="body2">
                                      <strong>{operation.stringOp === 'split_after' ? 'Extract After:' : 'Extract Before:'}</strong> 
                                      {operation.stringOp === 'split_after' 
                                        ? ` "Warehouse-WHALUN" with delimiter "-" → "WHALUN"`
                                        : ` "Warehouse-WHALUN" with delimiter "-" → "Warehouse"`}
                                    </Typography>
                                  </Alert>
                                </Grid>
                              </>
                            )}
                            {(operation.stringOp === 'substring' || operation.stringOp === 'replace' || operation.stringOp === 'split' || operation.stringOp === 'concat') && (
                              <Grid item xs={6}>
                                <TextField
                                  label={
                                    operation.stringOp === 'substring' ? 'Start,Length (e.g., 0,5)' :
                                    operation.stringOp === 'replace' ? 'Find,Replace (e.g., old,new)' :
                                    operation.stringOp === 'split' ? 'Delimiter (e.g., ,)' :
                                    'Text to Concatenate'
                                  }
                                  value={operation.stringParams || ''}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, stringParams: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                  fullWidth
                                />
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {operation.type === 'math_operation' && (
                          <>
                            <Grid item xs={2}>
                              <TextField
                                label="Variable A"
                                value={operation.variableA || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, variableA: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <FormControl fullWidth>
                                <InputLabel>Operation</InputLabel>
                                <Select
                                  value={operation.mathOp || 'add'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, mathOp: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="add">Add (+)</MenuItem>
                                  <MenuItem value="subtract">Subtract (-)</MenuItem>
                                  <MenuItem value="multiply">Multiply (×)</MenuItem>
                                  <MenuItem value="divide">Divide (÷)</MenuItem>
                                  <MenuItem value="modulo">Modulo (%)</MenuItem>
                                  <MenuItem value="power">Power (^)</MenuItem>
                                  <MenuItem value="round">Round</MenuItem>
                                  <MenuItem value="ceil">Round Up</MenuItem>
                                  <MenuItem value="floor">Round Down</MenuItem>
                                  <MenuItem value="abs">Absolute</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {!['round', 'ceil', 'floor', 'abs'].includes(operation.mathOp) && (
                              <Grid item xs={2}>
                                <TextField
                                  label="Variable B / Value"
                                  value={operation.variableB || ''}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, variableB: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                  fullWidth
                                />
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {operation.type === 'format_dates' && (
                          <>
                            <Grid item xs={6}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="extractedProducts"
                                helperText="Variable containing array of objects with date fields"
                              />
                            </Grid>
                            
                            <Grid item xs={8}>
                              <TextField
                                label="Date Fields to Format"
                                value={operation._dateFieldsRaw ?? operation.dateFields?.join(', ') ?? 'expiryDate'}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = {
                                    ...operation,
                                    _dateFieldsRaw: e.target.value,
                                    dateFields: e.target.value.split(',').map(f => f.trim()).filter(f => f)
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                onBlur={(e) => {
                                  const fields = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                                  const updatedOperations = [...step.config.operations];
                                  const { _dateFieldsRaw, ...cleanOp } = operation;
                                  updatedOperations[index] = { ...cleanOp, dateFields: fields };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="expiryDate, manufacturingDate, deliveryDate"
                                helperText="Comma-separated list of date field names to format"
                              />
                            </Grid>
                            
                            <Grid item xs={4}>
                              <FormControl fullWidth>
                                <InputLabel>Output Format</InputLabel>
                                <Select
                                  value={operation.outputFormat || 'dd.mm.yyyy'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, outputFormat: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="dd.mm.yyyy">dd.mm.yyyy</MenuItem>
                                  <MenuItem value="dd/mm/yyyy">dd/mm/yyyy</MenuItem>
                                  <MenuItem value="mm/dd/yyyy">mm/dd/yyyy (US)</MenuItem>
                                  <MenuItem value="yyyy-mm-dd">yyyy-mm-dd (ISO)</MenuItem>
                                  <MenuItem value="dd-mm-yyyy">dd-mm-yyyy</MenuItem>
                                  <MenuItem value="mm-dd-yyyy">mm-dd-yyyy</MenuItem>
                                  <MenuItem value="dd mmm yyyy">dd mmm yyyy</MenuItem>
                                  <MenuItem value="mmm dd, yyyy">mmm dd, yyyy</MenuItem>
                                  <MenuItem value="mmmm dd, yyyy">mmmm dd, yyyy</MenuItem>
                                </Select>
                                <FormHelperText>Target date format</FormHelperText>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                                  📅 Input → Output Format Examples:
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                                  {(() => {
                                    const format = operation.outputFormat || 'dd.mm.yyyy';
                                    const examples = {
                                      'dd.mm.yyyy': '2028-04-15 → 15.04.2028',
                                      'dd/mm/yyyy': '2028-04-15 → 15/04/2028',
                                      'mm/dd/yyyy': '2028-04-15 → 04/15/2028',
                                      'yyyy-mm-dd': '15.04.2028 → 2028-04-15',
                                      'dd-mm-yyyy': '2028-04-15 → 15-04-2028',
                                      'mm-dd-yyyy': '2028-04-15 → 04-15-2028',
                                      'dd mmm yyyy': '2028-04-15 → 15 Apr 2028',
                                      'mmm dd, yyyy': '2028-04-15 → Apr 15, 2028',
                                      'mmmm dd, yyyy': '2028-04-15 → April 15, 2028'
                                    };
                                    return (
                                      <>
                                        • <strong>AI Output:</strong> {examples[format]}<br/>
                                        • <strong>MMM-YY:</strong> NOV-27 → {format === 'dd.mm.yyyy' ? '30.11.2027' : format === 'yyyy-mm-dd' ? '2027-11-30' : '30/11/2027'}<br/>
                                        • <strong>MM.YY:</strong> 11.27 → {format === 'dd.mm.yyyy' ? '30.11.2027' : format === 'yyyy-mm-dd' ? '2027-11-30' : '30/11/2027'}<br/>
                                        • <strong>Plus many other formats automatically detected</strong>
                                      </>
                                    );
                                  })()}
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}

                        {operation.type === 'format_numbers' && (
                          <>
                            <Grid item xs={6}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="cleanedMasterData"
                                helperText="Variable containing array of objects with numeric fields"
                              />
                            </Grid>

                            <Grid item xs={8}>
                              <TextField
                                label="Numeric Fields to Format"
                                value={operation._numericFieldsRaw ?? operation.numericFields?.join(', ') ?? ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = {
                                    ...operation,
                                    _numericFieldsRaw: e.target.value,
                                    numericFields: e.target.value.split(',').map(f => f.trim()).filter(f => f)
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                onBlur={(e) => {
                                  const fields = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                                  const updatedOperations = [...step.config.operations];
                                  const { _numericFieldsRaw, ...cleanOp } = operation;
                                  updatedOperations[index] = { ...cleanOp, numericFields: fields };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="unitPrice, remainingValue, totalAmount"
                                helperText="Comma-separated list of numeric field names to format"
                              />
                            </Grid>

                            <Grid item xs={4}>
                              <TextField
                                label="Decimal Places"
                                type="number"
                                value={operation.decimalPlaces !== undefined ? operation.decimalPlaces : 2}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, decimalPlaces: parseInt(e.target.value) || 0 };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                inputProps={{ min: 0, max: 10 }}
                                helperText="Number of decimal places"
                              />
                            </Grid>

                            <Grid item xs={4}>
                              <FormControl fullWidth>
                                <InputLabel>Rounding Mode</InputLabel>
                                <Select
                                  value={operation.roundingMode || 'round'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, roundingMode: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="round">Round (standard)</MenuItem>
                                  <MenuItem value="floor">Floor (round down)</MenuItem>
                                  <MenuItem value="ceil">Ceil (round up)</MenuItem>
                                </Select>
                                <FormHelperText>How to handle rounding</FormHelperText>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                                  🔢 Examples (with {operation.decimalPlaces !== undefined ? operation.decimalPlaces : 2} decimal places, {operation.roundingMode || 'round'}):
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                                  {(() => {
                                    const dp = operation.decimalPlaces !== undefined ? operation.decimalPlaces : 2;
                                    const mode = operation.roundingMode || 'round';
                                    const roundFn = (v) => {
                                      const f = Math.pow(10, dp);
                                      if (mode === 'floor') return Math.floor(v * f) / f;
                                      if (mode === 'ceil') return Math.ceil(v * f) / f;
                                      return Math.round(v * f) / f;
                                    };
                                    return (
                                      <>
                                        • 0.11000000000000001 → <strong>{roundFn(0.11000000000000001)}</strong><br/>
                                        • 1753.716 → <strong>{roundFn(1753.716)}</strong><br/>
                                        • 8768.555 → <strong>{roundFn(8768.555)}</strong><br/>
                                        • 2.2 → <strong>{roundFn(2.2)}</strong>
                                      </>
                                    );
                                  })()}
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'string_operation_array' && (
                          <>
                            <Grid item xs={6}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="extractedProducts"
                                helperText="Variable containing array of objects with string fields"
                              />
                            </Grid>
                            
                            <Grid item xs={8}>
                              <TextField
                                label="String Fields to Process"
                                value={operation._stringFieldsRaw ?? operation.stringFields?.join(', ') ?? ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = {
                                    ...operation,
                                    _stringFieldsRaw: e.target.value,
                                    stringFields: e.target.value.split(',').map(f => f.trim()).filter(f => f)
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                onBlur={(e) => {
                                  const fields = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                                  const updatedOperations = [...step.config.operations];
                                  const { _stringFieldsRaw, ...cleanOp } = operation;
                                  updatedOperations[index] = { ...cleanOp, stringFields: fields };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="name, description, category"
                                helperText="Comma-separated list of string field names to process"
                              />
                            </Grid>
                            
                            <Grid item xs={8}>
                              <TextField
                                label="Output Fields (Optional)"
                                value={operation._outputFieldsRaw ?? operation.outputFields?.join(', ') ?? ''}
                                onChange={(e) => {
                                  const fields = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = {
                                    ...operation,
                                    _outputFieldsRaw: e.target.value,
                                    outputFields: fields.length > 0 ? fields : null
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                onBlur={(e) => {
                                  const fields = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                                  const updatedOperations = [...step.config.operations];
                                  const { _outputFieldsRaw, ...cleanOp } = operation;
                                  updatedOperations[index] = { ...cleanOp, outputFields: fields.length > 0 ? fields : null };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="supplier, productName, category"
                                helperText="⚡ NEW: Save results to new fields instead of replacing source fields. Must match order of String Fields above. Leave empty to replace source fields (default behavior)."
                              />
                            </Grid>
                            
                            <Grid item xs={4}>
                              <FormControl fullWidth>
                                <InputLabel>String Operation</InputLabel>
                                <Select
                                  value={operation.stringOp || 'trim'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, stringOp: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="trim">Trim Whitespace</MenuItem>
                                  <MenuItem value="uppercase">Uppercase</MenuItem>
                                  <MenuItem value="lowercase">Lowercase</MenuItem>
                                  <MenuItem value="substring">Extract Substring</MenuItem>
                                  <MenuItem value="replace">Find & Replace</MenuItem>
                                  <MenuItem value="concat">Concatenate</MenuItem>
                                  <MenuItem value="split_after">✂️ Extract After Delimiter</MenuItem>
                                  <MenuItem value="split_before">✂️ Extract Before Delimiter</MenuItem>
                                  <MenuItem value="format_time">⏰ Format Time</MenuItem>
                                </Select>
                                <FormHelperText>Operation to apply to string fields</FormHelperText>
                              </FormControl>
                            </Grid>
                            
                            {operation.stringOp === 'format_time' && (
                              <Grid item xs={6}>
                                <FormControl fullWidth>
                                  <InputLabel>Time Format</InputLabel>
                                  <Select
                                    value={operation.timeFormat || 'HH:MM:SS'}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, timeFormat: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                  >
                                    <MenuItem value="HH:MM:SS">HH:MM:SS (e.g., 16:48:11)</MenuItem>
                                    <MenuItem value="HH:MM">HH:MM (e.g., 16:48)</MenuItem>
                                    <MenuItem value="HH.MM.SS">HH.MM.SS (e.g., 16.48.11)</MenuItem>
                                    <MenuItem value="HH.MM">HH.MM (e.g., 16.48)</MenuItem>
                                    <MenuItem value="HH-MM-SS">HH-MM-SS (e.g., 16-48-11)</MenuItem>
                                    <MenuItem value="HH-MM">HH-MM (e.g., 16-48)</MenuItem>
                                    <MenuItem value="HHMM">HHMM (e.g., 1648)</MenuItem>
                                  </Select>
                                  <FormHelperText>Convert HHMMSS format to readable time</FormHelperText>
                                </FormControl>
                              </Grid>
                            )}
                            
                            {operation.stringOp === 'format_time' && (
                              <>
                                <Grid item xs={6}>
                                  <TextField
                                    label="Convert to Timezone (optional)"
                                    value={operation.convertToTimezone || ''}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, convertToTimezone: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                    fullWidth
                                    placeholder="+14:00 or -05:00"
                                    helperText="Enter timezone offset to convert time (e.g., +14:00 for UTC+14)"
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={operation.includeTimezone || false}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = { ...operation, includeTimezone: e.target.checked };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                      />
                                    }
                                    label="Include Timezone"
                                  />
                                </Grid>
                                {operation.includeTimezone && (
                                  <>
                                    <Grid item xs={3}>
                                      <FormControl fullWidth>
                                        <InputLabel>Timezone Source</InputLabel>
                                        <Select
                                          value={operation.timezoneType || 'offset'}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            updatedOperations[index] = { ...operation, timezoneType: e.target.value };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                        >
                                          <MenuItem value="offset">Server Offset (e.g., +07:00)</MenuItem>
                                          <MenuItem value="abbr">Server Abbreviation (e.g., WIB)</MenuItem>
                                          <MenuItem value="name">Server Full Name</MenuItem>
                                          <MenuItem value="custom">Custom Timezone</MenuItem>
                                        </Select>
                                        <FormHelperText>Timezone to append</FormHelperText>
                                      </FormControl>
                                    </Grid>
                                    {operation.timezoneType === 'custom' && (
                                      <Grid item xs={3}>
                                        <TextField
                                          label="Custom Timezone"
                                          value={operation.customTimezone || ''}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            updatedOperations[index] = { ...operation, customTimezone: e.target.value };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          fullWidth
                                          placeholder="+05:30 or EST"
                                          helperText="Enter timezone offset or abbreviation"
                                        />
                                      </Grid>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                            
                            {(operation.stringOp === 'split_after' || operation.stringOp === 'split_before') && (
                              <>
                                <Grid item xs={3}>
                                  <TextField
                                    label="Delimiter"
                                    value={operation.delimiter || ''}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, delimiter: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                    fullWidth
                                    placeholder="- or @ or ."
                                    helperText="Character or string to split on"
                                  />
                                </Grid>
                                <Grid item xs={2}>
                                  <FormControl fullWidth>
                                    <InputLabel>Occurrence</InputLabel>
                                    <Select
                                      value={operation.occurrence || 'first'}
                                      onChange={(e) => {
                                        const updatedOperations = [...step.config.operations];
                                        updatedOperations[index] = { ...operation, occurrence: e.target.value };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                    >
                                      <MenuItem value="first">First</MenuItem>
                                      <MenuItem value="last">Last</MenuItem>
                                      <MenuItem value="1">1st</MenuItem>
                                      <MenuItem value="2">2nd</MenuItem>
                                      <MenuItem value="3">3rd</MenuItem>
                                      <MenuItem value="4">4th</MenuItem>
                                    </Select>
                                    <FormHelperText>Which occurrence to use</FormHelperText>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={2}>
                                  <FormControl fullWidth>
                                    <InputLabel>If Not Found</InputLabel>
                                    <Select
                                      value={operation.fallback || 'original'}
                                      onChange={(e) => {
                                        const updatedOperations = [...step.config.operations];
                                        updatedOperations[index] = { ...operation, fallback: e.target.value };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                    >
                                      <MenuItem value="original">Return Original</MenuItem>
                                      <MenuItem value="empty">Return Empty</MenuItem>
                                    </Select>
                                    <FormHelperText>Fallback behavior</FormHelperText>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={2}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={operation.includeDelimiter || false}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = { ...operation, includeDelimiter: e.target.checked };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                      />
                                    }
                                    label="Include Delimiter"
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <Alert severity="info" sx={{ py: 1 }}>
                                    <Typography variant="body2">
                                      <strong>{operation.stringOp === 'split_after' ? 'Extract After:' : 'Extract Before:'}</strong> 
                                      {operation.stringOp === 'split_after' 
                                        ? ` "Warehouse-WHALUN" with delimiter "-" → "WHALUN"`
                                        : ` "Warehouse-WHALUN" with delimiter "-" → "Warehouse"`}
                                    </Typography>
                                  </Alert>
                                </Grid>
                              </>
                            )}
                            
                            {(operation.stringOp === 'substring') && (
                              <>
                                <Grid item xs={3}>
                                  <TextField
                                    label="Start Position"
                                    type="number"
                                    value={operation.startPos || 0}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, startPos: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    label="End Position"
                                    type="number"
                                    value={operation.endPos || ''}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, endPos: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                    fullWidth
                                    placeholder="Leave empty for end of string"
                                  />
                                </Grid>
                              </>
                            )}
                            
                            {(operation.stringOp === 'replace') && (
                              <>
                                <Grid item xs={3}>
                                  <TextField
                                    label="Find Text"
                                    value={operation.findText || ''}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, findText: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <TextField
                                    label="Replace With"
                                    value={operation.replaceText || ''}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, replaceText: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                    fullWidth
                                  />
                                </Grid>
                              </>
                            )}
                            
                            {(operation.stringOp === 'concat') && (
                              <Grid item xs={6}>
                                <TextField
                                  label="Text to Concatenate"
                                  value={operation.concatValue || ''}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, concatValue: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                  fullWidth
                                  placeholder="Text or {{variableName}}"
                                  helperText="Text to append to each field (supports template variables)"
                                />
                              </Grid>
                            )}
                            
                            <Grid item xs={12}>
                              <Box sx={{ p: 2, backgroundColor: '#f0f9ff', borderRadius: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                                  📝 String Operation Examples:
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                                  {(() => {
                                    const stringOp = operation.stringOp || 'trim';
                                    const examples = {
                                      'trim': '&quot;  Apple  &quot; → &quot;Apple&quot;',
                                      'uppercase': '&quot;apple&quot; → &quot;APPLE&quot;',
                                      'lowercase': '&quot;APPLE&quot; → &quot;apple&quot;',
                                      'substring': '&quot;Hello World&quot; → &quot;Hello&quot; (0-5)',
                                      'replace': '&quot;Hello World&quot; → &quot;Hello Universe&quot; (World→Universe)',
                                      'concat': '&quot;Apple&quot; → &quot;Apple - Fresh&quot; (+ &quot; - Fresh&quot;)',
                                      'split_after': '&quot;Warehouse-WHALUN&quot; → &quot;WHALUN&quot; (after &quot;-&quot;)',
                                      'split_before': '&quot;Warehouse-WHALUN&quot; → &quot;Warehouse&quot; (before &quot;-&quot;)',
                                      'format_time': '&quot;164811&quot; → &quot;16:48:11&quot; (HHMMSS → HH:MM:SS)'
                                    };
                                    return (
                                      <>
                                        • <strong>{stringOp} operation:</strong> {examples[stringOp]}<br/>
                                        • <strong>Processes all specified fields</strong> in each array item<br/>
                                        • <strong>Works with objects and nested arrays</strong> recursively
                                      </>
                                    );
                                  })()}
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'object_to_string_array' && (
                          <>
                            <Grid item xs={12}>
                              <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                  <strong>Object to String Array:</strong> Extract a specific field from each object in an array and create a simple array of those values.
                                  <br />
                                  Example: [{'{'}name: "A"{'}'}, {'{'}name: "B"{'}'}] → ["A", "B"]
                                </Typography>
                              </Alert>
                            </Grid>
                            
                            <Grid item xs={6}>
                              <TextField
                                label="Source Array Variable"
                                value={operation.objectToStringConfig?.sourceArray || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = {
                                    ...operation,
                                    objectToStringConfig: {
                                      ...operation.objectToStringConfig,
                                      sourceArray: e.target.value
                                    }
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="myArrayOfObjects"
                                helperText="Variable containing the array of objects"
                              />
                            </Grid>
                            
                            <Grid item xs={6}>
                              <TextField
                                label="Field to Extract"
                                value={operation.objectToStringConfig?.fieldName || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = {
                                    ...operation,
                                    objectToStringConfig: {
                                      ...operation.objectToStringConfig,
                                      fieldName: e.target.value
                                    }
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="official_description, name, id, etc."
                                helperText="Name of the field to extract from each object"
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                  📋 Example:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', mb: 1 }}>
                                  <strong>Input:</strong> [{'{'}description: "abc", official_description: "ABCD"{'}'}, {'{'}description: "efg", official_description: "EFG"{'}'}]
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                  <strong>Field:</strong> official_description
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                  <strong>Output:</strong> ["ABCD", "EFG"]
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'base64_encode' && (
                          <>
                            <Grid item xs={6}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                helperText="Variable containing the data to encode to base64"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={operation.compressWithGzip || false}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, compressWithGzip: e.target.checked };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                  />
                                }
                                label="Compress with Gzip before encoding"
                              />
                              <Typography variant="caption" display="block" sx={{ ml: 4, mt: 0.5, color: 'text.secondary' }}>
                                Applies gzip compression before base64 encoding (like PHP's gzencode)
                              </Typography>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'hash' && (
                          <>
                            <Grid item xs={6}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                helperText="Variable containing the data to hash"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth>
                                <InputLabel>Hash Algorithm</InputLabel>
                                <Select
                                  value={operation.hashAlgorithm || 'sha256'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, hashAlgorithm: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="md5">MD5</MenuItem>
                                  <MenuItem value="sha1">SHA1</MenuItem>
                                  <MenuItem value="sha256">SHA256 (default)</MenuItem>
                                  <MenuItem value="sha512">SHA512</MenuItem>
                                </Select>
                              </FormControl>
                              <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                Creates a cryptographic hash of the source data (like PHP's sha1, md5, etc.)
                              </Typography>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'normalize_field' && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                🏢 Supplier Name Normalization Configuration
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Normalize similar supplier names using fuzzy matching or AI to group variations like "ABC Corp" and "ABC Corporation" together.
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="e.g., salesData"
                                helperText="Array variable containing the data to normalize"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Target Field"
                                value={operation.normalizationConfig?.targetField || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = {
                                    ...operation,
                                    normalizationConfig: {
                                      ...operation.normalizationConfig,
                                      targetField: e.target.value
                                    }
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="e.g., supplierName"
                                helperText="Field name to normalize"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Output Field (Optional)"
                                value={operation.normalizationConfig?.outputField || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = {
                                    ...operation,
                                    normalizationConfig: {
                                      ...operation.normalizationConfig,
                                      outputField: e.target.value
                                    }
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="e.g., supplierNameNormalized"
                                helperText="Leave empty to overwrite target field"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <FormControl fullWidth>
                                <InputLabel>Normalization Method</InputLabel>
                                <Select
                                  value={operation.normalizationConfig?.method || 'fuzzy'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = {
                                      ...operation,
                                      normalizationConfig: {
                                        ...operation.normalizationConfig,
                                        method: e.target.value
                                      }
                                    };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="fuzzy">Fuzzy Matching</MenuItem>
                                  <MenuItem value="ai">AI-Powered</MenuItem>
                                  <MenuItem value="manual">Manual Mappings Only</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {operation.normalizationConfig?.method === 'fuzzy' && (
                              <Grid item xs={6}>
                                <TextField
                                  label="Similarity Threshold"
                                  type="number"
                                  value={operation.normalizationConfig?.threshold || 0.8}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = {
                                      ...operation,
                                      normalizationConfig: {
                                        ...operation.normalizationConfig,
                                        threshold: parseFloat(e.target.value)
                                      }
                                    };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                  fullWidth
                                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                                  helperText="0.0 (loose) to 1.0 (exact match)"
                                />
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {operation.type === 'split_array_by_field' && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                ✂️ Split Array by Field Configuration
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Split an array into groups based on a field value. For example, split sales data by supplier to create separate arrays for each supplier.
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="e.g., salesData"
                                helperText="Array variable to split into groups"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Split By Field"
                                value={operation.splitByField || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, splitByField: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="e.g., supplierName"
                                helperText="Field name to group by"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                Output will be an object where keys are unique field values and values are arrays of matching items.
                                <br />
                                Example: If splitting by "supplierName", result = &#123;"Supplier A": [...], "Supplier B": [...]&#125;
                              </Typography>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'parse_date_column' && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                📅 Parse Date Column Configuration
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Parse date strings in an array and add extracted date components (year, month, day, timestamp) for aggregation and filtering.
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="e.g., salesData"
                                helperText="Array variable containing date values"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Date Field"
                                value={operation.dateField || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, dateField: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="e.g., orderDate"
                                helperText="Field name containing date strings"
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Date Format (Optional)"
                                value={operation.dateFormat || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, dateFormat: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="e.g., YYYY-MM-DD"
                                helperText="Leave empty for auto-detection"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                Adds new fields to each item: fieldName_parsed (ISO string), fieldName_year, fieldName_month, fieldName_day, fieldName_timestamp
                              </Typography>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'date_create' && (
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <InputLabel>Date Source</InputLabel>
                              <Select
                                value={operation.dateSource || 'now'}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, dateSource: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                              >
                                <MenuItem value="now">Current Date/Time</MenuItem>
                                <MenuItem value="date_string">Parse Date String</MenuItem>
                                <MenuItem value="timestamp">From Timestamp</MenuItem>
                                <MenuItem value="components">From Components</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        
                        {operation.type === 'date_format' && (
                          <>
                            <Grid item xs={3}>
                              <TextField
                                label="Date Variable"
                                value={operation.dateVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, dateVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                label="Format Pattern"
                                value={operation.dateFormat || 'YYYY-MM-DD'}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, dateFormat: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="YYYY-MM-DD HH:mm:ss"
                              />
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'date_calculate' && (
                          <>
                            <Grid item xs={2}>
                              <TextField
                                label="Date Variable"
                                value={operation.dateVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, dateVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <FormControl fullWidth>
                                <InputLabel>Operation</InputLabel>
                                <Select
                                  value={operation.dateCalcOp || 'add'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, dateCalcOp: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="add">Add</MenuItem>
                                  <MenuItem value="subtract">Subtract</MenuItem>
                                  <MenuItem value="diff">Difference</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                              <TextField
                                label="Amount"
                                value={operation.dateAmount || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, dateAmount: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="1"
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <FormControl fullWidth>
                                <InputLabel>Unit</InputLabel>
                                <Select
                                  value={operation.dateUnit || 'days'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, dateUnit: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="years">Years</MenuItem>
                                  <MenuItem value="months">Months</MenuItem>
                                  <MenuItem value="days">Days</MenuItem>
                                  <MenuItem value="hours">Hours</MenuItem>
                                  <MenuItem value="minutes">Minutes</MenuItem>
                                  <MenuItem value="seconds">Seconds</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'date_combine' && (
                          <>
                            <Grid item xs={3}>
                              <TextField
                                label="Date Variable"
                                value={operation.dateVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, dateVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                label="Time Variable"
                                value={operation.timeVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, timeVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                              />
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'generate_random' && (
                          <>
                            <Grid item xs={3}>
                              <FormControl fullWidth>
                                <InputLabel>Random Type</InputLabel>
                                <Select
                                  value={operation.randomType || 'number'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, randomType: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="number">Number</MenuItem>
                                  <MenuItem value="string">String</MenuItem>
                                  <MenuItem value="uuid">UUID</MenuItem>
                                  <MenuItem value="timestamp">Timestamp</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {operation.randomType === 'number' && (
                              <>
                                <Grid item xs={1.5}>
                                  <TextField
                                    label="Min"
                                    type="number"
                                    value={operation.randomMin || '0'}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, randomMin: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={1.5}>
                                  <TextField
                                    label="Max"
                                    type="number"
                                    value={operation.randomMax || '100'}
                                    onChange={(e) => {
                                      const updatedOperations = [...step.config.operations];
                                      updatedOperations[index] = { ...operation, randomMax: e.target.value };
                                      handleConfigChange('operations', updatedOperations);
                                    }}
                                    fullWidth
                                  />
                                </Grid>
                              </>
                            )}
                            {operation.randomType === 'string' && (
                              <Grid item xs={3}>
                                <TextField
                                  label="Length"
                                  type="number"
                                  value={operation.randomLength || '10'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, randomLength: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                  fullWidth
                                />
                              </Grid>
                            )}
                          </>
                        )}
                        
                        {operation.type === 'conditional_assign' && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Set conditions to check variables and assign values with data type conversion when conditions are met.
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={4}>
                              <TextField
                                label="Variable to Check"
                                value={operation.conditionVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, conditionVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="e.g., customerName, sapItems[*].EXP_DATE"
                                helperText="Variable or array field. Use [*] for all array items"
                              />
                            </Grid>
                            
                            <Grid item xs={4}>
                              <FormControl fullWidth>
                                <InputLabel>Condition Type</InputLabel>
                                <Select
                                  value={operation.conditionType || 'null'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, conditionType: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                  label="Condition Type"
                                >
                                  <MenuItem value="null">Is Null</MenuItem>
                                  <MenuItem value="not_null">Is Not Null</MenuItem>
                                  <MenuItem value="exists">Exists (not null/undefined)</MenuItem>
                                  <MenuItem value="not_exists">Does Not Exist</MenuItem>
                                  <MenuItem value="empty">Is Empty</MenuItem>
                                  <MenuItem value="not_empty">Is Not Empty</MenuItem>
                                  <MenuItem value="equals">Equals</MenuItem>
                                  <MenuItem value="not_equals">Not Equals</MenuItem>
                                  <MenuItem value="greater_than">Greater Than</MenuItem>
                                  <MenuItem value="less_than">Less Than</MenuItem>
                                  <MenuItem value="greater_equal">Greater or Equal</MenuItem>
                                  <MenuItem value="less_equal">Less or Equal</MenuItem>
                                  <MenuItem value="contains">Contains</MenuItem>
                                  <MenuItem value="not_contains">Does Not Contain</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            {/* Show comparison value field for conditions that need it */}
                            {['equals', 'not_equals', 'greater_than', 'less_than', 'greater_equal', 'less_equal', 'contains', 'not_contains'].includes(operation.conditionType) && (
                              <Grid item xs={4}>
                                <TextField
                                  label="Compare Value"
                                  value={operation.conditionValue || ''}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, conditionValue: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                  fullWidth
                                  placeholder="Value to compare against"
                                  helperText="Value for comparison (supports {{variables}})"
                                />
                              </Grid>
                            )}
                            
                            <Grid item xs={6}>
                              <TextField
                                label="Assign Value (when condition is true)"
                                value={operation.assignValue || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, assignValue: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="Value to assign (supports {{variables}})"
                                helperText="Value to set when condition is met"
                              />
                            </Grid>
                            
                            <Grid item xs={6}>
                              <FormControl fullWidth>
                                <InputLabel>Data Type</InputLabel>
                                <Select
                                  value={operation.assignDataType || 'string'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, assignDataType: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                  label="Data Type"
                                >
                                  <MenuItem value="string">String (Text)</MenuItem>
                                  <MenuItem value="number">Number</MenuItem>
                                  <MenuItem value="boolean">Boolean (True/False)</MenuItem>
                                  <MenuItem value="array">Array (List)</MenuItem>
                                  <MenuItem value="object">Object</MenuItem>
                                  <MenuItem value="date">Date</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Box sx={{ 
                                p: 2, 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: 1, 
                                border: '1px solid #e9ecef' 
                              }}>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                  <strong>Examples:</strong>
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                  • Single variable: <code>customerName</code> → Check if customerName is null, assign "" (empty string)
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                  • Array wildcard: <code>sapItems[*].EXP_DATE</code> → Check all EXP_DATE fields in sapItems array
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                  • Array processing: <code>products[*].price</code> → Process all price fields in products array
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Use <strong>[*]</strong> notation to process all items in an array automatically.
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'ai_transform' && (
                          <>
                            <Grid item xs={6}>
                              <TextField
                                label="Input Variables"
                                value={operation.inputVariables || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, inputVariables: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="lineItems, responseItems"
                                helperText="Comma-separated variable names to process with AI"
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <FormControl fullWidth>
                                <InputLabel>LLM Model</InputLabel>
                                <Select
                                  value={operation.llmModel || 'gpt-4o-mini'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, llmModel: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="gpt-4o">GPT-4o (Most Capable)</MenuItem>
                                  <MenuItem value="gpt-4o-mini">GPT-4o Mini (Balanced)</MenuItem>
                                  <MenuItem value="gpt-4.1">GPT-4.1 (Advanced)</MenuItem>
                                  <MenuItem value="gpt-4.1-mini">GPT-4.1 Mini (Fast)</MenuItem>
                                  <MenuItem value="gpt-4.1-nano">GPT-4.1 Nano (Ultra-fast)</MenuItem>
                                  <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo (Ultra-fast)</MenuItem>
                                  <MenuItem value="anthropic-claude-sonnet-4">Claude Sonnet 4.5 (Anthropic)</MenuItem>
                                  <MenuItem value="anthropic-claude-opus-4">Claude Opus 4.5 (Anthropic - Most Capable)</MenuItem>
                                </Select>
                                <FormHelperText>Choose model based on complexity</FormHelperText>
                              </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                label="Timeout (seconds)"
                                type="number"
                                value={operation.timeout ? Math.round(operation.timeout / 1000) : 180}
                                onChange={(e) => {
                                  const timeoutMs = parseInt(e.target.value) * 1000;
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, timeout: timeoutMs };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                inputProps={{ min: 30, max: 600 }}
                                helperText="Max wait time (30-600s)"
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <Box display="flex" alignItems="center" height="100%">
                                <Typography variant="body2" color="textSecondary">
                                  💡 For complex transformations with large datasets, consider using GPT-4.1 (vs Nano) and increasing timeout.
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label="Transformation Prompt"
                                value={operation.transformPrompt || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, transformPrompt: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                multiline
                                rows={8}
                                fullWidth
                                placeholder="Describe the data transformation you want the AI to perform. For example: 'Combine the lineItems and responseItems arrays by matching product names and adding orderedQuantity field...'"
                                helperText="Detailed instructions for the AI on how to transform the input data. Be specific about the expected output format."
                                sx={{
                                  '& .MuiInputBase-root': {
                                    alignItems: 'flex-start'
                                  },
                                  '& .MuiInputBase-input': {
                                    resize: 'vertical'
                                  }
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                                  💡 Example Array Combination Prompt:
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                  "Combine lineItems and responseItems arrays: 1) Match products by name using fuzzy matching, 2) Add 'orderedQuantity' field from responseItems to lineItems, 3) Use responseItems UOM if different, 4) Add 'exists_in_purchase_order' boolean, 5) Add unmatched responseItems with 'exists_in_GR' false. Return enhanced lineItems array."
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'combine_arrays' && (
                          <>
                            <Grid item xs={12}>
                              <TextField
                                label="Source Arrays (comma-separated)"
                                value={operation.sourceArrays || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceArrays: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="array1, array2, array3"
                                helperText="Variable names of arrays to combine (first array is primary)"
                              />
                            </Grid>
                            
                            {/* Matching Configuration */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                🔍 Matching Configuration
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <FormControl fullWidth>
                                    <InputLabel>Matching Method</InputLabel>
                                    <Select
                                      value={operation.matchingConfig?.method || 'fuzzy'}
                                      onChange={(e) => {
                                        const updatedOperations = [...step.config.operations];
                                        updatedOperations[index] = {
                                          ...operation,
                                          matchingConfig: { ...operation.matchingConfig, method: e.target.value }
                                        };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                    >
                                      <MenuItem value="fuzzy">Fuzzy Matching</MenuItem>
                                      <MenuItem value="exact">Exact Matching</MenuItem>
                                      <MenuItem value="regex">Regex Pattern</MenuItem>
                                      <MenuItem value="ai_semantic">AI Semantic</MenuItem>
                                      <MenuItem value="library_enhanced">📚 Library Enhanced</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                
                                {(operation.matchingConfig?.method === 'fuzzy' || !operation.matchingConfig?.method) && (
                                  <>
                                    <Grid item xs={6}>
                                      <TextField
                                        label="Fuzzy Threshold"
                                        type="number"
                                        inputProps={{ min: 0, max: 1, step: 0.1 }}
                                        value={operation.matchingConfig?.threshold || 0.6}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = {
                                            ...operation,
                                            matchingConfig: { ...operation.matchingConfig, threshold: parseFloat(e.target.value) }
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                        fullWidth
                                        helperText="0.0 = exact, 1.0 = very loose (default: 0.6)"
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            checked={operation.matchingConfig?.enablePreprocessing !== false}
                                            onChange={(e) => {
                                              const updatedOperations = [...step.config.operations];
                                              updatedOperations[index] = {
                                                ...operation,
                                                matchingConfig: { ...operation.matchingConfig, enablePreprocessing: e.target.checked }
                                              };
                                              handleConfigChange('operations', updatedOperations);
                                            }}
                                          />
                                        }
                                        label="Smart Preprocessing"
                                        helperText="Remove prefixes/suffixes for better matching"
                                      />
                                    </Grid>
                                    
                                    {/* One-to-One Matching Configuration */}
                                    <Grid item xs={12}>
                                      <Box sx={{ p: 2, backgroundColor: '#f8f9ff', borderRadius: 1, border: '1px solid #e3e7ff', mt: 2 }}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={operation.matchingConfig?.preventDuplicateMatches || false}
                                              onChange={(e) => {
                                                const updatedOperations = [...step.config.operations];
                                                updatedOperations[index] = {
                                                  ...operation,
                                                  matchingConfig: { 
                                                    ...operation.matchingConfig, 
                                                    preventDuplicateMatches: e.target.checked,
                                                    // Set default overflow handling when enabling
                                                    overflowHandling: e.target.checked ? (operation.matchingConfig?.overflowHandling || 'leave_unmatched') : undefined
                                                  }
                                                };
                                                handleConfigChange('operations', updatedOperations);
                                              }}
                                            />
                                          }
                                          label={
                                            <Box>
                                              <Typography variant="body2" fontWeight="600">
                                                🔒 One-to-One Matching (Prevent Duplicate Matches)
                                              </Typography>
                                              <Typography variant="caption" color="textSecondary" display="block">
                                                When enabled, each item in the secondary array can only be matched once. 
                                                Useful for invoice-to-PO matching where each line should match uniquely.
                                              </Typography>
                                            </Box>
                                          }
                                        />
                                        
                                        {operation.matchingConfig?.preventDuplicateMatches && (
                                          <Box sx={{ mt: 2, ml: 4 }}>
                                            <FormControl fullWidth>
                                              <InputLabel>Overflow Handling</InputLabel>
                                              <Select
                                                value={operation.matchingConfig?.overflowHandling || 'leave_unmatched'}
                                                onChange={(e) => {
                                                  const updatedOperations = [...step.config.operations];
                                                  updatedOperations[index] = {
                                                    ...operation,
                                                    matchingConfig: { ...operation.matchingConfig, overflowHandling: e.target.value }
                                                  };
                                                  handleConfigChange('operations', updatedOperations);
                                                }}
                                                size="small"
                                              >
                                                <MenuItem value="leave_unmatched">
                                                  <Box>
                                                    <Typography variant="body2">⚠️ Leave Unmatched</Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                      Strict 1:1 - Extra primary items remain unmatched when all secondary items are used
                                                    </Typography>
                                                  </Box>
                                                </MenuItem>
                                                <MenuItem value="reuse_best_match">
                                                  <Box>
                                                    <Typography variant="body2">🔄 Reuse Best Match</Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                      Allow multiple primary items to match the same secondary item when needed
                                                    </Typography>
                                                  </Box>
                                                </MenuItem>
                                              </Select>
                                              <FormHelperText>
                                                {operation.matchingConfig?.overflowHandling === 'leave_unmatched' 
                                                  ? "Recommended for invoice-to-PO matching where items should be unique"
                                                  : "Useful when secondary array is master data that can be referenced multiple times"}
                                              </FormHelperText>
                                            </FormControl>
                                            
                                            {/* Example visualization */}
                                            <Alert severity="info" sx={{ mt: 2 }}>
                                              <Typography variant="caption" fontWeight="600" display="block">
                                                Example:
                                              </Typography>
                                              <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', mt: 1 }}>
                                                {operation.matchingConfig?.overflowHandling === 'leave_unmatched' ? (
                                                  <>
                                                    Primary: [Item A, Item B, Item A] (3 items)<br/>
                                                    Secondary: [Item A-ID1, Item B-ID2] (2 items)<br/>
                                                    <Box component="span" sx={{ color: 'success.main' }}>Result:</Box><br/>
                                                    • Item A → Item A-ID1 ✅<br/>
                                                    • Item B → Item B-ID2 ✅<br/>
                                                    • Item A → <Box component="span" sx={{ color: 'warning.main' }}>No match (unmatched)</Box> ⚠️
                                                  </>
                                                ) : (
                                                  <>
                                                    Primary: [Item A, Item B, Item A] (3 items)<br/>
                                                    Secondary: [Item A-ID1, Item B-ID2] (2 items)<br/>
                                                    <Box component="span" sx={{ color: 'success.main' }}>Result:</Box><br/>
                                                    • Item A → Item A-ID1 ✅<br/>
                                                    • Item B → Item B-ID2 ✅<br/>
                                                    • Item A → Item A-ID1 (reused) 🔄
                                                  </>
                                                )}
                                              </Typography>
                                            </Alert>
                                          </Box>
                                        )}
                                      </Box>
                                    </Grid>
                                  </>
                                )}
                                
                                {/* Library Enhanced Matching Configuration */}
                                {operation.matchingConfig?.method === 'library_enhanced' && (
                                  <>
                                    <Grid item xs={12}>
                                      <Alert severity="info" sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                          📚 Library Enhanced Matching uses pre-configured matching libraries to improve accuracy.
                                          Libraries map different variations of the same value to a canonical name.
                                        </Typography>
                                      </Alert>
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                      <Box sx={{ p: 2, backgroundColor: '#f3f4f6', borderRadius: 1, mt: 2 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                          <Typography variant="subtitle2" fontWeight="600">
                                            Matching Libraries
                                          </Typography>
                                          <Chip 
                                            label="Manage in Project Settings" 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                          />
                                        </Box>
                                        
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<AddIcon />}
                                          onClick={() => {
                                            const updatedOperations = [...step.config.operations];
                                            const libraries = operation.matchingConfig?.libraries || [];
                                            libraries.push({
                                              libraryId: '',
                                              weight: 1.0,
                                              fields: []
                                            });
                                            updatedOperations[index] = {
                                              ...operation,
                                              matchingConfig: { ...operation.matchingConfig, libraries }
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          fullWidth
                                          sx={{ mb: 2 }}
                                        >
                                          Add Library
                                        </Button>
                                        
                                        {operation.matchingConfig?.libraries?.map((library, libIdx) => (
                                          <Box key={libIdx} sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, mb: 2 }}>
                                            <Grid container spacing={2}>
                                              <Grid item xs={6}>
                                                <FormControl fullWidth size="small">
                                                  <InputLabel>Select Library</InputLabel>
                                                  <Select
                                                    value={library.libraryId || ''}
                                                    onChange={(e) => {
                                                      const updatedOperations = [...step.config.operations];
                                                      const libraries = [...(operation.matchingConfig?.libraries || [])];
                                                      const libs = availableLibraries || [];
                                                      const selectedLib = libs.find(lib => lib.libraryId === e.target.value);
                                                      libraries[libIdx] = { 
                                                        ...libraries[libIdx], 
                                                        libraryId: e.target.value,
                                                        // Store library name for display purposes
                                                        libraryName: selectedLib?.name || ''
                                                      };
                                                      updatedOperations[index] = {
                                                        ...operation,
                                                        matchingConfig: { ...operation.matchingConfig, libraries }
                                                      };
                                                      handleConfigChange('operations', updatedOperations);
                                                    }}
                                                    label="Select Library"
                                                  >
                                                    {(!availableLibraries || availableLibraries.length === 0) ? (
                                                      <MenuItem value="" disabled>
                                                        No libraries available - create one first
                                                      </MenuItem>
                                                    ) : (
                                                      (availableLibraries || []).map((lib) => (
                                                        <MenuItem key={lib.libraryId} value={lib.libraryId}>
                                                          <Box>
                                                            <Typography variant="body2" fontWeight="medium">
                                                              {lib.name}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                              {lib.category} • {lib.entryCount || lib.entries?.length || 0} entries
                                                            </Typography>
                                                          </Box>
                                                        </MenuItem>
                                                      ))
                                                    )}
                                                  </Select>
                                                  <FormHelperText>
                                                    {(!availableLibraries || availableLibraries.length === 0)
                                                      ? 'Go to Project Settings → Matching Libraries to create libraries'
                                                      : 'Choose a matching library for this operation'
                                                    }
                                                  </FormHelperText>
                                                </FormControl>
                                              </Grid>
                                              <Grid item xs={3}>
                                                <TextField
                                                  label="Weight"
                                                  type="number"
                                                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                                                  value={library.weight || 1.0}
                                                  onChange={(e) => {
                                                    const updatedOperations = [...step.config.operations];
                                                    const libraries = [...(operation.matchingConfig?.libraries || [])];
                                                    libraries[libIdx] = { ...libraries[libIdx], weight: parseFloat(e.target.value) };
                                                    updatedOperations[index] = {
                                                      ...operation,
                                                      matchingConfig: { ...operation.matchingConfig, libraries }
                                                    };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  fullWidth
                                                  size="small"
                                                />
                                              </Grid>
                                              <Grid item xs={3}>
                                                <Button
                                                  variant="outlined"
                                                  color="error"
                                                  size="small"
                                                  fullWidth
                                                  onClick={() => {
                                                    const updatedOperations = [...step.config.operations];
                                                    const libraries = [...(operation.matchingConfig?.libraries || [])];
                                                    libraries.splice(libIdx, 1);
                                                    updatedOperations[index] = {
                                                      ...operation,
                                                      matchingConfig: { ...operation.matchingConfig, libraries }
                                                    };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  sx={{ mt: 2.5 }}
                                                >
                                                  Remove
                                                </Button>
                                              </Grid>
                                              <Grid item xs={12}>
                                                <Divider sx={{ my: 1 }} />
                                                <Typography variant="caption" fontWeight="600" display="block" gutterBottom>
                                                  📋 Field Matching Configuration (Optional)
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                                                  Specify which fields this library should match. Leave empty to match all fields.
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                <TextField
                                                  label="Primary Array Fields"
                                                  value={library._fieldsPrimaryRaw ?? library.fieldsPrimary?.join(', ') ?? ''}
                                                  onChange={(e) => {
                                                    const updatedOperations = [...step.config.operations];
                                                    const libraries = [...(operation.matchingConfig?.libraries || [])];
                                                    libraries[libIdx] = {
                                                      ...libraries[libIdx],
                                                      _fieldsPrimaryRaw: e.target.value,
                                                      fieldsPrimary: e.target.value.split(',').map(f => f.trim()).filter(f => f)
                                                    };
                                                    updatedOperations[index] = {
                                                      ...operation,
                                                      matchingConfig: { ...operation.matchingConfig, libraries }
                                                    };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  onBlur={(e) => {
                                                    const fieldsPrimary = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                                                    const updatedOperations = [...step.config.operations];
                                                    const libraries = [...(operation.matchingConfig?.libraries || [])];
                                                    const { _fieldsPrimaryRaw, ...cleanLib } = libraries[libIdx];
                                                    libraries[libIdx] = { ...cleanLib, fieldsPrimary };
                                                    updatedOperations[index] = {
                                                      ...operation,
                                                      matchingConfig: { ...operation.matchingConfig, libraries }
                                                    };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  fullWidth
                                                  size="small"
                                                  placeholder="productName, itemCode"
                                                  helperText="Primary array fields to match"
                                                />
                                              </Grid>
                                              <Grid item xs={6}>
                                                <TextField
                                                  label="Secondary Array Fields"
                                                  value={library._fieldsSecondaryRaw ?? library.fieldsSecondary?.join(', ') ?? ''}
                                                  onChange={(e) => {
                                                    const updatedOperations = [...step.config.operations];
                                                    const libraries = [...(operation.matchingConfig?.libraries || [])];
                                                    libraries[libIdx] = {
                                                      ...libraries[libIdx],
                                                      _fieldsSecondaryRaw: e.target.value,
                                                      fieldsSecondary: e.target.value.split(',').map(f => f.trim()).filter(f => f)
                                                    };
                                                    updatedOperations[index] = {
                                                      ...operation,
                                                      matchingConfig: { ...operation.matchingConfig, libraries }
                                                    };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  onBlur={(e) => {
                                                    const fieldsSecondary = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                                                    const updatedOperations = [...step.config.operations];
                                                    const libraries = [...(operation.matchingConfig?.libraries || [])];
                                                    const { _fieldsSecondaryRaw, ...cleanLib } = libraries[libIdx];
                                                    libraries[libIdx] = { ...cleanLib, fieldsSecondary };
                                                    updatedOperations[index] = {
                                                      ...operation,
                                                      matchingConfig: { ...operation.matchingConfig, libraries }
                                                    };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  fullWidth
                                                  size="small"
                                                  placeholder="materialName, materialId"
                                                  helperText="Secondary array fields to match"
                                                />
                                              </Grid>
                                              <Grid item xs={12}>
                                                <Divider sx={{ my: 1 }} />
                                                <Typography variant="caption" fontWeight="600" display="block" gutterBottom>
                                                  🎯 Source Field Matching (Optional)
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                                                  Match aliases based on their source field. Example: If alias "Forklift" has source "Company ABC", 
                                                  set Primary Source Field to "companyName" to match only when companyName = "Company ABC".
                                                </Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                <TextField
                                                  label="Primary Array Source Field"
                                                  value={library.sourceFieldPrimary || ''}
                                                  onChange={(e) => {
                                                    const updatedOperations = [...step.config.operations];
                                                    const libraries = [...(operation.matchingConfig?.libraries || [])];
                                                    libraries[libIdx] = { ...libraries[libIdx], sourceFieldPrimary: e.target.value };
                                                    updatedOperations[index] = {
                                                      ...operation,
                                                      matchingConfig: { ...operation.matchingConfig, libraries }
                                                    };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  size="small"
                                                  fullWidth
                                                  placeholder="e.g., companyName"
                                                  helperText="Field to match with alias source"
                                                />
                                              </Grid>
                                              <Grid item xs={6}>
                                                <TextField
                                                  label="Secondary Array Source Field"
                                                  value={library.sourceFieldSecondary || ''}
                                                  onChange={(e) => {
                                                    const updatedOperations = [...step.config.operations];
                                                    const libraries = [...(operation.matchingConfig?.libraries || [])];
                                                    libraries[libIdx] = { ...libraries[libIdx], sourceFieldSecondary: e.target.value };
                                                    updatedOperations[index] = {
                                                      ...operation,
                                                      matchingConfig: { ...operation.matchingConfig, libraries }
                                                    };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  size="small"
                                                  fullWidth
                                                  placeholder="e.g., supplierName"
                                                  helperText="Field to match with alias source"
                                                />
                                              </Grid>
                                            </Grid>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                      <TextField
                                        label="Fallback Methods (comma-separated)"
                                        value={operation._fallbackMethodsRaw ?? operation.matchingConfig?.fallbackMethods?.join(', ') ?? 'fuzzy'}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = {
                                            ...operation,
                                            _fallbackMethodsRaw: e.target.value,
                                            matchingConfig: {
                                              ...operation.matchingConfig,
                                              fallbackMethods: e.target.value.split(',').map(m => m.trim()).filter(m => m)
                                            }
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                        onBlur={(e) => {
                                          const methods = e.target.value.split(',').map(m => m.trim()).filter(m => m);
                                          const updatedOperations = [...step.config.operations];
                                          const { _fallbackMethodsRaw, ...cleanOp } = operation;
                                          updatedOperations[index] = {
                                            ...cleanOp,
                                            matchingConfig: { ...operation.matchingConfig, fallbackMethods: methods }
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                        fullWidth
                                        helperText="Matching methods to use for items not found in libraries (fuzzy, exact, regex, ai_semantic)"
                                      />
                                    </Grid>
                                    
                                    <Grid item xs={6}>
                                      <TextField
                                        label="Match Threshold"
                                        type="number"
                                        inputProps={{ min: 0, max: 1, step: 0.1 }}
                                        value={operation.matchingConfig?.threshold || 0.7}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = {
                                            ...operation,
                                            matchingConfig: { ...operation.matchingConfig, threshold: parseFloat(e.target.value) }
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                        fullWidth
                                        helperText="Minimum confidence score for library matches"
                                      />
                                    </Grid>
                                  </>
                                )}
                              </Grid>
                            </Grid>
                            
                            {/* Field Mapping Configuration */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                📋 Field Mapping
                              </Typography>
                              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                  Configure which fields to match between arrays:
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={5}>
                                    <TextField
                                      label="Primary Array Field"
                                      value={operation.matchingConfig?.matchFields?.[0]?.field1 || ''}
                                      onChange={(e) => {
                                        const updatedOperations = [...step.config.operations];
                                        const matchFields = operation.matchingConfig?.matchFields || [];
                                        if (matchFields.length === 0) {
                                          matchFields.push({ field1: e.target.value, field2: '', weight: 1.0 });
                                        } else {
                                          matchFields[0].field1 = e.target.value;
                                        }
                                        updatedOperations[index] = {
                                          ...operation,
                                          matchingConfig: { ...operation.matchingConfig, matchFields }
                                        };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                      fullWidth
                                      placeholder="name, email, id"
                                      helperText="Field in primary array"
                                    />
                                  </Grid>
                                  <Grid item xs={5}>
                                    <TextField
                                      label="Secondary Array Field"
                                      value={operation.matchingConfig?.matchFields?.[0]?.field2 || ''}
                                      onChange={(e) => {
                                        const updatedOperations = [...step.config.operations];
                                        const matchFields = operation.matchingConfig?.matchFields || [];
                                        if (matchFields.length === 0) {
                                          matchFields.push({ field1: '', field2: e.target.value, weight: 1.0 });
                                        } else {
                                          matchFields[0].field2 = e.target.value;
                                        }
                                        updatedOperations[index] = {
                                          ...operation,
                                          matchingConfig: { ...operation.matchingConfig, matchFields }
                                        };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                      fullWidth
                                      placeholder="customerName, contactEmail, customerId"
                                      helperText="Field in secondary array"
                                    />
                                  </Grid>
                                  <Grid item xs={2}>
                                    <TextField
                                      label="Weight"
                                      type="number"
                                      inputProps={{ min: 0, max: 1, step: 0.1 }}
                                      value={operation.matchingConfig?.matchFields?.[0]?.weight || 1.0}
                                      onChange={(e) => {
                                        const updatedOperations = [...step.config.operations];
                                        const matchFields = operation.matchingConfig?.matchFields || [];
                                        if (matchFields.length === 0) {
                                          matchFields.push({ field1: '', field2: '', weight: parseFloat(e.target.value) });
                                        } else {
                                          matchFields[0].weight = parseFloat(e.target.value);
                                        }
                                        updatedOperations[index] = {
                                          ...operation,
                                          matchingConfig: { ...operation.matchingConfig, matchFields }
                                        };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                      fullWidth
                                      helperText="Match importance"
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            </Grid>
                            
                                                         {/* Data Extraction Configuration */}
                             <Grid item xs={12}>
                               <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                 📤 Data Extraction & Conditional Fields
                               </Typography>
                               <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
                                 <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                   <Typography variant="body2" color="textSecondary">
                                     Extract specific fields from each array and add conditional fields:
                                   </Typography>
                                   <Box display="flex" gap={1}>
                                     <Button
                                       size="small"
                                       startIcon={<AddIcon />}
                                       onClick={() => {
                                         const updatedOperations = [...step.config.operations];
                                         const fields = operation.extractionConfig?.fields || [];
                                         fields.push({
                                           id: `field-${Date.now()}`,
                                           extractionType: 'from_array',
                                           sourceArray: '',
                                           sourceField: '',
                                           targetField: ''
                                         });
                                         updatedOperations[index] = {
                                           ...operation,
                                           extractionConfig: { ...operation.extractionConfig, fields }
                                         };
                                         handleConfigChange('operations', updatedOperations);
                                       }}
                                     >
                                       Add Field
                                     </Button>
                                     <Button
                                       size="small"
                                       variant="outlined"
                                       startIcon={<AddIcon />}
                                       onClick={() => {
                                         const updatedOperations = [...step.config.operations];
                                         const fields = operation.extractionConfig?.fields || [];
                                         fields.push({
                                           id: `conditional-${Date.now()}`,
                                           extractionType: 'conditional_field',
                                           condition: 'exists_in',
                                           sourceArray: '',
                                           targetField: '',
                                           trueValue: true,
                                           falseValue: false
                                         });
                                         updatedOperations[index] = {
                                           ...operation,
                                           extractionConfig: { ...operation.extractionConfig, fields }
                                         };
                                         handleConfigChange('operations', updatedOperations);
                                       }}
                                     >
                                       Add Conditional
                                     </Button>
                                   </Box>
                                 </Box>
                                 
                                 {(operation.extractionConfig?.fields || []).map((field, fieldIndex) => (
                                   <Card key={field.id || fieldIndex} sx={{ mb: 2, p: 2, backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}>
                                     <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                                       <Box flexGrow={1}>
                                         <Grid container spacing={2}>
                                           <Grid item xs={12}>
                                             <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                               <Typography variant="body2" fontWeight="bold" color={field.extractionType === 'conditional_field' ? 'primary' : 'text.primary'}>
                                                 {field.extractionType === 'conditional_field' ? '🔧 Conditional Field' : '📦 Extract Field'} {fieldIndex + 1}
                                               </Typography>
                                               <IconButton
                                                 size="small"
                                                 onClick={() => {
                                                   const updatedOperations = [...step.config.operations];
                                                   const fields = operation.extractionConfig?.fields || [];
                                                   fields.splice(fieldIndex, 1);
                                                   updatedOperations[index] = {
                                                     ...operation,
                                                     extractionConfig: { ...operation.extractionConfig, fields }
                                                   };
                                                   handleConfigChange('operations', updatedOperations);
                                                 }}
                                                 sx={{ color: '#d32f2f' }}
                                               >
                                                 <DeleteIcon fontSize="small" />
                                               </IconButton>
                                             </Box>
                                           </Grid>
                                           
                                           {field.extractionType === 'conditional_field' ? (
                                             // Conditional field configuration
                                             <>
                                               <Grid item xs={3}>
                                                 <FormControl fullWidth>
                                                   <InputLabel>Condition</InputLabel>
                                                   <Select
                                                     value={field.condition || 'exists_in'}
                                                     onChange={(e) => {
                                                       const updatedOperations = [...step.config.operations];
                                                       const fields = [...operation.extractionConfig.fields];
                                                       fields[fieldIndex] = { ...field, condition: e.target.value };
                                                       updatedOperations[index] = {
                                                         ...operation,
                                                         extractionConfig: { ...operation.extractionConfig, fields }
                                                       };
                                                       handleConfigChange('operations', updatedOperations);
                                                     }}
                                                   >
                                                     <MenuItem value="exists_in">Exists In</MenuItem>
                                                     <MenuItem value="matched_in">Matched In</MenuItem>
                                                     <MenuItem value="not_matched_in">Not Matched In</MenuItem>
                                                     <MenuItem value="only_in">Only In</MenuItem>
                                                   </Select>
                                                   <FormHelperText>When to set field to true</FormHelperText>
                                                 </FormControl>
                                               </Grid>
                                               <Grid item xs={3}>
                                                 <TextField
                                                   label="Source Array"
                                                   value={field.sourceArray || ''}
                                                   onChange={(e) => {
                                                     const updatedOperations = [...step.config.operations];
                                                     const fields = [...operation.extractionConfig.fields];
                                                     fields[fieldIndex] = { ...field, sourceArray: e.target.value };
                                                     updatedOperations[index] = {
                                                       ...operation,
                                                       extractionConfig: { ...operation.extractionConfig, fields }
                                                     };
                                                     handleConfigChange('operations', updatedOperations);
                                                   }}
                                                   fullWidth
                                                   placeholder="responseItems, secondArray"
                                                   helperText="Array to check (empty = primary)"
                                                 />
                                               </Grid>
                                               <Grid item xs={2}>
                                                 <TextField
                                                   label="Field Name"
                                                   value={field.targetField || ''}
                                                   onChange={(e) => {
                                                     const updatedOperations = [...step.config.operations];
                                                     const fields = [...operation.extractionConfig.fields];
                                                     fields[fieldIndex] = { ...field, targetField: e.target.value };
                                                     updatedOperations[index] = {
                                                       ...operation,
                                                       extractionConfig: { ...operation.extractionConfig, fields }
                                                     };
                                                     handleConfigChange('operations', updatedOperations);
                                                   }}
                                                   fullWidth
                                                   placeholder="exists_in_GR"
                                                   helperText="Result field name"
                                                 />
                                               </Grid>
                                               <Grid item xs={2}>
                                                 <TextField
                                                   label="True Value"
                                                   value={field.trueValue !== undefined ? String(field.trueValue) : 'true'}
                                                   onChange={(e) => {
                                                     const updatedOperations = [...step.config.operations];
                                                     const fields = [...operation.extractionConfig.fields];
                                                     let value = e.target.value;
                                                     if (value === 'true') value = true;
                                                     if (value === 'false') value = false;
                                                     if (!isNaN(value) && value !== '') value = Number(value);
                                                     fields[fieldIndex] = { ...field, trueValue: value };
                                                     updatedOperations[index] = {
                                                       ...operation,
                                                       extractionConfig: { ...operation.extractionConfig, fields }
                                                     };
                                                     handleConfigChange('operations', updatedOperations);
                                                   }}
                                                   fullWidth
                                                   helperText="When condition is true"
                                                 />
                                               </Grid>
                                               <Grid item xs={2}>
                                                 <TextField
                                                   label="False Value"
                                                   value={field.falseValue !== undefined ? String(field.falseValue) : 'false'}
                                                   onChange={(e) => {
                                                     const updatedOperations = [...step.config.operations];
                                                     const fields = [...operation.extractionConfig.fields];
                                                     let value = e.target.value;
                                                     if (value === 'true') value = true;
                                                     if (value === 'false') value = false;
                                                     if (!isNaN(value) && value !== '') value = Number(value);
                                                     fields[fieldIndex] = { ...field, falseValue: value };
                                                     updatedOperations[index] = {
                                                       ...operation,
                                                       extractionConfig: { ...operation.extractionConfig, fields }
                                                     };
                                                     handleConfigChange('operations', updatedOperations);
                                                   }}
                                                   fullWidth
                                                   helperText="When condition is false"
                                                 />
                                               </Grid>
                                             </>
                                           ) : (
                                             // Regular field extraction configuration
                                             <>
                                               <Grid item xs={3}>
                                                 <TextField
                                                   label="Source Array"
                                                   value={field.sourceArray || ''}
                                                   onChange={(e) => {
                                                     const updatedOperations = [...step.config.operations];
                                                     const fields = [...operation.extractionConfig.fields];
                                                     fields[fieldIndex] = { ...field, sourceArray: e.target.value };
                                                     updatedOperations[index] = {
                                                       ...operation,
                                                       extractionConfig: { ...operation.extractionConfig, fields }
                                                     };
                                                     handleConfigChange('operations', updatedOperations);
                                                   }}
                                                   fullWidth
                                                   placeholder="lineItems, responseItems"
                                                   helperText="Array to extract from (empty = primary)"
                                                 />
                                               </Grid>
                                               <Grid item xs={3}>
                                                 <TextField
                                                   label="Source Field"
                                                   value={field.sourceField || ''}
                                                   onChange={(e) => {
                                                     const updatedOperations = [...step.config.operations];
                                                     const fields = [...operation.extractionConfig.fields];
                                                     fields[fieldIndex] = { ...field, sourceField: e.target.value };
                                                     updatedOperations[index] = {
                                                       ...operation,
                                                       extractionConfig: { ...operation.extractionConfig, fields }
                                                     };
                                                     handleConfigChange('operations', updatedOperations);
                                                   }}
                                                   fullWidth
                                                   placeholder="productName, quantity, uom"
                                                   helperText="Field name in source array"
                                                 />
                                               </Grid>
                                               <Grid item xs={3}>
                                                 <TextField
                                                   label="Target Field"
                                                   value={field.targetField || ''}
                                                   onChange={(e) => {
                                                     const updatedOperations = [...step.config.operations];
                                                     const fields = [...operation.extractionConfig.fields];
                                                     fields[fieldIndex] = { ...field, targetField: e.target.value };
                                                     updatedOperations[index] = {
                                                       ...operation,
                                                       extractionConfig: { ...operation.extractionConfig, fields }
                                                     };
                                                     handleConfigChange('operations', updatedOperations);
                                                   }}
                                                   fullWidth
                                                   placeholder="productName, orderQuantity"
                                                   helperText="Field name in result"
                                                 />
                                               </Grid>
                                               <Grid item xs={3}>
                                                 <Box sx={{ pt: 1 }}>
                                                   <Typography variant="body2" color="textSecondary">
                                                     Extract "{field.sourceField || 'field'}" from "{field.sourceArray || 'primary array'}" as "{field.targetField || 'target'}"
                                                   </Typography>
                                                 </Box>
                                               </Grid>
                                             </>
                                           )}
                                         </Grid>
                                       </Box>
                                     </Box>
                                   </Card>
                                 ))}
                                 
                                 {(!operation.extractionConfig?.fields || operation.extractionConfig.fields.length === 0) && (
                                   <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                                     <Typography variant="body2">
                                       No extraction fields defined. Add fields to specify which data to extract and from which sources.
                                     </Typography>
                                   </Box>
                                 )}
                               </Box>
                             </Grid>
                            
                            {/* Unmatched Items Handling Configuration */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                🔄 Unmatched Items Handling
                              </Typography>
                              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                  Configure what happens to items that don't find matches during array combination:
                                </Typography>
                                
                                <Grid container spacing={2}>
                                  {/* Primary Array Unmatched Handling */}
                                  <Grid item xs={12}>
                                    <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                                      📦 Primary Array (First Array) - Unmatched Items:
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={operation.conditionalFields?.unmatchedHandling?.includePrimaryUnmatched !== false}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            updatedOperations[index] = {
                                              ...operation,
                                              conditionalFields: {
                                                ...operation.conditionalFields,
                                                unmatchedHandling: {
                                                  ...operation.conditionalFields?.unmatchedHandling,
                                                  includePrimaryUnmatched: e.target.checked
                                                }
                                              }
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                        />
                                      }
                                      label="Include unmatched items from primary array"
                                    />
                                  </Grid>
                                  
                                  {operation.conditionalFields?.unmatchedHandling?.includePrimaryUnmatched !== false && (
                                    <>
                                      <Grid item xs={3}>
                                        <TextField
                                          label="Marker Field Name"
                                          value={operation.conditionalFields?.unmatchedHandling?.primaryUnmatchedField || 'source'}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            updatedOperations[index] = {
                                              ...operation,
                                              conditionalFields: {
                                                ...operation.conditionalFields,
                                                unmatchedHandling: {
                                                  ...operation.conditionalFields?.unmatchedHandling,
                                                  primaryUnmatchedField: e.target.value
                                                }
                                              }
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          fullWidth
                                          placeholder="source, origin, type"
                                          helperText="Field to mark source"
                                        />
                                      </Grid>
                                      <Grid item xs={3}>
                                        <TextField
                                          label="Marker Value"
                                          value={operation.conditionalFields?.unmatchedHandling?.primaryUnmatchedValue || 'primary_only'}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            updatedOperations[index] = {
                                              ...operation,
                                              conditionalFields: {
                                                ...operation.conditionalFields,
                                                unmatchedHandling: {
                                                  ...operation.conditionalFields?.unmatchedHandling,
                                                  primaryUnmatchedValue: e.target.value
                                                }
                                              }
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          fullWidth
                                          placeholder="primary_only, unmatched"
                                          helperText="Value for unmatched items"
                                        />
                                      </Grid>
                                    </>
                                  )}
                                  
                                  {/* Secondary Array Unmatched Handling */}
                                  <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography variant="body2" fontWeight="bold" color="secondary" sx={{ mb: 1 }}>
                                      📋 Secondary Arrays - Unmatched Items:
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={operation.conditionalFields?.unmatchedHandling?.includeSecondaryUnmatched !== false}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            updatedOperations[index] = {
                                              ...operation,
                                              conditionalFields: {
                                                ...operation.conditionalFields,
                                                unmatchedHandling: {
                                                  ...operation.conditionalFields?.unmatchedHandling,
                                                  includeSecondaryUnmatched: e.target.checked
                                                }
                                              }
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                        />
                                      }
                                      label="Include unmatched items from secondary arrays"
                                    />
                                  </Grid>
                                  
                                  {operation.conditionalFields?.unmatchedHandling?.includeSecondaryUnmatched !== false && (
                                    <>
                                      <Grid item xs={3}>
                                        <TextField
                                          label="Marker Field Name"
                                          value={operation.conditionalFields?.unmatchedHandling?.secondaryUnmatchedField || 'source'}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            updatedOperations[index] = {
                                              ...operation,
                                              conditionalFields: {
                                                ...operation.conditionalFields,
                                                unmatchedHandling: {
                                                  ...operation.conditionalFields?.unmatchedHandling,
                                                  secondaryUnmatchedField: e.target.value
                                                }
                                              }
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          fullWidth
                                          placeholder="source, origin, type"
                                          helperText="Field to mark source"
                                        />
                                      </Grid>
                                      <Grid item xs={3}>
                                        <TextField
                                          label="Marker Value"
                                          value={operation.conditionalFields?.unmatchedHandling?.secondaryUnmatchedValue || 'secondary_only'}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            updatedOperations[index] = {
                                              ...operation,
                                              conditionalFields: {
                                                ...operation.conditionalFields,
                                                unmatchedHandling: {
                                                  ...operation.conditionalFields?.unmatchedHandling,
                                                  secondaryUnmatchedValue: e.target.value
                                                }
                                              }
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          fullWidth
                                          placeholder="secondary_only, api_only"
                                          helperText="Value for unmatched items"
                                        />
                                      </Grid>
                                    </>
                                  )}
                                  
                                  {/* Global Unmatched Switch for backward compatibility */}
                                  <Grid item xs={12} sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={operation.conditionalFields?.unmatchedHandling?.includeUnmatched || 
                                                   operation.conditionalFields?.unmatchedHandling?.includePrimaryUnmatched !== false ||
                                                   operation.conditionalFields?.unmatchedHandling?.includeSecondaryUnmatched !== false}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            const isIncluding = e.target.checked;
                                            updatedOperations[index] = {
                                              ...operation,
                                              conditionalFields: {
                                                ...operation.conditionalFields,
                                                unmatchedHandling: {
                                                  ...operation.conditionalFields?.unmatchedHandling,
                                                  includeUnmatched: isIncluding,
                                                  includePrimaryUnmatched: isIncluding,
                                                  includeSecondaryUnmatched: isIncluding
                                                }
                                              }
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          disabled={true} // This is now just for display
                                        />
                                      }
                                      label={
                                        <Box>
                                          <Typography variant="body2" fontWeight="bold">
                                            📊 Summary: Include Any Unmatched Items
                                          </Typography>
                                          <Typography variant="caption" color="textSecondary">
                                            {operation.conditionalFields?.unmatchedHandling?.includePrimaryUnmatched !== false && 
                                             operation.conditionalFields?.unmatchedHandling?.includeSecondaryUnmatched !== false
                                              ? "✅ Including unmatched from both arrays"
                                              : operation.conditionalFields?.unmatchedHandling?.includePrimaryUnmatched !== false
                                              ? "✅ Including unmatched from primary array only"
                                              : operation.conditionalFields?.unmatchedHandling?.includeSecondaryUnmatched !== false
                                              ? "✅ Including unmatched from secondary arrays only"
                                              : "❌ No unmatched items will be included"}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                  </Grid>
                                  
                                  {/* Enhanced: Additional Fields for Unmatched Items */}
                                  {(operation.conditionalFields?.unmatchedHandling?.includePrimaryUnmatched !== false ||
                                    operation.conditionalFields?.unmatchedHandling?.includeSecondaryUnmatched !== false) && (
                                    <Grid item xs={12} sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                      <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                                        📋 Additional Fields to Include in Unmatched Items:
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                                        Specify additional fields to include from unmatched items (e.g., productName, batchNumber, itemCode)
                                      </Typography>
                                      
                                      <Box sx={{ mb: 2 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            Include Fields
                                          </Typography>
                                          <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                              const updatedOperations = [...step.config.operations];
                                              const currentFields = operation.conditionalFields?.unmatchedHandling?.includeFields || [];
                                              const newField = `field_${Date.now()}`;
                                              updatedOperations[index] = {
                                                ...operation,
                                                conditionalFields: {
                                                  ...operation.conditionalFields,
                                                  unmatchedHandling: {
                                                    ...operation.conditionalFields?.unmatchedHandling,
                                                    includeFields: [...currentFields, newField]
                                                  }
                                                }
                                              };
                                              handleConfigChange('operations', updatedOperations);
                                            }}
                                          >
                                            Add Field
                                          </Button>
                                        </Box>
                                        
                                        {(operation.conditionalFields?.unmatchedHandling?.includeFields || []).map((fieldName, fieldIndex) => (
                                          <Box key={fieldIndex} display="flex" gap={1} alignItems="center" sx={{ mb: 1 }}>
                                            <TextField
                                              size="small"
                                              label="Field Name"
                                              value={fieldName}
                                              onChange={(e) => {
                                                const updatedOperations = [...step.config.operations];
                                                const currentFields = [...(operation.conditionalFields?.unmatchedHandling?.includeFields || [])];
                                                currentFields[fieldIndex] = e.target.value;
                                                updatedOperations[index] = {
                                                  ...operation,
                                                  conditionalFields: {
                                                    ...operation.conditionalFields,
                                                    unmatchedHandling: {
                                                      ...operation.conditionalFields?.unmatchedHandling,
                                                      includeFields: currentFields
                                                    }
                                                  }
                                                };
                                                handleConfigChange('operations', updatedOperations);
                                              }}
                                              placeholder="e.g., productName, batchNumber, itemCode"
                                              sx={{ flex: 1 }}
                                              helperText="Field name from source arrays to include in unmatched items"
                                            />
                                            <IconButton
                                              size="small"
                                              onClick={() => {
                                                const updatedOperations = [...step.config.operations];
                                                const currentFields = operation.conditionalFields?.unmatchedHandling?.includeFields || [];
                                                const filteredFields = currentFields.filter((_, i) => i !== fieldIndex);
                                                updatedOperations[index] = {
                                                  ...operation,
                                                  conditionalFields: {
                                                    ...operation.conditionalFields,
                                                    unmatchedHandling: {
                                                      ...operation.conditionalFields?.unmatchedHandling,
                                                      includeFields: filteredFields
                                                    }
                                                  }
                                                };
                                                handleConfigChange('operations', updatedOperations);
                                              }}
                                              color="error"
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </Box>
                                        ))}
                                        
                                        {(!operation.conditionalFields?.unmatchedHandling?.includeFields || 
                                          operation.conditionalFields?.unmatchedHandling?.includeFields.length === 0) && (
                                          <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary', border: '1px dashed #e0e0e0', borderRadius: 1 }}>
                                            <Typography variant="body2">
                                              No additional fields specified. Unmatched items will only include basic fields and marker fields.
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                      
                                      <Box sx={{ p: 1, backgroundColor: '#f0f8ff', borderRadius: 1, border: '1px solid #e1e7ff' }}>
                                        <Typography variant="caption" color="textSecondary">
                                          💡 <strong>Example:</strong> If you add "productName" and "batchNumber", unmatched items will include these fields from the original arrays, making it easier to identify which products were unmatched.
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            </Grid>
                            
                            {/* Sorting Configuration */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                📈 Sorting & Grouping
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={operation.sortingConfig?.enabled || false}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = {
                                            ...operation,
                                            sortingConfig: { ...operation.sortingConfig, enabled: e.target.checked }
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                      />
                                    }
                                    label="Enable Sorting"
                                  />
                                </Grid>
                                
                                {operation.sortingConfig?.enabled && (
                                  <>
                                    <Grid item xs={12}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Sort Method</InputLabel>
                                        <Select
                                          value={operation.sortingConfig?.orderByArray ? 'array_order' : 'field_value'}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            const newSortingConfig = { ...operation.sortingConfig };
                                            
                                            if (e.target.value === 'array_order') {
                                              // Switch to array order sorting
                                              // Handle both string and array formats for sourceArrays
                                              let firstArray = '';
                                              if (Array.isArray(operation.sourceArrays)) {
                                                firstArray = operation.sourceArrays[0] || '';
                                              } else if (typeof operation.sourceArrays === 'string') {
                                                firstArray = operation.sourceArrays.split(',')[0]?.trim() || '';
                                              }
                                              newSortingConfig.orderByArray = firstArray;
                                              newSortingConfig.orderMatchField = '';
                                            } else {
                                              // Switch to field value sorting
                                              delete newSortingConfig.orderByArray;
                                              delete newSortingConfig.orderMatchField;
                                            }
                                            
                                            updatedOperations[index] = {
                                              ...operation,
                                              sortingConfig: newSortingConfig
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                        >
                                          <MenuItem value="field_value">
                                            <Box>
                                              <Typography variant="body2" fontWeight="medium">
                                                📊 Sort by Field Value
                                              </Typography>
                                              <Typography variant="caption" color="textSecondary">
                                                Sort by comparing field values (default)
                                              </Typography>
                                            </Box>
                                          </MenuItem>
                                          <MenuItem value="array_order">
                                            <Box>
                                              <Typography variant="body2" fontWeight="medium">
                                                🔢 Sort by Array Order
                                              </Typography>
                                              <Typography variant="caption" color="textSecondary">
                                                Follow the ordering of a specific source array
                                              </Typography>
                                            </Box>
                                          </MenuItem>
                                        </Select>
                                        <FormHelperText>
                                          Choose how to order the combined array items
                                        </FormHelperText>
                                      </FormControl>
                                    </Grid>
                                    
                                    {!operation.sortingConfig?.orderByArray && (
                                      <>
                                        {/* Field-based sorting (existing) */}
                                        <Grid item xs={6}>
                                          <TextField
                                            label="Sort Field"
                                            value={operation.sortingConfig?.rules?.[0]?.field || ''}
                                            onChange={(e) => {
                                              const updatedOperations = [...step.config.operations];
                                              const rules = operation.sortingConfig?.rules || [];
                                              if (rules.length === 0) {
                                                rules.push({ field: e.target.value, order: 'asc', priority: 1, groupBy: false });
                                              } else {
                                                rules[0].field = e.target.value;
                                              }
                                              updatedOperations[index] = {
                                                ...operation,
                                                sortingConfig: { ...operation.sortingConfig, rules }
                                              };
                                              handleConfigChange('operations', updatedOperations);
                                            }}
                                            fullWidth
                                            placeholder="isActive, priority, name"
                                            helperText="Field to sort by"
                                          />
                                        </Grid>
                                        <Grid item xs={3}>
                                          <FormControl fullWidth>
                                            <InputLabel>Order</InputLabel>
                                            <Select
                                              value={operation.sortingConfig?.rules?.[0]?.order || 'asc'}
                                              onChange={(e) => {
                                                const updatedOperations = [...step.config.operations];
                                                const rules = operation.sortingConfig?.rules || [];
                                                if (rules.length === 0) {
                                                  rules.push({ field: '', order: e.target.value, priority: 1, groupBy: false });
                                                } else {
                                                  rules[0].order = e.target.value;
                                                }
                                                updatedOperations[index] = {
                                                  ...operation,
                                                  sortingConfig: { ...operation.sortingConfig, rules }
                                                };
                                                handleConfigChange('operations', updatedOperations);
                                              }}
                                            >
                                              <MenuItem value="asc">Ascending</MenuItem>
                                              <MenuItem value="desc">Descending</MenuItem>
                                            </Select>
                                          </FormControl>
                                        </Grid>
                                        <Grid item xs={3}>
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                checked={operation.sortingConfig?.rules?.[0]?.groupBy || false}
                                                onChange={(e) => {
                                                  const updatedOperations = [...step.config.operations];
                                                  const rules = operation.sortingConfig?.rules || [];
                                                  if (rules.length === 0) {
                                                    rules.push({ field: '', order: 'asc', priority: 1, groupBy: e.target.checked });
                                                  } else {
                                                    rules[0].groupBy = e.target.checked;
                                                  }
                                                  updatedOperations[index] = {
                                                    ...operation,
                                                    sortingConfig: { ...operation.sortingConfig, rules }
                                                  };
                                                  handleConfigChange('operations', updatedOperations);
                                                }}
                                              />
                                            }
                                            label="Group By"
                                          />
                                        </Grid>
                                      </>
                                    )}
                                    
                                    {operation.sortingConfig?.orderByArray && (
                                      <>
                                        {/* Array order sorting (new) */}
                                        <Grid item xs={12}>
                                          <FormControl fullWidth>
                                            <InputLabel>Reference Array</InputLabel>
                                            <Select
                                              value={operation.sortingConfig?.orderByArray || ''}
                                              onChange={(e) => {
                                                const updatedOperations = [...step.config.operations];
                                                updatedOperations[index] = {
                                                  ...operation,
                                                  sortingConfig: { 
                                                    ...operation.sortingConfig, 
                                                    orderByArray: e.target.value 
                                                  }
                                                };
                                                handleConfigChange('operations', updatedOperations);
                                              }}
                                            >
                                              {(() => {
                                                // Handle both string (comma-separated) and array formats
                                                let arrayNames = [];
                                                if (Array.isArray(operation.sourceArrays)) {
                                                  arrayNames = operation.sourceArrays;
                                                } else if (typeof operation.sourceArrays === 'string') {
                                                  arrayNames = operation.sourceArrays.split(',').map(s => s.trim()).filter(s => s);
                                                }
                                                
                                                return arrayNames.map((arrayName) => {
                                                  const trimmedName = typeof arrayName === 'string' ? arrayName.trim() : arrayName;
                                                  return (
                                                    <MenuItem key={trimmedName} value={trimmedName}>
                                                      {trimmedName}
                                                    </MenuItem>
                                                  );
                                                });
                                              })()}
                                            </Select>
                                            <FormHelperText>
                                              Select which array's order to follow
                                            </FormHelperText>
                                          </FormControl>
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                            💡 Tip: Specify which fields to use for matching items between arrays
                                          </Typography>
                                        </Grid>
                                        
                                        <Grid item xs={6}>
                                          <TextField
                                            label="Reference Array Match Field"
                                            value={
                                              typeof operation.sortingConfig?.orderMatchField === 'object'
                                                ? operation.sortingConfig?.orderMatchField?.reference || ''
                                                : operation.sortingConfig?.orderMatchField || ''
                                            }
                                            onChange={(e) => {
                                              const updatedOperations = [...step.config.operations];
                                              const currentMatchField = operation.sortingConfig?.orderMatchField;
                                              
                                              let newMatchField;
                                              if (typeof currentMatchField === 'object') {
                                                // Already an object, update reference field
                                                newMatchField = {
                                                  reference: e.target.value,
                                                  combined: currentMatchField.combined || e.target.value
                                                };
                                              } else {
                                                // Convert to object format
                                                newMatchField = {
                                                  reference: e.target.value,
                                                  combined: e.target.value // Default to same field name
                                                };
                                              }
                                              
                                              updatedOperations[index] = {
                                                ...operation,
                                                sortingConfig: { 
                                                  ...operation.sortingConfig, 
                                                  orderMatchField: newMatchField
                                                }
                                              };
                                              handleConfigChange('operations', updatedOperations);
                                            }}
                                            fullWidth
                                            placeholder="description, productId, itemCode"
                                            helperText={`Field in ${operation.sortingConfig?.orderByArray || 'reference array'}`}
                                          />
                                        </Grid>
                                        
                                        <Grid item xs={6}>
                                          <TextField
                                            label="Combined Array Match Field"
                                            value={
                                              typeof operation.sortingConfig?.orderMatchField === 'object'
                                                ? operation.sortingConfig?.orderMatchField?.combined || ''
                                                : operation.sortingConfig?.orderMatchField || ''
                                            }
                                            onChange={(e) => {
                                              const updatedOperations = [...step.config.operations];
                                              const currentMatchField = operation.sortingConfig?.orderMatchField;
                                              
                                              let newMatchField;
                                              if (typeof currentMatchField === 'object') {
                                                // Already an object, update combined field
                                                newMatchField = {
                                                  reference: currentMatchField.reference || e.target.value,
                                                  combined: e.target.value
                                                };
                                              } else {
                                                // Convert to object format
                                                newMatchField = {
                                                  reference: e.target.value, // Default to same field name
                                                  combined: e.target.value
                                                };
                                              }
                                              
                                              updatedOperations[index] = {
                                                ...operation,
                                                sortingConfig: { 
                                                  ...operation.sortingConfig, 
                                                  orderMatchField: newMatchField
                                                }
                                              };
                                              handleConfigChange('operations', updatedOperations);
                                            }}
                                            fullWidth
                                            placeholder="extractedDescription, productId, itemId"
                                            helperText="Field in combined result array"
                                          />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                          <Alert severity="info" icon={<InfoIcon />}>
                                            <Typography variant="body2">
                                              <strong>How Array Order works:</strong>
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                              • The combined array will follow the same order as items in the selected reference array
                                            </Typography>
                                            <Typography variant="body2">
                                              • Items not found in the reference array will be placed at the end
                                            </Typography>
                                            <Typography variant="body2">
                                              • <strong>Match Fields:</strong> Specify which fields to use for matching items
                                            </Typography>
                                            <Typography variant="body2" sx={{ ml: 2 }}>
                                              - <strong>Reference Array Field:</strong> Field in the reference array (e.g., "description")
                                            </Typography>
                                            <Typography variant="body2" sx={{ ml: 2 }}>
                                              - <strong>Combined Array Field:</strong> Field in the combined result (e.g., "extractedDescription")
                                            </Typography>
                                            <Typography variant="body2" sx={{ ml: 2 }}>
                                              - These can be different if your extraction/combination renamed fields
                                            </Typography>
                                            <Typography variant="body2">
                                              • Leave both fields empty to match by complete item data (exact match)
                                            </Typography>
                                          </Alert>
                                        </Grid>
                                      </>
                                    )}
                                  </>
                                )}
                              </Grid>
                            </Grid>
                            
                            {/* Enhanced Matching Options */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                🚀 Enhanced Matching Options
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={operation.enableRelaxedMatching !== false}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = {
                                            ...operation,
                                            enableRelaxedMatching: e.target.checked
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                      />
                                    }
                                    label={
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                          🎯 Multi-Stage Matching
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                          Use progressive thresholds to find more matches
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={operation.enableAIFallback === true}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = {
                                            ...operation,
                                            enableAIFallback: e.target.checked
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                      />
                                    }
                                    label={
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                          🤖 AI Semantic Fallback
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                          Use AI for unmatched items (costs tokens)
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </Grid>
                                
                                <Grid item xs={12}>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={operation.enableAliasMatching === true}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          updatedOperations[index] = {
                                            ...operation,
                                            enableAliasMatching: e.target.checked
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                      />
                                    }
                                    label={
                                      <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                          🔄 Alias Field Matching
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                          Use secondary alias field for unmatched items
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </Grid>
                                
                                {operation.enableAliasMatching && (
                                  <Grid item xs={12}>
                                    <TextField
                                      label="Secondary Alias Field"
                                      value={operation.matchingConfig?.aliasField || ''}
                                      onChange={(e) => {
                                        const updatedOperations = [...step.config.operations];
                                        updatedOperations[index] = {
                                          ...operation,
                                          matchingConfig: { ...operation.matchingConfig, aliasField: e.target.value }
                                        };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                      fullWidth
                                      placeholder="SEARCH_ALIASES, ALT_NAMES, SIMILAR_NAMES"
                                      helperText="Field containing comma-separated alias names for fallback matching"
                                      size="small"
                                    />
                                  </Grid>
                                )}

                              </Grid>
                             </Grid>
                           </>
                         )}
                        
                        {operation.type === 'calculate_fields' && (
                          <>
                            <Grid item xs={6}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="items"
                                helperText="Array variable containing items with fields to calculate"
                              />
                            </Grid>
                            
                            {/* Field Calculations Configuration */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                🧮 Field Calculations
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Define formulas to calculate missing field values. Use {'{{fieldName}}'} to reference other fields in the same item.
                              </Typography>
                              
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  const updatedOperations = [...step.config.operations];
                                  const currentCalculations = operation.fieldCalculations || [];
                                  updatedOperations[index] = {
                                    ...operation,
                                    fieldCalculations: [
                                      ...currentCalculations,
                                      {
                                        id: `calc-${Date.now()}`,
                                        fieldName: '',
                                        formula: '',
                                        overwriteExisting: false
                                      }
                                    ]
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                sx={{ mb: 2 }}
                              >
                                Add Field Calculation
                              </Button>
                              
                              {(operation.fieldCalculations || []).map((calc, calcIndex) => (
                                <Box key={calc.id} sx={{ p: 2, mb: 2, backgroundColor: '#f0f8ff', borderRadius: 1, border: '1px solid #b3d9ff' }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="body2" fontWeight="bold">
                                      Calculation {calcIndex + 1}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const updatedOperations = [...step.config.operations];
                                        const updatedCalculations = [...operation.fieldCalculations];
                                        updatedCalculations.splice(calcIndex, 1);
                                        updatedOperations[index] = {
                                          ...operation,
                                          fieldCalculations: updatedCalculations
                                        };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  
                                  <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                      <TextField
                                        label="Field Name"
                                        value={calc.fieldName || ''}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          const updatedCalculations = [...operation.fieldCalculations];
                                          updatedCalculations[calcIndex] = { ...calc, fieldName: e.target.value };
                                          updatedOperations[index] = {
                                            ...operation,
                                            fieldCalculations: updatedCalculations
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                        fullWidth
                                        placeholder="netPrice"
                                        helperText="Field to calculate"
                                      />
                                    </Grid>
                                    
                                    <Grid item xs={6}>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            size="small"
                                            checked={calc.overwriteExisting || false}
                                            onChange={(e) => {
                                              const updatedOperations = [...step.config.operations];
                                              const updatedCalculations = [...operation.fieldCalculations];
                                              updatedCalculations[calcIndex] = { ...calc, overwriteExisting: e.target.checked };
                                              updatedOperations[index] = {
                                                ...operation,
                                                fieldCalculations: updatedCalculations
                                              };
                                              handleConfigChange('operations', updatedOperations);
                                            }}
                                          />
                                        }
                                        label="Overwrite Existing Values"
                                      />
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                      <TextField
                                        label="Formula"
                                        value={calc.formula || ''}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          const updatedCalculations = [...operation.fieldCalculations];
                                          updatedCalculations[calcIndex] = { ...calc, formula: e.target.value };
                                          updatedOperations[index] = {
                                            ...operation,
                                            fieldCalculations: updatedCalculations
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                        fullWidth
                                        multiline
                                        rows={2}
                                        placeholder="({{price}} * {{quantity}}) * (1 - {{discount}})"
                                        helperText="Use {{fieldName}} to reference fields. Supports +, -, *, /, parentheses, etc."
                                      />
                                    </Grid>
                                  </Grid>
                                  
                                  <Box sx={{ mt: 2, p: 1, backgroundColor: '#e8f4f8', borderRadius: 1 }}>
                                    <Typography variant="caption" color="textSecondary">
                                      💡 <strong>Examples:</strong><br/>
                                      • Simple: {'{{price}}'} * {'{{quantity}}'}<br/>
                                      • With discount: ({'{{price}}'} * {'{{quantity}}'}) * (1 - {'{{discount}}'})<br/>
                                      • Complex: ({'{{price}}'} + {'{{tax}}'}) * {'{{quantity}}'} - {'{{discount}}'}<br/>
                                      • Conditional: {'{{status}}'} === 'active' ? {'{{fullPrice}}'} : {'{{discountPrice}}'}<br/>
                                      <br/>
                                      📝 Empty/null fields default to 0 in calculations
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'convert_field_types' && (
                          <>
                            <Grid item xs={6}>
                              <TextField
                                label="Source Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="items"
                                helperText="Array variable containing items with fields to convert"
                              />
                            </Grid>
                            
                            {/* Field Conversions Configuration */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                🔄 Field Type Conversions
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Convert field types (string to number, number to string, etc.) and set default values for null fields.
                              </Typography>
                              
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  const updatedOperations = [...step.config.operations];
                                  const currentConversions = operation.fieldConversions || [];
                                  updatedOperations[index] = {
                                    ...operation,
                                    fieldConversions: [
                                      ...currentConversions,
                                      {
                                        id: `conv-${Date.now()}`,
                                        fieldName: '',
                                        targetType: 'number',
                                        defaultForNull: '',
                                        overwriteExisting: false
                                      }
                                    ]
                                  };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                sx={{ mb: 2 }}
                              >
                                Add Field Conversion
                              </Button>
                              
                              {(operation.fieldConversions || []).map((conversion, convIndex) => (
                                <Box key={conversion.id} sx={{ p: 2, mb: 2, backgroundColor: '#f0f8ff', borderRadius: 1, border: '1px solid #b3d9ff' }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="body2" fontWeight="bold">
                                      Conversion {convIndex + 1}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const updatedOperations = [...step.config.operations];
                                        const updatedConversions = [...operation.fieldConversions];
                                        updatedConversions.splice(convIndex, 1);
                                        updatedOperations[index] = {
                                          ...operation,
                                          fieldConversions: updatedConversions
                                        };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  
                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <TextField
                                        label="Field Name"
                                        value={conversion.fieldName || ''}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          const updatedConversions = [...operation.fieldConversions];
                                          updatedConversions[convIndex] = { ...conversion, fieldName: e.target.value };
                                          updatedOperations[index] = {
                                            ...operation,
                                            fieldConversions: updatedConversions
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                        fullWidth
                                        placeholder="grossAmt"
                                        helperText="Field name to convert"
                                      />
                                    </Grid>
                                    
                                    <Grid item xs={6}>
                                      <FormControl fullWidth>
                                        <InputLabel>Target Type</InputLabel>
                                        <Select
                                          value={conversion.targetType || 'number'}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            const updatedConversions = [...operation.fieldConversions];
                                            updatedConversions[convIndex] = { ...conversion, targetType: e.target.value };
                                            updatedOperations[index] = {
                                              ...operation,
                                              fieldConversions: updatedConversions
                                            };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          label="Target Type"
                                        >
                                          <MenuItem value="number">Number (Float)</MenuItem>
                                          <MenuItem value="integer">Integer (Whole Number)</MenuItem>
                                          <MenuItem value="float">Float (Decimal)</MenuItem>
                                          <MenuItem value="string">String (Text)</MenuItem>
                                          <MenuItem value="boolean">Boolean (True/False)</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    
                                    <Grid item xs={6}>
                                      <TextField
                                        label="Default for Null"
                                        value={conversion.defaultForNull !== undefined ? conversion.defaultForNull : ''}
                                        onChange={(e) => {
                                          const updatedOperations = [...step.config.operations];
                                          const updatedConversions = [...operation.fieldConversions];
                                          updatedConversions[convIndex] = { ...conversion, defaultForNull: e.target.value };
                                          updatedOperations[index] = {
                                            ...operation,
                                            fieldConversions: updatedConversions
                                          };
                                          handleConfigChange('operations', updatedOperations);
                                        }}
                                        fullWidth
                                        placeholder="0"
                                        helperText="Value to use when field is null/empty"
                                      />
                                    </Grid>
                                    
                                    {['number', 'integer', 'float'].includes(conversion.targetType) && (
                                      <>
                                        <Grid item xs={6}>
                                          <TextField
                                            label="Thousands Separator (Optional)"
                                            value={conversion.thousandsSeparator !== undefined ? conversion.thousandsSeparator : ''}
                                            onChange={(e) => {
                                              const updatedOperations = [...step.config.operations];
                                              const updatedConversions = [...operation.fieldConversions];
                                              updatedConversions[convIndex] = { ...conversion, thousandsSeparator: e.target.value };
                                              updatedOperations[index] = {
                                                ...operation,
                                                fieldConversions: updatedConversions
                                              };
                                              handleConfigChange('operations', updatedOperations);
                                            }}
                                            fullWidth
                                            placeholder=". or , or leave empty"
                                            helperText="⚡ NEW: Character used as thousands separator (e.g. . for 29.500)"
                                          />
                                        </Grid>
                                        
                                        <Grid item xs={6}>
                                          <TextField
                                            label="Decimal Separator (Optional)"
                                            value={conversion.decimalSeparator !== undefined ? conversion.decimalSeparator : ''}
                                            onChange={(e) => {
                                              const updatedOperations = [...step.config.operations];
                                              const updatedConversions = [...operation.fieldConversions];
                                              updatedConversions[convIndex] = { ...conversion, decimalSeparator: e.target.value };
                                              updatedOperations[index] = {
                                                ...operation,
                                                fieldConversions: updatedConversions
                                              };
                                              handleConfigChange('operations', updatedOperations);
                                            }}
                                            fullWidth
                                            placeholder=". or , or leave empty"
                                            helperText="⚡ NEW: Character used as decimal separator (e.g. , for 29,50)"
                                          />
                                        </Grid>
                                      </>
                                    )}
                                    
                                    <Grid item xs={12}>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            size="small"
                                            checked={conversion.overwriteExisting !== false}
                                            onChange={(e) => {
                                              const updatedOperations = [...step.config.operations];
                                              const updatedConversions = [...operation.fieldConversions];
                                              updatedConversions[convIndex] = { ...conversion, overwriteExisting: e.target.checked };
                                              updatedOperations[index] = {
                                                ...operation,
                                                fieldConversions: updatedConversions
                                              };
                                              handleConfigChange('operations', updatedOperations);
                                            }}
                                          />
                                        }
                                        label="Overwrite existing values"
                                      />
                                    </Grid>
                                  </Grid>
                                  
                                  <Box sx={{ mt: 2, p: 1, backgroundColor: '#e8f4f8', borderRadius: 1 }}>
                                    <Typography variant="caption" color="textSecondary">
                                      💡 <strong>Type Conversions:</strong><br/>
                                      • Number: "1000.00" → 1000 | true → 1 | "1,234.56" → 1234.56<br/>
                                      • String: 1000 → "1000" | true → "true"<br/>
                                      • Boolean: "true"/"1"/"yes" → true | "false"/"0"/"no" → false<br/>
                                      • Integer: 1000.99 → 1000 | "42" → 42<br/>
                                      <br/>
                                      ⚡ <strong>NEW: Locale Support:</strong><br/>
                                      • Set thousands separator (e.g. "." for "29.500" → 29500)<br/>
                                      • Set decimal separator (e.g. "," for "29,50" → 29.5)<br/>
                                      • Default: comma for thousands, dot for decimal (US format)<br/>
                                      <br/>
                                      📝 Null/empty values use default or type-specific default (0 for numbers, "" for strings, false for booleans)
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'calculate_array' && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                                🧮 Calculate Array Values - Simple array calculations without grouping
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={6}>
                              <TextField
                                label="Source Array Variable"
                                value={operation.sourceVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="products"
                                helperText="Name of array variable"
                              />
                            </Grid>
                            
                            <Grid item xs={6}>
                              <TextField
                                label="Field to Calculate"
                                value={operation.field || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, field: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="quantity"
                                helperText="Field name to perform calculation on"
                              />
                            </Grid>
                            
                            <Grid item xs={6}>
                              <FormControl fullWidth>
                                <InputLabel>Calculation Operation</InputLabel>
                                <Select
                                  value={operation.operation || 'sum'}
                                  onChange={(e) => {
                                    const updatedOperations = [...step.config.operations];
                                    updatedOperations[index] = { ...operation, operation: e.target.value };
                                    handleConfigChange('operations', updatedOperations);
                                  }}
                                >
                                  <MenuItem value="sum">Sum - Add all values</MenuItem>
                                  <MenuItem value="average">Average - Calculate mean</MenuItem>
                                  <MenuItem value="min">Minimum - Find lowest value</MenuItem>
                                  <MenuItem value="max">Maximum - Find highest value</MenuItem>
                                  <MenuItem value="count">Count - Count non-null values</MenuItem>
                                  <MenuItem value="multiply">Multiply - Multiply all values</MenuItem>
                                </Select>
                                <FormHelperText>Select the calculation to perform</FormHelperText>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={6}>
                              <TextField
                                label="Result Variable"
                                value={operation.targetVariable || ''}
                                onChange={(e) => {
                                  const updatedOperations = [...step.config.operations];
                                  updatedOperations[index] = { ...operation, targetVariable: e.target.value };
                                  handleConfigChange('operations', updatedOperations);
                                }}
                                fullWidth
                                placeholder="totalQuantity"
                                helperText="Variable to store the result"
                              />
                            </Grid>
                            
                            {/* Example Display */}
                            <Grid item xs={12}>
                              <Box sx={{ p: 2, backgroundColor: '#f0f7ff', borderRadius: 1, border: '1px solid #90caf9' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                  💡 Example:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                  If <strong>{operation.sourceVariable || 'products'}</strong> = [
                                  {'{'}productName: "A", {operation.field || 'quantity'}: 2{'}'},
                                  {'{'}productName: "B", {operation.field || 'quantity'}: 7{'}'}]
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', mt: 1 }}>
                                  Result: <strong>{operation.targetVariable || 'result'}</strong> = 
                                  {operation.operation === 'sum' && ' 9 (2 + 7)'}
                                  {operation.operation === 'average' && ' 4.5 ((2 + 7) / 2)'}
                                  {operation.operation === 'min' && ' 2'}
                                  {operation.operation === 'max' && ' 7'}
                                  {operation.operation === 'count' && ' 2'}
                                  {operation.operation === 'multiply' && ' 14 (2 × 7)'}
                                  {!operation.operation && ' 9 (2 + 7)'}
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                        
                        {operation.type === 'aggregate_array' && (
                           <>
                             <Grid item xs={6}>
                               <TextField
                                 label="Source Variable"
                                 value={operation.sourceVariable || ''}
                                 onChange={(e) => {
                                   const updatedOperations = [...step.config.operations];
                                   updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                   handleConfigChange('operations', updatedOperations);
                                 }}
                                 fullWidth
                                 placeholder="combinedItems"
                                 helperText="Array variable to aggregate"
                               />
                             </Grid>
                             
                             <Grid item xs={6}>
                               <TextField
                                 label="Group By Field"
                                 value={operation.aggregationConfig?.groupBy || ''}
                                 onChange={(e) => {
                                   const updatedOperations = [...step.config.operations];
                                   updatedOperations[index] = {
                                     ...operation,
                                     aggregationConfig: { 
                                       ...operation.aggregationConfig, 
                                       groupBy: e.target.value 
                                     }
                                   };
                                   handleConfigChange('operations', updatedOperations);
                                 }}
                                 fullWidth
                                 placeholder="productName"
                                 helperText="Field to group items by"
                               />
                             </Grid>
                             
                             {/* Aggregation Configuration */}
                             <Grid item xs={12}>
                               <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                 📊 Aggregation Settings
                               </Typography>
                               <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
                                 <Grid container spacing={2}>
                                   <Grid item xs={4}>
                                     <TextField
                                       label="Source Field"
                                       value={operation.aggregationConfig?.aggregations?.[0]?.sourceField || 'quantity'}
                                       onChange={(e) => {
                                         const updatedOperations = [...step.config.operations];
                                         const aggregations = operation.aggregationConfig?.aggregations || [{}];
                                         aggregations[0] = { ...aggregations[0], sourceField: e.target.value };
                                         updatedOperations[index] = {
                                           ...operation,
                                           aggregationConfig: { 
                                             ...operation.aggregationConfig, 
                                             aggregations 
                                           }
                                         };
                                         handleConfigChange('operations', updatedOperations);
                                       }}
                                       fullWidth
                                       placeholder="quantity"
                                       helperText="Field to aggregate"
                                     />
                                   </Grid>
                                   <Grid item xs={4}>
                                     <TextField
                                       label="Target Field"
                                       value={operation.aggregationConfig?.aggregations?.[0]?.targetField || 'cumulativeQuantity'}
                                       onChange={(e) => {
                                         const updatedOperations = [...step.config.operations];
                                         const aggregations = operation.aggregationConfig?.aggregations || [{}];
                                         aggregations[0] = { ...aggregations[0], targetField: e.target.value };
                                         updatedOperations[index] = {
                                           ...operation,
                                           aggregationConfig: { 
                                             ...operation.aggregationConfig, 
                                             aggregations 
                                           }
                                         };
                                         handleConfigChange('operations', updatedOperations);
                                       }}
                                       fullWidth
                                       placeholder="cumulativeQuantity"
                                       helperText="New field name"
                                     />
                                   </Grid>
                                   <Grid item xs={4}>
                                     <FormControl fullWidth>
                                       <InputLabel>Operation</InputLabel>
                                       <Select
                                         value={operation.aggregationConfig?.aggregations?.[0]?.operation || 'sum'}
                                         onChange={(e) => {
                                           const updatedOperations = [...step.config.operations];
                                           const aggregations = operation.aggregationConfig?.aggregations || [{}];
                                           aggregations[0] = { ...aggregations[0], operation: e.target.value };
                                           updatedOperations[index] = {
                                             ...operation,
                                             aggregationConfig: { 
                                               ...operation.aggregationConfig, 
                                               aggregations 
                                             }
                                           };
                                           handleConfigChange('operations', updatedOperations);
                                         }}
                                       >
                                         <MenuItem value="sum">Sum</MenuItem>
                                         <MenuItem value="average">Average</MenuItem>
                                         <MenuItem value="min">Minimum</MenuItem>
                                         <MenuItem value="max">Maximum</MenuItem>
                                         <MenuItem value="count">Count</MenuItem>
                                         <MenuItem value="concat">Concatenate</MenuItem>
                                       </Select>
                                     </FormControl>
                                   </Grid>
                                 </Grid>
                               </Box>
                             </Grid>
                             
                             {/* Preserve Fields */}
                             <Grid item xs={12}>
                               <TextField
                                 label="Preserve Fields (comma-separated)"
                                 value={operation.aggregationConfig?.preserveFieldsRaw || operation.aggregationConfig?.preserveFields?.join(', ') || ''}
                                 onChange={(e) => {
                                   const rawValue = e.target.value;
                                   const fields = rawValue.split(',').map(s => s.trim()).filter(s => s);
                                   const updatedOperations = [...step.config.operations];
                                   updatedOperations[index] = {
                                     ...operation,
                                     aggregationConfig: { 
                                       ...operation.aggregationConfig, 
                                       preserveFields: fields,
                                       preserveFieldsRaw: rawValue // Store raw value for display
                                     }
                                   };
                                   handleConfigChange('operations', updatedOperations);
                                 }}
                                 fullWidth
                                 placeholder="productName, uom, plant, materialId"
                                 helperText="Fields to keep from first occurrence (separate with commas)"
                               />
                             </Grid>
                             
                             {/* Handle Multiple Values */}
                             <Grid item xs={12}>
                               <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                 🔄 Multiple Values Handling
                               </Typography>
                               <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                 When grouping, specify how to handle fields with multiple values
                               </Typography>
                               
                               {operation.aggregationConfig?.preserveFields && operation.aggregationConfig.preserveFields.length > 0 ? (
                                 <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                                   <Grid container spacing={2}>
                                     {operation.aggregationConfig.preserveFields.map((field, fieldIndex) => (
                                       <Grid item xs={6} key={field}>
                                         <TextField
                                           label={`${field} Strategy`}
                                           value={operation.aggregationConfig?.handleMultipleValues?.[field] || 'first'}
                                           onChange={(e) => {
                                             const updatedOperations = [...step.config.operations];
                                             updatedOperations[index] = {
                                               ...operation,
                                               aggregationConfig: { 
                                                 ...operation.aggregationConfig, 
                                                 handleMultipleValues: {
                                                   ...operation.aggregationConfig?.handleMultipleValues,
                                                   [field]: e.target.value
                                                 }
                                               }
                                             };
                                             handleConfigChange('operations', updatedOperations);
                                           }}
                                           select
                                           fullWidth
                                           helperText={`How to handle multiple ${field} values`}
                                         >
                                           <MenuItem value="first">Use First</MenuItem>
                                           <MenuItem value="last">Use Last</MenuItem>
                                           <MenuItem value="array">Collect as Array</MenuItem>
                                           <MenuItem value="concatenate">Concatenate with Comma</MenuItem>
                                         </TextField>
                                       </Grid>
                                     ))}
                                   </Grid>
                                 </Box>
                               ) : (
                                 <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                                   <Typography variant="body2" color="#e65100">
                                     💡 Add preserve fields above to configure multiple values handling
                                   </Typography>
                                 </Box>
                               )}
                             </Grid>
                           </>
                         )}
                         
                         {operation.type === 'enrich_array' && (
                           <>
                             <Grid item xs={6}>
                               <TextField
                                 label="Source Variable"
                                 value={operation.sourceVariable || ''}
                                 onChange={(e) => {
                                   const updatedOperations = [...step.config.operations];
                                   updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                   handleConfigChange('operations', updatedOperations);
                                 }}
                                 fullWidth
                                 placeholder="combinedItems"
                                 helperText="Array variable to enrich"
                               />
                             </Grid>
                             
                             
                             
                             {/* Enrichment Configuration */}
                             <Grid item xs={12}>
                               <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                 🔍 Enrichment Settings
                               </Typography>
                               <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
                                 <Grid container spacing={2}>
                                   <Grid item xs={4}>
                                     <TextField
                                       label="Source Field"
                                       value={operation.enrichmentConfig?.enrichments?.[0]?.sourceField || 'quantity'}
                                       onChange={(e) => {
                                         const updatedOperations = [...step.config.operations];
                                         const enrichments = operation.enrichmentConfig?.enrichments || [{}];
                                         enrichments[0] = { ...enrichments[0], sourceField: e.target.value };
                                         updatedOperations[index] = {
                                           ...operation,
                                           enrichmentConfig: { 
                                             ...operation.enrichmentConfig, 
                                             enrichments 
                                           }
                                         };
                                         handleConfigChange('operations', updatedOperations);
                                         console.log()
                                       }}
                                       fullWidth
                                       placeholder="quantity"
                                       helperText="Field to enrich"
                                     />
                                   </Grid>
                                   <Grid item xs={4}>
                                     <TextField
                                       label="Target Field"
                                       value={operation.enrichmentConfig?.enrichments?.[0]?.targetField || 'cumulativeQuantity'}
                                       onChange={(e) => {
                                         const updatedOperations = [...step.config.operations];
                                         const enrichments = operation.enrichmentConfig?.enrichments || [{}];
                                         enrichments[0] = { ...enrichments[0], targetField: e.target.value };
                                         updatedOperations[index] = {
                                           ...operation,
                                           enrichmentConfig: { 
                                             ...operation.enrichmentConfig, 
                                             enrichments 
                                           }
                                         };
                                         handleConfigChange('operations', updatedOperations);
                                       }}
                                       fullWidth
                                       placeholder="cumulativeQuantity"
                                       helperText="New field name"
                                     />
                                   </Grid>
                                   <Grid item xs={4}>
                                     <FormControl fullWidth>
                                       <InputLabel>Operation</InputLabel>
                                       <Select
                                         value={operation.enrichmentConfig?.enrichments?.[0]?.operation || 'sum'}
                                         onChange={(e) => {
                                           const updatedOperations = [...step.config.operations];
                                           const enrichments = operation.enrichmentConfig?.enrichments || [{}];
                                           enrichments[0] = { ...enrichments[0], operation: e.target.value };
                                           updatedOperations[index] = {
                                             ...operation,
                                             enrichmentConfig: { 
                                               ...operation.enrichmentConfig, 
                                               enrichments 
                                             }
                                           };
                                           handleConfigChange('operations', updatedOperations);
                                         }}
                                       >
                                         <MenuItem value="sum">Sum</MenuItem>
                                         <MenuItem value="average">Average</MenuItem>
                                         <MenuItem value="min">Minimum</MenuItem>
                                         <MenuItem value="max">Maximum</MenuItem>
                                         <MenuItem value="count">Count</MenuItem>
                                         <MenuItem value="concat">Concatenate</MenuItem>
                                       </Select>
                                     </FormControl>
                                   </Grid>
                                 </Grid>
                               </Box>

                               
                               <TextField
                                 label="Group By Field"
                                 value={operation.enrichmentConfig?.groupBy || ''}
                                 onChange={(e) => {
                                   const updatedOperations = [...step.config.operations];
                                   updatedOperations[index] = {
                                     ...operation,
                                     enrichmentConfig: { 
                                       ...operation.enrichmentConfig, 
                                       groupBy: e.target.value 
                                     }
                                   };
                                   handleConfigChange('operations', updatedOperations);
                                 }}
                                 fullWidth
                                 placeholder="productName"
                                 helperText="Field to group items by"
                               />
                             
                             </Grid>
                           </>
                         )}
                         
                         {operation.type === 'lookup_library' && (
                           <>
                             <Grid item xs={6}>
                               <TextField
                                 label="Source Variable"
                                 value={operation.sourceVariable || ''}
                                 onChange={(e) => {
                                   const updatedOperations = [...step.config.operations];
                                   updatedOperations[index] = { ...operation, sourceVariable: e.target.value };
                                   handleConfigChange('operations', updatedOperations);
                                 }}
                                 fullWidth
                                 placeholder="extractedProducts"
                                 helperText="Variable containing data to lookup (array or single object)"
                               />
                             </Grid>
                             
                             {/* Library Selection */}
                             <Grid item xs={12}>
                               <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                 📚 Matching Library
                               </Typography>
                               <FormControl fullWidth size="small">
                                 <InputLabel>Select Library</InputLabel>
                                 <Select
                                   value={operation.libraryId || ''}
                                   onChange={(e) => {
                                     const updatedOperations = [...step.config.operations];
                                     updatedOperations[index] = { ...operation, libraryId: e.target.value };
                                     handleConfigChange('operations', updatedOperations);
                                   }}
                                   label="Select Library"
                                 >
                                   {(availableLibraries || []).map(lib => (
                                     <MenuItem key={lib.libraryId} value={lib.libraryId}>
                                       {lib.name} ({lib.entryCount || lib.entries?.length || 0} entries)
                                     </MenuItem>
                                   ))}
                                 </Select>
                                 <FormHelperText>
                                   {!availableLibraries || availableLibraries.length === 0
                                     ? 'Go to Project Settings → Matching Libraries to create libraries'
                                     : 'Library to use for canonical name lookup'
                                   }
                                 </FormHelperText>
                               </FormControl>
                             </Grid>
                             
                             {/* Field Mappings */}
                             <Grid item xs={12}>
                               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                                 <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                   🔄 Field Mappings
                                 </Typography>
                                 <Button
                                   size="small"
                                   variant="outlined"
                                   startIcon={<AddIcon />}
                                   onClick={() => {
                                     const updatedOperations = [...step.config.operations];
                                     const fieldMappings = operation.fieldMappings || [];
                                     fieldMappings.push({
                                       sourceField: '',
                                       targetField: '',
                                       includeMatchedValue: false
                                     });
                                     updatedOperations[index] = { ...operation, fieldMappings };
                                     handleConfigChange('operations', updatedOperations);
                                   }}
                                 >
                                   Add Field Mapping
                                 </Button>
                               </Box>
                               
                               {(operation.fieldMappings || []).map((mapping, mapIdx) => (
                                 <Box key={mapIdx} sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, mb: 2, border: '1px solid #e0e0e0' }}>
                                   <Grid container spacing={2}>
                                     <Grid item xs={5}>
                                       <TextField
                                         label="Source Field"
                                         value={mapping.sourceField || ''}
                                         onChange={(e) => {
                                           const updatedOperations = [...step.config.operations];
                                           const fieldMappings = [...(operation.fieldMappings || [])];
                                           fieldMappings[mapIdx] = { ...fieldMappings[mapIdx], sourceField: e.target.value };
                                           updatedOperations[index] = { ...operation, fieldMappings };
                                           handleConfigChange('operations', updatedOperations);
                                         }}
                                         fullWidth
                                         size="small"
                                         placeholder="productName"
                                         helperText="Field containing alias to lookup"
                                       />
                                     </Grid>
                                     <Grid item xs={5}>
                                       <TextField
                                         label="Target Field (optional)"
                                         value={mapping.targetField || ''}
                                         onChange={(e) => {
                                           const updatedOperations = [...step.config.operations];
                                           const fieldMappings = [...(operation.fieldMappings || [])];
                                           fieldMappings[mapIdx] = { ...fieldMappings[mapIdx], targetField: e.target.value };
                                           updatedOperations[index] = { ...operation, fieldMappings };
                                           handleConfigChange('operations', updatedOperations);
                                         }}
                                         fullWidth
                                         size="small"
                                         placeholder="standardProductName"
                                         helperText="Field to store canonical name (defaults to source field)"
                                       />
                                     </Grid>
                                     <Grid item xs={2}>
                                       <Button
                                         variant="outlined"
                                         color="error"
                                         size="small"
                                         fullWidth
                                         onClick={() => {
                                           const updatedOperations = [...step.config.operations];
                                           const fieldMappings = [...(operation.fieldMappings || [])];
                                           fieldMappings.splice(mapIdx, 1);
                                           updatedOperations[index] = { ...operation, fieldMappings };
                                           handleConfigChange('operations', updatedOperations);
                                         }}
                                         sx={{ mt: 0.5 }}
                                       >
                                         Remove
                                       </Button>
                                     </Grid>
                                     
                                     {/* Source Filter Field */}
                                     <Grid item xs={6}>
                                       <TextField
                                         label="Source Filter Field (optional)"
                                         value={mapping.sourceFilterField || ''}
                                         onChange={(e) => {
                                           const updatedOperations = [...step.config.operations];
                                           const fieldMappings = [...(operation.fieldMappings || [])];
                                           fieldMappings[mapIdx] = { ...fieldMappings[mapIdx], sourceFilterField: e.target.value };
                                           updatedOperations[index] = { ...operation, fieldMappings };
                                           handleConfigChange('operations', updatedOperations);
                                         }}
                                         fullWidth
                                         size="small"
                                         placeholder="companyName, systemSource"
                                         helperText="Field name (not {{variable}}) containing source to filter by"
                                       />
                                     </Grid>
                                     
                                    {/* Auto-add Source Mode */}
                                    <Grid item xs={6}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Auto-add New Source</InputLabel>
                                        <Select
                                          value={mapping.autoAddSource || 'disabled'}
                                          onChange={(e) => {
                                            const updatedOperations = [...step.config.operations];
                                            const fieldMappings = [...(operation.fieldMappings || [])];
                                            fieldMappings[mapIdx] = { ...fieldMappings[mapIdx], autoAddSource: e.target.value };
                                            updatedOperations[index] = { ...operation, fieldMappings };
                                            handleConfigChange('operations', updatedOperations);
                                          }}
                                          label="Auto-add New Source"
                                        >
                                          <MenuItem value="disabled">Disabled</MenuItem>
                                          <MenuItem value="silent">Add Silently</MenuItem>
                                          <MenuItem value="pending">Pending Approval</MenuItem>
                                        </Select>
                                        <FormHelperText>
                                          What to do when source not found in library
                                        </FormHelperText>
                                      </FormControl>
                                    </Grid>
                                    
                                    {/* Alias Source Mappings */}
                                    <Grid item xs={12}>
                                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            🔗 Return Alias Values by Source
                                          </Typography>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                              const updatedOperations = [...step.config.operations];
                                              const fieldMappings = [...(operation.fieldMappings || [])];
                                              const aliasSourceMappings = mapping.aliasSourceMappings || [];
                                              aliasSourceMappings.push({
                                                sourceToMatch: '',
                                                targetField: ''
                                              });
                                              fieldMappings[mapIdx] = { ...fieldMappings[mapIdx], aliasSourceMappings };
                                              updatedOperations[index] = { ...operation, fieldMappings };
                                              handleConfigChange('operations', updatedOperations);
                                            }}
                                          >
                                            Add Source Mapping
                                          </Button>
                                        </Box>
                                        
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                          Return alias values from specific sources alongside the canonical name.
                                          Example: Return "itemId" alias value into "itemId" field.
                                        </Typography>
                                        
                                        {(mapping.aliasSourceMappings || []).map((aliasMapping, aliasIdx) => (
                                          <Box key={aliasIdx} sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, mb: 1, border: '1px solid #e0e0e0' }}>
                                            <Grid container spacing={2} alignItems="center">
                                              <Grid item xs={5}>
                                                <TextField
                                                  label="Source Name"
                                                  value={aliasMapping.sourceToMatch || ''}
                                                  onChange={(e) => {
                                                    const updatedOperations = [...step.config.operations];
                                                    const fieldMappings = [...(operation.fieldMappings || [])];
                                                    const aliasSourceMappings = [...(mapping.aliasSourceMappings || [])];
                                                    aliasSourceMappings[aliasIdx] = { ...aliasSourceMappings[aliasIdx], sourceToMatch: e.target.value };
                                                    fieldMappings[mapIdx] = { ...fieldMappings[mapIdx], aliasSourceMappings };
                                                    updatedOperations[index] = { ...operation, fieldMappings };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  fullWidth
                                                  size="small"
                                                  placeholder="itemId, companyCode"
                                                  helperText="Source name to get alias from"
                                                />
                                              </Grid>
                                              <Grid item xs={5}>
                                                <TextField
                                                  label="Target Field"
                                                  value={aliasMapping.targetField || ''}
                                                  onChange={(e) => {
                                                    const updatedOperations = [...step.config.operations];
                                                    const fieldMappings = [...(operation.fieldMappings || [])];
                                                    const aliasSourceMappings = [...(mapping.aliasSourceMappings || [])];
                                                    aliasSourceMappings[aliasIdx] = { ...aliasSourceMappings[aliasIdx], targetField: e.target.value };
                                                    fieldMappings[mapIdx] = { ...fieldMappings[mapIdx], aliasSourceMappings };
                                                    updatedOperations[index] = { ...operation, fieldMappings };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  fullWidth
                                                  size="small"
                                                  placeholder="itemId"
                                                  helperText="Field to store alias value"
                                                />
                                              </Grid>
                                              <Grid item xs={2}>
                                                <Button
                                                  variant="outlined"
                                                  color="error"
                                                  size="small"
                                                  fullWidth
                                                  onClick={() => {
                                                    const updatedOperations = [...step.config.operations];
                                                    const fieldMappings = [...(operation.fieldMappings || [])];
                                                    const aliasSourceMappings = [...(mapping.aliasSourceMappings || [])];
                                                    aliasSourceMappings.splice(aliasIdx, 1);
                                                    fieldMappings[mapIdx] = { ...fieldMappings[mapIdx], aliasSourceMappings };
                                                    updatedOperations[index] = { ...operation, fieldMappings };
                                                    handleConfigChange('operations', updatedOperations);
                                                  }}
                                                  sx={{ mt: 0.5 }}
                                                >
                                                  Remove
                                                </Button>
                                              </Grid>
                                            </Grid>
                                          </Box>
                                        ))}
                                        
                                        {(!mapping.aliasSourceMappings || mapping.aliasSourceMappings.length === 0) && (
                                          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 1 }}>
                                            <Typography variant="caption">
                                              Add source mappings to return alias values from specific sources.
                                              This allows you to populate multiple fields with different alias values.
                                            </Typography>
                                          </Alert>
                                        )}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}
                               
                               {(!operation.fieldMappings || operation.fieldMappings.length === 0) && (
                                 <Alert severity="info" sx={{ mt: 1 }}>
                                   Add at least one field mapping to convert aliases to canonical names
                                 </Alert>
                               )}
                             </Grid>
                             
                             {/* Advanced Options */}
                             <Grid item xs={12}>
                               <Accordion sx={{ mt: 2 }}>
                                 <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                   <Typography variant="subtitle2" fontWeight="600">
                                     ⚙️ Advanced Options
                                   </Typography>
                                 </AccordionSummary>
                                 <AccordionDetails>
                                   <Grid container spacing={2}>
                                     <Grid item xs={6}>
                                       <TextField
                                         label="Fuzzy Threshold"
                                         type="number"
                                         inputProps={{ min: 0, max: 1, step: 0.05 }}
                                         value={operation.fuzzyThreshold || 0.8}
                                         onChange={(e) => {
                                           const updatedOperations = [...step.config.operations];
                                           updatedOperations[index] = { ...operation, fuzzyThreshold: parseFloat(e.target.value) };
                                           handleConfigChange('operations', updatedOperations);
                                         }}
                                         fullWidth
                                         size="small"
                                         helperText="Minimum similarity score (0-1) for matching"
                                       />
                                     </Grid>
                                     <Grid item xs={6}>
                                       <FormControl fullWidth size="small">
                                         <InputLabel>On No Match</InputLabel>
                                         <Select
                                           value={operation.onNoMatch || 'keep_original'}
                                           onChange={(e) => {
                                             const updatedOperations = [...step.config.operations];
                                             updatedOperations[index] = { ...operation, onNoMatch: e.target.value };
                                             handleConfigChange('operations', updatedOperations);
                                           }}
                                           label="On No Match"
                                         >
                                           <MenuItem value="keep_original">Keep Original Value</MenuItem>
                                           <MenuItem value="set_null">Set to Null</MenuItem>
                                           <MenuItem value="set_default">Set to Default Value</MenuItem>
                                         </Select>
                                         <FormHelperText>What to do when no match is found</FormHelperText>
                                       </FormControl>
                                     </Grid>
                                     
                                     {operation.onNoMatch === 'set_default' && (
                                       <Grid item xs={12}>
                                         <TextField
                                           label="Default Value"
                                           value={operation.defaultValue || ''}
                                           onChange={(e) => {
                                             const updatedOperations = [...step.config.operations];
                                             updatedOperations[index] = { ...operation, defaultValue: e.target.value };
                                             handleConfigChange('operations', updatedOperations);
                                           }}
                                           fullWidth
                                           size="small"
                                           placeholder="Unknown"
                                           helperText="Value to use when no match is found"
                                         />
                                       </Grid>
                                     )}
                                     
                                     <Grid item xs={6}>
                                       <FormControlLabel
                                         control={
                                           <Switch
                                             checked={operation.includeConfidence !== false}
                                             onChange={(e) => {
                                               const updatedOperations = [...step.config.operations];
                                               updatedOperations[index] = { ...operation, includeConfidence: e.target.checked };
                                               handleConfigChange('operations', updatedOperations);
                                             }}
                                             size="small"
                                           />
                                         }
                                         label="Include Confidence Score"
                                       />
                                     </Grid>
                                     <Grid item xs={6}>
                                       <FormControlLabel
                                         control={
                                           <Switch
                                             checked={operation.includeSource !== false}
                                             onChange={(e) => {
                                               const updatedOperations = [...step.config.operations];
                                               updatedOperations[index] = { ...operation, includeSource: e.target.checked };
                                               handleConfigChange('operations', updatedOperations);
                                             }}
                                             size="small"
                                           />
                                         }
                                         label="Include Source Info"
                                       />
                                     </Grid>
                                     
                                     <Grid item xs={12}>
                                       <FormControlLabel
                                         control={
                                           <Switch
                                             checked={operation.storeStats || false}
                                             onChange={(e) => {
                                               const updatedOperations = [...step.config.operations];
                                               updatedOperations[index] = { ...operation, storeStats: e.target.checked };
                                               handleConfigChange('operations', updatedOperations);
                                             }}
                                             size="small"
                                           />
                                         }
                                         label="Store Statistics"
                                       />
                                     </Grid>
                                     
                                     {operation.storeStats && (
                                       <Grid item xs={12}>
                                         <TextField
                                           label="Statistics Variable Name"
                                           value={operation.statsVariable || 'lookupStats'}
                                           onChange={(e) => {
                                             const updatedOperations = [...step.config.operations];
                                             updatedOperations[index] = { ...operation, statsVariable: e.target.value };
                                             handleConfigChange('operations', updatedOperations);
                                           }}
                                           fullWidth
                                           size="small"
                                           placeholder="lookupStats"
                                           helperText="Variable name to store match statistics"
                                         />
                                       </Grid>
                                     )}
                                   </Grid>
                                 </AccordionDetails>
                               </Accordion>
                             </Grid>
                             
                             <Grid item xs={12}>
                               <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
                                 <Typography variant="body2" fontWeight="bold" gutterBottom>
                                   💡 Library Lookup Operation
                                 </Typography>
                                 <Typography variant="body2">
                                   Converts field values (aliases) to canonical names using a Matching Library.
                                   Perfect for standardizing product names, customer names, or any data that needs normalization.
                                 </Typography>
                                 <Typography variant="body2" sx={{ mt: 1 }}>
                                   Example: "pdol" → "Panadol", "Pan" → "Panadol"
                                 </Typography>
                                 <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
                                   🎯 Source Filtering (Optional)
                                 </Typography>
                                 <Typography variant="body2">
                                   When a Source Filter Field is specified, the lookup will prioritize matches:
                                 </Typography>
                                 <Typography variant="body2" component="div" sx={{ ml: 2, mt: 0.5 }}>
                                   1. <strong>First:</strong> Aliases with matching source (highest priority)<br/>
                                   2. <strong>Then:</strong> Other aliases in the library<br/>
                                   3. <strong>Finally:</strong> Canonical names
                                 </Typography>
                                 <Typography variant="body2" sx={{ mt: 1 }}>
                                   <strong>Auto-add New Source:</strong> When enabled, unknown sources can be automatically 
                                   added to the library (silently) or queued for approval (pending).
                                 </Typography>
                               </Alert>
                             </Grid>
                           </>
                         )}

                         {operation.type === 'transform_array' && (
                           <>
                             <Grid item xs={6}>
                               <TextField
                                 label="Source Array Variable"
                                 value={operation.transformArrayConfig?.sourceArray || ''}
                                 onChange={(e) => {
                                   const updatedOperations = [...step.config.operations];
                                   updatedOperations[index] = {
                                     ...operation,
                                     transformArrayConfig: {
                                       ...operation.transformArrayConfig,
                                       sourceArray: e.target.value
                                     }
                                   };
                                   handleConfigChange('operations', updatedOperations);
                                 }}
                                 fullWidth
                                 placeholder="enrichedCombinedItems"
                                 helperText="Source array variable to transform"
                               />
                             </Grid>
                             
                             {/* Field Mappings Configuration */}
                             <Grid item xs={12}>
                               <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                 💫 Field Mappings
                               </Typography>
                               
                               <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                 <Typography variant="body2" color="textSecondary">
                                   Configure how fields are mapped and transformed in the output array
                                 </Typography>
                                 <Button
                                   size="small"
                                   startIcon={<AddIcon />}
                                   onClick={() => {
                                     const updatedOperations = [...step.config.operations];
                                     const fieldMappings = operation.transformArrayConfig?.fieldMappings || [];
                                     const newMapping = {
                                       id: `mapping-${Date.now()}`,
                                       targetField: '',
                                       sourceField: '' // Only set sourceField by default
                                     };
                                     updatedOperations[index] = {
                                       ...operation,
                                       transformArrayConfig: {
                                         ...operation.transformArrayConfig,
                                         fieldMappings: [...fieldMappings, newMapping]
                                       }
                                     };
                                     handleConfigChange('operations', updatedOperations);
                                   }}
                                 >
                                   Add Field Mapping
                                 </Button>
                              </Box>
                              
                              <DndContext 
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleTransformFieldMappingsDragEnd(index)}
                              >
                                <SortableContext 
                                  items={operation.transformArrayConfig?.fieldMappings || []}
                                  strategy={verticalListSortingStrategy}
                                >
                                  {(operation.transformArrayConfig?.fieldMappings || []).map((mapping, mappingIndex) => (
                                    <SortableTransformFieldMappingCard
                                      key={mapping.id || mappingIndex}
                                      mapping={mapping}
                                      index={mappingIndex}
                                      onMappingDelete={(idx) => {
                                        const updatedOperations = [...step.config.operations];
                                        const fieldMappings = operation.transformArrayConfig.fieldMappings.filter((_, i) => i !== idx);
                                        updatedOperations[index] = {
                                          ...operation,
                                          transformArrayConfig: {
                                            ...operation.transformArrayConfig,
                                            fieldMappings
                                          }
                                        };
                                        handleConfigChange('operations', updatedOperations);
                                      }}
                                    >
                                      <Box flexGrow={1}>
                                       <Grid container spacing={2}>
                                         <Grid item xs={4}>
                                           <TextField
                                             label="Target Field Name"
                                             value={mapping.targetField || ''}
                                             onChange={(e) => {
                                               const updatedOperations = [...step.config.operations];
                                               const fieldMappings = [...operation.transformArrayConfig.fieldMappings];
                                               fieldMappings[mappingIndex] = { ...mapping, targetField: e.target.value };
                                               updatedOperations[index] = {
                                                 ...operation,
                                                 transformArrayConfig: {
                                                   ...operation.transformArrayConfig,
                                                   fieldMappings
                                                 }
                                               };
                                               handleConfigChange('operations', updatedOperations);
                                             }}
                                             fullWidth
                                             placeholder="NO_PO, DOC_DATE, etc."
                                             helperText="Field name in output object"
                                           />
                                         </Grid>
                                         
                                         <Grid item xs={8}>
                                           <FormControl fullWidth>
                                             <InputLabel>Value Source Type</InputLabel>
                                             <Select
                                               value={
                                                 mapping.sourceField !== undefined ? 'sourceField' :
                                                 mapping.variableSource !== undefined ? 'variableSource' :
                                                 mapping.staticValue !== undefined ? 'staticValue' :
                                                 mapping.computedValue ? 'computedValue' : ''
                                               }
                                               onChange={(e) => {
                                                 const updatedOperations = [...step.config.operations];
                                                 const fieldMappings = [...operation.transformArrayConfig.fieldMappings];
                                                 // Clear all source types first, then set the selected one
                                                 const clearedMapping = { ...mapping };
                                                 delete clearedMapping.sourceField;
                                                 delete clearedMapping.variableSource;
                                                 delete clearedMapping.staticValue;
                                                 delete clearedMapping.computedValue;
                                                 
                                                 // Set the selected type with a default value
                                                 switch (e.target.value) {
                                                   case 'sourceField':
                                                     clearedMapping.sourceField = '';
                                                     break;
                                                   case 'variableSource':
                                                     clearedMapping.variableSource = '';
                                                     break;
                                                   case 'staticValue':
                                                     clearedMapping.staticValue = '';
                                                     break;
                                                   case 'computedValue':
                                                     clearedMapping.computedValue = 'current_date';
                                                     break;
                                                 }
                                                 
                                                 fieldMappings[mappingIndex] = clearedMapping;
                                                 updatedOperations[index] = {
                                                   ...operation,
                                                   transformArrayConfig: {
                                                     ...operation.transformArrayConfig,
                                                     fieldMappings
                                                   }
                                                 };
                                                 handleConfigChange('operations', updatedOperations);
                                               }}
                                             >
                                               <MenuItem value="sourceField">Copy from Source Array Field</MenuItem>
                                               <MenuItem value="variableSource">Copy from Variable</MenuItem>
                                               <MenuItem value="staticValue">Static Value</MenuItem>
                                               <MenuItem value="computedValue">Computed Value</MenuItem>
                                             </Select>
                                             <FormHelperText>How to get the value for this field</FormHelperText>
                                           </FormControl>
                                         </Grid>
                                         
                                         {/* Source Field Input */}
                                         {(mapping.sourceField !== undefined) && (
                                           <Grid item xs={12}>
                                             <TextField
                                               label="Source Field in Array"
                                               value={mapping.sourceField || ''}
                                               onChange={(e) => {
                                                 const updatedOperations = [...step.config.operations];
                                                 const fieldMappings = [...operation.transformArrayConfig.fieldMappings];
                                                 fieldMappings[mappingIndex] = { ...mapping, sourceField: e.target.value };
                                                 updatedOperations[index] = {
                                                   ...operation,
                                                   transformArrayConfig: {
                                                     ...operation.transformArrayConfig,
                                                     fieldMappings
                                                   }
                                                 };
                                                 handleConfigChange('operations', updatedOperations);
                                               }}
                                               fullWidth
                                               placeholder="MATERIAL, MAT_DESC, QTY, etc."
                                               helperText="Field name in source array items"
                                             />
                                           </Grid>
                                         )}
                                         
                                         {/* Variable Source Input */}
                                         {(mapping.variableSource !== undefined) && (
                                           <Grid item xs={12}>
                                             <TextField
                                               label="Variable Name"
                                               value={mapping.variableSource || ''}
                                               onChange={(e) => {
                                                 const updatedOperations = [...step.config.operations];
                                                 const fieldMappings = [...operation.transformArrayConfig.fieldMappings];
                                                 fieldMappings[mappingIndex] = { ...mapping, variableSource: e.target.value };
                                                 updatedOperations[index] = {
                                                   ...operation,
                                                   transformArrayConfig: {
                                                     ...operation.transformArrayConfig,
                                                     fieldMappings
                                                   }
                                                 };
                                                 handleConfigChange('operations', updatedOperations);
                                               }}
                                               fullWidth
                                               placeholder="purchaseOrder, userEmail, etc."
                                               helperText="Variable name to copy value from"
                                             />
                                           </Grid>
                                         )}
                                         
                                         {/* Static Value Input */}
                                         {(mapping.staticValue !== undefined) && (
                                           <Grid item xs={12}>
                                             <TextField
                                               label="Static Value"
                                               value={mapping.staticValue || ''}
                                               onChange={(e) => {
                                                 const updatedOperations = [...step.config.operations];
                                                 const fieldMappings = [...operation.transformArrayConfig.fieldMappings];
                                                 fieldMappings[mappingIndex] = { ...mapping, staticValue: e.target.value };
                                                 updatedOperations[index] = {
                                                   ...operation,
                                                   transformArrayConfig: {
                                                     ...operation.transformArrayConfig,
                                                     fieldMappings
                                                   }
                                                 };
                                                 handleConfigChange('operations', updatedOperations);
                                               }}
                                               fullWidth
                                               placeholder="FARMASI, PC, etc."
                                               helperText="Fixed value to set for all items"
                                             />
                                           </Grid>
                                         )}
                                         
                                         {/* Computed Value Input */}
                                         {(mapping.computedValue !== undefined && mapping.computedValue !== '') && (
                                           <>
                                             <Grid item xs={6}>
                                               <FormControl fullWidth>
                                                 <InputLabel>Computed Value Type</InputLabel>
                                                 <Select
                                                   value={mapping.computedValue || ''}
                                                   onChange={(e) => {
                                                     const updatedOperations = [...step.config.operations];
                                                     const fieldMappings = [...operation.transformArrayConfig.fieldMappings];
                                                     fieldMappings[mappingIndex] = { ...mapping, computedValue: e.target.value };
                                                     updatedOperations[index] = {
                                                       ...operation,
                                                       transformArrayConfig: {
                                                         ...operation.transformArrayConfig,
                                                         fieldMappings
                                                       }
                                                     };
                                                     handleConfigChange('operations', updatedOperations);
                                                   }}
                                                 >
                                                   <MenuItem value="current_date">Current Date</MenuItem>
                                                   <MenuItem value="current_timestamp">Current Timestamp</MenuItem>
                                                   <MenuItem value="user_email">User Email</MenuItem>
                                                   <MenuItem value="sequential_number">Sequential Number</MenuItem>
                                                   <MenuItem value="random_uuid">Random UUID</MenuItem>
                                                 </Select>
                                                 <FormHelperText>Type of computed value</FormHelperText>
                                               </FormControl>
                                             </Grid>
                                             
                                             {mapping.computedValue === 'current_date' && (
                                               <Grid item xs={6}>
                                                 <TextField
                                                   label="Date Format"
                                                   value={mapping.dateFormat || 'YYYYMMDD'}
                                                   onChange={(e) => {
                                                     const updatedOperations = [...step.config.operations];
                                                     const fieldMappings = [...operation.transformArrayConfig.fieldMappings];
                                                     fieldMappings[mappingIndex] = { ...mapping, dateFormat: e.target.value };
                                                     updatedOperations[index] = {
                                                       ...operation,
                                                       transformArrayConfig: {
                                                         ...operation.transformArrayConfig,
                                                         fieldMappings
                                                       }
                                                     };
                                                     handleConfigChange('operations', updatedOperations);
                                                   }}
                                                   fullWidth
                                                   placeholder="YYYYMMDD"
                                                   helperText="Date format (default: YYYYMMDD)"
                                                 />
                                               </Grid>
                                             )}
                                             
                                             {mapping.computedValue === 'sequential_number' && (
                                               <>
                                                 <Grid item xs={3}>
                                                   <TextField
                                                     label="Start Number"
                                                     type="number"
                                                     value={mapping.startNumber || 1}
                                                     onChange={(e) => {
                                                       const updatedOperations = [...step.config.operations];
                                                       const fieldMappings = [...operation.transformArrayConfig.fieldMappings];
                                                       fieldMappings[mappingIndex] = { ...mapping, startNumber: parseInt(e.target.value) || 1 };
                                                       updatedOperations[index] = {
                                                         ...operation,
                                                         transformArrayConfig: {
                                                           ...operation.transformArrayConfig,
                                                           fieldMappings
                                                         }
                                                       };
                                                       handleConfigChange('operations', updatedOperations);
                                                     }}
                                                     fullWidth
                                                     helperText="Starting number"
                                                   />
                                                 </Grid>
                                                 <Grid item xs={3}>
                                                   <TextField
                                                     label="Increment"
                                                     type="number"
                                                     value={mapping.increment || 1}
                                                     onChange={(e) => {
                                                       const updatedOperations = [...step.config.operations];
                                                       const fieldMappings = [...operation.transformArrayConfig.fieldMappings];
                                                       fieldMappings[mappingIndex] = { ...mapping, increment: parseInt(e.target.value) || 1 };
                                                       updatedOperations[index] = {
                                                         ...operation,
                                                         transformArrayConfig: {
                                                           ...operation.transformArrayConfig,
                                                           fieldMappings
                                                         }
                                                       };
                                                       handleConfigChange('operations', updatedOperations);
                                                     }}
                                                     fullWidth
                                                     helperText="Increment by"
                                                   />
                                                 </Grid>
                                               </>
                                             )}
                                           </>
                                         )}
                                      </Grid>
                                    </Box>
                                    </SortableTransformFieldMappingCard>
                                  ))}
                                </SortableContext>
                              </DndContext>
                              
                              {(!operation.transformArrayConfig?.fieldMappings || operation.transformArrayConfig.fieldMappings.length === 0) && (
                                 <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                                   <Typography variant="body2">
                                     No field mappings defined. Add mappings to configure how array items are transformed.
                                   </Typography>
                                 </Box>
                               )}
                             </Grid>
                           </>
                         )}
                         
                         {operation.type === 'filter_array' && (
                           <>
                             <Grid item xs={6}>
                               <TextField
                                 label="Source Array Variable"
                                 value={operation.filterArrayConfig?.sourceArray || ''}
                                 onChange={(e) => {
                                   const updatedOperations = [...step.config.operations];
                                   updatedOperations[index] = {
                                     ...operation,
                                     filterArrayConfig: {
                                       ...operation.filterArrayConfig,
                                       sourceArray: e.target.value
                                     }
                                   };
                                   handleConfigChange('operations', updatedOperations);
                                 }}
                                 fullWidth
                                 placeholder="myArray"
                                 helperText="Source array variable to filter"
                               />
                             </Grid>
                             
                             <Grid item xs={6}>
                               <FormControl fullWidth>
                                 <InputLabel>Condition Logic</InputLabel>
                                 <Select
                                   value={operation.filterArrayConfig?.conditionLogic || 'AND'}
                                   onChange={(e) => {
                                     const updatedOperations = [...step.config.operations];
                                     updatedOperations[index] = {
                                       ...operation,
                                       filterArrayConfig: {
                                         ...operation.filterArrayConfig,
                                         conditionLogic: e.target.value
                                       }
                                     };
                                     handleConfigChange('operations', updatedOperations);
                                   }}
                                 >
                                   <MenuItem value="AND">AND (all conditions must match)</MenuItem>
                                   <MenuItem value="OR">OR (any condition can match)</MenuItem>
                                 </Select>
                               </FormControl>
                             </Grid>
                             
                             <Grid item xs={12}>
                               <FormControlLabel
                                 control={
                                   <Switch
                                     checked={operation.filterArrayConfig?.removeMatching !== false}
                                     onChange={(e) => {
                                       const updatedOperations = [...step.config.operations];
                                       updatedOperations[index] = {
                                         ...operation,
                                         filterArrayConfig: {
                                           ...operation.filterArrayConfig,
                                           removeMatching: e.target.checked
                                         }
                                       };
                                       handleConfigChange('operations', updatedOperations);
                                     }}
                                   />
                                 }
                                 label={operation.filterArrayConfig?.removeMatching !== false ? 
                                   "Remove items that match conditions" : 
                                   "Keep only items that match conditions"}
                               />
                             </Grid>
                             
                             {/* Filter Conditions Configuration */}
                             <Grid item xs={12}>
                               <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                                 🔍 Filter Conditions
                               </Typography>
                               
                               <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                 <Typography variant="body2" color="textSecondary">
                                   Configure conditions to filter array items
                                 </Typography>
                                 <Button
                                   size="small"
                                   startIcon={<AddIcon />}
                                   onClick={() => {
                                     const updatedOperations = [...step.config.operations];
                                     const conditions = operation.filterArrayConfig?.conditions || [];
                                     const newCondition = {
                                       id: `condition-${Date.now()}`,
                                       field: '',
                                       operator: 'exists',
                                       value: '',
                                       caseSensitive: true
                                     };
                                     updatedOperations[index] = {
                                       ...operation,
                                       filterArrayConfig: {
                                         ...operation.filterArrayConfig,
                                         conditions: [...conditions, newCondition]
                                       }
                                     };
                                     handleConfigChange('operations', updatedOperations);
                                   }}
                                 >
                                   Add Condition
                                 </Button>
                               </Box>
                               
                               {(operation.filterArrayConfig?.conditions || []).map((condition, conditionIndex) => (
                                 <Card
                                   key={condition.id}
                                   sx={{ 
                                     mb: 2, 
                                     p: 2, 
                                     backgroundColor: '#f8f9ff', 
                                     border: '1px solid #e1e5f2',
                                     position: 'relative'
                                   }}
                                 >
                                   <Box>
                                     <Typography variant="body2" fontWeight="bold" mb={2}>
                                       Condition {conditionIndex + 1}
                                     </Typography>
                                     
                                     <Grid container spacing={2}>
                                       <Grid item xs={6}>
                                         <TextField
                                           label="Field Path"
                                           value={condition.field || ''}
                                           onChange={(e) => {
                                             const updatedOperations = [...step.config.operations];
                                             const conditions = [...operation.filterArrayConfig.conditions];
                                             conditions[conditionIndex] = { ...condition, field: e.target.value };
                                             updatedOperations[index] = {
                                               ...operation,
                                               filterArrayConfig: {
                                                 ...operation.filterArrayConfig,
                                                 conditions
                                               }
                                             };
                                             handleConfigChange('operations', updatedOperations);
                                           }}
                                           fullWidth
                                           placeholder="name or user.email"
                                           helperText="Field path to check (supports dot notation)"
                                         />
                                       </Grid>
                                       
                                       <Grid item xs={6}>
                                         <FormControl fullWidth>
                                           <InputLabel>Operator</InputLabel>
                                           <Select
                                             value={condition.operator || 'exists'}
                                             onChange={(e) => {
                                               const updatedOperations = [...step.config.operations];
                                               const conditions = [...operation.filterArrayConfig.conditions];
                                               conditions[conditionIndex] = { ...condition, operator: e.target.value };
                                               updatedOperations[index] = {
                                                 ...operation,
                                                 filterArrayConfig: {
                                                   ...operation.filterArrayConfig,
                                                   conditions
                                                 }
                                               };
                                               handleConfigChange('operations', updatedOperations);
                                             }}
                                           >
                                             <MenuItem value="exists">Field exists</MenuItem>
                                             <MenuItem value="not_exists">Field does not exist</MenuItem>
                                             <MenuItem value="equals">Equals</MenuItem>
                                             <MenuItem value="not_equals">Not equals</MenuItem>
                                             <MenuItem value="contains">Contains</MenuItem>
                                             <MenuItem value="not_contains">Does not contain</MenuItem>
                                             <MenuItem value="starts_with">Starts with</MenuItem>
                                             <MenuItem value="ends_with">Ends with</MenuItem>
                                             <MenuItem value="greater_than">Greater than</MenuItem>
                                             <MenuItem value="less_than">Less than</MenuItem>
                                             <MenuItem value="greater_equal">Greater or equal</MenuItem>
                                             <MenuItem value="less_equal">Less or equal</MenuItem>
                                             <MenuItem value="is_empty">Is empty</MenuItem>
                                             <MenuItem value="is_not_empty">Is not empty</MenuItem>
                                             <MenuItem value="is_null">Is null</MenuItem>
                                             <MenuItem value="is_not_null">Is not null</MenuItem>
                                             <MenuItem value="in_array">In array</MenuItem>
                                             <MenuItem value="not_in_array">Not in array</MenuItem>
                                           </Select>
                                         </FormControl>
                                       </Grid>
                                       
                                       {/* Value field - show for operators that need a comparison value */}
                                       {['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 
                                         'greater_than', 'less_than', 'greater_equal', 'less_equal'].includes(condition.operator) && (
                                         <Grid item xs={6}>
                                           <TextField
                                             label="Value"
                                             value={condition.value || ''}
                                             onChange={(e) => {
                                               const updatedOperations = [...step.config.operations];
                                               const conditions = [...operation.filterArrayConfig.conditions];
                                               conditions[conditionIndex] = { ...condition, value: e.target.value };
                                               updatedOperations[index] = {
                                                 ...operation,
                                                 filterArrayConfig: {
                                                   ...operation.filterArrayConfig,
                                                   conditions
                                                 }
                                               };
                                               handleConfigChange('operations', updatedOperations);
                                             }}
                                             fullWidth
                                             placeholder="comparison value"
                                             helperText="Value to compare against"
                                           />
                                         </Grid>
                                       )}
                                       
                                       {/* Values array field - show for in_array/not_in_array operators */}
                                       {['in_array', 'not_in_array'].includes(condition.operator) && (
                                         <Grid item xs={6}>
                                           <TextField
                                             label="Values (comma-separated)"
                                             value={condition.values ? condition.values.join(', ') : ''}
                                             onChange={(e) => {
                                               const updatedOperations = [...step.config.operations];
                                               const conditions = [...operation.filterArrayConfig.conditions];
                                               const values = e.target.value.split(',').map(v => v.trim()).filter(v => v);
                                               conditions[conditionIndex] = { ...condition, values };
                                               updatedOperations[index] = {
                                                 ...operation,
                                                 filterArrayConfig: {
                                                   ...operation.filterArrayConfig,
                                                   conditions
                                                 }
                                               };
                                               handleConfigChange('operations', updatedOperations);
                                             }}
                                             fullWidth
                                             placeholder="value1, value2, value3"
                                             helperText="Comma-separated list of values"
                                           />
                                         </Grid>
                                       )}
                                       
                                       {/* Case sensitive option - show for string operators */}
                                       {['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with'].includes(condition.operator) && (
                                         <Grid item xs={6}>
                                           <FormControlLabel
                                             control={
                                               <Switch
                                                 checked={condition.caseSensitive !== false}
                                                 onChange={(e) => {
                                                   const updatedOperations = [...step.config.operations];
                                                   const conditions = [...operation.filterArrayConfig.conditions];
                                                   conditions[conditionIndex] = { ...condition, caseSensitive: e.target.checked };
                                                   updatedOperations[index] = {
                                                     ...operation,
                                                     filterArrayConfig: {
                                                       ...operation.filterArrayConfig,
                                                       conditions
                                                     }
                                                   };
                                                   handleConfigChange('operations', updatedOperations);
                                                 }}
                                               />
                                             }
                                             label="Case Sensitive"
                                           />
                                         </Grid>
                                       )}
                                     </Grid>
                                     
                                     <IconButton
                                       size="small"
                                       onClick={() => {
                                         const updatedOperations = [...step.config.operations];
                                         const conditions = operation.filterArrayConfig.conditions.filter((_, i) => i !== conditionIndex);
                                         updatedOperations[index] = {
                                           ...operation,
                                           filterArrayConfig: {
                                             ...operation.filterArrayConfig,
                                             conditions
                                           }
                                         };
                                         handleConfigChange('operations', updatedOperations);
                                       }}
                                       sx={{ 
                                         position: 'absolute',
                                         top: 8,
                                         right: 8,
                                         color: '#d32f2f' 
                                       }}
                                     >
                                       <DeleteIcon fontSize="small" />
                                     </IconButton>
                                   </Box>
                                 </Card>
                               ))}
                               
                               {(!operation.filterArrayConfig?.conditions || operation.filterArrayConfig.conditions.length === 0) && (
                                 <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                                   <Typography variant="body2">
                                     No filter conditions defined. Add conditions to configure what items to filter.
                                   </Typography>
                                 </Box>
                               )}
                             </Grid>
                           </>
                         )}
                         
                         {/* Exclude from Persistence Option - Common to all operations */}
                         <Grid item xs={12}>
                           <Divider sx={{ my: 1 }} />
                           <Tooltip title="When enabled, this variable will be available during flow execution but will NOT be saved to the database when the flow completes. Use this for large intermediate data to reduce storage size.">
                             <FormControlLabel
                               control={
                                 <Checkbox
                                   size="small"
                                   checked={operation.excludeFromPersistence || false}
                                   onChange={(e) => {
                                     const updatedOperations = [...step.config.operations];
                                     updatedOperations[index] = { ...operation, excludeFromPersistence: e.target.checked };
                                     handleConfigChange('operations', updatedOperations);
                                   }}
                                 />
                               }
                               label={
                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                   <Typography variant="body2">Exclude from persistence (transient variable)</Typography>
                                   <InfoIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                 </Box>
                               }
                             />
                           </Tooltip>
                           {operation.excludeFromPersistence && (
                             <Alert severity="info" sx={{ mt: 1 }} icon={<WarningIcon />}>
                               <Typography variant="body2">
                                 <strong>⚠️ Transient Variable:</strong> The variable <code>{operation.targetVariable || 'N/A'}</code> will NOT be saved to the database. 
                                 It will be available during flow execution but will be automatically deleted before the final save.
                                 Use this for large intermediate data to avoid MongoDB document size limits.
                               </Typography>
                             </Alert>
                           )}
                         </Grid>
                         
                      </Grid>
                    </Box>
                    </SortableVariableOperationCard>
                  ))}
                </SortableContext>
              </DndContext>
              
              {(!step.config?.operations || step.config.operations.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2">
                    No variable operations defined. Add operations to manipulate data.
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Quick Templates */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                🚀 Quick Operation Templates
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const dataCleaningOps = [
                      { id: `op-${Date.now()}-1`, type: 'string_operation', targetVariable: 'cleanName', sourceVariable: 'rawName', stringOp: 'trim', enabled: true },
                      { id: `op-${Date.now()}-2`, type: 'string_operation', targetVariable: 'cleanName', sourceVariable: 'cleanName', stringOp: 'uppercase', enabled: true },
                      { id: `op-${Date.now()}-3`, type: 'convert_type', targetVariable: 'amount', sourceVariable: 'amountStr', convertTo: 'number', enabled: true }
                    ];
                    handleConfigChange('operations', dataCleaningOps);
                  }}
                >
                  🧹 Data Cleaning
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const dateProcessingOps = [
                      { id: `op-${Date.now()}-1`, type: 'date_create', targetVariable: 'currentDate', dateSource: 'now', enabled: true },
                      { id: `op-${Date.now()}-2`, type: 'date_format', targetVariable: 'formattedDate', dateVariable: 'currentDate', dateFormat: 'YYYY-MM-DD', enabled: true },
                      { id: `op-${Date.now()}-3`, type: 'date_calculate', targetVariable: 'futureDate', dateVariable: 'currentDate', dateCalcOp: 'add', dateAmount: '30', dateUnit: 'days', enabled: true }
                    ];
                    handleConfigChange('operations', dateProcessingOps);
                  }}
                >
                  📅 Date Processing
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const dateFormatOp = {
                      id: `op-${Date.now()}`,
                      type: 'format_dates',
                      sourceVariable: 'extractedProducts',
                      targetVariable: 'formattedProducts',
                      dateFields: ['expiryDate'],
                      outputFormat: 'dd.mm.yyyy',
                      enabled: true
                    };
                    handleConfigChange('operations', [dateFormatOp]);
                  }}
                >
                  📅 Format Dates in Arrays
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const numberFormatOp = {
                      id: `op-${Date.now()}`,
                      type: 'format_numbers',
                      sourceVariable: 'extractedData',
                      targetVariable: 'formattedData',
                      numericFields: ['unitPrice', 'totalAmount'],
                      decimalPlaces: 2,
                      roundingMode: 'round',
                      enabled: true
                    };
                    handleConfigChange('operations', [numberFormatOp]);
                  }}
                >
                  🔢 Format Numbers in Arrays
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const stringArrayOp = {
                      id: `op-${Date.now()}`,
                      type: 'string_operation_array',
                      sourceVariable: 'extractedProducts',
                      targetVariable: 'cleanedProducts',
                      stringFields: ['name', 'description'],
                      stringOp: 'trim',
                      enabled: true
                    };
                    handleConfigChange('operations', [stringArrayOp]);
                  }}
                >
                  📝 Clean Strings in Arrays
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const arrayComboOp = {
                      id: `op-${Date.now()}`,
                      type: 'combine_arrays',
                      targetVariable: 'combinedLineItems',
                      sourceArrays: ['lineItems', 'responseItems'],
                      matchingConfig: {
                        method: 'fuzzy',
                        threshold: 0.6, // Configurable threshold for fuzzy matching
                        enablePreprocessing: true, // Remove prefixes/suffixes for better matching
                        matchFields: [
                          { field1: 'productName', field2: 'productName', weight: 1.0 }
                        ]
                      },
                      extractionConfig: {
                        fields: [
                          // Extract productName from array 2 (responseItems) as productName
                          { id: 'f1', extractionType: 'from_array', sourceArray: 'responseItems', sourceField: 'productName', targetField: 'productName' },
                          // Extract quantity from array 1 (lineItems) as quantity
                          { id: 'f2', extractionType: 'from_array', sourceArray: '', sourceField: 'quantity', targetField: 'quantity' },
                          // Extract quantity from array 2 (responseItems) as orderQuantity
                          { id: 'f3', extractionType: 'from_array', sourceArray: 'responseItems', sourceField: 'quantity', targetField: 'orderQuantity' },
                          // Extract expiryDate from array 1 (lineItems)
                          { id: 'f4', extractionType: 'from_array', sourceArray: '', sourceField: 'expiryDate', targetField: 'expiryDate' },
                          // Extract batch from array 1 (lineItems)
                          { id: 'f5', extractionType: 'from_array', sourceArray: '', sourceField: 'batch', targetField: 'batch' },
                          // Extract uom from array 2 (responseItems)
                          { id: 'f6', extractionType: 'from_array', sourceArray: 'responseItems', sourceField: 'uom', targetField: 'uom' },
                          // Extract unitPrice from array 1 (lineItems)
                          { id: 'f7', extractionType: 'from_array', sourceArray: '', sourceField: 'unitPrice', targetField: 'unitPrice' },
                          // Extract totalPrice from array 1 (lineItems)
                          { id: 'f8', extractionType: 'from_array', sourceArray: '', sourceField: 'totalPrice', targetField: 'totalPrice' },
                          // Conditional: exists_in_GR = true if product exists in array 1 (lineItems)
                          { id: 'c1', extractionType: 'conditional_field', condition: 'exists_in', sourceArray: '', targetField: 'exists_in_GR', trueValue: true, falseValue: false },
                          // Conditional: exists_in_PO = true if product exists in array 2 (responseItems)
                          { id: 'c2', extractionType: 'conditional_field', condition: 'exists_in', sourceArray: 'responseItems', targetField: 'exists_in_PO', trueValue: true, falseValue: false }
                        ]
                      },
                      conditionalFields: {
                        enabled: true,
                        matchedFields: [
                          { id: 'cond1', condition: 'exists_in', sourceArray: 'lineItems', fieldName: 'exists_in_GR', trueValue: true, falseValue: false },
                          { id: 'cond2', condition: 'exists_in', sourceArray: 'responseItems', fieldName: 'exists_in_PO', trueValue: true, falseValue: false },
                          { id: 'cond3', condition: 'matched_in', sourceArray: 'responseItems', fieldName: 'matched_with_PO', trueValue: 'matched', falseValue: 'unmatched' }
                        ],
                        unmatchedHandling: {
                          includeUnmatched: true, // Legacy support
                          includePrimaryUnmatched: true, // Include unmatched from lineItems
                          includeSecondaryUnmatched: true, // Include unmatched from responseItems
                          primaryUnmatchedField: 'source',
                          primaryUnmatchedValue: 'document_only',
                          secondaryUnmatchedField: 'source',
                          secondaryUnmatchedValue: 'api_only',
                          // Legacy support
                          unmatchedFromPrimary: { fieldName: 'exists_in_GR', value: true },
                          unmatchedFromSecondary: { fieldName: 'exists_in_PO', value: true }
                        }
                      },
                      sortingConfig: {
                        enabled: true,
                        rules: [
                          { field: 'exists_in_PO', order: 'desc', priority: 1, groupBy: true }
                        ]
                      },
                      enabled: true
                    };
                    handleConfigChange('operations', [arrayComboOp]);
                  }}
                >
                  🔗 Array Combination (LineItems + ResponseItems)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const dataStructureOps = [
                      { id: `op-${Date.now()}-1`, type: 'create_object', targetVariable: 'userProfile', objectData: '{"id": "{{userId}}", "name": "{{userName}}", "created": "{{currentDate}}"}', enabled: true },
                      { id: `op-${Date.now()}-2`, type: 'create_array', targetVariable: 'userActions', enabled: true },
                      { id: `op-${Date.now()}-3`, type: 'append_to_array', targetVariable: 'userActions', arrayVariable: 'userActions', value: 'profile_created', enabled: true }
                    ];
                    handleConfigChange('operations', dataStructureOps);
                  }}
                >
                  📦 Data Structures
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const filterArrayOp = {
                      id: `op-${Date.now()}`,
                      type: 'filter_array',
                      targetVariable: 'filteredItems',
                      filterArrayConfig: {
                        sourceArray: 'items',
                        conditionLogic: 'AND',
                        removeMatching: true, // Remove items that match
                        conditions: [
                          {
                            id: `condition-${Date.now()}-1`,
                            field: 'name',
                            operator: 'is_empty',
                            caseSensitive: true
                          },
                          {
                            id: `condition-${Date.now()}-2`,
                            field: 'status',
                            operator: 'equals',
                            value: 'inactive',
                            caseSensitive: false
                          }
                        ]
                      },
                      enabled: true
                    };
                    handleConfigChange('operations', [filterArrayOp]);
                  }}
                >
                  🔍 Filter Array (Remove Empty/Inactive)
                </Button>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">⚙️ Execution Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.continueOnError || false}
                  onChange={(e) => handleConfigChange('continueOnError', e.target.checked)}
                />
              }
              label="Continue on operation errors"
              helperText="Don't stop the workflow if an operation fails"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.logOperations || true}
                  onChange={(e) => handleConfigChange('logOperations', e.target.checked)}
                />
              }
              label="Log operation results"
              helperText="Save detailed logs of each operation for debugging"
            />

            <TextField
              label="Error Variable"
              value={step.config?.errorVariable || 'operationErrors'}
              onChange={(e) => handleConfigChange('errorVariable', e.target.value)}
              fullWidth
              helperText="Variable to store any operation errors"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderEndNodeConfig = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Configure how extracted data will be displayed and exported when the workflow completes.
      </Typography>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🎯 Output Interface Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Output Interface Type</InputLabel>
              <Select
                value={step.config?.interfaceType || 'summary_card'}
                onChange={(e) => handleConfigChange('interfaceType', e.target.value)}
              >
                <MenuItem value="summary_card">Summary Card</MenuItem>
                <MenuItem value="data_table">Data Table</MenuItem>
                <MenuItem value="detailed_report">Detailed Report</MenuItem>
                <MenuItem value="dashboard">Executive Dashboard</MenuItem>
                <MenuItem value="custom_template">Custom Template</MenuItem>
              </Select>
              <FormHelperText>
                {step.config?.interfaceType === 'summary_card' && "Clean card layout with key-value pairs - perfect for simple extractions"}
                {step.config?.interfaceType === 'data_table' && "Professional table format - ideal for structured data with multiple records"}
                {step.config?.interfaceType === 'detailed_report' && "Comprehensive report view - great for complex documents with multiple sections"}
                {step.config?.interfaceType === 'dashboard' && "Executive dashboard with charts and metrics - perfect for business insights"}
                {step.config?.interfaceType === 'custom_template' && "Full control with HTML templates - unlimited customization"}
              </FormHelperText>
            </FormControl>
            
            <TextField
              label="Success Message"
              value={step.config?.successMessage || ''}
              onChange={(e) => handleConfigChange('successMessage', e.target.value)}
              multiline
              rows={2}
              fullWidth
              placeholder="e.g., Data extraction completed successfully!"
              helperText="Message displayed when the workflow completes successfully"
            />
            
            <TextField
              label="Results Title"
              value={step.config?.resultsTitle || ''}
              onChange={(e) => handleConfigChange('resultsTitle', e.target.value)}
              fullWidth
              placeholder="e.g., Extracted Invoice Data, Processing Results"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Summary Card Configuration */}
      {step.config?.interfaceType === 'summary_card' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">📋 Summary Card Fields</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Configure which variables to display in the summary card and how they should be formatted.
              </Typography>
              
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Display Fields
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentFields = step.config?.displayFields || [];
                      const newField = {
                        id: `field-${Date.now()}`,
                        variable: '',
                        label: '',
                        type: 'text',
                        format: 'raw',
                        required: false,
                        priority: 'normal'
                      };
                      handleConfigChange('displayFields', [...currentFields, newField]);
                    }}
                  >
                    Add Field
                  </Button>
                </Box>
                
                {(step.config?.displayFields || []).length === 0 && (
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="textSecondary">
                      No display fields configured. Add fields to show extracted data in the results.
                    </Typography>
                  </Paper>
                )}
                
                {(step.config?.displayFields || []).map((field, index) => (
                  <Card key={field.id} sx={{ mb: 2, p: 2, backgroundColor: '#f0f8ff', border: '1px solid #e1e7ff' }}>
                    <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
                      <Box flexGrow={1}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Variable</InputLabel>
                              <Select
                                value={field.variable || ''}
                                onChange={(e) => {
                                  const updatedFields = [...(step.config?.displayFields || [])];
                                  updatedFields[index] = { ...field, variable: e.target.value };
                                  handleConfigChange('displayFields', updatedFields);
                                }}
                              >
                                {availableVariables.map(variable => (
                                  <MenuItem key={variable} value={variable}>
                                    {variable}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Display Label"
                              value={field.label || ''}
                              onChange={(e) => {
                                const updatedFields = [...(step.config?.displayFields || [])];
                                updatedFields[index] = { ...field, label: e.target.value };
                                handleConfigChange('displayFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., Company Name, Total Amount"
                            />
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Data Type</InputLabel>
                              <Select
                                value={field.type || 'text'}
                                onChange={(e) => {
                                  const updatedFields = [...(step.config?.displayFields || [])];
                                  updatedFields[index] = { ...field, type: e.target.value };
                                  handleConfigChange('displayFields', updatedFields);
                                }}
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="currency">Currency</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="time">Time</MenuItem>
                                <MenuItem value="boolean">Boolean</MenuItem>
                                <MenuItem value="array">Array/List</MenuItem>
                                <MenuItem value="object">Object</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Format</InputLabel>
                              <Select
                                value={field.format || 'raw'}
                                onChange={(e) => {
                                  const updatedFields = [...(step.config?.displayFields || [])];
                                  updatedFields[index] = { ...field, format: e.target.value };
                                  handleConfigChange('displayFields', updatedFields);
                                }}
                              >
                                <MenuItem value="raw">Raw Value</MenuItem>
                                <MenuItem value="formatted">Formatted</MenuItem>
                                <MenuItem value="truncated">Truncated (100 chars)</MenuItem>
                                <MenuItem value="highlighted">Highlighted</MenuItem>
                                {field.type === 'array' && <MenuItem value="count">Show Count Only</MenuItem>}
                                {field.type === 'array' && <MenuItem value="first">Show First Item</MenuItem>}
                                {field.type === 'currency' && <MenuItem value="usd">USD Currency</MenuItem>}
                                {field.type === 'date' && <MenuItem value="short">Short Date</MenuItem>}
                                {field.type === 'date' && <MenuItem value="long">Long Date</MenuItem>}
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Priority</InputLabel>
                              <Select
                                value={field.priority || 'normal'}
                                onChange={(e) => {
                                  const updatedFields = [...(step.config?.displayFields || [])];
                                  updatedFields[index] = { ...field, priority: e.target.value };
                                  handleConfigChange('displayFields', updatedFields);
                                }}
                              >
                                <MenuItem value="high">High (Bold)</MenuItem>
                                <MenuItem value="normal">Normal</MenuItem>
                                <MenuItem value="low">Low (Muted)</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                        
                        {/* Number and Currency Formatting Options */}
                        {(field.type === 'number' || field.type === 'currency') && (
                          <Box mt={2}>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Thousands Separator</InputLabel>
                                  <Select
                                    value={field.thousandsSeparator || ','}
                                    onChange={(e) => {
                                      const updatedFields = [...(step.config?.displayFields || [])];
                                      updatedFields[index] = { ...field, thousandsSeparator: e.target.value };
                                      handleConfigChange('displayFields', updatedFields);
                                    }}
                                    label="Thousands Separator"
                                  >
                                    <MenuItem value=",">Comma (,) - e.g., 1,000</MenuItem>
                                    <MenuItem value=".">Period (.) - e.g., 1.000</MenuItem>
                                    <MenuItem value=" ">Space ( ) - e.g., 1 000</MenuItem>
                                    <MenuItem value="">None - e.g., 1000</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Decimal Separator</InputLabel>
                                  <Select
                                    value={field.decimalSeparator || '.'}
                                    onChange={(e) => {
                                      const updatedFields = [...(step.config?.displayFields || [])];
                                      updatedFields[index] = { ...field, decimalSeparator: e.target.value };
                                      handleConfigChange('displayFields', updatedFields);
                                    }}
                                    label="Decimal Separator"
                                  >
                                    <MenuItem value=".">Period (.) - e.g., 10.50</MenuItem>
                                    <MenuItem value=",">Comma (,) - e.g., 10,50</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={field.type === 'currency' ? 6 : 12}>
                                <TextField
                                  size="small"
                                  label="Decimal Places"
                                  type="number"
                                  value={field.decimalPlaces !== undefined ? field.decimalPlaces : (field.type === 'currency' ? 2 : 0)}
                                  onChange={(e) => {
                                    const updatedFields = [...(step.config?.displayFields || [])];
                                    updatedFields[index] = { ...field, decimalPlaces: parseInt(e.target.value) || 0 };
                                    handleConfigChange('displayFields', updatedFields);
                                  }}
                                  fullWidth
                                  inputProps={{ min: 0, max: 10 }}
                                  helperText="Number of decimal places to display (0-10)"
                                />
                              </Grid>
                              
                              {/* Currency Symbol Configuration - only for currency fields */}
                              {field.type === 'currency' && (
                                <Grid item xs={6}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Currency Symbol</InputLabel>
                                    <Select
                                      value={field.currencySymbol || '$'}
                                      onChange={(e) => {
                                        const updatedFields = [...(step.config?.displayFields || [])];
                                        updatedFields[index] = { ...field, currencySymbol: e.target.value };
                                        handleConfigChange('displayFields', updatedFields);
                                      }}
                                      label="Currency Symbol"
                                    >
                                      <MenuItem value="$">Dollar ($)</MenuItem>
                                      <MenuItem value="€">Euro (€)</MenuItem>
                                      <MenuItem value="£">Pound (£)</MenuItem>
                                      <MenuItem value="¥">Yen (¥)</MenuItem>
                                      <MenuItem value="₹">Rupee (₹)</MenuItem>
                                      <MenuItem value="₽">Ruble (₽)</MenuItem>
                                      <MenuItem value="₦">Naira (₦)</MenuItem>
                                      <MenuItem value="R">Rand (R)</MenuItem>
                                      <MenuItem value="₨">Pakistani Rupee (₨)</MenuItem>
                                      <MenuItem value="Rp.">Indonesian Rupiah (Rp.)</MenuItem>
                                      <MenuItem value="custom">Custom...</MenuItem>
                                    </Select>
                                  </FormControl>
                                  {field.currencySymbol === 'custom' && (
                                    <TextField
                                      size="small"
                                      label="Custom Currency Symbol"
                                      value={field.customCurrencySymbol || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...(step.config?.displayFields || [])];
                                        updatedFields[index] = { 
                                          ...field, 
                                          customCurrencySymbol: e.target.value,
                                          currencySymbol: 'custom'
                                        };
                                        handleConfigChange('displayFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="Enter custom symbol (e.g., ¤, kr, etc.)"
                                      helperText="Enter your custom currency symbol"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                </Grid>
                              )}
                              
                              {/* Date Format Configuration - only for date fields */}
                              {field.type === 'date' && (
                                <Grid item xs={12}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Date Format</InputLabel>
                                    <Select
                                      value={field.dateFormat || 'MM/dd/yyyy'}
                                      onChange={(e) => {
                                        const updatedFields = [...(step.config?.displayFields || [])];
                                        updatedFields[index] = { ...field, dateFormat: e.target.value };
                                        handleConfigChange('displayFields', updatedFields);
                                      }}
                                      label="Date Format"
                                    >
                                      <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (12/25/2023)</MenuItem>
                                      <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (25/12/2023)</MenuItem>
                                      <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (2023-12-25)</MenuItem>
                                      <MenuItem value="dd.MM.yyyy">dd.MM.yyyy (25.12.2023)</MenuItem>
                                      <MenuItem value="MM-dd-yyyy">MM-dd-yyyy (12-25-2023)</MenuItem>
                                      <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (25-12-2023)</MenuItem>
                                      <MenuItem value="MMM dd, yyyy">MMM dd, yyyy (Dec 25, 2023)</MenuItem>
                                      <MenuItem value="MMMM dd, yyyy">MMMM dd, yyyy (December 25, 2023)</MenuItem>
                                      <MenuItem value="dd MMM yyyy">dd MMM yyyy (25 Dec 2023)</MenuItem>
                                      <MenuItem value="dd MMMM yyyy">dd MMMM yyyy (25 December 2023)</MenuItem>
                                      <MenuItem value="custom">Custom...</MenuItem>
                                    </Select>
                                  </FormControl>
                                  {field.dateFormat === 'custom' && (
                                    <TextField
                                      size="small"
                                      label="Custom Date Format"
                                      value={field.customDateFormat || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...(step.config?.displayFields || [])];
                                        updatedFields[index] = { 
                                          ...field, 
                                          customDateFormat: e.target.value,
                                          dateFormat: 'custom'
                                        };
                                        handleConfigChange('displayFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="e.g., DD/MM/YYYY, dd.mm.yyyy, MM-dd-yy"
                                      helperText="Enter custom date format (supports DD, MM, YYYY, dd, mm, yyyy)"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                </Grid>
                              )}

                              {/* Time Format Configuration - only for time fields */}
                              {field.type === 'time' && (
                                <Grid item xs={12}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Time Format</InputLabel>
                                    <Select
                                      value={field.timeFormat || 'HH:mm:ss'}
                                      onChange={(e) => {
                                        const updatedFields = [...(step.config?.displayFields || [])];
                                        updatedFields[index] = { ...field, timeFormat: e.target.value };
                                        handleConfigChange('displayFields', updatedFields);
                                      }}
                                      label="Time Format"
                                    >
                                      <MenuItem value="HH:mm:ss">24-hour with seconds (14:30:44)</MenuItem>
                                      <MenuItem value="HH:mm">24-hour without seconds (14:30)</MenuItem>
                                      <MenuItem value="hh:mm:ss A">12-hour with seconds (02:30:44 PM)</MenuItem>
                                      <MenuItem value="hh:mm A">12-hour without seconds (02:30 PM)</MenuItem>
                                      <MenuItem value="h:mm A">12-hour no leading zero (2:30 PM)</MenuItem>
                                      <MenuItem value="h:mm:ss A">12-hour no leading zero with seconds (2:30:44 PM)</MenuItem>
                                      <MenuItem value="custom">Custom...</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                      Choose how to display time values. Input format is HHMMSS (e.g., 035044 = 03:50:44)
                                    </FormHelperText>
                                  </FormControl>
                                  {field.timeFormat === 'custom' && (
                                    <TextField
                                      size="small"
                                      label="Custom Time Format"
                                      value={field.customTimeFormat || ''}
                                      onChange={(e) => {
                                        const updatedFields = [...(step.config?.displayFields || [])];
                                        updatedFields[index] = { 
                                          ...field, 
                                          customTimeFormat: e.target.value,
                                          timeFormat: 'custom'
                                        };
                                        handleConfigChange('displayFields', updatedFields);
                                      }}
                                      fullWidth
                                      placeholder="e.g., HH:mm, h:mm A, HH.mm.ss"
                                      helperText="Custom time format (HH=24hr, hh=12hr, mm=minutes, ss=seconds, A=AM/PM)"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                  
                                  {/* Timezone Configuration */}
                                  <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                                    <InputLabel>Timezone</InputLabel>
                                    <Select
                                      value={field.timezone || 'UTC'}
                                      onChange={(e) => {
                                        const updatedFields = [...(step.config?.displayFields || [])];
                                        updatedFields[index] = { ...field, timezone: e.target.value };
                                        handleConfigChange('displayFields', updatedFields);
                                      }}
                                      label="Timezone"
                                    >
                                      <MenuItem value="UTC">UTC (Server Time)</MenuItem>
                                      <MenuItem value="UTC+1">UTC+1 (Central European Time)</MenuItem>
                                      <MenuItem value="UTC+2">UTC+2 (Eastern European Time)</MenuItem>
                                      <MenuItem value="UTC+3">UTC+3 (Moscow Time)</MenuItem>
                                      <MenuItem value="UTC+4">UTC+4 (Gulf Standard Time)</MenuItem>
                                      <MenuItem value="UTC+5">UTC+5 (Pakistan Standard Time)</MenuItem>
                                      <MenuItem value="UTC+6">UTC+6 (Bangladesh Standard Time)</MenuItem>
                                      <MenuItem value="UTC+7">UTC+7 (Indochina Time)</MenuItem>
                                      <MenuItem value="UTC+8">UTC+8 (China Standard Time)</MenuItem>
                                      <MenuItem value="UTC+9">UTC+9 (Japan Standard Time)</MenuItem>
                                      <MenuItem value="UTC+10">UTC+10 (Australian Eastern Time)</MenuItem>
                                      <MenuItem value="UTC+11">UTC+11 (Solomon Islands Time)</MenuItem>
                                      <MenuItem value="UTC+12">UTC+12 (New Zealand Time)</MenuItem>
                                      <MenuItem value="UTC-1">UTC-1 (Cape Verde Time)</MenuItem>
                                      <MenuItem value="UTC-2">UTC-2 (South Georgia Time)</MenuItem>
                                      <MenuItem value="UTC-3">UTC-3 (Argentina Time)</MenuItem>
                                      <MenuItem value="UTC-4">UTC-4 (Atlantic Standard Time)</MenuItem>
                                      <MenuItem value="UTC-5">UTC-5 (Eastern Standard Time)</MenuItem>
                                      <MenuItem value="UTC-6">UTC-6 (Central Standard Time)</MenuItem>
                                      <MenuItem value="UTC-7">UTC-7 (Mountain Standard Time)</MenuItem>
                                      <MenuItem value="UTC-8">UTC-8 (Pacific Standard Time)</MenuItem>
                                      <MenuItem value="UTC-9">UTC-9 (Alaska Standard Time)</MenuItem>
                                      <MenuItem value="UTC-10">UTC-10 (Hawaii Standard Time)</MenuItem>
                                      <MenuItem value="UTC-11">UTC-11 (Samoa Standard Time)</MenuItem>
                                      <MenuItem value="UTC-12">UTC-12 (Baker Island Time)</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                      Select the timezone for time display. This will convert server time to your local timezone.
                                    </FormHelperText>
                                  </FormControl>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        )}
                        
                        {field.type === 'array' && (
                          <Box mt={2}>
                            <TextField
                              size="small"
                              label="Array Item Template"
                              value={field.itemTemplate || ''}
                              onChange={(e) => {
                                const updatedFields = [...(step.config?.displayFields || [])];
                                updatedFields[index] = { ...field, itemTemplate: e.target.value };
                                handleConfigChange('displayFields', updatedFields);
                              }}
                              placeholder="e.g., {{productName}} - {{quantity}} x {{price}}"
                              fullWidth
                              helperText="Template for displaying each array item (use {{property}} syntax)"
                            />
                          </Box>
                        )}
                      </Box>
                      
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => {
                          const updatedFields = (step.config?.displayFields || []).filter((_, i) => i !== index);
                          handleConfigChange('displayFields', updatedFields);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Data Table Configuration */}
      {step.config?.interfaceType === 'data_table' && (() => {
        // Use localStep (not step) here to avoid TDZ — const step below shadows the outer prop for this entire block
        const isMultiTab = localStep?.config?.enableMultiTab && localStep?.config?.tabs?.length > 0;
        const safeTabIdx = isMultiTab ? Math.min(activeDataTableTab, (localStep?.config?.tabs?.length || 1) - 1) : 0;

        const TAB_FIELDS = new Set(['dataSource', 'tableColumns', 'headerFields', 'rowColorRules', 'cellColorRules', 'includeHeaderFieldsInExport']);

        // Proxy step.config so reads of tab-specific fields route to tabs[activeTab]
        // eslint-disable-next-line no-shadow
        const step = new Proxy(
          { config: null },
          {
            get(target, prop) {
              if (prop !== 'config') return undefined;
              const realConfig = localStep?.config || {};
              if (!isMultiTab) return realConfig;
              return new Proxy(realConfig, {
                get(cfg, cfgProp) {
                  if (TAB_FIELDS.has(cfgProp)) {
                    return cfg.tabs?.[safeTabIdx]?.[cfgProp];
                  }
                  return cfg[cfgProp];
                }
              });
            }
          }
        );

        // Shadow handleConfigChange so writes to tab-specific fields route to tabs[activeTab]
        // eslint-disable-next-line no-shadow
        const handleConfigChange = (field, value) => {
          if (isMultiTab && TAB_FIELDS.has(field)) {
            setLocalStep((prev) => {
              const tabs = [...(prev.config?.tabs || [])];
              if (tabs[safeTabIdx]) {
                tabs[safeTabIdx] = { ...tabs[safeTabIdx], [field]: value };
              }
              return { ...prev, config: { ...prev.config, tabs } };
            });
          } else {
            setLocalStep((prev) => {
              return { ...prev, config: { ...prev.config, [field]: value } };
            });
          }
          isDirtyRef.current = true;
          scheduleAutoCommit();
        };

        return (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">📊 Data Table Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Configure table columns, header fields, and data source for displaying structured data.
              </Typography>

              {/* Multi-Tab Toggle */}
              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localStep?.config?.enableMultiTab || false}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        handleConfigChange('enableMultiTab', enabled);
                        if (enabled && (!localStep?.config?.tabs || localStep.config.tabs.length === 0)) {
                          const migratedTab = {
                            id: `tab-${Date.now()}`,
                            label: 'Sheet 1',
                            dataSource: localStep?.config?.dataSource || '',
                            tableColumns: localStep?.config?.tableColumns || [],
                            headerFields: localStep?.config?.headerFields || [],
                            includeHeaderFieldsInExport: localStep?.config?.includeHeaderFieldsInExport || false,
                            rowColorRules: localStep?.config?.rowColorRules || [],
                            cellColorRules: localStep?.config?.cellColorRules || []
                          };
                          handleConfigChange('tabs', [migratedTab]);
                          setActiveDataTableTab(0);
                        }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Enable Multiple Tabs</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Display data across multiple tabs (each tab becomes a separate Excel sheet on export)
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Tab Management Bar */}
              {isMultiTab && (
                <Box>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                    <Tabs
                      value={safeTabIdx}
                      onChange={(e, newVal) => setActiveDataTableTab(newVal)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ flexGrow: 1 }}
                    >
                      {(localStep?.config?.tabs || []).map((tab, idx) => (
                        <Tab key={tab.id} label={tab.label || `Sheet ${idx + 1}`} />
                      ))}
                    </Tabs>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const tabs = [...(localStep?.config?.tabs || [])];
                        tabs.push({
                          id: `tab-${Date.now()}`,
                          label: `Sheet ${tabs.length + 1}`,
                          dataSource: '',
                          tableColumns: [],
                          headerFields: [],
                          includeHeaderFieldsInExport: false,
                          rowColorRules: [],
                          cellColorRules: []
                        });
                        handleConfigChange('tabs', tabs);
                        setActiveDataTableTab(tabs.length - 1);
                      }}
                      sx={{ ml: 1 }}
                    >
                      Add Tab
                    </Button>
                  </Box>

                  {/* Tab label and remove */}
                  <Box display="flex" alignItems="center" gap={2} mt={2}>
                    <TextField
                      size="small"
                      label="Tab Label (Sheet Name)"
                      value={localStep?.config?.tabs?.[safeTabIdx]?.label || ''}
                      onChange={(e) => {
                        const tabs = [...(localStep?.config?.tabs || [])];
                        tabs[safeTabIdx] = { ...tabs[safeTabIdx], label: e.target.value };
                        handleConfigChange('tabs', tabs);
                      }}
                      sx={{ flexGrow: 1 }}
                      placeholder="e.g., Invoice Lines, Summary"
                    />
                    {(localStep?.config?.tabs || []).length > 1 && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => {
                          const tabs = (localStep?.config?.tabs || []).filter((_, i) => i !== safeTabIdx);
                          handleConfigChange('tabs', tabs);
                          setActiveDataTableTab(Math.max(0, safeTabIdx - 1));
                        }}
                      >
                        Remove Tab
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
              
              <FormControl fullWidth size="small">
                <InputLabel>Data Source Variable</InputLabel>
                <Select
                  value={step.config?.dataSource || ''}
                  onChange={(e) => handleConfigChange('dataSource', e.target.value)}
                >
                  {availableVariables.map(variable => (
                    <MenuItem key={variable} value={variable}>
                      {variable}
                      {(variable.toLowerCase().includes('items') || 
                        variable.toLowerCase().includes('products') || 
                        variable.toLowerCase().includes('data') ||
                        variable.toLowerCase().includes('list') ||
                        variable.toLowerCase().includes('array')) && 
                        <Chip size="small" label="Array" sx={{ ml: 1, height: 16 }} />
                      }
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Select any variable that contains array/table data. Variables with "Array" label are likely candidates.
                </FormHelperText>
              </FormControl>

              {/* Header Fields Configuration */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  📝 Header Fields (shown above table)
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Context fields displayed before the table (e.g., Purchase Order, Customer Name, Date)
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentFields = step.config?.headerFields || [];
                      const newField = {
                        id: `header-${Date.now()}`,
                        name: '',
                        label: '',
                        type: 'text',
                        defaultValue: '',
                        width: 'full',
                        fontColor: '#000000'
                      };
                      handleConfigChange('headerFields', [...currentFields, newField]);
                    }}
                  >
                    Add Header Field
                  </Button>
                </Box>

                {(step.config?.headerFields || []).map((field, index) => (
                  <Card key={field.id} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9ff', border: '1px solid #e3e7ff' }}>
                    <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
                      <Box flexGrow={1}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Field Name"
                              value={field.name}
                              onChange={(e) => {
                                const updatedFields = [...(step.config?.headerFields || [])];
                                updatedFields[index] = { ...field, name: e.target.value };
                                handleConfigChange('headerFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., purchaseOrder"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              size="small"
                              label="Field Label"
                              value={field.label}
                              onChange={(e) => {
                                const updatedFields = [...(step.config?.headerFields || [])];
                                updatedFields[index] = { ...field, label: e.target.value };
                                handleConfigChange('headerFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., Purchase Order Number"
                            />
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Field Type</InputLabel>
                              <Select
                                value={field.type}
                                onChange={(e) => {
                                  const updatedFields = [...(step.config?.headerFields || [])];
                                  updatedFields[index] = { ...field, type: e.target.value };
                                  handleConfigChange('headerFields', updatedFields);
                                }}
                                label="Field Type"
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="time">Time</MenuItem>
                                <MenuItem value="textarea">Text Area</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          {/* Character Limit Configuration for the second header fields section */}
                          {(field.type === 'text' || field.type === 'textarea' || field.type === 'email') && (
                            <Grid item xs={12} sx={{ mt: 2 }}>
                              <Box display="flex" gap={2} alignItems="center">
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={field.enableCharacterLimit || false}
                                      onChange={(e) => {
                                        const updatedFields = [...(step.config?.headerFields || [])];
                                        updatedFields[index] = { 
                                          ...field, 
                                          enableCharacterLimit: e.target.checked,
                                          ...(e.target.checked ? {} : { maxCharacters: undefined })
                                        };
                                        handleConfigChange('headerFields', updatedFields);
                                      }}
                                    />
                                  }
                                  label="Enable Character Limit"
                                />
                                
                                {field.enableCharacterLimit && (
                                  <TextField
                                    size="small"
                                    label="Max Characters"
                                    type="number"
                                    value={field.maxCharacters || ''}
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value) || undefined;
                                      const updatedFields = [...(step.config?.headerFields || [])];
                                      updatedFields[index] = { ...field, maxCharacters: value };
                                      handleConfigChange('headerFields', updatedFields);
                                    }}
                                    inputProps={{
                                      min: 1,
                                      max: 10000
                                    }}
                                    sx={{ width: 150 }}
                                    placeholder="e.g., 100"
                                    helperText="1-10000 characters"
                                  />
                                )}
                              </Box>
                              
                              {field.enableCharacterLimit && field.maxCharacters && (
                                <Box mt={1}>
                                  <Typography variant="caption" color="textSecondary">
                                    Users will see a character counter showing "{field.maxCharacters - (field.defaultValue?.length || 0)} characters remaining" as they type
                                  </Typography>
                                </Box>
                              )}
                            </Grid>
                          )}
                          
                          {/* Date Format Configuration - only for date fields */}
                          {field.type === 'date' && (
                            <Grid item xs={12}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Date Format</InputLabel>
                                <Select
                                  value={field.dateFormat || 'MM/dd/yyyy'}
                                  onChange={(e) => {
                                    const updatedFields = [...(step.config?.headerFields || [])];
                                    updatedFields[index] = { ...field, dateFormat: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  label="Date Format"
                                >
                                  <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (12/25/2023)</MenuItem>
                                  <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (25/12/2023)</MenuItem>
                                  <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (2023-12-25)</MenuItem>
                                  <MenuItem value="dd.MM.yyyy">dd.MM.yyyy (25.12.2023)</MenuItem>
                                  <MenuItem value="MM-dd-yyyy">MM-dd-yyyy (12-25-2023)</MenuItem>
                                  <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (25-12-2023)</MenuItem>
                                  <MenuItem value="MMM dd, yyyy">MMM dd, yyyy (Dec 25, 2023)</MenuItem>
                                  <MenuItem value="MMMM dd, yyyy">MMMM dd, yyyy (December 25, 2023)</MenuItem>
                                  <MenuItem value="dd MMM yyyy">dd MMM yyyy (25 Dec 2023)</MenuItem>
                                  <MenuItem value="dd MMMM yyyy">dd MMMM yyyy (25 December 2023)</MenuItem>
                                  <MenuItem value="custom">Custom...</MenuItem>
                                </Select>
                              </FormControl>
                              {field.dateFormat === 'custom' && (
                                <TextField
                                  size="small"
                                  label="Custom Date Format"
                                  value={field.customDateFormat || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...(step.config?.headerFields || [])];
                                    updatedFields[index] = { 
                                      ...field, 
                                      customDateFormat: e.target.value,
                                      dateFormat: 'custom'
                                    };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., DD/MM/YYYY, dd.mm.yyyy, MM-dd-yy"
                                  helperText="Enter custom date format (supports DD, MM, YYYY, dd, mm, yyyy)"
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Grid>
                          )}

                          {/* Time Format Configuration - only for time fields */}
                          {field.type === 'time' && (
                            <Grid item xs={12}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Time Format</InputLabel>
                                <Select
                                  value={field.timeFormat || 'HH:mm:ss'}
                                  onChange={(e) => {
                                    const updatedFields = [...(step.config?.headerFields || [])];
                                    updatedFields[index] = { ...field, timeFormat: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  label="Time Format"
                                >
                                  <MenuItem value="HH:mm:ss">24-hour with seconds (14:30:44)</MenuItem>
                                  <MenuItem value="HH:mm">24-hour without seconds (14:30)</MenuItem>
                                  <MenuItem value="hh:mm:ss A">12-hour with seconds (02:30:44 PM)</MenuItem>
                                  <MenuItem value="hh:mm A">12-hour without seconds (02:30 PM)</MenuItem>
                                  <MenuItem value="h:mm A">12-hour no leading zero (2:30 PM)</MenuItem>
                                  <MenuItem value="h:mm:ss A">12-hour no leading zero with seconds (2:30:44 PM)</MenuItem>
                                  <MenuItem value="custom">Custom...</MenuItem>
                                </Select>
                                <FormHelperText>
                                  Choose how to display time values. Input format is HHMMSS (e.g., 035044 = 03:50:44)
                                </FormHelperText>
                              </FormControl>
                              {field.timeFormat === 'custom' && (
                                <TextField
                                  size="small"
                                  label="Custom Time Format"
                                  value={field.customTimeFormat || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...(step.config?.headerFields || [])];
                                    updatedFields[index] = { 
                                      ...field, 
                                      customTimeFormat: e.target.value,
                                      timeFormat: 'custom'
                                    };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  fullWidth
                                  placeholder="e.g., HH:mm, h:mm A, HH.mm.ss"
                                  helperText="Custom time format (HH=24hr, hh=12hr, mm=minutes, ss=seconds, A=AM/PM)"
                                  sx={{ mt: 1 }}
                                />
                              )}
                              
                              {/* Timezone Configuration */}
                              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                                <InputLabel>Timezone</InputLabel>
                                <Select
                                  value={field.timezone || 'UTC'}
                                  onChange={(e) => {
                                    const updatedFields = [...(step.config?.headerFields || [])];
                                    updatedFields[index] = { ...field, timezone: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  label="Timezone"
                                >
                                  <MenuItem value="UTC">UTC (Server Time)</MenuItem>
                                  <MenuItem value="UTC+1">UTC+1 (Central European Time)</MenuItem>
                                  <MenuItem value="UTC+2">UTC+2 (Eastern European Time)</MenuItem>
                                  <MenuItem value="UTC+3">UTC+3 (Moscow Time)</MenuItem>
                                  <MenuItem value="UTC+4">UTC+4 (Gulf Standard Time)</MenuItem>
                                  <MenuItem value="UTC+5">UTC+5 (Pakistan Standard Time)</MenuItem>
                                  <MenuItem value="UTC+6">UTC+6 (Bangladesh Standard Time)</MenuItem>
                                  <MenuItem value="UTC+7">UTC+7 (Indochina Time)</MenuItem>
                                  <MenuItem value="UTC+8">UTC+8 (China Standard Time)</MenuItem>
                                  <MenuItem value="UTC+9">UTC+9 (Japan Standard Time)</MenuItem>
                                  <MenuItem value="UTC+10">UTC+10 (Australian Eastern Time)</MenuItem>
                                  <MenuItem value="UTC+11">UTC+11 (Solomon Islands Time)</MenuItem>
                                  <MenuItem value="UTC+12">UTC+12 (New Zealand Time)</MenuItem>
                                  <MenuItem value="UTC-1">UTC-1 (Cape Verde Time)</MenuItem>
                                  <MenuItem value="UTC-2">UTC-2 (South Georgia Time)</MenuItem>
                                  <MenuItem value="UTC-3">UTC-3 (Argentina Time)</MenuItem>
                                  <MenuItem value="UTC-4">UTC-4 (Atlantic Standard Time)</MenuItem>
                                  <MenuItem value="UTC-5">UTC-5 (Eastern Standard Time)</MenuItem>
                                  <MenuItem value="UTC-6">UTC-6 (Central Standard Time)</MenuItem>
                                  <MenuItem value="UTC-7">UTC-7 (Mountain Standard Time)</MenuItem>
                                  <MenuItem value="UTC-8">UTC-8 (Pacific Standard Time)</MenuItem>
                                  <MenuItem value="UTC-9">UTC-9 (Alaska Standard Time)</MenuItem>
                                  <MenuItem value="UTC-10">UTC-10 (Hawaii Standard Time)</MenuItem>
                                  <MenuItem value="UTC-11">UTC-11 (Samoa Standard Time)</MenuItem>
                                  <MenuItem value="UTC-12">UTC-12 (Baker Island Time)</MenuItem>
                                </Select>
                                <FormHelperText>
                                  Select the timezone for time display. This will convert server time to your local timezone.
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                          )}
                          
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Width</InputLabel>
                              <Select
                                value={field.width || 'full'}
                                onChange={(e) => {
                                  const updatedFields = [...(step.config?.headerFields || [])];
                                  updatedFields[index] = { ...field, width: e.target.value };
                                  handleConfigChange('headerFields', updatedFields);
                                }}
                                label="Width"
                              >
                                <MenuItem value="full">Full Width</MenuItem>
                                <MenuItem value="half">Half Width</MenuItem>
                                <MenuItem value="third">One Third</MenuItem>
                                <MenuItem value="quarter">One Quarter</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={4}>
                            <Box display="flex" flexDirection="column" gap={1}>
                              <Typography variant="caption" color="textSecondary">
                                Font Color
                              </Typography>
                              <Box display="flex" alignItems="center" gap={1}>
                                <input
                                  type="color"
                                  value={field.fontColor || '#000000'}
                                  onChange={(e) => {
                                    const updatedFields = [...(step.config?.headerFields || [])];
                                    updatedFields[index] = { ...field, fontColor: e.target.value };
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                />
                                <Typography variant="caption" color="textSecondary">
                                  {field.fontColor || '#000000'}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={4}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Box display="flex" flexDirection="column" gap={1}>
                                <Typography variant="caption" color="textSecondary">
                                  Actions
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const updatedFields = (step.config?.headerFields || []).filter((_, i) => i !== index);
                                    handleConfigChange('headerFields', updatedFields);
                                  }}
                                  sx={{ color: 'error.main', alignSelf: 'flex-start' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              size="small"
                              label="Default Value"
                              value={field.defaultValue}
                              onChange={(e) => {
                                const updatedFields = [...(step.config?.headerFields || [])];
                                updatedFields[index] = { ...field, defaultValue: e.target.value };
                                handleConfigChange('headerFields', updatedFields);
                              }}
                              fullWidth
                              placeholder="e.g., {{extractedPurchaseOrder}}"
                              helperText="Can use variables like {{extractedPurchaseOrder}}"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Card>
                ))}

                {(!step.config?.headerFields || step.config.headerFields.length === 0) && (
                  <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="body2">
                      No header fields defined. Add header fields to show context information above the table.
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />
              
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Table Columns
                  </Typography>
                </Box>
                
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={step.config?.tableColumns || []}
                    strategy={verticalListSortingStrategy}
                  >
                    {(step.config?.tableColumns || []).map((column, index) => (
                      <SortableColumnCard
                        key={column.id}
                        column={column}
                        index={index}
                        onColumnChange={(index, updatedColumn) => {
                          const updatedColumns = [...(step.config?.tableColumns || [])];
                          updatedColumns[index] = updatedColumn;
                          handleConfigChange('tableColumns', updatedColumns);
                        }}
                        onColumnDelete={(index) => {
                          const updatedColumns = (step.config?.tableColumns || []).filter((_, i) => i !== index);
                          handleConfigChange('tableColumns', updatedColumns);
                        }}
                      >
                        <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3}>
                        <TextField
                          size="small"
                          label="Property Key"
                          value={column.key || ''}
                          onChange={(e) => {
                            const updatedColumns = [...(step.config?.tableColumns || [])];
                            updatedColumns[index] = { ...column, key: e.target.value };
                            handleConfigChange('tableColumns', updatedColumns);
                          }}
                          fullWidth
                          placeholder="e.g., productName"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          size="small"
                          label="Column Label"
                          value={column.label || ''}
                          onChange={(e) => {
                            const updatedColumns = [...(step.config?.tableColumns || [])];
                            updatedColumns[index] = { ...column, label: e.target.value };
                            handleConfigChange('tableColumns', updatedColumns);
                          }}
                          fullWidth
                          placeholder="e.g., Product Name"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={column.type || 'text'}
                            onChange={(e) => {
                              const updatedColumns = [...(step.config?.tableColumns || [])];
                              updatedColumns[index] = { ...column, type: e.target.value };
                              handleConfigChange('tableColumns', updatedColumns);
                            }}
                          >
                            <MenuItem value="text">Text</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="currency">Currency</MenuItem>
                            <MenuItem value="date">Date</MenuItem>
                            <MenuItem value="time">Time</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Width</InputLabel>
                          <Select
                            value={column.width || 'auto'}
                            onChange={(e) => {
                              const updatedColumns = [...(step.config?.tableColumns || [])];
                              updatedColumns[index] = { ...column, width: e.target.value };
                              handleConfigChange('tableColumns', updatedColumns);
                            }}
                          >
                            <MenuItem value="auto">Auto</MenuItem>
                            <MenuItem value="narrow">Narrow</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="wide">Wide</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      {/* Number and Currency Formatting Options */}
                      {(column.type === 'number' || column.type === 'currency') && (
                        <>
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Thousands Separator</InputLabel>
                              <Select
                                value={column.thousandsSeparator || ','}
                                onChange={(e) => {
                                  const updatedColumns = [...(step.config?.tableColumns || [])];
                                  updatedColumns[index] = { ...column, thousandsSeparator: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                label="Thousands Separator"
                              >
                                <MenuItem value=",">Comma (,) - e.g., 1,000</MenuItem>
                                <MenuItem value=".">Period (.) - e.g., 1.000</MenuItem>
                                <MenuItem value=" ">Space ( ) - e.g., 1 000</MenuItem>
                                <MenuItem value="">None - e.g., 1000</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Decimal Separator</InputLabel>
                              <Select
                                value={column.decimalSeparator || '.'}
                                onChange={(e) => {
                                  const updatedColumns = [...(step.config?.tableColumns || [])];
                                  updatedColumns[index] = { ...column, decimalSeparator: e.target.value };
                                  handleConfigChange('tableColumns', updatedColumns);
                                }}
                                label="Decimal Separator"
                              >
                                <MenuItem value=".">Period (.) - e.g., 10.50</MenuItem>
                                <MenuItem value=",">Comma (,) - e.g., 10,50</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={column.type === 'currency' ? 3 : 4}>
                            <TextField
                              size="small"
                              label="Decimal Places"
                              type="number"
                              value={column.decimalPlaces !== undefined ? column.decimalPlaces : (column.type === 'currency' ? 2 : 0)}
                              onChange={(e) => {
                                const updatedColumns = [...(step.config?.tableColumns || [])];
                                updatedColumns[index] = { ...column, decimalPlaces: parseInt(e.target.value) || 0 };
                                handleConfigChange('tableColumns', updatedColumns);
                              }}
                              fullWidth
                              inputProps={{ min: 0, max: 10 }}
                              helperText="Decimal places (0-10)"
                            />
                          </Grid>
                          
                          {/* Currency Symbol Configuration - only for currency columns */}
                          {column.type === 'currency' && (
                            <Grid item xs={3}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Currency Symbol</InputLabel>
                                <Select
                                  value={column.currencySymbol || '$'}
                                  onChange={(e) => {
                                    const updatedColumns = [...(step.config?.tableColumns || [])];
                                    updatedColumns[index] = { ...column, currencySymbol: e.target.value };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  label="Currency Symbol"
                                >
                                  <MenuItem value="$">Dollar ($)</MenuItem>
                                  <MenuItem value="€">Euro (€)</MenuItem>
                                  <MenuItem value="£">Pound (£)</MenuItem>
                                  <MenuItem value="¥">Yen (¥)</MenuItem>
                                  <MenuItem value="₹">Rupee (₹)</MenuItem>
                                  <MenuItem value="₽">Ruble (₽)</MenuItem>
                                  <MenuItem value="₦">Naira (₦)</MenuItem>
                                  <MenuItem value="R">Rand (R)</MenuItem>
                                  <MenuItem value="₨">Pakistani Rupee (₨)</MenuItem>
                                  <MenuItem value="Rp.">Indonesian Rupiah (Rp.)</MenuItem>
                                  <MenuItem value="custom">Custom...</MenuItem>
                                </Select>
                              </FormControl>
                              {column.currencySymbol === 'custom' && (
                                <TextField
                                  size="small"
                                  label="Custom Currency Symbol"
                                  value={column.customCurrencySymbol || ''}
                                  onChange={(e) => {
                                    const updatedColumns = [...(step.config?.tableColumns || [])];
                                    updatedColumns[index] = { 
                                      ...column, 
                                      customCurrencySymbol: e.target.value,
                                      currencySymbol: 'custom'
                                    };
                                    handleConfigChange('tableColumns', updatedColumns);
                                  }}
                                  fullWidth
                                  placeholder="Enter custom symbol (e.g., ¤, kr, etc.)"
                                  helperText="Enter your custom currency symbol"
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Grid>
                          )}
                        </>
                      )}
                      
                      {/* Date Format Configuration - only for date columns */}
                      {column.type === 'date' && (
                        <Grid item xs={12}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Date Format</InputLabel>
                            <Select
                              value={column.dateFormat || 'MM/dd/yyyy'}
                              onChange={(e) => {
                                const updatedColumns = [...(step.config?.tableColumns || [])];
                                updatedColumns[index] = { ...column, dateFormat: e.target.value };
                                handleConfigChange('tableColumns', updatedColumns);
                              }}
                              label="Date Format"
                            >
                              <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (12/25/2023)</MenuItem>
                              <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (25/12/2023)</MenuItem>
                              <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (2023-12-25)</MenuItem>
                              <MenuItem value="dd.MM.yyyy">dd.MM.yyyy (25.12.2023)</MenuItem>
                              <MenuItem value="MM-dd-yyyy">MM-dd-yyyy (12-25-2023)</MenuItem>
                              <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (25-12-2023)</MenuItem>
                              <MenuItem value="MMM dd, yyyy">MMM dd, yyyy (Dec 25, 2023)</MenuItem>
                              <MenuItem value="MMMM dd, yyyy">MMMM dd, yyyy (December 25, 2023)</MenuItem>
                              <MenuItem value="dd MMM yyyy">dd MMM yyyy (25 Dec 2023)</MenuItem>
                              <MenuItem value="dd MMMM yyyy">dd MMMM yyyy (25 December 2023)</MenuItem>
                              <MenuItem value="custom">Custom...</MenuItem>
                            </Select>
                          </FormControl>
                          {column.dateFormat === 'custom' && (
                            <TextField
                              size="small"
                              label="Custom Date Format"
                              value={column.customDateFormat || ''}
                              onChange={(e) => {
                                const updatedColumns = [...(step.config?.tableColumns || [])];
                                updatedColumns[index] = { 
                                  ...column, 
                                  customDateFormat: e.target.value,
                                  dateFormat: 'custom'
                                };
                                handleConfigChange('tableColumns', updatedColumns);
                              }}
                              fullWidth
                              placeholder="e.g., DD/MM/YYYY, dd.mm.yyyy, MM-dd-yy"
                              helperText="Enter custom date format (supports DD, MM, YYYY, dd, mm, yyyy)"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Grid>
                      )}

                      {/* Time Format Configuration - only for time columns */}
                      {column.type === 'time' && (
                        <Grid item xs={12}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Time Format</InputLabel>
                            <Select
                              value={column.timeFormat || 'HH:mm:ss'}
                              onChange={(e) => {
                                const updatedColumns = [...(step.config?.tableColumns || [])];
                                updatedColumns[index] = { ...column, timeFormat: e.target.value };
                                handleConfigChange('tableColumns', updatedColumns);
                              }}
                              label="Time Format"
                            >
                              <MenuItem value="HH:mm:ss">24-hour with seconds (14:30:44)</MenuItem>
                              <MenuItem value="HH:mm">24-hour without seconds (14:30)</MenuItem>
                              <MenuItem value="hh:mm:ss A">12-hour with seconds (02:30:44 PM)</MenuItem>
                              <MenuItem value="hh:mm A">12-hour without seconds (02:30 PM)</MenuItem>
                              <MenuItem value="h:mm A">12-hour no leading zero (2:30 PM)</MenuItem>
                              <MenuItem value="h:mm:ss A">12-hour no leading zero with seconds (2:30:44 PM)</MenuItem>
                              <MenuItem value="custom">Custom...</MenuItem>
                            </Select>
                            <FormHelperText>
                              Choose how to display time values. Input format is HHMMSS (e.g., 035044 = 03:50:44)
                            </FormHelperText>
                          </FormControl>
                          {column.timeFormat === 'custom' && (
                            <TextField
                              size="small"
                              label="Custom Time Format"
                              value={column.customTimeFormat || ''}
                              onChange={(e) => {
                                const updatedColumns = [...(step.config?.tableColumns || [])];
                                updatedColumns[index] = { 
                                  ...column, 
                                  customTimeFormat: e.target.value,
                                  timeFormat: 'custom'
                                };
                                handleConfigChange('tableColumns', updatedColumns);
                              }}
                              fullWidth
                              placeholder="e.g., HH:mm, h:mm A, HH.mm.ss"
                              helperText="Custom time format (HH=24hr, hh=12hr, mm=minutes, ss=seconds, A=AM/PM)"
                              sx={{ mt: 1 }}
                            />
                          )}
                          
                          {/* Timezone Configuration */}
                          <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                            <InputLabel>Timezone</InputLabel>
                            <Select
                              value={column.timezone || 'UTC'}
                              onChange={(e) => {
                                const updatedColumns = [...(step.config?.tableColumns || [])];
                                updatedColumns[index] = { ...column, timezone: e.target.value };
                                handleConfigChange('tableColumns', updatedColumns);
                              }}
                              label="Timezone"
                            >
                              <MenuItem value="UTC">UTC (Server Time)</MenuItem>
                              <MenuItem value="UTC+1">UTC+1 (Central European Time)</MenuItem>
                              <MenuItem value="UTC+2">UTC+2 (Eastern European Time)</MenuItem>
                              <MenuItem value="UTC+3">UTC+3 (Moscow Time)</MenuItem>
                              <MenuItem value="UTC+4">UTC+4 (Gulf Standard Time)</MenuItem>
                              <MenuItem value="UTC+5">UTC+5 (Pakistan Standard Time)</MenuItem>
                              <MenuItem value="UTC+6">UTC+6 (Bangladesh Standard Time)</MenuItem>
                              <MenuItem value="UTC+7">UTC+7 (Indochina Time)</MenuItem>
                              <MenuItem value="UTC+8">UTC+8 (China Standard Time)</MenuItem>
                              <MenuItem value="UTC+9">UTC+9 (Japan Standard Time)</MenuItem>
                              <MenuItem value="UTC+10">UTC+10 (Australian Eastern Time)</MenuItem>
                              <MenuItem value="UTC+11">UTC+11 (Solomon Islands Time)</MenuItem>
                              <MenuItem value="UTC+12">UTC+12 (New Zealand Time)</MenuItem>
                              <MenuItem value="UTC-1">UTC-1 (Cape Verde Time)</MenuItem>
                              <MenuItem value="UTC-2">UTC-2 (South Georgia Time)</MenuItem>
                              <MenuItem value="UTC-3">UTC-3 (Argentina Time)</MenuItem>
                              <MenuItem value="UTC-4">UTC-4 (Atlantic Standard Time)</MenuItem>
                              <MenuItem value="UTC-5">UTC-5 (Eastern Standard Time)</MenuItem>
                              <MenuItem value="UTC-6">UTC-6 (Central Standard Time)</MenuItem>
                              <MenuItem value="UTC-7">UTC-7 (Mountain Standard Time)</MenuItem>
                              <MenuItem value="UTC-8">UTC-8 (Pacific Standard Time)</MenuItem>
                              <MenuItem value="UTC-9">UTC-9 (Alaska Standard Time)</MenuItem>
                              <MenuItem value="UTC-10">UTC-10 (Hawaii Standard Time)</MenuItem>
                              <MenuItem value="UTC-11">UTC-11 (Samoa Standard Time)</MenuItem>
                              <MenuItem value="UTC-12">UTC-12 (Baker Island Time)</MenuItem>
                            </Select>
                            <FormHelperText>
                              Select the timezone for time display. This will convert server time to your local timezone.
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </SortableColumnCard>
                ))}
              </SortableContext>
            </DndContext>
            
            {/* Add Column Button - moved to bottom for better UX */}
            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  const currentColumns = step.config?.tableColumns || [];
                  const newColumn = {
                    id: `col-${Date.now()}`,
                    key: '',
                    label: '',
                    type: 'text',
                    width: 'auto',
                    alignment: 'left'
                  };
                  handleConfigChange('tableColumns', [...currentColumns, newColumn]);
                }}
              >
                Add Column
              </Button>
            </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Row Color Rules Configuration */}
              <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  🎨 Row Highlight Rules
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                  Highlight entire rows based on field conditions. For example, highlight a row when _existsInPrimary is false.
                </Typography>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Row Color Rules
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentRules = step.config?.rowColorRules || [];
                      const newRule = {
                        condition: {
                          variable: '',
                          operator: 'equals',
                          value: '',
                          logicalOperator: 'AND',
                          secondVariable: '',
                          secondOperator: 'equals',
                          secondValue: ''
                        },
                        color: '#ffcdd2',
                        label: 'New Rule'
                      };
                      handleConfigChange('rowColorRules', [...currentRules, newRule]);
                    }}
                  >
                    Add Row Rule
                  </Button>
                </Box>

                {(step.config?.rowColorRules || []).map((rule, ruleIndex) => (
                  <Card key={ruleIndex} sx={{ mb: 2, p: 2, backgroundColor: '#f1f8e9', border: '1px solid #c8e6c9' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3}>
                        <TextField
                          size="small"
                          label="Field / Variable"
                          value={rule.condition.variable}
                          onChange={(e) => {
                            const updatedRules = [...(step.config?.rowColorRules || [])];
                            updatedRules[ruleIndex] = {
                              ...rule,
                              condition: { ...rule.condition, variable: e.target.value }
                            };
                            handleConfigChange('rowColorRules', updatedRules);
                          }}
                          fullWidth
                          placeholder="e.g., _existsInPrimary"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Operator</InputLabel>
                          <Select
                            value={rule.condition.operator}
                            onChange={(e) => {
                              const updatedRules = [...(step.config?.rowColorRules || [])];
                              updatedRules[ruleIndex] = {
                                ...rule,
                                condition: { ...rule.condition, operator: e.target.value }
                              };
                              handleConfigChange('rowColorRules', updatedRules);
                            }}
                            label="Operator"
                          >
                            <MenuItem value="equals">Equals</MenuItem>
                            <MenuItem value="not_equals">Not Equals</MenuItem>
                            <MenuItem value="contains">Contains</MenuItem>
                            <MenuItem value="not_contains">Not Contains</MenuItem>
                            <MenuItem value="exists">Exists</MenuItem>
                            <MenuItem value="not_exists">Not Exists</MenuItem>
                            <MenuItem value="greater_than">Greater Than</MenuItem>
                            <MenuItem value="less_than">Less Than</MenuItem>
                            <MenuItem value="is_true">Is True</MenuItem>
                            <MenuItem value="is_false">Is False</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          size="small"
                          label="Value"
                          value={rule.condition.value}
                          onChange={(e) => {
                            const updatedRules = [...(step.config?.rowColorRules || [])];
                            updatedRules[ruleIndex] = {
                              ...rule,
                              condition: { ...rule.condition, value: e.target.value }
                            };
                            handleConfigChange('rowColorRules', updatedRules);
                          }}
                          fullWidth
                          placeholder="false"
                          helperText="Use {{varName}} for row fields"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          size="small"
                          label="Label"
                          value={rule.label}
                          onChange={(e) => {
                            const updatedRules = [...(step.config?.rowColorRules || [])];
                            updatedRules[ruleIndex] = { ...rule, label: e.target.value };
                            handleConfigChange('rowColorRules', updatedRules);
                          }}
                          fullWidth
                          placeholder="e.g., Missing"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <input
                            type="color"
                            value={rule.color}
                            onChange={(e) => {
                              const updatedRules = [...(step.config?.rowColorRules || [])];
                              updatedRules[ruleIndex] = { ...rule, color: e.target.value };
                              handleConfigChange('rowColorRules', updatedRules);
                            }}
                            style={{ width: '40px', height: '32px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                          />
                          <Typography variant="caption">Color</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updatedRules = (step.config?.rowColorRules || []).filter((_, i) => i !== ruleIndex);
                            handleConfigChange('rowColorRules', updatedRules);
                          }}
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>

                    {/* Second condition */}
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                      <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                        Second Condition (Optional)
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={2}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Logic</InputLabel>
                            <Select
                              value={rule.condition.logicalOperator || 'AND'}
                              onChange={(e) => {
                                const updatedRules = [...(step.config?.rowColorRules || [])];
                                updatedRules[ruleIndex] = {
                                  ...rule,
                                  condition: { ...rule.condition, logicalOperator: e.target.value }
                                };
                                handleConfigChange('rowColorRules', updatedRules);
                              }}
                              label="Logic"
                            >
                              <MenuItem value="AND">AND</MenuItem>
                              <MenuItem value="OR">OR</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            size="small"
                            label="Second Variable"
                            value={rule.condition.secondVariable || ''}
                            onChange={(e) => {
                              const updatedRules = [...(step.config?.rowColorRules || [])];
                              updatedRules[ruleIndex] = {
                                ...rule,
                                condition: { ...rule.condition, secondVariable: e.target.value }
                              };
                              handleConfigChange('rowColorRules', updatedRules);
                            }}
                            fullWidth
                            placeholder="e.g., _confidence"
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Operator</InputLabel>
                            <Select
                              value={rule.condition.secondOperator || 'equals'}
                              onChange={(e) => {
                                const updatedRules = [...(step.config?.rowColorRules || [])];
                                updatedRules[ruleIndex] = {
                                  ...rule,
                                  condition: { ...rule.condition, secondOperator: e.target.value }
                                };
                                handleConfigChange('rowColorRules', updatedRules);
                              }}
                              label="Operator"
                            >
                              <MenuItem value="equals">Equals</MenuItem>
                              <MenuItem value="not_equals">Not Equals</MenuItem>
                              <MenuItem value="greater_than">Greater Than</MenuItem>
                              <MenuItem value="less_than">Less Than</MenuItem>
                              <MenuItem value="contains">Contains</MenuItem>
                              <MenuItem value="not_contains">Not Contains</MenuItem>
                              <MenuItem value="is_true">Is True</MenuItem>
                              <MenuItem value="is_false">Is False</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                          <TextField
                            size="small"
                            label="Second Value"
                            value={rule.condition.secondValue || ''}
                            onChange={(e) => {
                              const updatedRules = [...(step.config?.rowColorRules || [])];
                              updatedRules[ruleIndex] = {
                                ...rule,
                                condition: { ...rule.condition, secondValue: e.target.value }
                              };
                              handleConfigChange('rowColorRules', updatedRules);
                            }}
                            fullWidth
                            placeholder="false"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                ))}
              </Box>

              {/* Cell Color Rules Configuration */}
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  🎨 Cell Highlight Rules
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                  Highlight specific cells based on conditions. For example, if orderQuantity {'>'} masterRemainingQTY, color that cell. Use {`{{fieldName}}`} in values to reference other fields in the same row.
                </Typography>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Cell Color Rules
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentRules = step.config?.cellColorRules || [];
                      const newRule = {
                        fieldKey: '',
                        condition: {
                          variable: '',
                          operator: 'greater_than',
                          value: '',
                          logicalOperator: 'AND',
                          secondVariable: '',
                          secondOperator: 'equals',
                          secondValue: ''
                        },
                        color: '#d32f2f',
                        backgroundColor: '',
                        label: 'New Cell Rule'
                      };
                      handleConfigChange('cellColorRules', [...currentRules, newRule]);
                    }}
                  >
                    Add Cell Rule
                  </Button>
                </Box>

                {(step.config?.cellColorRules || []).map((rule, ruleIndex) => (
                  <Card key={ruleIndex} sx={{ mb: 2, p: 2, backgroundColor: '#fff8e1', border: '1px solid #ffb74d' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={2}>
                        <TextField
                          size="small"
                          label="Target Column Key"
                          value={rule.fieldKey}
                          onChange={(e) => {
                            const updatedRules = [...(step.config?.cellColorRules || [])];
                            updatedRules[ruleIndex] = { ...rule, fieldKey: e.target.value };
                            handleConfigChange('cellColorRules', updatedRules);
                          }}
                          fullWidth
                          placeholder="e.g., orderQuantity"
                          helperText="Column to highlight"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          size="small"
                          label="Compare Field"
                          value={rule.condition.variable}
                          onChange={(e) => {
                            const updatedRules = [...(step.config?.cellColorRules || [])];
                            updatedRules[ruleIndex] = {
                              ...rule,
                              condition: { ...rule.condition, variable: e.target.value }
                            };
                            handleConfigChange('cellColorRules', updatedRules);
                          }}
                          fullWidth
                          placeholder="e.g., orderQuantity"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Operator</InputLabel>
                          <Select
                            value={rule.condition.operator}
                            onChange={(e) => {
                              const updatedRules = [...(step.config?.cellColorRules || [])];
                              updatedRules[ruleIndex] = {
                                ...rule,
                                condition: { ...rule.condition, operator: e.target.value }
                              };
                              handleConfigChange('cellColorRules', updatedRules);
                            }}
                            label="Operator"
                          >
                            <MenuItem value="equals">Equals</MenuItem>
                            <MenuItem value="not_equals">Not Equals</MenuItem>
                            <MenuItem value="greater_than">Greater Than</MenuItem>
                            <MenuItem value="less_than">Less Than</MenuItem>
                            <MenuItem value="contains">Contains</MenuItem>
                            <MenuItem value="not_contains">Not Contains</MenuItem>
                            <MenuItem value="exists">Exists</MenuItem>
                            <MenuItem value="not_exists">Not Exists</MenuItem>
                            <MenuItem value="is_true">Is True</MenuItem>
                            <MenuItem value="is_false">Is False</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          size="small"
                          label="Compare Value"
                          value={rule.condition.value}
                          onChange={(e) => {
                            const updatedRules = [...(step.config?.cellColorRules || [])];
                            updatedRules[ruleIndex] = {
                              ...rule,
                              condition: { ...rule.condition, value: e.target.value }
                            };
                            handleConfigChange('cellColorRules', updatedRules);
                          }}
                          fullWidth
                          placeholder="{{masterRemainingQTY}}"
                          helperText="Use {{field}} for row fields"
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <input
                            type="color"
                            value={rule.color || '#d32f2f'}
                            onChange={(e) => {
                              const updatedRules = [...(step.config?.cellColorRules || [])];
                              updatedRules[ruleIndex] = { ...rule, color: e.target.value };
                              handleConfigChange('cellColorRules', updatedRules);
                            }}
                            style={{ width: '36px', height: '28px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                          />
                          <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>Font</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={1}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <input
                            type="color"
                            value={rule.backgroundColor || '#ffffff'}
                            onChange={(e) => {
                              const updatedRules = [...(step.config?.cellColorRules || [])];
                              updatedRules[ruleIndex] = { ...rule, backgroundColor: e.target.value };
                              handleConfigChange('cellColorRules', updatedRules);
                            }}
                            style={{ width: '36px', height: '28px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                          />
                          <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>Bg</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updatedRules = (step.config?.cellColorRules || []).filter((_, i) => i !== ruleIndex);
                            handleConfigChange('cellColorRules', updatedRules);
                          }}
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>

                    <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                      <Grid item xs={3}>
                        <TextField
                          size="small"
                          label="Rule Label"
                          value={rule.label}
                          onChange={(e) => {
                            const updatedRules = [...(step.config?.cellColorRules || [])];
                            updatedRules[ruleIndex] = { ...rule, label: e.target.value };
                            handleConfigChange('cellColorRules', updatedRules);
                          }}
                          fullWidth
                          placeholder="e.g., Qty exceeds stock"
                        />
                      </Grid>
                    </Grid>

                    {/* Second condition */}
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                      <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                        Second Condition (Optional)
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={2}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Logic</InputLabel>
                            <Select
                              value={rule.condition.logicalOperator || 'AND'}
                              onChange={(e) => {
                                const updatedRules = [...(step.config?.cellColorRules || [])];
                                updatedRules[ruleIndex] = {
                                  ...rule,
                                  condition: { ...rule.condition, logicalOperator: e.target.value }
                                };
                                handleConfigChange('cellColorRules', updatedRules);
                              }}
                              label="Logic"
                            >
                              <MenuItem value="AND">AND</MenuItem>
                              <MenuItem value="OR">OR</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            size="small"
                            label="Second Variable"
                            value={rule.condition.secondVariable || ''}
                            onChange={(e) => {
                              const updatedRules = [...(step.config?.cellColorRules || [])];
                              updatedRules[ruleIndex] = {
                                ...rule,
                                condition: { ...rule.condition, secondVariable: e.target.value }
                              };
                              handleConfigChange('cellColorRules', updatedRules);
                            }}
                            fullWidth
                            placeholder="e.g., _confidence"
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Operator</InputLabel>
                            <Select
                              value={rule.condition.secondOperator || 'equals'}
                              onChange={(e) => {
                                const updatedRules = [...(step.config?.cellColorRules || [])];
                                updatedRules[ruleIndex] = {
                                  ...rule,
                                  condition: { ...rule.condition, secondOperator: e.target.value }
                                };
                                handleConfigChange('cellColorRules', updatedRules);
                              }}
                              label="Operator"
                            >
                              <MenuItem value="equals">Equals</MenuItem>
                              <MenuItem value="not_equals">Not Equals</MenuItem>
                              <MenuItem value="greater_than">Greater Than</MenuItem>
                              <MenuItem value="less_than">Less Than</MenuItem>
                              <MenuItem value="contains">Contains</MenuItem>
                              <MenuItem value="not_contains">Not Contains</MenuItem>
                              <MenuItem value="is_true">Is True</MenuItem>
                              <MenuItem value="is_false">Is False</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            size="small"
                            label="Second Value"
                            value={rule.condition.secondValue || ''}
                            onChange={(e) => {
                              const updatedRules = [...(step.config?.cellColorRules || [])];
                              updatedRules[ruleIndex] = {
                                ...rule,
                                condition: { ...rule.condition, secondValue: e.target.value }
                              };
                              handleConfigChange('cellColorRules', updatedRules);
                            }}
                            fullWidth
                            placeholder="{{otherField}}"
                            helperText="Use {{field}} for row fields"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        );
      })()}

      {/* Custom Template Configuration */}
      {step.config?.interfaceType === 'custom_template' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">🎨 Custom Template</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                Create a custom HTML template for complete control over the output display.
              </Typography>
              
              <TextField
                label="HTML Template"
                value={step.config?.customTemplate || ''}
                onChange={(e) => handleConfigChange('customTemplate', e.target.value)}
                multiline
                rows={8}
                fullWidth
                placeholder={`<div class="extraction-result">
  <h2>{{companyName}}</h2>
  <div class="summary">
    <p><strong>Invoice Total:</strong> {{totalAmount}}</p>
    <p><strong>Date:</strong> {{invoiceDate}}</p>
  </div>
  
  <h3>Line Items</h3>
  <table class="items-table">
    {{#each lineItems}}
      <tr>
        <td>{{productName}}</td>
        <td>{{quantity}}</td>
        <td>{{unitPrice}}</td>
      </tr>
    {{/each}}
  </table>
</div>`}
                helperText="Use {{variable}} for single values and {{#each array}} for loops. HTML and CSS are supported."
              />
              
              <TextField
                label="Custom CSS (Optional)"
                value={step.config?.customCSS || ''}
                onChange={(e) => handleConfigChange('customCSS', e.target.value)}
                multiline
                rows={4}
                fullWidth
                placeholder={`.extraction-result {
  font-family: Arial, sans-serif;
  max-width: 800px;
}

.items-table {
  width: 100%;
  border-collapse: collapse;
}

.items-table td {
  padding: 8px;
  border: 1px solid #ddd;
}`}
                helperText="Add custom CSS styles for your template"
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Data Export Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📁 Data Export Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.enableExport !== false}
                  onChange={(e) => {
                    const isEnabled = e.target.checked;
                    handleConfigChange('enableExport', isEnabled);
                    
                    // When enabling export functionality, initialize all formats to true if not already configured
                    if (isEnabled && !step.config?.exportFormats) {
                      const allFormats = {
                        pdf: true,
                        xlsx: true,
                        csv: true,
                        json: true,
                        word: true,
                        html: true,
                        xml: true,
                        text: true
                      };
                      handleConfigChange('exportFormats', allFormats);
                    }
                  }}
                />
              }
              label="Enable data export functionality"
            />
            
            {step.config?.enableExport !== false && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                  Export Formats
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Select which export formats to make available to users:
                </Typography>
                
                <Box sx={{ pl: 2 }}>
                  {[
                    { key: 'pdf', label: 'PDF Document', description: 'Professional formatted document' },
                    { key: 'xlsx', label: 'Excel Spreadsheet', description: 'Data in Excel format (.xlsx)' },
                    { key: 'csv', label: 'CSV File', description: 'Comma-separated values for tables' },
                    { key: 'json', label: 'JSON Data', description: 'Raw structured data format' },
                    { key: 'word', label: 'Word Document', description: 'Microsoft Word format (.docx)' },
                    { key: 'html', label: 'HTML Report', description: 'Web-friendly formatted report' },
                    { key: 'xml', label: 'XML Data', description: 'Structured XML format' },
                    { key: 'text', label: 'Plain Text', description: 'Simple text format' }
                  ].map(format => (
                    <FormControlLabel
                      key={format.key}
                      control={
                        <Switch
                          checked={step.config?.exportFormats?.[format.key] !== false}
                          onChange={(e) => handleConfigChange(`exportFormats.${format.key}`, e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{format.label}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {format.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ display: 'block', mb: 1 }}
                    />
                  ))}
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <TextField
                  label="Export Filename Template"
                  value={step.config?.exportFilename || ''}
                  onChange={(e) => handleConfigChange('exportFilename', e.target.value)}
                  fullWidth
                  placeholder="e.g., {{companyName}}_extraction_{{date}}"
                  helperText="Use {{variable}} syntax. Available variables: {{date}}, {{time}}, {{executionId}}, and any extracted variables"
                />

                {step.config?.interfaceType === 'data_table' && (
                  step.config?.enableMultiTab
                    ? step.config?.tabs?.some(t => t.headerFields?.length > 0)
                    : step.config?.headerFields?.length > 0
                ) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={(() => {
                            if (step.config?.enableMultiTab && step.config?.tabs?.length > 0) {
                              const tabIdx = Math.min(activeDataTableTab, step.config.tabs.length - 1);
                              return step.config.tabs[tabIdx]?.includeHeaderFieldsInExport || false;
                            }
                            return step.config?.includeHeaderFieldsInExport || false;
                          })()}
                          onChange={(e) => {
                            if (step.config?.enableMultiTab && step.config?.tabs?.length > 0) {
                              const tabIdx = Math.min(activeDataTableTab, step.config.tabs.length - 1);
                              const tabs = [...step.config.tabs];
                              tabs[tabIdx] = { ...tabs[tabIdx], includeHeaderFieldsInExport: e.target.checked };
                              handleConfigChange('tabs', tabs);
                            } else {
                              handleConfigChange('includeHeaderFieldsInExport', e.target.checked);
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Include header fields in export</Typography>
                          <Typography variant="caption" color="textSecondary">
                            When enabled, header fields will appear above the table data in exported files (Excel, CSV)
                            {step.config?.enableMultiTab && ' (applies to the currently active tab)'}
                          </Typography>
                        </Box>
                      }
                      sx={{ display: 'block', mb: 1 }}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Action Buttons Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🎯 Custom Action Buttons</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Add custom action buttons that allow users to resume the workflow from a specific step.
              Perfect for error recovery, retry operations, or allowing users to go back and modify data.
            </Typography>
            
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                const currentButtons = step.config?.actionButtons || [];
                const newButton = {
                  id: `button-${Date.now()}`,
                  label: 'Retry',
                  targetStepId: '',
                  variant: 'outlined',
                  color: 'primary',
                  enabled: true,
                  showByDefault: false,
                  showOnCompletionPage: true, // Default to true - show on completion page
                  visibilityConditions: [],
                  variableMappings: []
                };
                handleConfigChange('actionButtons', [...currentButtons, newButton]);
              }}
            >
              Add Action Button
            </Button>
            
            {(step.config?.actionButtons || []).map((button, buttonIndex) => (
              <Card key={button.id} sx={{ p: 2, backgroundColor: '#fff3e0', border: '1px solid #ffb74d' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Button {buttonIndex + 1}: {button.label || 'Unnamed'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const updatedButtons = step.config.actionButtons.filter((_, i) => i !== buttonIndex);
                      handleConfigChange('actionButtons', updatedButtons);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                
                <Grid container spacing={2}>
                  {/* Button Label */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      size="small"
                      label="Button Label"
                      value={button.label || ''}
                      onChange={(e) => {
                        const updatedButtons = [...step.config.actionButtons];
                        updatedButtons[buttonIndex] = { ...button, label: e.target.value };
                        handleConfigChange('actionButtons', updatedButtons);
                      }}
                      fullWidth
                      placeholder="e.g., Retry API Call, Go Back, Re-process"
                    />
                  </Grid>
                  
                  {/* Target Step */}
                  <Grid item xs={12} md={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Target Step</InputLabel>
                      <Select
                        value={button.targetStepId || ''}
                        onChange={(e) => {
                          const updatedButtons = [...step.config.actionButtons];
                          updatedButtons[buttonIndex] = { ...button, targetStepId: e.target.value };
                          handleConfigChange('actionButtons', updatedButtons);
                        }}
                        label="Target Step"
                      >
                        <MenuItem value="">
                          <em>Select a step...</em>
                        </MenuItem>
                        {(flowData?.steps || [])
                          .filter(s => s.type !== 'end' && s.id !== step.id)
                          .map(s => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.name} ({s.type})
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText>Step to resume execution from when button is clicked</FormHelperText>
                    </FormControl>
                  </Grid>
                  
                  {/* Button Style */}
                  <Grid item xs={6} md={3}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Style</InputLabel>
                      <Select
                        value={button.variant || 'outlined'}
                        onChange={(e) => {
                          const updatedButtons = [...step.config.actionButtons];
                          updatedButtons[buttonIndex] = { ...button, variant: e.target.value };
                          handleConfigChange('actionButtons', updatedButtons);
                        }}
                        label="Style"
                      >
                        <MenuItem value="outlined">Outlined</MenuItem>
                        <MenuItem value="contained">Filled</MenuItem>
                        <MenuItem value="text">Text</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Button Color */}
                  <Grid item xs={6} md={3}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Color</InputLabel>
                      <Select
                        value={button.color || 'primary'}
                        onChange={(e) => {
                          const updatedButtons = [...step.config.actionButtons];
                          updatedButtons[buttonIndex] = { ...button, color: e.target.value };
                          handleConfigChange('actionButtons', updatedButtons);
                        }}
                        label="Color"
                      >
                        <MenuItem value="primary">Primary</MenuItem>
                        <MenuItem value="secondary">Secondary</MenuItem>
                        <MenuItem value="error">Error/Red</MenuItem>
                        <MenuItem value="warning">Warning/Orange</MenuItem>
                        <MenuItem value="info">Info/Blue</MenuItem>
                        <MenuItem value="success">Success/Green</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Enabled Switch */}
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={button.enabled !== false}
                          onChange={(e) => {
                            const updatedButtons = [...step.config.actionButtons];
                            updatedButtons[buttonIndex] = { ...button, enabled: e.target.checked };
                            handleConfigChange('actionButtons', updatedButtons);
                          }}
                        />
                      }
                      label="Button Enabled"
                    />
                  </Grid>
                  
                  {/* Show by Default */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={button.showByDefault !== false}
                          onChange={(e) => {
                            const updatedButtons = [...step.config.actionButtons];
                            updatedButtons[buttonIndex] = { ...button, showByDefault: e.target.checked };
                            handleConfigChange('actionButtons', updatedButtons);
                          }}
                        />
                      }
                      label="Show by default (when no conditions are defined or when none match)"
                    />
                  </Grid>
                  
                  {/* Show on Completion Page */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={button.showOnCompletionPage !== false}
                          onChange={(e) => {
                            const updatedButtons = [...step.config.actionButtons];
                            updatedButtons[buttonIndex] = { ...button, showOnCompletionPage: e.target.checked };
                            handleConfigChange('actionButtons', updatedButtons);
                          }}
                        />
                      }
                      label="Show on Flow Execution Page when complete (in addition to Results Page)"
                    />
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ ml: 4, mt: 0.5 }}>
                      If enabled, this button will appear on the Flow Execution completion screen. If disabled, it only shows on the Execution Results page.
                    </Typography>
                  </Grid>
                </Grid>
                
                {/* Visibility Conditions */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Visibility Conditions
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant={button.useAdvancedConditions ? 'outlined' : 'contained'}
                        onClick={() => {
                          const updatedButtons = [...step.config.actionButtons];
                          updatedButtons[buttonIndex] = {
                            ...button,
                            useAdvancedConditions: !button.useAdvancedConditions,
                            // Initialize condition groups if switching to advanced mode
                            conditionGroups: button.conditionGroups || [
                              {
                                id: `group-${Date.now()}`,
                                logic: 'AND',
                                conditions: []
                              }
                            ]
                          };
                          handleConfigChange('actionButtons', updatedButtons);
                        }}
                      >
                        {button.useAdvancedConditions ? 'Simple Mode' : 'Advanced Mode'}
                      </Button>
                    </Box>
                  </Box>
                  
                  <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                    {button.useAdvancedConditions 
                      ? 'Define complex conditions with groups (e.g., (A OR B) AND C)'
                      : 'Define when this button should be visible based on variable values'}
                  </Typography>
                  
                  {!button.useAdvancedConditions ? (
                    <>
                      {/* Simple Mode - Legacy Conditions */}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const updatedButtons = [...step.config.actionButtons];
                          const currentConditions = button.visibilityConditions || [];
                          updatedButtons[buttonIndex] = {
                            ...button,
                            visibilityConditions: [
                              ...currentConditions,
                              { variable: '', operator: 'equals', value: '' }
                            ]
                          };
                          handleConfigChange('actionButtons', updatedButtons);
                        }}
                        sx={{ mb: 1 }}
                      >
                        Add Condition
                      </Button>
                      
                      {(button.visibilityConditions || []).map((condition, condIndex) => (
                        <Box key={condIndex} sx={{ p: 1, mb: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} md={4}>
                              <TextField
                                size="small"
                                label="Variable"
                                value={condition.variable || ''}
                                onChange={(e) => {
                                  const updatedButtons = [...step.config.actionButtons];
                                  const updatedConditions = [...(button.visibilityConditions || [])];
                                  updatedConditions[condIndex] = { ...condition, variable: e.target.value };
                                  updatedButtons[buttonIndex] = { ...button, visibilityConditions: updatedConditions };
                                  handleConfigChange('actionButtons', updatedButtons);
                                }}
                                fullWidth
                                placeholder="e.g., apiStatus, errorType"
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={3}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Operator</InputLabel>
                                <Select
                                  value={condition.operator || 'equals'}
                                  onChange={(e) => {
                                    const updatedButtons = [...step.config.actionButtons];
                                    const updatedConditions = [...(button.visibilityConditions || [])];
                                    updatedConditions[condIndex] = { ...condition, operator: e.target.value };
                                    updatedButtons[buttonIndex] = { ...button, visibilityConditions: updatedConditions };
                                    handleConfigChange('actionButtons', updatedButtons);
                                  }}
                                  label="Operator"
                                >
                                  <MenuItem value="equals">Equals</MenuItem>
                                  <MenuItem value="not_equals">Not Equals</MenuItem>
                                  <MenuItem value="contains">Contains</MenuItem>
                                  <MenuItem value="not_contains">Not Contains</MenuItem>
                                  <MenuItem value="exists">Exists</MenuItem>
                                  <MenuItem value="not_exists">Not Exists</MenuItem>
                                  <MenuItem value="greater_than">Greater Than</MenuItem>
                                  <MenuItem value="less_than">Less Than</MenuItem>
                                  <MenuItem value="is_true">Is True</MenuItem>
                                  <MenuItem value="is_false">Is False</MenuItem>
                                  <MenuItem value="is_empty">Is Empty</MenuItem>
                                  <MenuItem value="is_not_empty">Is Not Empty</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                              <TextField
                                size="small"
                                label="Value"
                                value={condition.value || ''}
                                onChange={(e) => {
                                  const updatedButtons = [...step.config.actionButtons];
                                  const updatedConditions = [...(button.visibilityConditions || [])];
                                  updatedConditions[condIndex] = { ...condition, value: e.target.value };
                                  updatedButtons[buttonIndex] = { ...button, visibilityConditions: updatedConditions };
                                  handleConfigChange('actionButtons', updatedButtons);
                                }}
                                fullWidth
                                placeholder="e.g., error, failed"
                                disabled={['exists', 'not_exists', 'is_true', 'is_false', 'is_empty', 'is_not_empty'].includes(condition.operator)}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={1}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const updatedButtons = [...step.config.actionButtons];
                                  const updatedConditions = (button.visibilityConditions || []).filter((_, i) => i !== condIndex);
                                  updatedButtons[buttonIndex] = { ...button, visibilityConditions: updatedConditions };
                                  handleConfigChange('actionButtons', updatedButtons);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                      
                      {(button.visibilityConditions || []).length > 1 && (
                        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                          <InputLabel>Condition Logic</InputLabel>
                          <Select
                            value={button.conditionLogic || 'AND'}
                            onChange={(e) => {
                              const updatedButtons = [...step.config.actionButtons];
                              updatedButtons[buttonIndex] = { ...button, conditionLogic: e.target.value };
                              handleConfigChange('actionButtons', updatedButtons);
                            }}
                            label="Condition Logic"
                          >
                            <MenuItem value="AND">All conditions must match (AND)</MenuItem>
                            <MenuItem value="OR">Any condition can match (OR)</MenuItem>
                          </Select>
                          <FormHelperText>How multiple conditions should be evaluated</FormHelperText>
                        </FormControl>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Advanced Mode - Grouped Conditions */}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const updatedButtons = [...step.config.actionButtons];
                          const currentGroups = button.conditionGroups || [];
                          updatedButtons[buttonIndex] = {
                            ...button,
                            conditionGroups: [
                              ...currentGroups,
                              {
                                id: `group-${Date.now()}`,
                                logic: 'AND',
                                conditions: []
                              }
                            ]
                          };
                          handleConfigChange('actionButtons', updatedButtons);
                        }}
                        sx={{ mb: 2 }}
                      >
                        Add Condition Group
                      </Button>
                      
                      {(button.conditionGroups || []).map((group, groupIndex) => (
                        <Box key={group.id} sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '2px solid #2196f3' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="caption" fontWeight="bold" color="primary">
                              Group {groupIndex + 1}
                            </Typography>
                            <Box display="flex" gap={1} alignItems="center">
                              <FormControl size="small" sx={{ minWidth: 100 }}>
                                <Select
                                  value={group.logic || 'AND'}
                                  onChange={(e) => {
                                    const updatedButtons = [...step.config.actionButtons];
                                    const updatedGroups = [...(button.conditionGroups || [])];
                                    updatedGroups[groupIndex] = { ...group, logic: e.target.value };
                                    updatedButtons[buttonIndex] = { ...button, conditionGroups: updatedGroups };
                                    handleConfigChange('actionButtons', updatedButtons);
                                  }}
                                  size="small"
                                >
                                  <MenuItem value="AND">AND</MenuItem>
                                  <MenuItem value="OR">OR</MenuItem>
                                </Select>
                              </FormControl>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const updatedButtons = [...step.config.actionButtons];
                                  const updatedGroups = (button.conditionGroups || []).filter((_, i) => i !== groupIndex);
                                  updatedButtons[buttonIndex] = { ...button, conditionGroups: updatedGroups };
                                  handleConfigChange('actionButtons', updatedButtons);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const updatedButtons = [...step.config.actionButtons];
                              const updatedGroups = [...(button.conditionGroups || [])];
                              const currentConditions = updatedGroups[groupIndex].conditions || [];
                              updatedGroups[groupIndex] = {
                                ...group,
                                conditions: [
                                  ...currentConditions,
                                  { variable: '', operator: 'equals', value: '' }
                                ]
                              };
                              updatedButtons[buttonIndex] = { ...button, conditionGroups: updatedGroups };
                              handleConfigChange('actionButtons', updatedButtons);
                            }}
                            sx={{ mb: 1 }}
                            variant="outlined"
                          >
                            Add Condition to Group
                          </Button>
                          
                          {(group.conditions || []).map((condition, condIndex) => (
                            <Box key={condIndex} sx={{ p: 1, mb: 1, bgcolor: 'white', borderRadius: 1 }}>
                              <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    size="small"
                                    label="Variable"
                                    value={condition.variable || ''}
                                    onChange={(e) => {
                                      const updatedButtons = [...step.config.actionButtons];
                                      const updatedGroups = [...(button.conditionGroups || [])];
                                      const updatedConditions = [...(updatedGroups[groupIndex].conditions || [])];
                                      updatedConditions[condIndex] = { ...condition, variable: e.target.value };
                                      updatedGroups[groupIndex] = { ...group, conditions: updatedConditions };
                                      updatedButtons[buttonIndex] = { ...button, conditionGroups: updatedGroups };
                                      handleConfigChange('actionButtons', updatedButtons);
                                    }}
                                    fullWidth
                                    placeholder="e.g., sapErrors"
                                  />
                                </Grid>
                                
                                <Grid item xs={12} md={3}>
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                      value={condition.operator || 'equals'}
                                      onChange={(e) => {
                                        const updatedButtons = [...step.config.actionButtons];
                                        const updatedGroups = [...(button.conditionGroups || [])];
                                        const updatedConditions = [...(updatedGroups[groupIndex].conditions || [])];
                                        updatedConditions[condIndex] = { ...condition, operator: e.target.value };
                                        updatedGroups[groupIndex] = { ...group, conditions: updatedConditions };
                                        updatedButtons[buttonIndex] = { ...button, conditionGroups: updatedGroups };
                                        handleConfigChange('actionButtons', updatedButtons);
                                      }}
                                      label="Operator"
                                    >
                                      <MenuItem value="equals">Equals</MenuItem>
                                      <MenuItem value="not_equals">Not Equals</MenuItem>
                                      <MenuItem value="contains">Contains</MenuItem>
                                      <MenuItem value="not_contains">Not Contains</MenuItem>
                                      <MenuItem value="exists">Exists</MenuItem>
                                      <MenuItem value="not_exists">Not Exists</MenuItem>
                                      <MenuItem value="greater_than">Greater Than</MenuItem>
                                      <MenuItem value="less_than">Less Than</MenuItem>
                                      <MenuItem value="is_true">Is True</MenuItem>
                                      <MenuItem value="is_false">Is False</MenuItem>
                                      <MenuItem value="is_empty">Is Empty</MenuItem>
                                      <MenuItem value="is_not_empty">Is Not Empty</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                                
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    size="small"
                                    label="Value"
                                    value={condition.value || ''}
                                    onChange={(e) => {
                                      const updatedButtons = [...step.config.actionButtons];
                                      const updatedGroups = [...(button.conditionGroups || [])];
                                      const updatedConditions = [...(updatedGroups[groupIndex].conditions || [])];
                                      updatedConditions[condIndex] = { ...condition, value: e.target.value };
                                      updatedGroups[groupIndex] = { ...group, conditions: updatedConditions };
                                      updatedButtons[buttonIndex] = { ...button, conditionGroups: updatedGroups };
                                      handleConfigChange('actionButtons', updatedButtons);
                                    }}
                                    fullWidth
                                    placeholder="e.g., -"
                                    disabled={['exists', 'not_exists', 'is_true', 'is_false', 'is_empty', 'is_not_empty'].includes(condition.operator)}
                                  />
                                </Grid>
                                
                                <Grid item xs={12} md={1}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const updatedButtons = [...step.config.actionButtons];
                                      const updatedGroups = [...(button.conditionGroups || [])];
                                      const updatedConditions = (updatedGroups[groupIndex].conditions || []).filter((_, i) => i !== condIndex);
                                      updatedGroups[groupIndex] = { ...group, conditions: updatedConditions };
                                      updatedButtons[buttonIndex] = { ...button, conditionGroups: updatedGroups };
                                      handleConfigChange('actionButtons', updatedButtons);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                          
                          {(group.conditions || []).length === 0 && (
                            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                              No conditions in this group yet. Click "Add Condition to Group" to add one.
                            </Typography>
                          )}
                        </Box>
                      ))}
                      
                      {(button.conditionGroups || []).length > 1 && (
                        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                          <InputLabel>Group Logic</InputLabel>
                          <Select
                            value={button.groupLogic || 'AND'}
                            onChange={(e) => {
                              const updatedButtons = [...step.config.actionButtons];
                              updatedButtons[buttonIndex] = { ...button, groupLogic: e.target.value };
                              handleConfigChange('actionButtons', updatedButtons);
                            }}
                            label="Group Logic"
                          >
                            <MenuItem value="AND">All groups must match (AND)</MenuItem>
                            <MenuItem value="OR">Any group can match (OR)</MenuItem>
                          </Select>
                          <FormHelperText>How multiple condition groups should be combined</FormHelperText>
                        </FormControl>
                      )}
                      
                      {(button.conditionGroups || []).length === 0 && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            No condition groups yet. Click "Add Condition Group" to create complex visibility rules.
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                            <strong>Example:</strong> Create Group 1 with (sapErrors ≠ "-" OR sapErrors ≠ "") and Group 2 with (MBLNR = "0"), then combine with AND
                          </Typography>
                        </Alert>
                      )}
                    </>
                  )}
                </Box>
                
                {/* Confirmation Message (Optional) */}
                <TextField
                  size="small"
                  label="Confirmation Message (Optional)"
                  value={button.confirmMessage || ''}
                  onChange={(e) => {
                    const updatedButtons = [...step.config.actionButtons];
                    updatedButtons[buttonIndex] = { ...button, confirmMessage: e.target.value };
                    handleConfigChange('actionButtons', updatedButtons);
                  }}
                  fullWidth
                  placeholder="e.g., Are you sure you want to retry the API call?"
                  helperText="If provided, users will see a confirmation dialog before the button action is executed"
                  sx={{ mt: 2 }}
                />
              </Card>
            ))}
            
            {(step.config?.actionButtons || []).length === 0 && (
              <Alert severity="info">
                <Typography variant="body2">
                  No action buttons configured. Click "Add Action Button" to create buttons that allow users to resume the workflow from specific steps based on conditions.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  <strong>Example:</strong> Add a "Retry API Call" button that only shows when the API status equals "error"
                </Typography>
              </Alert>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Summary & Notifications */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📊 Summary & Notifications</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.showSummaryStats || false}
                  onChange={(e) => handleConfigChange('showSummaryStats', e.target.checked)}
                />
              }
              label="Show extraction statistics (processing time, confidence scores, etc.)"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.sendNotification || false}
                  onChange={(e) => handleConfigChange('sendNotification', e.target.checked)}
                />
              }
              label="Send completion notification"
            />
            
            {step.config?.sendNotification && (
              <TextField
                label="Notification Message Template"
                value={step.config?.notificationTemplate || ''}
                onChange={(e) => handleConfigChange('notificationTemplate', e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Extraction completed for {{companyName}}. {{totalItems}} items processed successfully."
                helperText="Use {{variable}} syntax for dynamic content"
              />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 🆕 Batch Export Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📦 Batch Export Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.enableBatchExport || false}
                  onChange={(e) => handleConfigChange('enableBatchExport', e.target.checked)}
                />
              }
              label="Enable Batch Export (Multiple Files as ZIP)"
            />
            <FormHelperText>
              When enabled, users can download a ZIP file containing separate files for each group (e.g., one file per supplier)
            </FormHelperText>
            
            {step.config?.enableBatchExport && (
              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9', mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 2 }}>
                  📊 Batch Export Settings:
                </Typography>
                
                <Box display="flex" flexDirection="column" gap={2}>
                  {/* Source Variable */}
                  <FormControl fullWidth>
                    <InputLabel>Source Variable</InputLabel>
                    <Select
                      value={step.config?.batchExportConfig?.sourceVariable || ''}
                      onChange={(e) => handleConfigChange('batchExportConfig', {
                        ...(step.config?.batchExportConfig || {}),
                        sourceVariable: e.target.value
                      })}
                    >
                      <MenuItem value="">
                        <em>Select variable...</em>
                      </MenuItem>
                      {availableVariables.map(variable => (
                        <MenuItem key={variable} value={variable}>
                          {variable}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Variable containing the data to export (array or pre-grouped object from Split Array by Field)
                    </FormHelperText>
                  </FormControl>
                  
                  {/* Export Format */}
                  <FormControl fullWidth>
                    <InputLabel>Export Format</InputLabel>
                    <Select
                      value={step.config?.batchExportConfig?.format || 'excel'}
                      onChange={(e) => handleConfigChange('batchExportConfig', {
                        ...(step.config?.batchExportConfig || {}),
                        format: e.target.value
                      })}
                    >
                      <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                      <MenuItem value="csv">CSV</MenuItem>
                    </Select>
                    <FormHelperText>
                      Format for exported files
                    </FormHelperText>
                  </FormControl>
                  
                  {/* Filename Template */}
                  <TextField
                    label="Filename Template"
                    value={step.config?.batchExportConfig?.filenameTemplate || ''}
                    onChange={(e) => handleConfigChange('batchExportConfig', {
                      ...(step.config?.batchExportConfig || {}),
                      filenameTemplate: e.target.value
                    })}
                    fullWidth
                    placeholder="e.g., {{groupName}}_Report, {{groupName}}_{{date}}"
                    helperText="Use {{groupName}} for the group key (e.g., supplier name). Variables like {{date}}, {{time}} are also supported"
                  />
                  
                  {/* Info Box */}
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>💡 How it works:</strong>
                      <br />
                      • If your data is pre-grouped (from "Split Array by Field" step), it will be used directly
                      <br />
                      • Each group will be exported as a separate file
                      <br />
                      • All files will be bundled into a single ZIP download
                      <br />
                      • Users will see a "Batch Export (ZIP)" button on the results page
                    </Typography>
                  </Alert>
                  
                  {/* Preview */}
                  {step.config?.batchExportConfig?.sourceVariable && step.config?.batchExportConfig?.filenameTemplate && (
                    <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Preview:
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Source: <code>{step.config.batchExportConfig.sourceVariable}</code>
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Format: {step.config.batchExportConfig.format?.toUpperCase() || 'EXCEL'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Example filename: {step.config.batchExportConfig.filenameTemplate.replace('{{groupName}}', 'Company_A')}.{step.config.batchExportConfig.format === 'csv' ? 'csv' : 'xlsx'}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Preview Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">👁️ Configuration Preview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="textSecondary" mb={1}>
              <strong>Interface Type:</strong> {step.config?.interfaceType?.replace('_', ' ').toUpperCase() || 'SUMMARY CARD'}
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={1}>
              <strong>Export Enabled:</strong> {step.config?.enableExport !== false ? 'Yes' : 'No'}
            </Typography>
            {step.config?.enableExport !== false && (
              <Typography variant="body2" color="textSecondary" mb={1}>
                <strong>Export Formats:</strong> {
                  Object.entries(step.config?.exportFormats || { pdf: true, xlsx: true, csv: true, json: true, word: true, html: true, xml: true, text: true })
                    .filter(([_, enabled]) => enabled)
                    .map(([format, _]) => format.toUpperCase())
                    .join(', ') || 'PDF, EXCEL, JSON'
                }
              </Typography>
            )}
            <Typography variant="body2" color="textSecondary" mb={1}>
              <strong>Display Fields:</strong> {(step.config?.displayFields || []).length || 0} configured
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Statistics:</strong> {step.config?.showSummaryStats ? 'Enabled' : 'Disabled'}
            </Typography>
          </Paper>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderWebhookConfig = (currentProjectId) => (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Generate a webhook URL that you can provide to external systems. The step will wait for incoming data and process it with response mapping.
      </Typography>
      
      {/* Auto-start webhook info for first steps */}
      {flowData?.steps?.length > 0 && (() => {
        const firstStep = flowData.steps.find(s => !flowData.steps.some(other => 
          other.connections?.nextStepId === s.id || 
          other.connections?.conditionalConnections?.some(conn => conn.targetStepId === s.id)
        ));
        return firstStep?.id === step.id && firstStep?.type === 'webhook';
      })() && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            🚀 Auto-Start Webhook Flow
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Since this is the first step, you can also use the auto-start endpoint to create and execute flows automatically:
          </Typography>
          <Box sx={{ 
            p: 1, 
            backgroundColor: '#f0f4ff', 
            borderRadius: 1, 
            border: '1px solid #c8d8ff',
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            wordBreak: 'break-all'
          }}>
            POST {(() => {
              const baseUrl = API_BASE_URL.replace('/api', '');
              return `${baseUrl}/api/webhooks/auto-start/${currentProjectId || '{projectId}'}`;
            })()}
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            ⚡ This endpoint will automatically create a new flow execution, process the webhook data, and continue executing steps until user input is required or the flow completes.
          </Typography>
        </Alert>
      )}
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🪝 Webhook Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Configure webhook timeout and response processing.
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={step.config?.enableTimeout || false}
                  onChange={(e) => handleConfigChange('enableTimeout', e.target.checked)}
                />
              }
              label="Enable timeout"
            />
            
            {step.config?.enableTimeout && (
              <TextField
                label="Timeout (seconds)"
                type="number"
                value={step.config?.timeoutSeconds || 300}
                onChange={(e) => handleConfigChange('timeoutSeconds', parseInt(e.target.value))}
                fullWidth
                helperText="How long to wait for webhook callback (default: 5 minutes)"
              />
            )}
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                🔗 Generated Webhook URL
              </Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 1, 
                border: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <TextField
                  size="small"
                  value={(() => {
                    const baseUrl = API_BASE_URL.replace('/api', '');
                    return `${baseUrl}/api/webhooks/${currentProjectId || '{projectId}'}/${step.id || '{stepId}'}`;
                  })()}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    style: { fontFamily: 'monospace', fontSize: '0.875rem' }
                  }}
                /> 
                <Tooltip title="Copy webhook URL">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const baseUrl = API_BASE_URL.replace('/api', '');
                      const webhookUrl = `${baseUrl}/api/webhooks/${currentProjectId || '{projectId}'}/${step.id || '{stepId}'}`;
                      try {
                        await navigator.clipboard.writeText(webhookUrl);
                        // Show success feedback
                        const button = document.activeElement;
                        const originalTitle = button.getAttribute('title');
                        button.setAttribute('title', 'Copied!');
                        setTimeout(() => {
                          button.setAttribute('title', originalTitle);
                        }, 2000);
                      } catch (error) {
                        console.error('Failed to copy webhook URL:', error);
                      }
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                💡 {currentProjectId ? 'This webhook URL is ready to use!' : 'The projectId will be filled in when you save the flow.'} 
                This URL remains consistent across all executions - provide it to your external system.
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🗺️ Response Mapping</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Map fields from the webhook response to flow variables (same as API Call step).
            </Typography>
            
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Response Field Mappings
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const currentMappings = step.config?.responseMappings || [];
                    const newMapping = {
                      id: `mapping-${Date.now()}`,
                      variableName: '',
                      jsonPath: '',
                      dataType: 'string',
                      description: '',
                      isArray: false,
                      arrayItemPath: ''
                    };
                    handleConfigChange('responseMappings', [...currentMappings, newMapping]);
                  }}
                >
                  Add Field Mapping
                </Button>
              </Box>
              
              {(step.config?.responseMappings || []).map((mapping, index) => (
                <Card key={mapping.id} sx={{ mb: 2, p: 2, backgroundColor: '#f0f8ff', border: '1px solid #e1e7ff' }}>
                  <Box display="flex" justifyContent="between" alignItems="flex-start" gap={2}>
                    <Box flexGrow={1}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="Variable Name"
                            value={mapping.variableName}
                            onChange={(e) => {
                              const updatedMappings = [...step.config.responseMappings];
                              updatedMappings[index] = { ...mapping, variableName: e.target.value };
                              handleConfigChange('responseMappings', updatedMappings);
                            }}
                            fullWidth
                            placeholder="e.g., productName, products, orderItems"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="JSON Path"
                            value={mapping.jsonPath}
                            onChange={(e) => {
                              const updatedMappings = [...step.config.responseMappings];
                              updatedMappings[index] = { ...mapping, jsonPath: e.target.value };
                              handleConfigChange('responseMappings', updatedMappings);
                            }}
                            fullWidth
                            placeholder="e.g., data.product.name, data.products, orderItems"
                            helperText="Use dot notation for nested objects"
                          />
                        </Grid>
                        
                        <Grid item xs={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Data Type</InputLabel>
                            <Select
                              value={mapping.dataType}
                              onChange={(e) => {
                                const updatedMappings = [...step.config.responseMappings];
                                updatedMappings[index] = { ...mapping, dataType: e.target.value };
                                handleConfigChange('responseMappings', updatedMappings);
                              }}
                              label="Data Type"
                            >
                              <MenuItem value="string">String</MenuItem>
                              <MenuItem value="number">Number</MenuItem>
                              <MenuItem value="boolean">Boolean</MenuItem>
                              <MenuItem value="object">Object</MenuItem>
                              <MenuItem value="array">Array</MenuItem>
                              <MenuItem value="date">Date</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={mapping.isArray || false}
                                onChange={(e) => {
                                  const updatedMappings = [...step.config.responseMappings];
                                  updatedMappings[index] = { ...mapping, isArray: e.target.checked };
                                  handleConfigChange('responseMappings', updatedMappings);
                                }}
                              />
                            }
                            label="Is Array"
                          />
                        </Grid>
                        
                        {mapping.isArray && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                                📋 Array Configuration
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                size="small"
                                label="Array Item Properties (comma-separated)"
                                value={mapping.arrayItemPath || ''}
                                onChange={(e) => {
                                  const updatedMappings = [...step.config.responseMappings];
                                  updatedMappings[index] = { ...mapping, arrayItemPath: e.target.value };
                                  handleConfigChange('responseMappings', updatedMappings);
                                }}
                                fullWidth
                                placeholder="name, sku, price, quantity"
                                helperText="Properties to extract from each array item for easy access"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1 }}>
                                  💡 Array Usage Examples:
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: '#1565c0', fontFamily: 'monospace' }}>
                                  • Access full array: {mapping.variableName || 'arrayVariable'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: '#1565c0', fontFamily: 'monospace' }}>
                                  • Access array items: {mapping.variableName || 'arrayVariable'}[*].{mapping.arrayItemPath?.split(',')[0]?.trim() || 'property'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: '#1565c0', fontFamily: 'monospace' }}>
                                  • Access specific item: {mapping.variableName || 'arrayVariable'}[0].{mapping.arrayItemPath?.split(',')[0]?.trim() || 'property'}
                                </Typography>
                              </Box>
                            </Grid>
                          </>
                        )}
                        
                        <Grid item xs={12}>
                          <TextField
                            size="small"
                            label="Description"
                            value={mapping.description}
                            onChange={(e) => {
                              const updatedMappings = [...step.config.responseMappings];
                              updatedMappings[index] = { ...mapping, description: e.target.value };
                              handleConfigChange('responseMappings', updatedMappings);
                            }}
                            fullWidth
                            placeholder="What this variable contains (e.g., 'List of products in the order')"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updatedMappings = step.config.responseMappings.filter((_, i) => i !== index);
                        handleConfigChange('responseMappings', updatedMappings);
                      }}
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              ))}
              
              {(!step.config?.responseMappings || step.config.responseMappings.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary', border: '2px dashed #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2">
                    No response mappings defined. Add mappings to extract specific data from webhook responses.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🔍 Response Filtering (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Filter webhook responses if the external system sends arrays or multiple records.
            </Typography>
            
            <TextField
              label="Filter Condition"
              value={step.config?.filterCondition || ''}
              onChange={(e) => handleConfigChange('filterCondition', e.target.value)}
              fullWidth
              placeholder="item.orderId === '{{orderId}}'"
              helperText="JavaScript condition to filter response arrays. Use 'item' to reference each array element."
              multiline
              rows={2}
            />
            
                         <Typography variant="caption" color="textSecondary">
               <strong>Examples:</strong><br/>
               • <code>item.status === 'completed'</code><br/>
               • <code>item.orderId === '&#123;&#123;orderId&#125;&#125;'</code><br/>
               • <code>item.amount &gt; 100</code>
             </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderWaitConfig = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Configure how long the flow should wait before continuing to the next step.
      </Typography>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">⏱️ Wait Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Wait Duration (seconds)"
              type="number"
              value={step.config?.waitSeconds || 5}
              onChange={(e) => handleConfigChange('waitSeconds', parseInt(e.target.value) || 5)}
              fullWidth
              inputProps={{ min: 1, max: 3600 }}
              helperText="How long to wait before proceeding (1 second to 1 hour)"
            />
            
            <TextField
              label="Wait Message"
              value={step.config?.waitMessage || 'Waiting...'}
              onChange={(e) => handleConfigChange('waitMessage', e.target.value)}
              fullWidth
              placeholder="Waiting..."
              helperText="Message to display while waiting"
            />
            
            <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #c8e6c9' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                ⏰ Wait Step Behavior:
              </Typography>
              <Typography variant="body2" color="#2e7d32" sx={{ fontSize: '0.875rem' }}>
                • The flow will pause for exactly {step.config?.waitSeconds || 5} second{(step.config?.waitSeconds || 5) !== 1 ? 's' : ''}<br/>
                • During the wait, the message "{step.config?.waitMessage || 'Waiting...'}" will be displayed<br/>
                • After the wait completes, the flow automatically continues to the next step<br/>
                • All variables remain available during and after the wait
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderExternalDataSourceConfig = (companyIdParam, projectIdParam) => (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Connect to external data sources like Google Sheets, MySQL databases, and Xero for seamless data integration and real-time data access.
      </Typography>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🔗 Provider & Service Selection</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Data Provider</InputLabel>
              <Select
                value={step.config?.provider || 'google'}
                onChange={(e) => handleConfigChange('provider', e.target.value)}
              >
                <MenuItem value="google">Google</MenuItem>
                <MenuItem value="mysql">MySQL Database</MenuItem>
                <MenuItem value="xero">Xero</MenuItem>
                {/* Future providers can be added here */}
              </Select>
              <FormHelperText>Choose the data source provider</FormHelperText>
            </FormControl>
            
            {step.config?.provider === 'google' && (
              <FormControl fullWidth>
                <InputLabel>Google Service</InputLabel>
                <Select
                  value={step.config?.service || 'sheets'}
                  onChange={(e) => handleConfigChange('service', e.target.value)}
                >
                  <MenuItem value="sheets">Google Sheets</MenuItem>
                  <MenuItem value="docs">Google Docs</MenuItem>
                  {/* Future services can be added here */}
                </Select>
                <FormHelperText>
                  {step.config?.service === 'sheets' && "Access and manipulate spreadsheet data"}
                  {step.config?.service === 'docs' && "Read and extract content from documents"}
                </FormHelperText>
              </FormControl>
            )}

            {/* Operation field - Only show for Google provider */}
            {step.config?.provider === 'google' && (
              <FormControl fullWidth>
                <InputLabel>Operation</InputLabel>
                <Select
                  value={step.config?.operation || 'read'}
                  onChange={(e) => handleConfigChange('operation', e.target.value)}
                >
                  <MenuItem value="read">Read Data</MenuItem>
                  <MenuItem value="search">Search Data</MenuItem>
                  <MenuItem value="write">Write Data</MenuItem>
                  {/* <MenuItem value="update">Update Data</MenuItem> */}
                </Select>
                <FormHelperText>
                  {step.config?.operation === 'read' && "Read all data from the source"}
                  {step.config?.operation === 'search' && "Search for specific data using criteria"}
                  {step.config?.operation === 'write' && "Write new data to the source"}
                </FormHelperText>
              </FormControl>
            )}

            {/* Xero Operation Selection */}
            {step.config?.provider === 'xero' && (
              <FormControl fullWidth>
                <InputLabel>Operation</InputLabel>
                <Select
                  value={step.config?.operation || 'getInvoices'}
                  onChange={(e) => handleConfigChange('operation', e.target.value)}
                >
                  <MenuItem value="getInvoices">Get Invoices</MenuItem>
                  <MenuItem value="getInvoice">Get Single Invoice</MenuItem>
                  <MenuItem value="createInvoice">Create Invoice</MenuItem>
                  <MenuItem value="updateInvoice">Update Invoice</MenuItem>
                  <MenuItem value="getContacts">Get Contacts</MenuItem>
                  <MenuItem value="getContact">Get Single Contact</MenuItem>
                  <MenuItem value="createContact">Create Contact</MenuItem>
                  <MenuItem value="updateContact">Update Contact</MenuItem>
                  <MenuItem value="getAccounts">Get Accounts</MenuItem>
                  <MenuItem value="getPayments">Get Payments</MenuItem>
                  <MenuItem value="createPayment">Create Payment</MenuItem>
                  <MenuItem value="getBankTransactions">Get Bank Transactions</MenuItem>
                </Select>
                <FormHelperText>
                  {step.config?.operation === 'getInvoices' && "Retrieve invoices from Xero with optional filtering"}
                  {step.config?.operation === 'getInvoice' && "Get a single invoice by ID"}
                  {step.config?.operation === 'createInvoice' && "Create a new invoice in Xero"}
                  {step.config?.operation === 'updateInvoice' && "Update an existing invoice"}
                  {step.config?.operation === 'getContacts' && "Retrieve contacts from Xero"}
                  {step.config?.operation === 'getContact' && "Get a single contact by ID"}
                  {step.config?.operation === 'createContact' && "Create a new contact in Xero"}
                  {step.config?.operation === 'updateContact' && "Update an existing contact"}
                  {step.config?.operation === 'getAccounts' && "Get chart of accounts"}
                  {step.config?.operation === 'getPayments' && "Retrieve payments from Xero"}
                  {step.config?.operation === 'createPayment' && "Create a new payment"}
                  {step.config?.operation === 'getBankTransactions' && "Retrieve bank transactions"}
                </FormHelperText>
              </FormControl>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Authentication Information - Only show for Google */}
      {step.config?.provider === 'google' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">🔐 Google Authentication</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Manage Your Google Account Connection</strong>
                  <br />
                  Connect or manage your Google Sheets authentication in the Project Settings.
                  <br />
                  <br />
                  If you haven't authenticated yet:
                  <br />
                  1. Go to Project Settings → External Data Integration tab
                  <br />
                  2. Click "Login with Google"
                  <br />
                  3. Grant access to Google Sheets
                  <br />
                  4. Return here to configure your step
                </Typography>
              </Alert>
              
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => {
                  // Open project settings with External Data Integration tab
                  if (companyIdParam && projectIdParam) {
                    window.open(`/company/${companyIdParam}/project/${projectIdParam}#external-data-integration`, '_blank');
                  } else {
                    window.open('/', '_blank');
                  }
                }}
                fullWidth
              >
                Open Project Settings
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Google Sheets Configuration */}
      {step.config?.provider === 'google' && step.config?.service === 'sheets' && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">📊 Google Sheets Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Google Sheets URL"
                value={step.config?.googleSheets?.spreadsheetUrl || ''}
                onChange={(e) => handleConfigChange('googleSheets', { 
                  ...step.config?.googleSheets, 
                  spreadsheetUrl: e.target.value 
                })}
                fullWidth
                placeholder="https://docs.google.com/spreadsheets/d/1234567890abcdef/edit"
                helperText="Paste the full Google Sheets URL or use template variables like {{sheetUrl}}"
              />

              <TextField
                label="Sheet Name"
                value={step.config?.googleSheets?.sheetName || 'Sheet1'}
                onChange={(e) => handleConfigChange('googleSheets', { 
                  ...step.config?.googleSheets, 
                  sheetName: e.target.value,
                  // Update range to use new sheet name
                  range: `${e.target.value}!${(step.config?.googleSheets?.range || 'Sheet1!A:Z').split('!')[1] || 'A:Z'}`
                })}
                fullWidth
                placeholder="Sheet1, Products, Customers, etc."
                helperText="Name of the sheet/tab within the spreadsheet"
              />

              <TextField
                label="Range"
                value={step.config?.googleSheets?.range || 'Sheet1!A:Z'}
                onChange={(e) => handleConfigChange('googleSheets', { 
                  ...step.config?.googleSheets, 
                  range: e.target.value 
                })}
                fullWidth
                placeholder="Sheet1!A:Z or Sheet1!A1:C100"
                helperText="Specify the range to read/write. Use A:Z for all columns"
              />

              {/* Search Configuration */}
              {step.config?.operation === 'search' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>🔍 Search Criteria</Typography>
                  
                  <TextField
                    label="Search Column"
                    value={step.config?.googleSheets?.searchCriteria?.column || ''}
                    onChange={(e) => handleConfigChange('googleSheets', {
                      ...step.config?.googleSheets,
                      searchCriteria: {
                        ...step.config?.googleSheets?.searchCriteria,
                        column: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="A, B, C or column header name"
                    helperText="Column to search in (can be letter like 'A' or header name)"
                    sx={{ mb: 2 }}
                  />

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Search Operator</InputLabel>
                    <Select
                      value={step.config?.googleSheets?.searchCriteria?.operator || 'contains'}
                      onChange={(e) => handleConfigChange('googleSheets', {
                        ...step.config?.googleSheets,
                        searchCriteria: {
                          ...step.config?.googleSheets?.searchCriteria,
                          operator: e.target.value
                        }
                      })}
                    >
                      <MenuItem value="equals">Equals</MenuItem>
                      <MenuItem value="contains">Contains</MenuItem>
                      <MenuItem value="startsWith">Starts With</MenuItem>
                      <MenuItem value="endsWith">Ends With</MenuItem>
                      <MenuItem value="not_equals">Not Equals</MenuItem>
                      <MenuItem value="not_contains">Does Not Contain</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={step.config?.googleSheets?.searchCriteria?.multipleMode || false}
                        onChange={(e) => handleConfigChange('googleSheets', {
                          ...step.config?.googleSheets,
                          searchCriteria: {
                            ...step.config?.googleSheets?.searchCriteria,
                            multipleMode: e.target.checked
                          }
                        })}
                      />
                    }
                    label="Search for multiple values"
                    sx={{ mb: 2 }}
                  />

                  {step.config?.googleSheets?.searchCriteria?.multipleMode ? (
                    <TextField
                      label="Search Values"
                      value={step.config?.googleSheets?.searchCriteria?.valuesText || step.config?.googleSheets?.searchCriteria?.values?.join(', ') || ''}
                      onChange={(e) => {
                        // Store the raw text value (don't process yet)
                        handleConfigChange('googleSheets', {
                          ...step.config?.googleSheets,
                          searchCriteria: {
                            ...step.config?.googleSheets?.searchCriteria,
                            valuesText: e.target.value
                          }
                        });
                      }}
                      onBlur={(e) => {
                        // Process the values when field loses focus
                        const values = e.target.value.split(',').map(v => v.trim()).filter(v => v.length > 0);
                        handleConfigChange('googleSheets', {
                          ...step.config?.googleSheets,
                          searchCriteria: {
                            ...step.config?.googleSheets?.searchCriteria,
                            values: values,
                            valuesText: e.target.value
                          }
                        });
                      }}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Product A, Product G, Product K"
                      helperText={
                        <Box>
                          <Typography variant="caption" display="block">
                            Enter multiple values separated by commas. Examples:
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            • Simple: Product A, Product G, Product K
                          </Typography>
                          <Typography variant="caption" display="block">
                            • With variables: {'{'}{'{'} product1 {'}'}{'}' }, {'{'}{'{'} product2 {'}'}{'}' }, {'{'}{'{'} product3 {'}'}{'}' }
                          </Typography>
                          <Typography variant="caption" display="block">
                            • From array: {'{'}{'{'} productList {'}'}{'}' } (if productList is an array)
                          </Typography>
                        </Box>
                      }
                    />
                  ) : (
                    <TextField
                      label="Search Value"
                      value={step.config?.googleSheets?.searchCriteria?.value || ''}
                      onChange={(e) => handleConfigChange('googleSheets', {
                        ...step.config?.googleSheets,
                        searchCriteria: {
                          ...step.config?.googleSheets?.searchCriteria,
                          value: e.target.value
                        }
                      })}
                      fullWidth
                      placeholder="Search term or {{variable}}"
                      helperText="Single value to search for. Can use template variables like {{customerEmail}}"
                    />
                  )}

                  {/* Preview of search configuration */}
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0', mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      🎯 Search Preview:
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {step.config?.googleSheets?.searchCriteria?.multipleMode ? (
                        <>
                          <strong>Multiple Search:</strong> Find rows where column "{step.config?.googleSheets?.searchCriteria?.column || 'Column'}" {step.config?.googleSheets?.searchCriteria?.operator || 'contains'} any of:
                          <br />
                          {step.config?.googleSheets?.searchCriteria?.values?.length > 0 ? (
                            step.config.googleSheets.searchCriteria.values.map((val, idx) => (
                              <span key={idx}>"{val}"{idx < step.config.googleSheets.searchCriteria.values.length - 1 ? ', ' : ''}</span>
                            ))
                          ) : (
                            <em>No values specified</em>
                          )}
                        </>
                      ) : (
                        <>
                          <strong>Single Search:</strong> Find rows where column "{step.config?.googleSheets?.searchCriteria?.column || 'Column'}" {step.config?.googleSheets?.searchCriteria?.operator || 'contains'} "{step.config?.googleSheets?.searchCriteria?.value || 'value'}"
                        </>
                      )}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Write Configuration */}
              {step.config?.operation === 'write' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>✍️ Write Configuration</Typography>
                  
                  <TextField
                    label="Data Variable"
                    value={step.config?.googleSheets?.writeDataVariable || ''}
                    onChange={(e) => handleConfigChange('googleSheets', {
                      ...step.config?.googleSheets,
                      writeDataVariable: e.target.value
                    })}
                    fullWidth
                    placeholder="dataToWrite"
                    helperText="Variable containing the data to write to the sheet"
                  />
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* MySQL Configuration */}
      {step.config?.provider === 'mysql' && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">🗄️ MySQL Database Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Setup Required:</strong> Configure your MySQL database connection in Project Settings → External Data Integration
                  before using this step.
                </Typography>
              </Alert>

              <FormControl fullWidth>
                <InputLabel>Query Type</InputLabel>
                <Select
                  value={step.config?.mysql?.queryType || 'visual'}
                  onChange={(e) => handleConfigChange('mysql', {
                    ...step.config?.mysql,
                    queryType: e.target.value
                  })}
                >
                  <MenuItem value="visual">Visual Query Builder (No Code Required)</MenuItem>
                  <MenuItem value="raw">Raw SQL Query (Advanced)</MenuItem>
                </Select>
                <FormHelperText>
                  {(!step.config?.mysql?.queryType || step.config?.mysql?.queryType === 'visual') && "Build queries visually without writing SQL"}
                  {step.config?.mysql?.queryType === 'raw' && "Write custom SQL queries directly"}
                </FormHelperText>
              </FormControl>

              {(!step.config?.mysql?.queryType || step.config?.mysql?.queryType === 'visual') ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>📊 Visual Query Builder</Typography>

                  <TextField
                    label="Table Name"
                    value={step.config?.mysql?.queryConfig?.table || ''}
                    onChange={(e) => handleConfigChange('mysql', {
                      ...step.config?.mysql,
                      queryConfig: {
                        ...step.config?.mysql?.queryConfig,
                        table: e.target.value
                      }
                    })}
                    fullWidth
                    required
                    placeholder="users, products, orders, etc."
                    helperText="Name of the database table to query"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Select Columns"
                    value={step.config?.mysql?.queryConfig?.columns?.join(', ') || '*'}
                    onChange={(e) => {
                      const columns = e.target.value === '*' ? ['*'] : e.target.value.split(',').map(c => c.trim()).filter(c => c);
                      handleConfigChange('mysql', {
                        ...step.config?.mysql,
                        queryConfig: {
                          ...step.config?.mysql?.queryConfig,
                          columns: columns
                        }
                      });
                    }}
                    fullWidth
                    placeholder="*, id, name, email"
                    helperText="Comma-separated column names or * for all columns"
                    sx={{ mb: 2 }}
                  />

                  <Divider sx={{ my: 2 }}>
                    <Chip label="WHERE Conditions" size="small" />
                  </Divider>

                  {(!step.config?.mysql?.queryConfig?.conditions || step.config.mysql.queryConfig.conditions.length === 0) && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No conditions added. The query will return all rows from the table. Click "Add Condition" to filter results.
                    </Alert>
                  )}

                  {step.config?.mysql?.queryConfig?.conditions?.map((condition, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Typography variant="body2" fontWeight="bold">
                          Condition {index + 1}
                        </Typography>
                        {index > 0 && (
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={condition.logicalOperator || 'AND'}
                              onChange={(e) => {
                                const newConditions = [...(step.config?.mysql?.queryConfig?.conditions || [])];
                                newConditions[index] = { ...newConditions[index], logicalOperator: e.target.value };
                                handleConfigChange('mysql', {
                                  ...step.config?.mysql,
                                  queryConfig: {
                                    ...step.config?.mysql?.queryConfig,
                                    conditions: newConditions
                                  }
                                });
                              }}
                            >
                              <MenuItem value="AND">AND</MenuItem>
                              <MenuItem value="OR">OR</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        <Box flexGrow={1} />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const newConditions = (step.config?.mysql?.queryConfig?.conditions || []).filter((_, i) => i !== index);
                            handleConfigChange('mysql', {
                              ...step.config?.mysql,
                              queryConfig: {
                                ...step.config?.mysql?.queryConfig,
                                conditions: newConditions
                              }
                            });
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      <Box display="flex" gap={1} flexWrap="wrap">
                        <TextField
                          label="Column"
                          value={condition.column || ''}
                          onChange={(e) => {
                            const newConditions = [...(step.config?.mysql?.queryConfig?.conditions || [])];
                            newConditions[index] = { ...newConditions[index], column: e.target.value };
                            handleConfigChange('mysql', {
                              ...step.config?.mysql,
                              queryConfig: {
                                ...step.config?.mysql?.queryConfig,
                                conditions: newConditions
                              }
                            });
                          }}
                          placeholder="column_name"
                          size="small"
                          sx={{ flexGrow: 1, minWidth: '150px' }}
                        />

                        <FormControl size="small" sx={{ minWidth: '150px' }}>
                          <InputLabel>Operator</InputLabel>
                          <Select
                            value={condition.operator || 'equals'}
                            onChange={(e) => {
                              const newConditions = [...(step.config?.mysql?.queryConfig?.conditions || [])];
                              newConditions[index] = { ...newConditions[index], operator: e.target.value };
                              handleConfigChange('mysql', {
                                ...step.config?.mysql,
                                queryConfig: {
                                  ...step.config?.mysql?.queryConfig,
                                  conditions: newConditions
                                }
                              });
                            }}
                          >
                            <MenuItem value="equals">=  Equals</MenuItem>
                            <MenuItem value="not_equals">≠  Not Equals</MenuItem>
                            <MenuItem value="greater_than">&gt;  Greater Than</MenuItem>
                            <MenuItem value="less_than">&lt;  Less Than</MenuItem>
                            <MenuItem value="greater_or_equal">≥  Greater or Equal</MenuItem>
                            <MenuItem value="less_or_equal">≤  Less or Equal</MenuItem>
                            <MenuItem value="contains">Contains</MenuItem>
                            <MenuItem value="not_contains">Not Contains</MenuItem>
                            <MenuItem value="starts_with">Starts With</MenuItem>
                            <MenuItem value="ends_with">Ends With</MenuItem>
                            <MenuItem value="is_null">Is NULL</MenuItem>
                            <MenuItem value="is_not_null">Is Not NULL</MenuItem>
                            <MenuItem value="in">IN (multiple values)</MenuItem>
                            <MenuItem value="not_in">NOT IN (multiple values)</MenuItem>
                          </Select>
                        </FormControl>

                        {condition.operator !== 'is_null' && condition.operator !== 'is_not_null' && (
                          <TextField
                            label="Value"
                            value={condition.value || ''}
                            onChange={(e) => {
                              const newConditions = [...(step.config?.mysql?.queryConfig?.conditions || [])];
                              newConditions[index] = { ...newConditions[index], value: e.target.value };
                              handleConfigChange('mysql', {
                                ...step.config?.mysql,
                                queryConfig: {
                                  ...step.config?.mysql?.queryConfig,
                                  conditions: newConditions
                                }
                              });
                            }}
                            placeholder={
                              condition.operator === 'in' || condition.operator === 'not_in'
                                ? "value1, value2, value3 or {{variableName}}"
                                : "value or {{variableName}}"
                            }
                            size="small"
                            sx={{ flexGrow: 2, minWidth: '200px' }}
                            multiline={condition.operator === 'in' || condition.operator === 'not_in'}
                            rows={condition.operator === 'in' || condition.operator === 'not_in' ? 2 : 1}
                          />
                        )}
                      </Box>
                    </Paper>
                  ))}

                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newCondition = {
                        column: '',
                        operator: 'equals',
                        value: '',
                        logicalOperator: 'AND'
                      };
                      const currentConditions = step.config?.mysql?.queryConfig?.conditions || [];
                      handleConfigChange('mysql', {
                        ...step.config?.mysql,
                        queryConfig: {
                          ...step.config?.mysql?.queryConfig,
                          conditions: [...currentConditions, newCondition]
                        }
                      });
                    }}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Add Condition
                  </Button>

                  <Divider sx={{ my: 2 }}>
                    <Chip label="Sorting & Limits" size="small" />
                  </Divider>

                  <Box display="flex" gap={2} mb={2}>
                    <TextField
                      label="Order By Column"
                      value={step.config?.mysql?.queryConfig?.orderBy?.column || ''}
                      onChange={(e) => handleConfigChange('mysql', {
                        ...step.config?.mysql,
                        queryConfig: {
                          ...step.config?.mysql?.queryConfig,
                          orderBy: {
                            ...step.config?.mysql?.queryConfig?.orderBy,
                            column: e.target.value
                          }
                        }
                      })}
                      placeholder="created_at, id, name, etc."
                      helperText="Optional: Column to sort results by"
                      sx={{ flexGrow: 1 }}
                    />

                    <FormControl sx={{ minWidth: 120 }}>
                      <InputLabel>Direction</InputLabel>
                      <Select
                        value={step.config?.mysql?.queryConfig?.orderBy?.direction || 'ASC'}
                        onChange={(e) => handleConfigChange('mysql', {
                          ...step.config?.mysql,
                          queryConfig: {
                            ...step.config?.mysql?.queryConfig,
                            orderBy: {
                              ...step.config?.mysql?.queryConfig?.orderBy,
                              direction: e.target.value
                            }
                          }
                        })}
                      >
                        <MenuItem value="ASC">Ascending</MenuItem>
                        <MenuItem value="DESC">Descending</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box display="flex" gap={2}>
                    <TextField
                      label="Limit"
                      type="number"
                      value={step.config?.mysql?.queryConfig?.limit || ''}
                      onChange={(e) => handleConfigChange('mysql', {
                        ...step.config?.mysql,
                        queryConfig: {
                          ...step.config?.mysql?.queryConfig,
                          limit: parseInt(e.target.value) || ''
                        }
                      })}
                      placeholder="100"
                      helperText="Maximum number of rows to return"
                      sx={{ flexGrow: 1 }}
                    />

                    <TextField
                      label="Offset"
                      type="number"
                      value={step.config?.mysql?.queryConfig?.offset || ''}
                      onChange={(e) => handleConfigChange('mysql', {
                        ...step.config?.mysql,
                        queryConfig: {
                          ...step.config?.mysql?.queryConfig,
                          offset: parseInt(e.target.value) || ''
                        }
                      })}
                      placeholder="0"
                      helperText="Number of rows to skip"
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>

                  {/* Query Preview */}
                  <Box sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9', mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                      📝 Generated SQL Preview:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: '#1b5e20' }}>
                      SELECT {step.config?.mysql?.queryConfig?.columns?.join(', ') || '*'}
                      {' FROM '}{step.config?.mysql?.queryConfig?.table || 'table_name'}
                      {step.config?.mysql?.queryConfig?.conditions?.length > 0 && (
                        <>
                          {' WHERE '}
                          {step.config.mysql.queryConfig.conditions.map((cond, idx) => (
                            <span key={idx}>
                              {idx > 0 && ` ${cond.logicalOperator || 'AND'} `}
                              {cond.column} {
                                cond.operator === 'equals' ? '=' :
                                cond.operator === 'not_equals' ? '!=' :
                                cond.operator === 'greater_than' ? '>' :
                                cond.operator === 'less_than' ? '<' :
                                cond.operator === 'greater_or_equal' ? '>=' :
                                cond.operator === 'less_or_equal' ? '<=' :
                                cond.operator === 'contains' ? 'LIKE' :
                                cond.operator === 'not_contains' ? 'NOT LIKE' :
                                cond.operator === 'starts_with' ? 'LIKE' :
                                cond.operator === 'ends_with' ? 'LIKE' :
                                cond.operator === 'is_null' ? 'IS NULL' :
                                cond.operator === 'is_not_null' ? 'IS NOT NULL' :
                                cond.operator === 'in' ? 'IN' :
                                cond.operator === 'not_in' ? 'NOT IN' :
                                '='
                              }{' '}
                              {cond.operator !== 'is_null' && cond.operator !== 'is_not_null' && (
                                cond.operator === 'in' || cond.operator === 'not_in' 
                                  ? `(${cond.value || '...'})` 
                                  : `'${cond.value || '...'}'`
                              )}
                            </span>
                          ))}
                        </>
                      )}
                      {step.config?.mysql?.queryConfig?.orderBy?.column && (
                        <> ORDER BY {step.config.mysql.queryConfig.orderBy.column} {step.config.mysql.queryConfig.orderBy.direction || 'ASC'}</>
                      )}
                      {step.config?.mysql?.queryConfig?.limit && (
                        <> LIMIT {step.config.mysql.queryConfig.limit}</>
                      )}
                      {step.config?.mysql?.queryConfig?.offset && (
                        <> OFFSET {step.config.mysql.queryConfig.offset}</>
                      )}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>✍️ Raw SQL Query</Typography>
                  
                  <TextField
                    label="SQL Query"
                    value={step.config?.mysql?.rawQuery || ''}
                    onChange={(e) => handleConfigChange('mysql', {
                      ...step.config?.mysql,
                      rawQuery: e.target.value
                    })}
                    fullWidth
                    multiline
                    rows={6}
                    placeholder="SELECT * FROM users WHERE email = ? AND status = ?"
                    helperText="Write your SQL query. Use ? for parameterized values or {{variableName}} for template variables."
                    sx={{ mb: 2, fontFamily: 'monospace' }}
                  />

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Security Note:</strong> For user input, always use parameterized queries (?) or template variables to prevent SQL injection.
                    </Typography>
                  </Alert>

                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Query Parameters (optional):
                  </Typography>
                  <TextField
                    label="Parameters"
                    value={step.config?.mysql?.queryParams?.join(', ') || ''}
                    onChange={(e) => {
                      const params = e.target.value.split(',').map(p => p.trim()).filter(p => p);
                      handleConfigChange('mysql', {
                        ...step.config?.mysql,
                        queryParams: params
                      });
                    }}
                    fullWidth
                    placeholder="param1, param2, {{variable}}"
                    helperText="Comma-separated values for ? placeholders in your query"
                  />
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Response Mapping */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🎯 Response Mapping</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Map the response data to flow variables for use in subsequent steps.
            </Typography>
            
            <TextField
              label="Data Variable Name"
              value={step.config?.responseMapping?.extractedData || 'externalData'}
              onChange={(e) => handleConfigChange('responseMapping', {
                ...step.config?.responseMapping,
                extractedData: e.target.value
              })}
              fullWidth
              placeholder="externalData"
              helperText="Variable name to store the retrieved data array"
            />

            <TextField
              label="Count Variable Name"
              value={step.config?.responseMapping?.totalRows || 'dataCount'}
              onChange={(e) => handleConfigChange('responseMapping', {
                ...step.config?.responseMapping,
                totalRows: e.target.value
              })}
              fullWidth
              placeholder="dataCount"
              helperText="Variable name to store the total number of rows found"
            />

            <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #c8e6c9' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                💡 Default Variables Created:
              </Typography>
              <Typography variant="body2" color="#2e7d32" sx={{ fontSize: '0.875rem' }}>
                • <strong>externalData</strong>: Array of objects with the retrieved data<br/>
                • <strong>dataCount</strong>: Number of rows/items found<br/>
                • <strong>externalDataResult</strong>: Full response object with metadata
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Xero Authentication Information */}
      {step.config?.provider === 'xero' && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">🔐 Xero Authentication</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Manage Your Xero Connection</strong>
                  <br />
                  Connect or manage your Xero authentication in the Project Settings.
                  <br />
                  <br />
                  If you haven't authenticated yet:
                  <br />
                  1. Go to Project Settings → External Data Integration tab
                  <br />
                  2. Click "Connect to Xero"
                  <br />
                  3. Log in and grant access to your Xero organization
                  <br />
                  4. Select which organization to use
                  <br />
                  5. Return here to configure your step
                </Typography>
              </Alert>
              
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => {
                  if (companyIdParam && projectIdParam) {
                    window.open(`/company/${companyIdParam}/project/${projectIdParam}#external-data-integration`, '_blank');
                  } else {
                    window.open('/', '_blank');
                  }
                }}
                fullWidth
              >
                Open Project Settings
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Xero Configuration */}
      {step.config?.provider === 'xero' && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">💼 Xero Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Setup Required:</strong> Configure your Xero connection in Project Settings → External Data Integration
                  before using this step.
                </Typography>
              </Alert>

              {/* Get Invoices Configuration */}
              {step.config?.operation === 'getInvoices' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>📄 Invoice Filters (Optional)</Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel shrink={step.config?.xero?.filters?.status !== undefined}>Status</InputLabel>
                    <Select
                      value={step.config?.xero?.filters?.status || ''}
                      onChange={(e) => handleConfigChange('xero', {
                        ...step.config?.xero,
                        filters: {
                          ...step.config?.xero?.filters,
                          status: e.target.value
                        }
                      })}
                      displayEmpty
                      notched={step.config?.xero?.filters?.status !== undefined}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="DRAFT">Draft</MenuItem>
                      <MenuItem value="SUBMITTED">Submitted</MenuItem>
                      <MenuItem value="AUTHORISED">Authorised</MenuItem>
                      <MenuItem value="PAID">Paid</MenuItem>
                      <MenuItem value="VOIDED">Voided</MenuItem>
                    </Select>
                    <FormHelperText>Filter invoices by status</FormHelperText>
                  </FormControl>

                  <TextField
                    label="Contact ID (Optional)"
                    value={step.config?.xero?.filters?.contactId || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      filters: {
                        ...step.config?.xero?.filters,
                        contactId: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="{{contactId}}"
                    helperText="Filter by specific contact (supports template variables)"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Date From (Optional)"
                    value={step.config?.xero?.filters?.dateFrom || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      filters: {
                        ...step.config?.xero?.filters,
                        dateFrom: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="2024-01-01 or {{startDate}}"
                    helperText="Start date (YYYY-MM-DD format)"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Date To (Optional)"
                    value={step.config?.xero?.filters?.dateTo || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      filters: {
                        ...step.config?.xero?.filters,
                        dateTo: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="2024-12-31 or {{endDate}}"
                    helperText="End date (YYYY-MM-DD format)"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Page Number (Optional)"
                    type="number"
                    value={step.config?.xero?.page || 1}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      page: parseInt(e.target.value) || 1
                    })}
                    fullWidth
                    helperText="Page number for pagination"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Order By (Optional)"
                    value={step.config?.xero?.orderBy || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      orderBy: e.target.value
                    })}
                    fullWidth
                    placeholder="Date DESC"
                    helperText="Sort order (e.g., 'Date DESC', 'InvoiceNumber ASC')"
                  />
                </Box>
              )}

              {/* Create Invoice Configuration */}
              {step.config?.operation === 'createInvoice' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>📝 Invoice Details</Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Invoice Type</InputLabel>
                    <Select
                      value={step.config?.xero?.invoiceData?.type || 'ACCREC'}
                      onChange={(e) => handleConfigChange('xero', {
                        ...step.config?.xero,
                        invoiceData: {
                          ...step.config?.xero?.invoiceData,
                          type: e.target.value
                        }
                      })}
                    >
                      <MenuItem value="ACCREC">Sales Invoice (ACCREC)</MenuItem>
                      <MenuItem value="ACCPAY">Purchase Invoice (ACCPAY)</MenuItem>
                    </Select>
                    <FormHelperText>Type of invoice to create</FormHelperText>
                  </FormControl>

                  <TextField
                    label="Contact ID"
                    value={step.config?.xero?.invoiceData?.contactId || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      invoiceData: {
                        ...step.config?.xero?.invoiceData,
                        contactId: e.target.value
                      }
                    })}
                    fullWidth
                    required
                    placeholder="{{contactId}}"
                    helperText="Xero contact ID (supports template variables)"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Invoice Date"
                    value={step.config?.xero?.invoiceData?.date || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      invoiceData: {
                        ...step.config?.xero?.invoiceData,
                        date: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="2024-01-15 or {{invoiceDate}}"
                    helperText="Invoice date (YYYY-MM-DD format)"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Due Date"
                    value={step.config?.xero?.invoiceData?.dueDate || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      invoiceData: {
                        ...step.config?.xero?.invoiceData,
                        dueDate: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="2024-02-15 or {{dueDate}}"
                    helperText="Due date (YYYY-MM-DD format)"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Invoice Number (Optional)"
                    value={step.config?.xero?.invoiceData?.invoiceNumber || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      invoiceData: {
                        ...step.config?.xero?.invoiceData,
                        invoiceNumber: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="INV-{{orderNumber}}"
                    helperText="Custom invoice number (Xero will auto-generate if not provided)"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Reference (Optional)"
                    value={step.config?.xero?.invoiceData?.reference || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      invoiceData: {
                        ...step.config?.xero?.invoiceData,
                        reference: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="Order #{{orderNumber}}"
                    helperText="Reference text for the invoice"
                    sx={{ mb: 2 }}
                  />

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={step.config?.xero?.invoiceData?.status || 'DRAFT'}
                      onChange={(e) => handleConfigChange('xero', {
                        ...step.config?.xero,
                        invoiceData: {
                          ...step.config?.xero?.invoiceData,
                          status: e.target.value
                        }
                      })}
                    >
                      <MenuItem value="DRAFT">Draft</MenuItem>
                      <MenuItem value="AUTHORISED">Authorised</MenuItem>
                    </Select>
                    <FormHelperText>Initial status of the invoice</FormHelperText>
                  </FormControl>

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Line Items:</strong> Line items should be provided as a template variable containing an array of objects with: description, quantity, unitAmount, accountCode
                    </Typography>
                  </Alert>

                  <TextField
                    label="Line Items Variable"
                    value={step.config?.xero?.invoiceData?.lineItemsVar || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      invoiceData: {
                        ...step.config?.xero?.invoiceData,
                        lineItemsVar: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="{{lineItems}}"
                    helperText="Variable containing array of line items"
                  />
                </Box>
              )}

              {/* Get Contacts Configuration */}
              {step.config?.operation === 'getContacts' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>👥 Contact Filters (Optional)</Typography>
                  
                  <TextField
                    label="Name (Optional)"
                    value={step.config?.xero?.filters?.name || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      filters: {
                        ...step.config?.xero?.filters,
                        name: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="{{searchName}}"
                    helperText="Search by contact name (partial match)"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Email Address (Optional)"
                    value={step.config?.xero?.filters?.emailAddress || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      filters: {
                        ...step.config?.xero?.filters,
                        emailAddress: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="{{email}}"
                    helperText="Filter by email address (exact match)"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Page Number (Optional)"
                    type="number"
                    value={step.config?.xero?.page || 1}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      page: parseInt(e.target.value) || 1
                    })}
                    fullWidth
                    helperText="Page number for pagination"
                  />
                </Box>
              )}

              {/* Create Contact Configuration */}
              {step.config?.operation === 'createContact' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>👤 Contact Details</Typography>
                  
                  <TextField
                    label="Contact Name"
                    value={step.config?.xero?.contactData?.name || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      contactData: {
                        ...step.config?.xero?.contactData,
                        name: e.target.value
                      }
                    })}
                    fullWidth
                    required
                    placeholder="{{companyName}}"
                    helperText="Contact or company name"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="First Name (Optional)"
                    value={step.config?.xero?.contactData?.firstName || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      contactData: {
                        ...step.config?.xero?.contactData,
                        firstName: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="{{firstName}}"
                    helperText="Contact's first name"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Last Name (Optional)"
                    value={step.config?.xero?.contactData?.lastName || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      contactData: {
                        ...step.config?.xero?.contactData,
                        lastName: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="{{lastName}}"
                    helperText="Contact's last name"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Email Address (Optional)"
                    value={step.config?.xero?.contactData?.emailAddress || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      contactData: {
                        ...step.config?.xero?.contactData,
                        emailAddress: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="{{email}}"
                    helperText="Contact's email address"
                    sx={{ mb: 2 }}
                  />

                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={step.config?.xero?.contactData?.contactStatus || 'ACTIVE'}
                      onChange={(e) => handleConfigChange('xero', {
                        ...step.config?.xero,
                        contactData: {
                          ...step.config?.xero?.contactData,
                          contactStatus: e.target.value
                        }
                      })}
                    >
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="ARCHIVED">Archived</MenuItem>
                    </Select>
                    <FormHelperText>Contact status</FormHelperText>
                  </FormControl>
                </Box>
              )}

              {/* Simple configurations for other operations */}
              {(step.config?.operation === 'getInvoice' || step.config?.operation === 'updateInvoice') && (
                <TextField
                  label="Invoice ID"
                  value={step.config?.xero?.invoiceId || ''}
                  onChange={(e) => handleConfigChange('xero', {
                    ...step.config?.xero,
                    invoiceId: e.target.value
                  })}
                  fullWidth
                  required
                  placeholder="{{invoiceId}}"
                  helperText="Xero invoice ID (supports template variables)"
                />
              )}

              {(step.config?.operation === 'getContact' || step.config?.operation === 'updateContact') && (
                <TextField
                  label="Contact ID"
                  value={step.config?.xero?.contactId || ''}
                  onChange={(e) => handleConfigChange('xero', {
                    ...step.config?.xero,
                    contactId: e.target.value
                  })}
                  fullWidth
                  required
                  placeholder="{{contactId}}"
                  helperText="Xero contact ID (supports template variables)"
                />
              )}

              {step.config?.operation === 'createPayment' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>💰 Payment Details</Typography>
                  
                  <TextField
                    label="Invoice ID"
                    value={step.config?.xero?.paymentData?.invoiceId || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      paymentData: {
                        ...step.config?.xero?.paymentData,
                        invoiceId: e.target.value
                      }
                    })}
                    fullWidth
                    required
                    placeholder="{{invoiceId}}"
                    helperText="Invoice to apply payment to"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Account ID"
                    value={step.config?.xero?.paymentData?.accountId || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      paymentData: {
                        ...step.config?.xero?.paymentData,
                        accountId: e.target.value
                      }
                    })}
                    fullWidth
                    required
                    placeholder="{{bankAccountId}}"
                    helperText="Bank account ID to receive payment"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Amount"
                    value={step.config?.xero?.paymentData?.amount || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      paymentData: {
                        ...step.config?.xero?.paymentData,
                        amount: e.target.value
                      }
                    })}
                    fullWidth
                    required
                    placeholder="{{paymentAmount}}"
                    helperText="Payment amount"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Date"
                    value={step.config?.xero?.paymentData?.date || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      paymentData: {
                        ...step.config?.xero?.paymentData,
                        date: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="2024-01-20 or {{paymentDate}}"
                    helperText="Payment date (YYYY-MM-DD format)"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Reference (Optional)"
                    value={step.config?.xero?.paymentData?.reference || ''}
                    onChange={(e) => handleConfigChange('xero', {
                      ...step.config?.xero,
                      paymentData: {
                        ...step.config?.xero?.paymentData,
                        reference: e.target.value
                      }
                    })}
                    fullWidth
                    placeholder="Payment received - {{referenceNumber}}"
                    helperText="Reference text for the payment"
                  />
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Authentication Note */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          {step.config?.provider === 'google' && (
            <><strong>Authentication Required:</strong> Google Sheets access requires OAuth2 authentication. 
            Configure Google credentials in your project settings before using this step.</>
          )}
          {step.config?.provider === 'mysql' && (
            <><strong>Database Connection Required:</strong> MySQL database access requires connection configuration. 
            Set up your MySQL connection in project settings before using this step.</>
          )}
          {step.config?.provider === 'xero' && (
            <><strong>Authentication Required:</strong> Xero access requires OAuth2 authentication. 
            Connect to Xero in your project settings before using this step.</>
          )}
        </Typography>
      </Alert>
    </Box>
  );

  const renderReportTemplateConfig = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Create formatted reports from your flow data with customizable headers, sections, and footers.
      </Typography>
      
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📝 Basic Report Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Template Name"
              value={step.config?.reportTemplateConfig?.templateName || ''}
              onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                ...step.config?.reportTemplateConfig, 
                templateName: e.target.value 
              })}
              fullWidth
              placeholder="Sales Report"
            />
            
            <TextField
              label="Description"
              value={step.config?.reportTemplateConfig?.description || ''}
              onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                ...step.config?.reportTemplateConfig, 
                description: e.target.value 
              })}
              fullWidth
              multiline
              rows={2}
              placeholder="Comprehensive sales report by supplier"
            />
            
            <TextField
              label="Source Variable"
              value={step.config?.reportTemplateConfig?.sourceVariable || ''}
              onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                ...step.config?.reportTemplateConfig, 
                sourceVariable: e.target.value 
              })}
              fullWidth
              placeholder="salesData"
              helperText="Variable containing the data to include in the report"
            />
            
            <TextField
              label="Output Variable"
              value={step.config?.reportTemplateConfig?.outputVariable || 'formattedReport'}
              onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                ...step.config?.reportTemplateConfig, 
                outputVariable: e.target.value 
              })}
              fullWidth
              placeholder="formattedReport"
              helperText="Variable to store the generated HTML report"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📋 Report Header</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Title"
              value={step.config?.reportTemplateConfig?.header?.title || ''}
              onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                ...step.config?.reportTemplateConfig,
                header: { ...step.config?.reportTemplateConfig?.header, title: e.target.value }
              })}
              fullWidth
              placeholder="{{supplierName}} Sales Report"
              helperText="Supports {{variable}} placeholders"
            />
            
            <TextField
              label="Subtitle"
              value={step.config?.reportTemplateConfig?.header?.subtitle || ''}
              onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                ...step.config?.reportTemplateConfig,
                header: { ...step.config?.reportTemplateConfig?.header, subtitle: e.target.value }
              })}
              fullWidth
              placeholder="Monthly Performance Summary"
            />
            
            <TextField
              label="Logo URL (Optional)"
              value={step.config?.reportTemplateConfig?.header?.logoUrl || ''}
              onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                ...step.config?.reportTemplateConfig,
                header: { ...step.config?.reportTemplateConfig?.header, logoUrl: e.target.value }
              })}
              fullWidth
              placeholder="https://example.com/logo.png"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.reportTemplateConfig?.header?.includeDate !== false}
                  onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                    ...step.config?.reportTemplateConfig,
                    header: { ...step.config?.reportTemplateConfig?.header, includeDate: e.target.checked }
                  })}
                />
              }
              label="Include Report Date"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">📊 Report Sections</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Add sections to your report: summary cards, data tables, text blocks, or dividers.
            </Typography>
            
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                const currentSections = step.config?.reportTemplateConfig?.sections || [];
                const newSection = {
                  id: `section-${Date.now()}`,
                  type: 'summary',
                  title: 'Summary',
                  summaryFields: []
                };
                handleConfigChange('reportTemplateConfig', {
                  ...step.config?.reportTemplateConfig,
                  sections: [...currentSections, newSection]
                });
              }}
            >
              Add Section
            </Button>
            
            {(step.config?.reportTemplateConfig?.sections || []).map((section, sectionIndex) => (
              <Card key={section.id} sx={{ p: 2, backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" fontWeight="bold">
                    Section {sectionIndex + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const updatedSections = [...step.config.reportTemplateConfig.sections];
                      updatedSections.splice(sectionIndex, 1);
                      handleConfigChange('reportTemplateConfig', {
                        ...step.config.reportTemplateConfig,
                        sections: updatedSections
                      });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Section Title"
                      value={section.title || ''}
                      onChange={(e) => {
                        const updatedSections = [...step.config.reportTemplateConfig.sections];
                        updatedSections[sectionIndex] = { ...section, title: e.target.value };
                        handleConfigChange('reportTemplateConfig', {
                          ...step.config.reportTemplateConfig,
                          sections: updatedSections
                        });
                      }}
                      fullWidth
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Section Type</InputLabel>
                      <Select
                        value={section.type || 'summary'}
                        onChange={(e) => {
                          const updatedSections = [...step.config.reportTemplateConfig.sections];
                          let updatedSection = { ...section, type: e.target.value };
                          
                          // Initialize default configs for each type
                          if (e.target.value === 'summary') {
                            updatedSection.summaryFields = [];
                          } else if (e.target.value === 'table') {
                            updatedSection.tableData = '';
                            updatedSection.columns = [];
                          } else if (e.target.value === 'text') {
                            updatedSection.content = '';
                          }
                          
                          updatedSections[sectionIndex] = updatedSection;
                          handleConfigChange('reportTemplateConfig', {
                            ...step.config.reportTemplateConfig,
                            sections: updatedSections
                          });
                        }}
                      >
                        <MenuItem value="summary">Summary Cards</MenuItem>
                        <MenuItem value="table">Data Table</MenuItem>
                        <MenuItem value="text">Text Block</MenuItem>
                        <MenuItem value="divider">Divider Line</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {section.type === 'summary' && (
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        Summary Fields
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const updatedSections = [...step.config.reportTemplateConfig.sections];
                          const currentFields = section.summaryFields || [];
                          updatedSections[sectionIndex] = {
                            ...section,
                            summaryFields: [
                              ...currentFields,
                              { label: '', variable: '', format: 'text', decimals: 2 }
                            ]
                          };
                          handleConfigChange('reportTemplateConfig', {
                            ...step.config.reportTemplateConfig,
                            sections: updatedSections
                          });
                        }}
                        sx={{ mb: 1 }}
                      >
                        Add Field
                      </Button>
                      
                      {(section.summaryFields || []).map((field, fieldIndex) => (
                        <Box key={fieldIndex} sx={{ p: 1, mb: 1, backgroundColor: '#fff', borderRadius: 1 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={5}>
                              <TextField
                                label="Label"
                                value={field.label || ''}
                                onChange={(e) => {
                                  const updatedSections = [...step.config.reportTemplateConfig.sections];
                                  const updatedFields = [...section.summaryFields];
                                  updatedFields[fieldIndex] = { ...field, label: e.target.value };
                                  updatedSections[sectionIndex] = { ...section, summaryFields: updatedFields };
                                  handleConfigChange('reportTemplateConfig', {
                                    ...step.config.reportTemplateConfig,
                                    sections: updatedSections
                                  });
                                }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                label="Variable"
                                value={field.variable || ''}
                                onChange={(e) => {
                                  const updatedSections = [...step.config.reportTemplateConfig.sections];
                                  const updatedFields = [...section.summaryFields];
                                  updatedFields[fieldIndex] = { ...field, variable: e.target.value };
                                  updatedSections[sectionIndex] = { ...section, summaryFields: updatedFields };
                                  handleConfigChange('reportTemplateConfig', {
                                    ...step.config.reportTemplateConfig,
                                    sections: updatedSections
                                  });
                                }}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={2}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Format</InputLabel>
                                <Select
                                  value={field.format || 'text'}
                                  onChange={(e) => {
                                    const updatedSections = [...step.config.reportTemplateConfig.sections];
                                    const updatedFields = [...section.summaryFields];
                                    updatedFields[fieldIndex] = { ...field, format: e.target.value };
                                    updatedSections[sectionIndex] = { ...section, summaryFields: updatedFields };
                                    handleConfigChange('reportTemplateConfig', {
                                      ...step.config.reportTemplateConfig,
                                      sections: updatedSections
                                    });
                                  }}
                                >
                                  <MenuItem value="text">Text</MenuItem>
                                  <MenuItem value="currency">Currency</MenuItem>
                                  <MenuItem value="number">Number</MenuItem>
                                  <MenuItem value="date">Date</MenuItem>
                                  <MenuItem value="percentage">Percentage</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={1}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const updatedSections = [...step.config.reportTemplateConfig.sections];
                                  const updatedFields = [...section.summaryFields];
                                  updatedFields.splice(fieldIndex, 1);
                                  updatedSections[sectionIndex] = { ...section, summaryFields: updatedFields };
                                  handleConfigChange('reportTemplateConfig', {
                                    ...step.config.reportTemplateConfig,
                                    sections: updatedSections
                                  });
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Grid>
                  )}
                  
                  {section.type === 'table' && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          label="Table Data Variable"
                          value={section.tableData || ''}
                          onChange={(e) => {
                            const updatedSections = [...step.config.reportTemplateConfig.sections];
                            updatedSections[sectionIndex] = { ...section, tableData: e.target.value };
                            handleConfigChange('reportTemplateConfig', {
                              ...step.config.reportTemplateConfig,
                              sections: updatedSections
                            });
                          }}
                          fullWidth
                          placeholder="salesData"
                          helperText="Variable containing array of data"
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                          Table Columns
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const updatedSections = [...step.config.reportTemplateConfig.sections];
                            const currentColumns = section.columns || [];
                            updatedSections[sectionIndex] = {
                              ...section,
                              columns: [...currentColumns, { field: '', header: '', format: 'text' }]
                            };
                            handleConfigChange('reportTemplateConfig', {
                              ...step.config.reportTemplateConfig,
                              sections: updatedSections
                            });
                          }}
                          sx={{ mb: 1 }}
                        >
                          Add Column
                        </Button>
                        
                        {(section.columns || []).map((column, colIndex) => (
                          <Box key={colIndex} sx={{ p: 1, mb: 1, backgroundColor: '#fff', borderRadius: 1 }}>
                            <Grid container spacing={1}>
                              <Grid item xs={5}>
                                <TextField
                                  label="Field Name"
                                  value={column.field || ''}
                                  onChange={(e) => {
                                    const updatedSections = [...step.config.reportTemplateConfig.sections];
                                    const updatedColumns = [...section.columns];
                                    updatedColumns[colIndex] = { ...column, field: e.target.value };
                                    updatedSections[sectionIndex] = { ...section, columns: updatedColumns };
                                    handleConfigChange('reportTemplateConfig', {
                                      ...step.config.reportTemplateConfig,
                                      sections: updatedSections
                                    });
                                  }}
                                  size="small"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField
                                  label="Header"
                                  value={column.header || ''}
                                  onChange={(e) => {
                                    const updatedSections = [...step.config.reportTemplateConfig.sections];
                                    const updatedColumns = [...section.columns];
                                    updatedColumns[colIndex] = { ...column, header: e.target.value };
                                    updatedSections[sectionIndex] = { ...section, columns: updatedColumns };
                                    handleConfigChange('reportTemplateConfig', {
                                      ...step.config.reportTemplateConfig,
                                      sections: updatedSections
                                    });
                                  }}
                                  size="small"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={2}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Format</InputLabel>
                                  <Select
                                    value={column.format || 'text'}
                                    onChange={(e) => {
                                      const updatedSections = [...step.config.reportTemplateConfig.sections];
                                      const updatedColumns = [...section.columns];
                                      updatedColumns[colIndex] = { ...column, format: e.target.value };
                                      updatedSections[sectionIndex] = { ...section, columns: updatedColumns };
                                      handleConfigChange('reportTemplateConfig', {
                                        ...step.config.reportTemplateConfig,
                                        sections: updatedSections
                                      });
                                    }}
                                  >
                                    <MenuItem value="text">Text</MenuItem>
                                    <MenuItem value="currency">Currency</MenuItem>
                                    <MenuItem value="number">Number</MenuItem>
                                    <MenuItem value="date">Date</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={1}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const updatedSections = [...step.config.reportTemplateConfig.sections];
                                    const updatedColumns = [...section.columns];
                                    updatedColumns.splice(colIndex, 1);
                                    updatedSections[sectionIndex] = { ...section, columns: updatedColumns };
                                    handleConfigChange('reportTemplateConfig', {
                                      ...step.config.reportTemplateConfig,
                                      sections: updatedSections
                                    });
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Box>
                        ))}
                      </Grid>
                    </>
                  )}
                  
                  {section.type === 'text' && (
                    <Grid item xs={12}>
                      <TextField
                        label="Text Content"
                        value={section.content || ''}
                        onChange={(e) => {
                          const updatedSections = [...step.config.reportTemplateConfig.sections];
                          updatedSections[sectionIndex] = { ...section, content: e.target.value };
                          handleConfigChange('reportTemplateConfig', {
                            ...step.config.reportTemplateConfig,
                            sections: updatedSections
                          });
                        }}
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Report text with {{variable}} support"
                        helperText="Supports {{variable}} placeholders"
                      />
                    </Grid>
                  )}
                </Grid>
              </Card>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">🎨 Styling</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Primary Color"
              value={step.config?.reportTemplateConfig?.styling?.primaryColor || '#1976d2'}
              onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                ...step.config?.reportTemplateConfig,
                styling: { ...step.config?.reportTemplateConfig?.styling, primaryColor: e.target.value }
              })}
              fullWidth
              placeholder="#1976d2"
              helperText="Hex color code for headers and accents"
            />
            
            <TextField
              label="Font Family"
              value={step.config?.reportTemplateConfig?.styling?.fontFamily || 'Arial'}
              onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                ...step.config?.reportTemplateConfig,
                styling: { ...step.config?.reportTemplateConfig?.styling, fontFamily: e.target.value }
              })}
              fullWidth
              placeholder="Arial"
            />
            
            <TextField
              label="Font Size"
              value={step.config?.reportTemplateConfig?.styling?.fontSize || '12px'}
              onChange={(e) => handleConfigChange('reportTemplateConfig', { 
                ...step.config?.reportTemplateConfig,
                styling: { ...step.config?.reportTemplateConfig?.styling, fontSize: e.target.value }
              })}
              fullWidth
              placeholder="12px"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const renderForEachLoopConfig = () => {
    // Get available steps for loop start/end selection
    const availableSteps = flowData?.steps?.filter(s => 
      s.id !== step.id && s.type !== 'smart_start' && s.type !== 'start' && s.type !== 'end'
    ) || [];

    // Extract variables from all previous steps (before current loop step)
    const extractedVariables = [];
    if (flowData?.steps) {
      flowData.steps.forEach((s, index) => {
        // Only look at steps before the current loop step
        const currentStepIndex = flowData.steps.findIndex(st => st.id === step.id);
        if (index >= currentStepIndex) return;
        
        // Extract output variables based on step type
        if (s.type === 'ai_extraction') {
          // Check both config.extractionFields and aiExtractionConfig.extractionFields
          const fields = s.config?.extractionFields || s.config?.aiExtractionConfig?.extractionFields || s.aiExtractionConfig?.extractionFields || [];
          fields.forEach(field => {
            if (field.variableName) {
              extractedVariables.push({
                name: field.variableName,
                type: field.dataType,
                from: s.name,
                isArray: field.dataType === 'array' || field.dataType === 'table'
              });
            }
          });
        } else if (s.type === 'variable_operations') {
          // Check both config.operations and variableOperationsConfig.operations
          const operations = s.config?.operations || s.config?.variableOperationsConfig?.operations || s.variableOperationsConfig?.operations || [];
          operations.forEach(op => {
            if (op.targetVariable) {
              const isArrayOp = [
                'create_array', 
                'append_to_array',
                'combine_arrays',
                'transform_array',
                'filter_array',
                'aggregate_array',
                'enrich_array',
                'split_array_by_field'
              ].includes(op.type);
              
              extractedVariables.push({
                name: op.targetVariable,
                type: isArrayOp ? 'array' : 'variable',
                from: s.name,
                isArray: isArrayOp
              });
            }
          });
        } else if (s.type === 'enhanced_api_call' || s.type === 'api_call') {
          // Check responseMappings in config or apiCallConfig
          const mappings = s.config?.responseMappings || s.config?.apiCallConfig?.responseMappings || s.apiCallConfig?.responseMappings || [];
          mappings.forEach(mapping => {
            if (mapping.outputVariable) {
              const isArray = mapping.sourcePath?.includes('[*]') || 
                             mapping.sourcePath?.includes('[]') ||
                             mapping.sourcePath?.includes('[i]');
              extractedVariables.push({
                name: mapping.outputVariable,
                type: 'variable',
                from: s.name,
                isArray: isArray
              });
            }
          });
          
          // Also check config.responseMapping (alternative structure)
          const responseMapping = s.config?.responseMapping || s.config?.apiCallConfig?.responseMapping || {};
          Object.entries(responseMapping).forEach(([varName, path]) => {
            if (varName && path) {
              const isArray = path.includes('[*]') || path.includes('[]') || path.includes('[i]');
              extractedVariables.push({
                name: varName,
                type: 'variable',
                from: s.name,
                isArray: isArray
              });
            }
          });
        } else if (s.type === 'advanced_collect_input' || s.type === 'collect_input') {
          const outputVar = s.config?.outputVariable || s.config?.collectInputConfig?.outputVariable || s.collectInputConfig?.outputVariable;
          if (outputVar) {
            const interfaceType = s.config?.interfaceType || s.config?.collectInputConfig?.interfaceType || s.collectInputConfig?.interfaceType;
            extractedVariables.push({
              name: outputVar,
              type: 'variable',
              from: s.name,
              isArray: interfaceType === 'table_editor' || interfaceType === 'table_with_header'
            });
          }
        } else if (s.type === 'external_data_source') {
          const outputVar = s.config?.outputVariable || s.config?.externalDataSourceConfig?.outputVariable;
          if (outputVar) {
            // External data sources typically return arrays
            extractedVariables.push({
              name: outputVar,
              type: 'array',
              from: s.name,
              isArray: true
            });
          }
        } else if (s.type === 'data_processing') {
          const outputVar = s.config?.outputVariable || s.config?.dataProcessingConfig?.outputVariable || s.dataProcessingConfig?.outputVariable;
          if (outputVar) {
            extractedVariables.push({
              name: outputVar,
              type: 'variable',
              from: s.name,
              isArray: false
            });

            const isSmartDoc = s.config?.excelCsvOutputFormat === 'smart-document' ||
                               s.config?.dataProcessingConfig?.excelCsvOutputFormat === 'smart-document';
            if (isSmartDoc) {
              extractedVariables.push({
                name: `${outputVar}_sheets`,
                type: 'array',
                from: s.name,
                isArray: true
              });
              if (s.config?.useExcelTemplates || s.config?.dataProcessingConfig?.useExcelTemplates) {
                extractedVariables.push({
                  name: `${outputVar}_templateExtracted`,
                  type: 'variable',
                  from: s.name,
                  isArray: false
                });
              }
            }
          }
        }
      });
    }

    // Filter for array variables and add a fallback for common array naming patterns
    const arrayVariables = extractedVariables.filter(v => v.isArray);
    
    // Also include variables with array-like names that might have been missed
    const allVariableNames = extractedVariables.map(v => v.name);
    allVariableNames.forEach(name => {
      if (!arrayVariables.find(v => v.name === name)) {
        // Check if name suggests it's an array
        if (name && (
          name.toLowerCase().includes('array') ||
          name.toLowerCase().includes('list') ||
          name.toLowerCase().includes('items') ||
          name.toLowerCase().endsWith('s') // plural names often indicate arrays
        )) {
          const originalVar = extractedVariables.find(v => v.name === name);
          if (originalVar) {
            arrayVariables.push({
              ...originalVar,
              isArray: true,
              name: name + ' (potential array)'
            });
          }
        }
      }
    });

    // Get loop start and end step details for display
    const loopStartStep = availableSteps.find(s => 
      s.id === step.config?.forEachLoopConfig?.loopStartStepId || 
      s.id === step.forEachLoopConfig?.loopStartStepId
    );
    const loopEndStep = availableSteps.find(s => 
      s.id === step.config?.forEachLoopConfig?.loopEndStepId || 
      s.id === step.forEachLoopConfig?.loopEndStepId
    );

    return (
      <Box>
        <Typography variant="body2" color="textSecondary" mb={2}>
          Iterate over an array and execute steps for each item. Steps between Loop Start and Loop End will be executed for each array item.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Loop variables will be available to all steps inside the loop:
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li><strong>{`{{${step.config?.forEachLoopConfig?.itemVariableName || step.forEachLoopConfig?.itemVariableName || 'currentItem'}}}`}</strong> - Current item from array (or object entry)</li>
              <li><strong>{`{{${step.config?.forEachLoopConfig?.indexVariableName || step.forEachLoopConfig?.indexVariableName || 'index'}}}`}</strong> - Current index (0-based)</li>
            </ul>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              <strong>Note:</strong> If source is an object (e.g., from "Split Array By Field"), it will be automatically converted to an array of entries. Each item will have <code>key</code> and <code>value</code> properties.
            </Typography>
          </Typography>
        </Alert>

        <Grid container spacing={2}>
          {/* Source Array */}
          <Grid item xs={12}>
            {arrayVariables.length > 0 ? (
              <>
                <FormControl fullWidth>
                  <InputLabel>Source Array Variable</InputLabel>
                  <Select
                    value={step.config?.forEachLoopConfig?.sourceArray || step.forEachLoopConfig?.sourceArray || ''}
                    onChange={(e) => handleConfigChange('forEachLoopConfig', { 
                      ...step.config?.forEachLoopConfig,
                      ...step.forEachLoopConfig,
                      sourceArray: e.target.value
                    })}
                  >
                    <MenuItem value="">
                      <em>Select array variable</em>
                    </MenuItem>
                    {arrayVariables.map((variable, idx) => (
                      <MenuItem key={idx} value={variable.name.replace(' (potential array)', '')}>
                        {variable.name} <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>from {variable.from}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the array variable to loop over ({arrayVariables.length} array{arrayVariables.length !== 1 ? 's' : ''} detected from previous steps)
                  </FormHelperText>
                </FormControl>
                
                {/* Show all detected variables for debugging */}
                {extractedVariables.length > arrayVariables.length && (
                  <Accordion sx={{ mt: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="caption">
                        Show all {extractedVariables.length} detected variables (debug info)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography variant="caption" color="textSecondary" gutterBottom>
                          Variables from previous steps:
                        </Typography>
                        {extractedVariables.map((v, idx) => (
                          <Chip
                            key={idx}
                            label={`${v.name} (${v.isArray ? 'array' : 'variable'}) from ${v.from}`}
                            size="small"
                            color={v.isArray ? "primary" : "default"}
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            ) : (
              <>
                <TextField
                  label="Source Array Variable"
                  value={step.config?.forEachLoopConfig?.sourceArray || step.forEachLoopConfig?.sourceArray || ''}
                  onChange={(e) => handleConfigChange('forEachLoopConfig', { 
                    ...step.config?.forEachLoopConfig,
                    ...step.forEachLoopConfig,
                    sourceArray: e.target.value
                  })}
                  fullWidth
                  placeholder="e.g., myArrayVariable, invoiceLines, items"
                  helperText="Enter the name of the array variable (no array variables detected from previous steps)"
                />
                {extractedVariables.length > 0 ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Found {extractedVariables.length} variable(s) but none detected as arrays.</strong> If you have array variables, you can manually type their names above.
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption">Detected variables:</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {extractedVariables.map((v, idx) => (
                          <Chip key={idx} label={`${v.name} from ${v.from}`} size="small" />
                        ))}
                      </Box>
                    </Box>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Tip:</strong> Create an array variable in a step before this loop (e.g., using Variable Operations "Create Array" or an API call that returns an array).
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </Grid>

          {/* Item Variable Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Item Variable Name"
              value={step.config?.forEachLoopConfig?.itemVariableName || step.forEachLoopConfig?.itemVariableName || 'currentItem'}
              onChange={(e) => handleConfigChange('forEachLoopConfig', { 
                ...step.config?.forEachLoopConfig,
                ...step.forEachLoopConfig,
                itemVariableName: e.target.value
              })}
              fullWidth
              placeholder="currentItem"
              helperText="Variable name for each array item"
            />
          </Grid>

          {/* Index Variable Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Index Variable Name"
              value={step.config?.forEachLoopConfig?.indexVariableName || step.forEachLoopConfig?.indexVariableName || 'index'}
              onChange={(e) => handleConfigChange('forEachLoopConfig', { 
                ...step.config?.forEachLoopConfig,
                ...step.forEachLoopConfig,
                indexVariableName: e.target.value
              })}
              fullWidth
              placeholder="index"
              helperText="Variable name for array index"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Loop Boundaries
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Select which steps should execute inside the loop. All steps between the start and end step will be executed for each item.
            </Typography>
          </Grid>

          {/* Loop Start Step */}
          <Grid item xs={12}>
            {availableSteps.length === 0 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>No steps available yet.</strong> Create the steps you want to execute inside the loop first, then come back to configure the loop boundaries.
                </Typography>
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Loop Start Step</InputLabel>
                <Select
                  value={step.config?.forEachLoopConfig?.loopStartStepId || step.forEachLoopConfig?.loopStartStepId || ''}
                  onChange={(e) => handleConfigChange('forEachLoopConfig', { 
                    ...step.config?.forEachLoopConfig,
                    ...step.forEachLoopConfig,
                    loopStartStepId: e.target.value
                  })}
                >
                  <MenuItem value="">
                    <em>Select start step</em>
                  </MenuItem>
                  {availableSteps.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name} ({s.type})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>First step to execute in each loop iteration</FormHelperText>
              </FormControl>
            )}
          </Grid>

          {/* Loop End Step */}
          <Grid item xs={12}>
            {availableSteps.length === 0 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>No steps available yet.</strong> Create the steps you want to execute inside the loop first, then come back to configure the loop boundaries.
                </Typography>
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Loop End Step</InputLabel>
                <Select
                  value={step.config?.forEachLoopConfig?.loopEndStepId || step.forEachLoopConfig?.loopEndStepId || ''}
                  onChange={(e) => handleConfigChange('forEachLoopConfig', { 
                    ...step.config?.forEachLoopConfig,
                    ...step.forEachLoopConfig,
                    loopEndStepId: e.target.value
                  })}
                >
                  <MenuItem value="">
                    <em>Select end step</em>
                  </MenuItem>
                  {availableSteps.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name} ({s.type})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Last step to execute in each loop iteration</FormHelperText>
              </FormControl>
            )}
          </Grid>

          {/* Visual indicator of loop boundaries */}
          {loopStartStep && loopEndStep && (
            <Grid item xs={12}>
              <Alert severity="success" icon={<LoopIcon />}>
                <Typography variant="body2">
                  <strong>Loop will execute:</strong> {loopStartStep.name} → ... → {loopEndStep.name}
                </Typography>
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Loop Settings
            </Typography>
          </Grid>

          {/* Max Iterations */}
          <Grid item xs={12}>
            <Typography gutterBottom>
              Max Iterations: {step.config?.forEachLoopConfig?.maxIterations || step.forEachLoopConfig?.maxIterations || 50}
            </Typography>
            <Slider
              value={step.config?.forEachLoopConfig?.maxIterations || step.forEachLoopConfig?.maxIterations || 50}
              onChange={(e, value) => handleConfigChange('forEachLoopConfig', { 
                ...step.config?.forEachLoopConfig,
                ...step.forEachLoopConfig,
                maxIterations: value
              })}
              min={1}
              max={1000}
              marks={[
                { value: 1, label: '1' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
                { value: 500, label: '500' },
                { value: 1000, label: '1000' }
              ]}
              valueLabelDisplay="auto"
            />
            <FormHelperText>Maximum number of iterations (safety limit)</FormHelperText>
          </Grid>

          {/* Persist Variables */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.forEachLoopConfig?.persistVariables || step.forEachLoopConfig?.persistVariables || false}
                  onChange={(e) => handleConfigChange('forEachLoopConfig', { 
                    ...step.config?.forEachLoopConfig,
                    ...step.forEachLoopConfig,
                    persistVariables: e.target.checked
                  })}
                />
              }
              label="Persist Loop Variables After Completion"
            />
            <FormHelperText>
              If enabled, loop variables will keep their last values after the loop completes. Otherwise, they will be removed.
            </FormHelperText>
          </Grid>

          {/* Validation Warnings */}
          {step.config?.forEachLoopConfig?.loopStartStepId && step.config?.forEachLoopConfig?.loopEndStepId && (
            <>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Important:</strong> Make sure the flow path from Loop Start to Loop End is correct. Branching logic inside loops is supported, but ensure all branches eventually lead to the Loop End step or back to the loop flow.
                  </Typography>
                </Alert>
              </Grid>
              
              {/* Check for nested loops */}
              {(() => {
                const loopStartId = step.config?.forEachLoopConfig?.loopStartStepId || step.forEachLoopConfig?.loopStartStepId;
                const loopEndId = step.config?.forEachLoopConfig?.loopEndStepId || step.forEachLoopConfig?.loopEndStepId;
                
                // Find steps in loop boundary
                const stepMap = new Map();
                availableSteps.forEach(s => stepMap.set(s.id, s));
                
                const stepsInLoop = new Set();
                const visited = new Set();
                const queue = [loopStartId];
                
                while (queue.length > 0) {
                  const currentId = queue.shift();
                  if (visited.has(currentId) || currentId === loopEndId) {
                    if (currentId === loopEndId) stepsInLoop.add(currentId);
                    break;
                  }
                  visited.add(currentId);
                  stepsInLoop.add(currentId);
                  
                  const connections = flowData?.connections?.filter(c => c.source === currentId) || [];
                  connections.forEach(conn => {
                    if (!visited.has(conn.target)) {
                      queue.push(conn.target);
                    }
                  });
                }
                
                // Count nested loops
                let nestedLoopCount = 0;
                let nestedLoops = [];
                stepsInLoop.forEach(stepId => {
                  const s = stepMap.get(stepId);
                  if (s && s.type === 'for_each_loop' && s.id !== step.id) {
                    nestedLoopCount++;
                    nestedLoops.push(s.name);
                  }
                });
                
                if (nestedLoopCount > 0) {
                  return (
                    <Grid item xs={12}>
                      <Alert 
                        severity={nestedLoopCount >= 3 ? "error" : "warning"} 
                        sx={{ mt: 1 }}
                      >
                        <Typography variant="body2">
                          <strong>{nestedLoopCount >= 3 ? "Critical:" : "Warning:"}</strong> {nestedLoopCount} nested loop{nestedLoopCount > 1 ? 's' : ''} detected inside this loop: {nestedLoops.join(', ')}
                          {nestedLoopCount >= 3 && (
                            <><br />Nested loops beyond depth 3 may cause performance issues. Consider refactoring.</>
                          )}
                        </Typography>
                      </Alert>
                    </Grid>
                  );
                }
                return null;
              })()}
              
              {/* Check for circular references */}
              {(() => {
                const loopStartId = step.config?.forEachLoopConfig?.loopStartStepId || step.forEachLoopConfig?.loopStartStepId;
                const loopEndId = step.config?.forEachLoopConfig?.loopEndStepId || step.forEachLoopConfig?.loopEndStepId;
                
                // Check if there's a path from loop step to loop start (which would be circular if loop end connects back)
                const hasCircularPath = flowData?.connections?.some(c => 
                  c.source === loopEndId && c.target === step.id
                );
                
                if (hasCircularPath) {
                  return (
                    <Grid item xs={12}>
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Error:</strong> Circular reference detected! The loop end step connects back to the loop step itself. This will create an infinite loop. The loop should connect to the next step in the flow after completion.
                        </Typography>
                      </Alert>
                    </Grid>
                  );
                }
                return null;
              })()}
              
              {/* Check if loop is configured with valid source array */}
              {!step.config?.forEachLoopConfig?.sourceArray && !step.forEachLoopConfig?.sourceArray && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Configuration Error:</strong> No source array selected. Please select an array variable to loop over.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
    );
  };

  const renderConfigFields = () => {
    switch (step.type) {
      case 'smart_start':
        return renderSmartStartConfig();
      case 'data_processing':
        return renderDataProcessingConfig();
      case 'ai_extraction':
        return renderAiExtractionConfig();
      case 'enhanced_api_call':
        return renderEnhancedApiCallConfig();
      case 'advanced_collect_input':
        return renderAdvancedCollectInputConfig();
      case 'branch_logic':
        return renderBranchLogicConfig();
      case 'variable_operations':
        return renderVariableOperationsConfig();
      case 'webhook':
        return renderWebhookConfig(projectId);
      case 'wait':
        return renderWaitConfig();
      case 'external_data_source':
        return renderExternalDataSourceConfig(companyId, projectId);
      case 'report_template':
        return renderReportTemplateConfig();
      case 'for_each_loop':
        return renderForEachLoopConfig();
      case 'end':
        return renderEndNodeConfig();
      default:
        return (
          <TextField
            label="Step Description"
            value={step.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            multiline
            rows={2}
            fullWidth
            helperText="Description of what this step does"
          />
        );
    }
  };

  // Reusable configuration content component
  const renderConfigurationContent = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      <TextField
        label="Step Name"
        value={localStep.name || ''}
        onChange={(e) => handleFieldChange('name', e.target.value)}
        onBlur={commitChanges}
        fullWidth
      />
      
      <FormControlLabel
        control={
          <Switch
            checked={localStep.enabled !== false}
            onChange={(e) => handleFieldChangeAndCommit('enabled', e.target.checked)}
          />
        }
        label="Step Enabled"
        sx={{ 
          mb: 1,
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem',
            fontWeight: localStep.enabled === false ? 400 : 500,
            color: localStep.enabled === false ? 'text.secondary' : 'text.primary'
          }
        }}
      />
      
      {localStep.enabled === false && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2">
            This step is disabled and will be skipped during execution.
            {localStep.type === 'branch_logic' && ' For branch logic steps, the flow will always take the true/left path.'}
          </Typography>
        </Alert>
      )}
      
      <Divider />
      
      {renderConfigFields()}
      
      <Divider />
      
      <FormControlLabel
        control={
          <Switch
            checked={localStep.required || false}
            onChange={(e) => handleFieldChangeAndCommit('required', e.target.checked)}
          />
        }
        label="Required Step"
      />
    </Box>
  );

  // Panel content component - reusable for both desktop and mobile
  const panelContent = (
    <>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Configure Node
          </Typography>
          <Box display="flex" gap={1}>
            {!isMobile && (
              <Tooltip title="Expand to Full View">
                <IconButton size="small" onClick={() => setIsModalOpen(true)}>
                  <ExpandIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => { commitChanges(); onClose(); }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Chip 
          label={step.type?.replace('_', ' ').toUpperCase()} 
          size="small" 
          sx={{ mt: 1 }}
        />
      </Box>
      
      {/* Configuration Form */}
      <Box sx={{ 
        p: 3, 
        flexGrow: 1, 
        height: 0, // Force flex child to constrain height
        overflow: 'auto',
        overscrollBehavior: 'contain',
        scrollbarGutter: 'stable',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
          '&:hover': {
            background: '#a8a8a8',
          },
        },
      }}>
        {renderConfigurationContent()}
      </Box>
    </>
  );

  return (
    <>
      {/* Mobile: Drawer overlay */}
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={configPanelOpen && !!step}
          onClose={() => {
            commitChanges();
            setConfigPanelOpen(false);
            onClose();
          }}
          PaperProps={{
            sx: {
              height: '80vh',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }
          }}
        >
          {panelContent}
        </Drawer>
      ) : (
        /* Desktop: Side Panel */
        <Paper sx={{ 
          width: 500, 
          height: '100%', 
          minHeight: 0, // Important for flex child
          display: 'flex', 
          flexDirection: 'column', 
          borderLeft: '1px solid #e0e0e0',
          overflow: 'hidden' // Prevent panel from growing beyond container
        }}>
          {panelContent}
        </Paper>
      )}

      {/* Expanded Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => { commitChanges(); setIsModalOpen(false); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}>
          <Box>
            <Typography variant="h5">
              Configure Node
            </Typography>
            <Chip 
              label={localStep.type?.replace('_', ' ').toUpperCase()} 
              size="small" 
              sx={{ mt: 1 }}
            />
          </Box>
          <IconButton onClick={() => { commitChanges(); setIsModalOpen(false); }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 4,
          pt: 3,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': {
              background: '#a8a8a8',
            },
          },
        }}>
          <Box sx={{ mt: 2 }}>
            {renderConfigurationContent()}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Test Flow Dialog Component
const TestFlowDialog = ({ open, onClose, flowData, availableVariables = [], projectId }) => {
  const [testState, setTestState] = useState({
    isRunning: false,
    currentStepId: null,
    currentStepIndex: 0,
    stepResults: {},
    flowVariables: {},
    errors: [],
    completedSteps: [],
    totalSteps: 0,
    startTime: null,
    endTime: null,
    executionId: null,
    executionOrder: []
  });

  const [testInputs, setTestInputs] = useState({
    uploadedFiles: [],
    customData: '{}',
    apiEndpoints: {},
    userInputs: {}
  });

  // Get execution order of steps based on connections
  const getExecutionOrder = useCallback(() => {
    if (!flowData.steps || !flowData.connections) return [];
    
    const startNode = flowData.steps.find(step => step.type === 'smart_start');
    if (!startNode) return [];

    const visited = new Set();
    const executionOrder = [];
    
    const dfs = (nodeId) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      
      const step = flowData.steps.find(s => s.id === nodeId);
      if (step && step.type !== 'smart_start') {
        executionOrder.push(step);
      }
      
      // Find next steps
      const connections = flowData.connections.filter(conn => conn.source === nodeId);
      connections.forEach(conn => dfs(conn.target));
    };
    
    dfs(startNode.id);
    return executionOrder;
  }, [flowData]);

  // Generate sample data for different step types
  const generateSampleData = (stepType, stepConfig) => {
    switch (stepType) {
      case 'data_processing':
        const hasUploadedFiles = testInputs.uploadedFiles && testInputs.uploadedFiles.length > 0;
        const primaryFile = hasUploadedFiles ? testInputs.uploadedFiles[0] : null;
        const isAiVisionEnabled = stepConfig?.enableAiVision || false;
        const primaryOCR = stepConfig?.ocrEngine || 'tesseract';
        const getOCRDisplayName = (engine) => {
          const ocrNames = {
            'tesseract': 'Tesseract',
            'guten_ocr': 'Guten OCR',
            'google_ai_vision': 'Google AI Vision',
            'aws_textract': 'AWS Textract',
            'qwen_vision': 'QWEN Vision'
          };
          return ocrNames[engine] || engine;
        };
        const hasFallback = stepConfig?.enableFallback ?? true;
        const rotationMethod = stepConfig?.rotationDetectionMethod || 'disabled';
        const hasRotationEnabled = rotationMethod !== 'disabled';
        const manualRotation = stepConfig?.manualRotation || 0;
        
        // Determine processing method priority
        const processingMethods = [];
        if (isAiVisionEnabled) {
          processingMethods.push('AI Vision (GPT-4)');
        }
        if (hasRotationEnabled) {
          processingMethods.push(`Rotation Detection (${rotationMethod === 'manual' ? `Manual ${manualRotation}°` : rotationMethod})`);
        }
        processingMethods.push(`OCR (${primaryOCR})`);
        if (hasFallback) {
          processingMethods.push('Fallback OCR');
        }
        
        return {
          processingEngine: isAiVisionEnabled ? 'AI Vision + OCR Hybrid' : `Primary OCR (${primaryOCR})`,
          extractedText: hasUploadedFiles 
            ? `${isAiVisionEnabled ? '🤖 AI VISION ANALYSIS' : '👁️ OCR EXTRACTION'} from uploaded file: ${primaryFile.name}\n\nProcessing ${primaryFile.type} file (${(primaryFile.size / 1024).toFixed(1)} KB)...\n\n${hasRotationEnabled ? `🔄 ROTATION DETECTION (${rotationMethod.toUpperCase()}): ${rotationMethod === 'manual' ? `Applied manual ${manualRotation}° rotation` : 'Automatically detected and corrected image orientation'} for optimal OCR accuracy.\n\n` : ''}${isAiVisionEnabled ? 'GPT-4 Vision analyzed the document structure and content intelligently.\n\n✅ AI Vision Results:\n- Intelligent document understanding\n- Context-aware extraction\n- Smart field identification\n\n📋 OCR Backup Results:\n- Raw text extraction\n- Character recognition for validation' : 'Traditional OCR extracted raw text from the document.'}`
            : `${isAiVisionEnabled ? '🤖 AI VISION ANALYSIS' : '👁️ OCR EXTRACTION'}\n\n${hasRotationEnabled ? `🔄 ROTATION DETECTION (${rotationMethod.toUpperCase()}): ${rotationMethod === 'manual' ? `Will apply manual ${manualRotation}° rotation` : 'Will automatically detect and correct rotated images'}.\n\n` : ''}${isAiVisionEnabled ? 'GPT-4 Vision would intelligently analyze document structure and extract data with context awareness.' : 'Sample extracted text from document processing...'}`,
          extractedData: hasUploadedFiles ? {
            fileName: primaryFile.name,
            fileType: primaryFile.type,
            fileSize: primaryFile.size,
            uploadTime: new Date().toISOString(),
            processingMethod: isAiVisionEnabled ? 'AI Vision + OCR Hybrid' : `OCR (${primaryOCR})`,
            rotationDetection: {
              enabled: hasRotationEnabled,
              method: rotationMethod,
              manualAngle: rotationMethod === 'manual' ? manualRotation : null,
              description: hasRotationEnabled 
                ? (rotationMethod === 'manual' 
                    ? `Manual rotation: ${manualRotation}°` 
                    : `Automatic detection using ${rotationMethod}`)
                : 'Rotation detection disabled'
            },
            extractedFields: isAiVisionEnabled ? {
              // AI Vision results - more intelligent extraction
              customerName: "SmartCorp Solutions Inc. (AI Vision detected full company name)",
              invoiceNumber: "INV-AI-2024-001 (AI identified invoice format pattern)",
              totalAmount: 3247.85,
              confidence: 0.94,
              aiInsights: [
                "Document appears to be a professional invoice",
                "High confidence in extracted fields",
                "Detected table structure automatically"
              ],
              items: [
                { name: "AI-Enhanced Product A (detected from table)", quantity: 4, price: 812.50 },
                { name: "AI-Enhanced Product B (intelligent parsing)", quantity: 3, price: 875.25 }
              ]
            } : {
              // Standard OCR results
              customerName: "Extracted Customer Name",
              invoiceNumber: "INV-REAL-001",
              totalAmount: 2450.50,
              items: [
                { name: "Real Product A", quantity: 3, price: 750.00 },
                { name: "Real Product B", quantity: 2, price: 475.25 }
              ]
            },
          } : {
            processingMethod: isAiVisionEnabled ? 'AI Vision + OCR Hybrid' : `OCR (${primaryOCR})`,
            rotationDetection: {
              enabled: hasRotationEnabled,
              method: rotationMethod,
              manualAngle: rotationMethod === 'manual' ? manualRotation : null,
              description: hasRotationEnabled 
                ? (rotationMethod === 'manual' 
                    ? `Manual rotation: ${manualRotation}°` 
                    : `Will detect and correct rotated images using ${rotationMethod}`)
                : 'Rotation detection disabled'
            },
            extractedFields: isAiVisionEnabled ? {
              customerName: "Acme Corporation (AI Vision enhanced)",
              invoiceNumber: "INV-AI-2024-001 (intelligent pattern recognition)",
              totalAmount: 1847.99,
              aiInsights: [
                "Sample document analysis",
                "AI Vision would provide contextual understanding",
                "Enhanced accuracy over traditional OCR"
              ]
            } : {
              customerName: "Acme Corporation",
              invoiceNumber: "INV-2024-001", 
              totalAmount: 1250.75,
              items: [
                { name: "Product A", quantity: 2, price: 500.00 },
                { name: "Product B", quantity: 1, price: 250.75 }
              ]
            },
          },
          processingSteps: [
            isAiVisionEnabled ? "✅ AI Vision Analysis - Primary method" : "⚠️ AI Vision - Disabled",
            hasRotationEnabled 
              ? `✅ Rotation Detection (${rotationMethod}) - ${rotationMethod === 'manual' ? `Fixed ${manualRotation}°` : 'Auto-detect'}`
              : "⚠️ Rotation Detection - Disabled",
            `✅ ${getOCRDisplayName(primaryOCR)} OCR Engine - ${isAiVisionEnabled ? 'Backup/Validation' : 'Primary method'}`,
            hasFallback ? "✅ Fallback OCR - Available if primary fails" : "⚠️ Fallback OCR - Disabled"
          ],
          uploadedFile: primaryFile ? {
            name: primaryFile.name,
            type: primaryFile.type,
            size: primaryFile.size,
            lastModified: primaryFile.lastModified
          } : null,
          confidence: isAiVisionEnabled ? (hasUploadedFiles ? 0.94 : 0.91) : (hasUploadedFiles ? 0.89 : 0.92),
          processingTime: isAiVisionEnabled ? (hasUploadedFiles ? 4.1 : 3.2) : (hasUploadedFiles ? 2.3 : 1.8),
          aiVisionEnabled: isAiVisionEnabled,
          visionPrompt: stepConfig?.visionPrompt || null,
          rotationDetectionMethod: rotationMethod,
          rotationDetectionEnabled: hasRotationEnabled,
          manualRotationAngle: rotationMethod === 'manual' ? manualRotation : null
        };
      
      case 'ai_extraction':
        const fields = stepConfig?.extractionFields || [];
        const extractedData = {};
        const hasUploadedFilesAI = testInputs.uploadedFiles && testInputs.uploadedFiles.length > 0;
        const sourceFile = hasUploadedFilesAI ? testInputs.uploadedFiles[0] : null;
        
        fields.forEach(field => {
          switch (field.dataType) {
            case 'string':
              extractedData[field.name] = hasUploadedFilesAI 
                ? `${field.name} extracted from ${sourceFile.name}`
                : `Sample ${field.name}`;
              break;
            case 'number':
              extractedData[field.name] = Math.floor(Math.random() * 1000);
              break;
            case 'currency':
              extractedData[field.name] = (Math.random() * 1000).toFixed(2);
              break;
            case 'date':
              extractedData[field.name] = new Date().toISOString().split('T')[0];
              break;
            case 'array':
              extractedData[field.name] = hasUploadedFilesAI 
                ? [`Item 1 from ${sourceFile.name}`, `Item 2 from ${sourceFile.name}`, `Item 3 from ${sourceFile.name}`]
                : [`Item 1`, `Item 2`, `Item 3`];
              break;
            default:
              extractedData[field.name] = hasUploadedFilesAI 
                ? `${field.name} from ${sourceFile.name}`
                : `Sample ${field.name}`;
          }
        });
        
        return {
          extractedData,
          sourceFile: sourceFile ? {
            name: sourceFile.name,
            type: sourceFile.type,
            size: sourceFile.size
          } : null,
          confidence: hasUploadedFilesAI ? 0.84 : 0.87,
          processingTime: hasUploadedFilesAI ? 3.1 : 2.3
        };
      
      case 'enhanced_api_call':
        return {
          status: 200,
          data: {
            success: true,
            message: "API call successful",
            result: {
              processedData: "Sample API response data"
            }
          },
          responseTime: 0.5
        };
      
      case 'branch_logic':
        const conditions = stepConfig?.conditions || [];
        const evaluationResults = conditions.map(condition => ({
          condition: condition.variable,
          operator: condition.operator,
          expectedValue: condition.value,
          actualValue: "Sample Value",
          passed: Math.random() > 0.5
        }));
        return {
          evaluationResults,
          branchTaken: evaluationResults.some(r => r.passed) ? 'right' : 'left',
          allConditionsMet: evaluationResults.every(r => r.passed)
        };
      
      case 'advanced_collect_input':
        return {
          userInputReceived: true,
          inputData: {
            purchaseOrder: "PO-2024-001",
            reviewedItems: [
              { name: "Product A", quantity: 2, approved: true },
              { name: "Product B", quantity: 1, approved: true }
            ]
          }
        };
      
      case 'variable_operations':
        return {
          variablesUpdated: ["var1", "var2", "var3"],
          operations: ["calculation", "transformation", "validation"],
          result: "Variables processed successfully"
        };
      
      default:
        return { message: `Sample output for ${stepType}` };
    }
  };

  // Execute a single step using real backend
  const executeStep = async (step, executionId) => {
    try {
      console.log(`📤 Sending step execution request for step: ${step.id}`);
      
      // Call backend step execution API
      const response = await api.post(`/flows/execute/${executionId}/step`, {
        stepId: step.id,
        userInput: null // For now, we'll handle user input steps separately
      });

      const result = response.data;
      console.log(`📥 Step execution response:`, result);

      return {
        success: result.success,
        stepId: result.stepId,
        stepName: result.stepName,
        stepType: result.stepType,
        result: result.result,
        error: result.error,
        executionTime: result.executionTime,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error(`❌ Step execution error for step ${step.id}:`, error);
      
      return {
        success: false,
        stepId: step.id,
        stepName: step.name,
        stepType: step.type,
        error: error.response?.data?.message || error.message || 'Step execution failed',
        executionTime: 0,
        timestamp: new Date().toISOString()
      };
    }
  };

  // Start test execution with real backend
  const startTest = async () => {
    const executionOrder = getExecutionOrder();
    if (executionOrder.length === 0) {
      setTestState(prev => ({
        ...prev,
        errors: ['No executable steps found in the flow']
      }));
      return;
    }

    if (!projectId) {
      setTestState(prev => ({
        ...prev,
        errors: ['Project ID is required for flow execution']
      }));
      return;
    }

    setTestState(prev => ({
      ...prev,
      isRunning: true,
      currentStepIndex: 0,
      currentStepId: null,
      stepResults: {},
      flowVariables: JSON.parse(testInputs.customData || '{}'),
      errors: [],
      completedSteps: [],
      totalSteps: executionOrder.length,
      startTime: new Date().toISOString(),
      endTime: null,
      executionOrder
    }));

    try {
      // Prepare FormData for file upload
      const formData = new FormData();
      
      // Add uploaded files
      if (testInputs.uploadedFiles && testInputs.uploadedFiles.length > 0) {
        testInputs.uploadedFiles.forEach(file => {
          formData.append('files', file);
        });
      }
      
      // Add custom data
      formData.append('customData', testInputs.customData || '{}');
      formData.append('testMode', 'true');

      console.log('🚀 Starting flow execution for project:', projectId);
      
      // Start flow execution on backend
      const executionResponse = await api.post(`/flows/execute/${projectId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const { executionId, flowSteps } = executionResponse.data;
      console.log('✅ Flow execution started:', { executionId, flowSteps });

      setTestState(prev => ({
        ...prev,
        executionId
      }));

      // Execute steps sequentially using the backend
      for (let i = 0; i < executionOrder.length; i++) {
        const step = executionOrder[i];
        
        setTestState(prev => ({
          ...prev,
          currentStepIndex: i,
          currentStepId: step.id
        }));

        console.log(`🔄 Executing step ${i + 1}/${executionOrder.length}:`, step.name);

        const result = await executeStep(step, executionId);
        
        setTestState(prev => ({
          ...prev,
          stepResults: {
            ...prev.stepResults,
            [step.id]: result
          },
          completedSteps: [...prev.completedSteps, step.id],
          errors: result.success ? prev.errors : [...prev.errors, result.error]
        }));

        // Stop if there's an error and step is required
        if (!result.success && step.required) {
          console.error('❌ Step execution failed:', result.error);
          setTestState(prev => ({
            ...prev,
            isRunning: false,
            endTime: new Date().toISOString()
          }));
          return;
        }
      }

      // Test completed
      console.log('🎉 Flow execution completed successfully');
      setTestState(prev => ({
        ...prev,
        isRunning: false,
        currentStepId: null,
        endTime: new Date().toISOString()
      }));

    } catch (error) {
      console.error('❌ Flow execution failed:', error);
      setTestState(prev => ({
        ...prev,
        isRunning: false,
        errors: [...prev.errors, error.response?.data?.message || error.message || 'Flow execution failed'],
        endTime: new Date().toISOString()
      }));
    }
  };

  // Reset test state
  const resetTest = () => {
    setTestState({
      isRunning: false,
      currentStepId: null,
      currentStepIndex: 0,
      stepResults: {},
      flowVariables: {},
      errors: [],
      completedSteps: [],
      totalSteps: 0,
      startTime: null,
      endTime: null,
      executionId: null,
      executionOrder: []
    });
    // Clear uploaded files on reset
    setTestInputs(prev => ({
      ...prev,
      uploadedFiles: []
    }));
  };

  // Get step status for display
  const getStepStatus = (stepId) => {
    if (testState.currentStepId === stepId) return 'running';
    if (testState.completedSteps.includes(stepId)) {
      const result = testState.stepResults[stepId];
      return result?.success ? 'success' : 'error';
    }
    return 'pending';
  };

  // Calculate test duration
  const getTestDuration = () => {
    if (!testState.startTime) return null;
    const end = testState.endTime || new Date().toISOString();
    const duration = new Date(end) - new Date(testState.startTime);
    return (duration / 1000).toFixed(1);
  };

  const executionOrder = getExecutionOrder();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <TestIcon color="primary" />
            <Typography variant="h6">Test Flow</Typography>
            {testState.isRunning && (
              <Chip 
                label="Running..." 
                color="primary" 
                size="small"
                icon={<PauseIcon />}
              />
            )}
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 0 }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
          {/* Test Configuration */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">🔧 Test Configuration</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} mb={2}>
                  <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 3, textAlign: 'center' }}>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        setTestInputs(prev => ({ ...prev, uploadedFiles: files }));
                      }}
                      style={{ display: 'none' }}
                      id="file-upload-input"
                    />
                    <label htmlFor="file-upload-input">
                      <Button
                        component="span"
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        sx={{ mb: 1 }}
                      >
                        Upload Test Files
                      </Button>
                    </label>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      Upload documents to test with real files instead of sample data
                    </Typography>
                    {testInputs.uploadedFiles && testInputs.uploadedFiles.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="primary" gutterBottom>
                          📎 {testInputs.uploadedFiles.length} file(s) uploaded:
                        </Typography>
                        {testInputs.uploadedFiles.map((file, index) => (
                          <Chip
                            key={index}
                            label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onDelete={() => {
                              setTestInputs(prev => ({
                                ...prev,
                                uploadedFiles: prev.uploadedFiles.filter((_, i) => i !== index)
                              }));
                            }}
                            sx={{ mr: 1, mb: 1 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Custom Test Data (JSON)"
                    value={testInputs.customData}
                    onChange={(e) => setTestInputs(prev => ({ ...prev, customData: e.target.value }))}
                    multiline
                    rows={4}
                    fullWidth
                    placeholder='{"customVar": "value", "testData": "sample"}'
                    helperText="Initial variables to start the test with"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="body2" color="textSecondary">
                      Test will execute {executionOrder.length} steps in sequence using real backend processing
                    </Typography>
                    {projectId && (
                      <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                        ⚡ Real-time execution with project: {projectId}
                      </Typography>
                    )}
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        startIcon={<TestIcon />}
                        onClick={startTest}
                        disabled={testState.isRunning || executionOrder.length === 0}
                      >
                        Start Test
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={resetTest}
                        disabled={testState.isRunning}
                      >
                        Reset
                      </Button>
                    </Box>
                    {getTestDuration() && (
                      <Typography variant="body2" color="textSecondary">
                        Duration: {getTestDuration()}s
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Test Progress */}
        {executionOrder.length > 0 && (
          <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              📊 Test Progress
            </Typography>
            
            {/* Show uploaded files info */}
            {testInputs.uploadedFiles && testInputs.uploadedFiles.length > 0 && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f3f8ff', borderRadius: 1, border: '1px solid #bbdefb' }}>
                <Typography variant="body2" color="primary" gutterBottom>
                  📎 Testing with {testInputs.uploadedFiles.length} uploaded file(s):
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {testInputs.uploadedFiles.map(file => `${file.name} (${(file.size / 1024).toFixed(1)} KB)`).join(', ')}
                </Typography>
              </Box>
            )}
            
            {/* Progress Bar */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">
                  Step {testState.currentStepIndex + 1} of {testState.totalSteps}
                </Typography>
                <Typography variant="body2">
                  {Math.round(((testState.completedSteps.length) / testState.totalSteps) * 100)}% Complete
                </Typography>
              </Box>
              <Box sx={{ 
                height: 8, 
                backgroundColor: '#f0f0f0', 
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                <Box sx={{
                  height: '100%',
                  backgroundColor: testState.errors.length > 0 ? '#f44336' : '#4caf50',
                  width: `${(testState.completedSteps.length / testState.totalSteps) * 100}%`,
                  transition: 'width 0.3s ease'
                }} />
              </Box>
            </Box>

            {/* Step Results */}
            <Box display="flex" flexDirection="column" gap={2}>
              {executionOrder.map((step, index) => {
                const status = getStepStatus(step.id);
                const result = testState.stepResults[step.id];
                
                return (
                  <Card 
                    key={step.id} 
                    sx={{ 
                      border: status === 'running' ? '2px solid #2196f3' : '1px solid #e0e0e0',
                      backgroundColor: status === 'running' ? '#f3f8ff' : 'white'
                    }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" sx={{ 
                            backgroundColor: '#f5f5f5', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            fontSize: '0.75rem'
                          }}>
                            {index + 1}
                          </Typography>
                          <Typography variant="subtitle2">
                            {step.name}
                          </Typography>
                          <Chip 
                            label={step.type.replace('_', ' ')} 
                            size="small" 
                            variant="outlined"
                          />
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          {status === 'running' && <PauseIcon color="primary" sx={{ fontSize: 20 }} />}
                          {status === 'success' && <SuccessIcon color="success" sx={{ fontSize: 20 }} />}
                          {status === 'error' && <ErrorIcon color="error" sx={{ fontSize: 20 }} />}
                          {status === 'pending' && <WarningIcon color="disabled" sx={{ fontSize: 20 }} />}
                        </Box>
                      </Box>
                      
                      {result && (
                        <Box>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Execution Time: {(result.executionTime / 1000).toFixed(2)}s
                          </Typography>
                          
                          {result.success ? (
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2" sx={{ color: 'success.main' }}>
                                  ✅ Success - View Results
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box sx={{ 
                                  backgroundColor: '#f9f9f9', 
                                  p: 2, 
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0'
                                }}>
                                  <pre style={{ 
                                    margin: 0, 
                                    fontSize: '0.8rem', 
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {JSON.stringify(result.result, null, 2)}
                                  </pre>
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          ) : (
                            <Box sx={{ 
                              backgroundColor: '#fff5f5', 
                              p: 2, 
                              borderRadius: 1,
                              border: '1px solid #ffcdd2'
                            }}>
                              <Typography variant="body2" color="error">
                                ❌ Error: {result.error}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {status === 'running' && (
                        <Box sx={{ 
                          backgroundColor: '#e3f2fd', 
                          p: 2, 
                          borderRadius: 1,
                          border: '1px solid #bbdefb'
                        }}>
                          <Typography variant="body2" color="primary">
                            🔄 Executing step...
                          </Typography>
                        </Box>
                      )}
                      
                      {status === 'pending' && (
                        <Box sx={{ 
                          backgroundColor: '#f5f5f5', 
                          p: 2, 
                          borderRadius: 1,
                          border: '1px solid #e0e0e0'
                        }}>
                          <Typography variant="body2" color="textSecondary">
                            ⏳ Waiting to execute...
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose}>Close</Button>
        {testState.endTime && !testState.isRunning && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => {
              const testReport = {
                flowName: `Flow Test Report`,
                timestamp: new Date().toISOString(),
                duration: getTestDuration(),
                totalSteps: testState.totalSteps,
                completedSteps: testState.completedSteps.length,
                errors: testState.errors,
                uploadedFiles: testInputs.uploadedFiles ? testInputs.uploadedFiles.map(file => ({
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  lastModified: file.lastModified
                })) : [],
                customTestData: testInputs.customData,
                results: testState.stepResults
              };
              
              const blob = new Blob([JSON.stringify(testReport, null, 2)], { 
                type: 'application/json' 
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `flow-test-report-${Date.now()}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Download Report
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Wizard Form Field Configuration Dialog Component
const WizardFormFieldDialog = ({ open, field, context, onClose, onSave }) => {
  const [localField, setLocalField] = useState(field || {
    id: `field-${Date.now()}`,
    name: '',
    label: '',
    type: 'text',
    required: true,
    outputVariable: '',
    placeholder: '',
    helperText: '',
    options: '',
    autocompleteConfig: {
      dataSource: 'sql',
      searchMode: 'hybrid',
      minSearchLength: 2,
      debounceMs: 300,
      maxResults: 50
    }
  });

  useEffect(() => {
    if (field) {
      setLocalField(field);
    }
  }, [field]);

  const handleFieldChange = (fieldName, value) => {
    setLocalField(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSave = () => {
    onSave(localField);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {field ? 'Edit Form Field' : 'Add Form Field'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}>
            <TextField
              size="small"
              label="Field Name"
              value={localField.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              fullWidth
              placeholder="e.g., fullName"
              helperText="Internal field identifier"
            />
          </Grid>
          
          <Grid item xs={6}>
            <TextField
              size="small"
              label="Field Label"
              value={localField.label}
              onChange={(e) => handleFieldChange('label', e.target.value)}
              fullWidth
              placeholder="e.g., Full Name"
              helperText="Label shown to user"
            />
          </Grid>
          
          <Grid item xs={6}>
            <FormControl size="small" fullWidth>
              <InputLabel>Field Type</InputLabel>
              <Select
                value={localField.type}
                onChange={(e) => handleFieldChange('type', e.target.value)}
                label="Field Type"
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="password">Password</MenuItem>
                <MenuItem value="textarea">Text Area</MenuItem>
                <MenuItem value="select">Dropdown</MenuItem>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="time">Time</MenuItem>
                <MenuItem value="checkbox">Checkbox</MenuItem>
                <MenuItem value="autocomplete">Autocomplete (SQL/API Search)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6}>
            <TextField
              size="small"
              label="Output Variable"
              value={localField.outputVariable}
              onChange={(e) => handleFieldChange('outputVariable', e.target.value)}
              fullWidth
              placeholder="e.g., userName"
              helperText="Variable to store the value"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              size="small"
              label="Placeholder Text"
              value={localField.placeholder}
              onChange={(e) => handleFieldChange('placeholder', e.target.value)}
              fullWidth
              placeholder="e.g., Enter your full name"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              size="small"
              label="Helper Text"
              value={localField.helperText}
              onChange={(e) => handleFieldChange('helperText', e.target.value)}
              fullWidth
              placeholder="e.g., This will be shown on your certificate"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={localField.required}
                  onChange={(e) => handleFieldChange('required', e.target.checked)}
                />
              }
              label="Required Field"
            />
          </Grid>
          
          {/* Dropdown options */}
          {localField.type === 'select' && (
            <Grid item xs={12}>
              <TextField
                size="small"
                label="Options (comma-separated)"
                value={localField.options}
                onChange={(e) => handleFieldChange('options', e.target.value)}
                fullWidth
                placeholder="Option 1, Option 2, Option 3"
                helperText="Enter dropdown options separated by commas"
              />
            </Grid>
          )}
          
          {/* Autocomplete Configuration */}
          {localField.type === 'autocomplete' && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Autocomplete fields allow searching and selecting data from SQL databases or APIs. 
                  Configure the data source below.
                </Typography>
              </Alert>
              
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">🔗 Data Source</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <FormControl fullWidth>
                      <InputLabel>Data Source</InputLabel>
                      <Select
                        value={localField.autocompleteConfig?.dataSource || 'sql'}
                        onChange={(e) => {
                          handleFieldChange('autocompleteConfig', {
                            ...localField.autocompleteConfig,
                            dataSource: e.target.value
                          });
                        }}
                        label="Data Source"
                      >
                        <MenuItem value="sql">SQL Database</MenuItem>
                        <MenuItem value="api">API Endpoint</MenuItem>
                      </Select>
                      <FormHelperText>Choose where to fetch autocomplete data from</FormHelperText>
                    </FormControl>
                    
                    <FormControl fullWidth>
                      <InputLabel>Search Mode</InputLabel>
                      <Select
                        value={localField.autocompleteConfig?.searchMode || 'realtime'}
                        onChange={(e) => {
                          handleFieldChange('autocompleteConfig', {
                            ...localField.autocompleteConfig,
                            searchMode: e.target.value
                          });
                        }}
                        label="Search Mode"
                      >
                        <MenuItem value="realtime">Real-time (as you type)</MenuItem>
                        <MenuItem value="manual">Manual (click to search)</MenuItem>
                        <MenuItem value="hybrid">Hybrid (both)</MenuItem>
                        <MenuItem value="prepopulated">Prepopulated (load all, filter locally)</MenuItem>
                      </Select>
                      <FormHelperText>
                        {localField.autocompleteConfig?.searchMode === 'prepopulated' 
                          ? 'Loads all data on mount, filters client-side (best for <500 items)'
                          : 'When to trigger search'}
                      </FormHelperText>
                    </FormControl>
                    
                    {/* Depends On Field Selector */}
                    <FormControl fullWidth>
                      <InputLabel>Depends On (Optional)</InputLabel>
                      <Select
                        value={localField.autocompleteConfig?.dependsOn || ''}
                        onChange={(e) => {
                          handleFieldChange('autocompleteConfig', {
                            ...localField.autocompleteConfig,
                            dependsOn: e.target.value || undefined
                          });
                        }}
                        label="Depends On (Optional)"
                      >
                        <MenuItem value="">None</MenuItem>
                        {(context?.allFields || [])
                          .filter((f, i) => context?.fieldIndex === null || i < context.fieldIndex) // Only show fields before this one
                          .map((prevField) => (
                            <MenuItem key={prevField.id} value={prevField.id}>
                              {prevField.label || prevField.name} ({prevField.outputVariable || prevField.name})
                            </MenuItem>
                          ))
                        }
                      </Select>
                      <FormHelperText>
                        Select a previous field if this autocomplete depends on its value. The parent field's value will be available as a variable in your conditions.
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </AccordionDetails>
              </Accordion>
              
              {/* SQL Database Configuration */}
              {(!localField.autocompleteConfig?.dataSource || localField.autocompleteConfig?.dataSource === 'sql') && (
                <>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">🗄️ SQL Query Configuration</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <FormControl fullWidth>
                          <InputLabel>Query Type</InputLabel>
                          <Select
                            value={localField.autocompleteConfig?.sql?.queryType || 'visual'}
                            onChange={(e) => {
                              handleFieldChange('autocompleteConfig', {
                                ...localField.autocompleteConfig,
                                sql: {
                                  ...localField.autocompleteConfig?.sql,
                                  queryType: e.target.value
                                }
                              });
                            }}
                            label="Query Type"
                          >
                            <MenuItem value="visual">Visual Query Builder</MenuItem>
                            <MenuItem value="raw">Raw SQL Query</MenuItem>
                          </Select>
                        </FormControl>
                        
                        {(!localField.autocompleteConfig?.sql?.queryType || localField.autocompleteConfig?.sql?.queryType === 'visual') ? (
                          <>
                            <TextField
                              label="Table Name"
                              value={localField.autocompleteConfig?.sql?.table || ''}
                              onChange={(e) => {
                                handleFieldChange('autocompleteConfig', {
                                  ...localField.autocompleteConfig,
                                  sql: {
                                    ...localField.autocompleteConfig?.sql,
                                    table: e.target.value
                                  }
                                });
                              }}
                              fullWidth
                              required
                              placeholder="users, products, orders, etc."
                              helperText="Name of the database table"
                            />
                            
                            <TextField
                              label="Select Columns"
                              value={localField.autocompleteConfig?.sql?.columns || '*'}
                              onChange={(e) => {
                                handleFieldChange('autocompleteConfig', {
                                  ...localField.autocompleteConfig,
                                  sql: {
                                    ...localField.autocompleteConfig?.sql,
                                    columns: e.target.value
                                  }
                                });
                              }}
                              fullWidth
                              placeholder="*, id, name, email"
                              helperText="Comma-separated column names or * for all"
                            />
                            
                            {localField.autocompleteConfig?.searchMode !== 'prepopulated' && (
                              <TextField
                                label="Search Column"
                                value={localField.autocompleteConfig?.sql?.searchColumn || ''}
                                onChange={(e) => {
                                  handleFieldChange('autocompleteConfig', {
                                    ...localField.autocompleteConfig,
                                    sql: {
                                      ...localField.autocompleteConfig?.sql,
                                      searchColumn: e.target.value
                                    }
                                  });
                                }}
                                fullWidth
                                required
                                placeholder="name"
                                helperText="Column to search in (e.g., customer name)"
                              />
                            )}
                            
                            {localField.autocompleteConfig?.searchMode === 'prepopulated' && (
                              <Alert severity="info" sx={{ mb: 2 }}>
                                <strong>Prepopulated Mode:</strong> Search Column not needed. Data is loaded once using conditions below, then filtered client-side as user types.
                              </Alert>
                            )}
                            
                            <TextField
                              label="Order By Column (Optional)"
                              value={localField.autocompleteConfig?.sql?.orderBy || ''}
                              onChange={(e) => {
                                handleFieldChange('autocompleteConfig', {
                                  ...localField.autocompleteConfig,
                                  sql: {
                                    ...localField.autocompleteConfig?.sql,
                                    orderBy: e.target.value
                                  }
                                });
                              }}
                              fullWidth
                              placeholder="name, created_at, id"
                              helperText="Column to sort results by"
                            />
                            
                            <TextField
                              label="Limit"
                              type="number"
                              value={localField.autocompleteConfig?.sql?.limit || 50}
                              onChange={(e) => {
                                handleFieldChange('autocompleteConfig', {
                                  ...localField.autocompleteConfig,
                                  sql: {
                                    ...localField.autocompleteConfig?.sql,
                                    limit: parseInt(e.target.value) || 50
                                  }
                                });
                              }}
                              fullWidth
                              inputProps={{ min: 1, max: 200 }}
                              helperText="Maximum number of results"
                            />
                            
                            <Divider sx={{ my: 2 }}>
                              <Chip label="WHERE Conditions (Filter Results)" size="small" />
                            </Divider>
                            
                            {(!localField.autocompleteConfig?.sql?.conditions || localField.autocompleteConfig.sql.conditions.length === 0) && (
                              <Alert severity="info" sx={{ mb: 2 }}>
                                No conditions added. {localField.autocompleteConfig?.searchMode === 'prepopulated' ? 'All rows will be loaded.' : 'Results will be filtered only by search term.'} Click "Add Condition" to filter results by variables or static values.
                              </Alert>
                            )}
                            
                            {localField.autocompleteConfig?.sql?.conditions?.map((condition, conditionIndex) => (
                              <Paper key={conditionIndex} sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                  <Typography variant="body2" fontWeight="bold">
                                    Condition {conditionIndex + 1}
                                  </Typography>
                                  {conditionIndex > 0 && (
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                      <Select
                                        value={condition.logicalOperator || 'AND'}
                                        onChange={(e) => {
                                          const newConditions = [...(localField.autocompleteConfig?.sql?.conditions || [])];
                                          newConditions[conditionIndex] = { ...newConditions[conditionIndex], logicalOperator: e.target.value };
                                          handleFieldChange('autocompleteConfig', {
                                            ...localField.autocompleteConfig,
                                            sql: {
                                              ...localField.autocompleteConfig?.sql,
                                              conditions: newConditions
                                            }
                                          });
                                        }}
                                      >
                                        <MenuItem value="AND">AND</MenuItem>
                                        <MenuItem value="OR">OR</MenuItem>
                                      </Select>
                                    </FormControl>
                                  )}
                                  <Box flexGrow={1} />
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      const newConditions = (localField.autocompleteConfig?.sql?.conditions || []).filter((_, i) => i !== conditionIndex);
                                      handleFieldChange('autocompleteConfig', {
                                        ...localField.autocompleteConfig,
                                        sql: {
                                          ...localField.autocompleteConfig?.sql,
                                          conditions: newConditions
                                        }
                                      });
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                                
                                <Box display="flex" gap={1} flexWrap="wrap">
                                  <TextField
                                    label="Column"
                                    value={condition.column || ''}
                                    onChange={(e) => {
                                      const newConditions = [...(localField.autocompleteConfig?.sql?.conditions || [])];
                                      newConditions[conditionIndex] = { ...newConditions[conditionIndex], column: e.target.value };
                                      handleFieldChange('autocompleteConfig', {
                                        ...localField.autocompleteConfig,
                                        sql: {
                                          ...localField.autocompleteConfig?.sql,
                                          conditions: newConditions
                                        }
                                      });
                                    }}
                                    placeholder="column_name"
                                    size="small"
                                    sx={{ flexGrow: 1, minWidth: '150px' }}
                                  />
                                  
                                  <FormControl size="small" sx={{ minWidth: '150px' }}>
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                      value={condition.operator || 'equals'}
                                      onChange={(e) => {
                                        const newConditions = [...(localField.autocompleteConfig?.sql?.conditions || [])];
                                        newConditions[conditionIndex] = { ...newConditions[conditionIndex], operator: e.target.value };
                                        handleFieldChange('autocompleteConfig', {
                                          ...localField.autocompleteConfig,
                                          sql: {
                                            ...localField.autocompleteConfig?.sql,
                                            conditions: newConditions
                                          }
                                        });
                                      }}
                                      label="Operator"
                                    >
                                      <MenuItem value="equals">=  Equals</MenuItem>
                                      <MenuItem value="not_equals">≠  Not Equals</MenuItem>
                                      <MenuItem value="greater_than">&gt;  Greater Than</MenuItem>
                                      <MenuItem value="less_than">&lt;  Less Than</MenuItem>
                                      <MenuItem value="greater_or_equal">≥  Greater or Equal</MenuItem>
                                      <MenuItem value="less_or_equal">≤  Less or Equal</MenuItem>
                                      <MenuItem value="contains">Contains</MenuItem>
                                      <MenuItem value="not_contains">Not Contains</MenuItem>
                                      <MenuItem value="starts_with">Starts With</MenuItem>
                                      <MenuItem value="ends_with">Ends With</MenuItem>
                                      <MenuItem value="is_null">Is NULL</MenuItem>
                                      <MenuItem value="is_not_null">Is Not NULL</MenuItem>
                                      <MenuItem value="in">IN (multiple values)</MenuItem>
                                      <MenuItem value="not_in">NOT IN (multiple values)</MenuItem>
                                    </Select>
                                  </FormControl>
                                  
                                  {condition.operator !== 'is_null' && condition.operator !== 'is_not_null' && (
                                    <TextField
                                      label="Value"
                                      value={condition.value || ''}
                                      onChange={(e) => {
                                        const newConditions = [...(localField.autocompleteConfig?.sql?.conditions || [])];
                                        newConditions[conditionIndex] = { ...newConditions[conditionIndex], value: e.target.value };
                                        handleFieldChange('autocompleteConfig', {
                                          ...localField.autocompleteConfig,
                                          sql: {
                                            ...localField.autocompleteConfig?.sql,
                                            conditions: newConditions
                                          }
                                        });
                                      }}
                                      placeholder={
                                        condition.operator === 'in' || condition.operator === 'not_in'
                                          ? "value1, value2, value3 or {{variableName}}"
                                          : "value or {{variable}}"
                                      }
                                      size="small"
                                      sx={{ flexGrow: 1, minWidth: '150px' }}
                                      multiline={condition.operator === 'in' || condition.operator === 'not_in'}
                                      rows={condition.operator === 'in' || condition.operator === 'not_in' ? 2 : 1}
                                      helperText={
                                        condition.operator === 'in' || condition.operator === 'not_in'
                                          ? "Comma-separated values or {{variableName}}"
                                          : "Use {{variableName}} for flow variables"
                                      }
                                    />
                                  )}
                                </Box>
                              </Paper>
                            ))}
                            
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                const newConditions = [...(localField.autocompleteConfig?.sql?.conditions || [])];
                                newConditions.push({
                                  column: '',
                                  operator: 'equals',
                                  value: '',
                                  logicalOperator: newConditions.length > 0 ? 'AND' : undefined
                                });
                                handleFieldChange('autocompleteConfig', {
                                  ...localField.autocompleteConfig,
                                  sql: {
                                    ...localField.autocompleteConfig?.sql,
                                    conditions: newConditions
                                  }
                                });
                              }}
                              fullWidth
                            >
                              Add Condition
                            </Button>
                          </>
                        ) : (
                          <TextField
                            label="Raw SQL Query"
                            value={localField.autocompleteConfig?.sql?.rawQuery || ''}
                            onChange={(e) => {
                              handleFieldChange('autocompleteConfig', {
                                ...localField.autocompleteConfig,
                                sql: {
                                  ...localField.autocompleteConfig?.sql,
                                  rawQuery: e.target.value
                                }
                              });
                            }}
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="SELECT id, name FROM customers WHERE name LIKE CONCAT('%', ?, '%') LIMIT 50"
                            helperText="Use ? as placeholder for search term. The user's input will replace ?"
                          />
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}
              
              {/* API Endpoint Configuration */}
              {localField.autocompleteConfig?.dataSource === 'api' && (
                <>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">🌐 API Request</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <FormControl fullWidth>
                          <InputLabel>HTTP Method</InputLabel>
                          <Select
                            value={localField.autocompleteConfig?.api?.method || 'GET'}
                            onChange={(e) => {
                              handleFieldChange('autocompleteConfig', {
                                ...localField.autocompleteConfig,
                                api: {
                                  ...localField.autocompleteConfig?.api,
                                  method: e.target.value
                                }
                              });
                            }}
                            label="HTTP Method"
                          >
                            <MenuItem value="GET">GET</MenuItem>
                            <MenuItem value="POST">POST</MenuItem>
                            <MenuItem value="PUT">PUT</MenuItem>
                            <MenuItem value="PATCH">PATCH</MenuItem>
                            <MenuItem value="DELETE">DELETE</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <TextField
                          label="API URL"
                          value={localField.autocompleteConfig?.api?.url || ''}
                          onChange={(e) => {
                            handleFieldChange('autocompleteConfig', {
                              ...localField.autocompleteConfig,
                              api: {
                                ...localField.autocompleteConfig?.api,
                                url: e.target.value
                              }
                            });
                          }}
                          fullWidth
                          required
                          placeholder="https://api.example.com/search"
                          helperText="Use {{searchTerm}} for search input and {{variables}} for other data"
                        />
                        
                        <TextField
                          label="Search Parameter Name"
                          value={localField.autocompleteConfig?.api?.searchParameter || 'q'}
                          onChange={(e) => {
                            handleFieldChange('autocompleteConfig', {
                              ...localField.autocompleteConfig,
                              api: {
                                ...localField.autocompleteConfig?.api,
                                searchParameter: e.target.value
                              }
                            });
                          }}
                          fullWidth
                          placeholder="q, search, query, term"
                          helperText="Query parameter name for search term (GET) or body field name (POST)"
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">🔐 Authentication</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <FormControl fullWidth>
                          <InputLabel>Authentication Type</InputLabel>
                          <Select
                            value={localField.autocompleteConfig?.api?.authType || 'none'}
                            onChange={(e) => {
                              handleFieldChange('autocompleteConfig', {
                                ...localField.autocompleteConfig,
                                api: {
                                  ...localField.autocompleteConfig?.api,
                                  authType: e.target.value
                                }
                              });
                            }}
                            label="Authentication Type"
                          >
                            <MenuItem value="none">No Authentication</MenuItem>
                            <MenuItem value="bearer">Bearer Token</MenuItem>
                            <MenuItem value="apiKey">API Key</MenuItem>
                            <MenuItem value="basic">Basic Auth</MenuItem>
                          </Select>
                        </FormControl>
                        
                        {localField.autocompleteConfig?.api?.authType === 'bearer' && (
                          <TextField
                            label="Bearer Token"
                            value={localField.autocompleteConfig?.api?.bearerToken || ''}
                            onChange={(e) => {
                              handleFieldChange('autocompleteConfig', {
                                ...localField.autocompleteConfig,
                                api: {
                                  ...localField.autocompleteConfig?.api,
                                  bearerToken: e.target.value
                                }
                              });
                            }}
                            fullWidth
                            placeholder="token or {{tokenVariable}}"
                            helperText="Token for Authorization header"
                          />
                        )}
                        
                        {localField.autocompleteConfig?.api?.authType === 'apiKey' && (
                          <>
                            <TextField
                              label="API Key"
                              value={localField.autocompleteConfig?.api?.apiKey || ''}
                              onChange={(e) => {
                                handleFieldChange('autocompleteConfig', {
                                  ...localField.autocompleteConfig,
                                  api: {
                                    ...localField.autocompleteConfig?.api,
                                    apiKey: e.target.value
                                  }
                                });
                              }}
                              fullWidth
                              placeholder="key or {{keyVariable}}"
                            />
                            <TextField
                              label="API Key Header Name"
                              value={localField.autocompleteConfig?.api?.apiKeyHeader || 'X-API-Key'}
                              onChange={(e) => {
                                handleFieldChange('autocompleteConfig', {
                                  ...localField.autocompleteConfig,
                                  api: {
                                    ...localField.autocompleteConfig?.api,
                                    apiKeyHeader: e.target.value
                                  }
                                });
                              }}
                              fullWidth
                              placeholder="X-API-Key"
                            />
                          </>
                        )}
                        
                        {localField.autocompleteConfig?.api?.authType === 'basic' && (
                          <Box display="flex" gap={2}>
                            <TextField
                              label="Username"
                              value={localField.autocompleteConfig?.api?.username || ''}
                              onChange={(e) => {
                                handleFieldChange('autocompleteConfig', {
                                  ...localField.autocompleteConfig,
                                  api: {
                                    ...localField.autocompleteConfig?.api,
                                    username: e.target.value
                                  }
                                });
                              }}
                              fullWidth
                            />
                            <TextField
                              label="Password"
                              type="password"
                              value={localField.autocompleteConfig?.api?.password || ''}
                              onChange={(e) => {
                                handleFieldChange('autocompleteConfig', {
                                  ...localField.autocompleteConfig,
                                  api: {
                                    ...localField.autocompleteConfig?.api,
                                    password: e.target.value
                                  }
                                });
                              }}
                              fullWidth
                            />
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">❓ Query Parameters</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="body2" color="textSecondary">
                          Additional query parameters to include in the URL (e.g., ?limit=10&sort=asc)
                        </Typography>
                        <TextField
                          label="Query Parameters (JSON)"
                          value={localField.autocompleteConfig?.api?.queryParams || ''}
                          onChange={(e) => {
                            handleFieldChange('autocompleteConfig', {
                              ...localField.autocompleteConfig,
                              api: {
                                ...localField.autocompleteConfig?.api,
                                queryParams: e.target.value
                              }
                            });
                          }}
                          fullWidth
                          multiline
                          rows={3}
                          placeholder='{"limit": "10", "sort": "asc"}'
                          helperText="JSON object of key-value pairs. Supports {{variables}}"
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">📋 Headers</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="body2" color="textSecondary">
                          Custom headers to include in the request
                        </Typography>
                        <TextField
                          label="Custom Headers (JSON)"
                          value={localField.autocompleteConfig?.api?.headers || ''}
                          onChange={(e) => {
                            handleFieldChange('autocompleteConfig', {
                              ...localField.autocompleteConfig,
                              api: {
                                ...localField.autocompleteConfig?.api,
                                headers: e.target.value
                              }
                            });
                          }}
                          fullWidth
                          multiline
                          rows={3}
                          placeholder='{"Content-Type": "application/json", "Accept": "application/json"}'
                          helperText="JSON object of header key-value pairs. Supports {{variables}}"
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  
                  {['POST', 'PUT', 'PATCH'].includes(localField.autocompleteConfig?.api?.method) && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">📦 Request Body</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box display="flex" flexDirection="column" gap={2}>
                          <TextField
                            label="Request Body Template (JSON)"
                            value={localField.autocompleteConfig?.api?.bodyTemplate || ''}
                            onChange={(e) => {
                              handleFieldChange('autocompleteConfig', {
                                ...localField.autocompleteConfig,
                                api: {
                                  ...localField.autocompleteConfig?.api,
                                  bodyTemplate: e.target.value
                                }
                              });
                            }}
                            fullWidth
                            multiline
                            rows={4}
                            placeholder='{"query": "{{searchTerm}}", "filters": {"status": "active"}}'
                            helperText="Use {{searchTerm}} for user input and {{variables}} for other data"
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </>
              )}
              
              {/* Response Mapping (Common for both SQL and API) */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">🎯 Response Mapping</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="body2" color="textSecondary">
                      Map response data to dropdown display and stored value
                    </Typography>
                    
                    {localField.autocompleteConfig?.dataSource === 'api' && (
                      <TextField
                        label="Response Data Path"
                        value={localField.autocompleteConfig?.responseDataPath || ''}
                        onChange={(e) => {
                          handleFieldChange('autocompleteConfig', {
                            ...localField.autocompleteConfig,
                            responseDataPath: e.target.value
                          });
                        }}
                        fullWidth
                        placeholder="data.results or results"
                        helperText="Path to array in response (e.g., 'data.items' or leave empty if response is array)"
                      />
                    )}
                    
                    <TextField
                      label="Display Field"
                      value={localField.autocompleteConfig?.displayField || ''}
                      onChange={(e) => {
                        handleFieldChange('autocompleteConfig', {
                          ...localField.autocompleteConfig,
                          displayField: e.target.value
                        });
                      }}
                      fullWidth
                      required
                      placeholder="name"
                      helperText="Field to show in dropdown (e.g., '{{name}}' or '{{firstname}} {{lastname}}')"
                    />
                    
                    <TextField
                      label="Value Field"
                      value={localField.autocompleteConfig?.valueField || ''}
                      onChange={(e) => {
                        handleFieldChange('autocompleteConfig', {
                          ...localField.autocompleteConfig,
                          valueField: e.target.value
                        });
                      }}
                      fullWidth
                      required
                      placeholder="id"
                      helperText="Field to store as value (e.g., '{{id}}' or '{{customerId}}')"
                    />
                    
                    <Divider sx={{ my: 2 }} />
                    
                    {/* Display Field Variables */}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        📋 Display Field Variables (Optional)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        Extract additional variables from the selected row. These can be used in dependent fields or SQL conditions.
                      </Typography>
                      
                      {(() => {
                        const displayFieldVars = localField.autocompleteConfig?.displayFieldVariables || {};
                        const varEntries = Object.entries(displayFieldVars);
                        
                        return (
                          <>
                            {varEntries.map(([varName, template], varIndex) => (
                              <Box key={varIndex} display="flex" gap={1} mb={1} alignItems="flex-start">
                                <TextField
                                  label="Variable Name"
                                  value={varName}
                                  onChange={(e) => {
                                    const newVars = { ...displayFieldVars };
                                    const oldName = varName;
                                    const newName = e.target.value;
                                    
                                    // Rename the key
                                    if (newName && newName !== oldName) {
                                      delete newVars[oldName];
                                      newVars[newName] = template;
                                    }
                                    
                                    handleFieldChange('autocompleteConfig', {
                                      ...localField.autocompleteConfig,
                                      displayFieldVariables: newVars
                                    });
                                  }}
                                  size="small"
                                  placeholder="entityId"
                                  sx={{ flex: 1 }}
                                />
                                <TextField
                                  label="Template"
                                  value={template}
                                  onChange={(e) => {
                                    const newVars = { ...displayFieldVars };
                                    newVars[varName] = e.target.value;
                                    
                                    handleFieldChange('autocompleteConfig', {
                                      ...localField.autocompleteConfig,
                                      displayFieldVariables: newVars
                                    });
                                  }}
                                  size="small"
                                  placeholder="{{EntityID}}"
                                  sx={{ flex: 1 }}
                                />
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    const newVars = { ...displayFieldVars };
                                    delete newVars[varName];
                                    
                                    handleFieldChange('autocompleteConfig', {
                                      ...localField.autocompleteConfig,
                                      displayFieldVariables: newVars
                                    });
                                  }}
                                  sx={{ mt: 0.5 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                            
                            <Button
                              startIcon={<AddIcon />}
                              onClick={() => {
                                const newVars = { ...displayFieldVars };
                                
                                // Generate unique variable name
                                let counter = 1;
                                let newVarName = 'variable1';
                                while (newVars[newVarName]) {
                                  counter++;
                                  newVarName = `variable${counter}`;
                                }
                                
                                newVars[newVarName] = '';
                                
                                handleFieldChange('autocompleteConfig', {
                                  ...localField.autocompleteConfig,
                                  displayFieldVariables: newVars
                                });
                              }}
                              size="small"
                              variant="outlined"
                            >
                              Add Variable
                            </Button>
                            
                            {varEntries.length > 0 && (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                  <strong>Example:</strong> If you set <code>entityId: {'{'}{'{'} EntityID {'}'}{'}'}</code>, 
                                  when a user selects an option, the <code>entityId</code> variable will be extracted 
                                  and available for use in dependent fields' SQL conditions.
                                </Typography>
                              </Alert>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
              
              {/* Performance & Behavior */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">⚙️ Performance & Behavior</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <TextField
                      label="Min Search Length"
                      type="number"
                      value={localField.autocompleteConfig?.minSearchLength || 2}
                      onChange={(e) => {
                        handleFieldChange('autocompleteConfig', {
                          ...localField.autocompleteConfig,
                          minSearchLength: parseInt(e.target.value) || 2
                        });
                      }}
                      fullWidth
                      inputProps={{ min: 0, max: 10 }}
                      helperText="Minimum characters before search is triggered"
                    />
                    
                    <TextField
                      label="Debounce Delay (ms)"
                      type="number"
                      value={localField.autocompleteConfig?.debounceMs || 300}
                      onChange={(e) => {
                        handleFieldChange('autocompleteConfig', {
                          ...localField.autocompleteConfig,
                          debounceMs: parseInt(e.target.value) || 300
                        });
                      }}
                      fullWidth
                      inputProps={{ min: 0, max: 2000 }}
                      helperText="Delay after typing before search (prevents excessive requests)"
                    />
                    
                    <TextField
                      label="Cache Duration (seconds)"
                      type="number"
                      value={localField.autocompleteConfig?.cacheDuration || 0}
                      onChange={(e) => {
                        handleFieldChange('autocompleteConfig', {
                          ...localField.autocompleteConfig,
                          cacheDuration: parseInt(e.target.value) || 0
                        });
                      }}
                      fullWidth
                      inputProps={{ min: 0, max: 3600 }}
                      helperText="Cache search results for this duration (0 = no cache)"
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={localField.autocompleteConfig?.removeDuplicates || false}
                          onChange={(e) => {
                            handleFieldChange('autocompleteConfig', {
                              ...localField.autocompleteConfig,
                              removeDuplicates: e.target.checked
                            });
                          }}
                        />
                      }
                      label="Remove Duplicate Values"
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ mt: -1, display: 'block' }}>
                      When enabled, duplicate display values will be filtered out from the dropdown. Useful when SQL queries return the same display value multiple times.
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Field
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Move nodeTypes outside component to prevent recreation
// Use Object.freeze to ensure immutability and prevent React warnings
const nodeTypes = Object.freeze({
  smart_start: SmartStartNode,
  end: EndNode,
  step: StepNode
});

// Main Visual Flow Editor Component
const VisualFlowEditorInner = ({ 
  flowData, 
  onFlowDataChange, 
  availableVariables = [],
  availableApiEndpoints = [],
  availableIntents = [],
  projectId,
  companyId
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [availableLibraries, setAvailableLibraries] = useState([]);
  const [librarySources, setLibrarySources] = useState({});
  
  // Wizard form field dialog state
  const [wizardFieldDialogOpen, setWizardFieldDialogOpen] = useState(false);
  const [wizardFieldDialogData, setWizardFieldDialogData] = useState(null);
  const [wizardFieldDialogContext, setWizardFieldDialogContext] = useState(null); // { stepIndex, fieldIndex }
  
  // Add drag state tracking to prevent feedback loops
  const [isDragging, setIsDragging] = useState(false);
  const [dragTimeoutId, setDragTimeoutId] = useState(null);
  const [pendingPositionUpdates, setPendingPositionUpdates] = useState({});
  
  // Mobile responsive state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const [configPanelOpen, setConfigPanelOpen] = useState(true);
  
  // AI Flow Builder Modal state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  
  // On mobile, don't auto-open config panel to allow node manipulation
  // Users can open it via the FAB button when needed
  useEffect(() => {
    if (isMobile && selectedStep) {
      // Don't auto-open, just show the FAB button
      console.log('📱 [MOBILE DEBUG] Selected step changed, closing config panel. FAB should now be visible.');
      setConfigPanelOpen(false);
    }
  }, [isMobile, selectedStep]);
  
  // ReactFlow instance for viewport management
  const reactFlowInstance = useReactFlow();
  
  // Ref to access the ReactFlow wrapper for getting dimensions
  const reactFlowWrapperRef = useCallback((node) => {
    if (node) {
      console.log('📐 [FLOW DEBUG] ReactFlow wrapper dimensions:', {
        width: node.offsetWidth,
        height: node.offsetHeight,
        clientWidth: node.clientWidth,
        clientHeight: node.clientHeight
      });
    }
  }, []);
  
  // Helper function to find next available position near viewport center
  const findAvailablePosition = useCallback(() => {
    try {
      // Get current viewport information - this method should be available in all ReactFlow versions
      const viewport = reactFlowInstance?.getViewport();
      
      console.log('🎯 [FLOW DEBUG] Starting viewport center calculation:', {
        viewport,
        hasReactFlowInstance: !!reactFlowInstance,
        reactFlowMethods: reactFlowInstance ? Object.keys(reactFlowInstance) : []
      });
      
      if (!viewport || !reactFlowInstance) {
        console.warn('⚠️ [FLOW DEBUG] Viewport or ReactFlow instance not available, using fallback:', {
          hasViewport: !!viewport,
          hasInstance: !!reactFlowInstance
        });
        return { x: 400, y: 300 };
      }

      // Try to get actual viewport size from various sources
      let viewportWidth = 800;  // Default fallback
      let viewportHeight = 600; // Default fallback
      
      // Try to get actual dimensions from the ReactFlow wrapper
      const reactFlowWrapper = document.querySelector('.react-flow');
      if (reactFlowWrapper) {
        viewportWidth = reactFlowWrapper.clientWidth || reactFlowWrapper.offsetWidth || 800;
        viewportHeight = reactFlowWrapper.clientHeight || reactFlowWrapper.offsetHeight || 600;
        console.log('📐 [FLOW DEBUG] Found ReactFlow wrapper dimensions:', { viewportWidth, viewportHeight });
      } else {
        // Fallback to window dimensions with reasonable constraints
        viewportWidth = Math.min(window.innerWidth * 0.8, 1200); // 80% of window, max 1200px
        viewportHeight = Math.min(window.innerHeight * 0.8, 800); // 80% of window, max 800px
        console.log('📐 [FLOW DEBUG] Using window-based dimensions:', { viewportWidth, viewportHeight });
      }

      // Calculate the center of the visible area in flow coordinates
      // This accounts for the current zoom and pan position
      const viewportCenterFlow = {
        x: (viewportWidth / 2 - viewport.x) / viewport.zoom,
        y: (viewportHeight / 2 - viewport.y) / viewport.zoom
      };

      // Validate the calculated position (make sure it's reasonable)
      if (isNaN(viewportCenterFlow.x) || isNaN(viewportCenterFlow.y)) {
        console.error('❌ [FLOW DEBUG] Invalid viewport center calculation, using simple fallback');
        return { x: 400, y: 300 };
      }

      console.log('🎯 [FLOW DEBUG] Calculated viewport center:', {
        viewport,
        actualViewportSize: { width: viewportWidth, height: viewportHeight },
        viewportCenterFlow,
        calculation: {
          centerX: `(${viewportWidth}/2 - ${viewport.x}) / ${viewport.zoom} = ${viewportCenterFlow.x}`,
          centerY: `(${viewportHeight}/2 - ${viewport.y}) / ${viewport.zoom} = ${viewportCenterFlow.y}`
        }
      });

      // Define spacing for collision detection
      const spacing = 150; // Minimum spacing between nodes

      // Function to check if a position overlaps with existing nodes
      const isPositionOccupied = (x, y) => {
        return flowData.steps?.some(step => {
          const distance = Math.sqrt(
            Math.pow(step.position.x - x, 2) + 
            Math.pow(step.position.y - y, 2)
          );
          return distance < spacing;
        }) || false;
      };

      // Start with the viewport center
      let position = { ...viewportCenterFlow };
      
      // If the center position is occupied, spiral outward to find an available spot
      if (isPositionOccupied(position.x, position.y)) {
        console.log('🔍 [FLOW DEBUG] Center position occupied, searching for alternatives...');
        let found = false;
        let radius = spacing;
        let maxRadius = 600; // Maximum search radius
        
        while (!found && radius <= maxRadius) {
          // Try 8 positions around the circle at current radius
          for (let angle = 0; angle < 360; angle += 45) {
            const radian = (angle * Math.PI) / 180;
            const testX = viewportCenterFlow.x + Math.cos(radian) * radius;
            const testY = viewportCenterFlow.y + Math.sin(radian) * radius;
            
            if (!isPositionOccupied(testX, testY)) {
              position = { x: testX, y: testY };
              found = true;
              console.log(`🎯 [FLOW DEBUG] Found available position at radius ${radius}, angle ${angle}:`, position);
              break;
            }
          }
          radius += spacing * 0.5; // Increase radius for next iteration
        }
        
        if (!found) {
          console.warn('⚠️ [FLOW DEBUG] Could not find available position near viewport center, using fallback');
          // Fallback: place to the right of the viewport center
          position = { 
            x: viewportCenterFlow.x + spacing, 
            y: viewportCenterFlow.y
          };
        }
      } else {
        console.log('✅ [FLOW DEBUG] Center position is available!');
      }

      console.log('✅ [FLOW DEBUG] Final position selected:', position);
      return position;
      
    } catch (error) {
      console.error('❌ [FLOW DEBUG] Error finding available position:', error);
      // Fallback to original logic
      return { x: 400, y: 300 };
    }
  }, [reactFlowInstance, flowData.steps]);


  // Debounced position update function
  const flushPositionUpdates = useCallback(() => {
    console.log('🔄 [FLOW DEBUG] Flushing pending position updates:', pendingPositionUpdates);
    
    if (Object.keys(pendingPositionUpdates).length === 0) {
      console.log('⚠️ [FLOW DEBUG] No pending position updates to flush');
      return;
    }

    const updatedFlowData = { ...flowData };
    let hasChanges = false;

    Object.entries(pendingPositionUpdates).forEach(([stepId, position]) => {
      const stepIndex = updatedFlowData.steps.findIndex(step => step.id === stepId);
      if (stepIndex !== -1) {
        console.log(`📍 [FLOW DEBUG] Flushing position for step ${stepId}:`, {
          oldPosition: updatedFlowData.steps[stepIndex].position,
          newPosition: position
        });
        updatedFlowData.steps[stepIndex].position = position;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      console.log('✅ [FLOW DEBUG] Applying batched position updates to flowData');
      onFlowDataChange(updatedFlowData);
    }

    setPendingPositionUpdates({});
  }, [flowData, onFlowDataChange, pendingPositionUpdates]);

  // Initialize flow with Smart Start and End nodes if empty
  // Fetch available matching libraries when component mounts
  useEffect(() => {
    const fetchLibraries = async () => {
      if (!companyId || !projectId) return;
      
      try {
        console.log('📚 Fetching matching libraries for project:', projectId);
        const response = await api.get(`/companies/${companyId}/projects/${projectId}/matching-libraries`);
        setAvailableLibraries(response.data.libraries || []);
        console.log('✅ Loaded', response.data.libraries?.length || 0, 'matching libraries');
      } catch (error) {
        console.error('❌ Error fetching matching libraries:', error);
        setAvailableLibraries([]);
      }
    };
    
    fetchLibraries();
  }, [companyId, projectId]);

  useEffect(() => {
    // Only initialize if flowData exists and has no steps or empty steps array
    // Also check if flowData.steps is specifically an empty array (not undefined/null)
    if (flowData && Array.isArray(flowData.steps) && flowData.steps.length === 0) {
      console.log('🚀 [FLOW DEBUG] Initializing empty flow with start and end nodes');
      const initialSteps = [
        {
          id: 'smart-start-node',
          type: 'smart_start',
          name: 'Smart Start',
          config: {},
          position: { x: 250, y: 50 }
        },
        {
          id: 'end-node', 
          type: 'end',
          name: 'End',
          config: {},
          position: { x: 250, y: 500 }
        }
      ];
      
      onFlowDataChange({
        ...flowData,
        steps: initialSteps
      });
    }
  }, [flowData]);

  // Listen for flowStepModified events from AI Flow Builder
  // When a step is modified by the AI, reload the flow data from the server
  useEffect(() => {
    const handleFlowStepModified = async (event) => {
      console.log('🔧 [VisualFlowEditor] flowStepModified event received:', event.detail);
      
      if (!projectId) {
        console.warn('⚠️ [VisualFlowEditor] No projectId, cannot reload flow');
        return;
      }
      
      try {
        // Reload flow data from the server to get the latest step config
        console.log('🔄 [VisualFlowEditor] Reloading flow data from server...');
        const { default: flowStepsApi } = await import('../api/flowStepsApi');
        const responseData = await flowStepsApi.getFlowData(projectId);
        
        if (responseData && responseData.steps) {
          console.log('✅ [VisualFlowEditor] Flow data reloaded, steps:', responseData.steps.length);
          
          // Update the flow data with the new steps from server
          // Preserve existing connections and other data
          onFlowDataChange({
            ...flowData,
            steps: responseData.steps,
            connections: responseData.connections || flowData?.connections || []
          });
          
          // If the modified step is currently selected, update the selected step too
          const { stepId, stepName } = event.detail;
          if (selectedStep && (selectedStep.stepId === stepId || selectedStep.id === stepId)) {
            const updatedStep = responseData.steps.find(
              s => s.stepId === stepId || s.id === stepId || s._id === stepId
            );
            if (updatedStep) {
              console.log('🔄 [VisualFlowEditor] Updating selected step:', stepName);
              setSelectedStep(updatedStep);
            }
          }
        }
      } catch (error) {
        console.error('❌ [VisualFlowEditor] Error reloading flow data:', error);
      }
    };
    
    window.addEventListener('flowStepModified', handleFlowStepModified);
    
    return () => {
      window.removeEventListener('flowStepModified', handleFlowStepModified);
    };
  }, [projectId, flowData, onFlowDataChange, selectedStep]);

  // Sync flowData to ReactFlow nodes and edges
  useEffect(() => {
    console.log('🔄 [VisualFlowEditor] useEffect triggered - syncing flowData to nodes/edges:', {
      hasFlowData: !!flowData,
      hasSteps: !!flowData?.steps,
      stepsCount: flowData?.steps?.length,
      hasConnections: !!flowData?.connections,
      connectionsCount: flowData?.connections?.length
    });
    
    if (!flowData || !flowData.steps) {
      console.log('⚠️ [VisualFlowEditor] No flowData or steps, skipping sync');
      return;
    }

    // CRITICAL FIX: Handle step ID changes after save operations
    // When the server returns new step IDs, update selectedNodeId and selectedStep
    if (selectedStep && selectedNodeId) {
      const stepStillExists = flowData.steps.some(step => step.id === selectedNodeId);
      if (!stepStillExists) {
        // Find the step with the same name (step IDs changed but names stay the same)
        const newSelectedStep = flowData.steps.find(step => step.name === selectedStep.name);
        if (newSelectedStep) {
          console.log('🔄 [STEP ID FIX] Updating selected step after ID change:', {
            oldId: selectedNodeId,
            newId: newSelectedStep.id,
            stepName: newSelectedStep.name
          });
          setSelectedNodeId(newSelectedStep.id);
          setSelectedStep(newSelectedStep);
        } else {
          // Step was deleted, clear selection
          setSelectedNodeId(null);
          setSelectedStep(null);
        }
      } else {
        // Step still exists with same ID, but update selectedStep with latest data
        const updatedSelectedStep = flowData.steps.find(step => step.id === selectedNodeId);
        if (updatedSelectedStep && JSON.stringify(updatedSelectedStep.config) !== JSON.stringify(selectedStep.config)) {
          console.log('🔄 [CONFIG UPDATE] Updating selected step config from server response');
          setSelectedStep(updatedSelectedStep);
        }
      }
    }

    // Determine which steps are inside loop boundaries for visual highlighting
    const stepsInLoops = new Set();
    if (selectedStep && selectedStep.type === 'for_each_loop') {
      const loopStartId = selectedStep.config?.forEachLoopConfig?.loopStartStepId || selectedStep.forEachLoopConfig?.loopStartStepId;
      const loopEndId = selectedStep.config?.forEachLoopConfig?.loopEndStepId || selectedStep.forEachLoopConfig?.loopEndStepId;
      
      if (loopStartId && loopEndId) {
        // Build step map for traversal
        const stepMap = new Map();
        flowData.steps.forEach(s => stepMap.set(s.id, s));
        
        // Traverse from loopStartId to loopEndId to find all steps in loop
        const visited = new Set();
        const queue = [loopStartId];
        
        while (queue.length > 0) {
          const currentId = queue.shift();
          if (visited.has(currentId)) continue;
          visited.add(currentId);
          
          stepsInLoops.add(currentId);
          
          // Stop if we reached the end
          if (currentId === loopEndId) break;
          
          // Find connections from this step
          const connections = flowData.connections?.filter(c => c.source === currentId) || [];
          connections.forEach(conn => {
            if (!visited.has(conn.target)) {
              queue.push(conn.target);
            }
          });
        }
      }
    }

    const flowNodes = flowData.steps.map((step) => ({
      id: step.id,
      type: step.type === 'smart_start' ? 'smart_start' : step.type === 'end' ? 'end' : 'step',
      position: step.position || { x: 100, y: 100 },
      selected: selectedNodeId === step.id,
      data: { 
        step,
        type: step.type,
        onSelect: handleNodeSelect,
        onDelete: handleNodeDelete,
        onCopy: handleNodeCopy,
        isInLoopBoundary: stepsInLoops.has(step.id)
      }
    }));

    const flowEdges = flowData.connections?.map((connection, index) => ({
      id: `edge-${index}`,
      source: connection.source,
      target: connection.target,
      sourceHandle: connection.sourceHandle || null,
      targetHandle: connection.targetHandle || null,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed },
      deletable: true,
      style: { 
        strokeWidth: 2,
        stroke: '#64748b'
      }
    })) || [];

    console.log('✅ [VisualFlowEditor] Setting nodes and edges:', {
      nodesCount: flowNodes.length,
      edgesCount: flowEdges.length,
      nodeIds: flowNodes.map(n => n.id),
      edgeIds: flowEdges.map(e => e.id)
    });
    
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowData, selectedNodeId]);

  // Handle node selection
  const handleNodeSelect = useCallback((nodeId, step) => {
    console.log('📱 [MOBILE DEBUG] Node selected:', { nodeId, stepName: step?.name, isMobile });
    setSelectedStep(step);
    setSelectedNodeId(nodeId);
  }, [isMobile]);

  // Handle node deletion
  const handleNodeDelete = useCallback((nodeId) => {
    // Prevent deletion of smart start and end nodes
    if (nodeId === 'smart-start-node' || nodeId === 'end-node') {
      return;
    }
    
    const updatedSteps = flowData.steps.filter(step => step.id !== nodeId);
    const updatedConnections = flowData.connections?.filter(
      conn => conn.source !== nodeId && conn.target !== nodeId
    ) || [];
    
    onFlowDataChange({
      ...flowData,
      steps: updatedSteps,
      connections: updatedConnections
    });
    
    // Clear selection if deleted node was selected
    if (selectedNodeId === nodeId) {
      setSelectedStep(null);
      setSelectedNodeId(null);
    }
  }, [flowData, onFlowDataChange, selectedNodeId]);

  // Wizard form field dialog handlers
  const openWizardFieldDialog = useCallback((field, stepIndex, fieldIndex) => {
    // Get all fields in the wizard step for dependency options
    const allFields = selectedStep?.config?.wizardSteps?.[stepIndex]?.stepConfig?.formFields || [];
    setWizardFieldDialogData(field);
    setWizardFieldDialogContext({ stepIndex, fieldIndex, allFields });
    setWizardFieldDialogOpen(true);
  }, [selectedStep]);

  const closeWizardFieldDialog = useCallback(() => {
    setWizardFieldDialogOpen(false);
    setWizardFieldDialogData(null);
    setWizardFieldDialogContext(null);
  }, []);

  const saveWizardField = useCallback((fieldData) => {
    if (!selectedStep || !wizardFieldDialogContext) return;
    
    const { stepIndex, fieldIndex } = wizardFieldDialogContext;
    const wizardSteps = [...(selectedStep.config?.wizardSteps || [])];
    const currentStep = wizardSteps[stepIndex];
    
    if (!currentStep) return;
    
    const formFields = [...(currentStep.stepConfig?.formFields || [])];
    
    if (fieldIndex === null || fieldIndex === undefined) {
      // Adding new field
      formFields.push({ ...fieldData, id: fieldData.id || `field-${Date.now()}` });
    } else {
      // Editing existing field
      formFields[fieldIndex] = fieldData;
    }
    
    wizardSteps[stepIndex] = {
      ...currentStep,
      stepConfig: { ...currentStep.stepConfig, formFields }
    };
    
    // Update the selected step
    const updatedStep = {
      ...selectedStep,
      config: {
        ...selectedStep.config,
        wizardSteps
      }
    };
    
    // Update in flowData
    const updatedSteps = flowData.steps.map(step =>
      step.id === selectedStep.id ? updatedStep : step
    );
    
    onFlowDataChange({
      ...flowData,
      steps: updatedSteps
    });
    
    setSelectedStep(updatedStep);
  }, [selectedStep, wizardFieldDialogContext, flowData, onFlowDataChange]);

  // Handle node copying
  const handleNodeCopy = useCallback((nodeId, step) => {
    // Don't copy smart start and end nodes
    if (step.type === 'smart_start' || step.type === 'end') {
      return;
    }
    
    // Find a good position near the viewport center for the copied node
    const availablePosition = findAvailablePosition();
    
    const newStepId = `step-${Date.now()}`;
    const newStep = {
      ...step,
      id: newStepId,
      stepId: `step_${crypto.randomUUID()}`,
      name: `${step.name} (Copy)`,
      position: availablePosition
    };

    console.log('📋 [FLOW DEBUG] Copying step with intelligent positioning:', {
      originalStep: step.name,
      originalStepId: step.stepId,
      newStepId: newStep.stepId,
      originalPosition: step.position,
      newPosition: availablePosition,
      positionSource: 'viewport-center-calculation'
    });

    const updatedSteps = [...flowData.steps, newStep];
    onFlowDataChange({
      ...flowData,
      steps: updatedSteps
    });
    
    // Auto-select the copied step for immediate configuration
    setSelectedStep(newStep);
    setSelectedNodeId(newStep.id);
    console.log('🎯 [FLOW DEBUG] Auto-selected copied step for configuration:', newStep.id);
  }, [flowData, onFlowDataChange, findAvailablePosition]);

  // Handle new connections
  const onConnect = useCallback((params) => {
    console.log('🔗 [FLOW DEBUG] New connection attempt:', params);
    
    // Check for existing connections and enforce single connection rules
    let updatedConnections = [...(flowData.connections || [])];
    
    // Enable multiple input connections for all nodes
    // Previous logic only allowed multiple inputs for End Node, now all nodes can accept multiple inputs
    // This allows for more flexible flow design where multiple paths can lead to the same node
    console.log('🔗 [FLOW DEBUG] Multiple input connections enabled for all nodes');
    
    // Note: We no longer filter out existing connections to the same target
    // This allows multiple paths to converge at the same node
    
    // For output handles (sources), remove any existing connection from the same source handle
    // This is important for non-branch logic nodes that should only have one output
    if (params.source) {
      const sourceStep = flowData.steps?.find(step => step.id === params.source);
      const isBranchLogic = sourceStep?.type === 'branch_logic';
      
      // Only enforce single output for non-branch logic nodes
      if (!isBranchLogic) {
        const sourceKey = params.sourceHandle ? `${params.source}.${params.sourceHandle}` : params.source;
        updatedConnections = updatedConnections.filter(conn => {
          const existingSourceKey = conn.sourceHandle ? `${conn.source}.${conn.sourceHandle}` : conn.source;
          return existingSourceKey !== sourceKey;
        });
        console.log('🔗 [FLOW DEBUG] Removed existing connections from non-branch source:', sourceKey);
      }
    }
    
    // Add the new connection
    const newConnection = {
      source: params.source,
      target: params.target,
      sourceHandle: params.sourceHandle || null,
      targetHandle: params.targetHandle || null
    };
    
    updatedConnections.push(newConnection);
    console.log('🔗 [FLOW DEBUG] Adding new connection:', newConnection);
    
    // Update flowData with the new connections
    onFlowDataChange({
      ...flowData,
      connections: updatedConnections
    });
    
    // Also update the edges state for immediate visual feedback
    const newEdge = {
      ...params,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed },
      deletable: true,
      focusable: true,
      selectable: true
    };
    
    setEdges((eds) => {
      // Allow multiple input connections for all nodes
      let filteredEdges = eds;
      
      // Note: We no longer filter out edges to the same target
      // This allows multiple input connections to all nodes, not just the End Node
      
      // Remove edges from the same source (for non-branch logic)
      if (params.source) {
        const sourceStep = flowData.steps?.find(step => step.id === params.source);
        const isBranchLogic = sourceStep?.type === 'branch_logic';
        
        if (!isBranchLogic) {
          const sourceKey = params.sourceHandle ? `${params.source}.${params.sourceHandle}` : params.source;
          filteredEdges = filteredEdges.filter(edge => {
            const edgeSourceKey = edge.sourceHandle ? `${edge.source}.${edge.sourceHandle}` : edge.source;
            return edgeSourceKey !== sourceKey;
          });
        }
      }
      
      return addEdge(newEdge, filteredEdges);
    });
    
    console.log('✅ [FLOW DEBUG] Connection established with single connection enforcement');
  }, [flowData, onFlowDataChange]);

  // Enhanced handleNodesChange with drag-aware position updates
  const handleNodesChange = useCallback((changes) => {
    console.log('🔍 [FLOW DEBUG] handleNodesChange called:', {
      changesCount: changes.length,
      changeTypes: changes.map(change => change.type),
      changeIds: changes.map(change => change.id),
      timestamp: new Date().toISOString()
    });

    // Process position changes differently during dragging
    const positionChanges = changes.filter(change => change.type === 'position');
    
    if (positionChanges.length > 0) {
      console.log('📍 [FLOW DEBUG] Position changes detected:', {
        count: positionChanges.length,
        positions: positionChanges.map(change => ({
          id: change.id,
          x: change.position?.x,
          y: change.position?.y
        }))
      });

      if (isDragging) {
        // During dragging, accumulate position changes without triggering flowData sync
        console.log('🚀 [FLOW DEBUG] Accumulating position changes during drag');
        const newPendingUpdates = { ...pendingPositionUpdates };
        
        positionChanges.forEach(change => {
          if (change.position) {
            newPendingUpdates[change.id] = change.position;
          }
        });
        
        setPendingPositionUpdates(newPendingUpdates);
      } else {
        // When not dragging, update flowData immediately (existing logic)
        console.log('🔄 [FLOW DEBUG] Updating flowData with position changes...');
        const updatedFlowData = { ...flowData };
        
        positionChanges.forEach(change => {
          if (change.position) {
            const stepIndex = updatedFlowData.steps.findIndex(step => step.id === change.id);
            if (stepIndex !== -1) {
              console.log(`📍 [FLOW DEBUG] Updating position for step ${change.id}:`, {
                oldPosition: updatedFlowData.steps[stepIndex].position,
                newPosition: change.position
              });
              updatedFlowData.steps[stepIndex].position = change.position;
            }
          }
        });
        
        onFlowDataChange(updatedFlowData);
        console.log('✅ [FLOW DEBUG] flowData position update completed');
      }
    }

    onNodesChange(changes);
    console.log('✅ [FLOW DEBUG] onNodesChange completed successfully');
  }, [onNodesChange, flowData, onFlowDataChange, isDragging, pendingPositionUpdates]);

  // Enhanced drag handlers with state management
  const onNodeDragStart = useCallback((event, node) => {
    console.log('🚀 [FLOW DEBUG] Node drag started:', node.id);
    setIsDragging(true);
    
    // Clear any pending flush timeout
    if (dragTimeoutId) {
      clearTimeout(dragTimeoutId);
      setDragTimeoutId(null);
    }
  }, [dragTimeoutId]);

  const onNodeDrag = useCallback((event, node) => {
    console.log('👆 [FLOW DEBUG] Node dragging:', node.id, node.position);
    // Position updates are handled in handleNodesChange during dragging
  }, []);

  const onNodeDragStop = useCallback((event, node) => {
    console.log('🛑 [FLOW DEBUG] Node drag stopped:', node.id);
    
    // Set a timeout to flush position updates and end drag state
    const timeoutId = setTimeout(() => {
      console.log('⏰ [FLOW DEBUG] Drag timeout - flushing updates and ending drag state');
      setIsDragging(false);
      flushPositionUpdates();
      setDragTimeoutId(null);
    }, 100); // Small delay to ensure all position changes are captured
    
    setDragTimeoutId(timeoutId);
  }, [flushPositionUpdates]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dragTimeoutId) {
        clearTimeout(dragTimeoutId);
      }
    };
  }, [dragTimeoutId]);

  // Fetch library sources when a library is selected for Re-Match configuration
  useEffect(() => {
    const fetchLibrarySources = async (libraryId) => {
      if (!libraryId) return;
      
      // Check if we already have sources for this library
      if (librarySources[libraryId]) return;
      
      try {
        console.log(`📚 Fetching sources for library: ${libraryId}`);
        const response = await api.get(`/matching-libraries/${libraryId}/sources/search`, {
          params: { limit: 100 }
        });
        
        console.log(`✅ Received ${response.data.sources?.length || 0} sources for library ${libraryId}`);
        setLibrarySources(prev => ({
          ...prev,
          [libraryId]: response.data.sources || []
        }));
      } catch (error) {
        console.error('Error fetching library sources:', error);
        // Set empty array on error to prevent infinite retry
        setLibrarySources(prev => ({
          ...prev,
          [libraryId]: []
        }));
      }
    };

    // Fetch sources for all steps that have library re-match enabled
    if (flowData && flowData.steps) {
      flowData.steps.forEach(step => {
        const libraryId = step.config?.libraryReMatchConfig?.libraryId;
        if (libraryId) {
          fetchLibrarySources(libraryId);
        }
      });
    }
  }, [flowData, librarySources]);

  // Handle edge deletions explicitly
  const onEdgesDelete = useCallback((edgesToDelete) => {
    console.log('🗑️ [FLOW DEBUG] onEdgesDelete called:', edgesToDelete.map(e => e.id));
    
    const deletedEdgeIds = edgesToDelete.map(edge => edge.id);
    
    // Update flowData.connections by filtering out deleted connections
    const updatedConnections = (flowData.connections || []).filter((connection, index) => {
      const edgeId = `edge-${index}`;
      return !deletedEdgeIds.includes(edgeId);
    });
    
    console.log('🔄 [FLOW DEBUG] Updating flowData connections after edge deletion...');
    onFlowDataChange({
      ...flowData,
      connections: updatedConnections
    });
    console.log('✅ [FLOW DEBUG] Edge deletion completed');
  }, [flowData, onFlowDataChange]);

  // Handle edge changes (including deletions)
  const handleEdgesChange = useCallback((changes) => {
    console.log('🔍 [FLOW DEBUG] handleEdgesChange called:', {
      changesCount: changes.length,
      changeTypes: changes.map(c => c.type),
      changeIds: changes.map(c => c.id),
      timestamp: new Date().toISOString()
    });

    try {
      onEdgesChange(changes);
      console.log('✅ [FLOW DEBUG] onEdgesChange completed successfully');
    } catch (error) {
      console.error('❌ [FLOW DEBUG] Error in onEdgesChange:', error);
      throw error;
    }
    
    // Check for edge deletions and update flowData.connections
    const deletedEdges = changes.filter(change => change.type === 'remove');
    if (deletedEdges.length > 0) {
      console.log('🗑️ [FLOW DEBUG] Edge deletions detected:', deletedEdges.map(e => e.id));
      
      const deletedEdgeIds = deletedEdges.map(change => change.id);
      
      // Update flowData.connections by filtering out deleted connections
      const updatedConnections = (flowData.connections || []).filter((connection, index) => {
        const edgeId = `edge-${index}`;
        return !deletedEdgeIds.includes(edgeId);
      });
      
      console.log('🔄 [FLOW DEBUG] Updating flowData connections after edge deletion...');
      onFlowDataChange({
        ...flowData,
        connections: updatedConnections
      });
      console.log('✅ [FLOW DEBUG] flowData connections update completed');
    }
  }, [onEdgesChange, flowData, onFlowDataChange]);

  // Add new step
  const handleAddStep = (stepType) => {
    console.log('🔍 [FLOW DEBUG] handleAddStep called:', {
      stepType,
      currentStepsCount: flowData.steps?.length || 0,
      timestamp: new Date().toISOString()
    });

    const stepNames = {
      data_processing: 'Document OCR & Vision',
      ai_extraction: 'AI Field Mapping',
      enhanced_api_call: 'API Call',
      advanced_collect_input: 'Collect Input',
      branch_logic: 'Branch Logic',
      variable_operations: 'Variable Operations',
      webhook: 'Webhook',
      wait: 'Wait',
      external_data_source: 'External Data Source',
      report_template: 'Report Template'
    };

    // Find the best available position near the current viewport center
    const availablePosition = findAvailablePosition();

    // Initialize with default config based on step type
    let defaultConfig = {};
    if (stepType === 'external_data_source') {
      defaultConfig = {
        provider: 'google',
        service: 'sheets',
        operation: 'read',
        googleSheets: {
          spreadsheetUrl: '',
          sheetName: 'Sheet1',
          range: 'Sheet1!A:Z',
          searchCriteria: {
            column: '',
            operator: 'contains',
            value: '',
            multipleMode: false,
            values: []
          }
        },
        responseMapping: {
          // Output variable name: Source path in result object
          externalData: 'result.data',
          dataCount: 'result.totalRows',
          externalDataResult: 'result'
        }
      };
    }

    const newStep = {
      id: `step-${Date.now()}`,
      type: stepType,
      name: stepNames[stepType] || 'New Step',
      config: defaultConfig,
      position: availablePosition,
      enabled: true
    };

    console.log('🆕 [FLOW DEBUG] Creating new step:', {
      ...newStep,
      positionSource: 'viewport-center-calculation'
    });

    const updatedSteps = [...(flowData.steps || []), newStep];
    
    console.log('🔄 [FLOW DEBUG] Calling onFlowDataChange with new step...');
    onFlowDataChange({
      ...flowData,
      steps: updatedSteps
    });
    
    // Auto-select the newly created step for immediate configuration
    setSelectedStep(newStep);
    setSelectedNodeId(newStep.id);
    console.log('🎯 [FLOW DEBUG] Auto-selected new step for configuration:', newStep.id);
    
    console.log('✅ [FLOW DEBUG] handleAddStep completed');
  };

  // Save step configuration
  const handleSaveStepConfig = (updatedStep) => {
    if (updatedStep) {
      const updatedSteps = flowData.steps.map(step => 
        step.id === updatedStep.id ? updatedStep : step
      );
      
      onFlowDataChange({
        ...flowData,
        steps: updatedSteps
      });
      setSelectedStep(updatedStep);
    }
  };

  // Handle clicks on canvas to deselect
  const onPaneClick = useCallback(() => {
    console.log('🔍 [FLOW DEBUG] onPaneClick - deselecting nodes');
    setSelectedStep(null);
    setSelectedNodeId(null);
  }, []);

  console.log('🔍 [FLOW DEBUG] VisualFlowEditorInner render:', {
    nodesCount: nodes.length,
    edgesCount: edges.length,
    selectedNodeId,
    selectedStepId: selectedStep?.id,
    timestamp: new Date().toISOString()
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', overflow: 'hidden' }}>
      {/* Enhanced Edge Selection Styles */}
      <style>
        {`
          .react-flow__edge.selected .react-flow__edge-path {
            stroke: #3b82f6 !important;
            stroke-width: 4px !important;
            filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.6));
          }
          
          .react-flow__edge.selected .react-flow__arrowhead {
            fill: #3b82f6 !important;
          }
          
          .react-flow__edge:hover .react-flow__edge-path {
            stroke: #6366f1 !important;
            stroke-width: 3px !important;
          }
          
          .react-flow__edge:hover .react-flow__arrowhead {
            fill: #6366f1 !important;
          }
          
          .custom-edge .react-flow__edge-path {
            transition: all 0.2s ease;
          }
        `}
      </style>
      
      {/* Main Flow Editor */}
      <Box sx={{ 
        flexGrow: 1, 
        position: 'relative',
        height: '100%',
        minHeight: 0, // Important for flex child
        paddingBottom: isMobile ? '70px' : 0 // Add padding for bottom toolbar on mobile
      }}>
        {(() => {
          console.log('🔍 [FLOW DEBUG] About to render ReactFlow component...');
          return (
            <ReactFlow
              ref={reactFlowWrapperRef}
              nodes={nodes}
              edges={edges}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onEdgesDelete={onEdgesDelete}
              onConnect={onConnect}
              onPaneClick={onPaneClick}
              nodeTypes={nodeTypes}
              fitView
              snapToGrid
              snapGrid={[20, 20]}
              nodesDraggable={true}
              nodesConnectable={true}
              edgesUpdatable={false}
              selectNodesOnDrag={false}
              nodeOrigin={[0.5, 0.5]}
              deleteKeyCode={['Delete', 'Backspace']}
              multiSelectionKeyCode="Shift"
              panOnDrag={true}
              panOnScroll={true}
              zoomOnScroll={true}
              zoomOnPinch={true}
              zoomOnDoubleClick={true}
              preventScrolling={false}
              nodeExtent={undefined}
              maxZoom={2}
              minZoom={0.1}
              attributionPosition="bottom-left"
              onInit={() => console.log('✅ [FLOW DEBUG] ReactFlow initialized')}
              onNodeDragStart={onNodeDragStart}
              onNodeDrag={onNodeDrag}
              onNodeDragStop={onNodeDragStop}
              onSelectionStart={() => console.log('🔍 [FLOW DEBUG] Selection started')}
              onSelectionEnd={() => console.log('🔍 [FLOW DEBUG] Selection ended')}
              style={{ 
                height: '100%', 
                width: '100%' 
              }}
            >
              <Controls />
              <MiniMap />
              <Background variant="dots" gap={20} size={1} />
            </ReactFlow>
          );
        })()}

        {/* Desktop: Enhanced Add Step Buttons with Specialized Types - Left Side Vertical */}
        {!isMobile && (
          <Box sx={{ position: 'absolute', top: 20, left: 20, display: 'flex', flexDirection: 'column', gap: 1, zIndex: 10 }}>
            <Tooltip title="AI Flow Builder Assistant" placement="right">
              <Fab size="small" sx={{ bgcolor: '#9333ea', color: 'white', mb: 1 }} onClick={() => setAiModalOpen(true)}>
                <AIAssistantIcon />
              </Fab>
            </Tooltip>
            <Divider sx={{ my: 1 }} />
            <Tooltip title="Document OCR & Vision - Text Extraction" placement="right">
              <Fab size="small" sx={{ bgcolor: '#3b82f6', color: 'white' }} onClick={() => handleAddStep('data_processing')}>
                <ProcessingIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="AI Field Mapping - Smart Data Extraction" placement="right">
              <Fab size="small" sx={{ bgcolor: '#8b5cf6', color: 'white' }} onClick={() => handleAddStep('ai_extraction')}>
                <AiIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Enhanced API Call - Full Integration" placement="right">
              <Fab size="small" sx={{ bgcolor: '#f59e0b', color: 'white' }} onClick={() => handleAddStep('enhanced_api_call')}>
                <ApiIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Advanced Input - Data Preview & Edit" placement="right">
              <Fab size="small" sx={{ bgcolor: '#06b6d4', color: 'white' }} onClick={() => handleAddStep('advanced_collect_input')}>
                <InputIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Branch Logic - Quality & Routing" placement="right">
              <Fab size="small" sx={{ bgcolor: '#f97316', color: 'white' }} onClick={() => handleAddStep('branch_logic')}>
                <BranchIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Variable Operations - Data Manipulation" placement="right">
              <Fab size="small" sx={{ bgcolor: '#10b981', color: 'white' }} onClick={() => handleAddStep('variable_operations')}>
                <EditIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="External Data Source - Google Sheets, Docs & More" placement="right">
              <Fab size="small" sx={{ bgcolor: '#7c3aed', color: 'white' }} onClick={() => handleAddStep('external_data_source')}>
                <DataIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Webhook - Async API Callback" placement="right">
              <Fab size="small" sx={{ bgcolor: '#dc2626', color: 'white' }} onClick={() => handleAddStep('webhook')}>
                <ApiIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Wait - Delay Execution" placement="right">
              <Fab size="small" sx={{ bgcolor: '#9333ea', color: 'white' }} onClick={() => handleAddStep('wait')}>
                <PauseIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Report Template - Generate Formatted Reports" placement="right">
              <Fab size="small" sx={{ bgcolor: '#f59e0b', color: 'white' }} onClick={() => handleAddStep('report_template')}>
                <ReportIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="For Each Loop - Iterate Over Arrays" placement="right">
              <Fab size="small" sx={{ bgcolor: '#9c27b0', color: 'white' }} onClick={() => handleAddStep('for_each_loop')}>
                <LoopIcon />
              </Fab>
            </Tooltip>
          </Box>
        )}

      </Box>

      {/* Mobile FAB to toggle configuration panel when step is selected - Outside main Box for proper positioning */}
      {isMobile && selectedStep && (
        <Tooltip title="Configure Node" placement="left">
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 80,
              right: 20,
              zIndex: 1200,
              boxShadow: 4,
              '&:hover': {
                boxShadow: 6,
              }
            }}
            onClick={() => {
              console.log('📱 [MOBILE DEBUG] FAB clicked, opening config panel');
              setConfigPanelOpen(true);
            }}
          >
            <SettingsIcon />
          </Fab>
        </Tooltip>
      )}

      {/* Mobile: Bottom Toolbar with Horizontal Scroll - Outside main Box for proper positioning */}
      {isMobile && (
        <Box sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0,
          bgcolor: 'white',
          borderTop: '2px solid #e0e0e0',
          p: 1.5,
          overflowX: 'auto',
          display: 'flex',
          gap: 1.5,
          zIndex: 1100,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '3px',
          },
        }}>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#9333ea', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<AIAssistantIcon fontSize="small" />}
            onClick={() => setAiModalOpen(true)}
          >
            AI Assistant
          </Button>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5, borderColor: '#ccc' }} />
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#3b82f6', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<ProcessingIcon fontSize="small" />}
            onClick={() => handleAddStep('data_processing')}
          >
            OCR
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#8b5cf6', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<AiIcon fontSize="small" />}
            onClick={() => handleAddStep('ai_extraction')}
          >
            AI Map
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#f59e0b', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<ApiIcon fontSize="small" />}
            onClick={() => handleAddStep('enhanced_api_call')}
          >
            API
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#06b6d4', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<InputIcon fontSize="small" />}
            onClick={() => handleAddStep('advanced_collect_input')}
          >
            Input
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#f97316', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<BranchIcon fontSize="small" />}
            onClick={() => handleAddStep('branch_logic')}
          >
            Branch
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#10b981', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => handleAddStep('variable_operations')}
          >
            Variables
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#7c3aed', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<DataIcon fontSize="small" />}
            onClick={() => handleAddStep('external_data_source')}
          >
            Data
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#dc2626', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<ApiIcon fontSize="small" />}
            onClick={() => handleAddStep('webhook')}
          >
            Webhook
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#9333ea', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<PauseIcon fontSize="small" />}
            onClick={() => handleAddStep('wait')}
          >
            Wait
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#f59e0b', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<ReportIcon fontSize="small" />}
            onClick={() => handleAddStep('report_template')}
          >
            Report
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#9c27b0', 
              color: 'white', 
              minWidth: 'auto',
              px: 2,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }} 
            startIcon={<LoopIcon fontSize="small" />}
            onClick={() => handleAddStep('for_each_loop')}
          >
            Loop
          </Button>
        </Box>
      )}

      {/* Enhanced Configuration Panel */}
      <NodeConfigurationPanel
        step={selectedStep}
        onStepChange={handleSaveStepConfig}
        availableApiEndpoints={availableApiEndpoints}
        availableVariables={availableVariables}
        availableLibraries={availableLibraries}
        librarySources={librarySources}
        flowData={flowData}
        projectId={projectId}
        companyId={companyId}
        openWizardFieldDialog={openWizardFieldDialog}
        isMobile={isMobile}
        configPanelOpen={configPanelOpen}
        setConfigPanelOpen={setConfigPanelOpen}
        onClose={() => {
          console.log('🔍 [FLOW DEBUG] Configuration panel closed');
          setSelectedStep(null);
          setSelectedNodeId(null);
          if (isMobile) {
            setConfigPanelOpen(false);
          }
        }}
      />
      
      {/* Wizard Form Field Configuration Dialog */}
      <WizardFormFieldDialog
        open={wizardFieldDialogOpen}
        field={wizardFieldDialogData}
        context={wizardFieldDialogContext}
        onClose={closeWizardFieldDialog}
        onSave={saveWizardField}
      />
      
      {/* AI Flow Builder Assistant Modal */}
      <AIFlowBuilderModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        projectId={projectId}
        currentFlow={flowData}
        onFlowUpdate={async (updatedFlow, isPreview = false) => {
          console.log('🤖 [AI] Flow update received:', { 
            isPreview, 
            hasNodes: !!updatedFlow?.nodes,
            hasEdges: !!updatedFlow?.edges,
            hasSteps: !!updatedFlow?.steps,
            flow: updatedFlow
          });
          
          if (updatedFlow && updatedFlow.nodes && updatedFlow.edges && updatedFlow.steps) {
            // Convert edges back to connections format
            const connections = updatedFlow.edges.map(edge => ({
              source: edge.source,
              target: edge.target,
              sourceHandle: edge.sourceHandle,
              targetHandle: edge.targetHandle
            }));
            
            const flowDataToUpdate = {
              ...flowData,  // Preserve existing variables and settings
              steps: updatedFlow.steps,
              connections: connections
            };
            
            if (isPreview) {
              // PREVIEW MODE: Just update local state, NO save, NO reload
              console.log('👁️ [AI] Preview mode: Displaying', updatedFlow.steps.length, 'steps in canvas (not saved)');
              console.log('👁️ [AI] Calling onFlowDataChange with:', {
                stepsCount: flowDataToUpdate.steps.length,
                connectionsCount: flowDataToUpdate.connections.length,
                hasVariables: !!flowDataToUpdate.variables,
                hasSettings: !!flowDataToUpdate.settings
              });
              onFlowDataChange(flowDataToUpdate);
            } else {
              // SAVE MODE: Save to database and reload
              console.log('💾 [AI] Save mode: Saving', updatedFlow.steps.length, 'steps to database');
              
              try {
                // Import the flowStepsApi
                const { default: flowStepsApi } = await import('../api/flowStepsApi');
                
                // Save to database (only steps and connections)
                const dataToSave = {
                  steps: updatedFlow.steps,
                  connections: connections
                };
                
                console.log('💾 [AI] Calling API to save flow...');
                await flowStepsApi.updateFlowData(projectId, dataToSave);
                console.log('✅ [AI] Flow saved successfully to database');
                
                // Update local state with full data (preserving variables and settings)
                onFlowDataChange(flowDataToUpdate);
                
                // Reload the page to show the updated flow
                setTimeout(() => {
                  window.location.reload();
                }, 500);
              } catch (error) {
                console.error('❌ [AI] Error saving flow:', error);
                alert('Failed to save flow. Please try again.');
              }
            }
          } else {
            console.warn('⚠️ [AI] Missing required flow data:', {
              hasNodes: !!updatedFlow?.nodes,
              hasEdges: !!updatedFlow?.edges,
              hasSteps: !!updatedFlow?.steps
            });
          }
        }}
      />
    </Box>
  );
};

// Wrapper with ReactFlowProvider
const VisualFlowEditor = (props) => {
  return (
    <ReactFlowProvider>
      <VisualFlowEditorInner {...props} />
    </ReactFlowProvider>
  );
};

export default VisualFlowEditor;
export { TestFlowDialog }; 